import type { ToolDefinition } from '../../../types/tool-registry.js';

export const pilotTools: readonly ToolDefinition[] = [
    {
        "toolId": "percentage",
        "slug": "yuzde-hesaplama",
        "path": "/matematik/yuzde-hesaplama",
        "category": {
            "id": "matematik",
            "label": "Matematik"
        },
        "classification": "timeless",
        "engineId": "percentage-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "2026.8.18",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-08-18",
        "effectiveTo": null,
        "minAppVersion": "0.1.0",
        "updateCadence": {
            "mode": "method-change-only",
            "days": null,
            "reviewAfter": null,
            "specialMonths": [],
            "note": "Aritmetik yöntem zamansızdır; yalnız yöntem, kapsam veya anlatım değişirse insan incelemesi yapılır."
        },
        "sources": [
            {
                "sourceId": "openstax-percent-applications",
                "sourceGroupId": null,
                "title": "Prealgebra 2e — Solve General Applications of Percent",
                "publisher": "OpenStax",
                "url": "https://openstax.org/books/prealgebra-2e/pages/6-2-solve-general-applications-of-percent",
                "kind": "authoritative-education",
                "reviewedOn": "2026-08-18",
                "watchFields": [
                    "bir sayının yüzdesi",
                    "yüzde artış",
                    "yüzde azalış"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "mode",
                    "type": "radio",
                    "label": "Yüzde Hesap Modu",
                    "required": true,
                    "defaultValue": "percentage-of",
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "percentage-of",
                            "label": "X'in %Y'si"
                        },
                        {
                            "value": "what-percentage",
                            "label": "X, Y'nin yüzde kaçı?"
                        },
                        {
                            "value": "increase-decrease",
                            "label": "%Y artış ve azalış"
                        },
                        {
                            "value": "percentage-change",
                            "label": "X'ten Y'ye yüzde değişim"
                        }
                    ],
                    "visibleWhen": null
                },
                {
                    "id": "x",
                    "type": "number",
                    "label": "İlk Değer (X)",
                    "required": true,
                    "defaultValue": null,
                    "placeholder": "Ör: 250",
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": 4,
                    "options": [],
                    "visibleWhen": null
                },
                {
                    "id": "y",
                    "type": "number",
                    "label": "İkinci Değer (Y)",
                    "required": true,
                    "defaultValue": null,
                    "placeholder": "Ör: 20",
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": 4,
                    "options": [],
                    "visibleWhen": null
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "mode",
                    "type": "text",
                    "label": "Hesap Modu",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "result",
                    "type": "number",
                    "label": "Sonuç",
                    "unit": null,
                    "nullable": true,
                    "prominent": true
                },
                {
                    "id": "percentage",
                    "type": "percentage",
                    "label": "Yüzde",
                    "unit": "%",
                    "nullable": true,
                    "prominent": true
                },
                {
                    "id": "increased",
                    "type": "number",
                    "label": "Artırılmış Değer",
                    "unit": null,
                    "nullable": true,
                    "prominent": true
                },
                {
                    "id": "decreased",
                    "type": "number",
                    "label": "Azaltılmış Değer",
                    "unit": null,
                    "nullable": true,
                    "prominent": true
                },
                {
                    "id": "percentageChange",
                    "type": "percentage",
                    "label": "Yüzde Değişim",
                    "unit": "%",
                    "nullable": true,
                    "prominent": true
                },
                {
                    "id": "absoluteDifference",
                    "type": "number",
                    "label": "Mutlak Fark",
                    "unit": null,
                    "nullable": true,
                    "prominent": false
                }
            ]
        },
        "content": {
            "title": "Yüzde Hesaplama: Artış, Düşüş ve İndirim Oranı Bul",
            "shortTitle": "Yüzde Hesaplama",
            "summary": "Dört farklı modla bir sayının yüzdesini, yüzdelik payı, artış-azalış sonucunu veya iki değer arasındaki yüzde değişimi hesaplar.",
            "method": "Yüzde hesaplama temel aritmetik orantıya dayanır. Araç bir sayının yüzdesi, bir sayının diğerine oranı, artış-azalış ve eski değerden yeni değere değişim işlemlerini ayrı ve önceden tanımlı yöntemlerle uygular; sonuçlar iki ondalık hassasiyetinde sunulur.",
            "limitations": [
                "Araç yalnız girilen sayılara aritmetik yüzde işlemi uygular; vergi, faiz, enflasyon veya indirim için özel mevzuat ve bileşik hesap kurallarını kendiliğinden yorumlamaz.",
                "Yüzdelik pay modunda bütün değer; yüzde değişim modunda eski değer sıfır olamaz."
            ],
            "faqs": [
                {
                    "question": "Yüzde nasıl hesaplanır?",
                    "answer": "Bir X sayısının %Y'si, X ile Y'nin çarpılıp 100'e bölünmesiyle bulunur: X × Y / 100. Örneğin 250 sayısının %20'si 50'dir."
                },
                {
                    "question": "%20 indirim ve %20 KDV birbirini nötrler mi?",
                    "answer": "Hayır. 100 TL'lik ürüne önce %20 indirim uygulanırsa fiyat 80 TL olur; bu tutara %20 eklendiğinde sonuç 96 TL'dir. Yüzde işlemleri farklı tabanlara uygulandığı için birbirini nötrlemez."
                },
                {
                    "question": "Yüzde değişim nasıl hesaplanır?",
                    "answer": "Yüzde değişim ((Yeni Değer − Eski Değer) / Eski Değer) × 100 formülüyle bulunur. Eski değer sıfırsa payda sıfır olacağı için sonuç tanımsızdır."
                },
                {
                    "question": "Yüzdelik puan ile yüzde değişim aynı şey mi?",
                    "answer": "Hayır. Bir oran %5'ten %7'ye çıktığında fark 2 yüzde puanıdır; başlangıç değerine göre yüzde değişim ise %40'tır."
                },
                {
                    "question": "Negatif yüzde olur mu?",
                    "answer": "Evet. Negatif yüzde matematiksel olarak geçerlidir ve işaretin anlamı bağlama göre yorumlanır. Bir değeri %20 azaltmak X × (1 − 0,20) ile modellenir."
                },
                {
                    "question": "Bir sayının %15'i nasıl bulunur?",
                    "answer": "Sayı 0,15 ile çarpılır. Örneğin 2.000 TL'nin %15'i 2.000 × 0,15 = 300 TL'dir."
                },
                {
                    "question": "İndirim oranı nasıl hesaplanır?",
                    "answer": "İndirim oranı (Eski Fiyat − Yeni Fiyat) / Eski Fiyat × 100 formülüyle hesaplanır. 500 TL'den 400 TL'ye düşen üründe oran %20'dir."
                },
                {
                    "question": "KDV oranı yüzde hesabıyla nasıl bulunur?",
                    "answer": "KDV hariç tutardan dahil tutara geçerken tutar (1 + KDV/100) ile çarpılır. KDV dahil tutardan hariç tutara geçerken aynı katsayıya bölünür."
                },
                {
                    "question": "X sayısının Y'ye oranı yüzde kaç?",
                    "answer": "(X / Y) × 100 formülü kullanılır. Örneğin 30 sayısı 200'ün %15'idir."
                }
            ]
        },
        "config": {
            "kind": "percentage-presentation-v1",
            "roundingScale": 2,
            "maxInputDecimalScale": 4,
            "supportedModes": [
                "percentage-of",
                "what-percentage",
                "increase-decrease",
                "percentage-change"
            ]
        },
        "testVectors": {
            "kind": "percentage-golden-v1",
            "cases": [
                {
                    "caseId": "percentage-of-250-20",
                    "input": {
                        "mode": "percentage-of",
                        "x": 250,
                        "y": 20
                    },
                    "expected": {
                        "mode": "percentage-of",
                        "result": 50
                    },
                    "tolerance": 1e-8
                },
                {
                    "caseId": "what-percentage-30-200",
                    "input": {
                        "mode": "what-percentage",
                        "x": 30,
                        "y": 200
                    },
                    "expected": {
                        "mode": "what-percentage",
                        "percentage": 15
                    },
                    "tolerance": 1e-8
                },
                {
                    "caseId": "increase-decrease-100-20",
                    "input": {
                        "mode": "increase-decrease",
                        "x": 100,
                        "y": 20
                    },
                    "expected": {
                        "mode": "increase-decrease",
                        "increased": 120,
                        "decreased": 80
                    },
                    "tolerance": 1e-8
                },
                {
                    "caseId": "percentage-change-100-120",
                    "input": {
                        "mode": "percentage-change",
                        "x": 100,
                        "y": 120
                    },
                    "expected": {
                        "mode": "percentage-change",
                        "percentageChange": 20,
                        "absoluteDifference": 20
                    },
                    "tolerance": 1e-8
                }
            ]
        },
        "lastReviewed": "2026-08-18"
    },
    {
        "toolId": "salary",
        "slug": "maas-hesaplama",
        "path": "/bireysel-finans/maas-hesaplama",
        "category": {
            "id": "bireysel-finans",
            "label": "Bireysel Finans"
        },
        "classification": "remote-config",
        "engineId": "salary-annual-average-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "2026.8.18",
        "configVersion": "2026.1.0",
        "effectiveFrom": "2026-01-01",
        "effectiveTo": "2026-12-31",
        "minAppVersion": "0.1.0",
        "updateCadence": {
            "mode": "scheduled-source-review",
            "days": 7,
            "reviewAfter": "2027-01-01",
            "specialMonths": [
                1,
                7,
                12
            ],
            "note": "SGK ve ücret parametreleri en geç yedi günde bir; gelir vergisi ve damga vergisi kaynakları kendi daha uzun periyotlarıyla ayrıca izlenir."
        },
        "sources": [
            {
                "sourceId": "gib-income-tax-2026",
                "sourceGroupId": "tr_income_tax_tariff",
                "title": "2026 gelir vergisi tarifesi",
                "publisher": "Gelir İdaresi Başkanlığı",
                "url": "https://cdn.gib.gov.tr/api/gibportal-file/file/getFileResources?objectKey=arsiv%2Fyardim-kaynaklar%2Fyararli-bilgiler%2Fgelir-vergisi-tarifeleri%2Fgelir-vergisi-tarifesi-2026.pdf",
                "kind": "official-government",
                "reviewedOn": "2026-08-18",
                "watchFields": [
                    "ücret geliri vergi dilimleri",
                    "matrah eşikleri",
                    "vergi oranları"
                ]
            },
            {
                "sourceId": "sgk-pec-2026",
                "sourceGroupId": "tr_labor_sgk_annual",
                "title": "2026 prime esas kazanç miktarları",
                "publisher": "Sosyal Güvenlik Kurumu",
                "url": "https://www.sgk.gov.tr/Content/Post/2e0c9e1a-2cfe-4456-af10-49d3de0c58ba/Prime-Esas-Kazanc-Miktarlari-2026-01-14-10-35-39",
                "kind": "official-government",
                "reviewedOn": "2026-08-18",
                "watchFields": [
                    "asgari ücret",
                    "prime esas kazanç alt sınırı",
                    "prime esas kazanç üst sınırı"
                ]
            },
            {
                "sourceId": "sgk-employer-rates-2026",
                "sourceGroupId": "tr_labor_sgk_annual",
                "title": "İşveren prim oranları",
                "publisher": "Sosyal Güvenlik Kurumu",
                "url": "https://www.sgk.gov.tr/Content/Post/c7812ea8-5087-413f-aeb5-d3c1d153e11a/Isveren-Prim-Oranlari-2026-01-13-04-52-38",
                "kind": "official-government",
                "reviewedOn": "2026-08-18",
                "watchFields": [
                    "işçi ve işveren prim oranları",
                    "işsizlik sigortası payları"
                ]
            },
            {
                "sourceId": "csgb-wage-statistics-2026-07",
                "sourceGroupId": "tr_labor_sgk_annual",
                "title": "Temmuz 2026 ücret ve sendikal istatistikleri",
                "publisher": "Çalışma ve Sosyal Güvenlik Bakanlığı",
                "url": "https://www.csgb.gov.tr/yayinlar/calisma-hayati-istatistikleri-e-bulteni/temmuz-2026/ucret-ve-sendikal-istatistikler.html",
                "kind": "official-government",
                "reviewedOn": "2026-08-18",
                "watchFields": [
                    "ücret dönemi",
                    "asgari ücret"
                ]
            },
            {
                "sourceId": "gib-stamp-tax-current",
                "sourceGroupId": "tr_stamp_tax",
                "title": "Vergi beyannamelerinde uygulanan damga vergisi tutarları",
                "publisher": "Gelir İdaresi Başkanlığı",
                "url": "https://cdn.gib.gov.tr/api/gibportal-file/file/getFileResources?objectKey=arsiv%2Fyardim-kaynaklar%2Fyararli-bilgiler%2Fvergi-beyannamelerinde-uygulanan-damga-vergisi-tutarlari.pdf",
                "kind": "official-government",
                "reviewedOn": "2026-08-18",
                "watchFields": [
                    "nispi damga vergisi oranı",
                    "istisna"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "mode",
                    "type": "radio",
                    "label": "Hesaplama Yöntemi",
                    "required": true,
                    "defaultValue": "gross-to-net",
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "gross-to-net",
                            "label": "Brütten Nete"
                        },
                        {
                            "value": "net-to-gross",
                            "label": "Netten Brüte"
                        }
                    ],
                    "visibleWhen": null
                },
                {
                    "id": "monthlyAmount",
                    "type": "number",
                    "label": "Aylık Maaş Tutarı",
                    "required": true,
                    "defaultValue": null,
                    "placeholder": "0,00",
                    "unit": "TL",
                    "minimum": 1,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 2,
                    "options": [],
                    "visibleWhen": null
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "gross",
                    "type": "money",
                    "label": "Aylık Brüt",
                    "unit": "TL",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "employeeSgk",
                    "type": "money",
                    "label": "SGK İşçi Payı",
                    "unit": "TL",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "employeeUnemployment",
                    "type": "money",
                    "label": "İşsizlik İşçi Payı",
                    "unit": "TL",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "incomeTax",
                    "type": "money",
                    "label": "Gelir Vergisi",
                    "unit": "TL",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "stampTax",
                    "type": "money",
                    "label": "Damga Vergisi",
                    "unit": "TL",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "net",
                    "type": "money",
                    "label": "Yıllık Ortalama Aylık Net",
                    "unit": "TL",
                    "nullable": false,
                    "prominent": true
                }
            ]
        },
        "content": {
            "title": "Maaş Hesaplama (Brütten Nete ve Netten Brüte) — 2026",
            "shortTitle": "Maaş Hesaplama",
            "summary": "Brüt veya net maaştan diğerini; 2026 vergi dilimleri, SGK, işsizlik sigortası, damga vergisi ve asgari ücret istisnasıyla hesaplar.",
            "method": "Standart 4/a çalışanında 12 aylık brüt ücret üzerinden yıllık vergi ve kesintiler hesaplanır; yıllık net tutar 12'ye bölünerek yıllık ortalama aylık net bulunur. Prime esas kazanç üzerinden işçi SGK payı %14 ve işsizlik payı %1 uygulanır.",
            "limitations": [
                "Sonuç belirli bir ayın bordrosu değil, yıllık verginin 12 aya bölünmüş ortalamasıdır.",
                "Prim, ikramiye, engellilik indirimi, yemek ve yol istisnası, eksik gün, özel sigorta veya başka gelirler bu modelde yer almaz.",
                "İşveren primi çalışanın net ücretinden kesilmez ve bu aracın sonucuna dahil değildir."
            ],
            "faqs": [
                {
                    "question": "Bu sonuç belirli bir ayın bordrosu mudur?",
                    "answer": "Hayır. Araç 12 aylık vergi matrahını hesaplayıp yıllık neti 12'ye böler; sonuç yıllık ortalama aylık nettir. Gerçek aylık net kümülatif matraha göre değişir."
                },
                {
                    "question": "2026 SGK prime esas kazanç tavanı nedir?",
                    "answer": "2026 aylık prime esas kazanç alt sınırı 33.030 TL, üst sınırı bunun 9 katı olan 297.270 TL'dir. İşçi SGK ve işsizlik kesintileri bu sınırlar içinde hesaplanır."
                },
                {
                    "question": "Hangi işçi kesintileri uygulanır?",
                    "answer": "Standart 4/a varsayımında işçi SGK payı %14, işsizlik payı %1, gelir vergisi artan oranlı tarife ve damga vergisi uygulanır; asgari ücret vergi istisnası hesaba katılır."
                },
                {
                    "question": "İşveren primi sonuçta var mı?",
                    "answer": "İşveren primi net ücretten düşülmez. Teşvikli veya indirimsiz işveren maliyeti ayrı İşveren Maliyeti aracında gerçek senaryoyla hesaplanmalıdır."
                }
            ]
        },
        "config": {
            "kind": "salary-annual-rules-v1",
            "calendarYear": 2026,
            "monthsPerYear": 12,
            "minimumGrossWage": 33030,
            "socialSecurityEmployeeRate": 0.14,
            "unemploymentEmployeeRate": 0.01,
            "stampTaxRate": 0.00759,
            "socialSecurityCeilingMultiplier": 9,
            "incomeTaxBrackets": [
                {
                    "upTo": 190000,
                    "rate": 0.15
                },
                {
                    "upTo": 400000,
                    "rate": 0.2
                },
                {
                    "upTo": 1500000,
                    "rate": 0.27
                },
                {
                    "upTo": 5300000,
                    "rate": 0.35
                },
                {
                    "upTo": null,
                    "rate": 0.4
                }
            ],
            "netToGrossIteration": {
                "initialMultiplier": 1.4,
                "maxIterations": 50,
                "tolerance": 0.01,
                "correctionFactor": 1.3
            }
        },
        "testVectors": {
            "kind": "salary-golden-v1",
            "cases": [
                {
                    "caseId": "gross-50000",
                    "input": {
                        "mode": "gross-to-net",
                        "monthlyAmount": 50000
                    },
                    "expected": {
                        "gross": 50000,
                        "employeeSgk": 7000.000000000001,
                        "employeeUnemployment": 500,
                        "incomeTax": 3526.566666666667,
                        "stampTax": 128.80229999999997,
                        "net": 38844.63103333333
                    },
                    "tolerance": 1e-8,
                    "formattedNet": "38.844,63"
                },
                {
                    "caseId": "net-38844-631033",
                    "input": {
                        "mode": "net-to-gross",
                        "monthlyAmount": 38844.63103333333
                    },
                    "expected": {
                        "gross": 50000,
                        "employeeSgk": 7000.000000000001,
                        "employeeUnemployment": 500,
                        "incomeTax": 3526.566666666667,
                        "stampTax": 128.80229999999997,
                        "net": 38844.63103333333
                    },
                    "tolerance": 0.02,
                    "formattedNet": null
                }
            ]
        },
        "lastReviewed": "2026-08-18"
    },
    {
        "toolId": "hgs-toll",
        "slug": "hgs-gecis-ucreti-hesaplama",
        "path": "/arac-emlak-yasam/hgs-gecis-ucreti-hesaplama",
        "category": {
            "id": "arac-emlak-yasam",
            "label": "Araç, Emlak ve Yaşam"
        },
        "classification": "remote-config",
        "engineId": "hgs-fixed-toll-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "2026.10.28",
        "configVersion": "2026.10.28",
        "effectiveFrom": "2026-07-01",
        "effectiveTo": null,
        "minAppVersion": "0.1.0",
        "updateCadence": {
            "mode": "scheduled-source-review",
            "days": 7,
            "reviewAfter": "2027-01-01",
            "specialMonths": [
                1,
                7
            ],
            "note": "KGM ve işletmeci tarifeleri yedi günde bir izlenir; yeni tutar ancak kaynak, altın test ve insan onayından sonra yayımlanır."
        },
        "sources": [
            {
                "sourceId": "kgm-current-tolls",
                "sourceGroupId": "tr_highway_bridge_tunnel_tolls",
                "title": "Güncel otoyol ve köprü ücretleri",
                "publisher": "Karayolları Genel Müdürlüğü",
                "url": "https://www.kgm.gov.tr/sayfalar/kgm/sitetr/otoyollar/ucretleryeni.aspx?v=1.0.22",
                "kind": "official-government",
                "reviewedOn": "2026-10-28",
                "watchFields": [
                    "tesis",
                    "araç sınıfı",
                    "tek yön ücret",
                    "yürürlük tarihi"
                ]
            },
            {
                "sourceId": "eurasia-tunnel-2026-07",
                "sourceGroupId": "tr_highway_bridge_tunnel_tolls",
                "title": "1 Temmuz 2026 itibarıyla geçerli Avrasya Tüneli geçiş ücretleri",
                "publisher": "Avrasya Tüneli İşletme İnşaat ve Yatırım A.Ş.",
                "url": "https://www.avrasyatuneli.com/haberler-ve-duyurular/detay/1-temmuz-2026-itibariyla-gecerli-avrasya-tuneli-gecis-ucretleri-tarifesi",
                "kind": "official-operator",
                "reviewedOn": "2026-10-28",
                "watchFields": [
                    "araç sınıfı",
                    "gündüz ve gece",
                    "tek yön ücret",
                    "yürürlük tarihi"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "vehicleClass",
                    "type": "select",
                    "label": "Araç Sınıfı",
                    "required": true,
                    "defaultValue": "class-1",
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "class-1",
                            "label": "1. sınıf — Otomobil"
                        },
                        {
                            "value": "class-2",
                            "label": "2. sınıf — Minibüs/Kamyonet/Karavan"
                        },
                        {
                            "value": "class-3",
                            "label": "3. sınıf — Otobüs (3 aks)"
                        },
                        {
                            "value": "class-4",
                            "label": "4. sınıf — Kamyon/TIR (4-5 aks)"
                        },
                        {
                            "value": "class-5",
                            "label": "5. sınıf — Ağır vasıta (6+ aks)"
                        },
                        {
                            "value": "class-6",
                            "label": "6. sınıf — Motosiklet"
                        }
                    ],
                    "visibleWhen": null
                },
                {
                    "id": "crossing",
                    "type": "select",
                    "label": "Geçiş",
                    "required": true,
                    "defaultValue": null,
                    "placeholder": "Lütfen seçiniz",
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "15-temmuz",
                            "label": "15 Temmuz Şehitler Köprüsü"
                        },
                        {
                            "value": "fsm",
                            "label": "Fatih Sultan Mehmet Köprüsü"
                        },
                        {
                            "value": "yss",
                            "label": "Yavuz Sultan Selim Köprüsü"
                        },
                        {
                            "value": "osmangazi",
                            "label": "Osmangazi Köprüsü"
                        },
                        {
                            "value": "1915-canakkale",
                            "label": "1915 Çanakkale Köprüsü"
                        },
                        {
                            "value": "avrasya",
                            "label": "Avrasya Tüneli"
                        }
                    ],
                    "visibleWhen": null
                },
                {
                    "id": "time",
                    "type": "select",
                    "label": "Geçiş Zamanı",
                    "required": true,
                    "defaultValue": "gunduz",
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "gunduz",
                            "label": "Gündüz (05:00-23:59)"
                        },
                        {
                            "value": "gece",
                            "label": "Gece (00:00-04:59)"
                        }
                    ],
                    "visibleWhen": {
                        "fieldId": "crossing",
                        "equals": "avrasya"
                    }
                },
                {
                    "id": "direction",
                    "type": "select",
                    "label": "Yön",
                    "required": true,
                    "defaultValue": "tek",
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "tek",
                            "label": "Tek Yön"
                        },
                        {
                            "value": "cift",
                            "label": "Gidiş-Dönüş (×2)"
                        }
                    ],
                    "visibleWhen": null
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "allowed",
                    "type": "boolean",
                    "label": "Hesaplanabilir",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "crossingName",
                    "type": "text",
                    "label": "Geçiş",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "vehicleClassName",
                    "type": "text",
                    "label": "Araç Sınıfı",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "oneWayFee",
                    "type": "money",
                    "label": "Tek Yön Ücreti",
                    "unit": "TL",
                    "nullable": true,
                    "prominent": false
                },
                {
                    "id": "time",
                    "type": "text",
                    "label": "Geçiş Zamanı",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "totalFee",
                    "type": "money",
                    "label": "Toplam Geçiş Ücreti",
                    "unit": "TL",
                    "nullable": true,
                    "prominent": true
                },
                {
                    "id": "direction",
                    "type": "text",
                    "label": "Yön",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "reason",
                    "type": "text",
                    "label": "Hesaplanamama Nedeni",
                    "unit": null,
                    "nullable": true,
                    "prominent": false
                }
            ]
        },
        "content": {
            "title": "HGS Köprü ve Tünel Geçiş Ücreti Hesaplama 2026",
            "shortTitle": "HGS Geçiş Ücreti",
            "summary": "Araç sınıfı, köprü veya tünel, geçiş zamanı ve yön seçimine göre güncel sabit tarife üzerinden geçiş ücretini hesaplar.",
            "method": "KGM köprü ücretleri 2026 merkezi tarifesinden; Avrasya Tüneli ücretleri 1 Temmuz 2026 tarifesinden alınır. Seçili araç sınıfının tek yön tutarı, gidiş-dönüş seçiminde ikiyle çarpılır. Avrasya gece tarifesi yalnız 00:00-04:59 aralığı için kullanılır.",
            "limitations": [
                "Araç yalnız listelenen sabit tarifeli köprü ve tünelleri kapsar; otoyol giriş-çıkışına bağlı mesafe ücreti ayrıca KGM sorgusundan kontrol edilmelidir.",
                "HGS sınıfı aks sayısı ve aks aralığına göre belirlenir; ruhsat türü tek başına yeterli değildir.",
                "İhlalli geçiş cezası, tahsilat süresi ve bakiye işlemleri hesaplamaya dahil değildir."
            ],
            "faqs": [
                {
                    "question": "Avrasya Tüneli 1 Temmuz 2026 ücretleri nedir?",
                    "answer": "Gündüz 1. sınıf 330 TL, 2. sınıf 495 TL, motosiklet 257,40 TL; gece 00:00-04:59 arasında sırasıyla 165 TL, 247,50 TL ve 128,70 TL'dir."
                },
                {
                    "question": "KGM köprü ücretleri hangi döneme aittir?",
                    "answer": "KGM köprü tutarları 2026 güncel merkezi tarifesinden; Avrasya tutarları ise 1 Temmuz 2026 özel tarifesinden alınır."
                },
                {
                    "question": "Araç sınıfı nasıl belirlenir?",
                    "answer": "Sınıf aks sayısı ve aks aralığına göre belirlenir. Standart otomobil 1. sınıftır; HGS hesabındaki resmî araç sınıfı esas alınmalıdır."
                },
                {
                    "question": "Otoyol mesafe ücreti dahil midir?",
                    "answer": "Hayır. Araç sabit tarifeli seçili köprü ve tünelleri hesaplar. Otoyol giriş-çıkış mesafe ücreti KGM'nin güncel sorgusundan ayrıca kontrol edilmelidir."
                }
            ]
        },
        "config": {
            "kind": "hgs-fixed-tariffs-v1",
            "currency": "TRY",
            "vatIncluded": true,
            "roundTripMultiplier": 2,
            "nightWindow": {
                "start": "00:00",
                "end": "05:00"
            },
            "vehicleClasses": [
                {
                    "id": "class-1",
                    "label": "1. sınıf (Otomobil)"
                },
                {
                    "id": "class-2",
                    "label": "2. sınıf (Minibüs/Kamyonet)"
                },
                {
                    "id": "class-3",
                    "label": "3. sınıf (Otobüs)"
                },
                {
                    "id": "class-4",
                    "label": "4. sınıf (Kamyon/TIR)"
                },
                {
                    "id": "class-5",
                    "label": "5. sınıf (Ağır vasıta)"
                },
                {
                    "id": "class-6",
                    "label": "6. sınıf (Motosiklet)"
                }
            ],
            "crossings": [
                {
                    "id": "15-temmuz",
                    "label": "15 Temmuz Şehitler Köprüsü",
                    "effectiveFrom": "2026-01-01",
                    "pricesByVehicleClass": {
                        "class-1": 59,
                        "class-2": 75,
                        "class-3": 168,
                        "class-4": 333,
                        "class-5": 440,
                        "class-6": 25
                    },
                    "nightPricesByVehicleClass": null
                },
                {
                    "id": "fsm",
                    "label": "Fatih Sultan Mehmet Köprüsü",
                    "effectiveFrom": "2026-01-01",
                    "pricesByVehicleClass": {
                        "class-1": 59,
                        "class-2": 75,
                        "class-3": 168,
                        "class-4": 333,
                        "class-5": 440,
                        "class-6": 25
                    },
                    "nightPricesByVehicleClass": null
                },
                {
                    "id": "yss",
                    "label": "Yavuz Sultan Selim Köprüsü",
                    "effectiveFrom": "2026-07-01",
                    "pricesByVehicleClass": {
                        "class-1": 110,
                        "class-2": 145,
                        "class-3": 270,
                        "class-4": 690,
                        "class-5": 860,
                        "class-6": 75
                    },
                    "nightPricesByVehicleClass": null
                },
                {
                    "id": "osmangazi",
                    "label": "Osmangazi Köprüsü",
                    "effectiveFrom": "2026-07-01",
                    "pricesByVehicleClass": {
                        "class-1": 1170,
                        "class-2": 1870,
                        "class-3": 2225,
                        "class-4": 2950,
                        "class-5": 3720,
                        "class-6": 820
                    },
                    "nightPricesByVehicleClass": null
                },
                {
                    "id": "1915-canakkale",
                    "label": "1915 Çanakkale Köprüsü",
                    "effectiveFrom": "2026-07-01",
                    "pricesByVehicleClass": {
                        "class-1": 1170,
                        "class-2": 1465,
                        "class-3": 2635,
                        "class-4": 2925,
                        "class-5": 5560,
                        "class-6": 295
                    },
                    "nightPricesByVehicleClass": null
                },
                {
                    "id": "avrasya",
                    "label": "Avrasya Tüneli",
                    "effectiveFrom": "2026-07-01",
                    "pricesByVehicleClass": {
                        "class-1": 330,
                        "class-2": 495,
                        "class-3": null,
                        "class-4": null,
                        "class-5": null,
                        "class-6": 257.4
                    },
                    "nightPricesByVehicleClass": {
                        "class-1": 165,
                        "class-2": 247.5,
                        "class-3": null,
                        "class-4": null,
                        "class-5": null,
                        "class-6": 128.7
                    }
                }
            ]
        },
        "testVectors": {
            "kind": "hgs-matrix-v1",
            "vehicleClasses": [
                "class-1",
                "class-2",
                "class-3",
                "class-4",
                "class-5",
                "class-6"
            ],
            "times": [
                "gunduz",
                "gece"
            ],
            "directions": [
                "tek",
                "cift"
            ],
            "roundTripMultiplier": 2,
            "nightWindow": {
                "startInclusive": "00:00",
                "endExclusive": "05:00"
            },
            "crossings": [
                {
                    "crossing": "15-temmuz",
                    "standardFees": [
                        59,
                        75,
                        168,
                        333,
                        440,
                        25
                    ],
                    "nightFees": null
                },
                {
                    "crossing": "fsm",
                    "standardFees": [
                        59,
                        75,
                        168,
                        333,
                        440,
                        25
                    ],
                    "nightFees": null
                },
                {
                    "crossing": "yss",
                    "standardFees": [
                        110,
                        145,
                        270,
                        690,
                        860,
                        75
                    ],
                    "nightFees": null
                },
                {
                    "crossing": "osmangazi",
                    "standardFees": [
                        1170,
                        1870,
                        2225,
                        2950,
                        3720,
                        820
                    ],
                    "nightFees": null
                },
                {
                    "crossing": "1915-canakkale",
                    "standardFees": [
                        1170,
                        1465,
                        2635,
                        2925,
                        5560,
                        295
                    ],
                    "nightFees": null
                },
                {
                    "crossing": "avrasya",
                    "standardFees": [
                        330,
                        495,
                        null,
                        null,
                        null,
                        257.4
                    ],
                    "nightFees": [
                        165,
                        247.5,
                        null,
                        null,
                        null,
                        128.7
                    ]
                }
            ]
        },
        "lastReviewed": "2026-10-28"
    },
    {
        "toolId": "unit-weight",
        "slug": "agirlik-hesaplama",
        "path": "/birim-cevirici/agirlik-hesaplama",
        "category": {
            "id": "birim-cevirici",
            "label": "Birim Çevirici"
        },
        "classification": "timeless",
        "engineId": "unit-converter-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-08-20",
        "effectiveTo": null,
        "minAppVersion": "0.1.0",
        "updateCadence": {
            "mode": "method-change-only",
            "days": null,
            "reviewAfter": null,
            "specialMonths": [],
            "note": "Yalnız uluslararası birim tanımı veya uygulanan dönüşüm yöntemi değişirse yeniden incelenir."
        },
        "sources": [
            {
                "sourceId": "nist-si-conversion-factors",
                "sourceGroupId": null,
                "title": "NIST Guide to the SI — Appendix B, Conversion Factors",
                "publisher": "National Institute of Standards and Technology",
                "url": "https://www.nist.gov/pml/special-publication-811/nist-guide-si-appendix-b-conversion-factors",
                "kind": "official-government",
                "reviewedOn": "2026-08-20",
                "watchFields": [
                    "SI ve SI dışı birim dönüşüm sabitleri"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "value",
                    "type": "number",
                    "label": "Değer",
                    "required": true,
                    "defaultValue": null,
                    "placeholder": "Çevrilecek değeri girin",
                    "unit": null,
                    "minimum": 0,
                    "maximum": 1000000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": null
                },
                {
                    "id": "fromUnit",
                    "type": "select",
                    "label": "Kaynak birim",
                    "required": true,
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "ton",
                            "label": "Ton (t)"
                        },
                        {
                            "value": "kg",
                            "label": "Kilogram (kg)"
                        },
                        {
                            "value": "g",
                            "label": "Gram (g)"
                        },
                        {
                            "value": "mg",
                            "label": "Miligram (mg)"
                        },
                        {
                            "value": "mcg",
                            "label": "Mikrogram (mcg)"
                        },
                        {
                            "value": "lb",
                            "label": "Libre / Pound (lb)"
                        },
                        {
                            "value": "oz",
                            "label": "Ons (oz)"
                        },
                        {
                            "value": "karat",
                            "label": "Karat (ct)"
                        },
                        {
                            "value": "stone",
                            "label": "Stone (st)"
                        }
                    ],
                    "visibleWhen": null
                },
                {
                    "id": "toUnit",
                    "type": "select",
                    "label": "Hedef birim",
                    "required": true,
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "ton",
                            "label": "Ton (t)"
                        },
                        {
                            "value": "kg",
                            "label": "Kilogram (kg)"
                        },
                        {
                            "value": "g",
                            "label": "Gram (g)"
                        },
                        {
                            "value": "mg",
                            "label": "Miligram (mg)"
                        },
                        {
                            "value": "mcg",
                            "label": "Mikrogram (mcg)"
                        },
                        {
                            "value": "lb",
                            "label": "Libre / Pound (lb)"
                        },
                        {
                            "value": "oz",
                            "label": "Ons (oz)"
                        },
                        {
                            "value": "karat",
                            "label": "Karat (ct)"
                        },
                        {
                            "value": "stone",
                            "label": "Stone (st)"
                        }
                    ],
                    "visibleWhen": null
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "converterId",
                    "type": "text",
                    "label": "Dönüştürücü",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "input",
                    "type": "number",
                    "label": "Girdi",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "fromUnit",
                    "type": "text",
                    "label": "Kaynak birim",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "toUnit",
                    "type": "text",
                    "label": "Hedef birim",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "result",
                    "type": "number",
                    "label": "Sonuç",
                    "unit": null,
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "conversions",
                    "type": "number-map",
                    "label": "Tüm dönüşümler",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                }
            ]
        },
        "content": {
            "title": "Ağırlık ve Kütle Birimi Dönüştürme",
            "shortTitle": "Ağırlık Dönüştürme",
            "summary": "Ton, kilogram, gram ve yaygın uluslararası kütle birimleri arasında çevrimdışı dönüşüm yapar.",
            "method": "Her değer, NIST tarafından yayımlanan kesin dönüşüm sabitleriyle önce kilogram bazına, ardından hedef birime çevrilir.",
            "limitations": [
                "Sonuçlar tanımlı sabit birimlerle sınırlıdır; ölçüm cihazı belirsizliğini içermez."
            ],
            "faqs": [
                {
                    "question": "Bu dönüştürücü internet olmadan çalışır mı?",
                    "answer": "Evet. Onaylı sabitler uygulamayla birlikte gelir ve hesaplama tamamen cihazda yapılır."
                }
            ]
        },
        "config": {
            "kind": "unit-converter-v1",
            "converterId": "weight",
            "strategy": "linear",
            "allowNegative": false,
            "maxAbsoluteInput": 1000000000000,
            "units": [
                {
                    "id": "ton",
                    "label": "Ton",
                    "symbol": "t",
                    "factor": 1000
                },
                {
                    "id": "kg",
                    "label": "Kilogram",
                    "symbol": "kg",
                    "factor": 1
                },
                {
                    "id": "g",
                    "label": "Gram",
                    "symbol": "g",
                    "factor": 0.001
                },
                {
                    "id": "mg",
                    "label": "Miligram",
                    "symbol": "mg",
                    "factor": 0.000001
                },
                {
                    "id": "mcg",
                    "label": "Mikrogram",
                    "symbol": "mcg",
                    "factor": 1e-9
                },
                {
                    "id": "lb",
                    "label": "Libre / Pound",
                    "symbol": "lb",
                    "factor": 0.45359237
                },
                {
                    "id": "oz",
                    "label": "Ons",
                    "symbol": "oz",
                    "factor": 0.028349523125
                },
                {
                    "id": "karat",
                    "label": "Karat",
                    "symbol": "ct",
                    "factor": 0.0002
                },
                {
                    "id": "stone",
                    "label": "Stone",
                    "symbol": "st",
                    "factor": 6.35029318
                }
            ]
        },
        "testVectors": {
            "kind": "unit-converter-oracle-v1",
            "cases": [
                {
                    "caseId": "ton-to-kg",
                    "input": {
                        "value": 1,
                        "fromUnit": "ton",
                        "toUnit": "kg"
                    },
                    "expected": {
                        "converterId": "weight",
                        "input": 1,
                        "fromUnit": "ton",
                        "toUnit": "kg",
                        "result": 1000
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "kg-to-kg",
                    "input": {
                        "value": 1,
                        "fromUnit": "kg",
                        "toUnit": "kg"
                    },
                    "expected": {
                        "converterId": "weight",
                        "input": 1,
                        "fromUnit": "kg",
                        "toUnit": "kg",
                        "result": 1
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "g-to-kg",
                    "input": {
                        "value": 1,
                        "fromUnit": "g",
                        "toUnit": "kg"
                    },
                    "expected": {
                        "converterId": "weight",
                        "input": 1,
                        "fromUnit": "g",
                        "toUnit": "kg",
                        "result": 0.001
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "mg-to-kg",
                    "input": {
                        "value": 1,
                        "fromUnit": "mg",
                        "toUnit": "kg"
                    },
                    "expected": {
                        "converterId": "weight",
                        "input": 1,
                        "fromUnit": "mg",
                        "toUnit": "kg",
                        "result": 0.000001
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "mcg-to-kg",
                    "input": {
                        "value": 1,
                        "fromUnit": "mcg",
                        "toUnit": "kg"
                    },
                    "expected": {
                        "converterId": "weight",
                        "input": 1,
                        "fromUnit": "mcg",
                        "toUnit": "kg",
                        "result": 1e-9
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "lb-to-kg",
                    "input": {
                        "value": 1,
                        "fromUnit": "lb",
                        "toUnit": "kg"
                    },
                    "expected": {
                        "converterId": "weight",
                        "input": 1,
                        "fromUnit": "lb",
                        "toUnit": "kg",
                        "result": 0.45359237
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "oz-to-kg",
                    "input": {
                        "value": 1,
                        "fromUnit": "oz",
                        "toUnit": "kg"
                    },
                    "expected": {
                        "converterId": "weight",
                        "input": 1,
                        "fromUnit": "oz",
                        "toUnit": "kg",
                        "result": 0.028349523125
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "karat-to-kg",
                    "input": {
                        "value": 1,
                        "fromUnit": "karat",
                        "toUnit": "kg"
                    },
                    "expected": {
                        "converterId": "weight",
                        "input": 1,
                        "fromUnit": "karat",
                        "toUnit": "kg",
                        "result": 0.0002
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "stone-to-kg",
                    "input": {
                        "value": 1,
                        "fromUnit": "stone",
                        "toUnit": "kg"
                    },
                    "expected": {
                        "converterId": "weight",
                        "input": 1,
                        "fromUnit": "stone",
                        "toUnit": "kg",
                        "result": 6.35029318
                    },
                    "tolerance": 1e-10
                }
            ]
        },
        "lastReviewed": "2026-08-20"
    },
    {
        "toolId": "unit-area",
        "slug": "alan-hesaplama",
        "path": "/birim-cevirici/alan-hesaplama",
        "category": {
            "id": "birim-cevirici",
            "label": "Birim Çevirici"
        },
        "classification": "timeless",
        "engineId": "unit-converter-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-08-20",
        "effectiveTo": null,
        "minAppVersion": "0.1.0",
        "updateCadence": {
            "mode": "method-change-only",
            "days": null,
            "reviewAfter": null,
            "specialMonths": [],
            "note": "Yalnız uluslararası birim tanımı veya uygulanan dönüşüm yöntemi değişirse yeniden incelenir."
        },
        "sources": [
            {
                "sourceId": "nist-si-conversion-factors",
                "sourceGroupId": null,
                "title": "NIST Guide to the SI — Appendix B, Conversion Factors",
                "publisher": "National Institute of Standards and Technology",
                "url": "https://www.nist.gov/pml/special-publication-811/nist-guide-si-appendix-b-conversion-factors",
                "kind": "official-government",
                "reviewedOn": "2026-08-20",
                "watchFields": [
                    "SI ve SI dışı birim dönüşüm sabitleri"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "value",
                    "type": "number",
                    "label": "Değer",
                    "required": true,
                    "defaultValue": null,
                    "placeholder": "Çevrilecek değeri girin",
                    "unit": null,
                    "minimum": 0,
                    "maximum": 1000000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": null
                },
                {
                    "id": "fromUnit",
                    "type": "select",
                    "label": "Kaynak birim",
                    "required": true,
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "km2",
                            "label": "Kilometrekare (km²)"
                        },
                        {
                            "value": "m2",
                            "label": "Metrekare (m²)"
                        },
                        {
                            "value": "cm2",
                            "label": "Santimetrekare (cm²)"
                        },
                        {
                            "value": "mm2",
                            "label": "Milimetrekare (mm²)"
                        },
                        {
                            "value": "hektar",
                            "label": "Hektar (ha)"
                        },
                        {
                            "value": "donum",
                            "label": "Dönüm (dönüm)"
                        },
                        {
                            "value": "ft2",
                            "label": "Ayak kare (ft²)"
                        },
                        {
                            "value": "in2",
                            "label": "İnç kare (in²)"
                        },
                        {
                            "value": "yd2",
                            "label": "Yard kare (yd²)"
                        },
                        {
                            "value": "akre",
                            "label": "Akre (acre)"
                        }
                    ],
                    "visibleWhen": null
                },
                {
                    "id": "toUnit",
                    "type": "select",
                    "label": "Hedef birim",
                    "required": true,
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "km2",
                            "label": "Kilometrekare (km²)"
                        },
                        {
                            "value": "m2",
                            "label": "Metrekare (m²)"
                        },
                        {
                            "value": "cm2",
                            "label": "Santimetrekare (cm²)"
                        },
                        {
                            "value": "mm2",
                            "label": "Milimetrekare (mm²)"
                        },
                        {
                            "value": "hektar",
                            "label": "Hektar (ha)"
                        },
                        {
                            "value": "donum",
                            "label": "Dönüm (dönüm)"
                        },
                        {
                            "value": "ft2",
                            "label": "Ayak kare (ft²)"
                        },
                        {
                            "value": "in2",
                            "label": "İnç kare (in²)"
                        },
                        {
                            "value": "yd2",
                            "label": "Yard kare (yd²)"
                        },
                        {
                            "value": "akre",
                            "label": "Akre (acre)"
                        }
                    ],
                    "visibleWhen": null
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "converterId",
                    "type": "text",
                    "label": "Dönüştürücü",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "input",
                    "type": "number",
                    "label": "Girdi",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "fromUnit",
                    "type": "text",
                    "label": "Kaynak birim",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "toUnit",
                    "type": "text",
                    "label": "Hedef birim",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "result",
                    "type": "number",
                    "label": "Sonuç",
                    "unit": null,
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "conversions",
                    "type": "number-map",
                    "label": "Tüm dönüşümler",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                }
            ]
        },
        "content": {
            "title": "Alan Birimi Dönüştürme",
            "shortTitle": "Alan Dönüştürme",
            "summary": "Metrik alan birimleri, dönüm, hektar, akre ve yaygın İngiliz alan birimleri arasında dönüşüm yapar.",
            "method": "Her değer, yayımlanmış kesin uzunluk sabitlerinin karesi kullanılarak metrekare bazına ve oradan hedef birime çevrilir.",
            "limitations": [
                "Dönüm, Türkiye'deki modern 1.000 metrekare tanımıyla modellenir; tarihsel yerel dönüm değerleri kapsanmaz."
            ],
            "faqs": [
                {
                    "question": "Bu dönüştürücü internet olmadan çalışır mı?",
                    "answer": "Evet. Onaylı sabitler uygulamayla birlikte gelir ve hesaplama tamamen cihazda yapılır."
                }
            ]
        },
        "config": {
            "kind": "unit-converter-v1",
            "converterId": "area",
            "strategy": "linear",
            "allowNegative": false,
            "maxAbsoluteInput": 1000000000000,
            "units": [
                {
                    "id": "km2",
                    "label": "Kilometrekare",
                    "symbol": "km²",
                    "factor": 1000000
                },
                {
                    "id": "m2",
                    "label": "Metrekare",
                    "symbol": "m²",
                    "factor": 1
                },
                {
                    "id": "cm2",
                    "label": "Santimetrekare",
                    "symbol": "cm²",
                    "factor": 0.0001
                },
                {
                    "id": "mm2",
                    "label": "Milimetrekare",
                    "symbol": "mm²",
                    "factor": 0.000001
                },
                {
                    "id": "hektar",
                    "label": "Hektar",
                    "symbol": "ha",
                    "factor": 10000
                },
                {
                    "id": "donum",
                    "label": "Dönüm",
                    "symbol": "dönüm",
                    "factor": 1000
                },
                {
                    "id": "ft2",
                    "label": "Ayak kare",
                    "symbol": "ft²",
                    "factor": 0.09290304
                },
                {
                    "id": "in2",
                    "label": "İnç kare",
                    "symbol": "in²",
                    "factor": 0.00064516
                },
                {
                    "id": "yd2",
                    "label": "Yard kare",
                    "symbol": "yd²",
                    "factor": 0.83612736
                },
                {
                    "id": "akre",
                    "label": "Akre",
                    "symbol": "acre",
                    "factor": 4046.8564224
                }
            ]
        },
        "testVectors": {
            "kind": "unit-converter-oracle-v1",
            "cases": [
                {
                    "caseId": "km2-to-m2",
                    "input": {
                        "value": 1,
                        "fromUnit": "km2",
                        "toUnit": "m2"
                    },
                    "expected": {
                        "converterId": "area",
                        "input": 1,
                        "fromUnit": "km2",
                        "toUnit": "m2",
                        "result": 1000000
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "m2-to-m2",
                    "input": {
                        "value": 1,
                        "fromUnit": "m2",
                        "toUnit": "m2"
                    },
                    "expected": {
                        "converterId": "area",
                        "input": 1,
                        "fromUnit": "m2",
                        "toUnit": "m2",
                        "result": 1
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "cm2-to-m2",
                    "input": {
                        "value": 1,
                        "fromUnit": "cm2",
                        "toUnit": "m2"
                    },
                    "expected": {
                        "converterId": "area",
                        "input": 1,
                        "fromUnit": "cm2",
                        "toUnit": "m2",
                        "result": 0.0001
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "mm2-to-m2",
                    "input": {
                        "value": 1,
                        "fromUnit": "mm2",
                        "toUnit": "m2"
                    },
                    "expected": {
                        "converterId": "area",
                        "input": 1,
                        "fromUnit": "mm2",
                        "toUnit": "m2",
                        "result": 0.000001
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "hektar-to-m2",
                    "input": {
                        "value": 1,
                        "fromUnit": "hektar",
                        "toUnit": "m2"
                    },
                    "expected": {
                        "converterId": "area",
                        "input": 1,
                        "fromUnit": "hektar",
                        "toUnit": "m2",
                        "result": 10000
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "donum-to-m2",
                    "input": {
                        "value": 1,
                        "fromUnit": "donum",
                        "toUnit": "m2"
                    },
                    "expected": {
                        "converterId": "area",
                        "input": 1,
                        "fromUnit": "donum",
                        "toUnit": "m2",
                        "result": 1000
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "ft2-to-m2",
                    "input": {
                        "value": 1,
                        "fromUnit": "ft2",
                        "toUnit": "m2"
                    },
                    "expected": {
                        "converterId": "area",
                        "input": 1,
                        "fromUnit": "ft2",
                        "toUnit": "m2",
                        "result": 0.09290304
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "in2-to-m2",
                    "input": {
                        "value": 1,
                        "fromUnit": "in2",
                        "toUnit": "m2"
                    },
                    "expected": {
                        "converterId": "area",
                        "input": 1,
                        "fromUnit": "in2",
                        "toUnit": "m2",
                        "result": 0.00064516
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "yd2-to-m2",
                    "input": {
                        "value": 1,
                        "fromUnit": "yd2",
                        "toUnit": "m2"
                    },
                    "expected": {
                        "converterId": "area",
                        "input": 1,
                        "fromUnit": "yd2",
                        "toUnit": "m2",
                        "result": 0.83612736
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "akre-to-m2",
                    "input": {
                        "value": 1,
                        "fromUnit": "akre",
                        "toUnit": "m2"
                    },
                    "expected": {
                        "converterId": "area",
                        "input": 1,
                        "fromUnit": "akre",
                        "toUnit": "m2",
                        "result": 4046.8564224
                    },
                    "tolerance": 1e-10
                }
            ]
        },
        "lastReviewed": "2026-08-20"
    },
    {
        "toolId": "unit-volume",
        "slug": "hacim-hesaplama",
        "path": "/birim-cevirici/hacim-hesaplama",
        "category": {
            "id": "birim-cevirici",
            "label": "Birim Çevirici"
        },
        "classification": "timeless",
        "engineId": "unit-converter-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-08-20",
        "effectiveTo": null,
        "minAppVersion": "0.1.0",
        "updateCadence": {
            "mode": "method-change-only",
            "days": null,
            "reviewAfter": null,
            "specialMonths": [],
            "note": "Yalnız uluslararası birim tanımı veya uygulanan dönüşüm yöntemi değişirse yeniden incelenir."
        },
        "sources": [
            {
                "sourceId": "bipm-si-brochure",
                "sourceGroupId": null,
                "title": "The International System of Units (SI Brochure)",
                "publisher": "Bureau International des Poids et Mesures",
                "url": "https://www.bipm.org/en/publications/si-brochure",
                "kind": "international-standards-body",
                "reviewedOn": "2026-08-20",
                "watchFields": [
                    "SI temel ve türetilmiş birim tanımları"
                ]
            },
            {
                "sourceId": "nist-si-conversion-factors",
                "sourceGroupId": null,
                "title": "NIST Guide to the SI — Appendix B, Conversion Factors",
                "publisher": "National Institute of Standards and Technology",
                "url": "https://www.nist.gov/pml/special-publication-811/nist-guide-si-appendix-b-conversion-factors",
                "kind": "official-government",
                "reviewedOn": "2026-08-20",
                "watchFields": [
                    "SI ve SI dışı birim dönüşüm sabitleri"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "value",
                    "type": "number",
                    "label": "Değer",
                    "required": true,
                    "defaultValue": null,
                    "placeholder": "Çevrilecek değeri girin",
                    "unit": null,
                    "minimum": 0,
                    "maximum": 1000000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": null
                },
                {
                    "id": "fromUnit",
                    "type": "select",
                    "label": "Kaynak birim",
                    "required": true,
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "m3",
                            "label": "Metreküp (m³)"
                        },
                        {
                            "value": "L",
                            "label": "Litre (L)"
                        },
                        {
                            "value": "mL",
                            "label": "Mililitre (mL)"
                        },
                        {
                            "value": "gal_us",
                            "label": "ABD sıvı galonu (US gal)"
                        },
                        {
                            "value": "gal_uk",
                            "label": "İngiliz galonu (UK gal)"
                        },
                        {
                            "value": "ft3",
                            "label": "Fit küp (ft³)"
                        },
                        {
                            "value": "in3",
                            "label": "İnç küp (in³)"
                        },
                        {
                            "value": "varil",
                            "label": "Petrol varili (bbl)"
                        },
                        {
                            "value": "bardak",
                            "label": "250 ml kap (kap)"
                        },
                        {
                            "value": "yk",
                            "label": "Yemek kaşığı (yk)"
                        },
                        {
                            "value": "tk",
                            "label": "Çay kaşığı (tk)"
                        }
                    ],
                    "visibleWhen": null
                },
                {
                    "id": "toUnit",
                    "type": "select",
                    "label": "Hedef birim",
                    "required": true,
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "m3",
                            "label": "Metreküp (m³)"
                        },
                        {
                            "value": "L",
                            "label": "Litre (L)"
                        },
                        {
                            "value": "mL",
                            "label": "Mililitre (mL)"
                        },
                        {
                            "value": "gal_us",
                            "label": "ABD sıvı galonu (US gal)"
                        },
                        {
                            "value": "gal_uk",
                            "label": "İngiliz galonu (UK gal)"
                        },
                        {
                            "value": "ft3",
                            "label": "Fit küp (ft³)"
                        },
                        {
                            "value": "in3",
                            "label": "İnç küp (in³)"
                        },
                        {
                            "value": "varil",
                            "label": "Petrol varili (bbl)"
                        },
                        {
                            "value": "bardak",
                            "label": "250 ml kap (kap)"
                        },
                        {
                            "value": "yk",
                            "label": "Yemek kaşığı (yk)"
                        },
                        {
                            "value": "tk",
                            "label": "Çay kaşığı (tk)"
                        }
                    ],
                    "visibleWhen": null
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "converterId",
                    "type": "text",
                    "label": "Dönüştürücü",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "input",
                    "type": "number",
                    "label": "Girdi",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "fromUnit",
                    "type": "text",
                    "label": "Kaynak birim",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "toUnit",
                    "type": "text",
                    "label": "Hedef birim",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "result",
                    "type": "number",
                    "label": "Sonuç",
                    "unit": null,
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "conversions",
                    "type": "number-map",
                    "label": "Tüm dönüşümler",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                }
            ]
        },
        "content": {
            "title": "Hacim Birimi Dönüştürme",
            "shortTitle": "Hacim Dönüştürme",
            "summary": "Litre, metreküp, galon, varil ve yaygın mutfak hacmi birimleri arasında dönüşüm yapar.",
            "method": "Her değer BIPM SI tanımı ve NIST sabitleriyle litre bazına, ardından hedef birime çevrilir.",
            "limitations": [
                "Bardak 250 ml, yemek kaşığı 15 ml ve çay kaşığı 5 ml sabit tarif varsayımıdır."
            ],
            "faqs": [
                {
                    "question": "Bu dönüştürücü internet olmadan çalışır mı?",
                    "answer": "Evet. Onaylı sabitler uygulamayla birlikte gelir ve hesaplama tamamen cihazda yapılır."
                }
            ]
        },
        "config": {
            "kind": "unit-converter-v1",
            "converterId": "volume",
            "strategy": "linear",
            "allowNegative": false,
            "maxAbsoluteInput": 1000000000000,
            "units": [
                {
                    "id": "m3",
                    "label": "Metreküp",
                    "symbol": "m³",
                    "factor": 1000
                },
                {
                    "id": "L",
                    "label": "Litre",
                    "symbol": "L",
                    "factor": 1
                },
                {
                    "id": "mL",
                    "label": "Mililitre",
                    "symbol": "mL",
                    "factor": 0.001
                },
                {
                    "id": "gal_us",
                    "label": "ABD sıvı galonu",
                    "symbol": "US gal",
                    "factor": 3.785411784
                },
                {
                    "id": "gal_uk",
                    "label": "İngiliz galonu",
                    "symbol": "UK gal",
                    "factor": 4.54609
                },
                {
                    "id": "ft3",
                    "label": "Fit küp",
                    "symbol": "ft³",
                    "factor": 28.316846592
                },
                {
                    "id": "in3",
                    "label": "İnç küp",
                    "symbol": "in³",
                    "factor": 0.016387064
                },
                {
                    "id": "varil",
                    "label": "Petrol varili",
                    "symbol": "bbl",
                    "factor": 158.987294928
                },
                {
                    "id": "bardak",
                    "label": "250 ml kap",
                    "symbol": "kap",
                    "factor": 0.25
                },
                {
                    "id": "yk",
                    "label": "Yemek kaşığı",
                    "symbol": "yk",
                    "factor": 0.015
                },
                {
                    "id": "tk",
                    "label": "Çay kaşığı",
                    "symbol": "tk",
                    "factor": 0.005
                }
            ]
        },
        "testVectors": {
            "kind": "unit-converter-oracle-v1",
            "cases": [
                {
                    "caseId": "m3-to-l",
                    "input": {
                        "value": 1,
                        "fromUnit": "m3",
                        "toUnit": "L"
                    },
                    "expected": {
                        "converterId": "volume",
                        "input": 1,
                        "fromUnit": "m3",
                        "toUnit": "L",
                        "result": 1000
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "l-to-l",
                    "input": {
                        "value": 1,
                        "fromUnit": "L",
                        "toUnit": "L"
                    },
                    "expected": {
                        "converterId": "volume",
                        "input": 1,
                        "fromUnit": "L",
                        "toUnit": "L",
                        "result": 1
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "ml-to-l",
                    "input": {
                        "value": 1,
                        "fromUnit": "mL",
                        "toUnit": "L"
                    },
                    "expected": {
                        "converterId": "volume",
                        "input": 1,
                        "fromUnit": "mL",
                        "toUnit": "L",
                        "result": 0.001
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "gal-us-to-l",
                    "input": {
                        "value": 1,
                        "fromUnit": "gal_us",
                        "toUnit": "L"
                    },
                    "expected": {
                        "converterId": "volume",
                        "input": 1,
                        "fromUnit": "gal_us",
                        "toUnit": "L",
                        "result": 3.785411784
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "gal-uk-to-l",
                    "input": {
                        "value": 1,
                        "fromUnit": "gal_uk",
                        "toUnit": "L"
                    },
                    "expected": {
                        "converterId": "volume",
                        "input": 1,
                        "fromUnit": "gal_uk",
                        "toUnit": "L",
                        "result": 4.54609
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "ft3-to-l",
                    "input": {
                        "value": 1,
                        "fromUnit": "ft3",
                        "toUnit": "L"
                    },
                    "expected": {
                        "converterId": "volume",
                        "input": 1,
                        "fromUnit": "ft3",
                        "toUnit": "L",
                        "result": 28.316846592
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "in3-to-l",
                    "input": {
                        "value": 1,
                        "fromUnit": "in3",
                        "toUnit": "L"
                    },
                    "expected": {
                        "converterId": "volume",
                        "input": 1,
                        "fromUnit": "in3",
                        "toUnit": "L",
                        "result": 0.016387064
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "varil-to-l",
                    "input": {
                        "value": 1,
                        "fromUnit": "varil",
                        "toUnit": "L"
                    },
                    "expected": {
                        "converterId": "volume",
                        "input": 1,
                        "fromUnit": "varil",
                        "toUnit": "L",
                        "result": 158.987294928
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "bardak-to-l",
                    "input": {
                        "value": 1,
                        "fromUnit": "bardak",
                        "toUnit": "L"
                    },
                    "expected": {
                        "converterId": "volume",
                        "input": 1,
                        "fromUnit": "bardak",
                        "toUnit": "L",
                        "result": 0.25
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "yk-to-l",
                    "input": {
                        "value": 1,
                        "fromUnit": "yk",
                        "toUnit": "L"
                    },
                    "expected": {
                        "converterId": "volume",
                        "input": 1,
                        "fromUnit": "yk",
                        "toUnit": "L",
                        "result": 0.015
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "tk-to-l",
                    "input": {
                        "value": 1,
                        "fromUnit": "tk",
                        "toUnit": "L"
                    },
                    "expected": {
                        "converterId": "volume",
                        "input": 1,
                        "fromUnit": "tk",
                        "toUnit": "L",
                        "result": 0.005
                    },
                    "tolerance": 1e-10
                }
            ]
        },
        "lastReviewed": "2026-08-20"
    },
    {
        "toolId": "unit-speed",
        "slug": "hiz-hesaplama",
        "path": "/birim-cevirici/hiz-hesaplama",
        "category": {
            "id": "birim-cevirici",
            "label": "Birim Çevirici"
        },
        "classification": "timeless",
        "engineId": "unit-converter-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-08-20",
        "effectiveTo": null,
        "minAppVersion": "0.1.0",
        "updateCadence": {
            "mode": "method-change-only",
            "days": null,
            "reviewAfter": null,
            "specialMonths": [],
            "note": "Yalnız uluslararası birim tanımı veya uygulanan dönüşüm yöntemi değişirse yeniden incelenir."
        },
        "sources": [
            {
                "sourceId": "nist-si-conversion-factors",
                "sourceGroupId": null,
                "title": "NIST Guide to the SI — Appendix B, Conversion Factors",
                "publisher": "National Institute of Standards and Technology",
                "url": "https://www.nist.gov/pml/special-publication-811/nist-guide-si-appendix-b-conversion-factors",
                "kind": "official-government",
                "reviewedOn": "2026-08-20",
                "watchFields": [
                    "SI ve SI dışı birim dönüşüm sabitleri"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "value",
                    "type": "number",
                    "label": "Değer",
                    "required": true,
                    "defaultValue": null,
                    "placeholder": "Çevrilecek değeri girin",
                    "unit": null,
                    "minimum": 0,
                    "maximum": 1000000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": null
                },
                {
                    "id": "fromUnit",
                    "type": "select",
                    "label": "Kaynak birim",
                    "required": true,
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "km_sa",
                            "label": "Kilometre/saat (km/sa)"
                        },
                        {
                            "value": "m_s",
                            "label": "Metre/saniye (m/s)"
                        },
                        {
                            "value": "cm_s",
                            "label": "Santimetre/saniye (cm/s)"
                        },
                        {
                            "value": "mil_sa",
                            "label": "Mil/saat (mph)"
                        },
                        {
                            "value": "knot",
                            "label": "Knot (kn)"
                        },
                        {
                            "value": "ft_s",
                            "label": "Fit/saniye (ft/s)"
                        },
                        {
                            "value": "mach",
                            "label": "Mach (15 °C referansı) (M)"
                        },
                        {
                            "value": "km_dk",
                            "label": "Kilometre/dakika (km/dk)"
                        }
                    ],
                    "visibleWhen": null
                },
                {
                    "id": "toUnit",
                    "type": "select",
                    "label": "Hedef birim",
                    "required": true,
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "km_sa",
                            "label": "Kilometre/saat (km/sa)"
                        },
                        {
                            "value": "m_s",
                            "label": "Metre/saniye (m/s)"
                        },
                        {
                            "value": "cm_s",
                            "label": "Santimetre/saniye (cm/s)"
                        },
                        {
                            "value": "mil_sa",
                            "label": "Mil/saat (mph)"
                        },
                        {
                            "value": "knot",
                            "label": "Knot (kn)"
                        },
                        {
                            "value": "ft_s",
                            "label": "Fit/saniye (ft/s)"
                        },
                        {
                            "value": "mach",
                            "label": "Mach (15 °C referansı) (M)"
                        },
                        {
                            "value": "km_dk",
                            "label": "Kilometre/dakika (km/dk)"
                        }
                    ],
                    "visibleWhen": null
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "converterId",
                    "type": "text",
                    "label": "Dönüştürücü",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "input",
                    "type": "number",
                    "label": "Girdi",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "fromUnit",
                    "type": "text",
                    "label": "Kaynak birim",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "toUnit",
                    "type": "text",
                    "label": "Hedef birim",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "result",
                    "type": "number",
                    "label": "Sonuç",
                    "unit": null,
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "conversions",
                    "type": "number-map",
                    "label": "Tüm dönüşümler",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                }
            ]
        },
        "content": {
            "title": "Hız Birimi Dönüştürme",
            "shortTitle": "Hız Dönüştürme",
            "summary": "Kilometre/saat, metre/saniye, mil/saat, knot ve diğer yaygın hız birimleri arasında dönüşüm yapar.",
            "method": "Her değer kesin uzunluk ve zaman sabitleriyle metre/saniye bazına, ardından hedef birime çevrilir.",
            "limitations": [
                "Mach değeri 15 °C ve deniz seviyesindeki 340,29 m/s referansını kullanır; gerçek ses hızı koşullara göre değişir."
            ],
            "faqs": [
                {
                    "question": "Bu dönüştürücü internet olmadan çalışır mı?",
                    "answer": "Evet. Onaylı sabitler uygulamayla birlikte gelir ve hesaplama tamamen cihazda yapılır."
                }
            ]
        },
        "config": {
            "kind": "unit-converter-v1",
            "converterId": "speed",
            "strategy": "linear",
            "allowNegative": false,
            "maxAbsoluteInput": 1000000000000,
            "units": [
                {
                    "id": "km_sa",
                    "label": "Kilometre/saat",
                    "symbol": "km/sa",
                    "factor": 0.2777777777777778
                },
                {
                    "id": "m_s",
                    "label": "Metre/saniye",
                    "symbol": "m/s",
                    "factor": 1
                },
                {
                    "id": "cm_s",
                    "label": "Santimetre/saniye",
                    "symbol": "cm/s",
                    "factor": 0.01
                },
                {
                    "id": "mil_sa",
                    "label": "Mil/saat",
                    "symbol": "mph",
                    "factor": 0.44704
                },
                {
                    "id": "knot",
                    "label": "Knot",
                    "symbol": "kn",
                    "factor": 0.5144444444444445
                },
                {
                    "id": "ft_s",
                    "label": "Fit/saniye",
                    "symbol": "ft/s",
                    "factor": 0.3048
                },
                {
                    "id": "mach",
                    "label": "Mach (15 °C referansı)",
                    "symbol": "M",
                    "factor": 340.29
                },
                {
                    "id": "km_dk",
                    "label": "Kilometre/dakika",
                    "symbol": "km/dk",
                    "factor": 16.666666666666668
                }
            ]
        },
        "testVectors": {
            "kind": "unit-converter-oracle-v1",
            "cases": [
                {
                    "caseId": "km-sa-to-m-s",
                    "input": {
                        "value": 1,
                        "fromUnit": "km_sa",
                        "toUnit": "m_s"
                    },
                    "expected": {
                        "converterId": "speed",
                        "input": 1,
                        "fromUnit": "km_sa",
                        "toUnit": "m_s",
                        "result": 0.2777777777777778
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "m-s-to-m-s",
                    "input": {
                        "value": 1,
                        "fromUnit": "m_s",
                        "toUnit": "m_s"
                    },
                    "expected": {
                        "converterId": "speed",
                        "input": 1,
                        "fromUnit": "m_s",
                        "toUnit": "m_s",
                        "result": 1
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "cm-s-to-m-s",
                    "input": {
                        "value": 1,
                        "fromUnit": "cm_s",
                        "toUnit": "m_s"
                    },
                    "expected": {
                        "converterId": "speed",
                        "input": 1,
                        "fromUnit": "cm_s",
                        "toUnit": "m_s",
                        "result": 0.01
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "mil-sa-to-m-s",
                    "input": {
                        "value": 1,
                        "fromUnit": "mil_sa",
                        "toUnit": "m_s"
                    },
                    "expected": {
                        "converterId": "speed",
                        "input": 1,
                        "fromUnit": "mil_sa",
                        "toUnit": "m_s",
                        "result": 0.44704
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "knot-to-m-s",
                    "input": {
                        "value": 1,
                        "fromUnit": "knot",
                        "toUnit": "m_s"
                    },
                    "expected": {
                        "converterId": "speed",
                        "input": 1,
                        "fromUnit": "knot",
                        "toUnit": "m_s",
                        "result": 0.5144444444444445
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "ft-s-to-m-s",
                    "input": {
                        "value": 1,
                        "fromUnit": "ft_s",
                        "toUnit": "m_s"
                    },
                    "expected": {
                        "converterId": "speed",
                        "input": 1,
                        "fromUnit": "ft_s",
                        "toUnit": "m_s",
                        "result": 0.3048
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "mach-to-m-s",
                    "input": {
                        "value": 1,
                        "fromUnit": "mach",
                        "toUnit": "m_s"
                    },
                    "expected": {
                        "converterId": "speed",
                        "input": 1,
                        "fromUnit": "mach",
                        "toUnit": "m_s",
                        "result": 340.29
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "km-dk-to-m-s",
                    "input": {
                        "value": 1,
                        "fromUnit": "km_dk",
                        "toUnit": "m_s"
                    },
                    "expected": {
                        "converterId": "speed",
                        "input": 1,
                        "fromUnit": "km_dk",
                        "toUnit": "m_s",
                        "result": 16.666666666666668
                    },
                    "tolerance": 1e-10
                }
            ]
        },
        "lastReviewed": "2026-08-20"
    },
    {
        "toolId": "unit-temperature",
        "slug": "sicaklik-hesaplama",
        "path": "/birim-cevirici/sicaklik-hesaplama",
        "category": {
            "id": "birim-cevirici",
            "label": "Birim Çevirici"
        },
        "classification": "timeless",
        "engineId": "unit-converter-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-08-20",
        "effectiveTo": null,
        "minAppVersion": "0.1.0",
        "updateCadence": {
            "mode": "method-change-only",
            "days": null,
            "reviewAfter": null,
            "specialMonths": [],
            "note": "Yalnız uluslararası birim tanımı veya uygulanan dönüşüm yöntemi değişirse yeniden incelenir."
        },
        "sources": [
            {
                "sourceId": "bipm-si-brochure",
                "sourceGroupId": null,
                "title": "The International System of Units (SI Brochure)",
                "publisher": "Bureau International des Poids et Mesures",
                "url": "https://www.bipm.org/en/publications/si-brochure",
                "kind": "international-standards-body",
                "reviewedOn": "2026-08-20",
                "watchFields": [
                    "SI temel ve türetilmiş birim tanımları"
                ]
            },
            {
                "sourceId": "nist-si-conversion-factors",
                "sourceGroupId": null,
                "title": "NIST Guide to the SI — Appendix B, Conversion Factors",
                "publisher": "National Institute of Standards and Technology",
                "url": "https://www.nist.gov/pml/special-publication-811/nist-guide-si-appendix-b-conversion-factors",
                "kind": "official-government",
                "reviewedOn": "2026-08-20",
                "watchFields": [
                    "SI ve SI dışı birim dönüşüm sabitleri"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "value",
                    "type": "number",
                    "label": "Değer",
                    "required": true,
                    "defaultValue": null,
                    "placeholder": "Çevrilecek değeri girin",
                    "unit": null,
                    "minimum": -1000000000,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": null
                },
                {
                    "id": "fromUnit",
                    "type": "select",
                    "label": "Kaynak birim",
                    "required": true,
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "C",
                            "label": "Celsius (°C)"
                        },
                        {
                            "value": "F",
                            "label": "Fahrenheit (°F)"
                        },
                        {
                            "value": "K",
                            "label": "Kelvin (K)"
                        }
                    ],
                    "visibleWhen": null
                },
                {
                    "id": "toUnit",
                    "type": "select",
                    "label": "Hedef birim",
                    "required": true,
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "C",
                            "label": "Celsius (°C)"
                        },
                        {
                            "value": "F",
                            "label": "Fahrenheit (°F)"
                        },
                        {
                            "value": "K",
                            "label": "Kelvin (K)"
                        }
                    ],
                    "visibleWhen": null
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "converterId",
                    "type": "text",
                    "label": "Dönüştürücü",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "input",
                    "type": "number",
                    "label": "Girdi",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "fromUnit",
                    "type": "text",
                    "label": "Kaynak birim",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "toUnit",
                    "type": "text",
                    "label": "Hedef birim",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "result",
                    "type": "number",
                    "label": "Sonuç",
                    "unit": null,
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "conversions",
                    "type": "number-map",
                    "label": "Tüm dönüşümler",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                }
            ]
        },
        "content": {
            "title": "Sıcaklık Birimi Dönüştürme",
            "shortTitle": "Sıcaklık Dönüştürme",
            "summary": "Celsius, Fahrenheit ve Kelvin ölçekleri arasında fiziksel sınırları gözeterek dönüşüm yapar.",
            "method": "Celsius, Fahrenheit ve Kelvin arasındaki BIPM/NIST ölçek bağıntıları doğrudan uygulanır.",
            "limitations": [
                "Mutlak sıfırın altındaki değerler reddedilir; ölçüm belirsizliği ve sıcaklık ölçeği kalibrasyonu kapsanmaz."
            ],
            "faqs": [
                {
                    "question": "Bu dönüştürücü internet olmadan çalışır mı?",
                    "answer": "Evet. Onaylı sabitler uygulamayla birlikte gelir ve hesaplama tamamen cihazda yapılır."
                }
            ]
        },
        "config": {
            "kind": "unit-converter-v1",
            "converterId": "temperature",
            "strategy": "temperature",
            "allowNegative": true,
            "maxAbsoluteInput": 1000000000,
            "units": [
                {
                    "id": "C",
                    "label": "Celsius",
                    "symbol": "°C",
                    "factor": 1
                },
                {
                    "id": "F",
                    "label": "Fahrenheit",
                    "symbol": "°F",
                    "factor": 1
                },
                {
                    "id": "K",
                    "label": "Kelvin",
                    "symbol": "K",
                    "factor": 1
                }
            ]
        },
        "testVectors": {
            "kind": "unit-converter-oracle-v1",
            "cases": [
                {
                    "caseId": "c-zero-to-f",
                    "input": {
                        "value": 0,
                        "fromUnit": "C",
                        "toUnit": "F"
                    },
                    "expected": {
                        "converterId": "temperature",
                        "input": 0,
                        "fromUnit": "C",
                        "toUnit": "F",
                        "result": 32
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "c-boiling-to-k",
                    "input": {
                        "value": 100,
                        "fromUnit": "C",
                        "toUnit": "K"
                    },
                    "expected": {
                        "converterId": "temperature",
                        "input": 100,
                        "fromUnit": "C",
                        "toUnit": "K",
                        "result": 373.15
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "f-freezing-to-c",
                    "input": {
                        "value": 32,
                        "fromUnit": "F",
                        "toUnit": "C"
                    },
                    "expected": {
                        "converterId": "temperature",
                        "input": 32,
                        "fromUnit": "F",
                        "toUnit": "C",
                        "result": 0
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "f-boiling-to-k",
                    "input": {
                        "value": 212,
                        "fromUnit": "F",
                        "toUnit": "K"
                    },
                    "expected": {
                        "converterId": "temperature",
                        "input": 212,
                        "fromUnit": "F",
                        "toUnit": "K",
                        "result": 373.15
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "k-freezing-to-c",
                    "input": {
                        "value": 273.15,
                        "fromUnit": "K",
                        "toUnit": "C"
                    },
                    "expected": {
                        "converterId": "temperature",
                        "input": 273.15,
                        "fromUnit": "K",
                        "toUnit": "C",
                        "result": 0
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "k-boiling-to-f",
                    "input": {
                        "value": 373.15,
                        "fromUnit": "K",
                        "toUnit": "F"
                    },
                    "expected": {
                        "converterId": "temperature",
                        "input": 373.15,
                        "fromUnit": "K",
                        "toUnit": "F",
                        "result": 212
                    },
                    "tolerance": 1e-10
                }
            ]
        },
        "lastReviewed": "2026-08-20"
    },
    {
        "toolId": "unit-length",
        "slug": "uzunluk-hesaplama",
        "path": "/birim-cevirici/uzunluk-hesaplama",
        "category": {
            "id": "birim-cevirici",
            "label": "Birim Çevirici"
        },
        "classification": "timeless",
        "engineId": "unit-converter-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-08-20",
        "effectiveTo": null,
        "minAppVersion": "0.1.0",
        "updateCadence": {
            "mode": "method-change-only",
            "days": null,
            "reviewAfter": null,
            "specialMonths": [],
            "note": "Yalnız uluslararası birim tanımı veya uygulanan dönüşüm yöntemi değişirse yeniden incelenir."
        },
        "sources": [
            {
                "sourceId": "bipm-si-brochure",
                "sourceGroupId": null,
                "title": "The International System of Units (SI Brochure)",
                "publisher": "Bureau International des Poids et Mesures",
                "url": "https://www.bipm.org/en/publications/si-brochure",
                "kind": "international-standards-body",
                "reviewedOn": "2026-08-20",
                "watchFields": [
                    "SI temel ve türetilmiş birim tanımları"
                ]
            },
            {
                "sourceId": "nist-si-conversion-factors",
                "sourceGroupId": null,
                "title": "NIST Guide to the SI — Appendix B, Conversion Factors",
                "publisher": "National Institute of Standards and Technology",
                "url": "https://www.nist.gov/pml/special-publication-811/nist-guide-si-appendix-b-conversion-factors",
                "kind": "official-government",
                "reviewedOn": "2026-08-20",
                "watchFields": [
                    "SI ve SI dışı birim dönüşüm sabitleri"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "value",
                    "type": "number",
                    "label": "Değer",
                    "required": true,
                    "defaultValue": null,
                    "placeholder": "Çevrilecek değeri girin",
                    "unit": null,
                    "minimum": 0,
                    "maximum": 1000000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": null
                },
                {
                    "id": "fromUnit",
                    "type": "select",
                    "label": "Kaynak birim",
                    "required": true,
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "km",
                            "label": "Kilometre (km)"
                        },
                        {
                            "value": "m",
                            "label": "Metre (m)"
                        },
                        {
                            "value": "cm",
                            "label": "Santimetre (cm)"
                        },
                        {
                            "value": "mm",
                            "label": "Milimetre (mm)"
                        },
                        {
                            "value": "mil",
                            "label": "Mil (mi)"
                        },
                        {
                            "value": "yard",
                            "label": "Yard (yd)"
                        },
                        {
                            "value": "ft",
                            "label": "Fit (ft)"
                        },
                        {
                            "value": "in",
                            "label": "İnç (in)"
                        },
                        {
                            "value": "dm",
                            "label": "Deniz mili (nmi)"
                        }
                    ],
                    "visibleWhen": null
                },
                {
                    "id": "toUnit",
                    "type": "select",
                    "label": "Hedef birim",
                    "required": true,
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "km",
                            "label": "Kilometre (km)"
                        },
                        {
                            "value": "m",
                            "label": "Metre (m)"
                        },
                        {
                            "value": "cm",
                            "label": "Santimetre (cm)"
                        },
                        {
                            "value": "mm",
                            "label": "Milimetre (mm)"
                        },
                        {
                            "value": "mil",
                            "label": "Mil (mi)"
                        },
                        {
                            "value": "yard",
                            "label": "Yard (yd)"
                        },
                        {
                            "value": "ft",
                            "label": "Fit (ft)"
                        },
                        {
                            "value": "in",
                            "label": "İnç (in)"
                        },
                        {
                            "value": "dm",
                            "label": "Deniz mili (nmi)"
                        }
                    ],
                    "visibleWhen": null
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "converterId",
                    "type": "text",
                    "label": "Dönüştürücü",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "input",
                    "type": "number",
                    "label": "Girdi",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "fromUnit",
                    "type": "text",
                    "label": "Kaynak birim",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "toUnit",
                    "type": "text",
                    "label": "Hedef birim",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "result",
                    "type": "number",
                    "label": "Sonuç",
                    "unit": null,
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "conversions",
                    "type": "number-map",
                    "label": "Tüm dönüşümler",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                }
            ]
        },
        "content": {
            "title": "Uzunluk Birimi Dönüştürme",
            "shortTitle": "Uzunluk Dönüştürme",
            "summary": "Metrik, İngiliz ve denizcilik uzunluk birimleri arasında kesin sabitlerle dönüşüm yapar.",
            "method": "Her değer BIPM SI tanımları ve NIST kesin sabitleriyle metre bazına, ardından hedef birime çevrilir.",
            "limitations": [
                "Sonuçlar tanımlı uluslararası birimleri kapsar; tarihsel veya yerel uzunluk birimleri dahil değildir."
            ],
            "faqs": [
                {
                    "question": "Bu dönüştürücü internet olmadan çalışır mı?",
                    "answer": "Evet. Onaylı sabitler uygulamayla birlikte gelir ve hesaplama tamamen cihazda yapılır."
                }
            ]
        },
        "config": {
            "kind": "unit-converter-v1",
            "converterId": "length",
            "strategy": "linear",
            "allowNegative": false,
            "maxAbsoluteInput": 1000000000000,
            "units": [
                {
                    "id": "km",
                    "label": "Kilometre",
                    "symbol": "km",
                    "factor": 1000
                },
                {
                    "id": "m",
                    "label": "Metre",
                    "symbol": "m",
                    "factor": 1
                },
                {
                    "id": "cm",
                    "label": "Santimetre",
                    "symbol": "cm",
                    "factor": 0.01
                },
                {
                    "id": "mm",
                    "label": "Milimetre",
                    "symbol": "mm",
                    "factor": 0.001
                },
                {
                    "id": "mil",
                    "label": "Mil",
                    "symbol": "mi",
                    "factor": 1609.344
                },
                {
                    "id": "yard",
                    "label": "Yard",
                    "symbol": "yd",
                    "factor": 0.9144
                },
                {
                    "id": "ft",
                    "label": "Fit",
                    "symbol": "ft",
                    "factor": 0.3048
                },
                {
                    "id": "in",
                    "label": "İnç",
                    "symbol": "in",
                    "factor": 0.0254
                },
                {
                    "id": "dm",
                    "label": "Deniz mili",
                    "symbol": "nmi",
                    "factor": 1852
                }
            ]
        },
        "testVectors": {
            "kind": "unit-converter-oracle-v1",
            "cases": [
                {
                    "caseId": "km-to-m",
                    "input": {
                        "value": 1,
                        "fromUnit": "km",
                        "toUnit": "m"
                    },
                    "expected": {
                        "converterId": "length",
                        "input": 1,
                        "fromUnit": "km",
                        "toUnit": "m",
                        "result": 1000
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "m-to-m",
                    "input": {
                        "value": 1,
                        "fromUnit": "m",
                        "toUnit": "m"
                    },
                    "expected": {
                        "converterId": "length",
                        "input": 1,
                        "fromUnit": "m",
                        "toUnit": "m",
                        "result": 1
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "cm-to-m",
                    "input": {
                        "value": 1,
                        "fromUnit": "cm",
                        "toUnit": "m"
                    },
                    "expected": {
                        "converterId": "length",
                        "input": 1,
                        "fromUnit": "cm",
                        "toUnit": "m",
                        "result": 0.01
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "mm-to-m",
                    "input": {
                        "value": 1,
                        "fromUnit": "mm",
                        "toUnit": "m"
                    },
                    "expected": {
                        "converterId": "length",
                        "input": 1,
                        "fromUnit": "mm",
                        "toUnit": "m",
                        "result": 0.001
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "mil-to-m",
                    "input": {
                        "value": 1,
                        "fromUnit": "mil",
                        "toUnit": "m"
                    },
                    "expected": {
                        "converterId": "length",
                        "input": 1,
                        "fromUnit": "mil",
                        "toUnit": "m",
                        "result": 1609.344
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "yard-to-m",
                    "input": {
                        "value": 1,
                        "fromUnit": "yard",
                        "toUnit": "m"
                    },
                    "expected": {
                        "converterId": "length",
                        "input": 1,
                        "fromUnit": "yard",
                        "toUnit": "m",
                        "result": 0.9144
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "ft-to-m",
                    "input": {
                        "value": 1,
                        "fromUnit": "ft",
                        "toUnit": "m"
                    },
                    "expected": {
                        "converterId": "length",
                        "input": 1,
                        "fromUnit": "ft",
                        "toUnit": "m",
                        "result": 0.3048
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "in-to-m",
                    "input": {
                        "value": 1,
                        "fromUnit": "in",
                        "toUnit": "m"
                    },
                    "expected": {
                        "converterId": "length",
                        "input": 1,
                        "fromUnit": "in",
                        "toUnit": "m",
                        "result": 0.0254
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "dm-to-m",
                    "input": {
                        "value": 1,
                        "fromUnit": "dm",
                        "toUnit": "m"
                    },
                    "expected": {
                        "converterId": "length",
                        "input": 1,
                        "fromUnit": "dm",
                        "toUnit": "m",
                        "result": 1852
                    },
                    "tolerance": 1e-10
                }
            ]
        },
        "lastReviewed": "2026-08-20"
    },
    {
        "toolId": "unit-data-size",
        "slug": "veri-boyutu-hesaplama",
        "path": "/birim-cevirici/veri-boyutu-hesaplama",
        "category": {
            "id": "birim-cevirici",
            "label": "Birim Çevirici"
        },
        "classification": "timeless",
        "engineId": "unit-converter-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-08-20",
        "effectiveTo": null,
        "minAppVersion": "0.1.0",
        "updateCadence": {
            "mode": "method-change-only",
            "days": null,
            "reviewAfter": null,
            "specialMonths": [],
            "note": "Yalnız uluslararası birim tanımı veya uygulanan dönüşüm yöntemi değişirse yeniden incelenir."
        },
        "sources": [
            {
                "sourceId": "nist-si-conversion-factors",
                "sourceGroupId": null,
                "title": "NIST Guide to the SI — Appendix B, Conversion Factors",
                "publisher": "National Institute of Standards and Technology",
                "url": "https://www.nist.gov/pml/special-publication-811/nist-guide-si-appendix-b-conversion-factors",
                "kind": "official-government",
                "reviewedOn": "2026-08-20",
                "watchFields": [
                    "SI ve SI dışı birim dönüşüm sabitleri"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "value",
                    "type": "number",
                    "label": "Değer",
                    "required": true,
                    "defaultValue": null,
                    "placeholder": "Çevrilecek değeri girin",
                    "unit": null,
                    "minimum": 0,
                    "maximum": 1000000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": null
                },
                {
                    "id": "fromUnit",
                    "type": "select",
                    "label": "Kaynak birim",
                    "required": true,
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "B",
                            "label": "Byte (B)"
                        },
                        {
                            "value": "KB",
                            "label": "Kilobyte (SI) (kB)"
                        },
                        {
                            "value": "MB",
                            "label": "Megabyte (SI) (MB)"
                        },
                        {
                            "value": "GB",
                            "label": "Gigabyte (SI) (GB)"
                        },
                        {
                            "value": "TB",
                            "label": "Terabyte (SI) (TB)"
                        },
                        {
                            "value": "PB",
                            "label": "Petabyte (SI) (PB)"
                        },
                        {
                            "value": "KiB",
                            "label": "Kibibyte (IEC) (KiB)"
                        },
                        {
                            "value": "MiB",
                            "label": "Mebibyte (IEC) (MiB)"
                        },
                        {
                            "value": "GiB",
                            "label": "Gibibyte (IEC) (GiB)"
                        },
                        {
                            "value": "TiB",
                            "label": "Tebibyte (IEC) (TiB)"
                        },
                        {
                            "value": "PiB",
                            "label": "Pebibyte (IEC) (PiB)"
                        },
                        {
                            "value": "bit",
                            "label": "Bit (bit)"
                        },
                        {
                            "value": "Kbit",
                            "label": "Kilobit (Kbit)"
                        },
                        {
                            "value": "Mbit",
                            "label": "Megabit (Mbit)"
                        },
                        {
                            "value": "Gbit",
                            "label": "Gigabit (Gbit)"
                        }
                    ],
                    "visibleWhen": null
                },
                {
                    "id": "toUnit",
                    "type": "select",
                    "label": "Hedef birim",
                    "required": true,
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "B",
                            "label": "Byte (B)"
                        },
                        {
                            "value": "KB",
                            "label": "Kilobyte (SI) (kB)"
                        },
                        {
                            "value": "MB",
                            "label": "Megabyte (SI) (MB)"
                        },
                        {
                            "value": "GB",
                            "label": "Gigabyte (SI) (GB)"
                        },
                        {
                            "value": "TB",
                            "label": "Terabyte (SI) (TB)"
                        },
                        {
                            "value": "PB",
                            "label": "Petabyte (SI) (PB)"
                        },
                        {
                            "value": "KiB",
                            "label": "Kibibyte (IEC) (KiB)"
                        },
                        {
                            "value": "MiB",
                            "label": "Mebibyte (IEC) (MiB)"
                        },
                        {
                            "value": "GiB",
                            "label": "Gibibyte (IEC) (GiB)"
                        },
                        {
                            "value": "TiB",
                            "label": "Tebibyte (IEC) (TiB)"
                        },
                        {
                            "value": "PiB",
                            "label": "Pebibyte (IEC) (PiB)"
                        },
                        {
                            "value": "bit",
                            "label": "Bit (bit)"
                        },
                        {
                            "value": "Kbit",
                            "label": "Kilobit (Kbit)"
                        },
                        {
                            "value": "Mbit",
                            "label": "Megabit (Mbit)"
                        },
                        {
                            "value": "Gbit",
                            "label": "Gigabit (Gbit)"
                        }
                    ],
                    "visibleWhen": null
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "converterId",
                    "type": "text",
                    "label": "Dönüştürücü",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "input",
                    "type": "number",
                    "label": "Girdi",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "fromUnit",
                    "type": "text",
                    "label": "Kaynak birim",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "toUnit",
                    "type": "text",
                    "label": "Hedef birim",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "result",
                    "type": "number",
                    "label": "Sonuç",
                    "unit": null,
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "conversions",
                    "type": "number-map",
                    "label": "Tüm dönüşümler",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                }
            ]
        },
        "content": {
            "title": "Veri Boyutu Birimi Dönüştürme",
            "shortTitle": "Veri Boyutu Dönüştürme",
            "summary": "Bit, byte, SI ondalık ve IEC ikili veri boyutu birimleri arasında dönüşüm yapar.",
            "method": "SI önekleri 1000, IEC ikili önekleri 1024 tabanıyla byte bazına, ardından hedef birime çevrilir.",
            "limitations": [
                "Depolama üreticisi gösterimiyle işletim sistemi gösterimi farklı olabilir; sonuç birim etiketiyle birlikte okunmalıdır."
            ],
            "faqs": [
                {
                    "question": "Bu dönüştürücü internet olmadan çalışır mı?",
                    "answer": "Evet. Onaylı sabitler uygulamayla birlikte gelir ve hesaplama tamamen cihazda yapılır."
                }
            ]
        },
        "config": {
            "kind": "unit-converter-v1",
            "converterId": "data-size",
            "strategy": "linear",
            "allowNegative": false,
            "maxAbsoluteInput": 1000000000000,
            "units": [
                {
                    "id": "B",
                    "label": "Byte",
                    "symbol": "B",
                    "factor": 1
                },
                {
                    "id": "KB",
                    "label": "Kilobyte (SI)",
                    "symbol": "kB",
                    "factor": 1000
                },
                {
                    "id": "MB",
                    "label": "Megabyte (SI)",
                    "symbol": "MB",
                    "factor": 1000000
                },
                {
                    "id": "GB",
                    "label": "Gigabyte (SI)",
                    "symbol": "GB",
                    "factor": 1000000000
                },
                {
                    "id": "TB",
                    "label": "Terabyte (SI)",
                    "symbol": "TB",
                    "factor": 1000000000000
                },
                {
                    "id": "PB",
                    "label": "Petabyte (SI)",
                    "symbol": "PB",
                    "factor": 1000000000000000
                },
                {
                    "id": "KiB",
                    "label": "Kibibyte (IEC)",
                    "symbol": "KiB",
                    "factor": 1024
                },
                {
                    "id": "MiB",
                    "label": "Mebibyte (IEC)",
                    "symbol": "MiB",
                    "factor": 1048576
                },
                {
                    "id": "GiB",
                    "label": "Gibibyte (IEC)",
                    "symbol": "GiB",
                    "factor": 1073741824
                },
                {
                    "id": "TiB",
                    "label": "Tebibyte (IEC)",
                    "symbol": "TiB",
                    "factor": 1099511627776
                },
                {
                    "id": "PiB",
                    "label": "Pebibyte (IEC)",
                    "symbol": "PiB",
                    "factor": 1125899906842624
                },
                {
                    "id": "bit",
                    "label": "Bit",
                    "symbol": "bit",
                    "factor": 0.125
                },
                {
                    "id": "Kbit",
                    "label": "Kilobit",
                    "symbol": "Kbit",
                    "factor": 125
                },
                {
                    "id": "Mbit",
                    "label": "Megabit",
                    "symbol": "Mbit",
                    "factor": 125000
                },
                {
                    "id": "Gbit",
                    "label": "Gigabit",
                    "symbol": "Gbit",
                    "factor": 125000000
                }
            ]
        },
        "testVectors": {
            "kind": "unit-converter-oracle-v1",
            "cases": [
                {
                    "caseId": "b-to-b",
                    "input": {
                        "value": 1,
                        "fromUnit": "B",
                        "toUnit": "B"
                    },
                    "expected": {
                        "converterId": "data-size",
                        "input": 1,
                        "fromUnit": "B",
                        "toUnit": "B",
                        "result": 1
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "kb-to-b",
                    "input": {
                        "value": 1,
                        "fromUnit": "KB",
                        "toUnit": "B"
                    },
                    "expected": {
                        "converterId": "data-size",
                        "input": 1,
                        "fromUnit": "KB",
                        "toUnit": "B",
                        "result": 1000
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "mb-to-b",
                    "input": {
                        "value": 1,
                        "fromUnit": "MB",
                        "toUnit": "B"
                    },
                    "expected": {
                        "converterId": "data-size",
                        "input": 1,
                        "fromUnit": "MB",
                        "toUnit": "B",
                        "result": 1000000
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "gb-to-b",
                    "input": {
                        "value": 1,
                        "fromUnit": "GB",
                        "toUnit": "B"
                    },
                    "expected": {
                        "converterId": "data-size",
                        "input": 1,
                        "fromUnit": "GB",
                        "toUnit": "B",
                        "result": 1000000000
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "tb-to-b",
                    "input": {
                        "value": 1,
                        "fromUnit": "TB",
                        "toUnit": "B"
                    },
                    "expected": {
                        "converterId": "data-size",
                        "input": 1,
                        "fromUnit": "TB",
                        "toUnit": "B",
                        "result": 1000000000000
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "pb-to-b",
                    "input": {
                        "value": 1,
                        "fromUnit": "PB",
                        "toUnit": "B"
                    },
                    "expected": {
                        "converterId": "data-size",
                        "input": 1,
                        "fromUnit": "PB",
                        "toUnit": "B",
                        "result": 1000000000000000
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "kib-to-b",
                    "input": {
                        "value": 1,
                        "fromUnit": "KiB",
                        "toUnit": "B"
                    },
                    "expected": {
                        "converterId": "data-size",
                        "input": 1,
                        "fromUnit": "KiB",
                        "toUnit": "B",
                        "result": 1024
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "mib-to-b",
                    "input": {
                        "value": 1,
                        "fromUnit": "MiB",
                        "toUnit": "B"
                    },
                    "expected": {
                        "converterId": "data-size",
                        "input": 1,
                        "fromUnit": "MiB",
                        "toUnit": "B",
                        "result": 1048576
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "gib-to-b",
                    "input": {
                        "value": 1,
                        "fromUnit": "GiB",
                        "toUnit": "B"
                    },
                    "expected": {
                        "converterId": "data-size",
                        "input": 1,
                        "fromUnit": "GiB",
                        "toUnit": "B",
                        "result": 1073741824
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "tib-to-b",
                    "input": {
                        "value": 1,
                        "fromUnit": "TiB",
                        "toUnit": "B"
                    },
                    "expected": {
                        "converterId": "data-size",
                        "input": 1,
                        "fromUnit": "TiB",
                        "toUnit": "B",
                        "result": 1099511627776
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "pib-to-b",
                    "input": {
                        "value": 1,
                        "fromUnit": "PiB",
                        "toUnit": "B"
                    },
                    "expected": {
                        "converterId": "data-size",
                        "input": 1,
                        "fromUnit": "PiB",
                        "toUnit": "B",
                        "result": 1125899906842624
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "bit-to-b",
                    "input": {
                        "value": 1,
                        "fromUnit": "bit",
                        "toUnit": "B"
                    },
                    "expected": {
                        "converterId": "data-size",
                        "input": 1,
                        "fromUnit": "bit",
                        "toUnit": "B",
                        "result": 0.125
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "kbit-to-b",
                    "input": {
                        "value": 1,
                        "fromUnit": "Kbit",
                        "toUnit": "B"
                    },
                    "expected": {
                        "converterId": "data-size",
                        "input": 1,
                        "fromUnit": "Kbit",
                        "toUnit": "B",
                        "result": 125
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "mbit-to-b",
                    "input": {
                        "value": 1,
                        "fromUnit": "Mbit",
                        "toUnit": "B"
                    },
                    "expected": {
                        "converterId": "data-size",
                        "input": 1,
                        "fromUnit": "Mbit",
                        "toUnit": "B",
                        "result": 125000
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "gbit-to-b",
                    "input": {
                        "value": 1,
                        "fromUnit": "Gbit",
                        "toUnit": "B"
                    },
                    "expected": {
                        "converterId": "data-size",
                        "input": 1,
                        "fromUnit": "Gbit",
                        "toUnit": "B",
                        "result": 125000000
                    },
                    "tolerance": 1e-10
                }
            ]
        },
        "lastReviewed": "2026-08-20"
    },
    {
        "toolId": "unit-time",
        "slug": "zaman-hesaplama",
        "path": "/birim-cevirici/zaman-hesaplama",
        "category": {
            "id": "birim-cevirici",
            "label": "Birim Çevirici"
        },
        "classification": "timeless",
        "engineId": "unit-converter-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-08-20",
        "effectiveTo": null,
        "minAppVersion": "0.1.0",
        "updateCadence": {
            "mode": "method-change-only",
            "days": null,
            "reviewAfter": null,
            "specialMonths": [],
            "note": "Yalnız uluslararası birim tanımı veya uygulanan dönüşüm yöntemi değişirse yeniden incelenir."
        },
        "sources": [
            {
                "sourceId": "bipm-si-brochure",
                "sourceGroupId": null,
                "title": "The International System of Units (SI Brochure)",
                "publisher": "Bureau International des Poids et Mesures",
                "url": "https://www.bipm.org/en/publications/si-brochure",
                "kind": "international-standards-body",
                "reviewedOn": "2026-08-20",
                "watchFields": [
                    "SI temel ve türetilmiş birim tanımları"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "value",
                    "type": "number",
                    "label": "Değer",
                    "required": true,
                    "defaultValue": null,
                    "placeholder": "Çevrilecek değeri girin",
                    "unit": null,
                    "minimum": 0,
                    "maximum": 1000000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": null
                },
                {
                    "id": "fromUnit",
                    "type": "select",
                    "label": "Kaynak birim",
                    "required": true,
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "ms",
                            "label": "Milisaniye (ms)"
                        },
                        {
                            "value": "s",
                            "label": "Saniye (s)"
                        },
                        {
                            "value": "dk",
                            "label": "Dakika (dk)"
                        },
                        {
                            "value": "sa",
                            "label": "Saat (sa)"
                        },
                        {
                            "value": "gun",
                            "label": "Gün (gün)"
                        },
                        {
                            "value": "hafta",
                            "label": "Hafta (hf)"
                        },
                        {
                            "value": "ay",
                            "label": "Sabit 30 günlük süre (30 gün)"
                        },
                        {
                            "value": "yil",
                            "label": "Jülyen yılı (365,25 gün)"
                        },
                        {
                            "value": "on_yil",
                            "label": "On Jülyen yılı (10 yıl)"
                        },
                        {
                            "value": "yuzyil",
                            "label": "Yüz Jülyen yılı (100 yıl)"
                        }
                    ],
                    "visibleWhen": null
                },
                {
                    "id": "toUnit",
                    "type": "select",
                    "label": "Hedef birim",
                    "required": true,
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "ms",
                            "label": "Milisaniye (ms)"
                        },
                        {
                            "value": "s",
                            "label": "Saniye (s)"
                        },
                        {
                            "value": "dk",
                            "label": "Dakika (dk)"
                        },
                        {
                            "value": "sa",
                            "label": "Saat (sa)"
                        },
                        {
                            "value": "gun",
                            "label": "Gün (gün)"
                        },
                        {
                            "value": "hafta",
                            "label": "Hafta (hf)"
                        },
                        {
                            "value": "ay",
                            "label": "Sabit 30 günlük süre (30 gün)"
                        },
                        {
                            "value": "yil",
                            "label": "Jülyen yılı (365,25 gün)"
                        },
                        {
                            "value": "on_yil",
                            "label": "On Jülyen yılı (10 yıl)"
                        },
                        {
                            "value": "yuzyil",
                            "label": "Yüz Jülyen yılı (100 yıl)"
                        }
                    ],
                    "visibleWhen": null
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "converterId",
                    "type": "text",
                    "label": "Dönüştürücü",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "input",
                    "type": "number",
                    "label": "Girdi",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "fromUnit",
                    "type": "text",
                    "label": "Kaynak birim",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "toUnit",
                    "type": "text",
                    "label": "Hedef birim",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "result",
                    "type": "number",
                    "label": "Sonuç",
                    "unit": null,
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "conversions",
                    "type": "number-map",
                    "label": "Tüm dönüşümler",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                }
            ]
        },
        "content": {
            "title": "Zaman Birimi Dönüştürme",
            "shortTitle": "Zaman Dönüştürme",
            "summary": "Milisaniyeden yüzyıla kadar sabit süre birimleri arasında çevrimdışı dönüşüm yapar.",
            "method": "Her değer SI saniye bazına çevrilir; ay 30 gün, yıl ise BIPM kullanımındaki 365,25 günlük Jülyen yılıdır.",
            "limitations": [
                "Takvim ayı değişken değildir: ay 30 gün, yıl 365,25 gün kabul edilir; artık saniyeler ayrıca modellenmez."
            ],
            "faqs": [
                {
                    "question": "Bu dönüştürücü internet olmadan çalışır mı?",
                    "answer": "Evet. Onaylı sabitler uygulamayla birlikte gelir ve hesaplama tamamen cihazda yapılır."
                }
            ]
        },
        "config": {
            "kind": "unit-converter-v1",
            "converterId": "time",
            "strategy": "linear",
            "allowNegative": false,
            "maxAbsoluteInput": 1000000000000,
            "units": [
                {
                    "id": "ms",
                    "label": "Milisaniye",
                    "symbol": "ms",
                    "factor": 0.001
                },
                {
                    "id": "s",
                    "label": "Saniye",
                    "symbol": "s",
                    "factor": 1
                },
                {
                    "id": "dk",
                    "label": "Dakika",
                    "symbol": "dk",
                    "factor": 60
                },
                {
                    "id": "sa",
                    "label": "Saat",
                    "symbol": "sa",
                    "factor": 3600
                },
                {
                    "id": "gun",
                    "label": "Gün",
                    "symbol": "gün",
                    "factor": 86400
                },
                {
                    "id": "hafta",
                    "label": "Hafta",
                    "symbol": "hf",
                    "factor": 604800
                },
                {
                    "id": "ay",
                    "label": "Sabit 30 günlük süre",
                    "symbol": "30 gün",
                    "factor": 2592000
                },
                {
                    "id": "yil",
                    "label": "Jülyen yılı",
                    "symbol": "365,25 gün",
                    "factor": 31557600
                },
                {
                    "id": "on_yil",
                    "label": "On Jülyen yılı",
                    "symbol": "10 yıl",
                    "factor": 315576000
                },
                {
                    "id": "yuzyil",
                    "label": "Yüz Jülyen yılı",
                    "symbol": "100 yıl",
                    "factor": 3155760000
                }
            ]
        },
        "testVectors": {
            "kind": "unit-converter-oracle-v1",
            "cases": [
                {
                    "caseId": "ms-to-s",
                    "input": {
                        "value": 1,
                        "fromUnit": "ms",
                        "toUnit": "s"
                    },
                    "expected": {
                        "converterId": "time",
                        "input": 1,
                        "fromUnit": "ms",
                        "toUnit": "s",
                        "result": 0.001
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "s-to-s",
                    "input": {
                        "value": 1,
                        "fromUnit": "s",
                        "toUnit": "s"
                    },
                    "expected": {
                        "converterId": "time",
                        "input": 1,
                        "fromUnit": "s",
                        "toUnit": "s",
                        "result": 1
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "dk-to-s",
                    "input": {
                        "value": 1,
                        "fromUnit": "dk",
                        "toUnit": "s"
                    },
                    "expected": {
                        "converterId": "time",
                        "input": 1,
                        "fromUnit": "dk",
                        "toUnit": "s",
                        "result": 60
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "sa-to-s",
                    "input": {
                        "value": 1,
                        "fromUnit": "sa",
                        "toUnit": "s"
                    },
                    "expected": {
                        "converterId": "time",
                        "input": 1,
                        "fromUnit": "sa",
                        "toUnit": "s",
                        "result": 3600
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "gun-to-s",
                    "input": {
                        "value": 1,
                        "fromUnit": "gun",
                        "toUnit": "s"
                    },
                    "expected": {
                        "converterId": "time",
                        "input": 1,
                        "fromUnit": "gun",
                        "toUnit": "s",
                        "result": 86400
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "hafta-to-s",
                    "input": {
                        "value": 1,
                        "fromUnit": "hafta",
                        "toUnit": "s"
                    },
                    "expected": {
                        "converterId": "time",
                        "input": 1,
                        "fromUnit": "hafta",
                        "toUnit": "s",
                        "result": 604800
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "ay-to-s",
                    "input": {
                        "value": 1,
                        "fromUnit": "ay",
                        "toUnit": "s"
                    },
                    "expected": {
                        "converterId": "time",
                        "input": 1,
                        "fromUnit": "ay",
                        "toUnit": "s",
                        "result": 2592000
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "yil-to-s",
                    "input": {
                        "value": 1,
                        "fromUnit": "yil",
                        "toUnit": "s"
                    },
                    "expected": {
                        "converterId": "time",
                        "input": 1,
                        "fromUnit": "yil",
                        "toUnit": "s",
                        "result": 31557600
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "on-yil-to-s",
                    "input": {
                        "value": 1,
                        "fromUnit": "on_yil",
                        "toUnit": "s"
                    },
                    "expected": {
                        "converterId": "time",
                        "input": 1,
                        "fromUnit": "on_yil",
                        "toUnit": "s",
                        "result": 315576000
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "yuzyil-to-s",
                    "input": {
                        "value": 1,
                        "fromUnit": "yuzyil",
                        "toUnit": "s"
                    },
                    "expected": {
                        "converterId": "time",
                        "input": 1,
                        "fromUnit": "yuzyil",
                        "toUnit": "s",
                        "result": 3155760000
                    },
                    "tolerance": 1e-10
                }
            ]
        },
        "lastReviewed": "2026-08-20"
    },
    {
        "toolId": "geometry",
        "slug": "alan-cevre-hacim-hesaplama",
        "path": "/matematik/alan-cevre-hacim-hesaplama",
        "category": {
            "id": "matematik",
            "label": "Matematik"
        },
        "classification": "timeless",
        "engineId": "geometry-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-08-20",
        "effectiveTo": null,
        "minAppVersion": "0.1.0",
        "updateCadence": {
            "mode": "method-change-only",
            "days": null,
            "reviewAfter": null,
            "specialMonths": [],
            "note": "Zamansız matematik aracıdır; yalnız matematiksel yöntem veya ürün kapsamı değişirse yeniden incelenir."
        },
        "sources": [
            {
                "sourceId": "openstax-geometry",
                "sourceGroupId": null,
                "title": "Prealgebra 2e — Geometry",
                "publisher": "OpenStax",
                "url": "https://openstax.org/books/prealgebra-2e/pages/9-introduction",
                "kind": "authoritative-education",
                "reviewedOn": "2026-08-20",
                "watchFields": [
                    "Öklid geometrisi alan, çevre, hacim ve yüzey alanı bağıntıları"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": "square",
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "square",
                            "label": "Kare"
                        },
                        {
                            "value": "rectangle",
                            "label": "Dikdörtgen"
                        },
                        {
                            "value": "triangle",
                            "label": "Üçgen"
                        },
                        {
                            "value": "circle",
                            "label": "Daire"
                        },
                        {
                            "value": "trapezoid",
                            "label": "Yamuk"
                        },
                        {
                            "value": "parallelogram",
                            "label": "Paralelkenar"
                        },
                        {
                            "value": "cube",
                            "label": "Küp"
                        },
                        {
                            "value": "sphere",
                            "label": "Küre"
                        },
                        {
                            "value": "cylinder",
                            "label": "Silindir"
                        },
                        {
                            "value": "cone",
                            "label": "Koni"
                        }
                    ],
                    "visibleWhen": null,
                    "id": "shape",
                    "type": "select",
                    "label": "Şekil",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": "Seçilen şeklin pozitif ölçülerini girin",
                    "unit": null,
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": null,
                    "id": "dimensions",
                    "type": "number-map",
                    "label": "Ölçüler",
                    "required": true
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "unit": null,
                    "nullable": false,
                    "prominent": false,
                    "id": "shape",
                    "type": "text",
                    "label": "Şekil"
                },
                {
                    "unit": null,
                    "nullable": false,
                    "prominent": false,
                    "id": "dimension",
                    "type": "text",
                    "label": "Boyut"
                },
                {
                    "unit": null,
                    "nullable": false,
                    "prominent": false,
                    "id": "primaryMetric",
                    "type": "text",
                    "label": "Ana ölçüm"
                },
                {
                    "unit": null,
                    "nullable": false,
                    "prominent": true,
                    "id": "primaryValue",
                    "type": "number",
                    "label": "Ana sonuç"
                },
                {
                    "unit": null,
                    "nullable": false,
                    "prominent": false,
                    "id": "metrics",
                    "type": "number-map",
                    "label": "Tüm ölçümler"
                }
            ]
        },
        "content": {
            "title": "Alan, Çevre ve Hacim Hesaplama",
            "shortTitle": "Geometri Hesaplama",
            "summary": "On temel iki ve üç boyutlu şeklin alan, çevre, hacim ve yüzey ölçülerini hesaplar.",
            "method": "Seçilen şekle ait derlenmiş Öklid geometrisi bağıntıları pozitif ölçülere uygulanır.",
            "limitations": [
                "Yamuk çevresi yalnız iki yan kenar birlikte verilirse üretilir; ölçüm belirsizliği kapsanmaz."
            ],
            "faqs": [
                {
                    "question": "Geometri Hesaplama hangi yöntemi kullanır?",
                    "answer": "Seçilen şekle ait derlenmiş Öklid geometrisi bağıntıları pozitif ölçülere uygulanır. Sonuç, girilen değerlerin matematiksel anlamı ve belirtilen sınırlar içinde değerlendirilmelidir."
                }
            ]
        },
        "config": {
            "kind": "geometry-static-v1",
            "maxAbsoluteInput": 1000000000,
            "supportedShapes": [
                "square",
                "rectangle",
                "triangle",
                "circle",
                "trapezoid",
                "parallelogram",
                "cube",
                "sphere",
                "cylinder",
                "cone"
            ]
        },
        "testVectors": {
            "kind": "geometry-oracle-v1",
            "cases": [
                {
                    "caseId": "square-four",
                    "input": {
                        "shape": "square",
                        "dimensions": {
                            "a": 4
                        }
                    },
                    "expected": {
                        "shape": "square",
                        "dimension": "2d",
                        "primaryMetric": "area",
                        "primaryValue": 16,
                        "metrics": {
                            "area": 16,
                            "perimeter": 16,
                            "diagonal": 5.656854249492381
                        }
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "rectangle-three-four",
                    "input": {
                        "shape": "rectangle",
                        "dimensions": {
                            "a": 3,
                            "b": 4
                        }
                    },
                    "expected": {
                        "shape": "rectangle",
                        "dimension": "2d",
                        "primaryMetric": "area",
                        "primaryValue": 12,
                        "metrics": {
                            "area": 12,
                            "perimeter": 14,
                            "diagonal": 5
                        }
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "triangle-ten-six",
                    "input": {
                        "shape": "triangle",
                        "dimensions": {
                            "base": 10,
                            "height": 6
                        }
                    },
                    "expected": {
                        "shape": "triangle",
                        "dimension": "2d",
                        "primaryMetric": "area",
                        "primaryValue": 30,
                        "metrics": {
                            "area": 30
                        }
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "circle-radius-two",
                    "input": {
                        "shape": "circle",
                        "dimensions": {
                            "radius": 2
                        }
                    },
                    "expected": {
                        "shape": "circle",
                        "dimension": "2d",
                        "primaryMetric": "area",
                        "primaryValue": 12.566370614359172,
                        "metrics": {
                            "area": 12.566370614359172,
                            "circumference": 12.566370614359172,
                            "diameter": 4
                        }
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "trapezoid-three-five-four",
                    "input": {
                        "shape": "trapezoid",
                        "dimensions": {
                            "a": 3,
                            "b": 5,
                            "height": 4
                        }
                    },
                    "expected": {
                        "shape": "trapezoid",
                        "dimension": "2d",
                        "primaryMetric": "area",
                        "primaryValue": 16,
                        "metrics": {
                            "area": 16
                        }
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "parallelogram-six-four-three",
                    "input": {
                        "shape": "parallelogram",
                        "dimensions": {
                            "base": 6,
                            "side": 4,
                            "height": 3
                        }
                    },
                    "expected": {
                        "shape": "parallelogram",
                        "dimension": "2d",
                        "primaryMetric": "area",
                        "primaryValue": 18,
                        "metrics": {
                            "area": 18,
                            "perimeter": 20
                        }
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "cube-three",
                    "input": {
                        "shape": "cube",
                        "dimensions": {
                            "a": 3
                        }
                    },
                    "expected": {
                        "shape": "cube",
                        "dimension": "3d",
                        "primaryMetric": "volume",
                        "primaryValue": 27,
                        "metrics": {
                            "volume": 27,
                            "surfaceArea": 54
                        }
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "sphere-radius-three",
                    "input": {
                        "shape": "sphere",
                        "dimensions": {
                            "radius": 3
                        }
                    },
                    "expected": {
                        "shape": "sphere",
                        "dimension": "3d",
                        "primaryMetric": "volume",
                        "primaryValue": 113.09733552923254,
                        "metrics": {
                            "volume": 113.09733552923254,
                            "surfaceArea": 113.09733552923255
                        }
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "cylinder-two-five",
                    "input": {
                        "shape": "cylinder",
                        "dimensions": {
                            "radius": 2,
                            "height": 5
                        }
                    },
                    "expected": {
                        "shape": "cylinder",
                        "dimension": "3d",
                        "primaryMetric": "volume",
                        "primaryValue": 62.83185307179586,
                        "metrics": {
                            "volume": 62.83185307179586,
                            "lateralArea": 62.83185307179586,
                            "surfaceArea": 87.96459430051421
                        }
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "cone-three-four",
                    "input": {
                        "shape": "cone",
                        "dimensions": {
                            "radius": 3,
                            "height": 4
                        }
                    },
                    "expected": {
                        "shape": "cone",
                        "dimension": "3d",
                        "primaryMetric": "volume",
                        "primaryValue": 37.69911184307752,
                        "metrics": {
                            "volume": 37.69911184307752,
                            "slantHeight": 5,
                            "lateralArea": 47.12388980384689,
                            "surfaceArea": 75.39822368615503
                        }
                    },
                    "tolerance": 1e-10
                }
            ]
        },
        "lastReviewed": "2026-08-20"
    },
    {
        "toolId": "prime-number",
        "slug": "asal-sayi-hesaplama",
        "path": "/matematik/asal-sayi-hesaplama",
        "category": {
            "id": "matematik",
            "label": "Matematik"
        },
        "classification": "timeless",
        "engineId": "prime-number-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-08-20",
        "effectiveTo": null,
        "minAppVersion": "0.1.0",
        "updateCadence": {
            "mode": "method-change-only",
            "days": null,
            "reviewAfter": null,
            "specialMonths": [],
            "note": "Zamansız matematik aracıdır; yalnız matematiksel yöntem veya ürün kapsamı değişirse yeniden incelenir."
        },
        "sources": [
            {
                "sourceId": "openstax-prime-number",
                "sourceGroupId": null,
                "title": "Prealgebra 2e — Prime Factorization and the Least Common Multiple",
                "publisher": "OpenStax",
                "url": "https://openstax.org/books/prealgebra-2e/pages/2-5-prime-factorization-and-the-least-common-multiple",
                "kind": "authoritative-education",
                "reviewedOn": "2026-08-20",
                "watchFields": [
                    "asal sayı, çarpanlara ayırma ve bölen sayısı tanımları"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": null,
                    "placeholder": "Ör: 360",
                    "unit": null,
                    "minimum": 1,
                    "maximum": 1000000000000000,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "value",
                    "type": "integer-string",
                    "label": "Pozitif tamsayı",
                    "required": true
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "unit": null,
                    "nullable": false,
                    "prominent": false,
                    "id": "value",
                    "type": "integer-string",
                    "label": "Sayı"
                },
                {
                    "unit": null,
                    "nullable": false,
                    "prominent": true,
                    "id": "classification",
                    "type": "text",
                    "label": "Sınıflandırma"
                },
                {
                    "unit": null,
                    "nullable": false,
                    "prominent": false,
                    "id": "factors",
                    "type": "object-list",
                    "label": "Asal çarpanlar"
                },
                {
                    "unit": null,
                    "nullable": false,
                    "prominent": false,
                    "id": "divisorCount",
                    "type": "integer-string",
                    "label": "Pozitif bölen sayısı"
                },
                {
                    "unit": null,
                    "nullable": true,
                    "prominent": false,
                    "id": "previousPrime",
                    "type": "integer-string",
                    "label": "Önceki asal"
                },
                {
                    "unit": null,
                    "nullable": false,
                    "prominent": false,
                    "id": "nextPrime",
                    "type": "integer-string",
                    "label": "Sonraki asal"
                }
            ]
        },
        "content": {
            "title": "Asal Sayı ve Asal Çarpan Hesaplama",
            "shortTitle": "Asal Sayı Hesaplama",
            "summary": "Pozitif bir tamsayıyı asal, bileşik veya birim olarak sınıflandırır ve asal çarpanlarını verir.",
            "method": "Deterministik tamsayı asallık testi ve tam çarpanlara ayırma algoritması uygulanır.",
            "limitations": [
                "Araç 1 ile 10^15 arasındaki pozitif tamsayılarla sınırlıdır."
            ],
            "faqs": [
                {
                    "question": "Asal Sayı Hesaplama hangi yöntemi kullanır?",
                    "answer": "Deterministik tamsayı asallık testi ve tam çarpanlara ayırma algoritması uygulanır. Sonuç, girilen değerlerin matematiksel anlamı ve belirtilen sınırlar içinde değerlendirilmelidir."
                }
            ]
        },
        "config": {
            "kind": "prime-number-static-v1",
            "maxAbsoluteInput": 1000000000000000,
            "maxInteger": "1000000000000000"
        },
        "testVectors": {
            "kind": "prime-number-oracle-v1",
            "cases": [
                {
                    "caseId": "unit-one",
                    "input": {
                        "value": "1"
                    },
                    "expected": {
                        "value": "1",
                        "classification": "unit",
                        "factors": [],
                        "divisorCount": "1",
                        "previousPrime": null,
                        "nextPrime": "2"
                    },
                    "tolerance": 1e-12
                },
                {
                    "caseId": "prime-ninety-seven",
                    "input": {
                        "value": "97"
                    },
                    "expected": {
                        "value": "97",
                        "classification": "prime",
                        "factors": [
                            {
                                "prime": "97",
                                "exponent": 1
                            }
                        ],
                        "divisorCount": "2",
                        "previousPrime": "89",
                        "nextPrime": "101"
                    },
                    "tolerance": 1e-12
                },
                {
                    "caseId": "composite-three-sixty",
                    "input": {
                        "value": "360"
                    },
                    "expected": {
                        "value": "360",
                        "classification": "composite",
                        "factors": [
                            {
                                "prime": "2",
                                "exponent": 3
                            },
                            {
                                "prime": "3",
                                "exponent": 2
                            },
                            {
                                "prime": "5",
                                "exponent": 1
                            }
                        ],
                        "divisorCount": "24",
                        "previousPrime": "359",
                        "nextPrime": "367"
                    },
                    "tolerance": 1e-12
                }
            ]
        },
        "lastReviewed": "2026-08-20"
    },
    {
        "toolId": "gcd-lcm",
        "slug": "ebob-ekok-hesaplama",
        "path": "/matematik/ebob-ekok-hesaplama",
        "category": {
            "id": "matematik",
            "label": "Matematik"
        },
        "classification": "timeless",
        "engineId": "gcd-lcm-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-08-20",
        "effectiveTo": null,
        "minAppVersion": "0.1.0",
        "updateCadence": {
            "mode": "method-change-only",
            "days": null,
            "reviewAfter": null,
            "specialMonths": [],
            "note": "Zamansız matematik aracıdır; yalnız matematiksel yöntem veya ürün kapsamı değişirse yeniden incelenir."
        },
        "sources": [
            {
                "sourceId": "openstax-gcd-lcm",
                "sourceGroupId": null,
                "title": "Prealgebra 2e — Introduction to Factoring Polynomials",
                "publisher": "OpenStax",
                "url": "https://openstax.org/books/prealgebra-2e/pages/10-6-introduction-to-factoring-polynomials",
                "kind": "authoritative-education",
                "reviewedOn": "2026-08-20",
                "watchFields": [
                    "en büyük ortak bölen ve en küçük ortak kat tanımları"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": null,
                    "placeholder": "2–6 sayı girin",
                    "unit": null,
                    "minimum": 2,
                    "maximum": 6,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "values",
                    "type": "integer-string-list",
                    "label": "Pozitif tamsayılar",
                    "required": true
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "unit": null,
                    "nullable": false,
                    "prominent": false,
                    "id": "values",
                    "type": "string-list",
                    "label": "Sayılar"
                },
                {
                    "unit": null,
                    "nullable": false,
                    "prominent": true,
                    "id": "gcd",
                    "type": "integer-string",
                    "label": "EBOB"
                },
                {
                    "unit": null,
                    "nullable": false,
                    "prominent": true,
                    "id": "lcm",
                    "type": "integer-string",
                    "label": "EKOK"
                }
            ]
        },
        "content": {
            "title": "EBOB ve EKOK Hesaplama",
            "shortTitle": "EBOB/EKOK Hesaplama",
            "summary": "İki ile altı pozitif tamsayının en büyük ortak bölenini ve en küçük ortak katını hesaplar.",
            "method": "EBOB için Öklid algoritması, EKOK için EBOB ile sadeleştirilmiş tam tamsayı çarpımı kullanılır.",
            "limitations": [
                "Her girdi 1 ile 10^12 arasında pozitif tamsayı olmalıdır."
            ],
            "faqs": [
                {
                    "question": "EBOB/EKOK Hesaplama hangi yöntemi kullanır?",
                    "answer": "EBOB için Öklid algoritması, EKOK için EBOB ile sadeleştirilmiş tam tamsayı çarpımı kullanılır. Sonuç, girilen değerlerin matematiksel anlamı ve belirtilen sınırlar içinde değerlendirilmelidir."
                }
            ]
        },
        "config": {
            "kind": "gcd-lcm-static-v1",
            "maxAbsoluteInput": 1000000000000,
            "maxInteger": "1000000000000",
            "minValues": 2,
            "maxValues": 6
        },
        "testVectors": {
            "kind": "gcd-lcm-oracle-v1",
            "cases": [
                {
                    "caseId": "forty-eight-eighteen",
                    "input": {
                        "values": [
                            "48",
                            "18"
                        ]
                    },
                    "expected": {
                        "values": [
                            "48",
                            "18"
                        ],
                        "gcd": "6",
                        "lcm": "144"
                    },
                    "tolerance": 1e-12
                },
                {
                    "caseId": "twenty-one-six-fourteen",
                    "input": {
                        "values": [
                            "21",
                            "6",
                            "14"
                        ]
                    },
                    "expected": {
                        "values": [
                            "21",
                            "6",
                            "14"
                        ],
                        "gcd": "1",
                        "lcm": "42"
                    },
                    "tolerance": 1e-12
                }
            ]
        },
        "lastReviewed": "2026-08-20"
    },
    {
        "toolId": "power-root",
        "slug": "us-ve-kok-hesaplama",
        "path": "/matematik/us-ve-kok-hesaplama",
        "category": {
            "id": "matematik",
            "label": "Matematik"
        },
        "classification": "timeless",
        "engineId": "power-root-v2",
        "schemaVersion": "2.0.0",
        "contentVersion": "2.0.0",
        "configVersion": "2.0.0",
        "effectiveFrom": "2026-08-22",
        "effectiveTo": null,
        "minAppVersion": "0.1.0",
        "updateCadence": {
            "mode": "method-change-only",
            "days": null,
            "reviewAfter": null,
            "specialMonths": [],
            "note": "Zamansız matematik aracıdır; yalnız matematiksel yöntem veya ürün kapsamı değişirse yeniden incelenir."
        },
        "sources": [
            {
                "sourceId": "openstax-power-root",
                "sourceGroupId": null,
                "title": "Algebra and Trigonometry 2e — Exponential and Logarithmic Functions",
                "publisher": "OpenStax",
                "url": "https://openstax.org/books/algebra-and-trigonometry-2e/pages/6-introduction-to-exponential-and-logarithmic-functions",
                "kind": "authoritative-education",
                "reviewedOn": "2026-08-22",
                "watchFields": [
                    "üs, kök, logaritma ve mutlak değer bağıntıları"
                ]
            },
            {
                "sourceId": "nist-dlmf-factorial",
                "sourceGroupId": null,
                "title": "NIST DLMF — Common Notations and Definitions",
                "publisher": "NIST Digital Library of Mathematical Functions",
                "url": "https://dlmf.nist.gov/front/introduction",
                "kind": "international-standards-body",
                "reviewedOn": "2026-08-22",
                "watchFields": [
                    "faktöriyel, çift faktöriyel ve 0! tanımları"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": "power",
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "power",
                            "label": "Üs"
                        },
                        {
                            "value": "sqrt",
                            "label": "Karekök"
                        },
                        {
                            "value": "nth-root",
                            "label": "n. dereceden kök"
                        },
                        {
                            "value": "logarithm",
                            "label": "Logaritma"
                        },
                        {
                            "value": "factorial",
                            "label": "Faktöriyel / kombinasyon"
                        },
                        {
                            "value": "absolute",
                            "label": "Mutlak değer"
                        }
                    ],
                    "visibleWhen": null,
                    "id": "mode",
                    "type": "select",
                    "label": "İşlem",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": -1000000000000,
                    "maximum": 1000000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": null,
                    "id": "x",
                    "type": "number",
                    "label": "x",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": -1000000000000,
                    "maximum": 1000000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": null,
                    "id": "n",
                    "type": "number",
                    "label": "n / taban / r",
                    "required": false
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "unit": null,
                    "nullable": false,
                    "prominent": false,
                    "id": "mode",
                    "type": "text",
                    "label": "İşlem"
                },
                {
                    "unit": null,
                    "nullable": false,
                    "prominent": true,
                    "id": "value",
                    "type": "number-or-text",
                    "label": "Sonuç"
                },
                {
                    "unit": null,
                    "nullable": true,
                    "prominent": false,
                    "id": "doubleFactorial",
                    "type": "integer-string",
                    "label": "Çift faktöriyel (n!!)"
                },
                {
                    "unit": null,
                    "nullable": true,
                    "prominent": false,
                    "id": "digits",
                    "type": "number",
                    "label": "Basamak sayısı"
                },
                {
                    "unit": null,
                    "nullable": true,
                    "prominent": false,
                    "id": "scientific",
                    "type": "object",
                    "label": "Bilimsel gösterim"
                },
                {
                    "unit": null,
                    "nullable": true,
                    "prominent": false,
                    "id": "combination",
                    "type": "integer-string",
                    "label": "Kombinasyon"
                },
                {
                    "unit": null,
                    "nullable": true,
                    "prominent": false,
                    "id": "permutation",
                    "type": "integer-string",
                    "label": "Permütasyon"
                },
                {
                    "unit": null,
                    "nullable": true,
                    "prominent": false,
                    "id": "sign",
                    "type": "text",
                    "label": "İşaret"
                }
            ]
        },
        "content": {
            "title": "Üs, Kök, Logaritma ve Faktöriyel Hesaplama",
            "shortTitle": "Üs, Kök ve Faktöriyel",
            "summary": "Reel sayılarda üs, kök, logaritma ve mutlak değer; tamsayılarda faktöriyel, çift faktöriyel, kombinasyon ve permütasyon hesaplar.",
            "method": "Seçilen alt moda ait derlenmiş matematiksel işlem tanım kümesi kontrollerinden sonra uygulanır; faktöriyel sonuçları tam tamsayı olarak üretilir.",
            "limitations": [
                "Karmaşık sayılı kökler kapsam dışıdır; faktöriyel, kombinasyon ve permütasyon bu aracın kontrollü 0–170 aralığıyla sınırlıdır."
            ],
            "faqs": [
                {
                    "question": "Üs, Kök ve Faktöriyel hangi yöntemi kullanır?",
                    "answer": "Seçilen alt moda ait derlenmiş matematiksel işlem tanım kümesi kontrollerinden sonra uygulanır; faktöriyel sonuçları tam tamsayı olarak üretilir. Sonuç, girilen değerlerin matematiksel anlamı ve belirtilen sınırlar içinde değerlendirilmelidir."
                }
            ]
        },
        "config": {
            "kind": "power-root-static-v2",
            "maxAbsoluteInput": 1000000000000,
            "maxN": 170,
            "supportedModes": [
                "power",
                "sqrt",
                "nth-root",
                "logarithm",
                "factorial",
                "absolute"
            ]
        },
        "testVectors": {
            "kind": "power-root-oracle-v2",
            "cases": [
                {
                    "caseId": "power-two-ten",
                    "input": {
                        "mode": "power",
                        "x": 2,
                        "n": 10
                    },
                    "expected": {
                        "mode": "power",
                        "value": 1024
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "sqrt-one-forty-four",
                    "input": {
                        "mode": "sqrt",
                        "x": 144
                    },
                    "expected": {
                        "mode": "sqrt",
                        "value": 12
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "cube-root-negative-twenty-seven",
                    "input": {
                        "mode": "nth-root",
                        "x": -27,
                        "n": 3
                    },
                    "expected": {
                        "mode": "nth-root",
                        "value": -3
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "log-thousand-base-ten",
                    "input": {
                        "mode": "logarithm",
                        "x": 1000,
                        "n": 10
                    },
                    "expected": {
                        "mode": "logarithm",
                        "value": 2.9999999999999996
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "factorial-five-choose-two",
                    "input": {
                        "mode": "factorial",
                        "x": 5,
                        "n": 2
                    },
                    "expected": {
                        "mode": "factorial",
                        "value": "120",
                        "doubleFactorial": "15",
                        "digits": 3,
                        "scientific": {
                            "mantissa": "1.2000",
                            "exponent": 2
                        },
                        "combination": "10",
                        "permutation": "20"
                    },
                    "tolerance": 1e-12
                },
                {
                    "caseId": "factorial-one-seventy-exact",
                    "input": {
                        "mode": "factorial",
                        "x": 170
                    },
                    "expected": {
                        "mode": "factorial",
                        "value": "7257415615307998967396728211129263114716991681296451376543577798900561843401706157852350749242617459511490991237838520776666022565442753025328900773207510902400430280058295603966612599658257104398558294257568966313439612262571094946806711205568880457193340212661452800000000000000000000000000000000000000000",
                        "doubleFactorial": "10898143681335202553967733471431367505237973760350892262635910940363393368804736234372287050252248634869041347308908961174198086428262400000000000000000000",
                        "digits": 307,
                        "scientific": {
                            "mantissa": "7.2574",
                            "exponent": 306
                        }
                    },
                    "tolerance": 1e-12
                },
                {
                    "caseId": "absolute-negative-seven-half",
                    "input": {
                        "mode": "absolute",
                        "x": -7.5
                    },
                    "expected": {
                        "mode": "absolute",
                        "value": 7.5,
                        "sign": "negative"
                    },
                    "tolerance": 1e-10
                }
            ]
        },
        "lastReviewed": "2026-08-22"
    },
    {
        "toolId": "quadratic-equation",
        "slug": "ikinci-derece-denklem-hesaplama",
        "path": "/matematik/ikinci-derece-denklem-hesaplama",
        "category": {
            "id": "matematik",
            "label": "Matematik"
        },
        "classification": "timeless",
        "engineId": "quadratic-equation-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-08-20",
        "effectiveTo": null,
        "minAppVersion": "0.1.0",
        "updateCadence": {
            "mode": "method-change-only",
            "days": null,
            "reviewAfter": null,
            "specialMonths": [],
            "note": "Zamansız matematik aracıdır; yalnız matematiksel yöntem veya ürün kapsamı değişirse yeniden incelenir."
        },
        "sources": [
            {
                "sourceId": "openstax-quadratic-equation",
                "sourceGroupId": null,
                "title": "Algebra and Trigonometry 2e — Quadratic Equations",
                "publisher": "OpenStax",
                "url": "https://openstax.org/books/algebra-and-trigonometry-2e/pages/2-5-quadratic-equations",
                "kind": "authoritative-education",
                "reviewedOn": "2026-08-20",
                "watchFields": [
                    "ikinci derece denklem kökleri, diskriminant ve tepe noktası"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": -1000000000000,
                    "maximum": 1000000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": null,
                    "id": "a",
                    "type": "number",
                    "label": "A",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": -1000000000000,
                    "maximum": 1000000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": null,
                    "id": "b",
                    "type": "number",
                    "label": "B",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": -1000000000000,
                    "maximum": 1000000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": null,
                    "id": "c",
                    "type": "number",
                    "label": "C",
                    "required": true
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "unit": null,
                    "nullable": false,
                    "prominent": false,
                    "id": "a",
                    "type": "number",
                    "label": "A"
                },
                {
                    "unit": null,
                    "nullable": false,
                    "prominent": false,
                    "id": "b",
                    "type": "number",
                    "label": "B"
                },
                {
                    "unit": null,
                    "nullable": false,
                    "prominent": false,
                    "id": "c",
                    "type": "number",
                    "label": "C"
                },
                {
                    "unit": null,
                    "nullable": false,
                    "prominent": false,
                    "id": "discriminant",
                    "type": "number",
                    "label": "Diskriminant"
                },
                {
                    "unit": null,
                    "nullable": false,
                    "prominent": false,
                    "id": "rootType",
                    "type": "text",
                    "label": "Kök türü"
                },
                {
                    "unit": null,
                    "nullable": false,
                    "prominent": true,
                    "id": "x1",
                    "type": "number-or-complex",
                    "label": "x₁"
                },
                {
                    "unit": null,
                    "nullable": false,
                    "prominent": true,
                    "id": "x2",
                    "type": "number-or-complex",
                    "label": "x₂"
                },
                {
                    "unit": null,
                    "nullable": false,
                    "prominent": false,
                    "id": "rootSum",
                    "type": "number",
                    "label": "Kökler toplamı"
                },
                {
                    "unit": null,
                    "nullable": false,
                    "prominent": false,
                    "id": "rootProduct",
                    "type": "number",
                    "label": "Kökler çarpımı"
                },
                {
                    "unit": null,
                    "nullable": false,
                    "prominent": false,
                    "id": "vertex",
                    "type": "object",
                    "label": "Tepe noktası"
                }
            ]
        },
        "content": {
            "title": "İkinci Derece Denklem Hesaplama",
            "shortTitle": "İkinci Derece Denklem",
            "summary": "ax²+bx+c denkleminin reel veya karmaşık köklerini, diskriminantını ve tepe noktasını hesaplar.",
            "method": "Diskriminant ve kararlı ikinci derece kök yöntemi uygulanır; kök toplamı ile çarpımı ayrıca üretilir.",
            "limitations": [
                "A katsayısı sıfır olamaz; sonuçlar IEEE 754 sayı aralığıyla sınırlıdır."
            ],
            "faqs": [
                {
                    "question": "İkinci Derece Denklem hangi yöntemi kullanır?",
                    "answer": "Diskriminant ve kararlı ikinci derece kök yöntemi uygulanır; kök toplamı ile çarpımı ayrıca üretilir. Sonuç, girilen değerlerin matematiksel anlamı ve belirtilen sınırlar içinde değerlendirilmelidir."
                }
            ]
        },
        "config": {
            "kind": "quadratic-equation-static-v1",
            "maxAbsoluteInput": 1000000000000
        },
        "testVectors": {
            "kind": "quadratic-equation-oracle-v1",
            "cases": [
                {
                    "caseId": "two-real-roots",
                    "input": {
                        "a": 1,
                        "b": -5,
                        "c": 6
                    },
                    "expected": {
                        "a": 1,
                        "b": -5,
                        "c": 6,
                        "discriminant": 1,
                        "rootType": "two-real",
                        "x1": 3,
                        "x2": 2,
                        "rootSum": 5,
                        "rootProduct": 6,
                        "vertex": {
                            "x": 2.5,
                            "y": -0.25
                        }
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "double-real-root",
                    "input": {
                        "a": 1,
                        "b": 2,
                        "c": 1
                    },
                    "expected": {
                        "a": 1,
                        "b": 2,
                        "c": 1,
                        "discriminant": 0,
                        "rootType": "double-real",
                        "x1": -1,
                        "x2": -1,
                        "rootSum": -2,
                        "rootProduct": 1,
                        "vertex": {
                            "x": -1,
                            "y": 0
                        }
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "complex-roots",
                    "input": {
                        "a": 1,
                        "b": 0,
                        "c": 1
                    },
                    "expected": {
                        "a": 1,
                        "b": 0,
                        "c": 1,
                        "discriminant": -4,
                        "rootType": "complex",
                        "x1": {
                            "real": 0,
                            "imaginary": 1
                        },
                        "x2": {
                            "real": 0,
                            "imaginary": -1
                        },
                        "rootSum": 0,
                        "rootProduct": 1,
                        "vertex": {
                            "x": 0,
                            "y": 1
                        }
                    },
                    "tolerance": 1e-10
                }
            ]
        },
        "lastReviewed": "2026-08-20"
    },
    {
        "toolId": "fraction",
        "slug": "kesir-hesaplama",
        "path": "/matematik/kesir-hesaplama",
        "category": {
            "id": "matematik",
            "label": "Matematik"
        },
        "classification": "timeless",
        "engineId": "fraction-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-08-20",
        "effectiveTo": null,
        "minAppVersion": "0.1.0",
        "updateCadence": {
            "mode": "method-change-only",
            "days": null,
            "reviewAfter": null,
            "specialMonths": [],
            "note": "Zamansız matematik aracıdır; yalnız matematiksel yöntem veya ürün kapsamı değişirse yeniden incelenir."
        },
        "sources": [
            {
                "sourceId": "openstax-fraction",
                "sourceGroupId": null,
                "title": "Prealgebra 2e — Fractions Key Concepts",
                "publisher": "OpenStax",
                "url": "https://openstax.org/books/prealgebra-2e/pages/4-key-concepts",
                "kind": "authoritative-education",
                "reviewedOn": "2026-08-20",
                "watchFields": [
                    "kesirlerde dört işlem ve sadeleştirme"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": "add",
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "add",
                            "label": "Toplama"
                        },
                        {
                            "value": "subtract",
                            "label": "Çıkarma"
                        },
                        {
                            "value": "multiply",
                            "label": "Çarpma"
                        },
                        {
                            "value": "divide",
                            "label": "Bölme"
                        }
                    ],
                    "visibleWhen": null,
                    "id": "operation",
                    "type": "select",
                    "label": "İşlem",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": -1000000000,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "numerator1",
                    "type": "number",
                    "label": "numerator1",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": -1000000000,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "denominator1",
                    "type": "number",
                    "label": "denominator1",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": -1000000000,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "numerator2",
                    "type": "number",
                    "label": "numerator2",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": -1000000000,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "denominator2",
                    "type": "number",
                    "label": "denominator2",
                    "required": true
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "unit": null,
                    "nullable": false,
                    "prominent": false,
                    "id": "operation",
                    "type": "text",
                    "label": "İşlem"
                },
                {
                    "unit": null,
                    "nullable": false,
                    "prominent": false,
                    "id": "numerator",
                    "type": "integer-string",
                    "label": "Pay"
                },
                {
                    "unit": null,
                    "nullable": false,
                    "prominent": false,
                    "id": "denominator",
                    "type": "integer-string",
                    "label": "Payda"
                },
                {
                    "unit": null,
                    "nullable": false,
                    "prominent": true,
                    "id": "fraction",
                    "type": "text",
                    "label": "Sade kesir"
                },
                {
                    "unit": null,
                    "nullable": false,
                    "prominent": false,
                    "id": "decimal",
                    "type": "number",
                    "label": "Ondalık değer"
                },
                {
                    "unit": null,
                    "nullable": true,
                    "prominent": false,
                    "id": "mixed",
                    "type": "object",
                    "label": "Tam sayılı kesir"
                }
            ]
        },
        "content": {
            "title": "Kesir Hesaplama ve Sadeleştirme",
            "shortTitle": "Kesir Hesaplama",
            "summary": "İki kesri toplar, çıkarır, çarpar veya böler; sonucu tam olarak sadeleştirir.",
            "method": "Pay ve paydalar güvenli tamsayı olarak alınır, işlem BigInt ile tam yürütülür ve ortak bölenle sadeleştirilir.",
            "limitations": [
                "Paydalar sıfır olamaz; ikinci kesir sıfırken bölme yapılamaz."
            ],
            "faqs": [
                {
                    "question": "Kesir Hesaplama hangi yöntemi kullanır?",
                    "answer": "Pay ve paydalar güvenli tamsayı olarak alınır, işlem BigInt ile tam yürütülür ve ortak bölenle sadeleştirilir. Sonuç, girilen değerlerin matematiksel anlamı ve belirtilen sınırlar içinde değerlendirilmelidir."
                }
            ]
        },
        "config": {
            "kind": "fraction-static-v1",
            "maxAbsoluteInput": 1000000000,
            "maxAbsoluteInteger": 1000000000,
            "supportedOperations": [
                "add",
                "subtract",
                "multiply",
                "divide"
            ]
        },
        "testVectors": {
            "kind": "fraction-oracle-v1",
            "cases": [
                {
                    "caseId": "add-half-third",
                    "input": {
                        "operation": "add",
                        "numerator1": 1,
                        "denominator1": 2,
                        "numerator2": 1,
                        "denominator2": 3
                    },
                    "expected": {
                        "operation": "add",
                        "numerator": "5",
                        "denominator": "6",
                        "fraction": "5/6",
                        "decimal": 0.8333333333333334,
                        "mixed": null
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "subtract-five-six-half",
                    "input": {
                        "operation": "subtract",
                        "numerator1": 5,
                        "denominator1": 6,
                        "numerator2": 1,
                        "denominator2": 2
                    },
                    "expected": {
                        "operation": "subtract",
                        "numerator": "1",
                        "denominator": "3",
                        "fraction": "1/3",
                        "decimal": 0.3333333333333333,
                        "mixed": null
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "multiply-negative-two-thirds",
                    "input": {
                        "operation": "multiply",
                        "numerator1": -2,
                        "denominator1": 3,
                        "numerator2": 9,
                        "denominator2": 4
                    },
                    "expected": {
                        "operation": "multiply",
                        "numerator": "-3",
                        "denominator": "2",
                        "fraction": "-3/2",
                        "decimal": -1.5,
                        "mixed": {
                            "whole": "-1",
                            "numerator": "1",
                            "denominator": "2"
                        }
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "divide-three-four-two-fifths",
                    "input": {
                        "operation": "divide",
                        "numerator1": 3,
                        "denominator1": 4,
                        "numerator2": 2,
                        "denominator2": 5
                    },
                    "expected": {
                        "operation": "divide",
                        "numerator": "15",
                        "denominator": "8",
                        "fraction": "15/8",
                        "decimal": 1.875,
                        "mixed": {
                            "whole": "1",
                            "numerator": "7",
                            "denominator": "8"
                        }
                    },
                    "tolerance": 1e-10
                }
            ]
        },
        "lastReviewed": "2026-08-20"
    },
    {
        "toolId": "ratio-proportion",
        "slug": "oran-oranti-hesaplama",
        "path": "/matematik/oran-oranti-hesaplama",
        "category": {
            "id": "matematik",
            "label": "Matematik"
        },
        "classification": "timeless",
        "engineId": "ratio-proportion-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-08-20",
        "effectiveTo": null,
        "minAppVersion": "0.1.0",
        "updateCadence": {
            "mode": "method-change-only",
            "days": null,
            "reviewAfter": null,
            "specialMonths": [],
            "note": "Zamansız matematik aracıdır; yalnız matematiksel yöntem veya ürün kapsamı değişirse yeniden incelenir."
        },
        "sources": [
            {
                "sourceId": "openstax-ratio-proportion",
                "sourceGroupId": null,
                "title": "Prealgebra 2e — Solve Proportions and their Applications",
                "publisher": "OpenStax",
                "url": "https://openstax.org/books/prealgebra-2e/pages/6-5-solve-proportions-and-their-applications",
                "kind": "authoritative-education",
                "reviewedOn": "2026-08-20",
                "watchFields": [
                    "doğru ve ters orantı"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": "direct",
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "direct",
                            "label": "Doğru orantı"
                        },
                        {
                            "value": "inverse",
                            "label": "Ters orantı"
                        }
                    ],
                    "visibleWhen": null,
                    "id": "mode",
                    "type": "radio",
                    "label": "Orantı türü",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": -1000000000000,
                    "maximum": 1000000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": null,
                    "id": "a",
                    "type": "number",
                    "label": "A",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": -1000000000000,
                    "maximum": 1000000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": null,
                    "id": "b",
                    "type": "number",
                    "label": "B",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": -1000000000000,
                    "maximum": 1000000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": null,
                    "id": "c",
                    "type": "number",
                    "label": "C",
                    "required": true
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "unit": null,
                    "nullable": false,
                    "prominent": false,
                    "id": "mode",
                    "type": "text",
                    "label": "Orantı türü"
                },
                {
                    "unit": null,
                    "nullable": false,
                    "prominent": false,
                    "id": "a",
                    "type": "number",
                    "label": "A"
                },
                {
                    "unit": null,
                    "nullable": false,
                    "prominent": false,
                    "id": "b",
                    "type": "number",
                    "label": "B"
                },
                {
                    "unit": null,
                    "nullable": false,
                    "prominent": false,
                    "id": "c",
                    "type": "number",
                    "label": "C"
                },
                {
                    "unit": null,
                    "nullable": false,
                    "prominent": true,
                    "id": "x",
                    "type": "number",
                    "label": "X"
                }
            ]
        },
        "content": {
            "title": "Oran ve Orantı Hesaplama",
            "shortTitle": "Oran/Orantı Hesaplama",
            "summary": "Üç bilinen değerden doğru veya ters orantıdaki bilinmeyen değeri hesaplar.",
            "method": "Doğru orantıda x=b×c/a, ters orantıda x=a×b/c bağıntısı uygulanır.",
            "limitations": [
                "Doğru orantıda A, ters orantıda C sıfır olamaz."
            ],
            "faqs": [
                {
                    "question": "Oran/Orantı Hesaplama hangi yöntemi kullanır?",
                    "answer": "Doğru orantıda x=b×c/a, ters orantıda x=a×b/c bağıntısı uygulanır. Sonuç, girilen değerlerin matematiksel anlamı ve belirtilen sınırlar içinde değerlendirilmelidir."
                }
            ]
        },
        "config": {
            "kind": "ratio-proportion-static-v1",
            "maxAbsoluteInput": 1000000000000,
            "supportedModes": [
                "direct",
                "inverse"
            ]
        },
        "testVectors": {
            "kind": "ratio-proportion-oracle-v1",
            "cases": [
                {
                    "caseId": "direct-two-three-four",
                    "input": {
                        "mode": "direct",
                        "a": 2,
                        "b": 3,
                        "c": 4
                    },
                    "expected": {
                        "mode": "direct",
                        "a": 2,
                        "b": 3,
                        "c": 4,
                        "x": 6
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "inverse-two-three-four",
                    "input": {
                        "mode": "inverse",
                        "a": 2,
                        "b": 3,
                        "c": 4
                    },
                    "expected": {
                        "mode": "inverse",
                        "a": 2,
                        "b": 3,
                        "c": 4,
                        "x": 1.5
                    },
                    "tolerance": 1e-10
                }
            ]
        },
        "lastReviewed": "2026-08-20"
    },
    {
        "toolId": "pythagorean",
        "slug": "pisagor-teoremi-hesaplama",
        "path": "/matematik/pisagor-teoremi-hesaplama",
        "category": {
            "id": "matematik",
            "label": "Matematik"
        },
        "classification": "timeless",
        "engineId": "pythagorean-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-08-20",
        "effectiveTo": null,
        "minAppVersion": "0.1.0",
        "updateCadence": {
            "mode": "method-change-only",
            "days": null,
            "reviewAfter": null,
            "specialMonths": [],
            "note": "Zamansız matematik aracıdır; yalnız matematiksel yöntem veya ürün kapsamı değişirse yeniden incelenir."
        },
        "sources": [
            {
                "sourceId": "openstax-pythagorean",
                "sourceGroupId": null,
                "title": "Prealgebra 2e — Use Properties of Angles, Triangles, and the Pythagorean Theorem",
                "publisher": "OpenStax",
                "url": "https://openstax.org/books/prealgebra-2e/pages/9-3-use-properties-of-angles-triangles-and-the-pythagorean-theorem",
                "kind": "authoritative-education",
                "reviewedOn": "2026-08-20",
                "watchFields": [
                    "Pisagor teoremi ve dik üçgen"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": "hypotenuse",
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "hypotenuse",
                            "label": "Hipotenüs"
                        },
                        {
                            "value": "leg",
                            "label": "Dik kenar"
                        }
                    ],
                    "visibleWhen": null,
                    "id": "mode",
                    "type": "radio",
                    "label": "Bilinmeyen kenar",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 0,
                    "maximum": 1000000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": null,
                    "id": "a",
                    "type": "number",
                    "label": "Dik kenar A",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 0,
                    "maximum": 1000000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": null,
                    "id": "other",
                    "type": "number",
                    "label": "Diğer kenar",
                    "required": true
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "unit": null,
                    "nullable": false,
                    "prominent": false,
                    "id": "mode",
                    "type": "text",
                    "label": "Hesap türü"
                },
                {
                    "unit": null,
                    "nullable": false,
                    "prominent": false,
                    "id": "a",
                    "type": "number",
                    "label": "A"
                },
                {
                    "unit": null,
                    "nullable": false,
                    "prominent": false,
                    "id": "b",
                    "type": "number",
                    "label": "B"
                },
                {
                    "unit": null,
                    "nullable": false,
                    "prominent": false,
                    "id": "c",
                    "type": "number",
                    "label": "C"
                },
                {
                    "unit": null,
                    "nullable": false,
                    "prominent": false,
                    "id": "unknown",
                    "type": "text",
                    "label": "Bilinmeyen"
                },
                {
                    "unit": null,
                    "nullable": false,
                    "prominent": true,
                    "id": "result",
                    "type": "number",
                    "label": "Sonuç"
                },
                {
                    "unit": null,
                    "nullable": false,
                    "prominent": false,
                    "id": "perimeter",
                    "type": "number",
                    "label": "Çevre"
                },
                {
                    "unit": null,
                    "nullable": false,
                    "prominent": false,
                    "id": "area",
                    "type": "number",
                    "label": "Alan"
                }
            ]
        },
        "content": {
            "title": "Pisagor Teoremi Hesaplama",
            "shortTitle": "Pisagor Hesaplama",
            "summary": "Dik üçgende iki kenardan hipotenüsü veya eksik dik kenarı, çevreyi ve alanı hesaplar.",
            "method": "Öklid düzlemindeki dik üçgen için a²+b²=c² bağıntısı sayısal olarak kararlı yöntemle uygulanır.",
            "limitations": [
                "Kenarlar pozitif olmalı; dik kenar aranırken hipotenüs bilinen dik kenardan büyük olmalıdır."
            ],
            "faqs": [
                {
                    "question": "Pisagor Hesaplama hangi yöntemi kullanır?",
                    "answer": "Öklid düzlemindeki dik üçgen için a²+b²=c² bağıntısı sayısal olarak kararlı yöntemle uygulanır. Sonuç, girilen değerlerin matematiksel anlamı ve belirtilen sınırlar içinde değerlendirilmelidir."
                }
            ]
        },
        "config": {
            "kind": "pythagorean-static-v1",
            "maxAbsoluteInput": 1000000000000,
            "supportedModes": [
                "hypotenuse",
                "leg"
            ]
        },
        "testVectors": {
            "kind": "pythagorean-oracle-v1",
            "cases": [
                {
                    "caseId": "hypotenuse-three-four",
                    "input": {
                        "mode": "hypotenuse",
                        "a": 3,
                        "other": 4
                    },
                    "expected": {
                        "mode": "hypotenuse",
                        "a": 3,
                        "b": 4,
                        "c": 5,
                        "unknown": "c",
                        "result": 5,
                        "perimeter": 12,
                        "area": 6
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "leg-three-five",
                    "input": {
                        "mode": "leg",
                        "a": 3,
                        "other": 5
                    },
                    "expected": {
                        "mode": "leg",
                        "a": 3,
                        "b": 4,
                        "c": 5,
                        "unknown": "b",
                        "result": 4,
                        "perimeter": 12,
                        "area": 6
                    },
                    "tolerance": 1e-10
                }
            ]
        },
        "lastReviewed": "2026-08-20"
    },
    {
        "toolId": "alan-fire",
        "slug": "alan-fire-hesaplama",
        "path": "/ev-tadilat/alan-fire-hesaplama",
        "category": {
            "id": "ev-tadilat",
            "label": "Ev ve Tadilat"
        },
        "classification": "timeless",
        "engineId": "home-renovation-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-08-20",
        "effectiveTo": null,
        "minAppVersion": "0.1.0",
        "updateCadence": {
            "mode": "method-change-only",
            "days": null,
            "reviewAfter": null,
            "specialMonths": [],
            "note": "Zamansız, derlenmiş geometri yöntemidir; ürün sarfiyatı, paket, fire ve fiyat değerleri config'e gömülmez."
        },
        "sources": [
            {
                "sourceId": "openstax-geometry-applications",
                "sourceGroupId": null,
                "title": "Prealgebra 2e — Solve Geometry Applications: Circles and Irregular Figures",
                "publisher": "OpenStax",
                "url": "https://openstax.org/books/prealgebra-2e/pages/9-5-solve-geometry-applications-circles-and-irregular-figures",
                "kind": "authoritative-education",
                "reviewedOn": "2026-08-20",
                "watchFields": [
                    "Dikdörtgen alanı, çevre ve ölçü birimi ilişkileri"
                ]
            },
            {
                "sourceId": "nist-si-conversion-factors",
                "sourceGroupId": null,
                "title": "NIST Guide to the SI — Appendix B, Conversion Factors",
                "publisher": "National Institute of Standards and Technology",
                "url": "https://www.nist.gov/pml/special-publication-811/nist-guide-si-appendix-b-conversion-factors",
                "kind": "official-government",
                "reviewedOn": "2026-08-20",
                "watchFields": [
                    "Metre, santimetre, milimetre ve alan birimi dönüşümleri"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": null,
                    "id": "netAmount",
                    "type": "number",
                    "label": "Net miktar",
                    "required": true,
                    "exclusiveMinimum": true
                },
                {
                    "defaultValue": 10,
                    "placeholder": null,
                    "unit": "%",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": null,
                    "id": "wastePercent",
                    "type": "number",
                    "label": "Fire oranı",
                    "required": false,
                    "exclusiveMinimum": false
                },
                {
                    "defaultValue": "m²",
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "m²",
                            "label": "Metrekare"
                        },
                        {
                            "value": "metre",
                            "label": "Metre"
                        },
                        {
                            "value": "adet",
                            "label": "Adet"
                        },
                        {
                            "value": "kg",
                            "label": "Kilogram"
                        },
                        {
                            "value": "litre",
                            "label": "Litre"
                        }
                    ],
                    "visibleWhen": null,
                    "id": "unit",
                    "type": "select",
                    "label": "Birim",
                    "required": false
                },
                {
                    "defaultValue": 0,
                    "placeholder": null,
                    "unit": "TL",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": null,
                    "id": "unitPrice",
                    "type": "number",
                    "label": "Birim fiyat",
                    "required": false,
                    "exclusiveMinimum": false
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "toolId",
                    "type": "text",
                    "label": "Araç",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "unit",
                    "type": "text",
                    "label": "Birim",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "netAmount",
                    "type": "number",
                    "label": "Net miktar",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "wastePercent",
                    "type": "percentage",
                    "label": "Fire oranı",
                    "unit": "%",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "wasteAmount",
                    "type": "number",
                    "label": "Fire miktarı",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "grossAmount",
                    "type": "number",
                    "label": "Toplam ihtiyaç",
                    "unit": null,
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "unitPrice",
                    "type": "money",
                    "label": "Birim fiyat",
                    "unit": "TL",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "estimatedCost",
                    "type": "money",
                    "label": "Tahmini maliyet",
                    "unit": "TL",
                    "nullable": true,
                    "prominent": true
                }
            ]
        },
        "content": {
            "title": "Fire Payı ile Brüt Malzeme İhtiyacı",
            "shortTitle": "Fire Payı ile Brüt İhtiyaç",
            "summary": "Net alan, uzunluk, adet, ağırlık veya hacme fire ekleyerek brüt miktarı; fiyat girilirse opsiyonel maliyeti hesaplar.",
            "method": "Fire miktarı net miktar × fire yüzdesi / 100, brüt ihtiyaç ise net miktar + fire miktarıdır.",
            "limitations": [
                "Sonuç bir keşif/tahmin desteğidir; uygulama, kesim ve yüzey koşulları gerçek ihtiyacı değiştirebilir.",
                "Ürün sarfiyatı, paket içeriği, fire oranı ve fiyat varsa kullanıcı tarafından ürün etiketi veya teknik föye göre girilmelidir.",
                "Fiyat girilmediğinde araç maliyet tahmini üretmez; çekirdekte güncel veya dönemsel fiyat bulunmaz."
            ],
            "faqs": [
                {
                    "question": "Fire Payı ile Brüt İhtiyaç hangi verileri kullanır?",
                    "answer": "Araç yalnız girilen ölçüleri ve formda açıkça görünen varsayılanları kullanır. Ürüne özgü sarfiyat, paket ve fiyat değerleri gerektiğinde kullanıcı girdisidir."
                }
            ]
        },
        "config": {
            "kind": "home-renovation-v1",
            "toolId": "alan-fire",
            "maxAbsoluteInput": 1000000000,
            "metadata": {
                "calculatorId": "alan-fire",
                "version": "1.0.0",
                "effectiveFrom": "2026-08-20",
                "lastVerifiedAt": "2026-08-20",
                "sources": [
                    {
                        "id": "openstax-geometry-applications",
                        "title": "Prealgebra 2e — Solve Geometry Applications: Circles and Irregular Figures",
                        "publisher": "OpenStax",
                        "url": "https://openstax.org/books/prealgebra-2e/pages/9-5-solve-geometry-applications-circles-and-irregular-figures",
                        "verifiedAt": "2026-08-20"
                    },
                    {
                        "id": "nist-si-conversion-factors",
                        "title": "NIST Guide to the SI — Appendix B, Conversion Factors",
                        "publisher": "National Institute of Standards and Technology",
                        "url": "https://www.nist.gov/pml/special-publication-811/nist-guide-si-appendix-b-conversion-factors",
                        "verifiedAt": "2026-08-20"
                    }
                ],
                "notes": [
                    "Hesap, geometrik ölçüler ile kullanıcının girdiği ürün değerlerini işleyen statik bir yöntemdir; dönemsel mevzuat veya tarife içermez.",
                    "Tüketim, verim, yoğunluk, paket ölçüsü, fire ve fiyat gibi ürüne özgü değerler kullanıcı girdisi veya formda açıkça gösterilen varsayılandır; config içinde dönemsel ürün verisi bulunmaz."
                ]
            }
        },
        "testVectors": {
            "kind": "home-renovation-oracle-v1",
            "cases": [
                {
                    "caseId": "positive-priced",
                    "scenario": "positive",
                    "input": {
                        "toolId": "alan-fire",
                        "netAmount": 20,
                        "wastePercent": 10,
                        "unit": "m²",
                        "unitPrice": 250
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "wasteAmount": 2,
                            "grossAmount": 22,
                            "estimatedCost": 5500
                        },
                        "absentFields": []
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "default-values",
                    "scenario": "default",
                    "input": {
                        "toolId": "alan-fire",
                        "netAmount": 10
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "unit": "m²",
                            "wastePercent": 10,
                            "grossAmount": 11,
                            "estimatedCost": null
                        },
                        "absentFields": []
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "boundary-zero-fire",
                    "scenario": "boundary-lower",
                    "input": {
                        "toolId": "alan-fire",
                        "netAmount": 100,
                        "wastePercent": 0
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "grossAmount": 100
                        },
                        "absentFields": []
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "boundary-ten-fire",
                    "scenario": "boundary-upper",
                    "input": {
                        "toolId": "alan-fire",
                        "netAmount": 100,
                        "wastePercent": 10
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "grossAmount": 110
                        },
                        "absentFields": []
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "price-absent",
                    "scenario": "price-absent",
                    "input": {
                        "toolId": "alan-fire",
                        "netAmount": 20,
                        "wastePercent": 10,
                        "unit": "m²"
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "estimatedCost": null
                        },
                        "absentFields": []
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "zero-net-amount",
                    "scenario": "adversarial",
                    "input": {
                        "toolId": "alan-fire",
                        "netAmount": 0
                    },
                    "expected": {
                        "ok": false,
                        "field": "netAmount",
                        "errorCode": "OUT_OF_RANGE"
                    },
                    "tolerance": 1e-10
                }
            ]
        },
        "lastReviewed": "2026-08-20"
    },
    {
        "toolId": "alci-siva-macun",
        "slug": "alci-siva-macun-hesaplama",
        "path": "/ev-tadilat/alci-siva-macun-hesaplama",
        "category": {
            "id": "ev-tadilat",
            "label": "Ev ve Tadilat"
        },
        "classification": "timeless",
        "engineId": "home-renovation-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-08-20",
        "effectiveTo": null,
        "minAppVersion": "0.1.0",
        "updateCadence": {
            "mode": "method-change-only",
            "days": null,
            "reviewAfter": null,
            "specialMonths": [],
            "note": "Zamansız, derlenmiş geometri yöntemidir; ürün sarfiyatı, paket, fire ve fiyat değerleri config'e gömülmez."
        },
        "sources": [
            {
                "sourceId": "openstax-geometry-applications",
                "sourceGroupId": null,
                "title": "Prealgebra 2e — Solve Geometry Applications: Circles and Irregular Figures",
                "publisher": "OpenStax",
                "url": "https://openstax.org/books/prealgebra-2e/pages/9-5-solve-geometry-applications-circles-and-irregular-figures",
                "kind": "authoritative-education",
                "reviewedOn": "2026-08-20",
                "watchFields": [
                    "Dikdörtgen alanı, çevre ve ölçü birimi ilişkileri"
                ]
            },
            {
                "sourceId": "nist-si-conversion-factors",
                "sourceGroupId": null,
                "title": "NIST Guide to the SI — Appendix B, Conversion Factors",
                "publisher": "National Institute of Standards and Technology",
                "url": "https://www.nist.gov/pml/special-publication-811/nist-guide-si-appendix-b-conversion-factors",
                "kind": "official-government",
                "reviewedOn": "2026-08-20",
                "watchFields": [
                    "Metre, santimetre, milimetre ve alan birimi dönüşümleri"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "m²",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": null,
                    "id": "areaM2",
                    "type": "number",
                    "label": "Uygulama alanı",
                    "required": true,
                    "exclusiveMinimum": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "mm",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": null,
                    "id": "thicknessMm",
                    "type": "number",
                    "label": "Katman kalınlığı",
                    "required": true,
                    "exclusiveMinimum": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "kg/m²/mm",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": null,
                    "id": "consumptionKgPerM2Mm",
                    "type": "number",
                    "label": "Ürün sarfiyatı",
                    "required": true,
                    "exclusiveMinimum": true
                },
                {
                    "defaultValue": 10,
                    "placeholder": null,
                    "unit": "%",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": null,
                    "id": "wastePercent",
                    "type": "number",
                    "label": "Fire oranı",
                    "required": false,
                    "exclusiveMinimum": false
                },
                {
                    "defaultValue": 25,
                    "placeholder": null,
                    "unit": "kg",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": null,
                    "id": "bagKg",
                    "type": "number",
                    "label": "Torba kapasitesi",
                    "required": false,
                    "exclusiveMinimum": true
                },
                {
                    "defaultValue": 0,
                    "placeholder": null,
                    "unit": "TL",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": null,
                    "id": "bagPrice",
                    "type": "number",
                    "label": "Torba fiyatı",
                    "required": false,
                    "exclusiveMinimum": false
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "toolId",
                    "type": "text",
                    "label": "Araç",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "areaM2",
                    "type": "number",
                    "label": "Alan",
                    "unit": "m²",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "thicknessMm",
                    "type": "number",
                    "label": "Kalınlık",
                    "unit": "mm",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "consumptionKgPerM2Mm",
                    "type": "number",
                    "label": "Sarfiyat",
                    "unit": "kg/m²/mm",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "wastePercent",
                    "type": "percentage",
                    "label": "Fire",
                    "unit": "%",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "netKg",
                    "type": "number",
                    "label": "Net miktar",
                    "unit": "kg",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "grossKg",
                    "type": "number",
                    "label": "Fireli miktar",
                    "unit": "kg",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "bagKg",
                    "type": "number",
                    "label": "Torba kapasitesi",
                    "unit": "kg",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "bagCount",
                    "type": "number",
                    "label": "Torba sayısı",
                    "unit": "adet",
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "purchasedKg",
                    "type": "number",
                    "label": "Satın alınan miktar",
                    "unit": "kg",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "bagPrice",
                    "type": "money",
                    "label": "Torba fiyatı",
                    "unit": "TL",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "estimatedCost",
                    "type": "money",
                    "label": "Tahmini maliyet",
                    "unit": "TL",
                    "nullable": true,
                    "prominent": true
                }
            ]
        },
        "content": {
            "title": "Alçı, Sıva ve Macun Hesaplama: Kaç Kg, Kaç Torba?",
            "shortTitle": "Alçı, Sıva ve Macun",
            "summary": "Alan, katman kalınlığı ve ürün teknik föyündeki sarfiyatla kilogram ve tam torba ihtiyacını hesaplar.",
            "method": "Net kg alan × kalınlık × kullanıcı sarfiyatıdır; fire sonrası miktar torba kapasitesine bölünüp yukarı yuvarlanır.",
            "limitations": [
                "Sonuç bir keşif/tahmin desteğidir; uygulama, kesim ve yüzey koşulları gerçek ihtiyacı değiştirebilir.",
                "Ürün sarfiyatı, paket içeriği, fire oranı ve fiyat varsa kullanıcı tarafından ürün etiketi veya teknik föye göre girilmelidir.",
                "Fiyat girilmediğinde araç maliyet tahmini üretmez; çekirdekte güncel veya dönemsel fiyat bulunmaz."
            ],
            "faqs": [
                {
                    "question": "Alçı, Sıva ve Macun hangi verileri kullanır?",
                    "answer": "Araç yalnız girilen ölçüleri ve formda açıkça görünen varsayılanları kullanır. Ürüne özgü sarfiyat, paket ve fiyat değerleri gerektiğinde kullanıcı girdisidir."
                }
            ]
        },
        "config": {
            "kind": "home-renovation-v1",
            "toolId": "alci-siva-macun",
            "maxAbsoluteInput": 1000000000,
            "metadata": {
                "calculatorId": "alci-siva-macun",
                "version": "1.0.0",
                "effectiveFrom": "2026-08-20",
                "lastVerifiedAt": "2026-08-20",
                "sources": [
                    {
                        "id": "openstax-geometry-applications",
                        "title": "Prealgebra 2e — Solve Geometry Applications: Circles and Irregular Figures",
                        "publisher": "OpenStax",
                        "url": "https://openstax.org/books/prealgebra-2e/pages/9-5-solve-geometry-applications-circles-and-irregular-figures",
                        "verifiedAt": "2026-08-20"
                    },
                    {
                        "id": "nist-si-conversion-factors",
                        "title": "NIST Guide to the SI — Appendix B, Conversion Factors",
                        "publisher": "National Institute of Standards and Technology",
                        "url": "https://www.nist.gov/pml/special-publication-811/nist-guide-si-appendix-b-conversion-factors",
                        "verifiedAt": "2026-08-20"
                    }
                ],
                "notes": [
                    "Hesap, geometrik ölçüler ile kullanıcının girdiği ürün değerlerini işleyen statik bir yöntemdir; dönemsel mevzuat veya tarife içermez.",
                    "Tüketim, verim, yoğunluk, paket ölçüsü, fire ve fiyat gibi ürüne özgü değerler kullanıcı girdisi veya formda açıkça gösterilen varsayılandır; config içinde dönemsel ürün verisi bulunmaz."
                ]
            }
        },
        "testVectors": {
            "kind": "home-renovation-oracle-v1",
            "cases": [
                {
                    "caseId": "positive-priced",
                    "scenario": "positive",
                    "input": {
                        "toolId": "alci-siva-macun",
                        "areaM2": 35,
                        "thicknessMm": 3,
                        "consumptionKgPerM2Mm": 1,
                        "wastePercent": 10,
                        "bagKg": 25,
                        "bagPrice": 180
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "netKg": 105,
                            "grossKg": 115.5,
                            "bagCount": 5,
                            "purchasedKg": 125,
                            "estimatedCost": 900
                        },
                        "absentFields": []
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "default-values",
                    "scenario": "default",
                    "input": {
                        "toolId": "alci-siva-macun",
                        "areaM2": 10,
                        "thicknessMm": 1,
                        "consumptionKgPerM2Mm": 1
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "wastePercent": 10,
                            "bagKg": 25,
                            "grossKg": 11,
                            "bagCount": 1,
                            "estimatedCost": null
                        },
                        "absentFields": []
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "boundary-one-bag",
                    "scenario": "boundary-lower",
                    "input": {
                        "toolId": "alci-siva-macun",
                        "areaM2": 10,
                        "thicknessMm": 1,
                        "consumptionKgPerM2Mm": 1,
                        "wastePercent": 0,
                        "bagKg": 10
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "bagCount": 1
                        },
                        "absentFields": []
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "boundary-two-bags",
                    "scenario": "boundary-upper",
                    "input": {
                        "toolId": "alci-siva-macun",
                        "areaM2": 10.001,
                        "thicknessMm": 1,
                        "consumptionKgPerM2Mm": 1,
                        "wastePercent": 0,
                        "bagKg": 10
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "bagCount": 2
                        },
                        "absentFields": []
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "price-absent",
                    "scenario": "price-absent",
                    "input": {
                        "toolId": "alci-siva-macun",
                        "areaM2": 35,
                        "thicknessMm": 3,
                        "consumptionKgPerM2Mm": 1,
                        "wastePercent": 10,
                        "bagKg": 25
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "estimatedCost": null
                        },
                        "absentFields": []
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "zero-area",
                    "scenario": "adversarial",
                    "input": {
                        "toolId": "alci-siva-macun",
                        "areaM2": 0,
                        "thicknessMm": 1,
                        "consumptionKgPerM2Mm": 1
                    },
                    "expected": {
                        "ok": false,
                        "field": "areaM2",
                        "errorCode": "OUT_OF_RANGE"
                    },
                    "tolerance": 1e-10
                }
            ]
        },
        "lastReviewed": "2026-08-20"
    },
    {
        "toolId": "boya",
        "slug": "boya-hesaplama",
        "path": "/ev-tadilat/boya-hesaplama",
        "category": {
            "id": "ev-tadilat",
            "label": "Ev ve Tadilat"
        },
        "classification": "timeless",
        "engineId": "home-renovation-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-08-20",
        "effectiveTo": null,
        "minAppVersion": "0.1.0",
        "updateCadence": {
            "mode": "method-change-only",
            "days": null,
            "reviewAfter": null,
            "specialMonths": [],
            "note": "Zamansız, derlenmiş geometri yöntemidir; ürün sarfiyatı, paket, fire ve fiyat değerleri config'e gömülmez."
        },
        "sources": [
            {
                "sourceId": "openstax-geometry-applications",
                "sourceGroupId": null,
                "title": "Prealgebra 2e — Solve Geometry Applications: Circles and Irregular Figures",
                "publisher": "OpenStax",
                "url": "https://openstax.org/books/prealgebra-2e/pages/9-5-solve-geometry-applications-circles-and-irregular-figures",
                "kind": "authoritative-education",
                "reviewedOn": "2026-08-20",
                "watchFields": [
                    "Dikdörtgen alanı, çevre ve ölçü birimi ilişkileri"
                ]
            },
            {
                "sourceId": "nist-si-conversion-factors",
                "sourceGroupId": null,
                "title": "NIST Guide to the SI — Appendix B, Conversion Factors",
                "publisher": "National Institute of Standards and Technology",
                "url": "https://www.nist.gov/pml/special-publication-811/nist-guide-si-appendix-b-conversion-factors",
                "kind": "official-government",
                "reviewedOn": "2026-08-20",
                "watchFields": [
                    "Metre, santimetre, milimetre ve alan birimi dönüşümleri"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "m",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": null,
                    "id": "roomWidthM",
                    "type": "number",
                    "label": "Oda eni",
                    "required": true,
                    "exclusiveMinimum": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "m",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": null,
                    "id": "roomLengthM",
                    "type": "number",
                    "label": "Oda boyu",
                    "required": true,
                    "exclusiveMinimum": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "m",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": null,
                    "id": "wallHeightM",
                    "type": "number",
                    "label": "Duvar yüksekliği",
                    "required": true,
                    "exclusiveMinimum": true
                },
                {
                    "defaultValue": 0,
                    "placeholder": null,
                    "unit": "m²",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": null,
                    "id": "openingsAreaM2",
                    "type": "number",
                    "label": "Kapı/pencere alanı",
                    "required": false,
                    "exclusiveMinimum": false
                },
                {
                    "defaultValue": 2,
                    "placeholder": null,
                    "unit": "kat",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "coatCount",
                    "type": "number",
                    "label": "Kat sayısı",
                    "required": false,
                    "exclusiveMinimum": true
                },
                {
                    "defaultValue": 10,
                    "placeholder": null,
                    "unit": "m²/L",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": null,
                    "id": "coverageM2PerLitre",
                    "type": "number",
                    "label": "Ürün örtücülüğü",
                    "required": false,
                    "exclusiveMinimum": true
                },
                {
                    "defaultValue": 10,
                    "placeholder": null,
                    "unit": "%",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": null,
                    "id": "wastePercent",
                    "type": "number",
                    "label": "Fire oranı",
                    "required": false,
                    "exclusiveMinimum": false
                },
                {
                    "defaultValue": 15,
                    "placeholder": null,
                    "unit": "L",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": null,
                    "id": "bucketLitres",
                    "type": "number",
                    "label": "Kova kapasitesi",
                    "required": false,
                    "exclusiveMinimum": true
                },
                {
                    "defaultValue": false,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [],
                    "visibleWhen": null,
                    "id": "includeCeiling",
                    "type": "boolean",
                    "label": "Tavanı dahil et",
                    "required": false
                },
                {
                    "defaultValue": 0,
                    "placeholder": null,
                    "unit": "TL/L",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": null,
                    "id": "litrePrice",
                    "type": "number",
                    "label": "Litre fiyatı",
                    "required": false,
                    "exclusiveMinimum": false
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "toolId",
                    "type": "text",
                    "label": "Araç",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "wallAreaM2",
                    "type": "number",
                    "label": "Duvar alanı",
                    "unit": "m²",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "ceilingAreaM2",
                    "type": "number",
                    "label": "Tavan alanı",
                    "unit": "m²",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "netPaintAreaM2",
                    "type": "number",
                    "label": "Net boya alanı",
                    "unit": "m²",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "coatCount",
                    "type": "number",
                    "label": "Kat sayısı",
                    "unit": "kat",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "coverageM2PerLitre",
                    "type": "number",
                    "label": "Örtücülük",
                    "unit": "m²/L",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "wastePercent",
                    "type": "percentage",
                    "label": "Fire",
                    "unit": "%",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "theoreticalLitres",
                    "type": "number",
                    "label": "Teorik ihtiyaç",
                    "unit": "L",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "grossLitres",
                    "type": "number",
                    "label": "Fireli ihtiyaç",
                    "unit": "L",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "bucketLitres",
                    "type": "number",
                    "label": "Kova kapasitesi",
                    "unit": "L",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "bucketCount",
                    "type": "number",
                    "label": "Kova sayısı",
                    "unit": "adet",
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "purchasedLitres",
                    "type": "number",
                    "label": "Satın alınan miktar",
                    "unit": "L",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "litrePrice",
                    "type": "money",
                    "label": "Litre fiyatı",
                    "unit": "TL/L",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "estimatedCost",
                    "type": "money",
                    "label": "Tahmini maliyet",
                    "unit": "TL",
                    "nullable": true,
                    "prominent": true
                }
            ]
        },
        "content": {
            "title": "Boya Hesaplama Aracı: Kaç Litre Boya Lazım?",
            "shortTitle": "Boya Hesaplama",
            "summary": "Oda duvarlarını, isteğe bağlı tavanı, açıklıkları, kat sayısını ve ürün örtücülüğünü kullanarak boya ihtiyacını hesaplar.",
            "method": "Boyanacak net alan kat sayısıyla çarpılır, kullanıcı örtücülüğüne bölünür; fire eklenip kova kapasitesine göre yukarı yuvarlanır.",
            "limitations": [
                "Sonuç bir keşif/tahmin desteğidir; uygulama, kesim ve yüzey koşulları gerçek ihtiyacı değiştirebilir.",
                "Ürün sarfiyatı, paket içeriği, fire oranı ve fiyat varsa kullanıcı tarafından ürün etiketi veya teknik föye göre girilmelidir.",
                "Fiyat girilmediğinde araç maliyet tahmini üretmez; çekirdekte güncel veya dönemsel fiyat bulunmaz."
            ],
            "faqs": [
                {
                    "question": "Boya Hesaplama hangi verileri kullanır?",
                    "answer": "Araç yalnız girilen ölçüleri ve formda açıkça görünen varsayılanları kullanır. Ürüne özgü sarfiyat, paket ve fiyat değerleri gerektiğinde kullanıcı girdisidir."
                }
            ]
        },
        "config": {
            "kind": "home-renovation-v1",
            "toolId": "boya",
            "maxAbsoluteInput": 1000000000,
            "metadata": {
                "calculatorId": "boya",
                "version": "1.0.0",
                "effectiveFrom": "2026-08-20",
                "lastVerifiedAt": "2026-08-20",
                "sources": [
                    {
                        "id": "openstax-geometry-applications",
                        "title": "Prealgebra 2e — Solve Geometry Applications: Circles and Irregular Figures",
                        "publisher": "OpenStax",
                        "url": "https://openstax.org/books/prealgebra-2e/pages/9-5-solve-geometry-applications-circles-and-irregular-figures",
                        "verifiedAt": "2026-08-20"
                    },
                    {
                        "id": "nist-si-conversion-factors",
                        "title": "NIST Guide to the SI — Appendix B, Conversion Factors",
                        "publisher": "National Institute of Standards and Technology",
                        "url": "https://www.nist.gov/pml/special-publication-811/nist-guide-si-appendix-b-conversion-factors",
                        "verifiedAt": "2026-08-20"
                    }
                ],
                "notes": [
                    "Hesap, geometrik ölçüler ile kullanıcının girdiği ürün değerlerini işleyen statik bir yöntemdir; dönemsel mevzuat veya tarife içermez.",
                    "Tüketim, verim, yoğunluk, paket ölçüsü, fire ve fiyat gibi ürüne özgü değerler kullanıcı girdisi veya formda açıkça gösterilen varsayılandır; config içinde dönemsel ürün verisi bulunmaz."
                ]
            }
        },
        "testVectors": {
            "kind": "home-renovation-oracle-v1",
            "cases": [
                {
                    "caseId": "positive-priced",
                    "scenario": "positive",
                    "input": {
                        "toolId": "boya",
                        "roomWidthM": 4,
                        "roomLengthM": 5,
                        "wallHeightM": 2.7,
                        "openingsAreaM2": 4,
                        "coatCount": 2,
                        "coverageM2PerLitre": 10,
                        "wastePercent": 10,
                        "bucketLitres": 15,
                        "includeCeiling": true,
                        "litrePrice": 120
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "wallAreaM2": 48.6,
                            "ceilingAreaM2": 20,
                            "netPaintAreaM2": 64.6,
                            "theoreticalLitres": 12.92,
                            "grossLitres": 14.212,
                            "bucketCount": 1,
                            "purchasedLitres": 15,
                            "estimatedCost": 1800
                        },
                        "absentFields": []
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "default-values",
                    "scenario": "default",
                    "input": {
                        "toolId": "boya",
                        "roomWidthM": 1,
                        "roomLengthM": 1,
                        "wallHeightM": 1
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "coatCount": 2,
                            "coverageM2PerLitre": 10,
                            "wastePercent": 10,
                            "bucketLitres": 15,
                            "ceilingAreaM2": 0,
                            "grossLitres": 0.88,
                            "estimatedCost": null
                        },
                        "absentFields": []
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "boundary-one-bucket",
                    "scenario": "boundary-lower",
                    "input": {
                        "toolId": "boya",
                        "roomWidthM": 1,
                        "roomLengthM": 1,
                        "wallHeightM": 1,
                        "coatCount": 1,
                        "coverageM2PerLitre": 1,
                        "wastePercent": 0,
                        "bucketLitres": 4
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "bucketCount": 1
                        },
                        "absentFields": []
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "boundary-two-buckets",
                    "scenario": "boundary-upper",
                    "input": {
                        "toolId": "boya",
                        "roomWidthM": 1,
                        "roomLengthM": 1,
                        "wallHeightM": 1.0001,
                        "coatCount": 1,
                        "coverageM2PerLitre": 1,
                        "wastePercent": 0,
                        "bucketLitres": 4
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "bucketCount": 2
                        },
                        "absentFields": []
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "price-absent",
                    "scenario": "price-absent",
                    "input": {
                        "toolId": "boya",
                        "roomWidthM": 4,
                        "roomLengthM": 5,
                        "wallHeightM": 2.7,
                        "openingsAreaM2": 4,
                        "coatCount": 2,
                        "coverageM2PerLitre": 10,
                        "wastePercent": 10,
                        "bucketLitres": 15,
                        "includeCeiling": true
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "estimatedCost": null
                        },
                        "absentFields": []
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "openings-exceed-gross-area",
                    "scenario": "adversarial",
                    "input": {
                        "toolId": "boya",
                        "roomWidthM": 1,
                        "roomLengthM": 1,
                        "wallHeightM": 1,
                        "openingsAreaM2": 4.0001
                    },
                    "expected": {
                        "ok": false,
                        "field": "openingsAreaM2",
                        "errorCode": "OUT_OF_RANGE"
                    },
                    "tolerance": 1e-10
                }
            ]
        },
        "lastReviewed": "2026-08-20"
    },
    {
        "toolId": "derz-dolgu",
        "slug": "derz-dolgu-hesaplama",
        "path": "/ev-tadilat/derz-dolgu-hesaplama",
        "category": {
            "id": "ev-tadilat",
            "label": "Ev ve Tadilat"
        },
        "classification": "timeless",
        "engineId": "home-renovation-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-08-20",
        "effectiveTo": null,
        "minAppVersion": "0.1.0",
        "updateCadence": {
            "mode": "method-change-only",
            "days": null,
            "reviewAfter": null,
            "specialMonths": [],
            "note": "Zamansız, derlenmiş geometri yöntemidir; ürün sarfiyatı, paket, fire ve fiyat değerleri config'e gömülmez."
        },
        "sources": [
            {
                "sourceId": "openstax-geometry-applications",
                "sourceGroupId": null,
                "title": "Prealgebra 2e — Solve Geometry Applications: Circles and Irregular Figures",
                "publisher": "OpenStax",
                "url": "https://openstax.org/books/prealgebra-2e/pages/9-5-solve-geometry-applications-circles-and-irregular-figures",
                "kind": "authoritative-education",
                "reviewedOn": "2026-08-20",
                "watchFields": [
                    "Dikdörtgen alanı, çevre ve ölçü birimi ilişkileri"
                ]
            },
            {
                "sourceId": "nist-si-conversion-factors",
                "sourceGroupId": null,
                "title": "NIST Guide to the SI — Appendix B, Conversion Factors",
                "publisher": "National Institute of Standards and Technology",
                "url": "https://www.nist.gov/pml/special-publication-811/nist-guide-si-appendix-b-conversion-factors",
                "kind": "official-government",
                "reviewedOn": "2026-08-20",
                "watchFields": [
                    "Metre, santimetre, milimetre ve alan birimi dönüşümleri"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "m²",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": null,
                    "id": "areaM2",
                    "type": "number",
                    "label": "Uygulama alanı",
                    "required": true,
                    "exclusiveMinimum": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "cm",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": null,
                    "id": "tileWidthCm",
                    "type": "number",
                    "label": "Karo eni",
                    "required": true,
                    "exclusiveMinimum": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "cm",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": null,
                    "id": "tileHeightCm",
                    "type": "number",
                    "label": "Karo boyu",
                    "required": true,
                    "exclusiveMinimum": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "mm",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": null,
                    "id": "jointWidthMm",
                    "type": "number",
                    "label": "Derz genişliği",
                    "required": true,
                    "exclusiveMinimum": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "mm",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": null,
                    "id": "jointDepthMm",
                    "type": "number",
                    "label": "Derz derinliği",
                    "required": true,
                    "exclusiveMinimum": true
                },
                {
                    "defaultValue": 1.6,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": null,
                    "id": "densityCoefficient",
                    "type": "number",
                    "label": "Ürün yoğunluk katsayısı",
                    "required": false,
                    "exclusiveMinimum": true
                },
                {
                    "defaultValue": 10,
                    "placeholder": null,
                    "unit": "%",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": null,
                    "id": "wastePercent",
                    "type": "number",
                    "label": "Fire oranı",
                    "required": false,
                    "exclusiveMinimum": false
                },
                {
                    "defaultValue": 5,
                    "placeholder": null,
                    "unit": "kg",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": null,
                    "id": "bagKg",
                    "type": "number",
                    "label": "Torba kapasitesi",
                    "required": false,
                    "exclusiveMinimum": true
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "toolId",
                    "type": "text",
                    "label": "Araç",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "areaM2",
                    "type": "number",
                    "label": "Alan",
                    "unit": "m²",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "consumptionKgPerM2",
                    "type": "number",
                    "label": "m² başına sarfiyat",
                    "unit": "kg/m²",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "netKg",
                    "type": "number",
                    "label": "Net miktar",
                    "unit": "kg",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "grossKg",
                    "type": "number",
                    "label": "Fireli miktar",
                    "unit": "kg",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "wastePercent",
                    "type": "percentage",
                    "label": "Fire",
                    "unit": "%",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "bagKg",
                    "type": "number",
                    "label": "Torba kapasitesi",
                    "unit": "kg",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "bagCount",
                    "type": "number",
                    "label": "Torba sayısı",
                    "unit": "adet",
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "purchasedKg",
                    "type": "number",
                    "label": "Satın alınan miktar",
                    "unit": "kg",
                    "nullable": false,
                    "prominent": false
                }
            ]
        },
        "content": {
            "title": "Derz Dolgu Sarfiyatı: Kaç Kg ve Kaç m² Gerekir?",
            "shortTitle": "Derz Dolgu Hesaplama",
            "summary": "Karo ölçüsü, derz genişliği/derinliği ve kullanıcı ürün katsayısıyla kilogram ve tam torba ihtiyacını hesaplar.",
            "method": "Derz geometrisinden m² başına sarfiyat bulunur; alan ve fireyle çarpılan miktar torba kapasitesine göre yukarı yuvarlanır.",
            "limitations": [
                "Sonuç bir keşif/tahmin desteğidir; uygulama, kesim ve yüzey koşulları gerçek ihtiyacı değiştirebilir.",
                "Ürün sarfiyatı, paket içeriği, fire oranı ve fiyat varsa kullanıcı tarafından ürün etiketi veya teknik föye göre girilmelidir.",
                "Fiyat girilmediğinde araç maliyet tahmini üretmez; çekirdekte güncel veya dönemsel fiyat bulunmaz."
            ],
            "faqs": [
                {
                    "question": "Derz Dolgu Hesaplama hangi verileri kullanır?",
                    "answer": "Araç yalnız girilen ölçüleri ve formda açıkça görünen varsayılanları kullanır. Ürüne özgü sarfiyat, paket ve fiyat değerleri gerektiğinde kullanıcı girdisidir."
                }
            ]
        },
        "config": {
            "kind": "home-renovation-v1",
            "toolId": "derz-dolgu",
            "maxAbsoluteInput": 1000000000,
            "metadata": {
                "calculatorId": "derz-dolgu",
                "version": "1.0.0",
                "effectiveFrom": "2026-08-20",
                "lastVerifiedAt": "2026-08-20",
                "sources": [
                    {
                        "id": "openstax-geometry-applications",
                        "title": "Prealgebra 2e — Solve Geometry Applications: Circles and Irregular Figures",
                        "publisher": "OpenStax",
                        "url": "https://openstax.org/books/prealgebra-2e/pages/9-5-solve-geometry-applications-circles-and-irregular-figures",
                        "verifiedAt": "2026-08-20"
                    },
                    {
                        "id": "nist-si-conversion-factors",
                        "title": "NIST Guide to the SI — Appendix B, Conversion Factors",
                        "publisher": "National Institute of Standards and Technology",
                        "url": "https://www.nist.gov/pml/special-publication-811/nist-guide-si-appendix-b-conversion-factors",
                        "verifiedAt": "2026-08-20"
                    }
                ],
                "notes": [
                    "Hesap, geometrik ölçüler ile kullanıcının girdiği ürün değerlerini işleyen statik bir yöntemdir; dönemsel mevzuat veya tarife içermez.",
                    "Tüketim, verim, yoğunluk, paket ölçüsü, fire ve fiyat gibi ürüne özgü değerler kullanıcı girdisi veya formda açıkça gösterilen varsayılandır; config içinde dönemsel ürün verisi bulunmaz."
                ]
            }
        },
        "testVectors": {
            "kind": "home-renovation-oracle-v1",
            "cases": [
                {
                    "caseId": "positive-product",
                    "scenario": "positive",
                    "input": {
                        "toolId": "derz-dolgu",
                        "areaM2": 12,
                        "tileWidthCm": 60,
                        "tileHeightCm": 60,
                        "jointWidthMm": 3,
                        "jointDepthMm": 6,
                        "densityCoefficient": 1.6,
                        "wastePercent": 10,
                        "bagKg": 5
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "consumptionKgPerM2": 0.096,
                            "netKg": 1.152,
                            "grossKg": 1.2672,
                            "bagCount": 1,
                            "purchasedKg": 5
                        },
                        "absentFields": []
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "default-values",
                    "scenario": "default",
                    "input": {
                        "toolId": "derz-dolgu",
                        "areaM2": 1,
                        "tileWidthCm": 100,
                        "tileHeightCm": 100,
                        "jointWidthMm": 1,
                        "jointDepthMm": 1
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "consumptionKgPerM2": 0.0032,
                            "wastePercent": 10,
                            "bagKg": 5,
                            "grossKg": 0.00352,
                            "bagCount": 1
                        },
                        "absentFields": []
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "boundary-one-bag",
                    "scenario": "boundary-lower",
                    "input": {
                        "toolId": "derz-dolgu",
                        "areaM2": 500,
                        "tileWidthCm": 100,
                        "tileHeightCm": 100,
                        "jointWidthMm": 1,
                        "jointDepthMm": 1,
                        "densityCoefficient": 1,
                        "wastePercent": 0,
                        "bagKg": 1
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "bagCount": 1
                        },
                        "absentFields": []
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "boundary-two-bags",
                    "scenario": "boundary-upper",
                    "input": {
                        "toolId": "derz-dolgu",
                        "areaM2": 500.001,
                        "tileWidthCm": 100,
                        "tileHeightCm": 100,
                        "jointWidthMm": 1,
                        "jointDepthMm": 1,
                        "densityCoefficient": 1,
                        "wastePercent": 0,
                        "bagKg": 1
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "bagCount": 2
                        },
                        "absentFields": []
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "price-not-supported",
                    "scenario": "price-absent",
                    "input": {
                        "toolId": "derz-dolgu",
                        "areaM2": 12,
                        "tileWidthCm": 60,
                        "tileHeightCm": 60,
                        "jointWidthMm": 3,
                        "jointDepthMm": 6
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "bagCount": 1
                        },
                        "absentFields": [
                            "estimatedCost"
                        ]
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "zero-area",
                    "scenario": "adversarial",
                    "input": {
                        "toolId": "derz-dolgu",
                        "areaM2": 0,
                        "tileWidthCm": 100,
                        "tileHeightCm": 100,
                        "jointWidthMm": 1,
                        "jointDepthMm": 1
                    },
                    "expected": {
                        "ok": false,
                        "field": "areaM2",
                        "errorCode": "OUT_OF_RANGE"
                    },
                    "tolerance": 1e-10
                }
            ]
        },
        "lastReviewed": "2026-08-20"
    },
    {
        "toolId": "duvar-kagidi-rulo",
        "slug": "duvar-kagidi-rulo-hesaplama",
        "path": "/ev-tadilat/duvar-kagidi-rulo-hesaplama",
        "category": {
            "id": "ev-tadilat",
            "label": "Ev ve Tadilat"
        },
        "classification": "timeless",
        "engineId": "home-renovation-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-08-20",
        "effectiveTo": null,
        "minAppVersion": "0.1.0",
        "updateCadence": {
            "mode": "method-change-only",
            "days": null,
            "reviewAfter": null,
            "specialMonths": [],
            "note": "Zamansız, derlenmiş geometri yöntemidir; ürün sarfiyatı, paket, fire ve fiyat değerleri config'e gömülmez."
        },
        "sources": [
            {
                "sourceId": "openstax-geometry-applications",
                "sourceGroupId": null,
                "title": "Prealgebra 2e — Solve Geometry Applications: Circles and Irregular Figures",
                "publisher": "OpenStax",
                "url": "https://openstax.org/books/prealgebra-2e/pages/9-5-solve-geometry-applications-circles-and-irregular-figures",
                "kind": "authoritative-education",
                "reviewedOn": "2026-08-20",
                "watchFields": [
                    "Dikdörtgen alanı, çevre ve ölçü birimi ilişkileri"
                ]
            },
            {
                "sourceId": "nist-si-conversion-factors",
                "sourceGroupId": null,
                "title": "NIST Guide to the SI — Appendix B, Conversion Factors",
                "publisher": "National Institute of Standards and Technology",
                "url": "https://www.nist.gov/pml/special-publication-811/nist-guide-si-appendix-b-conversion-factors",
                "kind": "official-government",
                "reviewedOn": "2026-08-20",
                "watchFields": [
                    "Metre, santimetre, milimetre ve alan birimi dönüşümleri"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "m",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": null,
                    "id": "wallWidthM",
                    "type": "number",
                    "label": "Duvar genişliği",
                    "required": true,
                    "exclusiveMinimum": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "m",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": null,
                    "id": "wallHeightM",
                    "type": "number",
                    "label": "Duvar yüksekliği",
                    "required": true,
                    "exclusiveMinimum": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "cm",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": null,
                    "id": "rollWidthCm",
                    "type": "number",
                    "label": "Rulo eni",
                    "required": true,
                    "exclusiveMinimum": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "m",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": null,
                    "id": "rollLengthM",
                    "type": "number",
                    "label": "Rulo boyu",
                    "required": true,
                    "exclusiveMinimum": true
                },
                {
                    "defaultValue": 0,
                    "placeholder": null,
                    "unit": "cm",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": null,
                    "id": "patternRepeatCm",
                    "type": "number",
                    "label": "Desen tekrarı",
                    "required": false,
                    "exclusiveMinimum": false
                },
                {
                    "defaultValue": 5,
                    "placeholder": null,
                    "unit": "%",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": null,
                    "id": "wastePercent",
                    "type": "number",
                    "label": "Fire oranı",
                    "required": false,
                    "exclusiveMinimum": false
                },
                {
                    "defaultValue": 0,
                    "placeholder": null,
                    "unit": "TL",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": null,
                    "id": "rollPrice",
                    "type": "number",
                    "label": "Rulo fiyatı",
                    "required": false,
                    "exclusiveMinimum": false
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "toolId",
                    "type": "text",
                    "label": "Araç",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "stripCount",
                    "type": "number",
                    "label": "Şerit sayısı",
                    "unit": "adet",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "baseStripLengthM",
                    "type": "number",
                    "label": "Temel şerit boyu",
                    "unit": "m",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "stripLengthM",
                    "type": "number",
                    "label": "Desene göre şerit boyu",
                    "unit": "m",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "totalStripLengthM",
                    "type": "number",
                    "label": "Toplam şerit boyu",
                    "unit": "m",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "wastePercent",
                    "type": "percentage",
                    "label": "Fire",
                    "unit": "%",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "rollCount",
                    "type": "number",
                    "label": "Rulo sayısı",
                    "unit": "adet",
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "rollLengthM",
                    "type": "number",
                    "label": "Rulo boyu",
                    "unit": "m",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "purchasedLengthM",
                    "type": "number",
                    "label": "Satın alınan uzunluk",
                    "unit": "m",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "rollPrice",
                    "type": "money",
                    "label": "Rulo fiyatı",
                    "unit": "TL",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "estimatedCost",
                    "type": "money",
                    "label": "Tahmini maliyet",
                    "unit": "TL",
                    "nullable": true,
                    "prominent": true
                }
            ]
        },
        "content": {
            "title": "Kaç Rulo Duvar Kağıdı Gerekir? Rulo Hesaplama",
            "shortTitle": "Duvar Kağıdı Rulo",
            "summary": "Duvar/rulo ölçüsü, desen tekrarı ve fireyle tam şerit ve rulo ihtiyacını hesaplar.",
            "method": "Şerit sayısı rulo enine, şerit boyu duvar yüksekliği ve desen tekrarına göre yukarı yuvarlanır; her ruloya sığan tam şerit kapasitesi bulunur ve fireli şerit ihtiyacı bu kapasiteye göre yukarı yuvarlanır.",
            "limitations": [
                "Sonuç bir keşif/tahmin desteğidir; uygulama, kesim ve yüzey koşulları gerçek ihtiyacı değiştirebilir.",
                "Ürün sarfiyatı, paket içeriği, fire oranı ve fiyat varsa kullanıcı tarafından ürün etiketi veya teknik föye göre girilmelidir.",
                "Fiyat girilmediğinde araç maliyet tahmini üretmez; çekirdekte güncel veya dönemsel fiyat bulunmaz."
            ],
            "faqs": [
                {
                    "question": "Duvar Kağıdı Rulo hangi verileri kullanır?",
                    "answer": "Araç yalnız girilen ölçüleri ve formda açıkça görünen varsayılanları kullanır. Ürüne özgü sarfiyat, paket ve fiyat değerleri gerektiğinde kullanıcı girdisidir."
                }
            ]
        },
        "config": {
            "kind": "home-renovation-v1",
            "toolId": "duvar-kagidi-rulo",
            "maxAbsoluteInput": 1000000000,
            "metadata": {
                "calculatorId": "duvar-kagidi-rulo",
                "version": "1.0.0",
                "effectiveFrom": "2026-08-20",
                "lastVerifiedAt": "2026-08-20",
                "sources": [
                    {
                        "id": "openstax-geometry-applications",
                        "title": "Prealgebra 2e — Solve Geometry Applications: Circles and Irregular Figures",
                        "publisher": "OpenStax",
                        "url": "https://openstax.org/books/prealgebra-2e/pages/9-5-solve-geometry-applications-circles-and-irregular-figures",
                        "verifiedAt": "2026-08-20"
                    },
                    {
                        "id": "nist-si-conversion-factors",
                        "title": "NIST Guide to the SI — Appendix B, Conversion Factors",
                        "publisher": "National Institute of Standards and Technology",
                        "url": "https://www.nist.gov/pml/special-publication-811/nist-guide-si-appendix-b-conversion-factors",
                        "verifiedAt": "2026-08-20"
                    }
                ],
                "notes": [
                    "Hesap, geometrik ölçüler ile kullanıcının girdiği ürün değerlerini işleyen statik bir yöntemdir; dönemsel mevzuat veya tarife içermez.",
                    "Tüketim, verim, yoğunluk, paket ölçüsü, fire ve fiyat gibi ürüne özgü değerler kullanıcı girdisi veya formda açıkça gösterilen varsayılandır; config içinde dönemsel ürün verisi bulunmaz."
                ]
            }
        },
        "testVectors": {
            "kind": "home-renovation-oracle-v1",
            "cases": [
                {
                    "caseId": "positive-priced",
                    "scenario": "positive",
                    "input": {
                        "toolId": "duvar-kagidi-rulo",
                        "wallWidthM": 5,
                        "wallHeightM": 2.7,
                        "rollWidthCm": 53,
                        "rollLengthM": 10,
                        "patternRepeatCm": 32,
                        "wastePercent": 5,
                        "rollPrice": 850
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "stripCount": 10,
                            "baseStripLengthM": 2.8,
                            "stripLengthM": 2.88,
                            "totalStripLengthM": 28.8,
                            "rollCount": 4,
                            "purchasedLengthM": 40,
                            "estimatedCost": 3400
                        },
                        "absentFields": []
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "default-values",
                    "scenario": "default",
                    "input": {
                        "toolId": "duvar-kagidi-rulo",
                        "wallWidthM": 0.53,
                        "wallHeightM": 2.4,
                        "rollWidthCm": 53,
                        "rollLengthM": 10
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "wastePercent": 5,
                            "stripLengthM": 2.5,
                            "rollCount": 1,
                            "estimatedCost": null
                        },
                        "absentFields": []
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "boundary-one-roll",
                    "scenario": "boundary-lower",
                    "input": {
                        "toolId": "duvar-kagidi-rulo",
                        "wallWidthM": 1,
                        "wallHeightM": 1.9,
                        "rollWidthCm": 50,
                        "rollLengthM": 4,
                        "patternRepeatCm": 0,
                        "wastePercent": 0
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "stripCount": 2,
                            "rollCount": 1
                        },
                        "absentFields": []
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "boundary-two-rolls",
                    "scenario": "boundary-upper",
                    "input": {
                        "toolId": "duvar-kagidi-rulo",
                        "wallWidthM": 1.0001,
                        "wallHeightM": 1.9,
                        "rollWidthCm": 50,
                        "rollLengthM": 4,
                        "patternRepeatCm": 0,
                        "wastePercent": 0
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "stripCount": 3,
                            "rollCount": 2
                        },
                        "absentFields": []
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "price-absent",
                    "scenario": "price-absent",
                    "input": {
                        "toolId": "duvar-kagidi-rulo",
                        "wallWidthM": 5,
                        "wallHeightM": 2.7,
                        "rollWidthCm": 53,
                        "rollLengthM": 10,
                        "patternRepeatCm": 32,
                        "wastePercent": 5
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "estimatedCost": null
                        },
                        "absentFields": []
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "roll-shorter-than-strip",
                    "scenario": "adversarial",
                    "input": {
                        "toolId": "duvar-kagidi-rulo",
                        "wallWidthM": 0.5,
                        "wallHeightM": 1.9001,
                        "rollWidthCm": 50,
                        "rollLengthM": 2,
                        "patternRepeatCm": 0,
                        "wastePercent": 0
                    },
                    "expected": {
                        "ok": false,
                        "field": "rollLengthM",
                        "errorCode": "OUT_OF_RANGE"
                    },
                    "tolerance": 1e-10
                }
            ]
        },
        "lastReviewed": "2026-08-20"
    },
    {
        "toolId": "fayans-seramik",
        "slug": "fayans-seramik-hesaplama",
        "path": "/ev-tadilat/fayans-seramik-hesaplama",
        "category": {
            "id": "ev-tadilat",
            "label": "Ev ve Tadilat"
        },
        "classification": "timeless",
        "engineId": "home-renovation-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-08-20",
        "effectiveTo": null,
        "minAppVersion": "0.1.0",
        "updateCadence": {
            "mode": "method-change-only",
            "days": null,
            "reviewAfter": null,
            "specialMonths": [],
            "note": "Zamansız, derlenmiş geometri yöntemidir; ürün sarfiyatı, paket, fire ve fiyat değerleri config'e gömülmez."
        },
        "sources": [
            {
                "sourceId": "openstax-geometry-applications",
                "sourceGroupId": null,
                "title": "Prealgebra 2e — Solve Geometry Applications: Circles and Irregular Figures",
                "publisher": "OpenStax",
                "url": "https://openstax.org/books/prealgebra-2e/pages/9-5-solve-geometry-applications-circles-and-irregular-figures",
                "kind": "authoritative-education",
                "reviewedOn": "2026-08-20",
                "watchFields": [
                    "Dikdörtgen alanı, çevre ve ölçü birimi ilişkileri"
                ]
            },
            {
                "sourceId": "nist-si-conversion-factors",
                "sourceGroupId": null,
                "title": "NIST Guide to the SI — Appendix B, Conversion Factors",
                "publisher": "National Institute of Standards and Technology",
                "url": "https://www.nist.gov/pml/special-publication-811/nist-guide-si-appendix-b-conversion-factors",
                "kind": "official-government",
                "reviewedOn": "2026-08-20",
                "watchFields": [
                    "Metre, santimetre, milimetre ve alan birimi dönüşümleri"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "m",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": null,
                    "id": "areaWidthM",
                    "type": "number",
                    "label": "Alan eni",
                    "required": true,
                    "exclusiveMinimum": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "m",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": null,
                    "id": "areaLengthM",
                    "type": "number",
                    "label": "Alan boyu",
                    "required": true,
                    "exclusiveMinimum": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "cm",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": null,
                    "id": "tileWidthCm",
                    "type": "number",
                    "label": "Karo eni",
                    "required": true,
                    "exclusiveMinimum": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "cm",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": null,
                    "id": "tileHeightCm",
                    "type": "number",
                    "label": "Karo boyu",
                    "required": true,
                    "exclusiveMinimum": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "adet",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "tilesPerBox",
                    "type": "number",
                    "label": "Kutudaki karo",
                    "required": true,
                    "exclusiveMinimum": true
                },
                {
                    "defaultValue": 10,
                    "placeholder": null,
                    "unit": "%",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": null,
                    "id": "wastePercent",
                    "type": "number",
                    "label": "Fire oranı",
                    "required": false,
                    "exclusiveMinimum": false
                },
                {
                    "defaultValue": 0,
                    "placeholder": null,
                    "unit": "TL",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": null,
                    "id": "boxPrice",
                    "type": "number",
                    "label": "Kutu fiyatı",
                    "required": false,
                    "exclusiveMinimum": false
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "toolId",
                    "type": "text",
                    "label": "Araç",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "areaM2",
                    "type": "number",
                    "label": "Alan",
                    "unit": "m²",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "tileAreaM2",
                    "type": "number",
                    "label": "Karo alanı",
                    "unit": "m²",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "wastePercent",
                    "type": "percentage",
                    "label": "Fire",
                    "unit": "%",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "neededTileCount",
                    "type": "number",
                    "label": "Gerekli karo",
                    "unit": "adet",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "tilesPerBox",
                    "type": "number",
                    "label": "Kutudaki karo",
                    "unit": "adet",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "boxCount",
                    "type": "number",
                    "label": "Kutu sayısı",
                    "unit": "adet",
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "purchasedAreaM2",
                    "type": "number",
                    "label": "Satın alınan alan",
                    "unit": "m²",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "boxPrice",
                    "type": "money",
                    "label": "Kutu fiyatı",
                    "unit": "TL",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "estimatedCost",
                    "type": "money",
                    "label": "Tahmini maliyet",
                    "unit": "TL",
                    "nullable": true,
                    "prominent": true
                }
            ]
        },
        "content": {
            "title": "Fayans ve Seramik Hesaplama: Kaç Adet ve Kaç Kutu Gerekir?",
            "shortTitle": "Fayans ve Seramik",
            "summary": "Alan/karo ölçüsü, kutudaki adet ve fireyle tam karo ve kutu ihtiyacını hesaplar.",
            "method": "Alan karo alanına bölünüp fire eklenerek adet yukarı yuvarlanır; adet kutu içeriğine bölünüp tam kutuya yuvarlanır.",
            "limitations": [
                "Sonuç bir keşif/tahmin desteğidir; uygulama, kesim ve yüzey koşulları gerçek ihtiyacı değiştirebilir.",
                "Ürün sarfiyatı, paket içeriği, fire oranı ve fiyat varsa kullanıcı tarafından ürün etiketi veya teknik föye göre girilmelidir.",
                "Fiyat girilmediğinde araç maliyet tahmini üretmez; çekirdekte güncel veya dönemsel fiyat bulunmaz."
            ],
            "faqs": [
                {
                    "question": "Fayans ve Seramik hangi verileri kullanır?",
                    "answer": "Araç yalnız girilen ölçüleri ve formda açıkça görünen varsayılanları kullanır. Ürüne özgü sarfiyat, paket ve fiyat değerleri gerektiğinde kullanıcı girdisidir."
                }
            ]
        },
        "config": {
            "kind": "home-renovation-v1",
            "toolId": "fayans-seramik",
            "maxAbsoluteInput": 1000000000,
            "metadata": {
                "calculatorId": "fayans-seramik",
                "version": "1.0.0",
                "effectiveFrom": "2026-08-20",
                "lastVerifiedAt": "2026-08-20",
                "sources": [
                    {
                        "id": "openstax-geometry-applications",
                        "title": "Prealgebra 2e — Solve Geometry Applications: Circles and Irregular Figures",
                        "publisher": "OpenStax",
                        "url": "https://openstax.org/books/prealgebra-2e/pages/9-5-solve-geometry-applications-circles-and-irregular-figures",
                        "verifiedAt": "2026-08-20"
                    },
                    {
                        "id": "nist-si-conversion-factors",
                        "title": "NIST Guide to the SI — Appendix B, Conversion Factors",
                        "publisher": "National Institute of Standards and Technology",
                        "url": "https://www.nist.gov/pml/special-publication-811/nist-guide-si-appendix-b-conversion-factors",
                        "verifiedAt": "2026-08-20"
                    }
                ],
                "notes": [
                    "Hesap, geometrik ölçüler ile kullanıcının girdiği ürün değerlerini işleyen statik bir yöntemdir; dönemsel mevzuat veya tarife içermez.",
                    "Tüketim, verim, yoğunluk, paket ölçüsü, fire ve fiyat gibi ürüne özgü değerler kullanıcı girdisi veya formda açıkça gösterilen varsayılandır; config içinde dönemsel ürün verisi bulunmaz."
                ]
            }
        },
        "testVectors": {
            "kind": "home-renovation-oracle-v1",
            "cases": [
                {
                    "caseId": "positive-priced",
                    "scenario": "positive",
                    "input": {
                        "toolId": "fayans-seramik",
                        "areaWidthM": 3,
                        "areaLengthM": 2,
                        "tileWidthCm": 50,
                        "tileHeightCm": 50,
                        "tilesPerBox": 4,
                        "wastePercent": 10,
                        "boxPrice": 420
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "areaM2": 6,
                            "tileAreaM2": 0.25,
                            "neededTileCount": 27,
                            "boxCount": 7,
                            "purchasedAreaM2": 7,
                            "estimatedCost": 2940
                        },
                        "absentFields": []
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "default-values",
                    "scenario": "default",
                    "input": {
                        "toolId": "fayans-seramik",
                        "areaWidthM": 1,
                        "areaLengthM": 1,
                        "tileWidthCm": 50,
                        "tileHeightCm": 50,
                        "tilesPerBox": 4
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "wastePercent": 10,
                            "neededTileCount": 5,
                            "boxCount": 2,
                            "estimatedCost": null
                        },
                        "absentFields": []
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "boundary-one-box",
                    "scenario": "boundary-lower",
                    "input": {
                        "toolId": "fayans-seramik",
                        "areaWidthM": 1,
                        "areaLengthM": 1,
                        "tileWidthCm": 100,
                        "tileHeightCm": 100,
                        "tilesPerBox": 1,
                        "wastePercent": 0
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "boxCount": 1
                        },
                        "absentFields": []
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "boundary-two-boxes",
                    "scenario": "boundary-upper",
                    "input": {
                        "toolId": "fayans-seramik",
                        "areaWidthM": 1,
                        "areaLengthM": 1.0001,
                        "tileWidthCm": 100,
                        "tileHeightCm": 100,
                        "tilesPerBox": 1,
                        "wastePercent": 0
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "boxCount": 2
                        },
                        "absentFields": []
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "price-absent",
                    "scenario": "price-absent",
                    "input": {
                        "toolId": "fayans-seramik",
                        "areaWidthM": 3,
                        "areaLengthM": 2,
                        "tileWidthCm": 50,
                        "tileHeightCm": 50,
                        "tilesPerBox": 4,
                        "wastePercent": 10
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "estimatedCost": null
                        },
                        "absentFields": []
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "zero-area-width",
                    "scenario": "adversarial",
                    "input": {
                        "toolId": "fayans-seramik",
                        "areaWidthM": 0,
                        "areaLengthM": 1,
                        "tileWidthCm": 50,
                        "tileHeightCm": 50,
                        "tilesPerBox": 4
                    },
                    "expected": {
                        "ok": false,
                        "field": "areaWidthM",
                        "errorCode": "OUT_OF_RANGE"
                    },
                    "tolerance": 1e-10
                }
            ]
        },
        "lastReviewed": "2026-08-20"
    },
    {
        "toolId": "curtain-fabric",
        "slug": "perde-kumasi-metresi-hesaplama",
        "path": "/ev-tadilat/perde-kumasi-metresi-hesaplama",
        "category": {
            "id": "ev-tadilat",
            "label": "Ev ve Tadilat"
        },
        "classification": "timeless",
        "engineId": "curtain-fabric-v2",
        "schemaVersion": "2.0.0",
        "contentVersion": "2.0.0",
        "configVersion": "2.0.0",
        "effectiveFrom": "2026-08-22",
        "effectiveTo": null,
        "minAppVersion": "0.1.0",
        "updateCadence": {
            "mode": "method-change-only",
            "days": null,
            "reviewAfter": null,
            "specialMonths": [],
            "note": "Zamansız, derlenmiş geometri yöntemidir; ürün sarfiyatı, paket, fire ve fiyat değerleri config'e gömülmez."
        },
        "sources": [
            {
                "sourceId": "openstax-geometry-applications",
                "sourceGroupId": null,
                "title": "Prealgebra 2e — Solve Geometry Applications: Circles and Irregular Figures",
                "publisher": "OpenStax",
                "url": "https://openstax.org/books/prealgebra-2e/pages/9-5-solve-geometry-applications-circles-and-irregular-figures",
                "kind": "authoritative-education",
                "reviewedOn": "2026-08-22",
                "watchFields": [
                    "Dikdörtgen alanı, çevre ve ölçü birimi ilişkileri"
                ]
            },
            {
                "sourceId": "nist-si-conversion-factors",
                "sourceGroupId": null,
                "title": "NIST Guide to the SI — Appendix B, Conversion Factors",
                "publisher": "National Institute of Standards and Technology",
                "url": "https://www.nist.gov/pml/special-publication-811/nist-guide-si-appendix-b-conversion-factors",
                "kind": "official-government",
                "reviewedOn": "2026-08-22",
                "watchFields": [
                    "Metre, santimetre, milimetre ve alan birimi dönüşümleri"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": "tul",
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "tul",
                            "label": "Tül perde"
                        },
                        {
                            "value": "fon",
                            "label": "Fon perde"
                        }
                    ],
                    "visibleWhen": null,
                    "id": "mode",
                    "type": "select",
                    "label": "Perde türü",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "cm",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": {
                        "fieldId": "mode",
                        "equals": "tul"
                    },
                    "id": "windowWidthCm",
                    "type": "number",
                    "label": "Pencere eni",
                    "required": true,
                    "exclusiveMinimum": true
                },
                {
                    "defaultValue": 2.5,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": 1,
                            "label": "1×"
                        },
                        {
                            "value": 2,
                            "label": "2×"
                        },
                        {
                            "value": 2.5,
                            "label": "2,5×"
                        },
                        {
                            "value": 3,
                            "label": "3×"
                        }
                    ],
                    "visibleWhen": {
                        "fieldId": "mode",
                        "equals": "tul"
                    },
                    "id": "tulleFullness",
                    "type": "select",
                    "label": "Tül pile oranı",
                    "required": false
                },
                {
                    "defaultValue": 20,
                    "placeholder": null,
                    "unit": "cm",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": {
                        "fieldId": "mode",
                        "equals": "tul"
                    },
                    "id": "sideAllowanceCm",
                    "type": "number",
                    "label": "Yan pay",
                    "required": false,
                    "exclusiveMinimum": false
                },
                {
                    "defaultValue": 20,
                    "placeholder": null,
                    "unit": "cm",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": {
                        "fieldId": "mode",
                        "equals": "tul"
                    },
                    "id": "tulleHemAllowanceCm",
                    "type": "number",
                    "label": "Tül kıvırma payı",
                    "required": false,
                    "exclusiveMinimum": false
                },
                {
                    "defaultValue": 2,
                    "placeholder": null,
                    "unit": "adet",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": {
                        "fieldId": "mode",
                        "equals": "fon"
                    },
                    "id": "panelCount",
                    "type": "number",
                    "label": "Panel sayısı",
                    "required": false,
                    "exclusiveMinimum": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "cm",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": {
                        "fieldId": "mode",
                        "equals": "fon"
                    },
                    "id": "finishedPanelWidthCm",
                    "type": "number",
                    "label": "Bitmiş panel eni",
                    "required": true,
                    "exclusiveMinimum": true
                },
                {
                    "defaultValue": 2,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": 1,
                            "label": "1×"
                        },
                        {
                            "value": 1.5,
                            "label": "1,5×"
                        },
                        {
                            "value": 2,
                            "label": "2×"
                        }
                    ],
                    "visibleWhen": {
                        "fieldId": "mode",
                        "equals": "fon"
                    },
                    "id": "draperyFullness",
                    "type": "select",
                    "label": "Fon pile oranı",
                    "required": false
                },
                {
                    "defaultValue": 25,
                    "placeholder": null,
                    "unit": "cm",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": {
                        "fieldId": "mode",
                        "equals": "fon"
                    },
                    "id": "draperyHemAllowanceCm",
                    "type": "number",
                    "label": "Fon kıvırma payı",
                    "required": false,
                    "exclusiveMinimum": false
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "cm",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": null,
                    "id": "curtainHeightCm",
                    "type": "number",
                    "label": "Perde yüksekliği",
                    "required": true,
                    "exclusiveMinimum": true
                },
                {
                    "defaultValue": 0,
                    "placeholder": null,
                    "unit": "TL/m",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": null,
                    "id": "metrePrice",
                    "type": "number",
                    "label": "Metre fiyatı",
                    "required": false,
                    "exclusiveMinimum": false
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "toolId",
                    "type": "text",
                    "label": "Araç",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "mode",
                    "type": "text",
                    "label": "Perde türü",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "panelCount",
                    "type": "number",
                    "label": "Panel sayısı",
                    "unit": "adet",
                    "nullable": true,
                    "prominent": false
                },
                {
                    "id": "finishedWidthCm",
                    "type": "number",
                    "label": "Bitmiş toplam en",
                    "unit": "cm",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "fullness",
                    "type": "number",
                    "label": "Pile oranı",
                    "unit": "×",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "fabricLengthM",
                    "type": "number",
                    "label": "Kumaş uzunluğu",
                    "unit": "m",
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "cutHeightM",
                    "type": "number",
                    "label": "Kesim yüksekliği",
                    "unit": "m",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "fabricAreaM2",
                    "type": "number",
                    "label": "Kumaş alanı",
                    "unit": "m²",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "metrePrice",
                    "type": "money",
                    "label": "Metre fiyatı",
                    "unit": "TL/m",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "estimatedCost",
                    "type": "money",
                    "label": "Tahmini maliyet",
                    "unit": "TL",
                    "nullable": true,
                    "prominent": true
                }
            ]
        },
        "content": {
            "title": "Tül ve Fon Perde Kumaşı Metresi Hesaplama",
            "shortTitle": "Perde Kumaşı Metresi",
            "summary": "Tül veya fon perde moduna göre yalnız ilgili ölçüleri açar; pile oranı, kesim payı ve isteğe bağlı fiyatla kumaş ihtiyacını hesaplar.",
            "method": "Tül modunda pencere eni pile oranıyla çarpılıp yan pay eklenir. Fon modunda panel sayısı ve bitmiş panel eni pile oranıyla çarpılır. Her iki modda yüksekliğe kendi kıvırma payı eklenir.",
            "limitations": [
                "Sonuç bir keşif/tahmin desteğidir; uygulama, kesim ve yüzey koşulları gerçek ihtiyacı değiştirebilir.",
                "Ürün sarfiyatı, paket içeriği, fire oranı ve fiyat varsa kullanıcı tarafından ürün etiketi veya teknik föye göre girilmelidir.",
                "Fiyat girilmediğinde araç maliyet tahmini üretmez; çekirdekte güncel veya dönemsel fiyat bulunmaz."
            ],
            "faqs": [
                {
                    "question": "Perde Kumaşı Metresi hangi verileri kullanır?",
                    "answer": "Araç yalnız girilen ölçüleri ve formda açıkça görünen varsayılanları kullanır. Ürüne özgü sarfiyat, paket ve fiyat değerleri gerektiğinde kullanıcı girdisidir."
                }
            ]
        },
        "config": {
            "kind": "curtain-fabric-v2",
            "toolId": "curtain-fabric",
            "maxAbsoluteInput": 1000000000,
            "supportedModes": [
                "tul",
                "fon"
            ],
            "metadata": {
                "calculatorId": "curtain-fabric",
                "version": "2.0.0",
                "effectiveFrom": "2026-08-22",
                "lastVerifiedAt": "2026-08-22",
                "sources": [
                    {
                        "id": "openstax-geometry-applications",
                        "title": "Prealgebra 2e — Solve Geometry Applications: Circles and Irregular Figures",
                        "publisher": "OpenStax",
                        "url": "https://openstax.org/books/prealgebra-2e/pages/9-5-solve-geometry-applications-circles-and-irregular-figures",
                        "verifiedAt": "2026-08-22"
                    },
                    {
                        "id": "nist-si-conversion-factors",
                        "title": "NIST Guide to the SI — Appendix B, Conversion Factors",
                        "publisher": "National Institute of Standards and Technology",
                        "url": "https://www.nist.gov/pml/special-publication-811/nist-guide-si-appendix-b-conversion-factors",
                        "verifiedAt": "2026-08-22"
                    }
                ],
                "notes": [
                    "Hesap, geometrik ölçüler ile kullanıcının girdiği ürün değerlerini işleyen statik bir yöntemdir; dönemsel mevzuat veya tarife içermez.",
                    "Tüketim, verim, yoğunluk, paket ölçüsü, fire ve fiyat gibi ürüne özgü değerler kullanıcı girdisi veya formda açıkça gösterilen varsayılandır; config içinde dönemsel ürün verisi bulunmaz."
                ]
            }
        },
        "testVectors": {
            "kind": "curtain-fabric-oracle-v2",
            "cases": [
                {
                    "caseId": "tul-positive-priced",
                    "scenario": "positive",
                    "input": {
                        "toolId": "curtain-fabric",
                        "mode": "tul",
                        "windowWidthCm": 300,
                        "curtainHeightCm": 260,
                        "tulleFullness": 2.5,
                        "sideAllowanceCm": 20,
                        "tulleHemAllowanceCm": 20,
                        "metrePrice": 180
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "mode": "tul",
                            "panelCount": null,
                            "finishedWidthCm": 300,
                            "fullness": 2.5,
                            "fabricLengthM": 7.7,
                            "cutHeightM": 2.8,
                            "fabricAreaM2": 21.56,
                            "estimatedCost": 1386
                        },
                        "absentFields": []
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "tul-default-values",
                    "scenario": "default",
                    "input": {
                        "toolId": "curtain-fabric",
                        "mode": "tul",
                        "windowWidthCm": 100,
                        "curtainHeightCm": 100
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "panelCount": null,
                            "fullness": 2.5,
                            "fabricLengthM": 2.7,
                            "cutHeightM": 1.2,
                            "estimatedCost": null
                        },
                        "absentFields": []
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "tul-boundary-single",
                    "scenario": "boundary-lower",
                    "input": {
                        "toolId": "curtain-fabric",
                        "mode": "tul",
                        "windowWidthCm": 100,
                        "curtainHeightCm": 100,
                        "tulleFullness": 1,
                        "sideAllowanceCm": 0,
                        "tulleHemAllowanceCm": 0
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "fabricLengthM": 1
                        },
                        "absentFields": []
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "tul-boundary-triple",
                    "scenario": "boundary-upper",
                    "input": {
                        "toolId": "curtain-fabric",
                        "mode": "tul",
                        "windowWidthCm": 100,
                        "curtainHeightCm": 100,
                        "tulleFullness": 3,
                        "sideAllowanceCm": 0,
                        "tulleHemAllowanceCm": 0
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "fabricLengthM": 3
                        },
                        "absentFields": []
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "fon-positive-priced",
                    "scenario": "positive",
                    "input": {
                        "toolId": "curtain-fabric",
                        "mode": "fon",
                        "panelCount": 2,
                        "finishedPanelWidthCm": 80,
                        "draperyFullness": 2,
                        "curtainHeightCm": 260,
                        "draperyHemAllowanceCm": 25,
                        "metrePrice": 350
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "mode": "fon",
                            "panelCount": 2,
                            "finishedWidthCm": 160,
                            "fullness": 2,
                            "fabricLengthM": 3.2,
                            "cutHeightM": 2.85,
                            "fabricAreaM2": 9.12,
                            "estimatedCost": 1120
                        },
                        "absentFields": []
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "fon-default-values",
                    "scenario": "default",
                    "input": {
                        "toolId": "curtain-fabric",
                        "mode": "fon",
                        "finishedPanelWidthCm": 50,
                        "curtainHeightCm": 100
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "panelCount": 2,
                            "fullness": 2,
                            "fabricLengthM": 2,
                            "cutHeightM": 1.25,
                            "estimatedCost": null
                        },
                        "absentFields": []
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "fon-boundary-single",
                    "scenario": "boundary-lower",
                    "input": {
                        "toolId": "curtain-fabric",
                        "mode": "fon",
                        "panelCount": 1,
                        "finishedPanelWidthCm": 100,
                        "draperyFullness": 1,
                        "curtainHeightCm": 100,
                        "draperyHemAllowanceCm": 0
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "fabricLengthM": 1
                        },
                        "absentFields": []
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "fon-boundary-double",
                    "scenario": "boundary-upper",
                    "input": {
                        "toolId": "curtain-fabric",
                        "mode": "fon",
                        "panelCount": 1,
                        "finishedPanelWidthCm": 100,
                        "draperyFullness": 2,
                        "curtainHeightCm": 100,
                        "draperyHemAllowanceCm": 0
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "fabricLengthM": 2
                        },
                        "absentFields": []
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "price-absent",
                    "scenario": "price-absent",
                    "input": {
                        "toolId": "curtain-fabric",
                        "mode": "fon",
                        "panelCount": 2,
                        "finishedPanelWidthCm": 80,
                        "draperyFullness": 2,
                        "curtainHeightCm": 260,
                        "draperyHemAllowanceCm": 25
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "estimatedCost": null
                        },
                        "absentFields": []
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "unsupported-fon-fullness",
                    "scenario": "adversarial",
                    "input": {
                        "toolId": "curtain-fabric",
                        "mode": "fon",
                        "finishedPanelWidthCm": 100,
                        "curtainHeightCm": 100,
                        "draperyFullness": 3
                    },
                    "expected": {
                        "ok": false,
                        "field": "draperyFullness",
                        "errorCode": "UNSUPPORTED_VALUE"
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "zero-tul-width",
                    "scenario": "adversarial",
                    "input": {
                        "toolId": "curtain-fabric",
                        "mode": "tul",
                        "windowWidthCm": 0,
                        "curtainHeightCm": 100
                    },
                    "expected": {
                        "ok": false,
                        "field": "windowWidthCm",
                        "errorCode": "OUT_OF_RANGE"
                    },
                    "tolerance": 1e-10
                }
            ]
        },
        "lastReviewed": "2026-08-22"
    },
    {
        "toolId": "hali-metrekare",
        "slug": "hali-metrekare-hesaplama",
        "path": "/ev-tadilat/hali-metrekare-hesaplama",
        "category": {
            "id": "ev-tadilat",
            "label": "Ev ve Tadilat"
        },
        "classification": "timeless",
        "engineId": "home-renovation-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-08-20",
        "effectiveTo": null,
        "minAppVersion": "0.1.0",
        "updateCadence": {
            "mode": "method-change-only",
            "days": null,
            "reviewAfter": null,
            "specialMonths": [],
            "note": "Zamansız, derlenmiş geometri yöntemidir; ürün sarfiyatı, paket, fire ve fiyat değerleri config'e gömülmez."
        },
        "sources": [
            {
                "sourceId": "openstax-geometry-applications",
                "sourceGroupId": null,
                "title": "Prealgebra 2e — Solve Geometry Applications: Circles and Irregular Figures",
                "publisher": "OpenStax",
                "url": "https://openstax.org/books/prealgebra-2e/pages/9-5-solve-geometry-applications-circles-and-irregular-figures",
                "kind": "authoritative-education",
                "reviewedOn": "2026-08-20",
                "watchFields": [
                    "Dikdörtgen alanı, çevre ve ölçü birimi ilişkileri"
                ]
            },
            {
                "sourceId": "nist-si-conversion-factors",
                "sourceGroupId": null,
                "title": "NIST Guide to the SI — Appendix B, Conversion Factors",
                "publisher": "National Institute of Standards and Technology",
                "url": "https://www.nist.gov/pml/special-publication-811/nist-guide-si-appendix-b-conversion-factors",
                "kind": "official-government",
                "reviewedOn": "2026-08-20",
                "watchFields": [
                    "Metre, santimetre, milimetre ve alan birimi dönüşümleri"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "cm",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": null,
                    "id": "widthCm",
                    "type": "number",
                    "label": "Halı eni",
                    "required": true,
                    "exclusiveMinimum": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "cm",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": null,
                    "id": "lengthCm",
                    "type": "number",
                    "label": "Halı boyu",
                    "required": true,
                    "exclusiveMinimum": true
                },
                {
                    "defaultValue": 0,
                    "placeholder": null,
                    "unit": "TL/m²",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": null,
                    "id": "pricePerM2",
                    "type": "number",
                    "label": "m² fiyatı",
                    "required": false,
                    "exclusiveMinimum": false
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "toolId",
                    "type": "text",
                    "label": "Araç",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "areaM2",
                    "type": "number",
                    "label": "Alan",
                    "unit": "m²",
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "perimeterM",
                    "type": "number",
                    "label": "Çevre",
                    "unit": "m",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "pricePerM2",
                    "type": "money",
                    "label": "m² fiyatı",
                    "unit": "TL/m²",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "estimatedCost",
                    "type": "money",
                    "label": "Tahmini maliyet",
                    "unit": "TL",
                    "nullable": true,
                    "prominent": true
                }
            ]
        },
        "content": {
            "title": "Halı Metrekare ve Fiyat Hesaplama Aracı",
            "shortTitle": "Halı Metrekare",
            "summary": "Santimetre cinsinden halı eni ve boyundan alanı, çevreyi ve fiyat girilirse tahmini maliyeti hesaplar.",
            "method": "Alan en × boy / 10.000, çevre 2 × (en + boy) / 100 bağıntısıyla bulunur.",
            "limitations": [
                "Sonuç bir keşif/tahmin desteğidir; uygulama, kesim ve yüzey koşulları gerçek ihtiyacı değiştirebilir.",
                "Ürün sarfiyatı, paket içeriği, fire oranı ve fiyat varsa kullanıcı tarafından ürün etiketi veya teknik föye göre girilmelidir.",
                "Fiyat girilmediğinde araç maliyet tahmini üretmez; çekirdekte güncel veya dönemsel fiyat bulunmaz."
            ],
            "faqs": [
                {
                    "question": "Halı Metrekare hangi verileri kullanır?",
                    "answer": "Araç yalnız girilen ölçüleri ve formda açıkça görünen varsayılanları kullanır. Ürüne özgü sarfiyat, paket ve fiyat değerleri gerektiğinde kullanıcı girdisidir."
                }
            ]
        },
        "config": {
            "kind": "home-renovation-v1",
            "toolId": "hali-metrekare",
            "maxAbsoluteInput": 1000000000,
            "metadata": {
                "calculatorId": "hali-metrekare",
                "version": "1.0.0",
                "effectiveFrom": "2026-08-20",
                "lastVerifiedAt": "2026-08-20",
                "sources": [
                    {
                        "id": "openstax-geometry-applications",
                        "title": "Prealgebra 2e — Solve Geometry Applications: Circles and Irregular Figures",
                        "publisher": "OpenStax",
                        "url": "https://openstax.org/books/prealgebra-2e/pages/9-5-solve-geometry-applications-circles-and-irregular-figures",
                        "verifiedAt": "2026-08-20"
                    },
                    {
                        "id": "nist-si-conversion-factors",
                        "title": "NIST Guide to the SI — Appendix B, Conversion Factors",
                        "publisher": "National Institute of Standards and Technology",
                        "url": "https://www.nist.gov/pml/special-publication-811/nist-guide-si-appendix-b-conversion-factors",
                        "verifiedAt": "2026-08-20"
                    }
                ],
                "notes": [
                    "Hesap, geometrik ölçüler ile kullanıcının girdiği ürün değerlerini işleyen statik bir yöntemdir; dönemsel mevzuat veya tarife içermez.",
                    "Tüketim, verim, yoğunluk, paket ölçüsü, fire ve fiyat gibi ürüne özgü değerler kullanıcı girdisi veya formda açıkça gösterilen varsayılandır; config içinde dönemsel ürün verisi bulunmaz."
                ]
            }
        },
        "testVectors": {
            "kind": "home-renovation-oracle-v1",
            "cases": [
                {
                    "caseId": "positive-priced",
                    "scenario": "positive",
                    "input": {
                        "toolId": "hali-metrekare",
                        "widthCm": 160,
                        "lengthCm": 230,
                        "pricePerM2": 900
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "areaM2": 3.68,
                            "perimeterM": 7.8,
                            "estimatedCost": 3312
                        },
                        "absentFields": []
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "default-price",
                    "scenario": "default",
                    "input": {
                        "toolId": "hali-metrekare",
                        "widthCm": 100,
                        "lengthCm": 100
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "areaM2": 1,
                            "perimeterM": 4,
                            "estimatedCost": null
                        },
                        "absentFields": []
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "boundary-square",
                    "scenario": "boundary-lower",
                    "input": {
                        "toolId": "hali-metrekare",
                        "widthCm": 100,
                        "lengthCm": 100
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "areaM2": 1,
                            "perimeterM": 4
                        },
                        "absentFields": []
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "boundary-shorter",
                    "scenario": "boundary-upper",
                    "input": {
                        "toolId": "hali-metrekare",
                        "widthCm": 100,
                        "lengthCm": 99
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "areaM2": 0.99,
                            "perimeterM": 3.98
                        },
                        "absentFields": []
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "price-absent",
                    "scenario": "price-absent",
                    "input": {
                        "toolId": "hali-metrekare",
                        "widthCm": 160,
                        "lengthCm": 230
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "estimatedCost": null
                        },
                        "absentFields": []
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "zero-width",
                    "scenario": "adversarial",
                    "input": {
                        "toolId": "hali-metrekare",
                        "widthCm": 0,
                        "lengthCm": 100
                    },
                    "expected": {
                        "ok": false,
                        "field": "widthCm",
                        "errorCode": "OUT_OF_RANGE"
                    },
                    "tolerance": 1e-10
                }
            ]
        },
        "lastReviewed": "2026-08-20"
    },
    {
        "toolId": "kutu-paket-sayisi",
        "slug": "kutu-paket-sayisi-hesaplama",
        "path": "/ev-tadilat/kutu-paket-sayisi-hesaplama",
        "category": {
            "id": "ev-tadilat",
            "label": "Ev ve Tadilat"
        },
        "classification": "timeless",
        "engineId": "home-renovation-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-08-20",
        "effectiveTo": null,
        "minAppVersion": "0.1.0",
        "updateCadence": {
            "mode": "method-change-only",
            "days": null,
            "reviewAfter": null,
            "specialMonths": [],
            "note": "Zamansız, derlenmiş geometri yöntemidir; ürün sarfiyatı, paket, fire ve fiyat değerleri config'e gömülmez."
        },
        "sources": [
            {
                "sourceId": "openstax-geometry-applications",
                "sourceGroupId": null,
                "title": "Prealgebra 2e — Solve Geometry Applications: Circles and Irregular Figures",
                "publisher": "OpenStax",
                "url": "https://openstax.org/books/prealgebra-2e/pages/9-5-solve-geometry-applications-circles-and-irregular-figures",
                "kind": "authoritative-education",
                "reviewedOn": "2026-08-20",
                "watchFields": [
                    "Dikdörtgen alanı, çevre ve ölçü birimi ilişkileri"
                ]
            },
            {
                "sourceId": "nist-si-conversion-factors",
                "sourceGroupId": null,
                "title": "NIST Guide to the SI — Appendix B, Conversion Factors",
                "publisher": "National Institute of Standards and Technology",
                "url": "https://www.nist.gov/pml/special-publication-811/nist-guide-si-appendix-b-conversion-factors",
                "kind": "official-government",
                "reviewedOn": "2026-08-20",
                "watchFields": [
                    "Metre, santimetre, milimetre ve alan birimi dönüşümleri"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "m²",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": null,
                    "id": "netNeedM2",
                    "type": "number",
                    "label": "Net ihtiyaç",
                    "required": true,
                    "exclusiveMinimum": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "m²",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": null,
                    "id": "packageAreaM2",
                    "type": "number",
                    "label": "Paket kapsamı",
                    "required": true,
                    "exclusiveMinimum": true
                },
                {
                    "defaultValue": 10,
                    "placeholder": null,
                    "unit": "%",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": null,
                    "id": "wastePercent",
                    "type": "number",
                    "label": "Fire oranı",
                    "required": false,
                    "exclusiveMinimum": false
                },
                {
                    "defaultValue": 0,
                    "placeholder": null,
                    "unit": "TL",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": null,
                    "id": "packagePrice",
                    "type": "number",
                    "label": "Paket fiyatı",
                    "required": false,
                    "exclusiveMinimum": false
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "toolId",
                    "type": "text",
                    "label": "Araç",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "netNeedM2",
                    "type": "number",
                    "label": "Net ihtiyaç",
                    "unit": "m²",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "wastePercent",
                    "type": "percentage",
                    "label": "Fire",
                    "unit": "%",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "grossNeedM2",
                    "type": "number",
                    "label": "Fireli ihtiyaç",
                    "unit": "m²",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "packageAreaM2",
                    "type": "number",
                    "label": "Paket kapsamı",
                    "unit": "m²",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "packageCount",
                    "type": "number",
                    "label": "Paket sayısı",
                    "unit": "adet",
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "purchasedAreaM2",
                    "type": "number",
                    "label": "Satın alınan alan",
                    "unit": "m²",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "excessAreaM2",
                    "type": "number",
                    "label": "Fazla alan",
                    "unit": "m²",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "packagePrice",
                    "type": "money",
                    "label": "Paket fiyatı",
                    "unit": "TL",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "estimatedCost",
                    "type": "money",
                    "label": "Tahmini maliyet",
                    "unit": "TL",
                    "nullable": true,
                    "prominent": true
                }
            ]
        },
        "content": {
            "title": "Kutu/Paket Sayısı ve Artan Alan",
            "shortTitle": "Kutu/Paket ve Artan Alan",
            "summary": "Net m², fire oranı ve paket kapasitesinden tam paket sayısını, satın alınan alanı ve artan alanı hesaplar.",
            "method": "Net alana fire eklenir; sonuç paket alanına bölünüp yukarı yuvarlanır, satın alınan ve fazla alan ayrıca gösterilir.",
            "limitations": [
                "Sonuç bir keşif/tahmin desteğidir; uygulama, kesim ve yüzey koşulları gerçek ihtiyacı değiştirebilir.",
                "Ürün sarfiyatı, paket içeriği, fire oranı ve fiyat varsa kullanıcı tarafından ürün etiketi veya teknik föye göre girilmelidir.",
                "Fiyat girilmediğinde araç maliyet tahmini üretmez; çekirdekte güncel veya dönemsel fiyat bulunmaz."
            ],
            "faqs": [
                {
                    "question": "Kutu/Paket ve Artan Alan hangi verileri kullanır?",
                    "answer": "Araç yalnız girilen ölçüleri ve formda açıkça görünen varsayılanları kullanır. Ürüne özgü sarfiyat, paket ve fiyat değerleri gerektiğinde kullanıcı girdisidir."
                }
            ]
        },
        "config": {
            "kind": "home-renovation-v1",
            "toolId": "kutu-paket-sayisi",
            "maxAbsoluteInput": 1000000000,
            "metadata": {
                "calculatorId": "kutu-paket-sayisi",
                "version": "1.0.0",
                "effectiveFrom": "2026-08-20",
                "lastVerifiedAt": "2026-08-20",
                "sources": [
                    {
                        "id": "openstax-geometry-applications",
                        "title": "Prealgebra 2e — Solve Geometry Applications: Circles and Irregular Figures",
                        "publisher": "OpenStax",
                        "url": "https://openstax.org/books/prealgebra-2e/pages/9-5-solve-geometry-applications-circles-and-irregular-figures",
                        "verifiedAt": "2026-08-20"
                    },
                    {
                        "id": "nist-si-conversion-factors",
                        "title": "NIST Guide to the SI — Appendix B, Conversion Factors",
                        "publisher": "National Institute of Standards and Technology",
                        "url": "https://www.nist.gov/pml/special-publication-811/nist-guide-si-appendix-b-conversion-factors",
                        "verifiedAt": "2026-08-20"
                    }
                ],
                "notes": [
                    "Hesap, geometrik ölçüler ile kullanıcının girdiği ürün değerlerini işleyen statik bir yöntemdir; dönemsel mevzuat veya tarife içermez.",
                    "Tüketim, verim, yoğunluk, paket ölçüsü, fire ve fiyat gibi ürüne özgü değerler kullanıcı girdisi veya formda açıkça gösterilen varsayılandır; config içinde dönemsel ürün verisi bulunmaz."
                ]
            }
        },
        "testVectors": {
            "kind": "home-renovation-oracle-v1",
            "cases": [
                {
                    "caseId": "positive-priced",
                    "scenario": "positive",
                    "input": {
                        "toolId": "kutu-paket-sayisi",
                        "netNeedM2": 18,
                        "packageAreaM2": 1.84,
                        "wastePercent": 10,
                        "packagePrice": 650
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "grossNeedM2": 19.8,
                            "packageCount": 11,
                            "purchasedAreaM2": 20.24,
                            "excessAreaM2": 0.44,
                            "estimatedCost": 7150
                        },
                        "absentFields": []
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "default-values",
                    "scenario": "default",
                    "input": {
                        "toolId": "kutu-paket-sayisi",
                        "netNeedM2": 1,
                        "packageAreaM2": 1
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "wastePercent": 10,
                            "grossNeedM2": 1.1,
                            "packageCount": 2,
                            "estimatedCost": null
                        },
                        "absentFields": []
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "boundary-one-package",
                    "scenario": "boundary-lower",
                    "input": {
                        "toolId": "kutu-paket-sayisi",
                        "netNeedM2": 2,
                        "packageAreaM2": 2,
                        "wastePercent": 0
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "packageCount": 1
                        },
                        "absentFields": []
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "boundary-two-packages",
                    "scenario": "boundary-upper",
                    "input": {
                        "toolId": "kutu-paket-sayisi",
                        "netNeedM2": 2.0001,
                        "packageAreaM2": 2,
                        "wastePercent": 0
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "packageCount": 2
                        },
                        "absentFields": []
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "price-absent",
                    "scenario": "price-absent",
                    "input": {
                        "toolId": "kutu-paket-sayisi",
                        "netNeedM2": 18,
                        "packageAreaM2": 1.84,
                        "wastePercent": 10
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "estimatedCost": null
                        },
                        "absentFields": []
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "zero-net-need",
                    "scenario": "adversarial",
                    "input": {
                        "toolId": "kutu-paket-sayisi",
                        "netNeedM2": 0,
                        "packageAreaM2": 1
                    },
                    "expected": {
                        "ok": false,
                        "field": "netNeedM2",
                        "errorCode": "OUT_OF_RANGE"
                    },
                    "tolerance": 1e-10
                }
            ]
        },
        "lastReviewed": "2026-08-20"
    },
    {
        "toolId": "laminat-parke",
        "slug": "laminat-parke-hesaplama",
        "path": "/ev-tadilat/laminat-parke-hesaplama",
        "category": {
            "id": "ev-tadilat",
            "label": "Ev ve Tadilat"
        },
        "classification": "timeless",
        "engineId": "home-renovation-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-08-20",
        "effectiveTo": null,
        "minAppVersion": "0.1.0",
        "updateCadence": {
            "mode": "method-change-only",
            "days": null,
            "reviewAfter": null,
            "specialMonths": [],
            "note": "Zamansız, derlenmiş geometri yöntemidir; ürün sarfiyatı, paket, fire ve fiyat değerleri config'e gömülmez."
        },
        "sources": [
            {
                "sourceId": "openstax-geometry-applications",
                "sourceGroupId": null,
                "title": "Prealgebra 2e — Solve Geometry Applications: Circles and Irregular Figures",
                "publisher": "OpenStax",
                "url": "https://openstax.org/books/prealgebra-2e/pages/9-5-solve-geometry-applications-circles-and-irregular-figures",
                "kind": "authoritative-education",
                "reviewedOn": "2026-08-20",
                "watchFields": [
                    "Dikdörtgen alanı, çevre ve ölçü birimi ilişkileri"
                ]
            },
            {
                "sourceId": "nist-si-conversion-factors",
                "sourceGroupId": null,
                "title": "NIST Guide to the SI — Appendix B, Conversion Factors",
                "publisher": "National Institute of Standards and Technology",
                "url": "https://www.nist.gov/pml/special-publication-811/nist-guide-si-appendix-b-conversion-factors",
                "kind": "official-government",
                "reviewedOn": "2026-08-20",
                "watchFields": [
                    "Metre, santimetre, milimetre ve alan birimi dönüşümleri"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "m",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": null,
                    "id": "roomWidthM",
                    "type": "number",
                    "label": "Oda eni",
                    "required": true,
                    "exclusiveMinimum": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "m",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": null,
                    "id": "roomLengthM",
                    "type": "number",
                    "label": "Oda boyu",
                    "required": true,
                    "exclusiveMinimum": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "m²",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": null,
                    "id": "packageAreaM2",
                    "type": "number",
                    "label": "Paket kapsamı",
                    "required": true,
                    "exclusiveMinimum": true
                },
                {
                    "defaultValue": 10,
                    "placeholder": null,
                    "unit": "%",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": null,
                    "id": "wastePercent",
                    "type": "number",
                    "label": "Fire oranı",
                    "required": false,
                    "exclusiveMinimum": false
                },
                {
                    "defaultValue": 0,
                    "placeholder": null,
                    "unit": "TL/m²",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": null,
                    "id": "parquetPricePerM2",
                    "type": "number",
                    "label": "Parke m² fiyatı",
                    "required": false,
                    "exclusiveMinimum": false
                },
                {
                    "defaultValue": true,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [],
                    "visibleWhen": null,
                    "id": "includeSkirting",
                    "type": "boolean",
                    "label": "Süpürgeliği dahil et",
                    "required": false
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "toolId",
                    "type": "text",
                    "label": "Araç",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "netAreaM2",
                    "type": "number",
                    "label": "Net alan",
                    "unit": "m²",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "wastePercent",
                    "type": "percentage",
                    "label": "Fire",
                    "unit": "%",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "grossNeedM2",
                    "type": "number",
                    "label": "Fireli ihtiyaç",
                    "unit": "m²",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "packageAreaM2",
                    "type": "number",
                    "label": "Paket kapsamı",
                    "unit": "m²",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "packageCount",
                    "type": "number",
                    "label": "Paket sayısı",
                    "unit": "adet",
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "purchasedAreaM2",
                    "type": "number",
                    "label": "Satın alınan alan",
                    "unit": "m²",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "excessAreaM2",
                    "type": "number",
                    "label": "Fazla alan",
                    "unit": "m²",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "includeSkirting",
                    "type": "boolean",
                    "label": "Süpürgelik dahil",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "skirtingLengthM",
                    "type": "number",
                    "label": "Süpürgelik uzunluğu",
                    "unit": "m",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "parquetPricePerM2",
                    "type": "money",
                    "label": "m² fiyatı",
                    "unit": "TL/m²",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "estimatedCost",
                    "type": "money",
                    "label": "Tahmini maliyet",
                    "unit": "TL",
                    "nullable": true,
                    "prominent": true
                }
            ]
        },
        "content": {
            "title": "Kaç Paket Laminat Parke Gerekir? m² ve Fire Hesaplama",
            "shortTitle": "Laminat Parke",
            "summary": "Oda ölçüsü, paket kapsamı ve fireyle tam paket sayısını; isteğe bağlı süpürgelik çevresini hesaplar.",
            "method": "Oda alanına fire eklenir ve paket alanına bölünerek yukarı yuvarlanır; fiyat kullanıcı m² fiyatı üzerinden satın alınan alana uygulanır.",
            "limitations": [
                "Sonuç bir keşif/tahmin desteğidir; uygulama, kesim ve yüzey koşulları gerçek ihtiyacı değiştirebilir.",
                "Ürün sarfiyatı, paket içeriği, fire oranı ve fiyat varsa kullanıcı tarafından ürün etiketi veya teknik föye göre girilmelidir.",
                "Fiyat girilmediğinde araç maliyet tahmini üretmez; çekirdekte güncel veya dönemsel fiyat bulunmaz."
            ],
            "faqs": [
                {
                    "question": "Laminat Parke hangi verileri kullanır?",
                    "answer": "Araç yalnız girilen ölçüleri ve formda açıkça görünen varsayılanları kullanır. Ürüne özgü sarfiyat, paket ve fiyat değerleri gerektiğinde kullanıcı girdisidir."
                }
            ]
        },
        "config": {
            "kind": "home-renovation-v1",
            "toolId": "laminat-parke",
            "maxAbsoluteInput": 1000000000,
            "metadata": {
                "calculatorId": "laminat-parke",
                "version": "1.0.0",
                "effectiveFrom": "2026-08-20",
                "lastVerifiedAt": "2026-08-20",
                "sources": [
                    {
                        "id": "openstax-geometry-applications",
                        "title": "Prealgebra 2e — Solve Geometry Applications: Circles and Irregular Figures",
                        "publisher": "OpenStax",
                        "url": "https://openstax.org/books/prealgebra-2e/pages/9-5-solve-geometry-applications-circles-and-irregular-figures",
                        "verifiedAt": "2026-08-20"
                    },
                    {
                        "id": "nist-si-conversion-factors",
                        "title": "NIST Guide to the SI — Appendix B, Conversion Factors",
                        "publisher": "National Institute of Standards and Technology",
                        "url": "https://www.nist.gov/pml/special-publication-811/nist-guide-si-appendix-b-conversion-factors",
                        "verifiedAt": "2026-08-20"
                    }
                ],
                "notes": [
                    "Hesap, geometrik ölçüler ile kullanıcının girdiği ürün değerlerini işleyen statik bir yöntemdir; dönemsel mevzuat veya tarife içermez.",
                    "Tüketim, verim, yoğunluk, paket ölçüsü, fire ve fiyat gibi ürüne özgü değerler kullanıcı girdisi veya formda açıkça gösterilen varsayılandır; config içinde dönemsel ürün verisi bulunmaz."
                ]
            }
        },
        "testVectors": {
            "kind": "home-renovation-oracle-v1",
            "cases": [
                {
                    "caseId": "positive-priced",
                    "scenario": "positive",
                    "input": {
                        "toolId": "laminat-parke",
                        "roomWidthM": 4,
                        "roomLengthM": 5,
                        "packageAreaM2": 1.84,
                        "wastePercent": 10,
                        "parquetPricePerM2": 450,
                        "includeSkirting": true
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "netAreaM2": 20,
                            "grossNeedM2": 22,
                            "packageCount": 12,
                            "purchasedAreaM2": 22.08,
                            "excessAreaM2": 0.08,
                            "skirtingLengthM": 18,
                            "estimatedCost": 9936
                        },
                        "absentFields": []
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "default-values",
                    "scenario": "default",
                    "input": {
                        "toolId": "laminat-parke",
                        "roomWidthM": 1,
                        "roomLengthM": 1,
                        "packageAreaM2": 1
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "wastePercent": 10,
                            "grossNeedM2": 1.1,
                            "packageCount": 2,
                            "includeSkirting": true,
                            "skirtingLengthM": 4,
                            "estimatedCost": null
                        },
                        "absentFields": []
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "boundary-one-package",
                    "scenario": "boundary-lower",
                    "input": {
                        "toolId": "laminat-parke",
                        "roomWidthM": 1,
                        "roomLengthM": 2,
                        "packageAreaM2": 2,
                        "wastePercent": 0
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "packageCount": 1
                        },
                        "absentFields": []
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "boundary-two-packages",
                    "scenario": "boundary-upper",
                    "input": {
                        "toolId": "laminat-parke",
                        "roomWidthM": 1.0001,
                        "roomLengthM": 2,
                        "packageAreaM2": 2,
                        "wastePercent": 0
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "packageCount": 2
                        },
                        "absentFields": []
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "price-absent",
                    "scenario": "price-absent",
                    "input": {
                        "toolId": "laminat-parke",
                        "roomWidthM": 4,
                        "roomLengthM": 5,
                        "packageAreaM2": 1.84,
                        "wastePercent": 10
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "estimatedCost": null
                        },
                        "absentFields": []
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "zero-width",
                    "scenario": "adversarial",
                    "input": {
                        "toolId": "laminat-parke",
                        "roomWidthM": 0,
                        "roomLengthM": 1,
                        "packageAreaM2": 1
                    },
                    "expected": {
                        "ok": false,
                        "field": "roomWidthM",
                        "errorCode": "OUT_OF_RANGE"
                    },
                    "tolerance": 1e-10
                }
            ]
        },
        "lastReviewed": "2026-08-20"
    },
    {
        "toolId": "oda-metrekare",
        "slug": "oda-metrekare-hesaplama",
        "path": "/ev-tadilat/oda-metrekare-hesaplama",
        "category": {
            "id": "ev-tadilat",
            "label": "Ev ve Tadilat"
        },
        "classification": "timeless",
        "engineId": "home-renovation-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-08-20",
        "effectiveTo": null,
        "minAppVersion": "0.1.0",
        "updateCadence": {
            "mode": "method-change-only",
            "days": null,
            "reviewAfter": null,
            "specialMonths": [],
            "note": "Zamansız, derlenmiş geometri yöntemidir; ürün sarfiyatı, paket, fire ve fiyat değerleri config'e gömülmez."
        },
        "sources": [
            {
                "sourceId": "openstax-geometry-applications",
                "sourceGroupId": null,
                "title": "Prealgebra 2e — Solve Geometry Applications: Circles and Irregular Figures",
                "publisher": "OpenStax",
                "url": "https://openstax.org/books/prealgebra-2e/pages/9-5-solve-geometry-applications-circles-and-irregular-figures",
                "kind": "authoritative-education",
                "reviewedOn": "2026-08-20",
                "watchFields": [
                    "Dikdörtgen alanı, çevre ve ölçü birimi ilişkileri"
                ]
            },
            {
                "sourceId": "nist-si-conversion-factors",
                "sourceGroupId": null,
                "title": "NIST Guide to the SI — Appendix B, Conversion Factors",
                "publisher": "National Institute of Standards and Technology",
                "url": "https://www.nist.gov/pml/special-publication-811/nist-guide-si-appendix-b-conversion-factors",
                "kind": "official-government",
                "reviewedOn": "2026-08-20",
                "watchFields": [
                    "Metre, santimetre, milimetre ve alan birimi dönüşümleri"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "m",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": null,
                    "id": "roomWidthM",
                    "type": "number",
                    "label": "Oda eni",
                    "required": true,
                    "exclusiveMinimum": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "m",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": null,
                    "id": "roomLengthM",
                    "type": "number",
                    "label": "Oda boyu",
                    "required": true,
                    "exclusiveMinimum": true
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "toolId",
                    "type": "text",
                    "label": "Araç",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "areaM2",
                    "type": "number",
                    "label": "Alan",
                    "unit": "m²",
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "perimeterM",
                    "type": "number",
                    "label": "Çevre",
                    "unit": "m",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "diagonalM",
                    "type": "number",
                    "label": "Köşegen",
                    "unit": "m",
                    "nullable": false,
                    "prominent": false
                }
            ]
        },
        "content": {
            "title": "Oda Metrekaresi Nasıl Hesaplanır? (En × Boy)",
            "shortTitle": "Oda Metrekare",
            "summary": "Dikdörtgen oda eni ve boyundan alanı, çevreyi ve köşegen uzunluğunu hesaplar.",
            "method": "Alan en × boy, çevre 2 × (en + boy), köşegen Pisagor bağıntısıyla hesaplanır.",
            "limitations": [
                "Sonuç bir keşif/tahmin desteğidir; uygulama, kesim ve yüzey koşulları gerçek ihtiyacı değiştirebilir.",
                "Ürün sarfiyatı, paket içeriği, fire oranı ve fiyat varsa kullanıcı tarafından ürün etiketi veya teknik föye göre girilmelidir.",
                "Fiyat girilmediğinde araç maliyet tahmini üretmez; çekirdekte güncel veya dönemsel fiyat bulunmaz."
            ],
            "faqs": [
                {
                    "question": "Oda Metrekare hangi verileri kullanır?",
                    "answer": "Araç yalnız girilen ölçüleri ve formda açıkça görünen varsayılanları kullanır. Ürüne özgü sarfiyat, paket ve fiyat değerleri gerektiğinde kullanıcı girdisidir."
                }
            ]
        },
        "config": {
            "kind": "home-renovation-v1",
            "toolId": "oda-metrekare",
            "maxAbsoluteInput": 1000000000,
            "metadata": {
                "calculatorId": "oda-metrekare",
                "version": "1.0.0",
                "effectiveFrom": "2026-08-20",
                "lastVerifiedAt": "2026-08-20",
                "sources": [
                    {
                        "id": "openstax-geometry-applications",
                        "title": "Prealgebra 2e — Solve Geometry Applications: Circles and Irregular Figures",
                        "publisher": "OpenStax",
                        "url": "https://openstax.org/books/prealgebra-2e/pages/9-5-solve-geometry-applications-circles-and-irregular-figures",
                        "verifiedAt": "2026-08-20"
                    },
                    {
                        "id": "nist-si-conversion-factors",
                        "title": "NIST Guide to the SI — Appendix B, Conversion Factors",
                        "publisher": "National Institute of Standards and Technology",
                        "url": "https://www.nist.gov/pml/special-publication-811/nist-guide-si-appendix-b-conversion-factors",
                        "verifiedAt": "2026-08-20"
                    }
                ],
                "notes": [
                    "Hesap, geometrik ölçüler ile kullanıcının girdiği ürün değerlerini işleyen statik bir yöntemdir; dönemsel mevzuat veya tarife içermez.",
                    "Tüketim, verim, yoğunluk, paket ölçüsü, fire ve fiyat gibi ürüne özgü değerler kullanıcı girdisi veya formda açıkça gösterilen varsayılandır; config içinde dönemsel ürün verisi bulunmaz."
                ]
            }
        },
        "testVectors": {
            "kind": "home-renovation-oracle-v1",
            "cases": [
                {
                    "caseId": "positive-three-four",
                    "scenario": "positive",
                    "input": {
                        "toolId": "oda-metrekare",
                        "roomWidthM": 3,
                        "roomLengthM": 4
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "areaM2": 12,
                            "perimeterM": 14,
                            "diagonalM": 5
                        },
                        "absentFields": []
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "default-none",
                    "scenario": "default",
                    "input": {
                        "toolId": "oda-metrekare",
                        "roomWidthM": 3,
                        "roomLengthM": 4
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "areaM2": 12,
                            "perimeterM": 14,
                            "diagonalM": 5
                        },
                        "absentFields": []
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "boundary-three-four",
                    "scenario": "boundary-lower",
                    "input": {
                        "toolId": "oda-metrekare",
                        "roomWidthM": 3,
                        "roomLengthM": 4
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "diagonalM": 5
                        },
                        "absentFields": []
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "boundary-six-eight",
                    "scenario": "boundary-upper",
                    "input": {
                        "toolId": "oda-metrekare",
                        "roomWidthM": 6,
                        "roomLengthM": 8
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "diagonalM": 10
                        },
                        "absentFields": []
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "price-not-supported",
                    "scenario": "price-absent",
                    "input": {
                        "toolId": "oda-metrekare",
                        "roomWidthM": 3,
                        "roomLengthM": 4
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "areaM2": 12
                        },
                        "absentFields": [
                            "estimatedCost"
                        ]
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "zero-width",
                    "scenario": "adversarial",
                    "input": {
                        "toolId": "oda-metrekare",
                        "roomWidthM": 0,
                        "roomLengthM": 4
                    },
                    "expected": {
                        "ok": false,
                        "field": "roomWidthM",
                        "errorCode": "OUT_OF_RANGE"
                    },
                    "tolerance": 1e-10
                }
            ]
        },
        "lastReviewed": "2026-08-20"
    },
    {
        "toolId": "supurgelik-metre",
        "slug": "supurgelik-metre-hesaplama",
        "path": "/ev-tadilat/supurgelik-metre-hesaplama",
        "category": {
            "id": "ev-tadilat",
            "label": "Ev ve Tadilat"
        },
        "classification": "timeless",
        "engineId": "home-renovation-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-08-20",
        "effectiveTo": null,
        "minAppVersion": "0.1.0",
        "updateCadence": {
            "mode": "method-change-only",
            "days": null,
            "reviewAfter": null,
            "specialMonths": [],
            "note": "Zamansız, derlenmiş geometri yöntemidir; ürün sarfiyatı, paket, fire ve fiyat değerleri config'e gömülmez."
        },
        "sources": [
            {
                "sourceId": "openstax-geometry-applications",
                "sourceGroupId": null,
                "title": "Prealgebra 2e — Solve Geometry Applications: Circles and Irregular Figures",
                "publisher": "OpenStax",
                "url": "https://openstax.org/books/prealgebra-2e/pages/9-5-solve-geometry-applications-circles-and-irregular-figures",
                "kind": "authoritative-education",
                "reviewedOn": "2026-08-20",
                "watchFields": [
                    "Dikdörtgen alanı, çevre ve ölçü birimi ilişkileri"
                ]
            },
            {
                "sourceId": "nist-si-conversion-factors",
                "sourceGroupId": null,
                "title": "NIST Guide to the SI — Appendix B, Conversion Factors",
                "publisher": "National Institute of Standards and Technology",
                "url": "https://www.nist.gov/pml/special-publication-811/nist-guide-si-appendix-b-conversion-factors",
                "kind": "official-government",
                "reviewedOn": "2026-08-20",
                "watchFields": [
                    "Metre, santimetre, milimetre ve alan birimi dönüşümleri"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "m",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": null,
                    "id": "roomWidthM",
                    "type": "number",
                    "label": "Oda eni",
                    "required": true,
                    "exclusiveMinimum": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "m",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": null,
                    "id": "roomLengthM",
                    "type": "number",
                    "label": "Oda boyu",
                    "required": true,
                    "exclusiveMinimum": true
                },
                {
                    "defaultValue": 1,
                    "placeholder": null,
                    "unit": "adet",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "doorCount",
                    "type": "number",
                    "label": "Kapı sayısı",
                    "required": false,
                    "exclusiveMinimum": false
                },
                {
                    "defaultValue": 90,
                    "placeholder": null,
                    "unit": "cm",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": null,
                    "id": "doorWidthCm",
                    "type": "number",
                    "label": "Kapı genişliği",
                    "required": false,
                    "exclusiveMinimum": false
                },
                {
                    "defaultValue": 10,
                    "placeholder": null,
                    "unit": "%",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": null,
                    "id": "wastePercent",
                    "type": "number",
                    "label": "Fire oranı",
                    "required": false,
                    "exclusiveMinimum": false
                },
                {
                    "defaultValue": 2.4,
                    "placeholder": null,
                    "unit": "m",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": null,
                    "id": "pieceLengthM",
                    "type": "number",
                    "label": "Parça boyu",
                    "required": false,
                    "exclusiveMinimum": true
                },
                {
                    "defaultValue": 0,
                    "placeholder": null,
                    "unit": "TL",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": null,
                    "id": "piecePrice",
                    "type": "number",
                    "label": "Parça fiyatı",
                    "required": false,
                    "exclusiveMinimum": false
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "toolId",
                    "type": "text",
                    "label": "Araç",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "perimeterM",
                    "type": "number",
                    "label": "Oda çevresi",
                    "unit": "m",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "doorOpeningM",
                    "type": "number",
                    "label": "Kapı açıklığı",
                    "unit": "m",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "netLengthM",
                    "type": "number",
                    "label": "Net uzunluk",
                    "unit": "m",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "wastePercent",
                    "type": "percentage",
                    "label": "Fire",
                    "unit": "%",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "grossLengthM",
                    "type": "number",
                    "label": "Fireli uzunluk",
                    "unit": "m",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "pieceLengthM",
                    "type": "number",
                    "label": "Parça boyu",
                    "unit": "m",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "pieceCount",
                    "type": "number",
                    "label": "Parça sayısı",
                    "unit": "adet",
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "purchasedLengthM",
                    "type": "number",
                    "label": "Satın alınan uzunluk",
                    "unit": "m",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "piecePrice",
                    "type": "money",
                    "label": "Parça fiyatı",
                    "unit": "TL",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "estimatedCost",
                    "type": "money",
                    "label": "Tahmini maliyet",
                    "unit": "TL",
                    "nullable": true,
                    "prominent": true
                }
            ]
        },
        "content": {
            "title": "Süpürgelik Metresi Nasıl Hesaplanır?",
            "shortTitle": "Süpürgelik Metresi",
            "summary": "Oda çevresinden kapı açıklıklarını çıkarır; fire ve parça boyuyla tam süpürgelik parçası ihtiyacını hesaplar.",
            "method": "Çevreden kapı genişlikleri çıkarılır, fire eklenir ve toplam uzunluk parça boyuna bölünüp yukarı yuvarlanır.",
            "limitations": [
                "Sonuç bir keşif/tahmin desteğidir; uygulama, kesim ve yüzey koşulları gerçek ihtiyacı değiştirebilir.",
                "Ürün sarfiyatı, paket içeriği, fire oranı ve fiyat varsa kullanıcı tarafından ürün etiketi veya teknik föye göre girilmelidir.",
                "Fiyat girilmediğinde araç maliyet tahmini üretmez; çekirdekte güncel veya dönemsel fiyat bulunmaz."
            ],
            "faqs": [
                {
                    "question": "Süpürgelik Metresi hangi verileri kullanır?",
                    "answer": "Araç yalnız girilen ölçüleri ve formda açıkça görünen varsayılanları kullanır. Ürüne özgü sarfiyat, paket ve fiyat değerleri gerektiğinde kullanıcı girdisidir."
                }
            ]
        },
        "config": {
            "kind": "home-renovation-v1",
            "toolId": "supurgelik-metre",
            "maxAbsoluteInput": 1000000000,
            "metadata": {
                "calculatorId": "supurgelik-metre",
                "version": "1.0.0",
                "effectiveFrom": "2026-08-20",
                "lastVerifiedAt": "2026-08-20",
                "sources": [
                    {
                        "id": "openstax-geometry-applications",
                        "title": "Prealgebra 2e — Solve Geometry Applications: Circles and Irregular Figures",
                        "publisher": "OpenStax",
                        "url": "https://openstax.org/books/prealgebra-2e/pages/9-5-solve-geometry-applications-circles-and-irregular-figures",
                        "verifiedAt": "2026-08-20"
                    },
                    {
                        "id": "nist-si-conversion-factors",
                        "title": "NIST Guide to the SI — Appendix B, Conversion Factors",
                        "publisher": "National Institute of Standards and Technology",
                        "url": "https://www.nist.gov/pml/special-publication-811/nist-guide-si-appendix-b-conversion-factors",
                        "verifiedAt": "2026-08-20"
                    }
                ],
                "notes": [
                    "Hesap, geometrik ölçüler ile kullanıcının girdiği ürün değerlerini işleyen statik bir yöntemdir; dönemsel mevzuat veya tarife içermez.",
                    "Tüketim, verim, yoğunluk, paket ölçüsü, fire ve fiyat gibi ürüne özgü değerler kullanıcı girdisi veya formda açıkça gösterilen varsayılandır; config içinde dönemsel ürün verisi bulunmaz."
                ]
            }
        },
        "testVectors": {
            "kind": "home-renovation-oracle-v1",
            "cases": [
                {
                    "caseId": "positive-priced",
                    "scenario": "positive",
                    "input": {
                        "toolId": "supurgelik-metre",
                        "roomWidthM": 4,
                        "roomLengthM": 5,
                        "doorCount": 1,
                        "doorWidthCm": 90,
                        "wastePercent": 10,
                        "pieceLengthM": 2.4,
                        "piecePrice": 85
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "perimeterM": 18,
                            "doorOpeningM": 0.9,
                            "netLengthM": 17.1,
                            "grossLengthM": 18.81,
                            "pieceCount": 8,
                            "purchasedLengthM": 19.2,
                            "estimatedCost": 680
                        },
                        "absentFields": []
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "default-values",
                    "scenario": "default",
                    "input": {
                        "toolId": "supurgelik-metre",
                        "roomWidthM": 1,
                        "roomLengthM": 1
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "doorOpeningM": 0.9,
                            "netLengthM": 3.1,
                            "wastePercent": 10,
                            "grossLengthM": 3.41,
                            "pieceLengthM": 2.4,
                            "pieceCount": 2,
                            "estimatedCost": null
                        },
                        "absentFields": []
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "boundary-one-piece",
                    "scenario": "boundary-lower",
                    "input": {
                        "toolId": "supurgelik-metre",
                        "roomWidthM": 1,
                        "roomLengthM": 1,
                        "doorCount": 0,
                        "wastePercent": 0,
                        "pieceLengthM": 4
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "pieceCount": 1
                        },
                        "absentFields": []
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "boundary-two-pieces",
                    "scenario": "boundary-upper",
                    "input": {
                        "toolId": "supurgelik-metre",
                        "roomWidthM": 1.0001,
                        "roomLengthM": 1,
                        "doorCount": 0,
                        "wastePercent": 0,
                        "pieceLengthM": 4
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "pieceCount": 2
                        },
                        "absentFields": []
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "price-absent",
                    "scenario": "price-absent",
                    "input": {
                        "toolId": "supurgelik-metre",
                        "roomWidthM": 4,
                        "roomLengthM": 5,
                        "doorCount": 1,
                        "doorWidthCm": 90,
                        "wastePercent": 10,
                        "pieceLengthM": 2.4
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "estimatedCost": null
                        },
                        "absentFields": []
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "door-openings-exceed-perimeter",
                    "scenario": "adversarial",
                    "input": {
                        "toolId": "supurgelik-metre",
                        "roomWidthM": 1,
                        "roomLengthM": 1,
                        "doorCount": 1,
                        "doorWidthCm": 400.01
                    },
                    "expected": {
                        "ok": false,
                        "field": "doorWidthCm",
                        "errorCode": "OUT_OF_RANGE"
                    },
                    "tolerance": 1e-10
                }
            ]
        },
        "lastReviewed": "2026-08-20"
    },
    {
        "toolId": "zebra-stor-perde-olcu",
        "slug": "zebra-stor-perde-olcu-hesaplama",
        "path": "/ev-tadilat/zebra-stor-perde-olcu-hesaplama",
        "category": {
            "id": "ev-tadilat",
            "label": "Ev ve Tadilat"
        },
        "classification": "timeless",
        "engineId": "home-renovation-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-08-20",
        "effectiveTo": null,
        "minAppVersion": "0.1.0",
        "updateCadence": {
            "mode": "method-change-only",
            "days": null,
            "reviewAfter": null,
            "specialMonths": [],
            "note": "Zamansız, derlenmiş geometri yöntemidir; ürün sarfiyatı, paket, fire ve fiyat değerleri config'e gömülmez."
        },
        "sources": [
            {
                "sourceId": "openstax-geometry-applications",
                "sourceGroupId": null,
                "title": "Prealgebra 2e — Solve Geometry Applications: Circles and Irregular Figures",
                "publisher": "OpenStax",
                "url": "https://openstax.org/books/prealgebra-2e/pages/9-5-solve-geometry-applications-circles-and-irregular-figures",
                "kind": "authoritative-education",
                "reviewedOn": "2026-08-20",
                "watchFields": [
                    "Dikdörtgen alanı, çevre ve ölçü birimi ilişkileri"
                ]
            },
            {
                "sourceId": "nist-si-conversion-factors",
                "sourceGroupId": null,
                "title": "NIST Guide to the SI — Appendix B, Conversion Factors",
                "publisher": "National Institute of Standards and Technology",
                "url": "https://www.nist.gov/pml/special-publication-811/nist-guide-si-appendix-b-conversion-factors",
                "kind": "official-government",
                "reviewedOn": "2026-08-20",
                "watchFields": [
                    "Metre, santimetre, milimetre ve alan birimi dönüşümleri"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "cm",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": null,
                    "id": "windowWidthCm",
                    "type": "number",
                    "label": "Pencere eni",
                    "required": true,
                    "exclusiveMinimum": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "cm",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": null,
                    "id": "windowHeightCm",
                    "type": "number",
                    "label": "Pencere yüksekliği",
                    "required": true,
                    "exclusiveMinimum": true
                },
                {
                    "defaultValue": 4,
                    "placeholder": null,
                    "unit": "cm",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": null,
                    "id": "mechanismAllowanceCm",
                    "type": "number",
                    "label": "Mekanizma payı",
                    "required": false,
                    "exclusiveMinimum": false
                },
                {
                    "defaultValue": 1,
                    "placeholder": null,
                    "unit": "m²",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": null,
                    "id": "minimumBillableAreaM2",
                    "type": "number",
                    "label": "Asgari faturalama alanı",
                    "required": false,
                    "exclusiveMinimum": true
                },
                {
                    "defaultValue": 0,
                    "placeholder": null,
                    "unit": "TL/m²",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 8,
                    "options": [],
                    "visibleWhen": null,
                    "id": "pricePerM2",
                    "type": "number",
                    "label": "m² fiyatı",
                    "required": false,
                    "exclusiveMinimum": false
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "toolId",
                    "type": "text",
                    "label": "Araç",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "orderWidthM",
                    "type": "number",
                    "label": "Sipariş eni",
                    "unit": "m",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "orderHeightM",
                    "type": "number",
                    "label": "Sipariş yüksekliği",
                    "unit": "m",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "actualAreaM2",
                    "type": "number",
                    "label": "Gerçek alan",
                    "unit": "m²",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "minimumBillableAreaM2",
                    "type": "number",
                    "label": "Asgari alan",
                    "unit": "m²",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "billableAreaM2",
                    "type": "number",
                    "label": "Faturalanabilir alan",
                    "unit": "m²",
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "pricePerM2",
                    "type": "money",
                    "label": "m² fiyatı",
                    "unit": "TL/m²",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "estimatedCost",
                    "type": "money",
                    "label": "Tahmini maliyet",
                    "unit": "TL",
                    "nullable": true,
                    "prominent": true
                }
            ]
        },
        "content": {
            "title": "Zebra ve Stor Perde Ölçüsü Nasıl Alınır, Kaç m² Tutar?",
            "shortTitle": "Zebra/Stor Perde Ölçüsü",
            "summary": "Pencere ölçüsü, mekanizma payı ve kullanıcı asgari faturalama alanıyla sipariş ve faturalama m² değerini hesaplar.",
            "method": "Mekanizma payı ene eklenir; gerçek alan en × boydur, faturalama alanı gerçek alan ile kullanıcı asgari alanının büyüğüdür.",
            "limitations": [
                "Sonuç bir keşif/tahmin desteğidir; uygulama, kesim ve yüzey koşulları gerçek ihtiyacı değiştirebilir.",
                "Ürün sarfiyatı, paket içeriği, fire oranı ve fiyat varsa kullanıcı tarafından ürün etiketi veya teknik föye göre girilmelidir.",
                "Fiyat girilmediğinde araç maliyet tahmini üretmez; çekirdekte güncel veya dönemsel fiyat bulunmaz."
            ],
            "faqs": [
                {
                    "question": "Zebra/Stor Perde Ölçüsü hangi verileri kullanır?",
                    "answer": "Araç yalnız girilen ölçüleri ve formda açıkça görünen varsayılanları kullanır. Ürüne özgü sarfiyat, paket ve fiyat değerleri gerektiğinde kullanıcı girdisidir."
                }
            ]
        },
        "config": {
            "kind": "home-renovation-v1",
            "toolId": "zebra-stor-perde-olcu",
            "maxAbsoluteInput": 1000000000,
            "metadata": {
                "calculatorId": "zebra-stor-perde-olcu",
                "version": "1.0.0",
                "effectiveFrom": "2026-08-20",
                "lastVerifiedAt": "2026-08-20",
                "sources": [
                    {
                        "id": "openstax-geometry-applications",
                        "title": "Prealgebra 2e — Solve Geometry Applications: Circles and Irregular Figures",
                        "publisher": "OpenStax",
                        "url": "https://openstax.org/books/prealgebra-2e/pages/9-5-solve-geometry-applications-circles-and-irregular-figures",
                        "verifiedAt": "2026-08-20"
                    },
                    {
                        "id": "nist-si-conversion-factors",
                        "title": "NIST Guide to the SI — Appendix B, Conversion Factors",
                        "publisher": "National Institute of Standards and Technology",
                        "url": "https://www.nist.gov/pml/special-publication-811/nist-guide-si-appendix-b-conversion-factors",
                        "verifiedAt": "2026-08-20"
                    }
                ],
                "notes": [
                    "Hesap, geometrik ölçüler ile kullanıcının girdiği ürün değerlerini işleyen statik bir yöntemdir; dönemsel mevzuat veya tarife içermez.",
                    "Tüketim, verim, yoğunluk, paket ölçüsü, fire ve fiyat gibi ürüne özgü değerler kullanıcı girdisi veya formda açıkça gösterilen varsayılandır; config içinde dönemsel ürün verisi bulunmaz."
                ]
            }
        },
        "testVectors": {
            "kind": "home-renovation-oracle-v1",
            "cases": [
                {
                    "caseId": "positive-priced",
                    "scenario": "positive",
                    "input": {
                        "toolId": "zebra-stor-perde-olcu",
                        "windowWidthCm": 150,
                        "windowHeightCm": 200,
                        "mechanismAllowanceCm": 4,
                        "minimumBillableAreaM2": 1,
                        "pricePerM2": 650
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "orderWidthM": 1.54,
                            "orderHeightM": 2,
                            "actualAreaM2": 3.08,
                            "billableAreaM2": 3.08,
                            "estimatedCost": 2002
                        },
                        "absentFields": []
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "default-values",
                    "scenario": "default",
                    "input": {
                        "toolId": "zebra-stor-perde-olcu",
                        "windowWidthCm": 50,
                        "windowHeightCm": 100
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "orderWidthM": 0.54,
                            "actualAreaM2": 0.54,
                            "minimumBillableAreaM2": 1,
                            "billableAreaM2": 1,
                            "estimatedCost": null
                        },
                        "absentFields": []
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "boundary-minimum-area",
                    "scenario": "boundary-lower",
                    "input": {
                        "toolId": "zebra-stor-perde-olcu",
                        "windowWidthCm": 46,
                        "windowHeightCm": 100,
                        "mechanismAllowanceCm": 4,
                        "minimumBillableAreaM2": 1
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "actualAreaM2": 0.5,
                            "billableAreaM2": 1
                        },
                        "absentFields": []
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "boundary-actual-area",
                    "scenario": "boundary-upper",
                    "input": {
                        "toolId": "zebra-stor-perde-olcu",
                        "windowWidthCm": 146,
                        "windowHeightCm": 100,
                        "mechanismAllowanceCm": 4,
                        "minimumBillableAreaM2": 1
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "actualAreaM2": 1.5,
                            "billableAreaM2": 1.5
                        },
                        "absentFields": []
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "price-absent",
                    "scenario": "price-absent",
                    "input": {
                        "toolId": "zebra-stor-perde-olcu",
                        "windowWidthCm": 150,
                        "windowHeightCm": 200,
                        "mechanismAllowanceCm": 4,
                        "minimumBillableAreaM2": 1
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "estimatedCost": null
                        },
                        "absentFields": []
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "zero-window-width",
                    "scenario": "adversarial",
                    "input": {
                        "toolId": "zebra-stor-perde-olcu",
                        "windowWidthCm": 0,
                        "windowHeightCm": 100
                    },
                    "expected": {
                        "ok": false,
                        "field": "windowWidthCm",
                        "errorCode": "OUT_OF_RANGE"
                    },
                    "tolerance": 1e-10
                }
            ]
        },
        "lastReviewed": "2026-08-20"
    },
    {
        "toolId": "age",
        "slug": "yas-hesaplama",
        "path": "/tarih-zaman/yas-hesaplama",
        "category": {
            "id": "tarih-zaman",
            "label": "Tarih ve Zaman"
        },
        "classification": "timeless",
        "engineId": "age-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-08-20",
        "effectiveTo": null,
        "minAppVersion": "0.1.0",
        "updateCadence": {
            "mode": "method-change-only",
            "days": null,
            "reviewAfter": null,
            "specialMonths": [],
            "note": "Takvim yöntemi veya dayanak standardı değiştiğinde insan incelemesi yapılır."
        },
        "sources": [
            {
                "sourceId": "iso-8601-date-time-format",
                "sourceGroupId": null,
                "title": "ISO 8601 — Date and time format",
                "publisher": "International Organization for Standardization",
                "url": "https://www.iso.org/iso-8601-date-and-time-format.html",
                "kind": "international-standards-body",
                "reviewedOn": "2026-08-20",
                "watchFields": [
                    "ISO tarih gösterimi ve hafta numarası tanımı"
                ]
            },
            {
                "sourceId": "usno-leap-years",
                "sourceGroupId": null,
                "title": "Leap Years",
                "publisher": "U.S. Naval Observatory",
                "url": "https://aa.usno.navy.mil/faq/leap_years",
                "kind": "official-government",
                "reviewedOn": "2026-08-20",
                "watchFields": [
                    "Gregoryen artık yıl ve 29 Şubat kuralları"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [],
                    "visibleWhen": null,
                    "id": "birthDate",
                    "type": "date",
                    "label": "Doğum tarihi",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [],
                    "visibleWhen": null,
                    "id": "referenceDate",
                    "type": "date",
                    "label": "Hedef tarih",
                    "required": true
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "birthDate",
                    "type": "text",
                    "label": "Doğum tarihi",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "referenceDate",
                    "type": "text",
                    "label": "Hedef tarih",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "years",
                    "type": "number",
                    "label": "Yıl",
                    "unit": null,
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "months",
                    "type": "number",
                    "label": "Ay",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "days",
                    "type": "number",
                    "label": "Gün",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "totalDays",
                    "type": "number",
                    "label": "Toplam gün",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "totalHours",
                    "type": "number",
                    "label": "Toplam saat",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "totalMinutes",
                    "type": "number",
                    "label": "Toplam dakika",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "nextBirthday",
                    "type": "text",
                    "label": "Sonraki doğum günü",
                    "unit": null,
                    "nullable": true,
                    "prominent": false
                },
                {
                    "id": "daysUntilNextBirthday",
                    "type": "number",
                    "label": "Sonraki doğum gününe kalan gün",
                    "unit": null,
                    "nullable": true,
                    "prominent": false
                }
            ]
        },
        "content": {
            "title": "Yaş Hesaplama",
            "shortTitle": "Yaş Hesaplama",
            "summary": "Doğum tarihi ile çağıranın açıkça verdiği hedef tarih arasındaki takvim yaşını ve toplam süreleri hesaplar.",
            "method": "Önce tamamlanan yıl, ardından ay ve gün bileşenleri bulunur; 29 Şubat yıldönümü artık olmayan yılda 28 Şubat kabul edilir.",
            "limitations": [
                "Araç hukuki yaş kararı vermez. Dinamik bugün değeri cihazın seçtiği yerel tarihten referenceDate olarak aktarılmalıdır."
            ],
            "faqs": [
                {
                    "question": "Yaş Hesaplama hangi tarih ve saat dilimi yaklaşımını kullanır?",
                    "answer": "Tarih girdileri saat bileşeni taşımayan UTC takvim günleri olarak işlenir. Çekirdek sistem saatini okumaz; gerekiyorsa hedef tarih çağıran tarafından açıkça verilir."
                }
            ]
        },
        "config": {
            "kind": "calendar-date-v1",
            "calculatorId": "tarih-zaman.yas-hesaplama",
            "minYear": 1,
            "maxYear": 9999
        },
        "testVectors": {
            "kind": "date-time-oracle-v1",
            "cases": [
                {
                    "caseId": "caller-date-positive",
                    "scenario": "positive",
                    "input": {
                        "birthDate": "1990-05-15",
                        "referenceDate": "2026-08-20"
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "birthDate": "1990-05-15",
                            "referenceDate": "2026-08-20",
                            "years": 36,
                            "months": 3,
                            "days": 5,
                            "totalDays": 13246,
                            "totalHours": 317904,
                            "totalMinutes": 19074240,
                            "nextBirthday": "2027-05-15",
                            "daysUntilNextBirthday": 268
                        }
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "same-date-zero",
                    "scenario": "zero",
                    "input": {
                        "birthDate": "2026-08-20",
                        "referenceDate": "2026-08-20"
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "birthDate": "2026-08-20",
                            "referenceDate": "2026-08-20",
                            "years": 0,
                            "months": 0,
                            "days": 0,
                            "totalDays": 0,
                            "totalHours": 0,
                            "totalMinutes": 0,
                            "nextBirthday": "2027-08-20",
                            "daysUntilNextBirthday": 365
                        }
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "leap-anniversary-before",
                    "scenario": "leap-day",
                    "input": {
                        "birthDate": "2000-02-29",
                        "referenceDate": "2004-02-28"
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "birthDate": "2000-02-29",
                            "referenceDate": "2004-02-28",
                            "years": 3,
                            "months": 11,
                            "days": 31,
                            "totalDays": 1460,
                            "totalHours": 35040,
                            "totalMinutes": 2102400,
                            "nextBirthday": "2004-02-29",
                            "daysUntilNextBirthday": 1
                        }
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "calendar-upper-bound",
                    "scenario": "boundary",
                    "input": {
                        "birthDate": "0001-01-01",
                        "referenceDate": "9999-12-31"
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "birthDate": "0001-01-01",
                            "referenceDate": "9999-12-31",
                            "years": 9998,
                            "months": 11,
                            "days": 30,
                            "totalDays": 3652058,
                            "totalHours": 87649392,
                            "totalMinutes": 5258963520,
                            "nextBirthday": null,
                            "daysUntilNextBirthday": null
                        }
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "impossible-birth-date",
                    "scenario": "adversarial",
                    "input": {
                        "birthDate": "2024-02-30",
                        "referenceDate": "2026-08-20"
                    },
                    "expected": {
                        "ok": false,
                        "field": "birthDate",
                        "errorCode": "UNSUPPORTED_VALUE"
                    },
                    "tolerance": 1e-10
                }
            ]
        },
        "lastReviewed": "2026-08-20"
    },
    {
        "toolId": "date-difference",
        "slug": "iki-tarih-arasi-fark-hesaplama",
        "path": "/tarih-zaman/iki-tarih-arasi-fark-hesaplama",
        "category": {
            "id": "tarih-zaman",
            "label": "Tarih ve Zaman"
        },
        "classification": "timeless",
        "engineId": "date-difference-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-08-20",
        "effectiveTo": null,
        "minAppVersion": "0.1.0",
        "updateCadence": {
            "mode": "method-change-only",
            "days": null,
            "reviewAfter": null,
            "specialMonths": [],
            "note": "Takvim yöntemi veya dayanak standardı değiştiğinde insan incelemesi yapılır."
        },
        "sources": [
            {
                "sourceId": "iso-8601-date-time-format",
                "sourceGroupId": null,
                "title": "ISO 8601 — Date and time format",
                "publisher": "International Organization for Standardization",
                "url": "https://www.iso.org/iso-8601-date-and-time-format.html",
                "kind": "international-standards-body",
                "reviewedOn": "2026-08-20",
                "watchFields": [
                    "ISO tarih gösterimi ve hafta numarası tanımı"
                ]
            },
            {
                "sourceId": "usno-leap-years",
                "sourceGroupId": null,
                "title": "Leap Years",
                "publisher": "U.S. Naval Observatory",
                "url": "https://aa.usno.navy.mil/faq/leap_years",
                "kind": "official-government",
                "reviewedOn": "2026-08-20",
                "watchFields": [
                    "Gregoryen artık yıl ve 29 Şubat kuralları"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [],
                    "visibleWhen": null,
                    "id": "startDate",
                    "type": "date",
                    "label": "Başlangıç tarihi",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [],
                    "visibleWhen": null,
                    "id": "endDate",
                    "type": "date",
                    "label": "Bitiş tarihi",
                    "required": true
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "startDate",
                    "type": "text",
                    "label": "Başlangıç tarihi",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "endDate",
                    "type": "text",
                    "label": "Bitiş tarihi",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "years",
                    "type": "number",
                    "label": "Yıl",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "months",
                    "type": "number",
                    "label": "Ay",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "days",
                    "type": "number",
                    "label": "Gün",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "totalDays",
                    "type": "number",
                    "label": "Toplam gün",
                    "unit": null,
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "totalWeeks",
                    "type": "number",
                    "label": "Toplam hafta",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "totalHours",
                    "type": "number",
                    "label": "Toplam saat",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "totalMinutes",
                    "type": "number",
                    "label": "Toplam dakika",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                }
            ]
        },
        "content": {
            "title": "İki Tarih Arasındaki Farkı Hesaplama",
            "shortTitle": "Tarih Farkı",
            "summary": "Başlangıç ve bitiş tarihleri arasındaki negatif olmayan takvim farkını ve toplam gün, hafta, saat, dakika karşılıklarını verir.",
            "method": "Önce tamamlanan yıl ve aylar ay-sonu sıkıştırmasıyla bulunur; kalan bölüm takvim günü olarak hesaplanır.",
            "limitations": [
                "Saat dilimi ve yaz/kış saati süresi hesaplanmaz; girdiler takvim günüdür ve bitiş başlangıçtan önce olamaz."
            ],
            "faqs": [
                {
                    "question": "Tarih Farkı hangi tarih ve saat dilimi yaklaşımını kullanır?",
                    "answer": "Tarih girdileri saat bileşeni taşımayan UTC takvim günleri olarak işlenir. Çekirdek sistem saatini okumaz; gerekiyorsa hedef tarih çağıran tarafından açıkça verilir."
                }
            ]
        },
        "config": {
            "kind": "calendar-date-v1",
            "calculatorId": "tarih-zaman.iki-tarih-arasi-fark-hesaplama",
            "minYear": 1,
            "maxYear": 9999
        },
        "testVectors": {
            "kind": "date-time-oracle-v1",
            "cases": [
                {
                    "caseId": "multi-year-positive",
                    "scenario": "positive",
                    "input": {
                        "startDate": "2019-01-15",
                        "endDate": "2021-03-20"
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "startDate": "2019-01-15",
                            "endDate": "2021-03-20",
                            "years": 2,
                            "months": 2,
                            "days": 5,
                            "totalDays": 795,
                            "totalWeeks": 113.57142857142857,
                            "totalHours": 19080,
                            "totalMinutes": 1144800
                        }
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "equal-dates-zero",
                    "scenario": "zero",
                    "input": {
                        "startDate": "2026-08-20",
                        "endDate": "2026-08-20"
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "startDate": "2026-08-20",
                            "endDate": "2026-08-20",
                            "years": 0,
                            "months": 0,
                            "days": 0,
                            "totalDays": 0,
                            "totalWeeks": 0,
                            "totalHours": 0,
                            "totalMinutes": 0
                        }
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "month-end-clamp",
                    "scenario": "month-end",
                    "input": {
                        "startDate": "2021-01-31",
                        "endDate": "2021-03-01"
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "startDate": "2021-01-31",
                            "endDate": "2021-03-01",
                            "years": 0,
                            "months": 1,
                            "days": 1,
                            "totalDays": 29,
                            "totalWeeks": 4.142857142857143,
                            "totalHours": 696,
                            "totalMinutes": 41760
                        }
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "dst-calendar-days",
                    "scenario": "dst-calendar",
                    "input": {
                        "startDate": "2024-03-09",
                        "endDate": "2024-03-11"
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "startDate": "2024-03-09",
                            "endDate": "2024-03-11",
                            "years": 0,
                            "months": 0,
                            "days": 2,
                            "totalDays": 2,
                            "totalWeeks": 0.2857142857142857,
                            "totalHours": 48,
                            "totalMinutes": 2880
                        }
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "reverse-range",
                    "scenario": "adversarial",
                    "input": {
                        "startDate": "2026-08-21",
                        "endDate": "2026-08-20"
                    },
                    "expected": {
                        "ok": false,
                        "field": "endDate",
                        "errorCode": "OUT_OF_RANGE"
                    },
                    "tolerance": 1e-10
                }
            ]
        },
        "lastReviewed": "2026-08-20"
    },
    {
        "toolId": "duration",
        "slug": "saat-sure-hesaplama",
        "path": "/tarih-zaman/saat-sure-hesaplama",
        "category": {
            "id": "tarih-zaman",
            "label": "Tarih ve Zaman"
        },
        "classification": "timeless",
        "engineId": "duration-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-08-20",
        "effectiveTo": null,
        "minAppVersion": "0.1.0",
        "updateCadence": {
            "mode": "method-change-only",
            "days": null,
            "reviewAfter": null,
            "specialMonths": [],
            "note": "Takvim yöntemi veya dayanak standardı değiştiğinde insan incelemesi yapılır."
        },
        "sources": [
            {
                "sourceId": "bipm-si-brochure",
                "sourceGroupId": null,
                "title": "The International System of Units (SI), 9th edition",
                "publisher": "Bureau International des Poids et Mesures",
                "url": "https://www.bipm.org/en/publications/si-brochure",
                "kind": "international-standards-body",
                "reviewedOn": "2026-08-20",
                "watchFields": [
                    "Saat ve dakika birimleri"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": "add",
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "add",
                            "label": "Topla"
                        },
                        {
                            "value": "subtract",
                            "label": "Çıkar"
                        }
                    ],
                    "visibleWhen": null,
                    "id": "operation",
                    "type": "select",
                    "label": "İşlem",
                    "required": true
                },
                {
                    "defaultValue": 0,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 0,
                    "maximum": 99999,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "firstHours",
                    "type": "number",
                    "label": "İlk süre saati",
                    "required": true
                },
                {
                    "defaultValue": 0,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 0,
                    "maximum": 9999,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "firstMinutes",
                    "type": "number",
                    "label": "İlk süre dakikası",
                    "required": true
                },
                {
                    "defaultValue": 0,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 0,
                    "maximum": 99999,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "secondHours",
                    "type": "number",
                    "label": "İkinci süre saati",
                    "required": true
                },
                {
                    "defaultValue": 0,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 0,
                    "maximum": 9999,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "secondMinutes",
                    "type": "number",
                    "label": "İkinci süre dakikası",
                    "required": true
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "operation",
                    "type": "text",
                    "label": "İşlem",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "first",
                    "type": "object",
                    "label": "İlk normalize süre",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "second",
                    "type": "object",
                    "label": "İkinci normalize süre",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "hours",
                    "type": "number",
                    "label": "Saat",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "minutes",
                    "type": "number",
                    "label": "Dakika",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "signedTotalMinutes",
                    "type": "number",
                    "label": "İşaretli toplam dakika",
                    "unit": null,
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "absoluteTotalMinutes",
                    "type": "number",
                    "label": "Mutlak toplam dakika",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "decimalHours",
                    "type": "number",
                    "label": "Ondalık saat",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "isNegative",
                    "type": "boolean",
                    "label": "Negatif sonuç",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                }
            ]
        },
        "content": {
            "title": "Saat ve Süre Hesaplama",
            "shortTitle": "Süre Hesaplama",
            "summary": "İki saat-dakika süresini toplar veya çıkarır; dakika taşmasını normalize eder ve sonucun işaretini korur.",
            "method": "Her süre toplam dakikaya çevrilir, seçilen aritmetik işlem uygulanır ve sonuç saat-dakika bileşenlerine ayrılır.",
            "limitations": [
                "Bu araç iki tarih arasındaki gerçek geçen zamanı veya saat dilimi/DST farkını hesaplamaz."
            ],
            "faqs": [
                {
                    "question": "Süre Hesaplama hangi tarih ve saat dilimi yaklaşımını kullanır?",
                    "answer": "Tarih girdileri saat bileşeni taşımayan UTC takvim günleri olarak işlenir. Çekirdek sistem saatini okumaz; gerekiyorsa hedef tarih çağıran tarafından açıkça verilir."
                }
            ]
        },
        "config": {
            "kind": "duration-v1",
            "calculatorId": "tarih-zaman.saat-sure-hesaplama",
            "maxHours": 99999,
            "maxMinutes": 9999
        },
        "testVectors": {
            "kind": "date-time-oracle-v1",
            "cases": [
                {
                    "caseId": "minute-normalization",
                    "scenario": "positive",
                    "input": {
                        "operation": "add",
                        "firstHours": 1,
                        "firstMinutes": 90,
                        "secondHours": 2,
                        "secondMinutes": 30
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "operation": "add",
                            "first": {
                                "hours": 2,
                                "minutes": 30,
                                "totalMinutes": 150
                            },
                            "second": {
                                "hours": 2,
                                "minutes": 30,
                                "totalMinutes": 150
                            },
                            "hours": 5,
                            "minutes": 0,
                            "signedTotalMinutes": 300,
                            "absoluteTotalMinutes": 300,
                            "decimalHours": 5,
                            "isNegative": false
                        }
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "negative-subtraction",
                    "scenario": "boundary",
                    "input": {
                        "operation": "subtract",
                        "firstHours": 1,
                        "firstMinutes": 0,
                        "secondHours": 2,
                        "secondMinutes": 30
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "operation": "subtract",
                            "first": {
                                "hours": 1,
                                "minutes": 0,
                                "totalMinutes": 60
                            },
                            "second": {
                                "hours": 2,
                                "minutes": 30,
                                "totalMinutes": 150
                            },
                            "hours": 1,
                            "minutes": 30,
                            "signedTotalMinutes": -90,
                            "absoluteTotalMinutes": 90,
                            "decimalHours": -1.5,
                            "isNegative": true
                        }
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "equal-subtraction-zero",
                    "scenario": "zero",
                    "input": {
                        "operation": "subtract",
                        "firstHours": 1,
                        "firstMinutes": 0,
                        "secondHours": 1,
                        "secondMinutes": 0
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "operation": "subtract",
                            "first": {
                                "hours": 1,
                                "minutes": 0,
                                "totalMinutes": 60
                            },
                            "second": {
                                "hours": 1,
                                "minutes": 0,
                                "totalMinutes": 60
                            },
                            "hours": 0,
                            "minutes": 0,
                            "signedTotalMinutes": 0,
                            "absoluteTotalMinutes": 0,
                            "decimalHours": 0,
                            "isNegative": false
                        }
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "exact-input-limits",
                    "scenario": "boundary",
                    "input": {
                        "operation": "add",
                        "firstHours": 99999,
                        "firstMinutes": 9999,
                        "secondHours": 99999,
                        "secondMinutes": 9999
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "operation": "add",
                            "first": {
                                "hours": 100165,
                                "minutes": 39,
                                "totalMinutes": 6009939
                            },
                            "second": {
                                "hours": 100165,
                                "minutes": 39,
                                "totalMinutes": 6009939
                            },
                            "hours": 200331,
                            "minutes": 18,
                            "signedTotalMinutes": 12019878,
                            "absoluteTotalMinutes": 12019878,
                            "decimalHours": 200331.3,
                            "isNegative": false
                        }
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "hour-over-limit",
                    "scenario": "adversarial",
                    "input": {
                        "operation": "add",
                        "firstHours": 100000,
                        "firstMinutes": 0,
                        "secondHours": 0,
                        "secondMinutes": 0
                    },
                    "expected": {
                        "ok": false,
                        "field": "firstHours",
                        "errorCode": "OUT_OF_RANGE"
                    },
                    "tolerance": 1e-10
                }
            ]
        },
        "lastReviewed": "2026-08-20"
    },
    {
        "toolId": "date-adjustment",
        "slug": "tarihe-gun-ekleme-cikarma-hesaplama",
        "path": "/tarih-zaman/tarihe-gun-ekleme-cikarma-hesaplama",
        "category": {
            "id": "tarih-zaman",
            "label": "Tarih ve Zaman"
        },
        "classification": "timeless",
        "engineId": "date-adjustment-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-08-20",
        "effectiveTo": null,
        "minAppVersion": "0.1.0",
        "updateCadence": {
            "mode": "method-change-only",
            "days": null,
            "reviewAfter": null,
            "specialMonths": [],
            "note": "Takvim yöntemi veya dayanak standardı değiştiğinde insan incelemesi yapılır."
        },
        "sources": [
            {
                "sourceId": "iso-8601-date-time-format",
                "sourceGroupId": null,
                "title": "ISO 8601 — Date and time format",
                "publisher": "International Organization for Standardization",
                "url": "https://www.iso.org/iso-8601-date-and-time-format.html",
                "kind": "international-standards-body",
                "reviewedOn": "2026-08-20",
                "watchFields": [
                    "ISO tarih gösterimi ve hafta numarası tanımı"
                ]
            },
            {
                "sourceId": "usno-leap-years",
                "sourceGroupId": null,
                "title": "Leap Years",
                "publisher": "U.S. Naval Observatory",
                "url": "https://aa.usno.navy.mil/faq/leap_years",
                "kind": "official-government",
                "reviewedOn": "2026-08-20",
                "watchFields": [
                    "Gregoryen artık yıl ve 29 Şubat kuralları"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": "add",
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "add",
                            "label": "Ekle"
                        },
                        {
                            "value": "subtract",
                            "label": "Çıkar"
                        }
                    ],
                    "visibleWhen": null,
                    "id": "operation",
                    "type": "select",
                    "label": "İşlem",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [],
                    "visibleWhen": null,
                    "id": "startDate",
                    "type": "date",
                    "label": "Başlangıç tarihi",
                    "required": true
                },
                {
                    "defaultValue": 0,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 0,
                    "maximum": 100,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "years",
                    "type": "number",
                    "label": "Yıl",
                    "required": true
                },
                {
                    "defaultValue": 0,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 0,
                    "maximum": 1200,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "months",
                    "type": "number",
                    "label": "Ay",
                    "required": true
                },
                {
                    "defaultValue": 0,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 0,
                    "maximum": 9999,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "weeks",
                    "type": "number",
                    "label": "Hafta",
                    "required": true
                },
                {
                    "defaultValue": 0,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 0,
                    "maximum": 999999,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "days",
                    "type": "number",
                    "label": "Gün",
                    "required": true
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "operation",
                    "type": "text",
                    "label": "İşlem",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "startDate",
                    "type": "text",
                    "label": "Başlangıç tarihi",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "years",
                    "type": "number",
                    "label": "Yıl",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "months",
                    "type": "number",
                    "label": "Ay",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "weeks",
                    "type": "number",
                    "label": "Hafta",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "days",
                    "type": "number",
                    "label": "Gün",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "resultDate",
                    "type": "text",
                    "label": "Sonuç tarihi",
                    "unit": null,
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "totalDays",
                    "type": "number",
                    "label": "Toplam gün farkı",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "isoWeekday",
                    "type": "number",
                    "label": "ISO hafta günü",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "dayOfYear",
                    "type": "number",
                    "label": "Yılın günü",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                }
            ]
        },
        "content": {
            "title": "Tarihe Gün, Hafta, Ay veya Yıl Ekleme ve Çıkarma",
            "shortTitle": "Tarih Ekleme/Çıkarma",
            "summary": "Bir takvim tarihine yıl, ay, hafta ve gün ekler veya çıkarır; ay sonlarını güvenli biçimde sıkıştırır.",
            "method": "Önce yıl ve ay uygulanır; hedef ay daha kısaysa gün ayın sonuna sıkıştırılır. Ardından hafta ve gün takvim günü olarak uygulanır.",
            "limitations": [
                "Sonuç 0001-9999 tarih aralığında kalmalıdır; işlem saat dilimi veya saat bileşeni içermez."
            ],
            "faqs": [
                {
                    "question": "Tarih Ekleme/Çıkarma hangi tarih ve saat dilimi yaklaşımını kullanır?",
                    "answer": "Tarih girdileri saat bileşeni taşımayan UTC takvim günleri olarak işlenir. Çekirdek sistem saatini okumaz; gerekiyorsa hedef tarih çağıran tarafından açıkça verilir."
                }
            ]
        },
        "config": {
            "kind": "date-adjustment-v1",
            "calculatorId": "tarih-zaman.tarihe-gun-ekleme-cikarma-hesaplama",
            "minYear": 1,
            "maxYear": 9999,
            "maxYears": 100,
            "maxMonths": 1200,
            "maxWeeks": 9999,
            "maxDays": 999999
        },
        "testVectors": {
            "kind": "date-time-oracle-v1",
            "cases": [
                {
                    "caseId": "mixed-positive",
                    "scenario": "positive",
                    "input": {
                        "operation": "add",
                        "startDate": "2020-01-15",
                        "years": 1,
                        "months": 2,
                        "weeks": 1,
                        "days": 3
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "operation": "add",
                            "startDate": "2020-01-15",
                            "years": 1,
                            "months": 2,
                            "weeks": 1,
                            "days": 3,
                            "resultDate": "2021-03-25",
                            "totalDays": 435,
                            "isoWeekday": 4,
                            "dayOfYear": 84
                        }
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "all-zero-noop",
                    "scenario": "zero",
                    "input": {
                        "operation": "add",
                        "startDate": "2026-08-20",
                        "years": 0,
                        "months": 0,
                        "weeks": 0,
                        "days": 0
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "operation": "add",
                            "startDate": "2026-08-20",
                            "years": 0,
                            "months": 0,
                            "weeks": 0,
                            "days": 0,
                            "resultDate": "2026-08-20",
                            "totalDays": 0,
                            "isoWeekday": 4,
                            "dayOfYear": 232
                        }
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "month-clamp-then-day",
                    "scenario": "month-end",
                    "input": {
                        "operation": "add",
                        "startDate": "2021-01-31",
                        "years": 0,
                        "months": 1,
                        "weeks": 0,
                        "days": 1
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "operation": "add",
                            "startDate": "2021-01-31",
                            "years": 0,
                            "months": 1,
                            "weeks": 0,
                            "days": 1,
                            "resultDate": "2021-03-01",
                            "totalDays": 29,
                            "isoWeekday": 1,
                            "dayOfYear": 60
                        }
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "dst-calendar-add",
                    "scenario": "dst-calendar",
                    "input": {
                        "operation": "add",
                        "startDate": "2024-03-09",
                        "years": 0,
                        "months": 0,
                        "weeks": 0,
                        "days": 2
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "operation": "add",
                            "startDate": "2024-03-09",
                            "years": 0,
                            "months": 0,
                            "weeks": 0,
                            "days": 2,
                            "resultDate": "2024-03-11",
                            "totalDays": 2,
                            "isoWeekday": 1,
                            "dayOfYear": 71
                        }
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "negative-component",
                    "scenario": "adversarial",
                    "input": {
                        "operation": "add",
                        "startDate": "2026-08-20",
                        "years": -1,
                        "months": 0,
                        "weeks": 0,
                        "days": 0
                    },
                    "expected": {
                        "ok": false,
                        "field": "years",
                        "errorCode": "OUT_OF_RANGE"
                    },
                    "tolerance": 1e-10
                }
            ]
        },
        "lastReviewed": "2026-08-20"
    },
    {
        "toolId": "iso-week",
        "slug": "hafta-hesaplama",
        "path": "/tarih-zaman/hafta-hesaplama",
        "category": {
            "id": "tarih-zaman",
            "label": "Tarih ve Zaman"
        },
        "classification": "timeless",
        "engineId": "iso-week-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-08-20",
        "effectiveTo": null,
        "minAppVersion": "0.1.0",
        "updateCadence": {
            "mode": "method-change-only",
            "days": null,
            "reviewAfter": null,
            "specialMonths": [],
            "note": "Takvim yöntemi veya dayanak standardı değiştiğinde insan incelemesi yapılır."
        },
        "sources": [
            {
                "sourceId": "iso-8601-date-time-format",
                "sourceGroupId": null,
                "title": "ISO 8601 — Date and time format",
                "publisher": "International Organization for Standardization",
                "url": "https://www.iso.org/iso-8601-date-and-time-format.html",
                "kind": "international-standards-body",
                "reviewedOn": "2026-08-20",
                "watchFields": [
                    "ISO tarih gösterimi ve hafta numarası tanımı"
                ]
            },
            {
                "sourceId": "usno-leap-years",
                "sourceGroupId": null,
                "title": "Leap Years",
                "publisher": "U.S. Naval Observatory",
                "url": "https://aa.usno.navy.mil/faq/leap_years",
                "kind": "official-government",
                "reviewedOn": "2026-08-20",
                "watchFields": [
                    "Gregoryen artık yıl ve 29 Şubat kuralları"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [],
                    "visibleWhen": null,
                    "id": "date",
                    "type": "date",
                    "label": "Tarih",
                    "required": true
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "date",
                    "type": "text",
                    "label": "Tarih",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "isoWeek",
                    "type": "number",
                    "label": "ISO hafta",
                    "unit": null,
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "isoYear",
                    "type": "number",
                    "label": "ISO hafta yılı",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "isoWeekday",
                    "type": "number",
                    "label": "ISO hafta günü",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "dayOfYear",
                    "type": "number",
                    "label": "Yılın günü",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "weekStart",
                    "type": "text",
                    "label": "Hafta başlangıcı",
                    "unit": null,
                    "nullable": true,
                    "prominent": false
                },
                {
                    "id": "weekEnd",
                    "type": "text",
                    "label": "Hafta bitişi",
                    "unit": null,
                    "nullable": true,
                    "prominent": false
                }
            ]
        },
        "content": {
            "title": "ISO Hafta Numarası Hesaplama",
            "shortTitle": "ISO Hafta Numarası",
            "summary": "Bir tarihin ISO 8601 hafta numarasını, hafta yılını, hafta gününü ve Pazartesi-Pazar aralığını verir.",
            "method": "ISO hafta Pazartesi başlar; birinci hafta yılın ilk Perşembesini içerir ve hafta yılı takvim yılından farklı olabilir.",
            "limitations": [
                "0001 ve 9999 sınırındaki haftalarda aralık ucu desteklenen yıl aralığını aşarsa null döner."
            ],
            "faqs": [
                {
                    "question": "ISO Hafta Numarası hangi tarih ve saat dilimi yaklaşımını kullanır?",
                    "answer": "Tarih girdileri saat bileşeni taşımayan UTC takvim günleri olarak işlenir. Çekirdek sistem saatini okumaz; gerekiyorsa hedef tarih çağıran tarafından açıkça verilir."
                }
            ]
        },
        "config": {
            "kind": "calendar-date-v1",
            "calculatorId": "tarih-zaman.hafta-hesaplama",
            "minYear": 1,
            "maxYear": 9999
        },
        "testVectors": {
            "kind": "date-time-oracle-v1",
            "cases": [
                {
                    "caseId": "ordinary-week",
                    "scenario": "positive",
                    "input": {
                        "date": "2026-08-20"
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "date": "2026-08-20",
                            "isoWeek": 34,
                            "isoYear": 2026,
                            "isoWeekday": 4,
                            "dayOfYear": 232,
                            "weekStart": "2026-08-17",
                            "weekEnd": "2026-08-23"
                        }
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "prior-iso-year",
                    "scenario": "year-boundary",
                    "input": {
                        "date": "2021-01-01"
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "date": "2021-01-01",
                            "isoWeek": 53,
                            "isoYear": 2020,
                            "isoWeekday": 5,
                            "dayOfYear": 1,
                            "weekStart": "2020-12-28",
                            "weekEnd": "2021-01-03"
                        }
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "leap-week-53",
                    "scenario": "leap-day",
                    "input": {
                        "date": "2020-12-31"
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "date": "2020-12-31",
                            "isoWeek": 53,
                            "isoYear": 2020,
                            "isoWeekday": 4,
                            "dayOfYear": 366,
                            "weekStart": "2020-12-28",
                            "weekEnd": "2021-01-03"
                        }
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "upper-calendar-bound",
                    "scenario": "boundary",
                    "input": {
                        "date": "9999-12-31"
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "date": "9999-12-31",
                            "isoWeek": 52,
                            "isoYear": 9999,
                            "isoWeekday": 5,
                            "dayOfYear": 365,
                            "weekStart": "9999-12-27",
                            "weekEnd": null
                        }
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "impossible-date",
                    "scenario": "adversarial",
                    "input": {
                        "date": "2024-02-30"
                    },
                    "expected": {
                        "ok": false,
                        "field": "date",
                        "errorCode": "UNSUPPORTED_VALUE"
                    },
                    "tolerance": 1e-10
                }
            ]
        },
        "lastReviewed": "2026-08-20"
    },
    {
        "toolId": "relative-grade",
        "slug": "bagil-sistem-not-hesaplama",
        "path": "/egitim-sinav/bagil-sistem-not-hesaplama",
        "category": {
            "id": "egitim-sinav",
            "label": "Eğitim ve Sınav"
        },
        "classification": "remote-config",
        "engineId": "relative-system-grade-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-08-20",
        "effectiveTo": null,
        "minAppVersion": "0.1.0",
        "updateCadence": {
            "mode": "scheduled-source-review",
            "days": 365,
            "reviewAfter": "2027-08-20",
            "specialMonths": [],
            "note": "Kurum eşik ve profil senaryoları yılda en az bir kez insan tarafından kaynaklarıyla incelenir."
        },
        "sources": [
            {
                "sourceId": "openstax-standard-normal-z-score",
                "sourceGroupId": null,
                "title": "Statistics — 6.1 The Standard Normal Distribution",
                "publisher": "OpenStax",
                "url": "https://openstax.org/books/statistics/pages/6-1-the-standard-normal-distribution",
                "kind": "authoritative-education",
                "reviewedOn": "2026-08-20",
                "watchFields": [
                    "Standart skor ve z-puanı yöntemi"
                ]
            },
            {
                "sourceId": "yok-mevzuat-directory",
                "sourceGroupId": null,
                "title": "Yükseköğretim Kurulu Mevzuat Dizini",
                "publisher": "Yükseköğretim Kurulu",
                "url": "https://www.yok.gov.tr/tr/page/mevzuat-aYrMB",
                "kind": "official-government",
                "reviewedOn": "2026-08-20",
                "watchFields": [
                    "Kurumların kendi yönetmelik ve not profillerine yönlendiren mevzuat kapsamı"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 0,
                    "maximum": 100,
                    "maxDecimalPlaces": 4,
                    "options": [],
                    "visibleWhen": null,
                    "id": "score",
                    "type": "number",
                    "label": "Not",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 0,
                    "maximum": 100,
                    "maxDecimalPlaces": 4,
                    "options": [],
                    "visibleWhen": null,
                    "id": "groupMean",
                    "type": "number",
                    "label": "Grup ortalaması",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 0,
                    "maximum": 100,
                    "maxDecimalPlaces": 4,
                    "options": [],
                    "visibleWhen": null,
                    "id": "standardDeviation",
                    "type": "number",
                    "label": "Standart sapma",
                    "required": true,
                    "exclusiveMinimum": true
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "score",
                    "type": "number",
                    "label": "Not",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "groupMean",
                    "type": "number",
                    "label": "Grup ortalaması",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "standardDeviation",
                    "type": "number",
                    "label": "Standart sapma",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "zScore",
                    "type": "number",
                    "label": "Z puanı",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "tScore",
                    "type": "number",
                    "label": "T puanı",
                    "unit": null,
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "grade",
                    "type": "object",
                    "label": "Örnek harf eşlemesi",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "scenario",
                    "type": "object",
                    "label": "Senaryo",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                }
            ]
        },
        "content": {
            "title": "Bağıl Sistem Not Hesaplama",
            "shortTitle": "Bağıl Not",
            "summary": "Not, grup ortalaması ve standart sapmadan z ve T puanı üretir; sürümlü senaryo varsa örnek harf bandını gösterir.",
            "method": "z=(not−ortalama)/standart sapma ve T=50+10z yöntemi uygulanır; harf tablosu matematikten ayrı tipli config'tir.",
            "limitations": [
                "Varsayılan bantlar yalnız örnektir. Kurumsal sonuç için onaylı kurum profili, yürürlük tarihi ve planlı inceleme kaydı gerekir."
            ],
            "faqs": [
                {
                    "question": "Bağıl Not sonucu resmî kurum kararı mıdır?",
                    "answer": "Hayır. Araç yalnız açıkça girilen değerleri ve gösterilen örnek senaryoyu hesaplar. Kuruma göre değişen eşik, harf veya katsayı tablosu için ilgili kurumun güncel yönetmeliği esas alınmalıdır."
                }
            ]
        },
        "config": {
            "kind": "relative-system-grade-v1",
            "calculatorId": "egitim-sinav.bagil-sistem-not-hesaplama",
            "maxScore": 100,
            "maxStandardDeviation": 100,
            "tScoreCenter": 50,
            "tScoreScale": 10,
            "gradeScenario": {
                "id": "example-relative-t-70-65-60",
                "label": "Örnek T-puanı harf tablosu (kurum tablosu değildir)",
                "kind": "example",
                "bands": [
                    {
                        "minimum": 70,
                        "label": "AA",
                        "coefficient": 4
                    },
                    {
                        "minimum": 65,
                        "label": "BA",
                        "coefficient": 3.5
                    },
                    {
                        "minimum": 60,
                        "label": "BB",
                        "coefficient": 3
                    },
                    {
                        "minimum": 55,
                        "label": "CB",
                        "coefficient": 2.5
                    },
                    {
                        "minimum": 50,
                        "label": "CC",
                        "coefficient": 2
                    },
                    {
                        "minimum": 45,
                        "label": "DC",
                        "coefficient": 1.5
                    },
                    {
                        "minimum": 40,
                        "label": "DD",
                        "coefficient": 1
                    },
                    {
                        "minimum": null,
                        "label": "FF",
                        "coefficient": 0
                    }
                ]
            }
        },
        "testVectors": {
            "kind": "education-oracle-v1",
            "cases": [
                {
                    "caseId": "positive-z-score",
                    "scenario": "positive",
                    "input": {
                        "score": 60,
                        "groupMean": 50,
                        "standardDeviation": 10
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "score": 60,
                            "groupMean": 50,
                            "standardDeviation": 10,
                            "zScore": 1,
                            "tScore": 60,
                            "grade": {
                                "label": "BB",
                                "coefficient": 3,
                                "minimum": 60
                            },
                            "scenario": {
                                "id": "example-relative-t-70-65-60",
                                "label": "Örnek T-puanı harf tablosu (kurum tablosu değildir)",
                                "kind": "example"
                            }
                        }
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "mean-equals-score",
                    "scenario": "zero",
                    "input": {
                        "score": 50,
                        "groupMean": 50,
                        "standardDeviation": 10
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "score": 50,
                            "groupMean": 50,
                            "standardDeviation": 10,
                            "zScore": 0,
                            "tScore": 50,
                            "grade": {
                                "label": "CC",
                                "coefficient": 2,
                                "minimum": 50
                            },
                            "scenario": {
                                "id": "example-relative-t-70-65-60",
                                "label": "Örnek T-puanı harf tablosu (kurum tablosu değildir)",
                                "kind": "example"
                            }
                        }
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "score-and-deviation-boundary",
                    "scenario": "boundary",
                    "input": {
                        "score": 100,
                        "groupMean": 0,
                        "standardDeviation": 100
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "score": 100,
                            "groupMean": 0,
                            "standardDeviation": 100,
                            "zScore": 1,
                            "tScore": 60,
                            "grade": {
                                "label": "BB",
                                "coefficient": 3,
                                "minimum": 60
                            },
                            "scenario": {
                                "id": "example-relative-t-70-65-60",
                                "label": "Örnek T-puanı harf tablosu (kurum tablosu değildir)",
                                "kind": "example"
                            }
                        }
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "zero-standard-deviation",
                    "scenario": "adversarial",
                    "input": {
                        "score": 50,
                        "groupMean": 50,
                        "standardDeviation": 0
                    },
                    "expected": {
                        "ok": false,
                        "field": "standardDeviation",
                        "errorCode": "DIVISION_BY_ZERO"
                    },
                    "tolerance": 1e-10
                }
            ]
        },
        "lastReviewed": "2026-08-20"
    },
    {
        "toolId": "report-card-average",
        "slug": "karne-notu-hesaplama",
        "path": "/egitim-sinav/karne-notu-hesaplama",
        "category": {
            "id": "egitim-sinav",
            "label": "Eğitim ve Sınav"
        },
        "classification": "timeless",
        "engineId": "report-card-average-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-08-20",
        "effectiveTo": null,
        "minAppVersion": "0.1.0",
        "updateCadence": {
            "mode": "method-change-only",
            "days": null,
            "reviewAfter": null,
            "specialMonths": [],
            "note": "Yalnız kullanıcı girdili matematik yöntemi veya dayanak kaynak değiştiğinde incelenir."
        },
        "sources": [
            {
                "sourceId": "openstax-weighted-mean",
                "sourceGroupId": null,
                "title": "Principles of Finance — 13.1 Measures of Center",
                "publisher": "OpenStax",
                "url": "https://openstax.org/books/principles-finance/pages/13-1-measures-of-center",
                "kind": "authoritative-education",
                "reviewedOn": "2026-08-20",
                "watchFields": [
                    "Ağırlıklı ortalama yöntemi"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [],
                    "visibleWhen": null,
                    "id": "courses",
                    "type": "object-list",
                    "label": "Dersler",
                    "required": true,
                    "itemFields": [
                        {
                            "id": "name",
                            "type": "text",
                            "label": "Ders adı",
                            "required": false,
                            "minimum": null,
                            "maximum": null,
                            "maxDecimalPlaces": null
                        },
                        {
                            "id": "score",
                            "type": "number",
                            "label": "Not",
                            "required": true,
                            "minimum": 0,
                            "maximum": 100,
                            "maxDecimalPlaces": 4
                        },
                        {
                            "id": "weeklyHours",
                            "type": "number",
                            "label": "Haftalık saat",
                            "required": true,
                            "minimum": 0,
                            "maximum": 40,
                            "maxDecimalPlaces": 4,
                            "exclusiveMinimum": true
                        }
                    ]
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "courses",
                    "type": "object-list",
                    "label": "Ders sonuçları",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "courseCount",
                    "type": "number",
                    "label": "Ders sayısı",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "totalWeeklyHours",
                    "type": "number",
                    "label": "Toplam haftalık saat",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "weightedPointTotal",
                    "type": "number",
                    "label": "Ağırlıklı puan toplamı",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "weightedAverage",
                    "type": "number",
                    "label": "Ağırlıklı ortalama",
                    "unit": null,
                    "nullable": false,
                    "prominent": true
                }
            ]
        },
        "content": {
            "title": "Karne Notu Ağırlıklı Ortalama Hesaplama",
            "shortTitle": "Karne Ortalaması",
            "summary": "Ders notlarını kullanıcının verdiği haftalık ders saatiyle ağırlıklandırarak sayısal ortalama üretir.",
            "method": "Her not haftalık saatle çarpılır; toplam puan, pozitif toplam haftalık saate bölünür.",
            "limitations": [
                "Belge, sınıf geçme veya resmî karne kararı üretmez; yalnız matematiksel ağırlıklı ortalamadır."
            ],
            "faqs": [
                {
                    "question": "Karne Ortalaması sonucu resmî kurum kararı mıdır?",
                    "answer": "Hayır. Araç yalnız açıkça girilen değerleri ve gösterilen örnek senaryoyu hesaplar. Kuruma göre değişen eşik, harf veya katsayı tablosu için ilgili kurumun güncel yönetmeliği esas alınmalıdır."
                }
            ]
        },
        "config": {
            "kind": "report-card-average-v1",
            "calculatorId": "egitim-sinav.karne-notu-hesaplama",
            "maxScore": 100,
            "maxCourses": 100,
            "maxWeeklyHours": 40
        },
        "testVectors": {
            "kind": "education-oracle-v1",
            "cases": [
                {
                    "caseId": "weighted-courses",
                    "scenario": "weighted",
                    "input": {
                        "courses": [
                            {
                                "name": "Matematik",
                                "score": 80,
                                "weeklyHours": 5
                            },
                            {
                                "name": "Türkçe",
                                "score": 90,
                                "weeklyHours": 2
                            }
                        ]
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "courses": [
                                {
                                    "name": "Matematik",
                                    "score": 80,
                                    "weeklyHours": 5,
                                    "weightedPoints": 400
                                },
                                {
                                    "name": "Türkçe",
                                    "score": 90,
                                    "weeklyHours": 2,
                                    "weightedPoints": 180
                                }
                            ],
                            "courseCount": 2,
                            "totalWeeklyHours": 7,
                            "weightedPointTotal": 580,
                            "weightedAverage": 82.85714285714286
                        }
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "zero-score-positive-denominator",
                    "scenario": "zero",
                    "input": {
                        "courses": [
                            {
                                "score": 0,
                                "weeklyHours": 4
                            },
                            {
                                "score": 100,
                                "weeklyHours": 4
                            }
                        ]
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "courses": [
                                {
                                    "name": "Ders 1",
                                    "score": 0,
                                    "weeklyHours": 4,
                                    "weightedPoints": 0
                                },
                                {
                                    "name": "Ders 2",
                                    "score": 100,
                                    "weeklyHours": 4,
                                    "weightedPoints": 400
                                }
                            ],
                            "courseCount": 2,
                            "totalWeeklyHours": 8,
                            "weightedPointTotal": 400,
                            "weightedAverage": 50
                        }
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "weekly-hour-upper-bound",
                    "scenario": "boundary",
                    "input": {
                        "courses": [
                            {
                                "score": 100,
                                "weeklyHours": 40
                            }
                        ]
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "courses": [
                                {
                                    "name": "Ders 1",
                                    "score": 100,
                                    "weeklyHours": 40,
                                    "weightedPoints": 4000
                                }
                            ],
                            "courseCount": 1,
                            "totalWeeklyHours": 40,
                            "weightedPointTotal": 4000,
                            "weightedAverage": 100
                        }
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "empty-course-list",
                    "scenario": "adversarial",
                    "input": {
                        "courses": []
                    },
                    "expected": {
                        "ok": false,
                        "field": "courses",
                        "errorCode": "REQUIRED"
                    },
                    "tolerance": 1e-10
                }
            ]
        },
        "lastReviewed": "2026-08-20"
    },
    {
        "toolId": "grade-average",
        "slug": "not-ortalamasi-hesaplama",
        "path": "/egitim-sinav/not-ortalamasi-hesaplama",
        "category": {
            "id": "egitim-sinav",
            "label": "Eğitim ve Sınav"
        },
        "classification": "remote-config",
        "engineId": "grade-average-v2",
        "schemaVersion": "2.0.0",
        "contentVersion": "2.0.0",
        "configVersion": "2.0.0",
        "effectiveFrom": "2026-08-22",
        "effectiveTo": null,
        "minAppVersion": "0.1.0",
        "updateCadence": {
            "mode": "scheduled-source-review",
            "days": 365,
            "reviewAfter": "2027-08-20",
            "specialMonths": [],
            "note": "Kurum eşik ve profil senaryoları yılda en az bir kez insan tarafından kaynaklarıyla incelenir."
        },
        "sources": [
            {
                "sourceId": "openstax-weighted-mean",
                "sourceGroupId": null,
                "title": "Principles of Finance — 13.1 Measures of Center",
                "publisher": "OpenStax",
                "url": "https://openstax.org/books/principles-finance/pages/13-1-measures-of-center",
                "kind": "authoritative-education",
                "reviewedOn": "2026-08-20",
                "watchFields": [
                    "Ağırlıklı ortalama yöntemi"
                ]
            },
            {
                "sourceId": "yok-mevzuat-directory",
                "sourceGroupId": null,
                "title": "Yükseköğretim Kurulu Mevzuat Dizini",
                "publisher": "Yükseköğretim Kurulu",
                "url": "https://www.yok.gov.tr/tr/page/mevzuat-aYrMB",
                "kind": "official-government",
                "reviewedOn": "2026-08-20",
                "watchFields": [
                    "Kurumların kendi yönetmelik ve not profillerine yönlendiren mevzuat kapsamı"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": "average",
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "average",
                            "label": "Ders ortalaması"
                        },
                        {
                            "value": "single-grade",
                            "label": "Tek not, harf ve baraj"
                        }
                    ],
                    "visibleWhen": null,
                    "id": "mode",
                    "type": "select",
                    "label": "Ne hesaplamak istiyorsun?",
                    "required": true
                },
                {
                    "defaultValue": "weighted",
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "weighted",
                            "label": "Kredili"
                        },
                        {
                            "value": "unweighted",
                            "label": "Kredisiz"
                        }
                    ],
                    "visibleWhen": {
                        "fieldId": "mode",
                        "equals": "average"
                    },
                    "id": "averageMode",
                    "type": "select",
                    "label": "Ortalama türü",
                    "required": false
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [],
                    "visibleWhen": {
                        "fieldId": "mode",
                        "equals": "average"
                    },
                    "id": "courses",
                    "type": "object-list",
                    "label": "Dersler",
                    "required": false,
                    "itemFields": [
                        {
                            "id": "name",
                            "type": "text",
                            "label": "Ders adı",
                            "required": false,
                            "minimum": null,
                            "maximum": null,
                            "maxDecimalPlaces": null
                        },
                        {
                            "id": "score",
                            "type": "number",
                            "label": "Not",
                            "required": true,
                            "minimum": 0,
                            "maximum": 100,
                            "maxDecimalPlaces": 4
                        },
                        {
                            "id": "credits",
                            "type": "number",
                            "label": "Kredi",
                            "required": false,
                            "minimum": 0,
                            "maximum": 10,
                            "maxDecimalPlaces": 4,
                            "exclusiveMinimum": true
                        }
                    ]
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 0,
                    "maximum": 100,
                    "maxDecimalPlaces": 4,
                    "options": [],
                    "visibleWhen": {
                        "fieldId": "mode",
                        "equals": "single-grade"
                    },
                    "id": "score",
                    "type": "number",
                    "label": "Not",
                    "required": false
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 0,
                    "maximum": 100,
                    "maxDecimalPlaces": 4,
                    "options": [],
                    "visibleWhen": {
                        "fieldId": "mode",
                        "equals": "single-grade"
                    },
                    "id": "threshold",
                    "type": "number",
                    "label": "Kurumundan aldığın sayısal baraj",
                    "required": false
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "mode",
                    "type": "text",
                    "label": "Hesaplama modu",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "averageMode",
                    "type": "text",
                    "label": "Ortalama türü",
                    "unit": null,
                    "nullable": true,
                    "prominent": false
                },
                {
                    "id": "courses",
                    "type": "object-list",
                    "label": "Ders sonuçları",
                    "unit": null,
                    "nullable": true,
                    "prominent": false
                },
                {
                    "id": "courseCount",
                    "type": "number",
                    "label": "Ders sayısı",
                    "unit": null,
                    "nullable": true,
                    "prominent": false
                },
                {
                    "id": "totalWeight",
                    "type": "number",
                    "label": "Toplam ağırlık",
                    "unit": null,
                    "nullable": true,
                    "prominent": false
                },
                {
                    "id": "weightedPointTotal",
                    "type": "number",
                    "label": "Ağırlıklı puan toplamı",
                    "unit": null,
                    "nullable": true,
                    "prominent": false
                },
                {
                    "id": "average100",
                    "type": "number",
                    "label": "100'lük ortalama",
                    "unit": null,
                    "nullable": true,
                    "prominent": true
                },
                {
                    "id": "gradePointAverage",
                    "type": "number",
                    "label": "Örnek katsayı ortalaması",
                    "unit": null,
                    "nullable": true,
                    "prominent": false
                },
                {
                    "id": "score",
                    "type": "number",
                    "label": "Not",
                    "unit": null,
                    "nullable": true,
                    "prominent": false
                },
                {
                    "id": "threshold",
                    "type": "number",
                    "label": "Baraj",
                    "unit": null,
                    "nullable": true,
                    "prominent": false
                },
                {
                    "id": "differenceFromThreshold",
                    "type": "number",
                    "label": "Baraj farkı",
                    "unit": null,
                    "nullable": true,
                    "prominent": false
                },
                {
                    "id": "meetsThreshold",
                    "type": "boolean",
                    "label": "Barajı sağlıyor",
                    "unit": null,
                    "nullable": true,
                    "prominent": false
                },
                {
                    "id": "classification",
                    "type": "object",
                    "label": "Örnek sınıflandırma",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "scenario",
                    "type": "object",
                    "label": "Senaryo",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                }
            ]
        },
        "content": {
            "title": "Not Ortalaması, Harf Notu ve Baraj Hesaplama",
            "shortTitle": "Not ve Harf",
            "summary": "Kredili/kredisiz ders ortalamasını veya tek notun örnek harf eşlemesi ile kullanıcı barajına göre durumunu ayrı modlarda hesaplar.",
            "method": "Ortalama modunda puanların ağırlıklı ortalaması; tek not modunda örnek bant eşlemesi ve not−baraj farkı hesaplanır.",
            "limitations": [
                "Gösterilen harf, katsayı ve başarı bantları evrensel kural değildir. Kullanıcı kendi kurumunun güncel tablosunu ve barajını doğrulamalıdır."
            ],
            "faqs": [
                {
                    "question": "Not ve Harf sonucu resmî kurum kararı mıdır?",
                    "answer": "Hayır. Araç yalnız açıkça girilen değerleri ve gösterilen örnek senaryoyu hesaplar. Kuruma göre değişen eşik, harf veya katsayı tablosu için ilgili kurumun güncel yönetmeliği esas alınmalıdır."
                }
            ]
        },
        "config": {
            "kind": "grade-average-v2",
            "calculatorId": "egitim-sinav.not-ortalamasi-hesaplama",
            "maxScore": 100,
            "maxCourses": 100,
            "maxCredits": 10,
            "weightedGradeScenario": {
                "id": "example-absolute-90-85-80",
                "label": "Örnek mutlak harf tablosu (kurum tablosu değildir)",
                "kind": "example",
                "bands": [
                    {
                        "minimum": 90,
                        "label": "AA",
                        "coefficient": 4
                    },
                    {
                        "minimum": 85,
                        "label": "BA",
                        "coefficient": 3.5
                    },
                    {
                        "minimum": 80,
                        "label": "BB",
                        "coefficient": 3
                    },
                    {
                        "minimum": 75,
                        "label": "CB",
                        "coefficient": 2.5
                    },
                    {
                        "minimum": 70,
                        "label": "CC",
                        "coefficient": 2
                    },
                    {
                        "minimum": 65,
                        "label": "DC",
                        "coefficient": 1.5
                    },
                    {
                        "minimum": 60,
                        "label": "DD",
                        "coefficient": 1
                    },
                    {
                        "minimum": 0,
                        "label": "FF",
                        "coefficient": 0
                    }
                ]
            },
            "unweightedBandScenario": {
                "id": "example-unweighted-bands-85-70-50",
                "label": "Örnek genel ortalama bantları (resmî belge kararı değildir)",
                "kind": "example",
                "bands": [
                    {
                        "minimum": 85,
                        "label": "Takdir Bandı",
                        "coefficient": null
                    },
                    {
                        "minimum": 70,
                        "label": "Teşekkür Bandı",
                        "coefficient": null
                    },
                    {
                        "minimum": 50,
                        "label": "Geçer",
                        "coefficient": null
                    },
                    {
                        "minimum": 0,
                        "label": "Başarısız",
                        "coefficient": null
                    }
                ]
            }
        },
        "testVectors": {
            "kind": "education-oracle-v1",
            "cases": [
                {
                    "caseId": "weighted-grade-average",
                    "scenario": "weighted",
                    "input": {
                        "mode": "average",
                        "averageMode": "weighted",
                        "courses": [
                            {
                                "name": "A",
                                "score": 85,
                                "credits": 4
                            },
                            {
                                "name": "B",
                                "score": 70,
                                "credits": 2
                            }
                        ]
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "mode": "average",
                            "averageMode": "weighted",
                            "courses": [
                                {
                                    "name": "A",
                                    "score": 85,
                                    "weight": 4,
                                    "weightedPoints": 340,
                                    "grade": {
                                        "label": "BA",
                                        "coefficient": 3.5,
                                        "minimum": 85
                                    }
                                },
                                {
                                    "name": "B",
                                    "score": 70,
                                    "weight": 2,
                                    "weightedPoints": 140,
                                    "grade": {
                                        "label": "CC",
                                        "coefficient": 2,
                                        "minimum": 70
                                    }
                                }
                            ],
                            "courseCount": 2,
                            "totalWeight": 6,
                            "weightedPointTotal": 480,
                            "average100": 80,
                            "gradePointAverage": 3,
                            "score": null,
                            "threshold": null,
                            "differenceFromThreshold": null,
                            "meetsThreshold": null,
                            "classification": {
                                "label": "BB",
                                "coefficient": 3,
                                "minimum": 80
                            },
                            "scenario": {
                                "id": "example-absolute-90-85-80",
                                "label": "Örnek mutlak harf tablosu (kurum tablosu değildir)",
                                "kind": "example"
                            }
                        }
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "unweighted-average",
                    "scenario": "unweighted",
                    "input": {
                        "mode": "average",
                        "averageMode": "unweighted",
                        "courses": [
                            {
                                "score": 85
                            },
                            {
                                "score": 55
                            },
                            {
                                "score": 70
                            }
                        ]
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "mode": "average",
                            "averageMode": "unweighted",
                            "courses": [
                                {
                                    "name": "Ders 1",
                                    "score": 85,
                                    "weight": 1,
                                    "weightedPoints": 85,
                                    "grade": null
                                },
                                {
                                    "name": "Ders 2",
                                    "score": 55,
                                    "weight": 1,
                                    "weightedPoints": 55,
                                    "grade": null
                                },
                                {
                                    "name": "Ders 3",
                                    "score": 70,
                                    "weight": 1,
                                    "weightedPoints": 70,
                                    "grade": null
                                }
                            ],
                            "courseCount": 3,
                            "totalWeight": 3,
                            "weightedPointTotal": 210,
                            "average100": 70,
                            "gradePointAverage": null,
                            "score": null,
                            "threshold": null,
                            "differenceFromThreshold": null,
                            "meetsThreshold": null,
                            "classification": {
                                "label": "Teşekkür Bandı",
                                "coefficient": null,
                                "minimum": 70
                            },
                            "scenario": {
                                "id": "example-unweighted-bands-85-70-50",
                                "label": "Örnek genel ortalama bantları (resmî belge kararı değildir)",
                                "kind": "example"
                            }
                        }
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "single-grade-threshold",
                    "scenario": "threshold",
                    "input": {
                        "mode": "single-grade",
                        "score": 78,
                        "threshold": 60
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "mode": "single-grade",
                            "averageMode": null,
                            "courses": null,
                            "courseCount": null,
                            "totalWeight": null,
                            "weightedPointTotal": null,
                            "average100": null,
                            "gradePointAverage": null,
                            "score": 78,
                            "threshold": 60,
                            "differenceFromThreshold": 18,
                            "meetsThreshold": true,
                            "classification": {
                                "label": "CB",
                                "coefficient": 2.5,
                                "minimum": 75
                            },
                            "scenario": {
                                "id": "example-absolute-90-85-80",
                                "label": "Örnek mutlak harf tablosu (kurum tablosu değildir)",
                                "kind": "example"
                            }
                        }
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "zero-credit",
                    "scenario": "adversarial",
                    "input": {
                        "mode": "average",
                        "averageMode": "weighted",
                        "courses": [
                            {
                                "score": 80,
                                "credits": 0
                            }
                        ]
                    },
                    "expected": {
                        "ok": false,
                        "field": "courses[0].credits",
                        "errorCode": "OUT_OF_RANGE"
                    },
                    "tolerance": 1e-10
                }
            ]
        },
        "lastReviewed": "2026-08-22"
    },
    {
        "toolId": "required-final-score",
        "slug": "vize-final-gecme-notu-hesaplama",
        "path": "/egitim-sinav/vize-final-gecme-notu-hesaplama",
        "category": {
            "id": "egitim-sinav",
            "label": "Eğitim ve Sınav"
        },
        "classification": "timeless",
        "engineId": "required-final-score-v2",
        "schemaVersion": "2.0.0",
        "contentVersion": "2.0.0",
        "configVersion": "2.0.0",
        "effectiveFrom": "2026-08-22",
        "effectiveTo": null,
        "minAppVersion": "0.1.0",
        "updateCadence": {
            "mode": "method-change-only",
            "days": null,
            "reviewAfter": null,
            "specialMonths": [],
            "note": "Yalnız kullanıcı girdili matematik yöntemi veya dayanak kaynak değiştiğinde incelenir."
        },
        "sources": [
            {
                "sourceId": "openstax-weighted-mean",
                "sourceGroupId": null,
                "title": "Principles of Finance — 13.1 Measures of Center",
                "publisher": "OpenStax",
                "url": "https://openstax.org/books/principles-finance/pages/13-1-measures-of-center",
                "kind": "authoritative-education",
                "reviewedOn": "2026-08-20",
                "watchFields": [
                    "Ağırlıklı ortalama yöntemi"
                ]
            },
            {
                "sourceId": "yok-mevzuat-directory",
                "sourceGroupId": null,
                "title": "Yükseköğretim Kurulu Mevzuat Dizini",
                "publisher": "Yükseköğretim Kurulu",
                "url": "https://www.yok.gov.tr/tr/page/mevzuat-aYrMB",
                "kind": "official-government",
                "reviewedOn": "2026-08-20",
                "watchFields": [
                    "Kurumların kendi yönetmelik ve not profillerine yönlendiren mevzuat kapsamı"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": "required-final",
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "weighted-total",
                            "label": "Vize-final toplamı"
                        },
                        {
                            "value": "required-final",
                            "label": "Gerekli final notu"
                        },
                        {
                            "value": "threshold-check",
                            "label": "Baraj karşılaştırma"
                        }
                    ],
                    "visibleWhen": null,
                    "id": "mode",
                    "type": "select",
                    "label": "Ne hesaplamak istiyorsun?",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 0,
                    "maximum": 100,
                    "maxDecimalPlaces": 4,
                    "options": [],
                    "visibleWhen": {
                        "fieldId": "mode",
                        "equals": "weighted-total"
                    },
                    "id": "observedMidtermScore",
                    "type": "number",
                    "label": "Vize notu",
                    "required": false
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 0,
                    "maximum": 100,
                    "maxDecimalPlaces": 4,
                    "options": [],
                    "visibleWhen": {
                        "fieldId": "mode",
                        "equals": "weighted-total"
                    },
                    "id": "observedFinalScore",
                    "type": "number",
                    "label": "Final notu",
                    "required": false
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "%",
                    "minimum": 0,
                    "maximum": 100,
                    "maxDecimalPlaces": 4,
                    "options": [],
                    "visibleWhen": {
                        "fieldId": "mode",
                        "equals": "weighted-total"
                    },
                    "id": "observedMidtermWeightPercent",
                    "type": "number",
                    "label": "Vize ağırlığı",
                    "required": false
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "%",
                    "minimum": 0,
                    "maximum": 100,
                    "maxDecimalPlaces": 4,
                    "options": [],
                    "visibleWhen": {
                        "fieldId": "mode",
                        "equals": "weighted-total"
                    },
                    "id": "observedFinalWeightPercent",
                    "type": "number",
                    "label": "Final ağırlığı",
                    "required": false
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 0,
                    "maximum": 100,
                    "maxDecimalPlaces": 4,
                    "options": [],
                    "visibleWhen": {
                        "fieldId": "mode",
                        "equals": "required-final"
                    },
                    "id": "midtermScore",
                    "type": "number",
                    "label": "Vize notu",
                    "required": false
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 0,
                    "maximum": 100,
                    "maxDecimalPlaces": 4,
                    "options": [],
                    "visibleWhen": {
                        "fieldId": "mode",
                        "equals": "required-final"
                    },
                    "id": "targetAverage",
                    "type": "number",
                    "label": "Hedef ortalama",
                    "required": false
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "%",
                    "minimum": 0,
                    "maximum": 100,
                    "maxDecimalPlaces": 4,
                    "options": [],
                    "visibleWhen": {
                        "fieldId": "mode",
                        "equals": "required-final"
                    },
                    "id": "midtermWeightPercent",
                    "type": "number",
                    "label": "Vize ağırlığı",
                    "required": false
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "%",
                    "minimum": 0,
                    "maximum": 100,
                    "maxDecimalPlaces": 4,
                    "options": [],
                    "visibleWhen": {
                        "fieldId": "mode",
                        "equals": "required-final"
                    },
                    "id": "finalWeightPercent",
                    "type": "number",
                    "label": "Final ağırlığı",
                    "required": false
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 0,
                    "maximum": 100,
                    "maxDecimalPlaces": 4,
                    "options": [],
                    "visibleWhen": {
                        "fieldId": "mode",
                        "equals": "threshold-check"
                    },
                    "id": "examScore",
                    "type": "number",
                    "label": "Sınav notu",
                    "required": false
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 0,
                    "maximum": 100,
                    "maxDecimalPlaces": 4,
                    "options": [],
                    "visibleWhen": {
                        "fieldId": "mode",
                        "equals": "threshold-check"
                    },
                    "id": "threshold",
                    "type": "number",
                    "label": "Kurumundan aldığın sayısal baraj",
                    "required": false
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 0,
                    "maximum": 100,
                    "maxDecimalPlaces": 4,
                    "options": [],
                    "visibleWhen": {
                        "fieldId": "mode",
                        "equals": "threshold-check"
                    },
                    "id": "priorScore",
                    "type": "number",
                    "label": "Önceki not (isteğe bağlı)",
                    "required": false
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "%",
                    "minimum": 0,
                    "maximum": 100,
                    "maxDecimalPlaces": 4,
                    "options": [],
                    "visibleWhen": {
                        "fieldId": "mode",
                        "equals": "threshold-check"
                    },
                    "id": "priorWeightPercent",
                    "type": "number",
                    "label": "Önceki not ağırlığı (isteğe bağlı)",
                    "required": false
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "mode",
                    "type": "text",
                    "label": "Hesaplama modu",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "observedMidtermScore",
                    "type": "number",
                    "label": "Vize notu",
                    "unit": null,
                    "nullable": true,
                    "prominent": false
                },
                {
                    "id": "observedFinalScore",
                    "type": "number",
                    "label": "Final notu",
                    "unit": null,
                    "nullable": true,
                    "prominent": false
                },
                {
                    "id": "observedMidtermWeightPercent",
                    "type": "percentage",
                    "label": "Vize ağırlığı",
                    "unit": "%",
                    "nullable": true,
                    "prominent": false
                },
                {
                    "id": "observedFinalWeightPercent",
                    "type": "percentage",
                    "label": "Final ağırlığı",
                    "unit": "%",
                    "nullable": true,
                    "prominent": false
                },
                {
                    "id": "observedMidtermContribution",
                    "type": "number",
                    "label": "Vize katkısı",
                    "unit": null,
                    "nullable": true,
                    "prominent": false
                },
                {
                    "id": "observedFinalContribution",
                    "type": "number",
                    "label": "Final katkısı",
                    "unit": null,
                    "nullable": true,
                    "prominent": false
                },
                {
                    "id": "weightedTotal",
                    "type": "number",
                    "label": "Ağırlıklı toplam",
                    "unit": null,
                    "nullable": true,
                    "prominent": true
                },
                {
                    "id": "weightTotal",
                    "type": "percentage",
                    "label": "Ağırlık toplamı",
                    "unit": "%",
                    "nullable": true,
                    "prominent": false
                },
                {
                    "id": "midtermScore",
                    "type": "number",
                    "label": "Vize notu",
                    "unit": null,
                    "nullable": true,
                    "prominent": false
                },
                {
                    "id": "targetAverage",
                    "type": "number",
                    "label": "Hedef ortalama",
                    "unit": null,
                    "nullable": true,
                    "prominent": false
                },
                {
                    "id": "midtermWeightPercent",
                    "type": "percentage",
                    "label": "Vize ağırlığı",
                    "unit": "%",
                    "nullable": true,
                    "prominent": false
                },
                {
                    "id": "finalWeightPercent",
                    "type": "percentage",
                    "label": "Final ağırlığı",
                    "unit": "%",
                    "nullable": true,
                    "prominent": false
                },
                {
                    "id": "midtermContribution",
                    "type": "number",
                    "label": "Vize katkısı",
                    "unit": null,
                    "nullable": true,
                    "prominent": false
                },
                {
                    "id": "requiredFinalScore",
                    "type": "number",
                    "label": "Gerekli final notu",
                    "unit": null,
                    "nullable": true,
                    "prominent": true
                },
                {
                    "id": "status",
                    "type": "text",
                    "label": "Erişilebilirlik durumu",
                    "unit": null,
                    "nullable": true,
                    "prominent": false
                },
                {
                    "id": "thresholdMode",
                    "type": "text",
                    "label": "Baraj değerlendirme türü",
                    "unit": null,
                    "nullable": true,
                    "prominent": false
                },
                {
                    "id": "examScore",
                    "type": "number",
                    "label": "Sınav notu",
                    "unit": null,
                    "nullable": true,
                    "prominent": false
                },
                {
                    "id": "threshold",
                    "type": "number",
                    "label": "Baraj",
                    "unit": null,
                    "nullable": true,
                    "prominent": false
                },
                {
                    "id": "priorScore",
                    "type": "number",
                    "label": "Önceki not",
                    "unit": null,
                    "nullable": true,
                    "prominent": false
                },
                {
                    "id": "priorWeightPercent",
                    "type": "percentage",
                    "label": "Önceki ağırlık",
                    "unit": "%",
                    "nullable": true,
                    "prominent": false
                },
                {
                    "id": "examWeightPercent",
                    "type": "percentage",
                    "label": "Sınav ağırlığı",
                    "unit": "%",
                    "nullable": true,
                    "prominent": false
                },
                {
                    "id": "evaluatedScore",
                    "type": "number",
                    "label": "Değerlendirilen not",
                    "unit": null,
                    "nullable": true,
                    "prominent": true
                },
                {
                    "id": "differenceFromThreshold",
                    "type": "number",
                    "label": "Baraj farkı",
                    "unit": null,
                    "nullable": true,
                    "prominent": false
                },
                {
                    "id": "meetsThreshold",
                    "type": "boolean",
                    "label": "Barajı sağlıyor",
                    "unit": null,
                    "nullable": true,
                    "prominent": false
                }
            ]
        },
        "content": {
            "title": "Vize-Final, Gerekli Not ve Baraj Hesaplama",
            "shortTitle": "Vize-Final Planlayıcı",
            "summary": "Vize-final toplamını, hedef için gerekli final notunu veya bir sınav notunun kullanıcı barajına göre durumunu ayrı modlarda hesaplar.",
            "method": "Toplam modunda yüzde katkıları toplanır; gerekli not modunda final bilinmeyeni yalnız bırakılır; baraj modunda doğrudan ya da isteğe bağlı önceki notla ağırlıklı karşılaştırma yapılır.",
            "limitations": [
                "Araç kurumun final alt barajını, sınava girme hakkını, mezuniyet veya resmî başarı kararını belirlemez. Güncel ağırlık, hedef ve barajı kullanıcı doğrulamalıdır."
            ],
            "faqs": [
                {
                    "question": "Vize-Final Planlayıcı sonucu resmî kurum kararı mıdır?",
                    "answer": "Hayır. Araç yalnız açıkça girilen değerleri ve gösterilen örnek senaryoyu hesaplar. Kuruma göre değişen eşik, harf veya katsayı tablosu için ilgili kurumun güncel yönetmeliği esas alınmalıdır."
                }
            ]
        },
        "config": {
            "kind": "weighted-assessment-v2",
            "calculatorId": "egitim-sinav.vize-final-gecme-notu-hesaplama",
            "maxScore": 100,
            "weightTotal": 100,
            "weightTolerance": 0.01
        },
        "testVectors": {
            "kind": "education-oracle-v1",
            "cases": [
                {
                    "caseId": "weighted-total",
                    "scenario": "positive",
                    "input": {
                        "mode": "weighted-total",
                        "observedMidtermScore": 70,
                        "observedFinalScore": 80,
                        "observedMidtermWeightPercent": 40,
                        "observedFinalWeightPercent": 60
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "mode": "weighted-total",
                            "observedMidtermScore": 70,
                            "observedFinalScore": 80,
                            "observedMidtermWeightPercent": 40,
                            "observedFinalWeightPercent": 60,
                            "observedMidtermContribution": 28,
                            "observedFinalContribution": 48,
                            "weightedTotal": 76,
                            "weightTotal": 100,
                            "midtermScore": null,
                            "targetAverage": null,
                            "midtermWeightPercent": null,
                            "finalWeightPercent": null,
                            "midtermContribution": null,
                            "requiredFinalScore": null,
                            "status": null,
                            "thresholdMode": null,
                            "examScore": null,
                            "threshold": null,
                            "priorScore": null,
                            "priorWeightPercent": null,
                            "examWeightPercent": null,
                            "evaluatedScore": null,
                            "differenceFromThreshold": null,
                            "meetsThreshold": null
                        }
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "reachable-final",
                    "scenario": "boundary",
                    "input": {
                        "mode": "required-final",
                        "midtermScore": 65,
                        "targetAverage": 60,
                        "midtermWeightPercent": 40,
                        "finalWeightPercent": 60
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "mode": "required-final",
                            "observedMidtermScore": null,
                            "observedFinalScore": null,
                            "observedMidtermWeightPercent": null,
                            "observedFinalWeightPercent": null,
                            "observedMidtermContribution": null,
                            "observedFinalContribution": null,
                            "weightedTotal": null,
                            "weightTotal": null,
                            "midtermScore": 65,
                            "targetAverage": 60,
                            "midtermWeightPercent": 40,
                            "finalWeightPercent": 60,
                            "midtermContribution": 26,
                            "requiredFinalScore": 56.66666666666667,
                            "status": "reachable",
                            "thresholdMode": null,
                            "examScore": null,
                            "threshold": null,
                            "priorScore": null,
                            "priorWeightPercent": null,
                            "examWeightPercent": null,
                            "evaluatedScore": null,
                            "differenceFromThreshold": null,
                            "meetsThreshold": null
                        }
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "weighted-threshold-check",
                    "scenario": "threshold",
                    "input": {
                        "mode": "threshold-check",
                        "examScore": 65,
                        "threshold": 50,
                        "priorScore": 55,
                        "priorWeightPercent": 40
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "mode": "threshold-check",
                            "observedMidtermScore": null,
                            "observedFinalScore": null,
                            "observedMidtermWeightPercent": null,
                            "observedFinalWeightPercent": null,
                            "observedMidtermContribution": null,
                            "observedFinalContribution": null,
                            "weightedTotal": null,
                            "weightTotal": null,
                            "midtermScore": null,
                            "targetAverage": null,
                            "midtermWeightPercent": null,
                            "finalWeightPercent": null,
                            "midtermContribution": null,
                            "requiredFinalScore": null,
                            "status": null,
                            "thresholdMode": "weighted",
                            "examScore": 65,
                            "threshold": 50,
                            "priorScore": 55,
                            "priorWeightPercent": 40,
                            "examWeightPercent": 60,
                            "evaluatedScore": 61,
                            "differenceFromThreshold": 11,
                            "meetsThreshold": true
                        }
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "zero-final-weight",
                    "scenario": "adversarial",
                    "input": {
                        "mode": "required-final",
                        "midtermScore": 60,
                        "targetAverage": 60,
                        "midtermWeightPercent": 100,
                        "finalWeightPercent": 0
                    },
                    "expected": {
                        "ok": false,
                        "field": "finalWeightPercent",
                        "errorCode": "DIVISION_BY_ZERO"
                    },
                    "tolerance": 1e-10
                }
            ]
        },
        "lastReviewed": "2026-08-22"
    },
    {
        "toolId": "lpg-payback",
        "slug": "lpg-donusum-amortisman-hesaplama",
        "path": "/arac-emlak-yasam/lpg-donusum-amortisman-hesaplama",
        "category": {
            "id": "arac-emlak-yasam",
            "label": "Araç, Emlak ve Yaşam"
        },
        "classification": "timeless",
        "engineId": "lpg-payback-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "2026.8.20",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-08-20",
        "effectiveTo": null,
        "minAppVersion": "0.1.0",
        "updateCadence": {
            "mode": "method-change-only",
            "days": null,
            "reviewAfter": null,
            "specialMonths": [],
            "note": "Hesap yöntemi değişmedikçe kural güncellemesi gerekmez; yıllık işlev ve metin QA'sı yapılabilir."
        },
        "sources": [],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "conversionCost",
                    "type": "number",
                    "label": "Dönüşüm Maliyeti",
                    "required": true,
                    "defaultValue": null,
                    "placeholder": "Ör: 25000",
                    "unit": "TL",
                    "minimum": 1e-9,
                    "exclusiveMinimum": false,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 2,
                    "options": [],
                    "visibleWhen": null
                },
                {
                    "id": "extraOneOffCost",
                    "type": "number",
                    "label": "Ek Tek Seferlik Masraf",
                    "required": false,
                    "defaultValue": 0,
                    "placeholder": "Ör: 5000",
                    "unit": "TL",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 2,
                    "options": [],
                    "visibleWhen": null
                },
                {
                    "id": "gasolineConsumptionL100Km",
                    "type": "number",
                    "label": "Benzin Tüketimi",
                    "required": true,
                    "defaultValue": null,
                    "placeholder": "Ör: 8",
                    "unit": "L/100 km",
                    "minimum": 1e-9,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 3,
                    "options": [],
                    "visibleWhen": null
                },
                {
                    "id": "lpgConsumptionL100Km",
                    "type": "number",
                    "label": "LPG Tüketimi",
                    "required": true,
                    "defaultValue": null,
                    "placeholder": "Ör: 9,6",
                    "unit": "L/100 km",
                    "minimum": 1e-9,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 3,
                    "options": [],
                    "visibleWhen": null
                },
                {
                    "id": "gasolinePricePerLitre",
                    "type": "number",
                    "label": "Benzin Litre Fiyatı",
                    "required": true,
                    "defaultValue": null,
                    "placeholder": "Ör: 50",
                    "unit": "TL/L",
                    "minimum": 1e-9,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 4,
                    "options": [],
                    "visibleWhen": null
                },
                {
                    "id": "lpgPricePerLitre",
                    "type": "number",
                    "label": "LPG Litre Fiyatı",
                    "required": true,
                    "defaultValue": null,
                    "placeholder": "Ör: 25",
                    "unit": "TL/L",
                    "minimum": 1e-9,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 4,
                    "options": [],
                    "visibleWhen": null
                },
                {
                    "id": "monthlyDistanceKm",
                    "type": "number",
                    "label": "Aylık Mesafe",
                    "required": true,
                    "defaultValue": null,
                    "placeholder": "Ör: 1500",
                    "unit": "km",
                    "minimum": 1e-9,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 2,
                    "options": [],
                    "visibleWhen": null
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "conversionCost",
                    "type": "money",
                    "label": "Dönüşüm Maliyeti",
                    "unit": "TL",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "extraOneOffCost",
                    "type": "money",
                    "label": "Ek Masraf",
                    "unit": "TL",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "gasolineConsumptionL100Km",
                    "type": "number",
                    "label": "Benzin Tüketimi",
                    "unit": "L/100 km",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "lpgConsumptionL100Km",
                    "type": "number",
                    "label": "LPG Tüketimi",
                    "unit": "L/100 km",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "gasolinePricePerLitre",
                    "type": "money",
                    "label": "Benzin Litre Fiyatı",
                    "unit": "TL/L",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "lpgPricePerLitre",
                    "type": "money",
                    "label": "LPG Litre Fiyatı",
                    "unit": "TL/L",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "monthlyDistanceKm",
                    "type": "number",
                    "label": "Aylık Mesafe",
                    "unit": "km",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "gasolineCostPerKm",
                    "type": "money",
                    "label": "Benzin Kilometre Maliyeti",
                    "unit": "TL/km",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "lpgCostPerKm",
                    "type": "money",
                    "label": "LPG Kilometre Maliyeti",
                    "unit": "TL/km",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "savingsPerKm",
                    "type": "money",
                    "label": "Kilometre Başına Tasarruf",
                    "unit": "TL/km",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "savingsPer100Km",
                    "type": "money",
                    "label": "100 km Tasarrufu",
                    "unit": "TL",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "investment",
                    "type": "money",
                    "label": "Toplam Yatırım",
                    "unit": "TL",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "monthlySavings",
                    "type": "money",
                    "label": "Aylık Tasarruf",
                    "unit": "TL",
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "annualSavings",
                    "type": "money",
                    "label": "Yıllık Tasarruf",
                    "unit": "TL",
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "paybackPossible",
                    "type": "boolean",
                    "label": "Amortisman Mümkün",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "paybackDistanceKm",
                    "type": "number",
                    "label": "Amortisman Mesafesi",
                    "unit": "km",
                    "nullable": true,
                    "prominent": true
                },
                {
                    "id": "paybackMonths",
                    "type": "number",
                    "label": "Amortisman Süresi",
                    "unit": "ay",
                    "nullable": true,
                    "prominent": true
                }
            ]
        },
        "content": {
            "title": "LPG Dönüşüm Amortisman Hesaplama",
            "shortTitle": "LPG Amortismanı",
            "summary": "Kullanıcının girdiği dönüşüm maliyeti, benzin ve LPG tüketimi, litre fiyatları ile aylık mesafeden tasarruf ve geri ödeme senaryosu hesaplar.",
            "method": "Benzin ve LPG için kilometre maliyeti ayrı ayrı bulunur. İki maliyet arasındaki olumlu fark kilometre başına tasarruftur. Dönüşüm ve tek seferlik ek masrafların toplamı bu tasarrufa bölünerek geri ödeme mesafesi; aylık mesafeyle de yaklaşık geri ödeme süresi hesaplanır. LPG kilometre maliyeti daha düşük değilse geri ödeme mümkün gösterilmez.",
            "limitations": [
                "Yalnız kullanıcı girdili aritmetik; dış tarife yok. Fiyat, tüketim, kilometre, dönüşüm ve ek masraf değerlerinin tamamı kullanıcı tarafından girilir.",
                "Sonuç teknik uyumluluk veya tasarruf garantisi değildir; kit uygunluğu, ruhsat işlemi, bakım, arıza, garanti, sigorta ve ikinci el değeri kendiliğinden hesaba katılmaz.",
                "Akaryakıt fiyatı, tüketim veya aylık mesafe değiştiğinde sonuç otomatik güncellenmez; güncel değerlerle yeniden hesaplanmalıdır."
            ],
            "faqs": [
                {
                    "question": "Geri ödeme süresi neden hesaplanmadı?",
                    "answer": "Girilen LPG kilometre maliyeti benzin kilometre maliyetinden düşük değilse pozitif tasarruf oluşmaz. Bu durumda araç geri ödeme mesafesi ve ay sayısı üretmez."
                },
                {
                    "question": "Akaryakıt fiyatları değişince sonuç otomatik güncellenir mi?",
                    "answer": "Hayır. Araç canlı fiyat kaynağına bağlanmaz; yeni benzin ve LPG litre fiyatlarını kendiniz girerek hesabı yenilemeniz gerekir."
                },
                {
                    "question": "Bakım ve ruhsat giderleri hesaba dahil midir?",
                    "answer": "Yalnız tek seferlik ek masraf alanına yazdığınız tutar yatırım toplamına eklenir. Sonradan doğabilecek bakım, arıza, ruhsat veya sigorta giderleri otomatik eklenmez."
                }
            ],
            "sourcePanel": {
                "title": "Kaynak ve veri kapsamı",
                "summary": "Dış tarife kaynağı yoktur; tüm fiyat, tüketim, mesafe ve masraf değerleri kullanıcı girdisidir. sourceIds boş kalır.",
                "sourceIds": [],
                "notes": [
                    "Yakıt fiyatı veya araç tüketimi uzaktan alınmaz.",
                    "Yöntem değişmedikçe yalnız yıllık işlev ve metin kontrolü gerekir."
                ]
            }
        },
        "config": {
            "kind": "lpg-payback-v1",
            "metadata": {
                "calculatorId": "lpg-payback",
                "version": "1.0.0",
                "effectiveFrom": "2026-08-20",
                "lastVerifiedAt": "2026-08-20",
                "sources": [],
                "notes": [
                    "Fiyat, tüketim, kilometre ve masraf değerlerinin tamamı kullanıcı girdisidir; piyasa fiyatı varsayılmaz.",
                    "Sonuç teknik uygunluk veya tasarruf garantisi değildir."
                ]
            },
            "maxAbsoluteInput": 1000000000
        },
        "testVectors": {
            "kind": "everyday-health-oracle-v1",
            "cases": [
                {
                    "caseId": "positive-payback",
                    "scenario": "positive",
                    "input": {
                        "conversionCost": 25000,
                        "extraOneOffCost": 5000,
                        "gasolineConsumptionL100Km": 8,
                        "lpgConsumptionL100Km": 9.6,
                        "gasolinePricePerLitre": 50,
                        "lpgPricePerLitre": 25,
                        "monthlyDistanceKm": 1500
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "conversionCost": 25000,
                            "extraOneOffCost": 5000,
                            "gasolineConsumptionL100Km": 8,
                            "lpgConsumptionL100Km": 9.6,
                            "gasolinePricePerLitre": 50,
                            "lpgPricePerLitre": 25,
                            "monthlyDistanceKm": 1500,
                            "gasolineCostPerKm": 4,
                            "lpgCostPerKm": 2.4,
                            "savingsPerKm": 1.6,
                            "savingsPer100Km": 160,
                            "investment": 30000,
                            "monthlySavings": 2400,
                            "annualSavings": 28800,
                            "paybackPossible": true,
                            "paybackDistanceKm": 18750,
                            "paybackMonths": 12.5
                        }
                    },
                    "tolerance": 1e-9
                },
                {
                    "caseId": "negative-saving-no-payback",
                    "scenario": "no-payback",
                    "input": {
                        "conversionCost": 20000,
                        "gasolineConsumptionL100Km": 7,
                        "lpgConsumptionL100Km": 10,
                        "gasolinePricePerLitre": 40,
                        "lpgPricePerLitre": 35,
                        "monthlyDistanceKm": 1000
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "conversionCost": 20000,
                            "gasolineConsumptionL100Km": 7,
                            "lpgConsumptionL100Km": 10,
                            "gasolinePricePerLitre": 40,
                            "lpgPricePerLitre": 35,
                            "monthlyDistanceKm": 1000,
                            "extraOneOffCost": 0,
                            "gasolineCostPerKm": 2.8,
                            "lpgCostPerKm": 3.5,
                            "savingsPerKm": -0.7000000000000002,
                            "savingsPer100Km": -70.00000000000001,
                            "investment": 20000,
                            "monthlySavings": -700.0000000000002,
                            "annualSavings": -8400.000000000004,
                            "paybackPossible": false,
                            "paybackDistanceKm": null,
                            "paybackMonths": null
                        }
                    },
                    "tolerance": 1e-9
                },
                {
                    "caseId": "zero-saving-threshold",
                    "scenario": "threshold",
                    "input": {
                        "conversionCost": 15000,
                        "extraOneOffCost": 0,
                        "gasolineConsumptionL100Km": 10,
                        "lpgConsumptionL100Km": 20,
                        "gasolinePricePerLitre": 40,
                        "lpgPricePerLitre": 20,
                        "monthlyDistanceKm": 1000
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "conversionCost": 15000,
                            "extraOneOffCost": 0,
                            "gasolineConsumptionL100Km": 10,
                            "lpgConsumptionL100Km": 20,
                            "gasolinePricePerLitre": 40,
                            "lpgPricePerLitre": 20,
                            "monthlyDistanceKm": 1000,
                            "gasolineCostPerKm": 4,
                            "lpgCostPerKm": 4,
                            "savingsPerKm": 0,
                            "savingsPer100Km": 0,
                            "investment": 15000,
                            "monthlySavings": 0,
                            "annualSavings": 0,
                            "paybackPossible": false,
                            "paybackDistanceKm": null,
                            "paybackMonths": null
                        }
                    },
                    "tolerance": 1e-9
                },
                {
                    "caseId": "zero-conversion-cost",
                    "scenario": "adversarial",
                    "input": {
                        "conversionCost": 0,
                        "gasolineConsumptionL100Km": 8,
                        "lpgConsumptionL100Km": 9.6,
                        "gasolinePricePerLitre": 50,
                        "lpgPricePerLitre": 25,
                        "monthlyDistanceKm": 1500
                    },
                    "expected": {
                        "ok": false,
                        "field": "conversionCost",
                        "errorCode": "OUT_OF_RANGE"
                    },
                    "tolerance": 0
                },
                {
                    "caseId": "negative-extra-cost",
                    "scenario": "adversarial",
                    "input": {
                        "conversionCost": 25000,
                        "extraOneOffCost": -1,
                        "gasolineConsumptionL100Km": 8,
                        "lpgConsumptionL100Km": 9.6,
                        "gasolinePricePerLitre": 50,
                        "lpgPricePerLitre": 25,
                        "monthlyDistanceKm": 1500
                    },
                    "expected": {
                        "ok": false,
                        "field": "extraOneOffCost",
                        "errorCode": "OUT_OF_RANGE"
                    },
                    "tolerance": 0
                },
                {
                    "caseId": "sub-stability-consumption",
                    "scenario": "adversarial",
                    "input": {
                        "conversionCost": 25000,
                        "gasolineConsumptionL100Km": 1e-12,
                        "lpgConsumptionL100Km": 9.6,
                        "gasolinePricePerLitre": 50,
                        "lpgPricePerLitre": 25,
                        "monthlyDistanceKm": 1500
                    },
                    "expected": {
                        "ok": false,
                        "field": "gasolineConsumptionL100Km",
                        "errorCode": "OUT_OF_RANGE"
                    },
                    "tolerance": 0
                },
                {
                    "caseId": "numeric-string-distance",
                    "scenario": "adversarial",
                    "input": {
                        "conversionCost": 25000,
                        "gasolineConsumptionL100Km": 8,
                        "lpgConsumptionL100Km": 9.6,
                        "gasolinePricePerLitre": 50,
                        "lpgPricePerLitre": 25,
                        "monthlyDistanceKm": "1500"
                    },
                    "expected": {
                        "ok": false,
                        "field": "monthlyDistanceKm",
                        "errorCode": "NOT_FINITE"
                    },
                    "tolerance": 0
                },
                {
                    "caseId": "missing-input-object",
                    "scenario": "adversarial",
                    "input": null,
                    "expected": {
                        "ok": false,
                        "field": "input",
                        "errorCode": "REQUIRED"
                    },
                    "tolerance": 0
                },
                {
                    "caseId": "invalid-config-limit",
                    "scenario": "adversarial",
                    "input": {
                        "conversionCost": 25000,
                        "gasolineConsumptionL100Km": 8,
                        "lpgConsumptionL100Km": 9.6,
                        "gasolinePricePerLitre": 50,
                        "lpgPricePerLitre": 25,
                        "monthlyDistanceKm": 1500
                    },
                    "configMutation": {
                        "path": [
                            "maxAbsoluteInput"
                        ],
                        "replacement": 0
                    },
                    "expected": {
                        "ok": false,
                        "field": "config.maxAbsoluteInput",
                        "errorCode": "INVALID_CONFIG"
                    },
                    "tolerance": 0
                }
            ]
        },
        "lastReviewed": "2026-08-20"
    },
    {
        "toolId": "fuel-consumption",
        "slug": "yakit-tuketimi-hesaplama",
        "path": "/arac-emlak-yasam/yakit-tuketimi-hesaplama",
        "category": {
            "id": "arac-emlak-yasam",
            "label": "Araç, Emlak ve Yaşam"
        },
        "classification": "timeless",
        "engineId": "fuel-consumption-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "2026.8.20",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-08-20",
        "effectiveTo": null,
        "minAppVersion": "0.1.0",
        "updateCadence": {
            "mode": "method-change-only",
            "days": null,
            "reviewAfter": null,
            "specialMonths": [],
            "note": "Hesap yöntemi değişmedikçe kural güncellemesi gerekmez; yıllık işlev ve metin QA'sı yapılabilir."
        },
        "sources": [],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "fuelType",
                    "type": "select",
                    "label": "Yakıt Türü",
                    "required": true,
                    "defaultValue": "benzin",
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "benzin",
                            "label": "Benzin"
                        },
                        {
                            "value": "motorin",
                            "label": "Motorin"
                        },
                        {
                            "value": "lpg",
                            "label": "LPG"
                        }
                    ],
                    "visibleWhen": null
                },
                {
                    "id": "distanceKm",
                    "type": "number",
                    "label": "Mesafe",
                    "required": true,
                    "defaultValue": null,
                    "placeholder": "Ör: 400",
                    "unit": "km",
                    "minimum": 1,
                    "maximum": 20000,
                    "maxDecimalPlaces": 2,
                    "options": [],
                    "visibleWhen": null
                },
                {
                    "id": "consumptionL100Km",
                    "type": "number",
                    "label": "Ortalama Tüketim",
                    "required": true,
                    "defaultValue": null,
                    "placeholder": "Ör: 7",
                    "unit": "L/100 km",
                    "minimum": 1,
                    "maximum": 50,
                    "maxDecimalPlaces": 3,
                    "options": [],
                    "visibleWhen": null
                },
                {
                    "id": "pricePerLitre",
                    "type": "number",
                    "label": "Litre Fiyatı",
                    "required": true,
                    "defaultValue": null,
                    "placeholder": "Ör: 45",
                    "unit": "TL/L",
                    "minimum": 1,
                    "maximum": 500,
                    "maxDecimalPlaces": 4,
                    "options": [],
                    "visibleWhen": null
                },
                {
                    "id": "direction",
                    "type": "radio",
                    "label": "Yol Yönü",
                    "required": true,
                    "defaultValue": "tekYon",
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "tekYon",
                            "label": "Tek Yön"
                        },
                        {
                            "value": "gidisDonus",
                            "label": "Gidiş Dönüş"
                        }
                    ],
                    "visibleWhen": null
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "fuelType",
                    "type": "text",
                    "label": "Yakıt Türü",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "distanceKm",
                    "type": "number",
                    "label": "Girilen Mesafe",
                    "unit": "km",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "consumptionL100Km",
                    "type": "number",
                    "label": "Ortalama Tüketim",
                    "unit": "L/100 km",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "pricePerLitre",
                    "type": "money",
                    "label": "Litre Fiyatı",
                    "unit": "TL/L",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "direction",
                    "type": "text",
                    "label": "Yol Yönü",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "effectiveDistanceKm",
                    "type": "number",
                    "label": "Toplam Mesafe",
                    "unit": "km",
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "consumedLitres",
                    "type": "number",
                    "label": "Tüketilen Yakıt",
                    "unit": "L",
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "costPerKm",
                    "type": "money",
                    "label": "Kilometre Maliyeti",
                    "unit": "TL/km",
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "costPer100Km",
                    "type": "money",
                    "label": "100 km Maliyeti",
                    "unit": "TL",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "totalCost",
                    "type": "money",
                    "label": "Toplam Yakıt Maliyeti",
                    "unit": "TL",
                    "nullable": false,
                    "prominent": true
                }
            ]
        },
        "content": {
            "title": "Yakıt Tüketim Maliyeti Hesaplama: Arabanız Ne Yakar?",
            "shortTitle": "Yakıt Tüketimi",
            "summary": "Mesafe, ortalama tüketim ve litre fiyatından yolculukta harcanacak yakıtı, kilometre maliyetini ve toplam yakıt giderini hesaplar.",
            "method": "Gidiş-dönüş seçilirse girilen tek yön mesafesi ikiyle çarpılır. Toplam mesafe ile L/100 km tüketim değeri kullanılarak tüketilecek litre bulunur; litre fiyatı bu miktarla çarpılarak toplam maliyet hesaplanır. Kilometre ve 100 kilometre başına maliyet aynı kullanıcı girdilerinden türetilir.",
            "limitations": [
                "Yalnız kullanıcı girdili aritmetik; dış tarife yok. Yakıt türü yalnız etikettir; fiyat ve tüketim otomatik atanmaz.",
                "Trafik, sürüş tarzı, yük, lastik, yol eğimi, hava koşulu ve aracın mekanik durumu gerçek tüketimi değiştirebilir; araç bu değişkenleri modellemez.",
                "Sonuç bütçe tahminidir; fiilî pompa veya araç bilgisayarı ölçümü değildir."
            ],
            "faqs": [
                {
                    "question": "Yakıt türünü seçmek litre fiyatını değiştirir mi?",
                    "answer": "Hayır. Benzin, motorin veya LPG seçimi yalnız sonuç etiketidir. Güncel litre fiyatını ve kendi aracınızın ortalama tüketimini siz girmelisiniz."
                },
                {
                    "question": "Gidiş-dönüş seçimi nasıl hesaplanır?",
                    "answer": "Girilen mesafe tek yön kabul edilir ve ikiyle çarpılır. Tüketilen litre ile toplam maliyet bu iki kat mesafe üzerinden hesaplanır."
                },
                {
                    "question": "Araç bilgisayarındaki değerle sonuç neden farklı olabilir?",
                    "answer": "Bu araç girdiğiniz sabit L/100 km değerini kullanır. Gerçek tüketim trafik, hız, yük, hava ve ölçüm yöntemine göre değişebileceği için pompa veya araç bilgisayarı sonucu farklı olabilir."
                }
            ],
            "sourcePanel": {
                "title": "Kaynak ve veri kapsamı",
                "summary": "Canlı akaryakıt fiyatı veya marka-model tüketim verisi kullanılmaz. Mesafe, L/100 km ve TL/L değerleri kullanıcı girdisidir; sourceIds boş kalır.",
                "sourceIds": [],
                "notes": [
                    "Yakıt türü seçimi dış veri çağırmaz.",
                    "Yöntem dönemsel oran veya mevzuat içermez."
                ]
            }
        },
        "config": {
            "kind": "fuel-consumption-v1",
            "metadata": {
                "calculatorId": "fuel-consumption",
                "version": "1.0.0",
                "effectiveFrom": "2026-08-20",
                "lastVerifiedAt": "2026-08-20",
                "sources": [],
                "notes": [
                    "Tüketim ve litre fiyatı kullanıcı girdisidir; yakıt türüne göre otomatik fiyat veya tüketim atanmaz."
                ]
            },
            "distanceKm": [
                1,
                20000
            ],
            "consumptionL100Km": [
                1,
                50
            ],
            "pricePerLitre": [
                1,
                500
            ]
        },
        "testVectors": {
            "kind": "everyday-health-oracle-v1",
            "cases": [
                {
                    "caseId": "round-trip-gasoline",
                    "scenario": "positive",
                    "input": {
                        "fuelType": "benzin",
                        "distanceKm": 400,
                        "consumptionL100Km": 7,
                        "pricePerLitre": 45,
                        "direction": "gidisDonus"
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "fuelType": "benzin",
                            "distanceKm": 400,
                            "consumptionL100Km": 7,
                            "pricePerLitre": 45,
                            "direction": "gidisDonus",
                            "effectiveDistanceKm": 800,
                            "consumedLitres": 56,
                            "costPerKm": 3.15,
                            "costPer100Km": 315,
                            "totalCost": 2520
                        }
                    },
                    "tolerance": 1e-9
                },
                {
                    "caseId": "minimum-bounds",
                    "scenario": "boundary-lower",
                    "input": {
                        "fuelType": "lpg",
                        "distanceKm": 1,
                        "consumptionL100Km": 1,
                        "pricePerLitre": 1,
                        "direction": "tekYon"
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "fuelType": "lpg",
                            "distanceKm": 1,
                            "consumptionL100Km": 1,
                            "pricePerLitre": 1,
                            "direction": "tekYon",
                            "effectiveDistanceKm": 1,
                            "consumedLitres": 0.01,
                            "costPerKm": 0.01,
                            "costPer100Km": 1,
                            "totalCost": 0.01
                        }
                    },
                    "tolerance": 1e-9
                },
                {
                    "caseId": "maximum-bounds-round-trip",
                    "scenario": "boundary-upper",
                    "input": {
                        "fuelType": "motorin",
                        "distanceKm": 20000,
                        "consumptionL100Km": 50,
                        "pricePerLitre": 500,
                        "direction": "gidisDonus"
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "fuelType": "motorin",
                            "distanceKm": 20000,
                            "consumptionL100Km": 50,
                            "pricePerLitre": 500,
                            "direction": "gidisDonus",
                            "effectiveDistanceKm": 40000,
                            "consumedLitres": 20000,
                            "costPerKm": 250,
                            "costPer100Km": 25000,
                            "totalCost": 10000000
                        }
                    },
                    "tolerance": 1e-9
                },
                {
                    "caseId": "zero-distance",
                    "scenario": "adversarial",
                    "input": {
                        "fuelType": "benzin",
                        "distanceKm": 0,
                        "consumptionL100Km": 7,
                        "pricePerLitre": 45,
                        "direction": "tekYon"
                    },
                    "expected": {
                        "ok": false,
                        "field": "distanceKm",
                        "errorCode": "OUT_OF_RANGE"
                    },
                    "tolerance": 0
                },
                {
                    "caseId": "distance-above-maximum",
                    "scenario": "adversarial",
                    "input": {
                        "fuelType": "benzin",
                        "distanceKm": 20001,
                        "consumptionL100Km": 7,
                        "pricePerLitre": 45,
                        "direction": "tekYon"
                    },
                    "expected": {
                        "ok": false,
                        "field": "distanceKm",
                        "errorCode": "OUT_OF_RANGE"
                    },
                    "tolerance": 0
                },
                {
                    "caseId": "consumption-above-maximum",
                    "scenario": "adversarial",
                    "input": {
                        "fuelType": "benzin",
                        "distanceKm": 100,
                        "consumptionL100Km": 51,
                        "pricePerLitre": 45,
                        "direction": "tekYon"
                    },
                    "expected": {
                        "ok": false,
                        "field": "consumptionL100Km",
                        "errorCode": "OUT_OF_RANGE"
                    },
                    "tolerance": 0
                },
                {
                    "caseId": "unsupported-fuel",
                    "scenario": "adversarial",
                    "input": {
                        "fuelType": "elektrik",
                        "distanceKm": 100,
                        "consumptionL100Km": 7,
                        "pricePerLitre": 45,
                        "direction": "tekYon"
                    },
                    "expected": {
                        "ok": false,
                        "field": "fuelType",
                        "errorCode": "UNSUPPORTED_VALUE"
                    },
                    "tolerance": 0
                },
                {
                    "caseId": "unsupported-direction",
                    "scenario": "adversarial",
                    "input": {
                        "fuelType": "benzin",
                        "distanceKm": 100,
                        "consumptionL100Km": 7,
                        "pricePerLitre": 45,
                        "direction": "cift"
                    },
                    "expected": {
                        "ok": false,
                        "field": "direction",
                        "errorCode": "UNSUPPORTED_VALUE"
                    },
                    "tolerance": 0
                },
                {
                    "caseId": "numeric-string-price",
                    "scenario": "adversarial",
                    "input": {
                        "fuelType": "benzin",
                        "distanceKm": 100,
                        "consumptionL100Km": 7,
                        "pricePerLitre": "45",
                        "direction": "tekYon"
                    },
                    "expected": {
                        "ok": false,
                        "field": "pricePerLitre",
                        "errorCode": "NOT_FINITE"
                    },
                    "tolerance": 0
                },
                {
                    "caseId": "invalid-config-distance-range",
                    "scenario": "adversarial",
                    "input": {
                        "fuelType": "benzin",
                        "distanceKm": 100,
                        "consumptionL100Km": 7,
                        "pricePerLitre": 45,
                        "direction": "tekYon"
                    },
                    "configMutation": {
                        "path": [
                            "distanceKm"
                        ],
                        "replacement": [
                            0,
                            20000
                        ]
                    },
                    "expected": {
                        "ok": false,
                        "field": "config.distanceKm",
                        "errorCode": "INVALID_CONFIG"
                    },
                    "tolerance": 0
                }
            ]
        },
        "lastReviewed": "2026-08-20"
    },
    {
        "toolId": "profit-margin",
        "slug": "kar-marji-ve-satis-fiyati-hesaplama",
        "path": "/ticari-muhasebe/kar-marji-ve-satis-fiyati-hesaplama",
        "category": {
            "id": "ticari-muhasebe",
            "label": "Ticari ve Muhasebe"
        },
        "classification": "timeless",
        "engineId": "profit-margin-v2",
        "schemaVersion": "2.0.0",
        "contentVersion": "2026.8.24",
        "configVersion": "2.0.0",
        "effectiveFrom": "2026-08-24",
        "effectiveTo": null,
        "minAppVersion": "0.2.0",
        "updateCadence": {
            "mode": "method-change-only",
            "days": null,
            "reviewAfter": null,
            "specialMonths": [],
            "note": "Aritmetik yöntem değişmedikçe kural güncellemesi gerekmez; yıllık işlev ve metin QA'sı yapılabilir."
        },
        "sources": [],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "mode",
                    "type": "radio",
                    "label": "Ne yapmak istiyorsunuz?",
                    "required": true,
                    "defaultValue": "targetSaleMargin",
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "targetSaleMargin",
                            "label": "Satış fiyatının belirli yüzdesi kâr olsun"
                        },
                        {
                            "value": "costMarkup",
                            "label": "Maliyetin üzerine belirli yüzde ekle"
                        },
                        {
                            "value": "analyzeSale",
                            "label": "Mevcut satışın kârlılığını gör"
                        }
                    ],
                    "visibleWhen": null
                },
                {
                    "id": "cost",
                    "type": "number",
                    "label": "Maliyet",
                    "required": true,
                    "defaultValue": null,
                    "placeholder": "Ör: 100",
                    "unit": "TL",
                    "minimum": 0.01,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 2,
                    "options": [],
                    "visibleWhen": null
                },
                {
                    "id": "targetMarginPercent",
                    "type": "number",
                    "label": "Hedef Satış Marjı",
                    "required": true,
                    "defaultValue": null,
                    "placeholder": "Ör: 30",
                    "unit": "%",
                    "minimum": 0,
                    "maximum": 99.99,
                    "maxDecimalPlaces": 2,
                    "options": [],
                    "visibleWhen": {
                        "fieldId": "mode",
                        "equals": "targetSaleMargin"
                    }
                },
                {
                    "id": "markupPercent",
                    "type": "number",
                    "label": "Maliyete Kâr Ekleme Oranı",
                    "required": true,
                    "defaultValue": null,
                    "placeholder": "Ör: 30",
                    "unit": "%",
                    "minimum": 0,
                    "maximum": 1000000,
                    "maxDecimalPlaces": 2,
                    "options": [],
                    "visibleWhen": {
                        "fieldId": "mode",
                        "equals": "costMarkup"
                    }
                },
                {
                    "id": "salePrice",
                    "type": "number",
                    "label": "Mevcut Satış Fiyatı",
                    "required": true,
                    "defaultValue": null,
                    "placeholder": "Ör: 130",
                    "unit": "TL",
                    "minimum": 0.01,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 2,
                    "options": [],
                    "visibleWhen": {
                        "fieldId": "mode",
                        "equals": "analyzeSale"
                    }
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "mode",
                    "type": "text",
                    "label": "Hesap Türü",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "cost",
                    "type": "money",
                    "label": "Maliyet",
                    "unit": "TL",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "salePrice",
                    "type": "money",
                    "label": "Satış Fiyatı",
                    "unit": "TL",
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "profit",
                    "type": "money",
                    "label": "Kâr veya Zarar",
                    "unit": "TL",
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "markupPercent",
                    "type": "percentage",
                    "label": "Maliyete Göre Kâr Ekleme Oranı",
                    "unit": "%",
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "marginPercent",
                    "type": "percentage",
                    "label": "Satış Fiyatındaki Kâr Marjı",
                    "unit": "%",
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "isLoss",
                    "type": "boolean",
                    "label": "Zarar Durumu",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                }
            ]
        },
        "content": {
            "title": "Kâr Marjı, Kâr Ekleme Oranı ve Satış Fiyatı Hesaplama",
            "shortTitle": "Kâr Marjı",
            "summary": "Hedef satış marjından veya maliyete eklenecek orandan satış fiyatını bulur; mevcut satış fiyatının kârını, zararını ve iki yüzdeyi de ayrı gösterir.",
            "method": "Hedef satış marjı modunda maliyet, bir eksi marj oranına bölünür; böylece kâr satış fiyatının seçilen yüzdesi olur. Kâr ekleme modunda oran doğrudan maliyete uygulanır. Mevcut satış analizi modunda satıştan maliyet çıkarılır. Kâr ekleme oranı kârın maliyete, satış marjı ise kârın satış fiyatına oranıdır ve aynı yüzde değildir.",
            "limitations": [
                "Yalnız kullanıcı girdili aritmetik; dış tarife yok. Vergi, KDV, komisyon, kargo, işçilik ve diğer giderler otomatik eklenmez.",
                "Gerçek kârlılık için bütün giderler maliyet alanına kullanıcı tarafından dahil edilmelidir.",
                "Araç muhasebe kaydı, vergi beyanı, fiyatlandırma tavsiyesi veya mevzuat yorumu üretmez."
            ],
            "faqs": [
                {
                    "question": "Kâr marjı ile kâr ekleme oranı aynı mıdır?",
                    "answer": "Hayır. Kâr ekleme oranı kârın maliyete, satış marjı ise kârın satış fiyatına oranıdır. 100 TL maliyetin üzerine %30 eklendiğinde satış 130 TL, satış marjı ise yaklaşık %23,08 olur."
                },
                {
                    "question": "100 TL maliyette %30 satış marjı için fiyat neden 130 TL değildir?",
                    "answer": "Çünkü 130 TL, maliyete %30 eklenmiş fiyattır. Kârın satışın %30'u olması için 100 TL maliyet 1 - 0,30 değerine bölünür ve satış fiyatı yaklaşık 142,86 TL olur."
                },
                {
                    "question": "Hedef satış marjına göre fiyat nasıl bulunur?",
                    "answer": "Maliyet, 1 eksi hedef marj oranına bölünür. Hedef marj yüzde 100 olamaz; pozitif maliyetle sonlu bir satış fiyatı üretmez."
                },
                {
                    "question": "Maliyetin üzerine oran ekleyerek fiyat nasıl bulunur?",
                    "answer": "Maliyet, 1 artı kâr ekleme oranıyla çarpılır. Yüzde sıfır seçilirse satış maliyete eşit olur ve başabaş sonucu çıkar."
                },
                {
                    "question": "Satış fiyatı maliyetin altındaysa ne olur?",
                    "answer": "Araç farkı zarar olarak gösterir. Kâr veya zarar tutarıyla birlikte maliyete göre oran ve satış marjı negatif çıkar; satış fiyatını değiştirmez veya gizlemez."
                },
                {
                    "question": "KDV ve komisyon hesaba dahil midir?",
                    "answer": "Hayır. KDV, pazar yeri komisyonu, kargo, işçilik ve benzeri giderler ayrıca modellenmez. Karşılaştırmak istediğiniz gerçek toplam maliyete bu kalemleri kendiniz eklemelisiniz."
                }
            ],
            "sourcePanel": {
                "title": "Yöntem ve veri kapsamı",
                "summary": "Dış oran veya canlı tarife kullanılmaz. Araç yalnız girilen maliyet, hedef satış marjı, kâr ekleme oranı veya satış fiyatına kapalı aritmetik uygular; sourceIds boş kalır.",
                "sourceIds": [],
                "notes": [
                    "Vergi ve gider oranları çekirdeğe gömülü değildir.",
                    "Aritmetik yöntem değişmedikçe yalnız yıllık işlev ve metin kontrolü gerekir."
                ]
            }
        },
        "config": {
            "kind": "profit-margin-v2",
            "metadata": {
                "calculatorId": "profit-margin",
                "version": "2.0.0",
                "effectiveFrom": "2026-08-24",
                "lastVerifiedAt": "2026-08-24",
                "sources": [],
                "notes": [
                    "Satış marjı kârın satış fiyatına, kâr ekleme oranı ise kârın maliyete oranıdır; bu iki yüzde aynı değildir.",
                    "Vergi, komisyon ve diğer giderler kullanıcı tarafından maliyete dahil edilmelidir."
                ]
            },
            "monetaryInput": [
                0.01,
                1000000000
            ],
            "targetMarginPercent": [
                0,
                99.99
            ],
            "markupPercent": [
                0,
                1000000
            ]
        },
        "testVectors": {
            "kind": "everyday-health-oracle-v1",
            "cases": [
                {
                    "caseId": "target-thirty-percent-sale-margin",
                    "scenario": "positive",
                    "input": {
                        "mode": "targetSaleMargin",
                        "cost": 100,
                        "targetMarginPercent": 30
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "mode": "targetSaleMargin",
                            "cost": 100,
                            "salePrice": 142.85714285714286,
                            "profit": 42.85714285714286,
                            "markupPercent": 42.85714285714286,
                            "marginPercent": 30.000000000000004,
                            "isLoss": false
                        }
                    },
                    "tolerance": 1e-9
                },
                {
                    "caseId": "thirty-percent-cost-markup",
                    "scenario": "positive",
                    "input": {
                        "mode": "costMarkup",
                        "cost": 100,
                        "markupPercent": 30
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "mode": "costMarkup",
                            "cost": 100,
                            "salePrice": 130,
                            "profit": 30,
                            "markupPercent": 30,
                            "marginPercent": 23.076923076923077,
                            "isLoss": false
                        }
                    },
                    "tolerance": 1e-9
                },
                {
                    "caseId": "analyze-profitable-sale",
                    "scenario": "positive",
                    "input": {
                        "mode": "analyzeSale",
                        "cost": 100,
                        "salePrice": 130
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "mode": "analyzeSale",
                            "cost": 100,
                            "salePrice": 130,
                            "profit": 30,
                            "markupPercent": 30,
                            "marginPercent": 23.076923076923077,
                            "isLoss": false
                        }
                    },
                    "tolerance": 1e-9
                },
                {
                    "caseId": "analyze-sale-below-cost",
                    "scenario": "loss",
                    "input": {
                        "mode": "analyzeSale",
                        "cost": 100,
                        "salePrice": 80
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "mode": "analyzeSale",
                            "cost": 100,
                            "salePrice": 80,
                            "profit": -20,
                            "markupPercent": -20,
                            "marginPercent": -25,
                            "isLoss": true
                        }
                    },
                    "tolerance": 1e-9
                },
                {
                    "caseId": "zero-target-margin-break-even",
                    "scenario": "threshold",
                    "input": {
                        "mode": "targetSaleMargin",
                        "cost": 100,
                        "targetMarginPercent": 0
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "mode": "targetSaleMargin",
                            "cost": 100,
                            "salePrice": 100,
                            "profit": 0,
                            "markupPercent": 0,
                            "marginPercent": 0,
                            "isLoss": false
                        }
                    },
                    "tolerance": 1e-9
                },
                {
                    "caseId": "zero-cost-markup-break-even",
                    "scenario": "threshold",
                    "input": {
                        "mode": "costMarkup",
                        "cost": 100,
                        "markupPercent": 0
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "mode": "costMarkup",
                            "cost": 100,
                            "salePrice": 100,
                            "profit": 0,
                            "markupPercent": 0,
                            "marginPercent": 0,
                            "isLoss": false
                        }
                    },
                    "tolerance": 1e-9
                },
                {
                    "caseId": "target-margin-upper-bound",
                    "scenario": "boundary-upper",
                    "input": {
                        "mode": "targetSaleMargin",
                        "cost": 100,
                        "targetMarginPercent": 99.99
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "mode": "targetSaleMargin",
                            "cost": 100,
                            "salePrice": 999999.9999989999,
                            "profit": 999899.9999989999,
                            "markupPercent": 999899.9999989999,
                            "marginPercent": 99.99,
                            "isLoss": false
                        }
                    },
                    "tolerance": 1e-7
                },
                {
                    "caseId": "target-margin-one-hundred",
                    "scenario": "adversarial",
                    "input": {
                        "mode": "targetSaleMargin",
                        "cost": 100,
                        "targetMarginPercent": 100
                    },
                    "expected": {
                        "ok": false,
                        "field": "targetMarginPercent",
                        "errorCode": "OUT_OF_RANGE"
                    },
                    "tolerance": 0
                },
                {
                    "caseId": "zero-cost",
                    "scenario": "adversarial",
                    "input": {
                        "mode": "costMarkup",
                        "cost": 0,
                        "markupPercent": 30
                    },
                    "expected": {
                        "ok": false,
                        "field": "cost",
                        "errorCode": "OUT_OF_RANGE"
                    },
                    "tolerance": 0
                },
                {
                    "caseId": "zero-sale-price",
                    "scenario": "adversarial",
                    "input": {
                        "mode": "analyzeSale",
                        "cost": 100,
                        "salePrice": 0
                    },
                    "expected": {
                        "ok": false,
                        "field": "salePrice",
                        "errorCode": "OUT_OF_RANGE"
                    },
                    "tolerance": 0
                },
                {
                    "caseId": "unsupported-mode",
                    "scenario": "adversarial",
                    "input": {
                        "mode": "margin",
                        "cost": 100,
                        "targetMarginPercent": 30
                    },
                    "expected": {
                        "ok": false,
                        "field": "mode",
                        "errorCode": "UNSUPPORTED_VALUE"
                    },
                    "tolerance": 0
                },
                {
                    "caseId": "numeric-string-cost",
                    "scenario": "adversarial",
                    "input": {
                        "mode": "costMarkup",
                        "cost": "100",
                        "markupPercent": 30
                    },
                    "expected": {
                        "ok": false,
                        "field": "cost",
                        "errorCode": "NOT_FINITE"
                    },
                    "tolerance": 0
                },
                {
                    "caseId": "missing-input-object",
                    "scenario": "adversarial",
                    "input": null,
                    "expected": {
                        "ok": false,
                        "field": "input",
                        "errorCode": "REQUIRED"
                    },
                    "tolerance": 0
                },
                {
                    "caseId": "invalid-target-margin-config",
                    "scenario": "adversarial",
                    "input": {
                        "mode": "targetSaleMargin",
                        "cost": 100,
                        "targetMarginPercent": 30
                    },
                    "configMutation": {
                        "path": [
                            "targetMarginPercent"
                        ],
                        "replacement": [
                            0,
                            100
                        ]
                    },
                    "expected": {
                        "ok": false,
                        "field": "config.targetMarginPercent",
                        "errorCode": "INVALID_CONFIG"
                    },
                    "tolerance": 0
                }
            ]
        },
        "lastReviewed": "2026-08-24"
    },
    {
        "toolId": "basal-metabolic-rate",
        "slug": "bazal-metabolizma-hesaplama",
        "path": "/saglik-yasam/bazal-metabolizma-hesaplama",
        "category": {
            "id": "saglik-yasam",
            "label": "Sağlık ve Yaşam"
        },
        "classification": "timeless",
        "engineId": "basal-metabolic-rate-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "2026.8.20",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-08-20",
        "effectiveTo": null,
        "minAppVersion": "0.1.0",
        "updateCadence": {
            "mode": "method-change-only",
            "days": null,
            "reviewAfter": null,
            "specialMonths": [],
            "note": "Denklem kodu yalnız yöntem değişiminde güncellenir; ayrı bakım sınıfı kaynak metodolojisini yılda bir insan incelemesine bağlar."
        },
        "sources": [
            {
                "sourceId": "mifflin-1990-pmid-2305711",
                "sourceGroupId": null,
                "title": "A new predictive equation for resting energy expenditure in healthy individuals",
                "publisher": "The American Journal of Clinical Nutrition / PubMed",
                "url": "https://pubmed.ncbi.nlm.nih.gov/2305711/",
                "kind": "peer-reviewed-medical-research",
                "reviewedOn": "2026-08-20",
                "watchFields": [
                    "denklem kapsamı",
                    "19–78 yaş örneklem kapsamı",
                    "tahmin niteliği"
                ]
            },
            {
                "sourceId": "roza-shizgal-1984-pmid-6741850",
                "sourceGroupId": null,
                "title": "The Harris Benedict equation reevaluated",
                "publisher": "The American Journal of Clinical Nutrition / PubMed",
                "url": "https://pubmed.ncbi.nlm.nih.gov/6741850/",
                "kind": "peer-reviewed-medical-research",
                "reviewedOn": "2026-08-20",
                "watchFields": [
                    "denklem kapsamı",
                    "yaş sınırı belirsizliği",
                    "tahmin niteliği"
                ]
            },
            {
                "sourceId": "muller-2001-pmid-11817239",
                "sourceGroupId": null,
                "title": "Calculating the basal metabolic rate and severe and morbid obesity",
                "publisher": "Praxis / PubMed",
                "url": "https://pubmed.ncbi.nlm.nih.gov/11817239/",
                "kind": "peer-reviewed-medical-research",
                "reviewedOn": "2026-08-20",
                "watchFields": [
                    "revize Harris–Benedict katsayılarının görünür doğrulaması",
                    "köken yayını olmadığı kanıt sınırı"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "sex",
                    "type": "radio",
                    "label": "Biyolojik Cinsiyet",
                    "required": true,
                    "defaultValue": "erkek",
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "erkek",
                            "label": "Erkek"
                        },
                        {
                            "value": "kadin",
                            "label": "Kadın"
                        }
                    ],
                    "visibleWhen": null
                },
                {
                    "id": "formula",
                    "type": "select",
                    "label": "Denklem",
                    "required": true,
                    "defaultValue": "mifflin",
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "mifflin",
                            "label": "Mifflin–St Jeor"
                        },
                        {
                            "value": "harris",
                            "label": "Revize Harris–Benedict"
                        }
                    ],
                    "visibleWhen": null
                },
                {
                    "id": "ageYears",
                    "type": "number",
                    "label": "Yaş",
                    "required": true,
                    "defaultValue": null,
                    "placeholder": "Ör: 30",
                    "unit": "yıl",
                    "minimum": 18,
                    "maximum": 100,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null
                },
                {
                    "id": "heightCm",
                    "type": "number",
                    "label": "Boy",
                    "required": true,
                    "defaultValue": null,
                    "placeholder": "Ör: 180",
                    "unit": "cm",
                    "minimum": 100,
                    "maximum": 250,
                    "maxDecimalPlaces": 1,
                    "options": [],
                    "visibleWhen": null
                },
                {
                    "id": "weightKg",
                    "type": "number",
                    "label": "Vücut Ağırlığı",
                    "required": true,
                    "defaultValue": null,
                    "placeholder": "Ör: 75",
                    "unit": "kg",
                    "minimum": 20,
                    "maximum": 300,
                    "maxDecimalPlaces": 2,
                    "options": [],
                    "visibleWhen": null
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "sex",
                    "type": "text",
                    "label": "Biyolojik Cinsiyet",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "formula",
                    "type": "text",
                    "label": "Seçilen Denklem",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "ageYears",
                    "type": "number",
                    "label": "Yaş",
                    "unit": "yıl",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "heightCm",
                    "type": "number",
                    "label": "Boy",
                    "unit": "cm",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "weightKg",
                    "type": "number",
                    "label": "Vücut Ağırlığı",
                    "unit": "kg",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "mifflinKcalPerDay",
                    "type": "number",
                    "label": "Mifflin–St Jeor Tahmini",
                    "unit": "kcal/gün",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "revisedHarrisBenedictKcalPerDay",
                    "type": "number",
                    "label": "Revize Harris–Benedict Tahmini",
                    "unit": "kcal/gün",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "selectedKcalPerDay",
                    "type": "number",
                    "label": "Seçilen Günlük Tahmin",
                    "unit": "kcal/gün",
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "selectedKcalPerHour",
                    "type": "number",
                    "label": "Saatlik Karşılık",
                    "unit": "kcal/saat",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "selectedSourceScope",
                    "type": "text",
                    "label": "Kaynak Kapsamı",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "selectedSourceScopeNote",
                    "type": "text",
                    "label": "Kaynak Kapsam Notu",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                }
            ]
        },
        "content": {
            "title": "Dinlenme Enerji Harcaması Tahmini",
            "shortTitle": "Dinlenme Enerjisi",
            "summary": "Biyolojik cinsiyet, yaş, boy ve ağırlıktan Mifflin–St Jeor veya revize Harris–Benedict yöntemiyle günlük dinlenme enerji harcaması tahmini üretir.",
            "method": "Araç iki yöntemin günlük kcal tahminini aynı girdilerden hesaplar ve seçilen yöntemin sonucunu öne çıkarır. Saatlik karşılık günlük tahminin 24'e bölünmesiyle gösterilir. Mifflin–St Jeor seçildiğinde yaş, kaynak çalışmanın 19–78 yaş örneklemiyle karşılaştırılır; 18 ve 79–100 yaş değerleri ekstrapolasyon olarak etiketlenir. Revize Harris–Benedict seçildiğinde birincil PubMed özetinde kesin yaş sınırı bulunmadığı ayrıca belirtilir.",
            "limitations": [
                "Sonuç dinlenme enerji harcaması tahminidir; doğrudan kalorimetri ölçümü, tıbbi tanı, tedavi veya kişisel kalori reçetesi değildir.",
                "Mifflin–St Jeor çalışmasının örneklemi 19–78 yaştır. Aracın yetişkin girdi sınırı 18–100 olsa da 18 ve 79–100 yaş sonuçları bu kaynak bakımından ekstrapolasyondur.",
                "Roza–Shizgal özeti kesin yaş alt ve üst sınırını yayımlamaz; normal beslenenlerde yaklaşık ±%14 hassasiyet bildirir ve malnütrisyonda denklemi güvenilmez bulur.",
                "Hastalık, gebelik, ateş, kas kütlesi, ilaçlar ve klinik durum gerçek enerji harcamasını değiştirebilir."
            ],
            "faqs": [
                {
                    "question": "18 yaş neden Mifflin–St Jeor için ekstrapolasyon sayılır?",
                    "answer": "Birincil çalışma 19–78 yaş katılımcıları kapsar. Araç yetişkin başlangıcı olarak 18 yaş girdisini kabul eder; ancak 18 yaş kaynak örnekleminin dışında olduğu için sonuç açıkça ekstrapolasyon olarak işaretlenir."
                },
                {
                    "question": "Mifflin–St Jeor ile revize Harris–Benedict'ten hangisi kesin sonuç verir?",
                    "answer": "Hiçbiri kesin ölçüm değildir. İki yöntem de boy, ağırlık, yaş ve biyolojik cinsiyetten istatistiksel tahmin üretir; doğrudan ölçüm için indirekt kalorimetri ve sağlık uzmanı değerlendirmesi gerekir."
                },
                {
                    "question": "Bu sonuç günlük toplam kalori ihtiyacım mıdır?",
                    "answer": "Hayır. Sonuç yalnız dinlenme enerji harcaması tahminidir. Günlük toplam enerji ihtiyacı için hareket, egzersiz, besinlerin termik etkisi ve kişisel sağlık koşulları ayrıca değerlendirilmelidir."
                },
                {
                    "question": "Revize Harris–Benedict için yaş kapsamı neden belirsiz gösterilir?",
                    "answer": "Roza–Shizgal'ın PubMed özetinde daha geniş yaş aralığından ek veriler kullanıldığı yazsa da kesin alt ve üst yaş sınırları verilmez. Araç kanıtlanmamış bir yaş aralığı uydurmak yerine bu sınırı kullanıcıya gösterir."
                }
            ],
            "sourcePanel": {
                "title": "Bilimsel kaynaklar ve kanıt sınırı",
                "summary": "Mifflin–St Jeor için birincil yöntem ve yaş örneklemi; revize Harris–Benedict için birincil yöntem kaydı ile katsayıları açıkça gösteren ek doğrulama kaydı birlikte sunulur.",
                "sourceIds": [
                    "mifflin-1990-pmid-2305711",
                    "roza-shizgal-1984-pmid-6741850",
                    "muller-2001-pmid-11817239"
                ],
                "notes": [
                    "PMID 2305711 exact Mifflin–St Jeor denklemlerini ve 19–78 yaş örneklemini yayımlar.",
                    "PMID 6741850 köken yöntem kaynağıdır; PubMed özeti exact yaş sınırlarını ve katsayıları göstermez.",
                    "PMID 11817239 revize Harris–Benedict katsayılarını görünür biçimde doğrular; köken yayını değildir."
                ]
            }
        },
        "config": {
            "kind": "basal-metabolic-rate-v1",
            "metadata": {
                "calculatorId": "basal-metabolic-rate",
                "version": "1.0.0",
                "effectiveFrom": "2026-08-20",
                "lastVerifiedAt": "2026-08-20",
                "sources": [
                    {
                        "id": "mifflin-1990-pmid-2305711",
                        "title": "A new predictive equation for resting energy expenditure in healthy individuals",
                        "publisher": "The American Journal of Clinical Nutrition / PubMed",
                        "url": "https://pubmed.ncbi.nlm.nih.gov/2305711/",
                        "verifiedAt": "2026-08-20"
                    },
                    {
                        "id": "roza-shizgal-1984-pmid-6741850",
                        "title": "The Harris Benedict equation reevaluated",
                        "publisher": "The American Journal of Clinical Nutrition / PubMed",
                        "url": "https://pubmed.ncbi.nlm.nih.gov/6741850/",
                        "verifiedAt": "2026-08-20"
                    }
                ],
                "notes": [
                    "Formüller dinlenme enerji harcamasını tahmin eder; ölçüm veya tıbbi tanı değildir.",
                    "Mifflin–St Jeor çalışması 19–78 yaş katılımcıları kapsar; bu aralığın dışındaki sonuç ekstrapolasyon olarak etiketlenir.",
                    "Roza–Shizgal PubMed özetinde kesin alt ve üst yaş sınırı verilmediğinden seçili Harris sonucu bu belirsizlikle etiketlenir.",
                    "Yetişkin kapsamı ve metodoloji yılda bir kaynak incelemesinden geçirilmelidir."
                ]
            },
            "ageYears": [
                18,
                100
            ],
            "heightCm": [
                100,
                250
            ],
            "weightKg": [
                20,
                300
            ]
        },
        "testVectors": {
            "kind": "everyday-health-oracle-v1",
            "cases": [
                {
                    "caseId": "male-mifflin-within-source-range",
                    "scenario": "positive",
                    "input": {
                        "sex": "erkek",
                        "formula": "mifflin",
                        "ageYears": 30,
                        "heightCm": 180,
                        "weightKg": 75
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "sex": "erkek",
                            "formula": "mifflin",
                            "ageYears": 30,
                            "heightCm": 180,
                            "weightKg": 75,
                            "mifflinKcalPerDay": 1730,
                            "revisedHarrisBenedictKcalPerDay": 1786.647,
                            "selectedKcalPerDay": 1730,
                            "selectedKcalPerHour": 72.08333333333333,
                            "selectedSourceScope": "within-cited-age-range",
                            "selectedSourceScopeNote": "Seçilen yaş, Mifflin–St Jeor çalışmasında bildirilen 19–78 yaş aralığındadır."
                        }
                    },
                    "tolerance": 1e-9
                },
                {
                    "caseId": "female-harris-age-range-not-explicit",
                    "scenario": "positive",
                    "input": {
                        "sex": "kadin",
                        "formula": "harris",
                        "ageYears": 35,
                        "heightCm": 165,
                        "weightKg": 60
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "sex": "kadin",
                            "formula": "harris",
                            "ageYears": 35,
                            "heightCm": 165,
                            "weightKg": 60,
                            "mifflinKcalPerDay": 1295.25,
                            "revisedHarrisBenedictKcalPerDay": 1362.0330000000001,
                            "selectedKcalPerDay": 1362.0330000000001,
                            "selectedKcalPerHour": 56.751375,
                            "selectedSourceScope": "cited-age-range-not-explicit",
                            "selectedSourceScopeNote": "Roza–Shizgal PubMed özetinde kesin alt ve üst yaş sınırı verilmediği için yaş kapsamı ayrıca doğrulanamamıştır."
                        }
                    },
                    "tolerance": 1e-9
                },
                {
                    "caseId": "mifflin-age-eighteen-extrapolation",
                    "scenario": "boundary-and-extrapolation",
                    "input": {
                        "sex": "erkek",
                        "formula": "mifflin",
                        "ageYears": 18,
                        "heightCm": 180,
                        "weightKg": 75
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "sex": "erkek",
                            "formula": "mifflin",
                            "ageYears": 18,
                            "heightCm": 180,
                            "weightKg": 75,
                            "mifflinKcalPerDay": 1790,
                            "revisedHarrisBenedictKcalPerDay": 1854.771,
                            "selectedKcalPerDay": 1790,
                            "selectedKcalPerHour": 74.58333333333333,
                            "selectedSourceScope": "outside-cited-age-range",
                            "selectedSourceScopeNote": "Seçilen yaş, Mifflin–St Jeor çalışmasında bildirilen 19–78 yaş aralığının dışındadır; sonuç ekstrapolasyondur."
                        }
                    },
                    "tolerance": 1e-9
                },
                {
                    "caseId": "age-below-input-range",
                    "scenario": "adversarial",
                    "input": {
                        "sex": "erkek",
                        "formula": "mifflin",
                        "ageYears": 17,
                        "heightCm": 180,
                        "weightKg": 75
                    },
                    "expected": {
                        "ok": false,
                        "field": "ageYears",
                        "errorCode": "OUT_OF_RANGE"
                    },
                    "tolerance": 0
                },
                {
                    "caseId": "age-above-input-range",
                    "scenario": "adversarial",
                    "input": {
                        "sex": "erkek",
                        "formula": "mifflin",
                        "ageYears": 101,
                        "heightCm": 180,
                        "weightKg": 75
                    },
                    "expected": {
                        "ok": false,
                        "field": "ageYears",
                        "errorCode": "OUT_OF_RANGE"
                    },
                    "tolerance": 0
                },
                {
                    "caseId": "height-below-input-range",
                    "scenario": "adversarial",
                    "input": {
                        "sex": "erkek",
                        "formula": "mifflin",
                        "ageYears": 30,
                        "heightCm": 99,
                        "weightKg": 75
                    },
                    "expected": {
                        "ok": false,
                        "field": "heightCm",
                        "errorCode": "OUT_OF_RANGE"
                    },
                    "tolerance": 0
                },
                {
                    "caseId": "weight-above-input-range",
                    "scenario": "adversarial",
                    "input": {
                        "sex": "erkek",
                        "formula": "mifflin",
                        "ageYears": 30,
                        "heightCm": 180,
                        "weightKg": 301
                    },
                    "expected": {
                        "ok": false,
                        "field": "weightKg",
                        "errorCode": "OUT_OF_RANGE"
                    },
                    "tolerance": 0
                },
                {
                    "caseId": "unsupported-sex",
                    "scenario": "adversarial",
                    "input": {
                        "sex": "diger",
                        "formula": "mifflin",
                        "ageYears": 30,
                        "heightCm": 180,
                        "weightKg": 75
                    },
                    "expected": {
                        "ok": false,
                        "field": "sex",
                        "errorCode": "UNSUPPORTED_VALUE"
                    },
                    "tolerance": 0
                },
                {
                    "caseId": "unsupported-formula",
                    "scenario": "adversarial",
                    "input": {
                        "sex": "erkek",
                        "formula": "owen",
                        "ageYears": 30,
                        "heightCm": 180,
                        "weightKg": 75
                    },
                    "expected": {
                        "ok": false,
                        "field": "formula",
                        "errorCode": "UNSUPPORTED_VALUE"
                    },
                    "tolerance": 0
                },
                {
                    "caseId": "source-verification-date-mismatch",
                    "scenario": "adversarial",
                    "input": {
                        "sex": "erkek",
                        "formula": "mifflin",
                        "ageYears": 30,
                        "heightCm": 180,
                        "weightKg": 75
                    },
                    "configMutation": {
                        "path": [
                            "metadata",
                            "lastVerifiedAt"
                        ],
                        "replacement": "2026-08-19"
                    },
                    "expected": {
                        "ok": false,
                        "field": "config.metadata.lastVerifiedAt",
                        "errorCode": "INVALID_CONFIG"
                    },
                    "tolerance": 0
                }
            ]
        },
        "lastReviewed": "2026-08-20"
    },
    {
        "toolId": "historical-ideal-weight-reference",
        "slug": "ideal-kilo-hesaplama",
        "path": "/saglik-yasam/ideal-kilo-hesaplama",
        "category": {
            "id": "saglik-yasam",
            "label": "Sağlık ve Yaşam"
        },
        "classification": "timeless",
        "engineId": "historical-ideal-weight-reference-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "2026.8.20",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-08-20",
        "effectiveTo": null,
        "minAppVersion": "0.1.0",
        "updateCadence": {
            "mode": "method-change-only",
            "days": null,
            "reviewAfter": null,
            "specialMonths": [],
            "note": "Denklem kodu yalnız yöntem değişiminde güncellenir; ayrı bakım sınıfı kaynak metodolojisini yılda bir insan incelemesine bağlar."
        },
        "sources": [
            {
                "sourceId": "peterson-2016-pmc4841935",
                "sourceGroupId": null,
                "title": "Universal equation for estimating ideal body weight and body weight at any BMI",
                "publisher": "The American Journal of Clinical Nutrition / PubMed Central",
                "url": "https://pmc.ncbi.nlm.nih.gov/articles/PMC4841935/",
                "kind": "peer-reviewed-medical-research",
                "reviewedOn": "2026-08-20",
                "watchFields": [
                    "tarihsel denklem kapsamı",
                    "beş fit referans noktası",
                    "kişisel hedef olmama uyarısı"
                ]
            },
            {
                "sourceId": "stachowska-2020-pmc7353890",
                "sourceGroupId": null,
                "title": "Nutritional Support in Coronavirus 2019 Disease",
                "publisher": "Medicina / PubMed Central",
                "url": "https://pmc.ncbi.nlm.nih.gov/articles/PMC7353890/",
                "kind": "peer-reviewed-medical-research",
                "reviewedOn": "2026-08-20",
                "watchFields": [
                    "metrik yuvarlanmış Hamwi katsayıları",
                    "özgün Hamwi yayını olmadığı kanıt sınırı"
                ]
            },
            {
                "sourceId": "robinson-1983-pmid-6869387",
                "sourceGroupId": null,
                "title": "Determination of ideal body weight for drug dosage calculations",
                "publisher": "American Journal of Hospital Pharmacy / PubMed",
                "url": "https://pubmed.ncbi.nlm.nih.gov/6869387/",
                "kind": "peer-reviewed-medical-research",
                "reviewedOn": "2026-08-20",
                "watchFields": [
                    "özgün Robinson katsayıları",
                    "ilaç dozu bağlamı",
                    "v1 yuvarlama farkı"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "sex",
                    "type": "radio",
                    "label": "Biyolojik Cinsiyet",
                    "required": true,
                    "defaultValue": "erkek",
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "erkek",
                            "label": "Erkek"
                        },
                        {
                            "value": "kadin",
                            "label": "Kadın"
                        }
                    ],
                    "visibleWhen": null
                },
                {
                    "id": "heightCm",
                    "type": "number",
                    "label": "Boy",
                    "required": true,
                    "defaultValue": null,
                    "placeholder": "Ör: 180",
                    "unit": "cm",
                    "minimum": 140,
                    "maximum": 220,
                    "maxDecimalPlaces": 1,
                    "options": [],
                    "visibleWhen": null
                },
                {
                    "id": "currentWeightKg",
                    "type": "number",
                    "label": "Mevcut Vücut Ağırlığı",
                    "required": false,
                    "defaultValue": null,
                    "placeholder": "İsteğe bağlı; ör: 75",
                    "unit": "kg",
                    "minimum": 30,
                    "maximum": 250,
                    "maxDecimalPlaces": 2,
                    "options": [],
                    "visibleWhen": null
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "sex",
                    "type": "text",
                    "label": "Biyolojik Cinsiyet",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "heightCm",
                    "type": "number",
                    "label": "Boy",
                    "unit": "cm",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "currentWeightKg",
                    "type": "number",
                    "label": "Mevcut Vücut Ağırlığı",
                    "unit": "kg",
                    "nullable": true,
                    "prominent": false
                },
                {
                    "id": "devineKg",
                    "type": "number",
                    "label": "Devine Referansı",
                    "unit": "kg",
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "robinsonKg",
                    "type": "number",
                    "label": "Robinson Referansı",
                    "unit": "kg",
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "hamwiKg",
                    "type": "number",
                    "label": "Hamwi Referansı",
                    "unit": "kg",
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "referenceMinimumKg",
                    "type": "number",
                    "label": "Referans Aralığı Alt Sınırı",
                    "unit": "kg",
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "referenceMaximumKg",
                    "type": "number",
                    "label": "Referans Aralığı Üst Sınırı",
                    "unit": "kg",
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "currentPosition",
                    "type": "text",
                    "label": "Mevcut Konum",
                    "unit": null,
                    "nullable": true,
                    "prominent": false
                },
                {
                    "id": "differenceFromRangeKg",
                    "type": "number",
                    "label": "Referans Aralığına Fark",
                    "unit": "kg",
                    "nullable": true,
                    "prominent": false
                },
                {
                    "id": "heightSourceScope",
                    "type": "text",
                    "label": "Boy Kaynak Kapsamı",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "heightSourceScopeNote",
                    "type": "text",
                    "label": "Boy Kaynak Kapsam Notu",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                }
            ]
        },
        "content": {
            "title": "Referans Vücut Ağırlığı Denklem Karşılaştırması",
            "shortTitle": "Referans Vücut Ağırlığı",
            "summary": "Boy ve biyolojik cinsiyetten Devine, Peterson tablosundaki yuvarlanmış Robinson ve metrik yuvarlanmış Hamwi sonuçlarını yan yana gösterir.",
            "method": "Boy santimetreden inçe çevrilir ve 5 fit, yani 60 inç üzerindeki fark bulunur. Bu fark Devine, yuvarlanmış Robinson ve metrik yuvarlanmış Hamwi katsayılarıyla ayrı ayrı değerlendirilir. En düşük ve en yüksek sonuç karşılaştırma aralığını oluşturur; mevcut ağırlık isteğe bağlı girilmişse bu aralığa göre yalnız sayısal konum gösterilir. 152,4 cm altındaki hesap geriye matematiksel ekstrapolasyon olarak etiketlenir.",
            "limitations": [
                "Devine, Robinson ve Hamwi tarihsel referanslardır; kişisel olarak olmanız gereken kilo, tıbbi tanı, tedavi hedefi veya ilaç dozu önerisi değildir.",
                "Araç v1 yalnız Devine, Robinson ve Hamwi sonuçlarını uygular. Peterson karşılaştırmasında yer alan Miller denklemi v1 kapsamı dışındadır.",
                "Robinson sonucu özgün makaledeki ondalıklı katsayılar yerine Peterson 2016 Tablo 3'teki yuvarlanmış gösterimi; Hamwi sonucu ise kaynakta açıkça gösterilen metrik yuvarlanmış varyantı kullanır.",
                "Kaynak gösterim 5 fit ve üzerini temel alır. 152,4 cm altındaki sonuç yalnız geriye ekstrapolasyondur ve doğrulanmış klinik hedef sayılmaz.",
                "Yaş, vücut kompozisyonu, kas kütlesi, gebelik, hastalık ve kişisel sağlık hedefleri bu tarihsel denklemlerde yer almaz."
            ],
            "faqs": [
                {
                    "question": "Üç referans sonucu neden birbirinden farklıdır?",
                    "answer": "Denklemler farklı tarihlerde, amaçlarla ve katsayılarla hazırlanmıştır. Evrensel tek bir ideal kilo üretmezler; bu nedenle sonuçlar tek hedef yerine tarihsel yöntem karşılaştırması olarak gösterilir."
                },
                {
                    "question": "152,4 santimetrenin altındaki sonuç nasıl yorumlanmalıdır?",
                    "answer": "Kaynak gösterim 60 inç, yani 152,4 santimetre ve üzerini temel alır. Daha kısa boyda denklem geriye doğru işletilir; sonuç matematiksel ekstrapolasyondur ve klinik hedef değildir."
                },
                {
                    "question": "Miller denklemi neden sonuçlarda yer almıyor?",
                    "answer": "Peterson 2016 karşılaştırma tablosu Miller denklemini de listeler; ancak HepHesapla v1 mevcut web aracıyla aynı kapsamda yalnız Devine, Robinson ve Hamwi'yi uygular. Miller v1 kapsamı dışındadır."
                },
                {
                    "question": "Robinson sonucu neden özgün makaledeki katsayılardan farklı görünebilir?",
                    "answer": "Özgün Robinson özeti ondalıklı katsayılar yayımlar. HepHesapla v1, Peterson 2016 Tablo 3'te verilen yuvarlanmış Robinson gösterimini kullanır ve kaynak panelinde bu farkı gizlemez."
                },
                {
                    "question": "Mevcut ağırlığımı girmek zorunda mıyım?",
                    "answer": "Hayır. Mevcut ağırlık isteğe bağlıdır. Girilmezse üç tarihsel sonuç ve bunların sayısal alt-üst aralığı gösterilir; mevcut ağırlığa ilişkin konum veya fark üretilmez."
                }
            ],
            "sourcePanel": {
                "title": "Bilimsel kaynaklar ve yöntem sürümü",
                "summary": "Peterson 2016 tarihsel denklemleri ve 5 fit sınırını karşılaştırır; ek kaynaklar kullanılan metrik Hamwi varyantını ve Robinson'ın özgün katsayı kökenini şeffaflaştırır.",
                "sourceIds": [
                    "peterson-2016-pmc4841935",
                    "stachowska-2020-pmc7353890",
                    "robinson-1983-pmid-6869387"
                ],
                "notes": [
                    "PMC4841935 Tablo 3 Devine, yuvarlanmış Robinson, Hamwi ve Miller gösterimlerini; metin ise 60 inç alt sınırını yayımlar.",
                    "PMC7353890 HepHesapla v1 ile aynı metrik yuvarlanmış Hamwi katsayılarını gösterir; özgün 1964 Hamwi yayını değildir.",
                    "PMID 6869387 Robinson'ın özgün ondalıklı katsayılarını ve ilaç dozu bağlamını gösterir; v1 Peterson tablosundaki yuvarlanmış gösterimi kullanır.",
                    "Miller kaynakta görünür fakat v1 engine ve sonuç şemasında yer almaz."
                ]
            }
        },
        "config": {
            "kind": "historical-ideal-weight-reference-v1",
            "metadata": {
                "calculatorId": "historical-ideal-weight-reference",
                "version": "1.0.0",
                "effectiveFrom": "2026-08-20",
                "lastVerifiedAt": "2026-08-20",
                "sources": [
                    {
                        "id": "peterson-2016-pmc4841935",
                        "title": "Universal equation for estimating ideal body weight and body weight at any BMI",
                        "publisher": "The American Journal of Clinical Nutrition / PubMed Central",
                        "url": "https://pmc.ncbi.nlm.nih.gov/articles/PMC4841935/",
                        "verifiedAt": "2026-08-20"
                    }
                ],
                "notes": [
                    "Devine, Robinson ve Hamwi çıktıları tarihsel referans formülleridir; kişisel hedef, tanı veya tedavi önerisi değildir.",
                    "Formüller beş fitin altındaki boylarda doğrulanmış klinik hedef olarak yorumlanmamalıdır.",
                    "Metodoloji ve kapsam yılda bir kaynak incelemesinden geçirilmelidir."
                ]
            },
            "heightCm": [
                140,
                220
            ],
            "optionalCurrentWeightKg": [
                30,
                250
            ]
        },
        "testVectors": {
            "kind": "everyday-health-oracle-v1",
            "cases": [
                {
                    "caseId": "male-within-reference-range",
                    "scenario": "positive",
                    "input": {
                        "sex": "erkek",
                        "heightCm": 180,
                        "currentWeightKg": 75
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "sex": "erkek",
                            "heightCm": 180,
                            "currentWeightKg": 75,
                            "devineKg": 74.99212598425197,
                            "robinsonKg": 72.64566929133858,
                            "hamwiKg": 77.33858267716536,
                            "referenceMinimumKg": 72.64566929133858,
                            "referenceMaximumKg": 77.33858267716536,
                            "currentPosition": "within",
                            "differenceFromRangeKg": 0,
                            "heightSourceScope": "within-five-foot-reference-basis",
                            "heightSourceScopeNote": "Boy, tarihsel formüllerin 5 fit (152,4 cm) başlangıç noktasındadır veya üzerindedir."
                        }
                    },
                    "tolerance": 1e-9
                },
                {
                    "caseId": "female-below-reference-range",
                    "scenario": "positive",
                    "input": {
                        "sex": "kadin",
                        "heightCm": 165,
                        "currentWeightKg": 50
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "sex": "kadin",
                            "heightCm": 165,
                            "currentWeightKg": 50,
                            "devineKg": 56.90944881889762,
                            "robinsonKg": 57.43307086614172,
                            "hamwiKg": 56.413385826771645,
                            "referenceMinimumKg": 56.413385826771645,
                            "referenceMaximumKg": 57.43307086614172,
                            "currentPosition": "below",
                            "differenceFromRangeKg": 6.413385826771645,
                            "heightSourceScope": "within-five-foot-reference-basis",
                            "heightSourceScopeNote": "Boy, tarihsel formüllerin 5 fit (152,4 cm) başlangıç noktasındadır veya üzerindedir."
                        }
                    },
                    "tolerance": 1e-9
                },
                {
                    "caseId": "optional-current-weight-absent",
                    "scenario": "optional-input",
                    "input": {
                        "sex": "kadin",
                        "heightCm": 165
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "sex": "kadin",
                            "heightCm": 165,
                            "currentWeightKg": null,
                            "devineKg": 56.90944881889762,
                            "robinsonKg": 57.43307086614172,
                            "hamwiKg": 56.413385826771645,
                            "referenceMinimumKg": 56.413385826771645,
                            "referenceMaximumKg": 57.43307086614172,
                            "currentPosition": null,
                            "differenceFromRangeKg": null,
                            "heightSourceScope": "within-five-foot-reference-basis",
                            "heightSourceScopeNote": "Boy, tarihsel formüllerin 5 fit (152,4 cm) başlangıç noktasındadır veya üzerindedir."
                        }
                    },
                    "tolerance": 1e-9
                },
                {
                    "caseId": "one-millimetre-below-five-foot",
                    "scenario": "extrapolation-threshold-below",
                    "input": {
                        "sex": "erkek",
                        "heightCm": 152.3,
                        "currentWeightKg": 50
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "sex": "erkek",
                            "heightCm": 152.3,
                            "currentWeightKg": 50,
                            "devineKg": 49.909448818897644,
                            "robinsonKg": 51.925196850393704,
                            "hamwiKg": 47.89370078740158,
                            "referenceMinimumKg": 47.89370078740158,
                            "referenceMaximumKg": 51.925196850393704,
                            "currentPosition": "within",
                            "differenceFromRangeKg": 0,
                            "heightSourceScope": "below-five-foot-extrapolation",
                            "heightSourceScopeNote": "Boy, tarihsel formüllerin 5 fit (152,4 cm) başlangıç noktasının altındadır; sonuç geriye doğru ekstrapolasyondur."
                        }
                    },
                    "tolerance": 1e-9
                },
                {
                    "caseId": "exactly-five-foot",
                    "scenario": "extrapolation-threshold-at",
                    "input": {
                        "sex": "erkek",
                        "heightCm": 152.4,
                        "currentWeightKg": 50
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "sex": "erkek",
                            "heightCm": 152.4,
                            "currentWeightKg": 50,
                            "devineKg": 50,
                            "robinsonKg": 52,
                            "hamwiKg": 48,
                            "referenceMinimumKg": 48,
                            "referenceMaximumKg": 52,
                            "currentPosition": "within",
                            "differenceFromRangeKg": 0,
                            "heightSourceScope": "within-five-foot-reference-basis",
                            "heightSourceScopeNote": "Boy, tarihsel formüllerin 5 fit (152,4 cm) başlangıç noktasındadır veya üzerindedir."
                        }
                    },
                    "tolerance": 1e-9
                },
                {
                    "caseId": "height-below-input-range",
                    "scenario": "adversarial",
                    "input": {
                        "sex": "erkek",
                        "heightCm": 139,
                        "currentWeightKg": 75
                    },
                    "expected": {
                        "ok": false,
                        "field": "heightCm",
                        "errorCode": "OUT_OF_RANGE"
                    },
                    "tolerance": 0
                },
                {
                    "caseId": "height-above-input-range",
                    "scenario": "adversarial",
                    "input": {
                        "sex": "erkek",
                        "heightCm": 221,
                        "currentWeightKg": 75
                    },
                    "expected": {
                        "ok": false,
                        "field": "heightCm",
                        "errorCode": "OUT_OF_RANGE"
                    },
                    "tolerance": 0
                },
                {
                    "caseId": "weight-below-input-range",
                    "scenario": "adversarial",
                    "input": {
                        "sex": "erkek",
                        "heightCm": 180,
                        "currentWeightKg": 29
                    },
                    "expected": {
                        "ok": false,
                        "field": "currentWeightKg",
                        "errorCode": "OUT_OF_RANGE"
                    },
                    "tolerance": 0
                },
                {
                    "caseId": "weight-above-input-range",
                    "scenario": "adversarial",
                    "input": {
                        "sex": "erkek",
                        "heightCm": 180,
                        "currentWeightKg": 251
                    },
                    "expected": {
                        "ok": false,
                        "field": "currentWeightKg",
                        "errorCode": "OUT_OF_RANGE"
                    },
                    "tolerance": 0
                },
                {
                    "caseId": "unsupported-sex",
                    "scenario": "adversarial",
                    "input": {
                        "sex": "diger",
                        "heightCm": 180,
                        "currentWeightKg": 75
                    },
                    "expected": {
                        "ok": false,
                        "field": "sex",
                        "errorCode": "UNSUPPORTED_VALUE"
                    },
                    "tolerance": 0
                },
                {
                    "caseId": "numeric-string-weight",
                    "scenario": "adversarial",
                    "input": {
                        "sex": "erkek",
                        "heightCm": 180,
                        "currentWeightKg": "75"
                    },
                    "expected": {
                        "ok": false,
                        "field": "currentWeightKg",
                        "errorCode": "NOT_FINITE"
                    },
                    "tolerance": 0
                },
                {
                    "caseId": "source-url-mismatch",
                    "scenario": "adversarial",
                    "input": {
                        "sex": "erkek",
                        "heightCm": 180,
                        "currentWeightKg": 75
                    },
                    "configMutation": {
                        "path": [
                            "metadata",
                            "sources",
                            0,
                            "url"
                        ],
                        "replacement": "https://example.com/not-accepted"
                    },
                    "expected": {
                        "ok": false,
                        "field": "config.metadata.sources",
                        "errorCode": "INVALID_CONFIG"
                    },
                    "tolerance": 0
                }
            ]
        },
        "lastReviewed": "2026-08-20"
    },
    {
        "toolId": "compound-interest",
        "slug": "bilesik-faiz-hesaplama",
        "path": "/bireysel-finans/bilesik-faiz-hesaplama",
        "category": {
            "id": "bireysel-finans",
            "label": "Bireysel Finans"
        },
        "classification": "timeless",
        "engineId": "compound-interest-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-08-24",
        "effectiveTo": null,
        "minAppVersion": "0.3.0",
        "updateCadence": {
            "mode": "method-change-only",
            "days": null,
            "reviewAfter": null,
            "specialMonths": [],
            "note": "Saf matematik çekirdeği yöntem değişene kadar çalışır; varsa dönemsel alt politika yalnız ilgili modu kapatır."
        },
        "sources": [
            {
                "sourceId": "openstax-time-value-money",
                "sourceGroupId": null,
                "title": "Principles of Finance — Methods for Solving Time Value of Money Problems",
                "publisher": "OpenStax",
                "url": "https://openstax.org/books/principles-finance/pages/7-3-methods-for-solving-time-value-of-money-problems",
                "kind": "authoritative-education",
                "reviewedOn": "2026-08-24",
                "watchFields": [
                    "Bileşik büyüme ve eşit dönemli ödeme yöntemi"
                ]
            },
            {
                "sourceId": "tr-deposit-withholding",
                "sourceGroupId": null,
                "title": "Gelir Vergisi Kanunu Geçici 67 nci Madde Uygulaması",
                "publisher": "Gelir İdaresi Başkanlığı",
                "url": "https://cdn.gib.gov.tr/api/gibportal-file/file/getFile?objectKey=DUYURU%2FUNIVERSAL%2F2026%2F2026_Gecici67.pdf",
                "kind": "official-government",
                "reviewedOn": "2026-08-24",
                "watchFields": [
                    "Mevduat türü ve vadeye göre stopaj oranları"
                ]
            },
            {
                "sourceId": "tr-tufe-monthly-manual",
                "sourceGroupId": null,
                "title": "Enflasyon ve Fiyat İstatistikleri",
                "publisher": "Türkiye İstatistik Kurumu",
                "url": "https://veriportali.tuik.gov.tr/Bulten/Index?dil=1&p=Tuketici-Fiyat-Endeksi-Temmuz-2026-58297",
                "kind": "official-government",
                "reviewedOn": "2026-08-24",
                "watchFields": [
                    "TÜFE aylık verileri ve on iki aylık ortalamalar"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "TRY",
                    "minimum": 1,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 4,
                    "options": [],
                    "visibleWhen": null,
                    "id": "principal",
                    "type": "number",
                    "label": "Anapara",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "%",
                    "minimum": 0,
                    "maximum": 100,
                    "maxDecimalPlaces": 4,
                    "options": [],
                    "visibleWhen": null,
                    "id": "annualRatePercent",
                    "type": "number",
                    "label": "Yıllık nominal faiz",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 1,
                    "maximum": 36500,
                    "maxDecimalPlaces": 4,
                    "options": [],
                    "visibleWhen": null,
                    "id": "term",
                    "type": "number",
                    "label": "Vade",
                    "required": true
                },
                {
                    "defaultValue": "year",
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "year",
                            "label": "Yıl"
                        },
                        {
                            "value": "month",
                            "label": "Ay"
                        },
                        {
                            "value": "day",
                            "label": "Gün"
                        }
                    ],
                    "visibleWhen": null,
                    "id": "termUnit",
                    "type": "select",
                    "label": "Vade birimi",
                    "required": true
                },
                {
                    "defaultValue": "annual",
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "annual",
                            "label": "Yıllık"
                        },
                        {
                            "value": "monthly",
                            "label": "Aylık"
                        },
                        {
                            "value": "daily",
                            "label": "Günlük"
                        },
                        {
                            "value": "continuous",
                            "label": "Sürekli"
                        }
                    ],
                    "visibleWhen": null,
                    "id": "compounding",
                    "type": "radio",
                    "label": "Bileşikleme",
                    "required": true
                },
                {
                    "defaultValue": 0,
                    "placeholder": null,
                    "unit": "TRY",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 4,
                    "options": [],
                    "visibleWhen": null,
                    "id": "monthlyContribution",
                    "type": "number",
                    "label": "Aylık katkı",
                    "required": false
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "principal",
                    "type": "money",
                    "label": "Anapara",
                    "unit": "TRY",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "annualRatePercent",
                    "type": "percentage",
                    "label": "Yıllık nominal faiz",
                    "unit": "%",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "term",
                    "type": "number",
                    "label": "Vade",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "termUnit",
                    "type": "text",
                    "label": "Vade birimi",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "termYears",
                    "type": "number",
                    "label": "Yıl karşılığı",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "compounding",
                    "type": "text",
                    "label": "Bileşikleme",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "monthlyContribution",
                    "type": "money",
                    "label": "Aylık katkı",
                    "unit": "TRY",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "contributionTiming",
                    "type": "text",
                    "label": "Katkı zamanı",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "contributionPaymentCount",
                    "type": "number",
                    "label": "Katkı ödeme adedi",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "totalInvested",
                    "type": "money",
                    "label": "Toplam yatırılan",
                    "unit": "TRY",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "contributionFutureValue",
                    "type": "money",
                    "label": "Katkıların gelecek değeri",
                    "unit": "TRY",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "totalInterest",
                    "type": "money",
                    "label": "Toplam faiz",
                    "unit": "TRY",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "futureValue",
                    "type": "money",
                    "label": "Vade sonu değer",
                    "unit": "TRY",
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "effectiveAnnualRatePercent",
                    "type": "percentage",
                    "label": "Efektif yıllık oran",
                    "unit": "%",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "simpleInterestValue",
                    "type": "money",
                    "label": "Basit faiz karşılığı",
                    "unit": "TRY",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "compoundAdvantage",
                    "type": "money",
                    "label": "Bileşik faiz farkı",
                    "unit": "TRY",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "withholdingScenario",
                    "type": "object",
                    "label": "Stopaj senaryosu",
                    "unit": null,
                    "nullable": true,
                    "prominent": false
                },
                {
                    "id": "inflationScenario",
                    "type": "object",
                    "label": "Enflasyon senaryosu",
                    "unit": null,
                    "nullable": true,
                    "prominent": false
                }
            ]
        },
        "content": {
            "title": "Bileşik Faiz Hesaplama",
            "shortTitle": "Bileşik Faiz",
            "summary": "Anapara ile ay sonu katkılarını seçilen bileşikleme yöntemine göre büyütür; vade sonu değeri ve efektif yıllık oranı verir.",
            "method": "Anapara seçilen yıllık, aylık, günlük veya sürekli yöntemle büyür. Aylık katkının efektif aylık oranı aynı seçili yöntemin yıllık getirisinden türetilir.",
            "limitations": [
                "Varsayılan kayıtta stopaj ve enflasyon karşılaştırması kapalıdır; tek bir oran bütün ürün ve vadeler için evrensel kabul edilmez.",
                "Aylık katkı yalnız tam ay sayısı üreten yıl veya ay vadelerinde, her ayın sonunda yapılmış kabul edilir; gün cinsinden vadede katkı hesabı kapalıdır."
            ],
            "faqs": [
                {
                    "question": "Bileşik Faiz kesin resmî veya banka sonucu verir mi?",
                    "answer": "Hayır. Araç yalnız gösterilen formülü, kullanıcı girdilerini ve yürürlük kaydı açık politika alanlarını uygular. Sözleşme, banka planı veya mükellef özelindeki kesin sonuç ilgili kurum ve uzman kaydıyla doğrulanmalıdır."
                }
            ],
            "sourcePanel": {
                "title": "Kaynak ve politika kapsamı",
                "summary": "Hesap yöntemi kaynak kimlikleriyle sürümlenir. Kullanıcı girdili zamansız matematik bir politika son kullanma veya inceleme tarihine bağlı olarak kapanmaz.",
                "sourceIds": [
                    "openstax-time-value-money",
                    "tr-deposit-withholding",
                    "tr-tufe-monthly-manual"
                ],
                "notes": [
                    "Oran, eşik ve hukuki uygunluk matematik çekirdeğine gömülü değildir."
                ]
            }
        },
        "config": {
            "kind": "compound-interest-v1",
            "policyLifecycle": {
                "configVersion": "1.0.0",
                "sourceIds": [
                    "openstax-time-value-money"
                ],
                "reviewedOn": "2026-08-24",
                "effectiveFrom": "2026-08-24",
                "effectiveTo": null,
                "reviewAfter": null
            },
            "principal": [
                1,
                1000000000
            ],
            "annualRatePercent": [
                0,
                100
            ],
            "term": [
                1,
                36500
            ],
            "maximumTermYears": 100,
            "monthlyContribution": [
                0,
                1000000000
            ],
            "policy": {
                "daysPerYear": 365,
                "monthsPerYear": 12,
                "annualPeriods": 1,
                "monthlyPeriods": 12,
                "dailyPeriods": 365,
                "withholdingScenario": null,
                "inflationScenario": null
            }
        },
        "testVectors": {
            "kind": "finance-business-oracle-v1",
            "cases": [
                {
                    "caseId": "monthly-contribution",
                    "scenario": "positive",
                    "input": {
                        "principal": 10000,
                        "annualRatePercent": 12,
                        "term": 1,
                        "termUnit": "year",
                        "compounding": "monthly",
                        "monthlyContribution": 1000
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "principal": 10000,
                            "annualRatePercent": 12,
                            "term": 1,
                            "termUnit": "year",
                            "termYears": 1,
                            "compounding": "monthly",
                            "monthlyContribution": 1000,
                            "contributionTiming": "end-of-month",
                            "contributionPaymentCount": 12,
                            "totalInvested": 22000,
                            "contributionFutureValue": 12682.503013196965,
                            "totalInterest": 1950.7533145166635,
                            "futureValue": 23950.753314516664,
                            "effectiveAnnualRatePercent": 12.682503013196978,
                            "simpleInterestValue": 23860,
                            "compoundAdvantage": 90.75331451666352
                        }
                    },
                    "tolerance": 1e-9
                },
                {
                    "caseId": "term-year-boundary",
                    "scenario": "boundary",
                    "input": {
                        "principal": 1000,
                        "annualRatePercent": 0.01,
                        "term": 100,
                        "termUnit": "year",
                        "compounding": "annual"
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "termYears": 100,
                            "contributionPaymentCount": 0,
                            "contributionTiming": "end-of-month"
                        }
                    },
                    "tolerance": 1e-9
                },
                {
                    "caseId": "term-month-boundary",
                    "scenario": "boundary",
                    "input": {
                        "principal": 1000,
                        "annualRatePercent": 0.01,
                        "term": 1200,
                        "termUnit": "month",
                        "compounding": "monthly"
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "termYears": 100,
                            "contributionPaymentCount": 0,
                            "contributionTiming": "end-of-month"
                        }
                    },
                    "tolerance": 1e-9
                },
                {
                    "caseId": "term-day-boundary",
                    "scenario": "boundary",
                    "input": {
                        "principal": 1000,
                        "annualRatePercent": 0.01,
                        "term": 36500,
                        "termUnit": "day",
                        "compounding": "daily"
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "termYears": 100,
                            "contributionPaymentCount": 0,
                            "contributionTiming": "end-of-month"
                        }
                    },
                    "tolerance": 1e-9
                },
                {
                    "caseId": "zero-rate-contribution-boundary",
                    "scenario": "zero",
                    "input": {
                        "principal": 10000,
                        "annualRatePercent": 0,
                        "term": 1,
                        "termUnit": "year",
                        "compounding": "monthly",
                        "monthlyContribution": 1000
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "contributionFutureValue": 12000,
                            "totalInvested": 22000,
                            "totalInterest": 0,
                            "futureValue": 22000,
                            "effectiveAnnualRatePercent": 0,
                            "simpleInterestValue": 22000,
                            "compoundAdvantage": 0,
                            "contributionTiming": "end-of-month",
                            "contributionPaymentCount": 12
                        }
                    },
                    "tolerance": 1e-9
                },
                {
                    "caseId": "term-year-over-boundary",
                    "scenario": "adversarial",
                    "input": {
                        "principal": 1000,
                        "annualRatePercent": 0.01,
                        "term": 100.0001,
                        "termUnit": "year",
                        "compounding": "annual"
                    },
                    "expected": {
                        "ok": false,
                        "field": "term",
                        "errorCode": "OUT_OF_RANGE"
                    },
                    "tolerance": 1e-9
                },
                {
                    "caseId": "term-month-over-boundary",
                    "scenario": "adversarial",
                    "input": {
                        "principal": 1000,
                        "annualRatePercent": 0.01,
                        "term": 1201,
                        "termUnit": "month",
                        "compounding": "monthly"
                    },
                    "expected": {
                        "ok": false,
                        "field": "term",
                        "errorCode": "OUT_OF_RANGE"
                    },
                    "tolerance": 1e-9
                },
                {
                    "caseId": "term-day-over-boundary",
                    "scenario": "adversarial",
                    "input": {
                        "principal": 1000,
                        "annualRatePercent": 0.01,
                        "term": 36501,
                        "termUnit": "day",
                        "compounding": "daily"
                    },
                    "expected": {
                        "ok": false,
                        "field": "term",
                        "errorCode": "OUT_OF_RANGE"
                    },
                    "tolerance": 1e-9
                },
                {
                    "caseId": "negative-contribution",
                    "scenario": "adversarial",
                    "input": {
                        "principal": 10000,
                        "annualRatePercent": 12,
                        "term": 1,
                        "termUnit": "year",
                        "compounding": "monthly",
                        "monthlyContribution": -1
                    },
                    "expected": {
                        "ok": false,
                        "field": "monthlyContribution",
                        "errorCode": "OUT_OF_RANGE"
                    },
                    "tolerance": 1e-9
                }
            ]
        },
        "lastReviewed": "2026-08-24"
    },
    {
        "toolId": "inflation-value",
        "slug": "enflasyon-hesaplama",
        "path": "/bireysel-finans/enflasyon-hesaplama",
        "category": {
            "id": "bireysel-finans",
            "label": "Bireysel Finans"
        },
        "classification": "timeless",
        "engineId": "inflation-value-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-08-24",
        "effectiveTo": null,
        "minAppVersion": "0.3.0",
        "updateCadence": {
            "mode": "method-change-only",
            "days": null,
            "reviewAfter": null,
            "specialMonths": [],
            "note": "Saf matematik çekirdeği yöntem değişene kadar çalışır; varsa dönemsel alt politika yalnız ilgili modu kapatır."
        },
        "sources": [
            {
                "sourceId": "tr-tufe-monthly-manual",
                "sourceGroupId": null,
                "title": "Enflasyon ve Fiyat İstatistikleri",
                "publisher": "Türkiye İstatistik Kurumu",
                "url": "https://veriportali.tuik.gov.tr/Bulten/Index?dil=1&p=Tuketici-Fiyat-Endeksi-Temmuz-2026-58297",
                "kind": "official-government",
                "reviewedOn": "2026-08-24",
                "watchFields": [
                    "TÜFE aylık verileri ve on iki aylık ortalamalar"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "TRY",
                    "minimum": 1,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 4,
                    "options": [],
                    "visibleWhen": null,
                    "id": "amount",
                    "type": "number",
                    "label": "Tutar",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "%",
                    "minimum": 0,
                    "maximum": 100,
                    "maxDecimalPlaces": 4,
                    "options": [],
                    "visibleWhen": null,
                    "id": "annualInflationPercent",
                    "type": "number",
                    "label": "Yıllık enflasyon",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "yıl",
                    "minimum": 1,
                    "maximum": 50,
                    "maxDecimalPlaces": 4,
                    "options": [],
                    "visibleWhen": null,
                    "id": "years",
                    "type": "number",
                    "label": "Süre",
                    "required": true
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "amount",
                    "type": "money",
                    "label": "Başlangıç tutarı",
                    "unit": "TRY",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "annualInflationPercent",
                    "type": "percentage",
                    "label": "Yıllık enflasyon",
                    "unit": "%",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "years",
                    "type": "number",
                    "label": "Süre",
                    "unit": "yıl",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "cumulativeFactor",
                    "type": "number",
                    "label": "Birikimli fiyat çarpanı",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "futureEquivalent",
                    "type": "money",
                    "label": "Gelecekte eşdeğer nominal tutar",
                    "unit": "TRY",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "presentPurchasingPower",
                    "type": "money",
                    "label": "Gelecekte aynı nominal tutarın bugünkü alım gücü",
                    "unit": "TRY",
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "purchasingPowerLoss",
                    "type": "money",
                    "label": "Alım gücü kaybı",
                    "unit": "TRY",
                    "nullable": false,
                    "prominent": false
                }
            ]
        },
        "content": {
            "title": "TÜFE ile Enflasyon ve Para Değer Kaybı Hesaplama",
            "shortTitle": "Enflasyon ve Para Değeri",
            "summary": "Girilen yıllık enflasyon senaryosuyla gelecekteki eşdeğer nominal tutarı ve aynı nominal tutarın bugünkü alım gücünü ayrı ayrı gösterir.",
            "method": "Fiyat çarpanı (1+oran)^yıl olarak bulunur; gelecek eşdeğer tutar çarpılır, gelecekteki aynı nominal tutarın bugünkü alım gücü bölünür.",
            "limitations": [
                "Araç güncel TÜFE değerini otomatik çekmez; kullanıcı resmî kaynaktaki uygun seriyi açıkça girmelidir. İki sonuç farklı ekonomik soruları yanıtlar."
            ],
            "faqs": [
                {
                    "question": "Enflasyon ve Para Değeri kesin resmî veya banka sonucu verir mi?",
                    "answer": "Hayır. Araç yalnız gösterilen formülü, kullanıcı girdilerini ve yürürlük kaydı açık politika alanlarını uygular. Sözleşme, banka planı veya mükellef özelindeki kesin sonuç ilgili kurum ve uzman kaydıyla doğrulanmalıdır."
                }
            ],
            "sourcePanel": {
                "title": "Kaynak ve politika kapsamı",
                "summary": "Hesap yöntemi kaynak kimlikleriyle sürümlenir. Kullanıcı girdili zamansız matematik bir politika son kullanma veya inceleme tarihine bağlı olarak kapanmaz.",
                "sourceIds": [
                    "tr-tufe-monthly-manual"
                ],
                "notes": [
                    "Oran, eşik ve hukuki uygunluk matematik çekirdeğine gömülü değildir."
                ]
            }
        },
        "config": {
            "kind": "inflation-value-v1",
            "policyLifecycle": {
                "configVersion": "1.0.0",
                "sourceIds": [
                    "tr-tufe-monthly-manual"
                ],
                "reviewedOn": "2026-08-24",
                "effectiveFrom": "2026-08-24",
                "effectiveTo": null,
                "reviewAfter": null
            },
            "amount": [
                1,
                1000000000
            ],
            "annualInflationPercent": [
                0,
                100
            ],
            "years": [
                1,
                50
            ]
        },
        "testVectors": {
            "kind": "finance-business-oracle-v1",
            "cases": [
                {
                    "caseId": "fifty-percent-two-years",
                    "scenario": "positive",
                    "input": {
                        "amount": 1000,
                        "annualInflationPercent": 50,
                        "years": 2
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "amount": 1000,
                            "annualInflationPercent": 50,
                            "years": 2,
                            "cumulativeFactor": 2.25,
                            "futureEquivalent": 2250,
                            "presentPurchasingPower": 444.44444444444446,
                            "purchasingPowerLoss": 555.5555555555555
                        }
                    },
                    "tolerance": 1e-9
                },
                {
                    "caseId": "non-finite-rate",
                    "scenario": "adversarial",
                    "input": {
                        "amount": 1000,
                        "annualInflationPercent": "NaN",
                        "years": 2
                    },
                    "expected": {
                        "ok": false,
                        "field": "annualInflationPercent",
                        "errorCode": "NOT_FINITE"
                    },
                    "tolerance": 1e-9
                }
            ]
        },
        "lastReviewed": "2026-08-24"
    },
    {
        "toolId": "housing-loan",
        "slug": "konut-kredisi-hesaplama",
        "path": "/bireysel-finans/konut-kredisi-hesaplama",
        "category": {
            "id": "bireysel-finans",
            "label": "Bireysel Finans"
        },
        "classification": "timeless",
        "engineId": "housing-loan-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-08-24",
        "effectiveTo": null,
        "minAppVersion": "0.3.0",
        "updateCadence": {
            "mode": "method-change-only",
            "days": null,
            "reviewAfter": null,
            "specialMonths": [],
            "note": "Saf matematik çekirdeği yöntem değişene kadar çalışır; varsa dönemsel alt politika yalnız ilgili modu kapatır."
        },
        "sources": [
            {
                "sourceId": "openstax-time-value-money",
                "sourceGroupId": null,
                "title": "Principles of Finance — Methods for Solving Time Value of Money Problems",
                "publisher": "OpenStax",
                "url": "https://openstax.org/books/principles-finance/pages/7-3-methods-for-solving-time-value-of-money-problems",
                "kind": "authoritative-education",
                "reviewedOn": "2026-08-24",
                "watchFields": [
                    "Bileşik büyüme ve eşit dönemli ödeme yöntemi"
                ]
            },
            {
                "sourceId": "tr-housing-credit-ltv",
                "sourceGroupId": null,
                "title": "Konut Kredilerinde Kredi Değer Oranı ve Azami Kredi Tutarı — 11364 sayılı Karar",
                "publisher": "Bankacılık Düzenleme ve Denetleme Kurumu",
                "url": "https://www.bddk.org.tr/Mevzuat/DokumanGetir/1327",
                "kind": "official-government",
                "reviewedOn": "2026-08-24",
                "watchFields": [
                    "Konut kredisi kredi-değer oranı ve mevcut konut sahipliği etkisi"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "TRY",
                    "minimum": 1,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 4,
                    "options": [],
                    "visibleWhen": null,
                    "id": "principal",
                    "type": "number",
                    "label": "Kredi tutarı",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "%",
                    "minimum": 0,
                    "maximum": 100,
                    "maxDecimalPlaces": 4,
                    "options": [],
                    "visibleWhen": null,
                    "id": "monthlyRatePercent",
                    "type": "number",
                    "label": "Aylık faiz",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "ay",
                    "minimum": 1,
                    "maximum": 360,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "termMonths",
                    "type": "number",
                    "label": "Vade",
                    "required": true
                },
                {
                    "defaultValue": 0,
                    "placeholder": null,
                    "unit": "%",
                    "minimum": 0,
                    "maximum": 100,
                    "maxDecimalPlaces": 4,
                    "options": [],
                    "visibleWhen": null,
                    "id": "fileFeePercent",
                    "type": "number",
                    "label": "Tahsis ücreti oranı",
                    "required": false
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "principal",
                    "type": "money",
                    "label": "Kredi tutarı",
                    "unit": "TRY",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "monthlyRatePercent",
                    "type": "percentage",
                    "label": "Aylık faiz",
                    "unit": "%",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "termMonths",
                    "type": "number",
                    "label": "Vade",
                    "unit": "ay",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "fileFeePercent",
                    "type": "percentage",
                    "label": "Tahsis ücreti oranı",
                    "unit": "%",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "monthlyPayment",
                    "type": "money",
                    "label": "Aylık taksit",
                    "unit": "TRY",
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "totalInterest",
                    "type": "money",
                    "label": "Toplam faiz",
                    "unit": "TRY",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "totalPayment",
                    "type": "money",
                    "label": "Toplam taksit ödemesi",
                    "unit": "TRY",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "fileFee",
                    "type": "money",
                    "label": "Tahsis ücreti",
                    "unit": "TRY",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "overallCost",
                    "type": "money",
                    "label": "Ücret dahil toplam",
                    "unit": "TRY",
                    "nullable": false,
                    "prominent": false
                }
            ]
        },
        "content": {
            "title": "Konut Kredisi Aylık Taksit Hesaplama",
            "shortTitle": "Konut Kredisi",
            "summary": "Kredi tutarı, aylık oran, vade ve açık tahsis ücreti girdisinden eşit taksit ve toplam ödeme ön hesabı yapar.",
            "method": "Sıfır faiz dışında standart dönem sonu anüite formülü uygulanır; tahsis ücreti ayrıca kredi tutarı üzerinden hesaplanır.",
            "limitations": [
                "Bu motor taşınmaz değeri, enerji sınıfı veya mevcut konut sahipliği girdisi almadığı için BDDK kredi-değer oranı ya da kredi uygunluğu sonucu üretmez."
            ],
            "faqs": [
                {
                    "question": "Konut Kredisi kesin resmî veya banka sonucu verir mi?",
                    "answer": "Hayır. Araç yalnız gösterilen formülü, kullanıcı girdilerini ve yürürlük kaydı açık politika alanlarını uygular. Sözleşme, banka planı veya mükellef özelindeki kesin sonuç ilgili kurum ve uzman kaydıyla doğrulanmalıdır."
                }
            ],
            "sourcePanel": {
                "title": "Kaynak ve politika kapsamı",
                "summary": "Hesap yöntemi kaynak kimlikleriyle sürümlenir. Kullanıcı girdili zamansız matematik bir politika son kullanma veya inceleme tarihine bağlı olarak kapanmaz.",
                "sourceIds": [
                    "openstax-time-value-money",
                    "tr-housing-credit-ltv"
                ],
                "notes": [
                    "Oran, eşik ve hukuki uygunluk matematik çekirdeğine gömülü değildir."
                ]
            }
        },
        "config": {
            "kind": "housing-loan-v1",
            "policyLifecycle": {
                "configVersion": "1.0.0",
                "sourceIds": [
                    "openstax-time-value-money"
                ],
                "reviewedOn": "2026-08-24",
                "effectiveFrom": "2026-08-24",
                "effectiveTo": null,
                "reviewAfter": null
            },
            "principal": [
                1,
                1000000000
            ],
            "monthlyRatePercent": [
                0,
                100
            ],
            "termMonths": [
                1,
                360
            ],
            "fileFeePercent": [
                0,
                100
            ]
        },
        "testVectors": {
            "kind": "finance-business-oracle-v1",
            "cases": [
                {
                    "caseId": "zero-rate-with-fee",
                    "scenario": "zero",
                    "input": {
                        "principal": 120000,
                        "monthlyRatePercent": 0,
                        "termMonths": 12,
                        "fileFeePercent": 0.5
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "principal": 120000,
                            "monthlyRatePercent": 0,
                            "termMonths": 12,
                            "fileFeePercent": 0.5,
                            "monthlyPayment": 10000,
                            "totalInterest": 0,
                            "totalPayment": 120000,
                            "fileFee": 600,
                            "overallCost": 120600
                        }
                    },
                    "tolerance": 1e-9
                },
                {
                    "caseId": "zero-rate-awkward-divisor",
                    "scenario": "boundary",
                    "input": {
                        "principal": 1000000000,
                        "monthlyRatePercent": 0,
                        "termMonths": 29
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "principal": 1000000000,
                            "monthlyRatePercent": 0,
                            "termMonths": 29,
                            "fileFeePercent": 0,
                            "monthlyPayment": 34482758.62068965,
                            "totalInterest": 0,
                            "totalPayment": 1000000000,
                            "fileFee": 0,
                            "overallCost": 1000000000
                        }
                    },
                    "tolerance": 1e-9
                },
                {
                    "caseId": "term-over-limit",
                    "scenario": "adversarial",
                    "input": {
                        "principal": 120000,
                        "monthlyRatePercent": 2,
                        "termMonths": 361
                    },
                    "expected": {
                        "ok": false,
                        "field": "termMonths",
                        "errorCode": "OUT_OF_RANGE"
                    },
                    "tolerance": 1e-9
                }
            ]
        },
        "lastReviewed": "2026-08-24"
    },
    {
        "toolId": "consumer-loan",
        "slug": "kredi-taksit-hesaplama",
        "path": "/bireysel-finans/kredi-taksit-hesaplama",
        "category": {
            "id": "bireysel-finans",
            "label": "Bireysel Finans"
        },
        "classification": "remote-config",
        "engineId": "consumer-loan-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.2",
        "effectiveFrom": "2026-10-21",
        "effectiveTo": null,
        "minAppVersion": "0.3.0",
        "updateCadence": {
            "mode": "scheduled-source-review",
            "days": 14,
            "reviewAfter": "2026-11-04",
            "specialMonths": [],
            "note": "Kaynak inceleme tarihi yönetici uyarısı üretir; yalnız doğrulanmış geçerlilik bitişi veya geçersiz yapılandırma hesaplamayı kapatır."
        },
        "sources": [
            {
                "sourceId": "openstax-time-value-money",
                "sourceGroupId": null,
                "title": "Principles of Finance — Methods for Solving Time Value of Money Problems",
                "publisher": "OpenStax",
                "url": "https://openstax.org/books/principles-finance/pages/7-3-methods-for-solving-time-value-of-money-problems",
                "kind": "authoritative-education",
                "reviewedOn": "2026-10-21",
                "watchFields": [
                    "Bileşik büyüme ve eşit dönemli ödeme yöntemi"
                ]
            },
            {
                "sourceId": "tr-consumer-credit-taxes",
                "sourceGroupId": null,
                "title": "98/11591 sayılı Karar — 7345 sayılı Kararla Güncellenen Tüketici Kredisi BSMV Oranı",
                "publisher": "Gelir İdaresi Başkanlığı",
                "url": "https://gib.gov.tr/mevzuat/kanun/445/bkk/1849",
                "kind": "official-government",
                "reviewedOn": "2026-10-21",
                "watchFields": [
                    "Tüketici kredisi faizindeki BSMV oranı"
                ]
            },
            {
                "sourceId": "tr-consumer-credit-kkdf",
                "sourceGroupId": null,
                "title": "Kaynak Kullanımını Destekleme Fonu Oranları — 2010/974 sayılı Karar",
                "publisher": "T.C. Maliye Bakanlığı Gelir İdaresi Başkanlığı",
                "url": "https://cdn.gib.gov.tr/api/gibportal-file/file/getFileResources?objectKey=arsiv%2Fyardim-kaynaklar%2Fyararli-bilgiler%2Fkkdf-oranlari.pdf",
                "kind": "official-government",
                "reviewedOn": "2026-10-21",
                "watchFields": [
                    "Ticari amaç dışında kullandırılan tüketici kredilerindeki KKDF oranı"
                ]
            },
            {
                "sourceId": "tr-consumer-credit-term",
                "sourceGroupId": null,
                "title": "Tüketici Kredileri Genel Vade Sınırının Değiştirilmesi — 11152 sayılı Karar",
                "publisher": "Bankacılık Düzenleme ve Denetleme Kurumu",
                "url": "https://www.bddk.org.tr/Duyuru/EkGetir/2101?ekId=866",
                "kind": "official-government",
                "reviewedOn": "2026-10-21",
                "watchFields": [
                    "Kredi tutarına bağlı 36, 24 ve 12 aylık azami vade sınırları"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "TRY",
                    "minimum": 1,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 4,
                    "options": [],
                    "visibleWhen": null,
                    "id": "principal",
                    "type": "number",
                    "label": "Kredi tutarı",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "%",
                    "minimum": 0,
                    "maximum": 100,
                    "maxDecimalPlaces": 4,
                    "options": [],
                    "visibleWhen": null,
                    "id": "monthlyRatePercent",
                    "type": "number",
                    "label": "Aylık nominal faiz",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "ay",
                    "minimum": 1,
                    "maximum": 36,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "termMonths",
                    "type": "number",
                    "label": "Vade",
                    "required": true
                },
                {
                    "defaultValue": "plain",
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "plain",
                            "label": "Vergi ayrıştırması olmadan"
                        },
                        {
                            "value": "tax-inclusive-interest",
                            "label": "BSMV ve KKDF eklenmiş faiz"
                        }
                    ],
                    "visibleWhen": null,
                    "id": "mode",
                    "type": "radio",
                    "label": "Faiz vergileri",
                    "required": true
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "principal",
                    "type": "money",
                    "label": "Kredi tutarı",
                    "unit": "TRY",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "monthlyRatePercent",
                    "type": "percentage",
                    "label": "Aylık nominal faiz",
                    "unit": "%",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "termMonths",
                    "type": "number",
                    "label": "Vade",
                    "unit": "ay",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "mode",
                    "type": "text",
                    "label": "Hesap modu",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "effectiveMonthlyRatePercent",
                    "type": "percentage",
                    "label": "Vergi dahil aylık oran",
                    "unit": "%",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "monthlyPayment",
                    "type": "money",
                    "label": "Aylık taksit",
                    "unit": "TRY",
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "totalInterestAndTaxes",
                    "type": "money",
                    "label": "Toplam finansman maliyeti",
                    "unit": "TRY",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "pureInterestAmount",
                    "type": "money",
                    "label": "Vergi hariç faiz payı",
                    "unit": "TRY",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "totalPayment",
                    "type": "money",
                    "label": "Toplam geri ödeme",
                    "unit": "TRY",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "bsmvAmount",
                    "type": "money",
                    "label": "BSMV payı",
                    "unit": "TRY",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "kkdfAmount",
                    "type": "money",
                    "label": "KKDF payı",
                    "unit": "TRY",
                    "nullable": false,
                    "prominent": false
                }
            ]
        },
        "content": {
            "title": "Kredi Taksit ve Toplam Geri Ödeme Hesaplama",
            "shortTitle": "Kredi Taksiti",
            "summary": "Tüketici kredisi tutarı, nominal aylık faiz ve vade için eşit taksiti; seçilirse BSMV ve KKDF paylarını ayrı gösterir.",
            "method": "Vergili modda nominal aylık oran, sürümlü BSMV ve KKDF oranlarıyla büyütülür; anüite hesaplandıktan sonra faiz ve vergi payları aynı çarpandan ayrıştırılır.",
            "limitations": [
                "Kredi tutarına göre doğrulanmış 36/24/12 aylık azami vade uygulanır. Sigorta, tahsis ücreti, günlük faiz ve banka yuvarlaması dahil değildir."
            ],
            "faqs": [
                {
                    "question": "Kredi Taksiti kesin resmî veya banka sonucu verir mi?",
                    "answer": "Hayır. Araç yalnız gösterilen formülü, kullanıcı girdilerini ve yürürlük kaydı açık politika alanlarını uygular. Sözleşme, banka planı veya mükellef özelindeki kesin sonuç ilgili kurum ve uzman kaydıyla doğrulanmalıdır."
                }
            ],
            "sourcePanel": {
                "title": "Kaynak ve politika kapsamı",
                "summary": "Oran ve eşikler tarihli, kaynak kimlikli politika kaydından gelir. Kayıt sona erdiğinde veya inceleme tarihi geldiğinde yeni insan onayı olmadan araç hesaplama yapmaz.",
                "sourceIds": [
                    "openstax-time-value-money",
                    "tr-consumer-credit-taxes",
                    "tr-consumer-credit-kkdf",
                    "tr-consumer-credit-term"
                ],
                "notes": [
                    "Oran, eşik ve hukuki uygunluk matematik çekirdeğine gömülü değildir."
                ]
            }
        },
        "config": {
            "kind": "consumer-loan-v1",
            "policyLifecycle": {
                "configVersion": "1.0.2",
                "sourceIds": [
                    "openstax-time-value-money",
                    "tr-consumer-credit-taxes",
                    "tr-consumer-credit-kkdf",
                    "tr-consumer-credit-term"
                ],
                "reviewedOn": "2026-10-21",
                "effectiveFrom": "2026-10-21",
                "effectiveTo": null,
                "reviewAfter": "2026-11-04"
            },
            "principal": [
                1,
                1000000000
            ],
            "monthlyRatePercent": [
                0,
                100
            ],
            "termMonths": [
                1,
                36
            ],
            "policy": {
                "bsmvRate": 0.15,
                "kkdfRate": 0.15,
                "maximumTermTiers": [
                    {
                        "upToPrincipal": 125000,
                        "maximumMonths": 36
                    },
                    {
                        "upToPrincipal": 250000,
                        "maximumMonths": 24
                    },
                    {
                        "upToPrincipal": null,
                        "maximumMonths": 12
                    }
                ]
            }
        },
        "testVectors": {
            "kind": "finance-business-oracle-v1",
            "cases": [
                {
                    "caseId": "tax-inclusive-golden",
                    "scenario": "positive",
                    "input": {
                        "principal": 100000,
                        "monthlyRatePercent": 2,
                        "termMonths": 12,
                        "mode": "tax-inclusive-interest"
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "principal": 100000,
                            "monthlyRatePercent": 2,
                            "termMonths": 12,
                            "mode": "tax-inclusive-interest",
                            "effectiveMonthlyRatePercent": 2.5999999999999996,
                            "monthlyPayment": 9807.83408250273,
                            "totalInterestAndTaxes": 17694.00899003276,
                            "pureInterestAmount": 13610.776146179043,
                            "totalPayment": 117694.00899003276,
                            "bsmvAmount": 2041.6164219268571,
                            "kkdfAmount": 2041.6164219268571
                        }
                    },
                    "tolerance": 1e-9
                },
                {
                    "caseId": "zero-rate-awkward-divisor",
                    "scenario": "zero",
                    "input": {
                        "principal": 100000,
                        "monthlyRatePercent": 0,
                        "termMonths": 29,
                        "mode": "tax-inclusive-interest"
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "principal": 100000,
                            "monthlyRatePercent": 0,
                            "termMonths": 29,
                            "mode": "tax-inclusive-interest",
                            "effectiveMonthlyRatePercent": 0,
                            "monthlyPayment": 3448.2758620689656,
                            "totalInterestAndTaxes": 0,
                            "pureInterestAmount": 0,
                            "totalPayment": 100000,
                            "bsmvAmount": 0,
                            "kkdfAmount": 0
                        }
                    },
                    "tolerance": 1e-9
                },
                {
                    "caseId": "tier-boundary",
                    "scenario": "boundary",
                    "input": {
                        "principal": 125000,
                        "monthlyRatePercent": 2,
                        "termMonths": 36,
                        "mode": "plain"
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "principal": 125000,
                            "monthlyRatePercent": 2,
                            "termMonths": 36,
                            "mode": "plain",
                            "effectiveMonthlyRatePercent": 2,
                            "monthlyPayment": 4904.106574724766,
                            "totalInterestAndTaxes": 51547.83669009156,
                            "pureInterestAmount": 51547.83669009156,
                            "totalPayment": 176547.83669009156,
                            "bsmvAmount": 0,
                            "kkdfAmount": 0
                        }
                    },
                    "tolerance": 1e-9
                },
                {
                    "caseId": "tier-cross-field",
                    "scenario": "adversarial",
                    "input": {
                        "principal": 125000.01,
                        "monthlyRatePercent": 2,
                        "termMonths": 36,
                        "mode": "plain"
                    },
                    "expected": {
                        "ok": false,
                        "field": "termMonths",
                        "errorCode": "OUT_OF_RANGE"
                    },
                    "tolerance": 1e-9
                }
            ]
        },
        "lastReviewed": "2026-10-21"
    },
    {
        "toolId": "depreciation-plan",
        "slug": "amortisman-hesaplama",
        "path": "/ticari-muhasebe/amortisman-hesaplama",
        "category": {
            "id": "ticari-muhasebe",
            "label": "Ticari ve Muhasebe"
        },
        "classification": "timeless",
        "engineId": "depreciation-plan-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-08-24",
        "effectiveTo": null,
        "minAppVersion": "0.3.0",
        "updateCadence": {
            "mode": "method-change-only",
            "days": null,
            "reviewAfter": null,
            "specialMonths": [],
            "note": "Saf matematik çekirdeği yöntem değişene kadar çalışır; varsa dönemsel alt politika yalnız ilgili modu kapatır."
        },
        "sources": [
            {
                "sourceId": "tr-vuk-depreciation",
                "sourceGroupId": null,
                "title": "Amortismana Tabi İktisadi Kıymetler Tablosu",
                "publisher": "Gelir İdaresi Başkanlığı",
                "url": "https://cdn.gib.gov.tr/api/gibportal-file/file/getFileResources?objectKey=arsiv%2Fyardim-kaynaklar%2Fyararli-bilgiler%2FAmortismanOranlariTablosu.pdf",
                "kind": "official-government",
                "reviewedOn": "2026-08-24",
                "watchFields": [
                    "Faydalı ömür ve normal amortisman oranı tablosu"
                ]
            },
            {
                "sourceId": "tr-vuk-depreciation-methods",
                "sourceGroupId": null,
                "title": "213 sayılı Vergi Usul Kanunu — Mükerrer 315 Azalan Bakiyeler Usulüyle Amortisman",
                "publisher": "Gelir İdaresi Başkanlığı",
                "url": "https://www.gib.gov.tr/mevzuat/kanun/434",
                "kind": "official-government",
                "reviewedOn": "2026-08-24",
                "watchFields": [
                    "Azalan bakiyeler yönteminin uygunluk şartları",
                    "Normal oran çarpanı ve azami oran"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "TRY",
                    "minimum": 1,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 4,
                    "options": [],
                    "visibleWhen": null,
                    "id": "cost",
                    "type": "number",
                    "label": "Maliyet bedeli",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "yıl",
                    "minimum": 1,
                    "maximum": 100,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "usefulLifeYears",
                    "type": "number",
                    "label": "Faydalı ömür",
                    "required": true
                },
                {
                    "defaultValue": "straight-line",
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "straight-line",
                            "label": "Normal amortisman"
                        },
                        {
                            "value": "declining-balance",
                            "label": "Azalan bakiyeler"
                        }
                    ],
                    "visibleWhen": null,
                    "id": "method",
                    "type": "radio",
                    "label": "Yöntem",
                    "required": true
                },
                {
                    "defaultValue": false,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [],
                    "visibleWhen": {
                        "fieldId": "method",
                        "equals": "declining-balance"
                    },
                    "id": "decliningBalanceEligibilityConfirmed",
                    "type": "boolean",
                    "label": "Azalan bakiyeler uygunluğunu doğruladım",
                    "required": false
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "cost",
                    "type": "money",
                    "label": "Maliyet",
                    "unit": "TRY",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "usefulLifeYears",
                    "type": "number",
                    "label": "Faydalı ömür",
                    "unit": "yıl",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "method",
                    "type": "text",
                    "label": "Yöntem",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "depreciationRatePercent",
                    "type": "percentage",
                    "label": "Amortisman oranı",
                    "unit": "%",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "firstYearExpense",
                    "type": "money",
                    "label": "İlk yıl gideri",
                    "unit": "TRY",
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "totalDepreciation",
                    "type": "money",
                    "label": "Toplam amortisman",
                    "unit": "TRY",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "schedule",
                    "type": "object-list",
                    "label": "Amortisman planı",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                }
            ]
        },
        "content": {
            "title": "VUK Amortisman Planı Ön Hesabı",
            "shortTitle": "Amortisman Planı",
            "summary": "Kullanıcının resmî tablodan seçtiği faydalı ömürle normal veya uygunluğu doğrulanmış azalan bakiyeler ön planı üretir.",
            "method": "Normal yöntemde maliyet ömre eşit dağılır. Azalan bakiyelerde sürümlü çarpan ve tavan uygulanır; son yıl kalan defter değeri sıfırlanır.",
            "limitations": [
                "Faydalı ömür genel bir varlık sınıfından tahmin edilmez; ilgili iktisadi kıymet satırı GİB tablosundan bulunup kullanıcı tarafından girilmelidir. Azalan bakiyeler için bilanço esası, kıymet ve daha önce seçilen yöntem uygunluğu ayrıca doğrulanmalıdır."
            ],
            "faqs": [
                {
                    "question": "Amortisman Planı kesin resmî veya banka sonucu verir mi?",
                    "answer": "Hayır. Araç yalnız gösterilen formülü, kullanıcı girdilerini ve yürürlük kaydı açık politika alanlarını uygular. Sözleşme, banka planı veya mükellef özelindeki kesin sonuç ilgili kurum ve uzman kaydıyla doğrulanmalıdır."
                }
            ],
            "sourcePanel": {
                "title": "Kaynak ve politika kapsamı",
                "summary": "Kullanıcı girdili temel matematik zamansızdır. Yalnız dönemsel politika kullanan mod, politika sona erdiğinde veya inceleme tarihi geldiğinde yeni insan onayı olmadan kapanır.",
                "sourceIds": [
                    "tr-vuk-depreciation",
                    "tr-vuk-depreciation-methods"
                ],
                "notes": [
                    "Oran, eşik ve hukuki uygunluk matematik çekirdeğine gömülü değildir."
                ]
            }
        },
        "config": {
            "kind": "depreciation-plan-v1",
            "policyLifecycle": {
                "configVersion": "1.0.0",
                "sourceIds": [
                    "tr-vuk-depreciation"
                ],
                "reviewedOn": "2026-08-24",
                "effectiveFrom": "2026-08-24",
                "effectiveTo": null,
                "reviewAfter": null
            },
            "cost": [
                1,
                1000000000
            ],
            "policy": {
                "decliningBalanceMultiplier": 2,
                "maximumDecliningRate": 0.5,
                "policyLifecycle": {
                    "configVersion": "1.0.0",
                    "sourceIds": [
                        "tr-vuk-depreciation-methods"
                    ],
                    "reviewedOn": "2026-08-24",
                    "effectiveFrom": "2026-08-24",
                    "effectiveTo": "2026-12-31",
                    "reviewAfter": "2027-01-01"
                }
            }
        },
        "testVectors": {
            "kind": "finance-business-oracle-v1",
            "cases": [
                {
                    "caseId": "straight-line-five-years",
                    "scenario": "positive",
                    "input": {
                        "cost": 100000,
                        "usefulLifeYears": 5,
                        "method": "straight-line"
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "cost": 100000,
                            "usefulLifeYears": 5,
                            "method": "straight-line",
                            "depreciationRatePercent": 20,
                            "firstYearExpense": 20000,
                            "totalDepreciation": 100000,
                            "schedule": [
                                {
                                    "year": 1,
                                    "expense": 20000,
                                    "cumulativeDepreciation": 20000,
                                    "netBookValue": 80000
                                },
                                {
                                    "year": 2,
                                    "expense": 20000,
                                    "cumulativeDepreciation": 40000,
                                    "netBookValue": 60000
                                },
                                {
                                    "year": 3,
                                    "expense": 20000,
                                    "cumulativeDepreciation": 60000,
                                    "netBookValue": 40000
                                },
                                {
                                    "year": 4,
                                    "expense": 20000,
                                    "cumulativeDepreciation": 80000,
                                    "netBookValue": 20000
                                },
                                {
                                    "year": 5,
                                    "expense": 20000,
                                    "cumulativeDepreciation": 100000,
                                    "netBookValue": 0
                                }
                            ]
                        }
                    },
                    "tolerance": 1e-9
                },
                {
                    "caseId": "declining-without-confirmation",
                    "scenario": "adversarial",
                    "input": {
                        "cost": 100000,
                        "usefulLifeYears": 5,
                        "method": "declining-balance"
                    },
                    "expected": {
                        "ok": false,
                        "field": "decliningBalanceEligibilityConfirmed",
                        "errorCode": "REQUIRED"
                    },
                    "tolerance": 1e-9
                },
                {
                    "caseId": "declining-with-confirmation",
                    "scenario": "positive",
                    "input": {
                        "cost": 100000,
                        "usefulLifeYears": 5,
                        "method": "declining-balance",
                        "decliningBalanceEligibilityConfirmed": true
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "cost": 100000,
                            "usefulLifeYears": 5,
                            "method": "declining-balance",
                            "depreciationRatePercent": 40,
                            "firstYearExpense": 40000,
                            "totalDepreciation": 100000
                        }
                    },
                    "tolerance": 1e-9
                }
            ]
        },
        "lastReviewed": "2026-08-24"
    },
    {
        "toolId": "discount",
        "slug": "indirim-iskonto-hesaplama",
        "path": "/ticari-muhasebe/indirim-iskonto-hesaplama",
        "category": {
            "id": "ticari-muhasebe",
            "label": "Ticari ve Muhasebe"
        },
        "classification": "timeless",
        "engineId": "discount-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-08-24",
        "effectiveTo": null,
        "minAppVersion": "0.3.0",
        "updateCadence": {
            "mode": "method-change-only",
            "days": null,
            "reviewAfter": null,
            "specialMonths": [],
            "note": "Saf matematik çekirdeği yöntem değişene kadar çalışır; varsa dönemsel alt politika yalnız ilgili modu kapatır."
        },
        "sources": [
            {
                "sourceId": "tr-discount-reference-price",
                "sourceGroupId": null,
                "title": "İndirimli Satışlarda Önceki Fiyat ve Tüketicinin Korunması",
                "publisher": "Ticaret Bakanlığı",
                "url": "https://ticaret.gov.tr/haberler/ticaret-bakanligindan-indirimli-satis-reklamlarinda-seffafligi-artiracak-yeni-duzenleme",
                "kind": "official-government",
                "reviewedOn": "2026-08-24",
                "watchFields": [
                    "İndirimli satışlarda referans fiyat açıklaması"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": "price-from-rate",
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "price-from-rate",
                            "label": "Orandan indirimli fiyat"
                        },
                        {
                            "value": "rate-from-price",
                            "label": "Fiyattan indirim oranı"
                        }
                    ],
                    "visibleWhen": null,
                    "id": "mode",
                    "type": "radio",
                    "label": "Hesap türü",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "TRY",
                    "minimum": 1,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 4,
                    "options": [],
                    "visibleWhen": null,
                    "id": "originalPrice",
                    "type": "number",
                    "label": "İlk fiyat",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "%",
                    "minimum": 0,
                    "maximum": 100,
                    "maxDecimalPlaces": 4,
                    "options": [],
                    "visibleWhen": {
                        "fieldId": "mode",
                        "equals": "price-from-rate"
                    },
                    "id": "discountPercent",
                    "type": "number",
                    "label": "İndirim oranı",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "TRY",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 4,
                    "options": [],
                    "visibleWhen": {
                        "fieldId": "mode",
                        "equals": "rate-from-price"
                    },
                    "id": "discountedPrice",
                    "type": "number",
                    "label": "İndirimli fiyat",
                    "required": true
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "mode",
                    "type": "text",
                    "label": "Hesap türü",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "originalPrice",
                    "type": "money",
                    "label": "İlk fiyat",
                    "unit": "TRY",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "discountPercent",
                    "type": "percentage",
                    "label": "İndirim oranı",
                    "unit": "%",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "discountAmount",
                    "type": "money",
                    "label": "İndirim tutarı",
                    "unit": "TRY",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "discountedPrice",
                    "type": "money",
                    "label": "İndirimli fiyat",
                    "unit": "TRY",
                    "nullable": false,
                    "prominent": true
                }
            ]
        },
        "content": {
            "title": "İndirim ve İskonto Hesaplama",
            "shortTitle": "İndirim ve İskonto",
            "summary": "İlk fiyat ile indirim oranından yeni fiyatı veya ilk ve indirimli fiyattan uygulanan oranı hesaplar.",
            "method": "İndirim tutarı ilk fiyat × oran; ters modda oran (ilk fiyat−indirimli fiyat)/ilk fiyat olarak bulunur.",
            "limitations": [
                "Araç yalnız aritmetik yapar; kampanya referans fiyatının güncel mevzuata uygunluğunu veya ilan geçmişindeki zorunlu referans fiyatı doğrulamaz."
            ],
            "faqs": [
                {
                    "question": "İndirim ve İskonto kesin resmî veya banka sonucu verir mi?",
                    "answer": "Hayır. Araç yalnız gösterilen formülü, kullanıcı girdilerini ve yürürlük kaydı açık politika alanlarını uygular. Sözleşme, banka planı veya mükellef özelindeki kesin sonuç ilgili kurum ve uzman kaydıyla doğrulanmalıdır."
                }
            ],
            "sourcePanel": {
                "title": "Kaynak ve politika kapsamı",
                "summary": "Hesap yöntemi kaynak kimlikleriyle sürümlenir. Kullanıcı girdili zamansız matematik bir politika son kullanma veya inceleme tarihine bağlı olarak kapanmaz.",
                "sourceIds": [
                    "tr-discount-reference-price"
                ],
                "notes": [
                    "Oran, eşik ve hukuki uygunluk matematik çekirdeğine gömülü değildir."
                ]
            }
        },
        "config": {
            "kind": "discount-v1",
            "policyLifecycle": {
                "configVersion": "1.0.0",
                "sourceIds": [
                    "tr-discount-reference-price"
                ],
                "reviewedOn": "2026-08-24",
                "effectiveFrom": "2026-08-24",
                "effectiveTo": null,
                "reviewAfter": null
            },
            "price": [
                1,
                1000000000
            ],
            "discountPercent": [
                0,
                100
            ]
        },
        "testVectors": {
            "kind": "finance-business-oracle-v1",
            "cases": [
                {
                    "caseId": "price-from-twenty-percent",
                    "scenario": "positive",
                    "input": {
                        "mode": "price-from-rate",
                        "originalPrice": 1000,
                        "discountPercent": 20
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "mode": "price-from-rate",
                            "originalPrice": 1000,
                            "discountPercent": 20,
                            "discountAmount": 200,
                            "discountedPrice": 800
                        }
                    },
                    "tolerance": 1e-9
                },
                {
                    "caseId": "rate-from-price",
                    "scenario": "positive",
                    "input": {
                        "mode": "rate-from-price",
                        "originalPrice": 1000,
                        "discountedPrice": 750
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "mode": "rate-from-price",
                            "originalPrice": 1000,
                            "discountPercent": 25,
                            "discountAmount": 250,
                            "discountedPrice": 750
                        }
                    },
                    "tolerance": 1e-9
                },
                {
                    "caseId": "zero-percent-equal-price",
                    "scenario": "boundary",
                    "input": {
                        "mode": "rate-from-price",
                        "originalPrice": 1000,
                        "discountedPrice": 1000
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "mode": "rate-from-price",
                            "originalPrice": 1000,
                            "discountPercent": 0,
                            "discountAmount": 0,
                            "discountedPrice": 1000
                        }
                    },
                    "tolerance": 1e-9
                },
                {
                    "caseId": "hundred-percent-zero-price",
                    "scenario": "boundary",
                    "input": {
                        "mode": "price-from-rate",
                        "originalPrice": 1000,
                        "discountPercent": 100
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "mode": "price-from-rate",
                            "originalPrice": 1000,
                            "discountPercent": 100,
                            "discountAmount": 1000,
                            "discountedPrice": 0
                        }
                    },
                    "tolerance": 1e-9
                },
                {
                    "caseId": "discounted-price-above-original",
                    "scenario": "adversarial",
                    "input": {
                        "mode": "rate-from-price",
                        "originalPrice": 1000,
                        "discountedPrice": 1001
                    },
                    "expected": {
                        "ok": false,
                        "field": "discountedPrice",
                        "errorCode": "OUT_OF_RANGE"
                    },
                    "tolerance": 1e-9
                }
            ]
        },
        "lastReviewed": "2026-08-24"
    },
    {
        "toolId": "rent-increase",
        "slug": "kira-artis-orani-hesaplama",
        "path": "/ticari-muhasebe/kira-artis-orani-hesaplama",
        "category": {
            "id": "ticari-muhasebe",
            "label": "Ticari ve Muhasebe"
        },
        "classification": "timeless",
        "engineId": "rent-increase-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-08-24",
        "effectiveTo": null,
        "minAppVersion": "0.3.0",
        "updateCadence": {
            "mode": "method-change-only",
            "days": null,
            "reviewAfter": null,
            "specialMonths": [],
            "note": "Saf matematik çekirdeği yöntem değişene kadar çalışır; varsa dönemsel alt politika yalnız ilgili modu kapatır."
        },
        "sources": [
            {
                "sourceId": "tr-rent-increase-tufe",
                "sourceGroupId": null,
                "title": "6098 sayılı Türk Borçlar Kanunu",
                "publisher": "T.C. Mevzuat Bilgi Sistemi",
                "url": "https://www.mevzuat.gov.tr/mevzuatmetin/1.5.6098.pdf",
                "kind": "official-government",
                "reviewedOn": "2026-08-24",
                "watchFields": [
                    "Konut ve çatılı işyeri kira artışında on iki aylık TÜFE ortalaması"
                ]
            },
            {
                "sourceId": "tr-tufe-monthly-manual",
                "sourceGroupId": null,
                "title": "Enflasyon ve Fiyat İstatistikleri",
                "publisher": "Türkiye İstatistik Kurumu",
                "url": "https://veriportali.tuik.gov.tr/Bulten/Index?dil=1&p=Tuketici-Fiyat-Endeksi-Temmuz-2026-58297",
                "kind": "official-government",
                "reviewedOn": "2026-08-24",
                "watchFields": [
                    "TÜFE aylık verileri ve on iki aylık ortalamalar"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "TRY",
                    "minimum": 1,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 4,
                    "options": [],
                    "visibleWhen": null,
                    "id": "currentMonthlyRent",
                    "type": "number",
                    "label": "Mevcut aylık kira",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "%",
                    "minimum": 0,
                    "maximum": 999,
                    "maxDecimalPlaces": 4,
                    "options": [],
                    "visibleWhen": null,
                    "id": "twelveMonthCpiAveragePercent",
                    "type": "number",
                    "label": "On iki aylık TÜFE ortalaması",
                    "required": true
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "currentMonthlyRent",
                    "type": "money",
                    "label": "Mevcut kira",
                    "unit": "TRY",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "twelveMonthCpiAveragePercent",
                    "type": "percentage",
                    "label": "Girilen on iki aylık TÜFE ortalaması",
                    "unit": "%",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "monthlyIncrease",
                    "type": "money",
                    "label": "Aylık artış",
                    "unit": "TRY",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "newMonthlyRent",
                    "type": "money",
                    "label": "Yeni aylık kira",
                    "unit": "TRY",
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "annualExtraCost",
                    "type": "money",
                    "label": "Yıllık ek kira",
                    "unit": "TRY",
                    "nullable": false,
                    "prominent": false
                }
            ]
        },
        "content": {
            "title": "TÜFE On İki Aylık Ortalamasına Göre Kira Artışı",
            "shortTitle": "Kira Artışı",
            "summary": "Mevcut kira ile kullanıcının resmî kaynaktan girdiği on iki aylık TÜFE ortalamasını çarparak üst sınır senaryosunu gösterir.",
            "method": "Aylık artış mevcut kira × oran; yeni kira toplamı ve on iki aylık fark ayrıca hesaplanır.",
            "limitations": [
                "TÜFE oranı otomatik alınmaz. Sözleşme türü, yenileme tarihi, beş yıl sonrası hakkaniyet veya mahkeme kararı değerlendirilmez; girilen oran kendiliğinden her sözleşmenin hukuki tavanı sayılmaz."
            ],
            "faqs": [
                {
                    "question": "Kira Artışı kesin resmî veya banka sonucu verir mi?",
                    "answer": "Hayır. Araç yalnız gösterilen formülü, kullanıcı girdilerini ve yürürlük kaydı açık politika alanlarını uygular. Sözleşme, banka planı veya mükellef özelindeki kesin sonuç ilgili kurum ve uzman kaydıyla doğrulanmalıdır."
                }
            ],
            "sourcePanel": {
                "title": "Kaynak ve politika kapsamı",
                "summary": "Hesap yöntemi kaynak kimlikleriyle sürümlenir. Kullanıcı girdili zamansız matematik bir politika son kullanma veya inceleme tarihine bağlı olarak kapanmaz.",
                "sourceIds": [
                    "tr-rent-increase-tufe",
                    "tr-tufe-monthly-manual"
                ],
                "notes": [
                    "Oran, eşik ve hukuki uygunluk matematik çekirdeğine gömülü değildir."
                ]
            }
        },
        "config": {
            "kind": "rent-increase-v1",
            "policyLifecycle": {
                "configVersion": "1.0.0",
                "sourceIds": [
                    "tr-rent-increase-tufe",
                    "tr-tufe-monthly-manual"
                ],
                "reviewedOn": "2026-08-24",
                "effectiveFrom": "2026-08-24",
                "effectiveTo": null,
                "reviewAfter": null
            },
            "currentMonthlyRent": [
                1,
                1000000000
            ],
            "twelveMonthCpiAveragePercent": [
                0,
                999
            ],
            "policy": {
                "monthsPerYear": 12
            }
        },
        "testVectors": {
            "kind": "finance-business-oracle-v1",
            "cases": [
                {
                    "caseId": "cpi-average-example",
                    "scenario": "positive",
                    "input": {
                        "currentMonthlyRent": 20000,
                        "twelveMonthCpiAveragePercent": 65
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "currentMonthlyRent": 20000,
                            "twelveMonthCpiAveragePercent": 65,
                            "monthlyIncrease": 13000,
                            "newMonthlyRent": 33000,
                            "annualExtraCost": 156000
                        }
                    },
                    "tolerance": 1e-9
                },
                {
                    "caseId": "negative-cpi",
                    "scenario": "adversarial",
                    "input": {
                        "currentMonthlyRent": 20000,
                        "twelveMonthCpiAveragePercent": -1
                    },
                    "expected": {
                        "ok": false,
                        "field": "twelveMonthCpiAveragePercent",
                        "errorCode": "OUT_OF_RANGE"
                    },
                    "tolerance": 1e-9
                }
            ]
        },
        "lastReviewed": "2026-08-24"
    },
    {
        "toolId": "pos-commission",
        "slug": "pos-komisyonu-hesaplama",
        "path": "/ticari-muhasebe/pos-komisyonu-hesaplama",
        "category": {
            "id": "ticari-muhasebe",
            "label": "Ticari ve Muhasebe"
        },
        "classification": "timeless",
        "engineId": "pos-commission-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.2",
        "effectiveFrom": "2026-10-21",
        "effectiveTo": null,
        "minAppVersion": "0.3.0",
        "updateCadence": {
            "mode": "method-change-only",
            "days": null,
            "reviewAfter": null,
            "specialMonths": [],
            "note": "Kaynak inceleme tarihi yönetici uyarısı üretir; doğrulanmış vergi politikası geçerliliği sona ererse yalnız BSMV modu kapanır."
        },
        "sources": [
            {
                "sourceId": "tr-pos-commission",
                "sourceGroupId": null,
                "title": "Üye İşyerlerine Uygulanacak Azami Komisyon Oranları",
                "publisher": "Türkiye Cumhuriyet Merkez Bankası",
                "url": "https://www.tcmb.gov.tr/wps/wcm/connect/tr/tcmb%2Btr/main%2Bmenu/istatistikler/bankacilik%2Bverileri/uye%2Bisyerlerine%2Buygulanacak%2Bazami%2Bkomisyon%2Boranlari",
                "kind": "official-government",
                "reviewedOn": "2026-10-21",
                "watchFields": [
                    "Üye işyeri komisyonu ve ilgili açıklamalar"
                ]
            },
            {
                "sourceId": "tr-pos-merchant-commission-bsmv-scope",
                "sourceGroupId": null,
                "title": "Bankaya ödenen kredi kartı komisyonlarının kısmi olarak bayilere yansıtılmasında KDV uygulanıp uygulanmayacağı — Özelge 64597866-130[1-2013]-106",
                "publisher": "Gelir İdaresi Başkanlığı / Büyük Mükellefler Vergi Dairesi Başkanlığı",
                "url": "https://gib.gov.tr/mevzuat/kanun/436/ozelge/29405",
                "kind": "official-government",
                "reviewedOn": "2026-10-21",
                "watchFields": [
                    "Bankanın üye işyerinden aldığı POS komisyonunun BSMV kapsamı"
                ]
            },
            {
                "sourceId": "tr-general-bsmv-rate",
                "sourceGroupId": null,
                "title": "28/8/1998 tarihli ve 98/11591 sayılı Kararnamenin Eki Karar",
                "publisher": "Gelir İdaresi Başkanlığı",
                "url": "https://gib.gov.tr/mevzuat/kanun/445/bkk/1849",
                "kind": "official-government",
                "reviewedOn": "2026-10-21",
                "watchFields": [
                    "Diğer banka ve sigorta muamelelerinde lehe alınan paralar için BSMV oranı"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "TRY",
                    "minimum": 1,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 4,
                    "options": [],
                    "visibleWhen": null,
                    "id": "amount",
                    "type": "number",
                    "label": "İşlem tutarı",
                    "required": true
                },
                {
                    "defaultValue": 1,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": 1,
                            "label": "1 taksit"
                        },
                        {
                            "value": 2,
                            "label": "2 taksit"
                        },
                        {
                            "value": 3,
                            "label": "3 taksit"
                        },
                        {
                            "value": 6,
                            "label": "6 taksit"
                        },
                        {
                            "value": 9,
                            "label": "9 taksit"
                        },
                        {
                            "value": 12,
                            "label": "12 taksit"
                        }
                    ],
                    "visibleWhen": null,
                    "id": "installmentCount",
                    "type": "select",
                    "label": "Taksit adedi",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "%",
                    "minimum": 0,
                    "maximum": 100,
                    "maxDecimalPlaces": 4,
                    "options": [],
                    "visibleWhen": null,
                    "id": "commissionPercent",
                    "type": "number",
                    "label": "Komisyon oranı",
                    "required": true
                },
                {
                    "defaultValue": "commission-only",
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "commission-only",
                            "label": "Yalnız komisyon"
                        },
                        {
                            "value": "commission-plus-bsmv",
                            "label": "Komisyona BSMV ekle"
                        }
                    ],
                    "visibleWhen": null,
                    "id": "mode",
                    "type": "radio",
                    "label": "BSMV hesabı",
                    "required": true
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "amount",
                    "type": "money",
                    "label": "İşlem tutarı",
                    "unit": "TRY",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "installmentCount",
                    "type": "number",
                    "label": "Taksit adedi",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "commissionPercent",
                    "type": "percentage",
                    "label": "Komisyon oranı",
                    "unit": "%",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "mode",
                    "type": "text",
                    "label": "BSMV hesabı",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "commissionAmount",
                    "type": "money",
                    "label": "Komisyon",
                    "unit": "TRY",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "bsmvAmount",
                    "type": "money",
                    "label": "Komisyona eklenen BSMV",
                    "unit": "TRY",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "totalDeduction",
                    "type": "money",
                    "label": "Toplam kesinti",
                    "unit": "TRY",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "netAmount",
                    "type": "money",
                    "label": "Net tutar",
                    "unit": "TRY",
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "netAllocationPerInstallment",
                    "type": "money",
                    "label": "Net tutarın taksit adedine matematiksel dağılımı",
                    "unit": "TRY",
                    "nullable": false,
                    "prominent": false
                }
            ]
        },
        "content": {
            "title": "POS Komisyonu ve Net Tutar Hesaplama",
            "shortTitle": "POS Komisyonu",
            "summary": "İşlem tutarı ile kullanıcının girdiği komisyon oranından kesinti ve net tutarı; seçilirse komisyona eklenen BSMV'yi ayrı gösterir.",
            "method": "Komisyon tutar üzerinden, BSMV yalnız komisyon üzerinden hesaplanır. Net tutarın taksit adedine bölümü yalnız matematiksel dağılımdır.",
            "limitations": [
                "BSMV modu yalnız bankanın veya ödeme sağlayıcısının ilgili komisyonu bu vergi rejiminde uyguladığı doğrulanan senaryo içindir. Araç banka anlaşmasındaki hesaba geçiş süresini, bloke süresini, erken ödemeyi, taksite göre değişen oranı veya ödeme aktarım takvimini hesaplamaz. Taksit adedi komisyon oranını otomatik değiştirmez."
            ],
            "faqs": [
                {
                    "question": "POS Komisyonu kesin resmî veya banka sonucu verir mi?",
                    "answer": "Hayır. Araç yalnız gösterilen formülü, kullanıcı girdilerini ve yürürlük kaydı açık politika alanlarını uygular. Sözleşme, banka planı veya mükellef özelindeki kesin sonuç ilgili kurum ve uzman kaydıyla doğrulanmalıdır."
                }
            ],
            "sourcePanel": {
                "title": "Kaynak ve politika kapsamı",
                "summary": "Kullanıcı girdili temel matematik zamansızdır. Yalnız dönemsel politika kullanan mod, politika sona erdiğinde veya inceleme tarihi geldiğinde yeni insan onayı olmadan kapanır.",
                "sourceIds": [
                    "tr-pos-commission",
                    "tr-pos-merchant-commission-bsmv-scope",
                    "tr-general-bsmv-rate"
                ],
                "notes": [
                    "Oran, eşik ve hukuki uygunluk matematik çekirdeğine gömülü değildir."
                ]
            }
        },
        "config": {
            "kind": "pos-commission-v1",
            "policyLifecycle": {
                "configVersion": "1.0.2",
                "sourceIds": [
                    "tr-pos-commission"
                ],
                "reviewedOn": "2026-10-21",
                "effectiveFrom": "2026-10-21",
                "effectiveTo": null,
                "reviewAfter": null
            },
            "amount": [
                1,
                1000000000
            ],
            "commissionPercent": [
                0,
                100
            ],
            "supportedInstallments": [
                1,
                2,
                3,
                6,
                9,
                12
            ],
            "policy": {
                "bsmvRate": 0.05,
                "policyLifecycle": {
                    "configVersion": "1.0.2",
                    "sourceIds": [
                        "tr-pos-merchant-commission-bsmv-scope",
                        "tr-general-bsmv-rate"
                    ],
                    "reviewedOn": "2026-10-21",
                    "effectiveFrom": "2026-10-21",
                    "effectiveTo": null,
                    "reviewAfter": "2026-11-04"
                }
            }
        },
        "testVectors": {
            "kind": "finance-business-oracle-v1",
            "cases": [
                {
                    "caseId": "commission-plus-tax",
                    "scenario": "positive",
                    "input": {
                        "amount": 10000,
                        "installmentCount": 3,
                        "commissionPercent": 3.5,
                        "mode": "commission-plus-bsmv"
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "amount": 10000,
                            "installmentCount": 3,
                            "commissionPercent": 3.5,
                            "mode": "commission-plus-bsmv",
                            "commissionAmount": 350,
                            "bsmvAmount": 17.5,
                            "totalDeduction": 367.5,
                            "netAmount": 9632.5,
                            "netAllocationPerInstallment": 3210.8333333333335
                        }
                    },
                    "tolerance": 1e-9
                },
                {
                    "caseId": "plus-bsmv-net-zero-boundary",
                    "scenario": "boundary",
                    "input": {
                        "amount": 10000,
                        "installmentCount": 1,
                        "commissionPercent": 95.23809523809524,
                        "mode": "commission-plus-bsmv"
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "netAmount": 0
                        }
                    },
                    "tolerance": 1e-9
                },
                {
                    "caseId": "plus-bsmv-negative-net",
                    "scenario": "adversarial",
                    "input": {
                        "amount": 10000,
                        "installmentCount": 1,
                        "commissionPercent": 95.23809523909524,
                        "mode": "commission-plus-bsmv"
                    },
                    "expected": {
                        "ok": false,
                        "field": "commissionPercent",
                        "errorCode": "OUT_OF_RANGE"
                    },
                    "tolerance": 1e-9
                },
                {
                    "caseId": "commission-only-hundred-percent",
                    "scenario": "boundary",
                    "input": {
                        "amount": 10000,
                        "installmentCount": 1,
                        "commissionPercent": 100,
                        "mode": "commission-only"
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "commissionAmount": 10000,
                            "bsmvAmount": 0,
                            "totalDeduction": 10000,
                            "netAmount": 0,
                            "netAllocationPerInstallment": 0
                        }
                    },
                    "tolerance": 1e-9
                },
                {
                    "caseId": "unsupported-installment",
                    "scenario": "adversarial",
                    "input": {
                        "amount": 10000,
                        "installmentCount": 4,
                        "commissionPercent": 3.5,
                        "mode": "commission-only"
                    },
                    "expected": {
                        "ok": false,
                        "field": "installmentCount",
                        "errorCode": "UNSUPPORTED_VALUE"
                    },
                    "tolerance": 1e-9
                }
            ]
        },
        "lastReviewed": "2026-10-21"
    },
    {
        "toolId": "vehicle-purchase-cost",
        "slug": "arac-alim-maliyeti-hesaplama",
        "path": "/arac-emlak-yasam/arac-alim-maliyeti-hesaplama",
        "category": {
            "id": "arac-emlak-yasam",
            "label": "Araç, Emlak ve Yaşam"
        },
        "classification": "remote-config",
        "engineId": "vehicle-purchase-cost-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.1.0",
        "configVersion": "1.1.0",
        "effectiveFrom": "2026-07-31",
        "effectiveTo": "2026-12-31",
        "minAppVersion": "0.4.1",
        "updateCadence": {
            "mode": "scheduled-source-review",
            "days": 7,
            "reviewAfter": "2026-09-02",
            "specialMonths": [],
            "note": "Belirlenen tarihte resmî kaynak incelenir; değişiklik varsa config, yoksa inceleme tarihi yenilenir."
        },
        "sources": [
            {
                "sourceId": "gib-vehicle-excise-list-current",
                "sourceGroupId": "tr_vehicle_purchase_taxes",
                "title": "Motorlu taşıtlarda güncel ÖTV oranları",
                "publisher": "Gelir İdaresi Başkanlığı",
                "url": "https://gib.gov.tr/yardim-kaynaklar/yararli-bilgiler/ozel-tuketim-vergisi-oranlari-2-sayili-liste",
                "kind": "official-government",
                "reviewedOn": "2026-08-26",
                "watchFields": [
                    "ÖTV matrah eşikleri",
                    "yakıt ve motor türü",
                    "ÖTV oranı"
                ]
            },
            {
                "sourceId": "tbmm-law-7590-vehicle-minimum-excise-2026",
                "sourceGroupId": "tr_vehicle_purchase_taxes",
                "title": "7590 sayılı Kanun ile 87.03 araçlarda asgari ÖTV",
                "publisher": "Türkiye Büyük Millet Meclisi",
                "url": "https://cdn.tbmm.gov.tr/KKBSPublicFile/D28/Y4/KanunMetni/152a9101-15fe-4138-aceb-99a304bb9ec3.htm",
                "kind": "official-government",
                "reviewedOn": "2026-08-26",
                "watchFields": [
                    "87.03",
                    "asgari maktu vergi",
                    "araç sınıfı",
                    "yürürlük"
                ]
            },
            {
                "sourceId": "gib-vehicle-transfer-fee-2026",
                "sourceGroupId": "tr_vehicle_purchase_taxes",
                "title": "7566 sayılı Kanun ile taşıt ilk tescil ve devir harcı",
                "publisher": "Gelir İdaresi Başkanlığı",
                "url": "https://cdn.gib.gov.tr/api/gibportal-file/file/getFile?objectKey=MEVZUAT_MADDE%2FUNIVERSAL%2F2025%2F7566.pdf",
                "kind": "official-government",
                "reviewedOn": "2026-08-26",
                "watchFields": [
                    "ilk tescil",
                    "binde iki",
                    "asgari tutar",
                    "yürürlük"
                ]
            },
            {
                "sourceId": "gib-registration-fee-tariff-2026",
                "sourceGroupId": "tr_vehicle_purchase_taxes",
                "title": "2026 Harçlar Kanunu Genel Tebliği taşıt tarifesi",
                "publisher": "Gelir İdaresi Başkanlığı",
                "url": "https://cdn.gib.gov.tr/api/gibportal-file/file/getFile?objectKey=MEVZUAT_TEBLIGLER%2FUNIVERSAL%2F2025%2F492_Teblig98.pdf",
                "kind": "official-government",
                "reviewedOn": "2026-08-26",
                "watchFields": [
                    "(2) sayılı tarife",
                    "ilk tescil",
                    "binde iki",
                    "1.000 TL"
                ]
            },
            {
                "sourceId": "gib-vat-rates-current",
                "sourceGroupId": "tr_vat_rates",
                "title": "Güncel KDV oranları",
                "publisher": "Gelir İdaresi Başkanlığı",
                "url": "https://cdn.gib.gov.tr/api/gibportal-file/file/getFileResources?objectKey=arsiv%2Fyardim-kaynaklar%2Fyararli-bilgiler%2Fkdv-oranlari.pdf",
                "kind": "official-government",
                "reviewedOn": "2026-08-26",
                "watchFields": [
                    "KDV oranları",
                    "motorlu taşıtlar"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "benzin",
                            "label": "Benzinli / Dizel"
                        },
                        {
                            "value": "hev",
                            "label": "Hibrit (şarj edilemeyen)"
                        },
                        {
                            "value": "phev",
                            "label": "Şarj edilebilir hibrit"
                        },
                        {
                            "value": "elektrik",
                            "label": "Elektrikli"
                        }
                    ],
                    "visibleWhen": null,
                    "id": "driveType",
                    "type": "select",
                    "label": "Yakıt ve tahrik türü",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "cm³",
                    "minimum": 1,
                    "maximum": 20000,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "engineCc",
                    "type": "number",
                    "label": "Motor hacmi",
                    "required": false
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "kW",
                    "minimum": 1,
                    "maximum": 5000,
                    "maxDecimalPlaces": 2,
                    "options": [],
                    "visibleWhen": null,
                    "id": "powerKw",
                    "type": "number",
                    "label": "Motor gücü",
                    "required": false
                },
                {
                    "defaultValue": false,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [],
                    "visibleWhen": null,
                    "id": "phevQualified",
                    "type": "boolean",
                    "label": "PHEV şartlarını sağlıyor",
                    "required": false
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "TL",
                    "minimum": 1,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 2,
                    "options": [],
                    "visibleWhen": null,
                    "id": "taxBase",
                    "type": "number",
                    "label": "ÖTV matrahı",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "TL",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 2,
                    "options": [],
                    "visibleWhen": null,
                    "id": "trafficInsurance",
                    "type": "number",
                    "label": "Zorunlu trafik sigortası",
                    "required": false
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "TL",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 2,
                    "options": [],
                    "visibleWhen": null,
                    "id": "documentedRegistrationCosts",
                    "type": "number",
                    "label": "Belgeli tescil giderleri",
                    "required": false
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "driveType",
                    "type": "text",
                    "label": "Tahrik türü",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "driveTypeLabel",
                    "type": "text",
                    "label": "Tahrik türü açıklaması",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "taxBase",
                    "type": "money",
                    "label": "ÖTV matrahı",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "exciseRatePercent",
                    "type": "percentage",
                    "label": "ÖTV oranı",
                    "unit": "%",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "exciseTax",
                    "type": "money",
                    "label": "ÖTV",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "vatBase",
                    "type": "money",
                    "label": "KDV matrahı",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "vat",
                    "type": "money",
                    "label": "KDV",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "totalTax",
                    "type": "money",
                    "label": "Toplam vergi",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "turnkeyPrice",
                    "type": "money",
                    "label": "Vergiler dâhil fiyat",
                    "unit": null,
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "taxSharePercent",
                    "type": "percentage",
                    "label": "Vergi payı",
                    "unit": "%",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "rateReason",
                    "type": "text",
                    "label": "Uygulanan tarife açıklaması",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "registrationFee",
                    "type": "money",
                    "label": "Tescil harcı",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "documentedRegistrationCosts",
                    "type": "money",
                    "label": "Belgeli tescil giderleri",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "trafficInsurance",
                    "type": "money",
                    "label": "Trafik sigortası",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "totalPurchaseCost",
                    "type": "money",
                    "label": "Toplam alım maliyeti",
                    "unit": null,
                    "nullable": false,
                    "prominent": true
                }
            ]
        },
        "content": {
            "title": "Araç Alım Maliyeti Hesaplama",
            "shortTitle": "Araç Alım Maliyeti Hesaplama",
            "summary": "Standart 87.03 binek otomobilde tahrik, motor ve matrah bilgisine göre nispi ÖTV ile 100.000 TL asgari ÖTV'nin büyüğünü; ardından KDV, ilk tescil ve belgeli giderleri ayrı hesaplar.",
            "method": "Standart 87.03 binek otomobilde tahrik, motor ve matrah bilgisine göre nispi ÖTV ile 100.000 TL asgari ÖTV'nin büyüğünü; ardından KDV, ilk tescil ve belgeli giderleri ayrı hesaplar.",
            "limitations": [
                "Yalnız standart 87.03 binek otomobilin ilk tescil ön hesabıdır; L ve T sınıfı araçlar, ikinci el devir, istisnalar, MTV ve bayi hizmet bedelleri kapsam dışıdır.",
                "Tescil harcı vergiler dâhil satış bedeli üzerinden ön hesaplanır; fatura ve belgeli işlem bedelleri yetkili kurumdan doğrulanmalıdır."
            ],
            "faqs": [
                {
                    "question": "Sonuç kesin veya resmî bir tutar mıdır?",
                    "answer": "Hayır. Sonuç, gösterilen girdiler ve tarihli kaynak kaydıyla üretilen yardımcı bir hesaplamadır."
                }
            ],
            "sourcePanel": {
                "title": "Kaynak ve inceleme durumu",
                "summary": "Formül ve oranlar belirlenen aralıkta resmî kaynaktan yeniden incelenir. Değişiklik yoksa inceleme tarihi, değişiklik varsa config sürümü yenilenir.",
                "sourceIds": [
                    "gib-vehicle-excise-list-current",
                    "tbmm-law-7590-vehicle-minimum-excise-2026",
                    "gib-vehicle-transfer-fee-2026",
                    "gib-registration-fee-tariff-2026",
                    "gib-vat-rates-current"
                ],
                "notes": [
                    "Yalnız standart 87.03 binek otomobilin ilk tescil ön hesabıdır; L ve T sınıfı araçlar, ikinci el devir, istisnalar, MTV ve bayi hizmet bedelleri kapsam dışıdır.",
                    "Tescil harcı vergiler dâhil satış bedeli üzerinden ön hesaplanır; fatura ve belgeli işlem bedelleri yetkili kurumdan doğrulanmalıdır."
                ]
            }
        },
        "config": {
            "kind": "vehicle-purchase-cost-v1",
            "calculatorId": "arac-emlak-yasam.arac-alim-maliyeti-hesaplama",
            "taxBase": [
                1,
                1000000000
            ],
            "engineCc": [
                1,
                20000
            ],
            "powerKw": [
                1,
                5000
            ],
            "optionalCost": [
                0,
                1000000000
            ],
            "registrationFeeRate": 0.002,
            "minimumRegistrationFee": 1000,
            "policy": {
                "vatRate": 0.2,
                "minimumExciseTax": 100000,
                "iceUpTo1400TaxBaseThresholds": [
                    650000,
                    900000,
                    1100000
                ],
                "iceUpTo1400Rates": [
                    0.7,
                    0.75,
                    0.8,
                    0.9
                ],
                "ice1401To1600TaxBaseThresholds": [
                    850000,
                    1100000,
                    1650000
                ],
                "ice1401To1600Rates": [
                    0.75,
                    0.8,
                    0.9,
                    1
                ],
                "ice1601To2000TaxBaseThreshold": 1650000,
                "ice1601To2000Rates": [
                    1.5,
                    1.7
                ],
                "iceAbove2000Rate": 2.2,
                "hevFirstMinimumPowerKwExclusive": 50,
                "hevFirstEngineCcExclusive": 1600,
                "hevFirstEngineCcMaximum": 1800,
                "hevFirstTaxBaseThreshold": 1250000,
                "hevFirstRates": [
                    0.7,
                    0.8
                ],
                "hevSecondMinimumPowerKwExclusive": 100,
                "hevSecondEngineCcExclusive": 2000,
                "hevSecondEngineCcMaximum": 2500,
                "hevSecondTaxBaseThreshold": 1650000,
                "hevSecondRates": [
                    1.5,
                    1.7
                ],
                "phevEngineCcFirstMaximum": 1600,
                "phevEngineCcSecondMaximum": 1800,
                "phevTaxBaseThreshold": 1350000,
                "phevFirstRates": [
                    0.45,
                    0.75
                ],
                "phevSecondRate": 0.85,
                "electricPowerThresholdKw": 160,
                "electricTaxBaseThreshold": 1650000,
                "electricLowPowerRates": [
                    0.25,
                    0.55
                ],
                "electricHighPowerRates": [
                    0.65,
                    0.75
                ]
            },
            "policyLifecycle": {
                "configVersion": "1.1.0",
                "sourceIds": [
                    "gib-vehicle-excise-list-current",
                    "tbmm-law-7590-vehicle-minimum-excise-2026",
                    "gib-vehicle-transfer-fee-2026",
                    "gib-registration-fee-tariff-2026",
                    "gib-vat-rates-current"
                ],
                "reviewedOn": "2026-08-26",
                "effectiveFrom": "2026-07-31",
                "effectiveTo": "2026-12-31",
                "reviewAfter": "2026-09-02"
            }
        },
        "testVectors": {
            "kind": "vehicle-finance-oracle-v1",
            "cases": [
                {
                    "caseId": "purchase-ice-500k",
                    "scenario": "positive",
                    "input": {
                        "driveType": "benzin",
                        "engineCc": 1400,
                        "taxBase": 500000,
                        "trafficInsurance": 10000
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "exciseRatePercent": 70,
                            "turnkeyPrice": 1020000,
                            "registrationFee": 2040,
                            "totalPurchaseCost": 1032040
                        }
                    },
                    "tolerance": 1e-8
                },
                {
                    "caseId": "purchase-electric-minimum-excise",
                    "scenario": "threshold",
                    "input": {
                        "driveType": "elektrik",
                        "powerKw": 160,
                        "taxBase": 100000
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "exciseRatePercent": 25,
                            "exciseTax": 100000,
                            "vat": 40000,
                            "turnkeyPrice": 240000,
                            "registrationFee": 1000,
                            "totalPurchaseCost": 241000
                        }
                    },
                    "tolerance": 1e-8
                },
                {
                    "caseId": "purchase-missing-tax-base",
                    "scenario": "adversarial",
                    "input": {
                        "driveType": "benzin",
                        "engineCc": 1400
                    },
                    "expected": {
                        "ok": false,
                        "field": "taxBase",
                        "errorCode": "REQUIRED"
                    },
                    "tolerance": 1e-9
                }
            ]
        },
        "lastReviewed": "2026-08-26"
    },
    {
        "toolId": "vehicle-inspection-fee",
        "slug": "arac-muayene-ucreti-hesaplama",
        "path": "/arac-emlak-yasam/arac-muayene-ucreti-hesaplama",
        "category": {
            "id": "arac-emlak-yasam",
            "label": "Araç, Emlak ve Yaşam"
        },
        "classification": "remote-config",
        "engineId": "vehicle-inspection-fee-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-01-01",
        "effectiveTo": "2026-12-31",
        "minAppVersion": "0.4.0",
        "updateCadence": {
            "mode": "scheduled-source-review",
            "days": 129,
            "reviewAfter": "2027-01-01",
            "specialMonths": [],
            "note": "Belirlenen tarihte resmî kaynak incelenir; değişiklik varsa config, yoksa inceleme tarihi yenilenir."
        },
        "sources": [
            {
                "sourceId": "tuvturk-inspection-fees-2026",
                "sourceGroupId": "tr_vehicle_inspection_fees",
                "title": "2026 araç muayene fiyatları",
                "publisher": "TÜVTÜRK",
                "url": "https://www.tuvturk.com.tr/hizmetlerimiz/muayene-hizmetleri/arac-muayene-fiyatlari",
                "kind": "official-operator",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "hafif araç",
                    "ağır araç",
                    "motosiklet",
                    "gecikme"
                ]
            },
            {
                "sourceId": "csidb-emission-measurement-fee-2026",
                "sourceGroupId": "tr_vehicle_inspection_fees",
                "title": "2026 egzoz emisyon ölçüm ücreti",
                "publisher": "Çevre, Şehircilik ve İklim Değişikliği Bakanlığı",
                "url": "https://webdosya.csb.gov.tr/v2/canakkale/2026/03/2026-Birim-Fiyat-Listesi-20260102-20260303154736.pdf",
                "kind": "official-government",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "egzoz emisyon ölçüm ücreti",
                    "2026 ücret dönemi"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "hafif",
                            "label": "Otomobil / Kamyonet / Minibüs"
                        },
                        {
                            "value": "agir",
                            "label": "Otobüs / Kamyon / Çekici / Tanker"
                        },
                        {
                            "value": "moto",
                            "label": "Motosiklet / Motorlu Bisiklet"
                        }
                    ],
                    "visibleWhen": null,
                    "id": "vehicleType",
                    "type": "select",
                    "label": "Araç türü",
                    "required": true
                },
                {
                    "defaultValue": true,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [],
                    "visibleWhen": null,
                    "id": "includeEmission",
                    "type": "boolean",
                    "label": "Egzoz emisyon ölçümü eklensin",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "zamaninda",
                            "label": "Zamanında"
                        },
                        {
                            "value": "gecikmeli",
                            "label": "Gecikmeli"
                        }
                    ],
                    "visibleWhen": null,
                    "id": "status",
                    "type": "radio",
                    "label": "Muayene durumu",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "ay",
                    "minimum": 0,
                    "maximum": 120,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "delayMonths",
                    "type": "number",
                    "label": "Gecikme ayı",
                    "required": false
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "vehicleType",
                    "type": "text",
                    "label": "Araç türü",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "vehicleTypeLabel",
                    "type": "text",
                    "label": "Araç türü açıklaması",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "inspectionFee",
                    "type": "money",
                    "label": "Muayene ücreti",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "emissionFee",
                    "type": "money",
                    "label": "Egzoz emisyon ücreti",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "appliedDelayMonths",
                    "type": "number",
                    "label": "Uygulanan gecikme",
                    "unit": "ay",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "lateSurcharge",
                    "type": "money",
                    "label": "Gecikme zammı",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "total",
                    "type": "money",
                    "label": "Toplam",
                    "unit": null,
                    "nullable": false,
                    "prominent": true
                }
            ]
        },
        "content": {
            "title": "Araç Muayene Ücreti Hesaplama",
            "shortTitle": "Araç Muayene Ücreti Hesaplama",
            "summary": "Araç sınıfının 2026 muayene ücretine isteğe bağlı egzoz ölçümü ve gecikme ayı zammını ekler.",
            "method": "Araç sınıfının 2026 muayene ücretine isteğe bağlı egzoz ölçümü ve gecikme ayı zammını ekler.",
            "limitations": [
                "Tarife, vergi ve mevzuat değerleri kişisel işlem tarihinde ayrıca doğrulanmalıdır."
            ],
            "faqs": [
                {
                    "question": "Sonuç kesin veya resmî bir tutar mıdır?",
                    "answer": "Hayır. Sonuç, gösterilen girdiler ve tarihli kaynak kaydıyla üretilen yardımcı bir hesaplamadır."
                }
            ],
            "sourcePanel": {
                "title": "Kaynak ve inceleme durumu",
                "summary": "Formül ve oranlar belirlenen aralıkta resmî kaynaktan yeniden incelenir. Değişiklik yoksa inceleme tarihi, değişiklik varsa config sürümü yenilenir.",
                "sourceIds": [
                    "tuvturk-inspection-fees-2026",
                    "csidb-emission-measurement-fee-2026"
                ],
                "notes": [
                    "Tarife, vergi ve mevzuat değerleri kişisel işlem tarihinde ayrıca doğrulanmalıdır."
                ]
            }
        },
        "config": {
            "kind": "vehicle-inspection-fee-v1",
            "calculatorId": "arac-emlak-yasam.arac-muayene-ucreti-hesaplama",
            "delayMonths": [
                0,
                120
            ],
            "policy": {
                "fees": {
                    "hafif": 3288,
                    "agir": 4446,
                    "moto": 1674
                },
                "emissionFee": 460,
                "monthlyLateRate": 0.05
            },
            "policyLifecycle": {
                "configVersion": "1.0.0",
                "sourceIds": [
                    "tuvturk-inspection-fees-2026",
                    "csidb-emission-measurement-fee-2026"
                ],
                "reviewedOn": "2026-08-25",
                "effectiveFrom": "2026-01-01",
                "effectiveTo": "2026-12-31",
                "reviewAfter": "2027-01-01"
            }
        },
        "testVectors": {
            "kind": "vehicle-finance-oracle-v1",
            "cases": [
                {
                    "caseId": "inspection-light-two-months",
                    "scenario": "positive",
                    "input": {
                        "vehicleType": "hafif",
                        "includeEmission": true,
                        "status": "gecikmeli",
                        "delayMonths": 2
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "inspectionFee": 3288,
                            "emissionFee": 460,
                            "lateSurcharge": 328.8,
                            "total": 4076.8
                        }
                    },
                    "tolerance": 1e-8
                },
                {
                    "caseId": "inspection-missing-type",
                    "scenario": "adversarial",
                    "input": {
                        "includeEmission": true,
                        "status": "zamaninda"
                    },
                    "expected": {
                        "ok": false,
                        "field": "vehicleType",
                        "errorCode": "REQUIRED"
                    },
                    "tolerance": 1e-9
                }
            ]
        },
        "lastReviewed": "2026-08-25"
    },
    {
        "toolId": "vehicle-excise-tax",
        "slug": "arac-otv-hesaplama",
        "path": "/arac-emlak-yasam/arac-otv-hesaplama",
        "category": {
            "id": "arac-emlak-yasam",
            "label": "Araç, Emlak ve Yaşam"
        },
        "classification": "remote-config",
        "engineId": "vehicle-excise-tax-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.1.0",
        "configVersion": "1.1.0",
        "effectiveFrom": "2026-07-31",
        "effectiveTo": "2026-12-31",
        "minAppVersion": "0.4.1",
        "updateCadence": {
            "mode": "scheduled-source-review",
            "days": 7,
            "reviewAfter": "2026-09-02",
            "specialMonths": [],
            "note": "Belirlenen tarihte resmî kaynak incelenir; değişiklik varsa config, yoksa inceleme tarihi yenilenir."
        },
        "sources": [
            {
                "sourceId": "gib-vehicle-excise-list-current",
                "sourceGroupId": "tr_vehicle_purchase_taxes",
                "title": "Motorlu taşıtlarda güncel ÖTV oranları",
                "publisher": "Gelir İdaresi Başkanlığı",
                "url": "https://gib.gov.tr/yardim-kaynaklar/yararli-bilgiler/ozel-tuketim-vergisi-oranlari-2-sayili-liste",
                "kind": "official-government",
                "reviewedOn": "2026-08-26",
                "watchFields": [
                    "ÖTV matrah eşikleri",
                    "yakıt ve motor türü",
                    "ÖTV oranı"
                ]
            },
            {
                "sourceId": "tbmm-law-7590-vehicle-minimum-excise-2026",
                "sourceGroupId": "tr_vehicle_purchase_taxes",
                "title": "7590 sayılı Kanun ile 87.03 araçlarda asgari ÖTV",
                "publisher": "Türkiye Büyük Millet Meclisi",
                "url": "https://cdn.tbmm.gov.tr/KKBSPublicFile/D28/Y4/KanunMetni/152a9101-15fe-4138-aceb-99a304bb9ec3.htm",
                "kind": "official-government",
                "reviewedOn": "2026-08-26",
                "watchFields": [
                    "87.03",
                    "asgari maktu vergi",
                    "araç sınıfı",
                    "yürürlük"
                ]
            },
            {
                "sourceId": "gib-vat-rates-current",
                "sourceGroupId": "tr_vat_rates",
                "title": "Güncel KDV oranları",
                "publisher": "Gelir İdaresi Başkanlığı",
                "url": "https://cdn.gib.gov.tr/api/gibportal-file/file/getFileResources?objectKey=arsiv%2Fyardim-kaynaklar%2Fyararli-bilgiler%2Fkdv-oranlari.pdf",
                "kind": "official-government",
                "reviewedOn": "2026-08-26",
                "watchFields": [
                    "KDV oranları",
                    "motorlu taşıtlar"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "benzin",
                            "label": "Benzinli / Dizel"
                        },
                        {
                            "value": "hev",
                            "label": "Hibrit (şarj edilemeyen)"
                        },
                        {
                            "value": "phev",
                            "label": "Şarj edilebilir hibrit"
                        },
                        {
                            "value": "elektrik",
                            "label": "Elektrikli"
                        }
                    ],
                    "visibleWhen": null,
                    "id": "driveType",
                    "type": "select",
                    "label": "Yakıt ve tahrik türü",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "cm³",
                    "minimum": 1,
                    "maximum": 20000,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "engineCc",
                    "type": "number",
                    "label": "Motor hacmi",
                    "required": false
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "kW",
                    "minimum": 1,
                    "maximum": 5000,
                    "maxDecimalPlaces": 2,
                    "options": [],
                    "visibleWhen": null,
                    "id": "powerKw",
                    "type": "number",
                    "label": "Motor gücü",
                    "required": false
                },
                {
                    "defaultValue": false,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [],
                    "visibleWhen": null,
                    "id": "phevQualified",
                    "type": "boolean",
                    "label": "PHEV şartlarını sağlıyor",
                    "required": false
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "TL",
                    "minimum": 1,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 2,
                    "options": [],
                    "visibleWhen": null,
                    "id": "taxBase",
                    "type": "number",
                    "label": "ÖTV matrahı",
                    "required": true
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "driveType",
                    "type": "text",
                    "label": "Tahrik türü",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "driveTypeLabel",
                    "type": "text",
                    "label": "Tahrik türü açıklaması",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "taxBase",
                    "type": "money",
                    "label": "ÖTV matrahı",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "exciseRatePercent",
                    "type": "percentage",
                    "label": "ÖTV oranı",
                    "unit": "%",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "exciseTax",
                    "type": "money",
                    "label": "ÖTV",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "vatBase",
                    "type": "money",
                    "label": "KDV matrahı",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "vat",
                    "type": "money",
                    "label": "KDV",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "totalTax",
                    "type": "money",
                    "label": "Toplam vergi",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "turnkeyPrice",
                    "type": "money",
                    "label": "Vergiler dâhil fiyat",
                    "unit": null,
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "taxSharePercent",
                    "type": "percentage",
                    "label": "Vergi payı",
                    "unit": "%",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "rateReason",
                    "type": "text",
                    "label": "Uygulanan tarife açıklaması",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                }
            ]
        },
        "content": {
            "title": "Araç ÖTV Hesaplama",
            "shortTitle": "Araç ÖTV Hesaplama",
            "summary": "Standart 87.03 binek otomobilde tahrik, motor ve matrahı 2026 tarifesinde eşleştirir; nispi tutar ile 100.000 TL asgari ÖTV'nin büyüğünü ve bu gerçek ÖTV dâhil matrah üzerinden KDV'yi üretir.",
            "method": "Standart 87.03 binek otomobilde tahrik, motor ve matrahı 2026 tarifesinde eşleştirir; nispi tutar ile 100.000 TL asgari ÖTV'nin büyüğünü ve bu gerçek ÖTV dâhil matrah üzerinden KDV'yi üretir.",
            "limitations": [
                "Yalnız standart 87.03 binek otomobilleri kapsar; L ve T sınıfı araçların farklı oran ve asgari tutarları bu araçta hesaplanmaz.",
                "Tarife, vergi ve mevzuat değerleri kişisel işlem tarihinde ayrıca doğrulanmalıdır."
            ],
            "faqs": [
                {
                    "question": "Sonuç kesin veya resmî bir tutar mıdır?",
                    "answer": "Hayır. Sonuç, gösterilen girdiler ve tarihli kaynak kaydıyla üretilen yardımcı bir hesaplamadır."
                }
            ],
            "sourcePanel": {
                "title": "Kaynak ve inceleme durumu",
                "summary": "Formül ve oranlar belirlenen aralıkta resmî kaynaktan yeniden incelenir. Değişiklik yoksa inceleme tarihi, değişiklik varsa config sürümü yenilenir.",
                "sourceIds": [
                    "gib-vehicle-excise-list-current",
                    "tbmm-law-7590-vehicle-minimum-excise-2026",
                    "gib-vat-rates-current"
                ],
                "notes": [
                    "Yalnız standart 87.03 binek otomobilleri kapsar; L ve T sınıfı araçların farklı oran ve asgari tutarları bu araçta hesaplanmaz.",
                    "Tarife, vergi ve mevzuat değerleri kişisel işlem tarihinde ayrıca doğrulanmalıdır."
                ]
            }
        },
        "config": {
            "kind": "vehicle-excise-tax-v1",
            "calculatorId": "arac-emlak-yasam.arac-otv-hesaplama",
            "taxBase": [
                1,
                1000000000
            ],
            "engineCc": [
                1,
                20000
            ],
            "powerKw": [
                1,
                5000
            ],
            "policy": {
                "vatRate": 0.2,
                "minimumExciseTax": 100000,
                "iceUpTo1400TaxBaseThresholds": [
                    650000,
                    900000,
                    1100000
                ],
                "iceUpTo1400Rates": [
                    0.7,
                    0.75,
                    0.8,
                    0.9
                ],
                "ice1401To1600TaxBaseThresholds": [
                    850000,
                    1100000,
                    1650000
                ],
                "ice1401To1600Rates": [
                    0.75,
                    0.8,
                    0.9,
                    1
                ],
                "ice1601To2000TaxBaseThreshold": 1650000,
                "ice1601To2000Rates": [
                    1.5,
                    1.7
                ],
                "iceAbove2000Rate": 2.2,
                "hevFirstMinimumPowerKwExclusive": 50,
                "hevFirstEngineCcExclusive": 1600,
                "hevFirstEngineCcMaximum": 1800,
                "hevFirstTaxBaseThreshold": 1250000,
                "hevFirstRates": [
                    0.7,
                    0.8
                ],
                "hevSecondMinimumPowerKwExclusive": 100,
                "hevSecondEngineCcExclusive": 2000,
                "hevSecondEngineCcMaximum": 2500,
                "hevSecondTaxBaseThreshold": 1650000,
                "hevSecondRates": [
                    1.5,
                    1.7
                ],
                "phevEngineCcFirstMaximum": 1600,
                "phevEngineCcSecondMaximum": 1800,
                "phevTaxBaseThreshold": 1350000,
                "phevFirstRates": [
                    0.45,
                    0.75
                ],
                "phevSecondRate": 0.85,
                "electricPowerThresholdKw": 160,
                "electricTaxBaseThreshold": 1650000,
                "electricLowPowerRates": [
                    0.25,
                    0.55
                ],
                "electricHighPowerRates": [
                    0.65,
                    0.75
                ]
            },
            "policyLifecycle": {
                "configVersion": "1.1.0",
                "sourceIds": [
                    "gib-vehicle-excise-list-current",
                    "tbmm-law-7590-vehicle-minimum-excise-2026",
                    "gib-vat-rates-current"
                ],
                "reviewedOn": "2026-08-26",
                "effectiveFrom": "2026-07-31",
                "effectiveTo": "2026-12-31",
                "reviewAfter": "2026-09-02"
            }
        },
        "testVectors": {
            "kind": "vehicle-finance-oracle-v1",
            "cases": [
                {
                    "caseId": "excise-electric-threshold",
                    "scenario": "threshold",
                    "input": {
                        "driveType": "elektrik",
                        "powerKw": 160,
                        "taxBase": 1000000
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "exciseRatePercent": 25,
                            "exciseTax": 250000,
                            "vat": 250000,
                            "turnkeyPrice": 1500000
                        }
                    },
                    "tolerance": 1e-8
                },
                {
                    "caseId": "excise-electric-minimum-floor",
                    "scenario": "threshold",
                    "input": {
                        "driveType": "elektrik",
                        "powerKw": 160,
                        "taxBase": 100000
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "exciseRatePercent": 25,
                            "exciseTax": 100000,
                            "vat": 40000,
                            "turnkeyPrice": 240000
                        }
                    },
                    "tolerance": 1e-8
                },
                {
                    "caseId": "excise-missing-power",
                    "scenario": "adversarial",
                    "input": {
                        "driveType": "elektrik",
                        "taxBase": 1000000
                    },
                    "expected": {
                        "ok": false,
                        "field": "powerKw",
                        "errorCode": "REQUIRED"
                    },
                    "tolerance": 1e-9
                }
            ]
        },
        "lastReviewed": "2026-08-26"
    },
    {
        "toolId": "natural-gas-bill",
        "slug": "dogalgaz-faturasi-hesaplama",
        "path": "/arac-emlak-yasam/dogalgaz-faturasi-hesaplama",
        "category": {
            "id": "arac-emlak-yasam",
            "label": "Araç, Emlak ve Yaşam"
        },
        "classification": "remote-config",
        "engineId": "natural-gas-bill-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-04-04",
        "effectiveTo": null,
        "minAppVersion": "0.4.0",
        "updateCadence": {
            "mode": "scheduled-source-review",
            "days": 7,
            "reviewAfter": "2026-09-01",
            "specialMonths": [],
            "note": "Belirlenen tarihte resmî kaynak incelenir; değişiklik varsa config, yoksa inceleme tarihi yenilenir."
        },
        "sources": [
            {
                "sourceId": "epdk-natural-gas-system-use-current",
                "sourceGroupId": "tr_natural_gas_tariffs",
                "title": "Doğal gaz sistem kullanım bedelleri",
                "publisher": "Enerji Piyasası Düzenleme Kurumu",
                "url": "https://lisans.epdk.gov.tr/epvys-web/faces/pages/online/tarifeDogalGaz/dogalGazSistemKullanimBedeliOzetSorgula.xhtml",
                "kind": "official-government",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "dağıtım bölgesi",
                    "sistem kullanım bedeli",
                    "yürürlük"
                ]
            },
            {
                "sourceId": "botas-natural-gas-tariff-2026-04",
                "sourceGroupId": "tr_natural_gas_tariffs",
                "title": "4 Nisan 2026 doğal gaz tarifesi",
                "publisher": "BOTAŞ",
                "url": "https://www.botas.gov.tr/uploads/dosyaYoneticisi/139364-4-nisan_2026_tarife.pdf",
                "kind": "official-operator",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "birim fiyat",
                    "abone grubu",
                    "yürürlük"
                ]
            },
            {
                "sourceId": "botas-kfu-limits-2026",
                "sourceGroupId": "tr_natural_gas_tariffs",
                "title": "2026 KFU limitleri",
                "publisher": "BOTAŞ",
                "url": "https://www.botas.gov.tr/uploads/dosyaYoneticisi/325499-kfu_limitler.pdf",
                "kind": "official-operator",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "KFU limiti",
                    "dönem"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "m³",
                    "minimum": 0,
                    "maximum": 100000,
                    "maxDecimalPlaces": 2,
                    "options": [],
                    "visibleWhen": null,
                    "id": "consumptionM3",
                    "type": "number",
                    "label": "Tüketim",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "TL/m³",
                    "minimum": 0,
                    "maximum": 100000,
                    "maxDecimalPlaces": 6,
                    "options": [],
                    "visibleWhen": null,
                    "id": "unitPriceExVat",
                    "type": "number",
                    "label": "KDV hariç birim fiyat",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "TL",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 2,
                    "options": [],
                    "visibleWhen": null,
                    "id": "otherTaxBase",
                    "type": "number",
                    "label": "Diğer KDV matrahı",
                    "required": false
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "consumptionM3",
                    "type": "number",
                    "label": "Tüketim",
                    "unit": "m³",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "unitPriceExVat",
                    "type": "money",
                    "label": "KDV hariç birim fiyat",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "gasCharge",
                    "type": "money",
                    "label": "Doğal gaz bedeli",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "otherTaxBase",
                    "type": "money",
                    "label": "Diğer KDV matrahı",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "vatBase",
                    "type": "money",
                    "label": "KDV matrahı",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "vat",
                    "type": "money",
                    "label": "KDV",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "total",
                    "type": "money",
                    "label": "Toplam",
                    "unit": null,
                    "nullable": false,
                    "prominent": true
                }
            ]
        },
        "content": {
            "title": "Doğal Gaz Faturası Hesaplama",
            "shortTitle": "Doğal Gaz Faturası Hesaplama",
            "summary": "Kullanıcının faturasındaki bölgesel KDV hariç birim fiyatı tüketimle çarpar, diğer vergi matrahını ve KDV'yi görünür biçimde ekler.",
            "method": "Kullanıcının faturasındaki bölgesel KDV hariç birim fiyatı tüketimle çarpar, diğer vergi matrahını ve KDV'yi görünür biçimde ekler.",
            "limitations": [
                "Bölgesel tarife ve düzeltme katsayısı faturadan alınmalıdır; sonuç dağıtım şirketi faturası değildir."
            ],
            "faqs": [
                {
                    "question": "Sonuç kesin veya resmî bir tutar mıdır?",
                    "answer": "Hayır. Sonuç, gösterilen girdiler ve tarihli kaynak kaydıyla üretilen yardımcı bir hesaplamadır."
                }
            ],
            "sourcePanel": {
                "title": "Kaynak ve inceleme durumu",
                "summary": "Formül ve oranlar belirlenen aralıkta resmî kaynaktan yeniden incelenir. Değişiklik yoksa inceleme tarihi, değişiklik varsa config sürümü yenilenir.",
                "sourceIds": [
                    "epdk-natural-gas-system-use-current",
                    "botas-natural-gas-tariff-2026-04",
                    "botas-kfu-limits-2026"
                ],
                "notes": [
                    "Bölgesel tarife ve düzeltme katsayısı faturadan alınmalıdır; sonuç dağıtım şirketi faturası değildir."
                ]
            }
        },
        "config": {
            "kind": "natural-gas-bill-v1",
            "calculatorId": "arac-emlak-yasam.dogalgaz-faturasi-hesaplama",
            "consumptionM3": [
                0,
                100000
            ],
            "unitPriceExVat": [
                0,
                100000
            ],
            "otherTaxBase": [
                0,
                1000000000
            ],
            "vatRate": 0.2,
            "policyLifecycle": {
                "configVersion": "1.0.0",
                "sourceIds": [
                    "epdk-natural-gas-system-use-current",
                    "botas-natural-gas-tariff-2026-04",
                    "botas-kfu-limits-2026"
                ],
                "reviewedOn": "2026-08-25",
                "effectiveFrom": "2026-04-04",
                "effectiveTo": null,
                "reviewAfter": "2026-09-01"
            }
        },
        "testVectors": {
            "kind": "vehicle-finance-oracle-v1",
            "cases": [
                {
                    "caseId": "gas-100m3",
                    "scenario": "positive",
                    "input": {
                        "consumptionM3": 100,
                        "unitPriceExVat": 10,
                        "otherTaxBase": 50
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "gasCharge": 1000,
                            "vatBase": 1050,
                            "vat": 210,
                            "total": 1260
                        }
                    },
                    "tolerance": 1e-8
                },
                {
                    "caseId": "gas-missing-consumption",
                    "scenario": "adversarial",
                    "input": {
                        "unitPriceExVat": 10
                    },
                    "expected": {
                        "ok": false,
                        "field": "consumptionM3",
                        "errorCode": "REQUIRED"
                    },
                    "tolerance": 1e-9
                }
            ]
        },
        "lastReviewed": "2026-08-25"
    },
    {
        "toolId": "electricity-bill",
        "slug": "elektrik-faturasi-hesaplama",
        "path": "/arac-emlak-yasam/elektrik-faturasi-hesaplama",
        "category": {
            "id": "arac-emlak-yasam",
            "label": "Araç, Emlak ve Yaşam"
        },
        "classification": "remote-config",
        "engineId": "electricity-bill-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-04-04",
        "effectiveTo": null,
        "minAppVersion": "0.4.0",
        "updateCadence": {
            "mode": "scheduled-source-review",
            "days": 37,
            "reviewAfter": "2026-10-01",
            "specialMonths": [],
            "note": "Belirlenen tarihte resmî kaynak incelenir; değişiklik varsa config, yoksa inceleme tarihi yenilenir."
        },
        "sources": [
            {
                "sourceId": "epdk-electricity-tariff-2026-04",
                "sourceGroupId": "tr_electricity_regulated_tariff",
                "title": "Nisan 2026 elektrik tarifesi",
                "publisher": "Enerji Piyasası Düzenleme Kurumu",
                "url": "https://epdk.gov.tr/Detay/DownloadDocument?id=pI%2BXZjfJIU0%3D",
                "kind": "official-government",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "enerji bedeli",
                    "dağıtım bedeli",
                    "kademe",
                    "abone grubu"
                ]
            },
            {
                "sourceId": "energy-ministry-electricity-funds-current",
                "sourceGroupId": "tr_electricity_regulated_tariff",
                "title": "Elektrik fon ve payları",
                "publisher": "Enerji ve Tabii Kaynaklar Bakanlığı",
                "url": "https://enerji.gov.tr/haber-detay?id=21077",
                "kind": "official-government",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "fon",
                    "pay",
                    "kapsam"
                ]
            },
            {
                "sourceId": "gib-electricity-etv-current",
                "sourceGroupId": "tr_electricity_regulated_tariff",
                "title": "Elektrik tüketim vergisi uygulaması",
                "publisher": "Gelir İdaresi Başkanlığı",
                "url": "https://gib.gov.tr/mevzuat/kanun/449/ozelge/37262",
                "kind": "official-government",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "elektrik tüketim vergisi",
                    "matrah",
                    "oran"
                ]
            },
            {
                "sourceId": "gib-vat-rates-current",
                "sourceGroupId": "tr_vat_rates",
                "title": "Güncel KDV oranları",
                "publisher": "Gelir İdaresi Başkanlığı",
                "url": "https://cdn.gib.gov.tr/api/gibportal-file/file/getFileResources?objectKey=arsiv%2Fyardim-kaynaklar%2Fyararli-bilgiler%2Fkdv-oranlari.pdf",
                "kind": "official-government",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "KDV oranları",
                    "elektrik ve su kapsamı"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "mesken",
                            "label": "Mesken"
                        },
                        {
                            "value": "ticarethane",
                            "label": "Ticarethane / Kamu-Özel Hizmetler"
                        }
                    ],
                    "visibleWhen": null,
                    "id": "subscriberType",
                    "type": "select",
                    "label": "Abone grubu",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "kWh",
                    "minimum": 1,
                    "maximum": 50000,
                    "maxDecimalPlaces": 2,
                    "options": [],
                    "visibleWhen": null,
                    "id": "consumptionKwh",
                    "type": "number",
                    "label": "Tüketim",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "gün",
                    "minimum": 1,
                    "maximum": 90,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "billingDays",
                    "type": "number",
                    "label": "Fatura gün sayısı",
                    "required": true
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "subscriberType",
                    "type": "text",
                    "label": "Abone grubu",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "consumptionKwh",
                    "type": "number",
                    "label": "Tüketim",
                    "unit": "kWh",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "billingDays",
                    "type": "number",
                    "label": "Fatura gün sayısı",
                    "unit": "gün",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "subscriberTypeLabel",
                    "type": "text",
                    "label": "Abone grubu açıklaması",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "thresholdKwh",
                    "type": "number",
                    "label": "Kademe eşiği",
                    "unit": "kWh",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "lowTierKwh",
                    "type": "number",
                    "label": "Düşük kademe tüketimi",
                    "unit": "kWh",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "highTierKwh",
                    "type": "number",
                    "label": "Yüksek kademe tüketimi",
                    "unit": "kWh",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "energyCharge",
                    "type": "money",
                    "label": "Enerji bedeli",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "distributionCharge",
                    "type": "money",
                    "label": "Dağıtım bedeli",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "electricityConsumptionTax",
                    "type": "money",
                    "label": "Elektrik tüketim vergisi",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "vatBase",
                    "type": "money",
                    "label": "KDV matrahı",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "vat",
                    "type": "money",
                    "label": "KDV",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "total",
                    "type": "money",
                    "label": "Toplam",
                    "unit": null,
                    "nullable": false,
                    "prominent": true
                }
            ]
        },
        "content": {
            "title": "Elektrik Faturası Hesaplama",
            "shortTitle": "Elektrik Faturası Hesaplama",
            "summary": "Günlük kademe eşiğini fatura günüyle çarpar; enerji, dağıtım, elektrik tüketim vergisi ve KDV'yi ayrı kalemler olarak hesaplar.",
            "method": "Günlük kademe eşiğini fatura günüyle çarpar; enerji, dağıtım, elektrik tüketim vergisi ve KDV'yi ayrı kalemler olarak hesaplar.",
            "limitations": [
                "Tarife, vergi ve mevzuat değerleri kişisel işlem tarihinde ayrıca doğrulanmalıdır."
            ],
            "faqs": [
                {
                    "question": "Sonuç kesin veya resmî bir tutar mıdır?",
                    "answer": "Hayır. Sonuç, gösterilen girdiler ve tarihli kaynak kaydıyla üretilen yardımcı bir hesaplamadır."
                }
            ],
            "sourcePanel": {
                "title": "Kaynak ve inceleme durumu",
                "summary": "Formül ve oranlar belirlenen aralıkta resmî kaynaktan yeniden incelenir. Değişiklik yoksa inceleme tarihi, değişiklik varsa config sürümü yenilenir.",
                "sourceIds": [
                    "epdk-electricity-tariff-2026-04",
                    "energy-ministry-electricity-funds-current",
                    "gib-electricity-etv-current",
                    "gib-vat-rates-current"
                ],
                "notes": [
                    "Tarife, vergi ve mevzuat değerleri kişisel işlem tarihinde ayrıca doğrulanmalıdır."
                ]
            }
        },
        "config": {
            "kind": "electricity-bill-v1",
            "calculatorId": "arac-emlak-yasam.elektrik-faturasi-hesaplama",
            "consumptionKwh": [
                1,
                50000
            ],
            "billingDays": [
                1,
                90
            ],
            "tariffs": {
                "mesken": {
                    "dailyThresholdKwh": 8,
                    "lowEnergyUnitPrice": 0.494065,
                    "highEnergyUnitPrice": 1.895808,
                    "distributionUnitPrice": 2.4249,
                    "electricityConsumptionTaxRate": 0.05,
                    "vatRate": 0.1
                },
                "ticarethane": {
                    "dailyThresholdKwh": 30,
                    "lowEnergyUnitPrice": 2.873087,
                    "highEnergyUnitPrice": 3.454688,
                    "distributionUnitPrice": 2.479368,
                    "electricityConsumptionTaxRate": 0.05,
                    "vatRate": 0.2
                }
            },
            "policyLifecycle": {
                "configVersion": "1.0.0",
                "sourceIds": [
                    "epdk-electricity-tariff-2026-04",
                    "energy-ministry-electricity-funds-current",
                    "gib-electricity-etv-current",
                    "gib-vat-rates-current"
                ],
                "reviewedOn": "2026-08-25",
                "effectiveFrom": "2026-04-04",
                "effectiveTo": null,
                "reviewAfter": "2026-10-01"
            }
        },
        "testVectors": {
            "kind": "vehicle-finance-oracle-v1",
            "cases": [
                {
                    "caseId": "electricity-commercial-1000",
                    "scenario": "threshold",
                    "input": {
                        "subscriberType": "ticarethane",
                        "consumptionKwh": 1000,
                        "billingDays": 30
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "thresholdKwh": 900,
                            "lowTierKwh": 900,
                            "highTierKwh": 100,
                            "energyCharge": 2931.2471,
                            "distributionCharge": 2479.368,
                            "total": 6668.612946
                        }
                    },
                    "tolerance": 1e-8
                },
                {
                    "caseId": "electricity-missing-days",
                    "scenario": "adversarial",
                    "input": {
                        "subscriberType": "mesken",
                        "consumptionKwh": 100
                    },
                    "expected": {
                        "ok": false,
                        "field": "billingDays",
                        "errorCode": "REQUIRED"
                    },
                    "tolerance": 1e-9
                }
            ]
        },
        "lastReviewed": "2026-08-25"
    },
    {
        "toolId": "motor-vehicle-tax",
        "slug": "mtv-hesaplama",
        "path": "/arac-emlak-yasam/mtv-hesaplama",
        "category": {
            "id": "arac-emlak-yasam",
            "label": "Araç, Emlak ve Yaşam"
        },
        "classification": "remote-config",
        "engineId": "motor-vehicle-tax-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-01-01",
        "effectiveTo": "2026-12-31",
        "minAppVersion": "0.4.0",
        "updateCadence": {
            "mode": "scheduled-source-review",
            "days": 129,
            "reviewAfter": "2027-01-01",
            "specialMonths": [],
            "note": "Belirlenen tarihte resmî kaynak incelenir; değişiklik varsa config, yoksa inceleme tarihi yenilenir."
        },
        "sources": [
            {
                "sourceId": "gib-mtv-guide-2026",
                "sourceGroupId": "tr_motor_vehicle_tax",
                "title": "2026 Motorlu Taşıtlar Vergisi rehberi",
                "publisher": "Gelir İdaresi Başkanlığı",
                "url": "https://cdn.gib.gov.tr/api/gibportal-file/file/getFileResources?objectKey=arsiv%2Ffileadmin%2Fbeyannamerehberi%2F2026%2F2026MtvTpcRehberi.pdf",
                "kind": "official-government",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "motor hacmi",
                    "taşıt değeri",
                    "yaş",
                    "yıllık vergi"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "otomobil",
                            "label": "Otomobil / Kaptıkaçtı / Arazi Taşıtı"
                        },
                        {
                            "value": "motosiklet",
                            "label": "Motosiklet"
                        }
                    ],
                    "visibleWhen": null,
                    "id": "vehicleType",
                    "type": "select",
                    "label": "Taşıt türü",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "cm³",
                    "minimum": 1,
                    "maximum": 20000,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "engineCc",
                    "type": "number",
                    "label": "Motor hacmi",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "yıl",
                    "minimum": 0,
                    "maximum": 99,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "vehicleAgeYears",
                    "type": "number",
                    "label": "Taşıt yaşı",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "dusuk",
                            "label": "Alt taşıt değeri bandı"
                        },
                        {
                            "value": "orta",
                            "label": "Orta taşıt değeri bandı"
                        },
                        {
                            "value": "yuksek",
                            "label": "Üst taşıt değeri bandı"
                        }
                    ],
                    "visibleWhen": null,
                    "id": "valueBand",
                    "type": "select",
                    "label": "Taşıt değeri bandı",
                    "required": false
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "vehicleType",
                    "type": "text",
                    "label": "Taşıt türü",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "engineCc",
                    "type": "number",
                    "label": "Motor hacmi",
                    "unit": "cm³",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "vehicleAgeYears",
                    "type": "number",
                    "label": "Taşıt yaşı",
                    "unit": "yıl",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "valueBand",
                    "type": "text",
                    "label": "Taşıt değeri bandı",
                    "unit": null,
                    "nullable": true,
                    "prominent": false
                },
                {
                    "id": "vehicleTypeLabel",
                    "type": "text",
                    "label": "Taşıt türü açıklaması",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "appliedAgeBand",
                    "type": "text",
                    "label": "Uygulanan yaş bandı",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "appliedEngineBand",
                    "type": "number",
                    "label": "Uygulanan motor bandı",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "appliedValueBand",
                    "type": "text",
                    "label": "Uygulanan değer bandı",
                    "unit": null,
                    "nullable": true,
                    "prominent": false
                },
                {
                    "id": "annualTax",
                    "type": "money",
                    "label": "Yıllık MTV",
                    "unit": null,
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "firstInstallment",
                    "type": "money",
                    "label": "Birinci taksit",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "secondInstallment",
                    "type": "money",
                    "label": "İkinci taksit",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                }
            ]
        },
        "content": {
            "title": "Motorlu Taşıtlar Vergisi Hesaplama",
            "shortTitle": "Motorlu Taşıtlar Vergisi Hesaplama",
            "summary": "Taşıt türü, motor hacmi, yaş ve otomobillerde değer bandını 2026 MTV tablosunda eşleştirir; iki taksiti ayrı gösterir.",
            "method": "Taşıt türü, motor hacmi, yaş ve otomobillerde değer bandını 2026 MTV tablosunda eşleştirir; iki taksiti ayrı gösterir.",
            "limitations": [
                "Tarife, vergi ve mevzuat değerleri kişisel işlem tarihinde ayrıca doğrulanmalıdır."
            ],
            "faqs": [
                {
                    "question": "Sonuç kesin veya resmî bir tutar mıdır?",
                    "answer": "Hayır. Sonuç, gösterilen girdiler ve tarihli kaynak kaydıyla üretilen yardımcı bir hesaplamadır."
                }
            ],
            "sourcePanel": {
                "title": "Kaynak ve inceleme durumu",
                "summary": "Formül ve oranlar belirlenen aralıkta resmî kaynaktan yeniden incelenir. Değişiklik yoksa inceleme tarihi, değişiklik varsa config sürümü yenilenir.",
                "sourceIds": [
                    "gib-mtv-guide-2026"
                ],
                "notes": [
                    "Tarife, vergi ve mevzuat değerleri kişisel işlem tarihinde ayrıca doğrulanmalıdır."
                ]
            }
        },
        "config": {
            "kind": "motor-vehicle-tax-v1",
            "calculatorId": "arac-emlak-yasam.mtv-hesaplama",
            "engineCc": [
                1,
                20000
            ],
            "vehicleAgeYears": [
                0,
                99
            ],
            "policy": {
                "automobile": [
                    {
                        "engineCcMaximum": 1300,
                        "valueBandCount": 3,
                        "annualAmountsByValueBand": [
                            [
                                5750,
                                4010,
                                2238,
                                1689,
                                593
                            ],
                            [
                                6319,
                                4409,
                                2459,
                                1861,
                                655
                            ],
                            [
                                6902,
                                4807,
                                2693,
                                2032,
                                706
                            ]
                        ]
                    },
                    {
                        "engineCcMaximum": 1600,
                        "valueBandCount": 3,
                        "annualAmountsByValueBand": [
                            [
                                10016,
                                7510,
                                4354,
                                3077,
                                1181
                            ],
                            [
                                11023,
                                8264,
                                4794,
                                3375,
                                1290
                            ],
                            [
                                12028,
                                9012,
                                5220,
                                3685,
                                1408
                            ]
                        ]
                    },
                    {
                        "engineCcMaximum": 1800,
                        "valueBandCount": 2,
                        "annualAmountsByValueBand": [
                            [
                                19472,
                                15226,
                                8948,
                                5458,
                                2113
                            ],
                            [
                                21251,
                                16600,
                                9775,
                                5964,
                                2307
                            ]
                        ]
                    },
                    {
                        "engineCcMaximum": 2000,
                        "valueBandCount": 2,
                        "annualAmountsByValueBand": [
                            [
                                30679,
                                23625,
                                13886,
                                8264,
                                3248
                            ],
                            [
                                33474,
                                25784,
                                15147,
                                9012,
                                3547
                            ]
                        ]
                    },
                    {
                        "engineCcMaximum": 2500,
                        "valueBandCount": 2,
                        "annualAmountsByValueBand": [
                            [
                                46027,
                                33413,
                                20874,
                                12465,
                                4930
                            ],
                            [
                                50217,
                                36448,
                                22768,
                                13606,
                                5378
                            ]
                        ]
                    },
                    {
                        "engineCcMaximum": 3000,
                        "valueBandCount": 2,
                        "annualAmountsByValueBand": [
                            [
                                64175,
                                55837,
                                34878,
                                18758,
                                6875
                            ],
                            [
                                70018,
                                60905,
                                38053,
                                20466,
                                7503
                            ]
                        ]
                    },
                    {
                        "engineCcMaximum": 3500,
                        "valueBandCount": 2,
                        "annualAmountsByValueBand": [
                            [
                                97744,
                                87954,
                                52976,
                                26443,
                                9684
                            ],
                            [
                                106641,
                                95940,
                                57791,
                                28839,
                                10578
                            ]
                        ]
                    },
                    {
                        "engineCcMaximum": 4000,
                        "valueBandCount": 2,
                        "annualAmountsByValueBand": [
                            [
                                153684,
                                132712,
                                78152,
                                34878,
                                13886
                            ],
                            [
                                167671,
                                144770,
                                85271,
                                38053,
                                15147
                            ]
                        ]
                    },
                    {
                        "engineCcMaximum": null,
                        "valueBandCount": 2,
                        "annualAmountsByValueBand": [
                            [
                                251554,
                                188627,
                                111714,
                                50206,
                                19472
                            ],
                            [
                                274415,
                                205781,
                                121873,
                                54769,
                                21251
                            ]
                        ]
                    }
                ],
                "motorcycle": [
                    {
                        "engineCcMaximum": 250,
                        "annualAmounts": [
                            1069,
                            799,
                            589,
                            362,
                            136
                        ]
                    },
                    {
                        "engineCcMaximum": 650,
                        "annualAmounts": [
                            2214,
                            1676,
                            1069,
                            589,
                            362
                        ]
                    },
                    {
                        "engineCcMaximum": 1200,
                        "annualAmounts": [
                            5719,
                            3398,
                            1676,
                            1069,
                            589
                        ]
                    },
                    {
                        "engineCcMaximum": null,
                        "annualAmounts": [
                            13876,
                            9167,
                            5719,
                            4540,
                            2214
                        ]
                    }
                ]
            },
            "policyLifecycle": {
                "configVersion": "1.0.0",
                "sourceIds": [
                    "gib-mtv-guide-2026"
                ],
                "reviewedOn": "2026-08-25",
                "effectiveFrom": "2026-01-01",
                "effectiveTo": "2026-12-31",
                "reviewAfter": "2027-01-01"
            }
        },
        "testVectors": {
            "kind": "vehicle-finance-oracle-v1",
            "cases": [
                {
                    "caseId": "mtv-1300-young-high",
                    "scenario": "threshold",
                    "input": {
                        "vehicleType": "otomobil",
                        "engineCc": 1300,
                        "vehicleAgeYears": 1,
                        "valueBand": "yuksek"
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "annualTax": 6902,
                            "firstInstallment": 3451,
                            "secondInstallment": 3451
                        }
                    },
                    "tolerance": 1e-9
                },
                {
                    "caseId": "mtv-missing-value-band",
                    "scenario": "adversarial",
                    "input": {
                        "vehicleType": "otomobil",
                        "engineCc": 1300,
                        "vehicleAgeYears": 1
                    },
                    "expected": {
                        "ok": false,
                        "field": "valueBand",
                        "errorCode": "REQUIRED"
                    },
                    "tolerance": 1e-9
                }
            ]
        },
        "lastReviewed": "2026-08-25"
    },
    {
        "toolId": "water-bill",
        "slug": "su-faturasi-hesaplama",
        "path": "/arac-emlak-yasam/su-faturasi-hesaplama",
        "category": {
            "id": "arac-emlak-yasam",
            "label": "Araç, Emlak ve Yaşam"
        },
        "classification": "remote-config",
        "engineId": "water-bill-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-01-01",
        "effectiveTo": "2026-12-31",
        "minAppVersion": "0.4.0",
        "updateCadence": {
            "mode": "scheduled-source-review",
            "days": 129,
            "reviewAfter": "2027-01-01",
            "specialMonths": [],
            "note": "Belirlenen tarihte resmî kaynak incelenir; değişiklik varsa config, yoksa inceleme tarihi yenilenir."
        },
        "sources": [
            {
                "sourceId": "gib-environmental-cleaning-tax-2026",
                "sourceGroupId": "tr_environmental_cleaning_and_water_tax",
                "title": "2026 çevre temizlik vergisi",
                "publisher": "Gelir İdaresi Başkanlığı",
                "url": "https://cdn.gib.gov.tr/api/gibportal-file/file/getFile?objectKey=MEVZUAT_TEBLIGLER%2FUNIVERSAL%2F2025%2F2464_Teblig59.pdf",
                "kind": "official-government",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "çevre temizlik vergisi",
                    "büyükşehir",
                    "indirimli yöre"
                ]
            },
            {
                "sourceId": "aski-water-tariff-2026",
                "sourceGroupId": "tr_environmental_cleaning_and_water_tax",
                "title": "2026 su ve atık su tarifesi",
                "publisher": "ASKİ",
                "url": "https://www.aski.gov.tr/Yukle/Dosya/YillikTarife/2026.pdf",
                "kind": "official-operator",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "su bedeli",
                    "atık su bedeli",
                    "kademe"
                ]
            },
            {
                "sourceId": "izsu-water-tariff-current",
                "sourceGroupId": "tr_environmental_cleaning_and_water_tax",
                "title": "Güncel su ve atık su tarifesi",
                "publisher": "İZSU",
                "url": "https://www.izsu.gov.tr/tr/abonelikislemleri/2/36?v=1.0.20",
                "kind": "official-operator",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "su bedeli",
                    "atık su bedeli",
                    "sabit bedel"
                ]
            },
            {
                "sourceId": "gib-vat-rates-current",
                "sourceGroupId": "tr_vat_rates",
                "title": "Güncel KDV oranları",
                "publisher": "Gelir İdaresi Başkanlığı",
                "url": "https://cdn.gib.gov.tr/api/gibportal-file/file/getFileResources?objectKey=arsiv%2Fyardim-kaynaklar%2Fyararli-bilgiler%2Fkdv-oranlari.pdf",
                "kind": "official-government",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "KDV oranları",
                    "elektrik ve su kapsamı"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "BUYUKSEHIR",
                            "label": "Büyükşehir Belediyesi"
                        },
                        {
                            "value": "DIGER",
                            "label": "Diğer Belediye"
                        },
                        {
                            "value": "INDIRIMLI",
                            "label": "İndirimli yöre"
                        }
                    ],
                    "visibleWhen": null,
                    "id": "municipalityType",
                    "type": "select",
                    "label": "Belediye türü",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "m³",
                    "minimum": 0,
                    "maximum": 100000,
                    "maxDecimalPlaces": 2,
                    "options": [],
                    "visibleWhen": null,
                    "id": "consumptionM3",
                    "type": "number",
                    "label": "Tüketim",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "TL/m³",
                    "minimum": 0,
                    "maximum": 100000,
                    "maxDecimalPlaces": 6,
                    "options": [],
                    "visibleWhen": null,
                    "id": "waterUnitPrice",
                    "type": "number",
                    "label": "Su birim fiyatı",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "TL/m³",
                    "minimum": 0,
                    "maximum": 100000,
                    "maxDecimalPlaces": 6,
                    "options": [],
                    "visibleWhen": null,
                    "id": "wastewaterUnitPrice",
                    "type": "number",
                    "label": "Atık su birim fiyatı",
                    "required": true
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "municipalityType",
                    "type": "text",
                    "label": "Belediye türü",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "consumptionM3",
                    "type": "number",
                    "label": "Tüketim",
                    "unit": "m³",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "waterUnitPrice",
                    "type": "money",
                    "label": "Su birim fiyatı",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "wastewaterUnitPrice",
                    "type": "money",
                    "label": "Atık su birim fiyatı",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "municipalityTypeLabel",
                    "type": "text",
                    "label": "Belediye türü açıklaması",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "waterCharge",
                    "type": "money",
                    "label": "Su bedeli",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "wastewaterCharge",
                    "type": "money",
                    "label": "Atık su bedeli",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "environmentalTaxPerM3",
                    "type": "money",
                    "label": "m³ başına ÇTV",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "environmentalTax",
                    "type": "money",
                    "label": "Çevre temizlik vergisi",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "vatBase",
                    "type": "money",
                    "label": "KDV matrahı",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "waterVat",
                    "type": "money",
                    "label": "Su KDV'si",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "wastewaterVat",
                    "type": "money",
                    "label": "Atık su KDV'si",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "vat",
                    "type": "money",
                    "label": "Toplam KDV",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "total",
                    "type": "money",
                    "label": "Toplam",
                    "unit": null,
                    "nullable": false,
                    "prominent": true
                }
            ]
        },
        "content": {
            "title": "Su Faturası Hesaplama",
            "shortTitle": "Su Faturası Hesaplama",
            "summary": "Kullanıcının belediye tarifesinden girdiği su ve atık su fiyatlarına belediye türüne göre ÇTV ve ayrı KDV oranlarını uygular.",
            "method": "Kullanıcının belediye tarifesinden girdiği su ve atık su fiyatlarına belediye türüne göre ÇTV ve ayrı KDV oranlarını uygular.",
            "limitations": [
                "Su ve atık su birim fiyatları belediyeye ve abone kademesine göre değiştiğinden faturadan girilmelidir."
            ],
            "faqs": [
                {
                    "question": "Sonuç kesin veya resmî bir tutar mıdır?",
                    "answer": "Hayır. Sonuç, gösterilen girdiler ve tarihli kaynak kaydıyla üretilen yardımcı bir hesaplamadır."
                }
            ],
            "sourcePanel": {
                "title": "Kaynak ve inceleme durumu",
                "summary": "Formül ve oranlar belirlenen aralıkta resmî kaynaktan yeniden incelenir. Değişiklik yoksa inceleme tarihi, değişiklik varsa config sürümü yenilenir.",
                "sourceIds": [
                    "gib-environmental-cleaning-tax-2026",
                    "aski-water-tariff-2026",
                    "izsu-water-tariff-current",
                    "gib-vat-rates-current"
                ],
                "notes": [
                    "Su ve atık su birim fiyatları belediyeye ve abone kademesine göre değiştiğinden faturadan girilmelidir."
                ]
            }
        },
        "config": {
            "kind": "water-bill-v1",
            "calculatorId": "arac-emlak-yasam.su-faturasi-hesaplama",
            "consumptionM3": [
                0,
                100000
            ],
            "unitPrice": [
                0,
                100000
            ],
            "policy": {
                "environmentalTaxPerM3": {
                    "BUYUKSEHIR": 4,
                    "DIGER": 3,
                    "INDIRIMLI": 1.5
                },
                "waterVatRate": 0.01,
                "wastewaterVatRate": 0.1
            },
            "policyLifecycle": {
                "configVersion": "1.0.0",
                "sourceIds": [
                    "gib-environmental-cleaning-tax-2026",
                    "aski-water-tariff-2026",
                    "izsu-water-tariff-current",
                    "gib-vat-rates-current"
                ],
                "reviewedOn": "2026-08-25",
                "effectiveFrom": "2026-01-01",
                "effectiveTo": "2026-12-31",
                "reviewAfter": "2027-01-01"
            }
        },
        "testVectors": {
            "kind": "vehicle-finance-oracle-v1",
            "cases": [
                {
                    "caseId": "water-big-city-10m3",
                    "scenario": "positive",
                    "input": {
                        "municipalityType": "BUYUKSEHIR",
                        "consumptionM3": 10,
                        "waterUnitPrice": 20,
                        "wastewaterUnitPrice": 10
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "waterCharge": 200,
                            "wastewaterCharge": 100,
                            "environmentalTax": 40,
                            "vat": 12,
                            "total": 352
                        }
                    },
                    "tolerance": 1e-8
                },
                {
                    "caseId": "water-missing-type",
                    "scenario": "adversarial",
                    "input": {
                        "consumptionM3": 10,
                        "waterUnitPrice": 20,
                        "wastewaterUnitPrice": 10
                    },
                    "expected": {
                        "ok": false,
                        "field": "municipalityType",
                        "errorCode": "REQUIRED"
                    },
                    "tolerance": 1e-9
                }
            ]
        },
        "lastReviewed": "2026-08-25"
    },
    {
        "toolId": "title-deed-fee",
        "slug": "tapu-harci-hesaplama",
        "path": "/arac-emlak-yasam/tapu-harci-hesaplama",
        "category": {
            "id": "arac-emlak-yasam",
            "label": "Araç, Emlak ve Yaşam"
        },
        "classification": "remote-config",
        "engineId": "title-deed-fee-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-01-01",
        "effectiveTo": "2026-12-31",
        "minAppVersion": "0.4.0",
        "updateCadence": {
            "mode": "scheduled-source-review",
            "days": 129,
            "reviewAfter": "2027-01-01",
            "specialMonths": [],
            "note": "Belirlenen tarihte resmî kaynak incelenir; değişiklik varsa config, yoksa inceleme tarihi yenilenir."
        },
        "sources": [
            {
                "sourceId": "tkgm-revolving-fund-fees-2026",
                "sourceGroupId": "tr_title_deed_fees",
                "title": "2026 tapu döner sermaye tarifesi",
                "publisher": "Tapu ve Kadastro Genel Müdürlüğü",
                "url": "https://cms-api.tkgm.gov.tr/media-library/7dc470ccb8334d68b73b5133fc1b7307.pdf",
                "kind": "official-government",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "alıcı ve satıcı harcı",
                    "döner sermaye",
                    "yöresel katsayı"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "konut",
                            "label": "Konut"
                        },
                        {
                            "value": "isyeri",
                            "label": "İşyeri"
                        },
                        {
                            "value": "arsa",
                            "label": "Arsa / Arazi"
                        }
                    ],
                    "visibleWhen": null,
                    "id": "propertyType",
                    "type": "select",
                    "label": "Taşınmaz türü",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "TL",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 2,
                    "options": [],
                    "visibleWhen": null,
                    "id": "salePrice",
                    "type": "number",
                    "label": "Satış bedeli",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "TL",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 2,
                    "options": [],
                    "visibleWhen": null,
                    "id": "propertyTaxValue",
                    "type": "number",
                    "label": "Emlak vergisi değeri",
                    "required": false
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "TL",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 2,
                    "options": [],
                    "visibleWhen": null,
                    "id": "revolvingFundCost",
                    "type": "number",
                    "label": "Döner sermaye bedeli",
                    "required": false
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "TL",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 2,
                    "options": [],
                    "visibleWhen": null,
                    "id": "otherDocumentedCosts",
                    "type": "number",
                    "label": "Diğer belgeli giderler",
                    "required": false
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "standart",
                            "label": "Yarı yarıya"
                        },
                        {
                            "value": "alici",
                            "label": "Tamamı alıcı"
                        },
                        {
                            "value": "satici",
                            "label": "Tamamı satıcı"
                        }
                    ],
                    "visibleWhen": null,
                    "id": "allocation",
                    "type": "radio",
                    "label": "Harcın paylaşımı",
                    "required": true
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "propertyType",
                    "type": "text",
                    "label": "Taşınmaz türü",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "salePrice",
                    "type": "money",
                    "label": "Satış bedeli",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "propertyTaxValue",
                    "type": "money",
                    "label": "Emlak vergisi değeri",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "revolvingFundCost",
                    "type": "money",
                    "label": "Döner sermaye bedeli",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "otherDocumentedCosts",
                    "type": "money",
                    "label": "Diğer belgeli giderler",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "allocation",
                    "type": "text",
                    "label": "Paylaşım",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "propertyTypeLabel",
                    "type": "text",
                    "label": "Taşınmaz türü açıklaması",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "assessmentBase",
                    "type": "money",
                    "label": "Harç matrahı",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "assessmentBaseReason",
                    "type": "text",
                    "label": "Matrah açıklaması",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "totalFee",
                    "type": "money",
                    "label": "Toplam tapu harcı",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "buyerFee",
                    "type": "money",
                    "label": "Alıcı harcı",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "sellerFee",
                    "type": "money",
                    "label": "Satıcı harcı",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "totalCost",
                    "type": "money",
                    "label": "Toplam işlem maliyeti",
                    "unit": null,
                    "nullable": false,
                    "prominent": true
                }
            ]
        },
        "content": {
            "title": "Tapu Harcı Hesaplama",
            "shortTitle": "Tapu Harcı Hesaplama",
            "summary": "Satış bedeli ile emlak vergisi değerinin büyüğüne toplam %4 harç uygular; seçilen paylaşımı ve belgeli giderleri ayrı gösterir.",
            "method": "Satış bedeli ile emlak vergisi değerinin büyüğüne toplam %4 harç uygular; seçilen paylaşımı ve belgeli giderleri ayrı gösterir.",
            "limitations": [
                "Tarife, vergi ve mevzuat değerleri kişisel işlem tarihinde ayrıca doğrulanmalıdır."
            ],
            "faqs": [
                {
                    "question": "Sonuç kesin veya resmî bir tutar mıdır?",
                    "answer": "Hayır. Sonuç, gösterilen girdiler ve tarihli kaynak kaydıyla üretilen yardımcı bir hesaplamadır."
                }
            ],
            "sourcePanel": {
                "title": "Kaynak ve inceleme durumu",
                "summary": "Formül ve oranlar belirlenen aralıkta resmî kaynaktan yeniden incelenir. Değişiklik yoksa inceleme tarihi, değişiklik varsa config sürümü yenilenir.",
                "sourceIds": [
                    "tkgm-revolving-fund-fees-2026"
                ],
                "notes": [
                    "Tarife, vergi ve mevzuat değerleri kişisel işlem tarihinde ayrıca doğrulanmalıdır."
                ]
            }
        },
        "config": {
            "kind": "title-deed-fee-v1",
            "calculatorId": "arac-emlak-yasam.tapu-harci-hesaplama",
            "amount": [
                0,
                1000000000
            ],
            "optionalCost": [
                0,
                1000000000
            ],
            "totalFeeRate": 0.04,
            "policyLifecycle": {
                "configVersion": "1.0.0",
                "sourceIds": [
                    "tkgm-revolving-fund-fees-2026"
                ],
                "reviewedOn": "2026-08-25",
                "effectiveFrom": "2026-01-01",
                "effectiveTo": "2026-12-31",
                "reviewAfter": "2027-01-01"
            }
        },
        "testVectors": {
            "kind": "vehicle-finance-oracle-v1",
            "cases": [
                {
                    "caseId": "deed-higher-tax-value",
                    "scenario": "positive",
                    "input": {
                        "propertyType": "konut",
                        "salePrice": 1000000,
                        "propertyTaxValue": 1200000,
                        "revolvingFundCost": 2000,
                        "otherDocumentedCosts": 1000,
                        "allocation": "standart"
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "assessmentBase": 1200000,
                            "totalFee": 48000,
                            "buyerFee": 24000,
                            "sellerFee": 24000,
                            "totalCost": 51000
                        }
                    },
                    "tolerance": 1e-8
                },
                {
                    "caseId": "deed-missing-sale",
                    "scenario": "adversarial",
                    "input": {
                        "propertyType": "konut",
                        "allocation": "standart"
                    },
                    "expected": {
                        "ok": false,
                        "field": "salePrice",
                        "errorCode": "REQUIRED"
                    },
                    "tolerance": 1e-9
                }
            ]
        },
        "lastReviewed": "2026-08-25"
    },
    {
        "toolId": "traffic-fine",
        "slug": "trafik-cezasi-hesaplama",
        "path": "/arac-emlak-yasam/trafik-cezasi-hesaplama",
        "category": {
            "id": "arac-emlak-yasam",
            "label": "Araç, Emlak ve Yaşam"
        },
        "classification": "remote-config",
        "engineId": "traffic-fine-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-01-01",
        "effectiveTo": "2026-12-31",
        "minAppVersion": "0.4.0",
        "updateCadence": {
            "mode": "scheduled-source-review",
            "days": 7,
            "reviewAfter": "2026-09-01",
            "specialMonths": [],
            "note": "Belirlenen tarihte resmî kaynak incelenir; değişiklik varsa config, yoksa inceleme tarihi yenilenir."
        },
        "sources": [
            {
                "sourceId": "egm-traffic-fines-2026",
                "sourceGroupId": "tr_traffic_fines",
                "title": "2026 trafik ceza rehberi",
                "publisher": "Emniyet Genel Müdürlüğü",
                "url": "https://trafik.gov.tr/kurumlar/trafik.gov.tr/03-Mevzuat/2026-Yili-Trafik-Ceza-Rehberi.pdf",
                "kind": "official-government",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "ceza tutarı",
                    "erken ödeme",
                    "yürürlük"
                ]
            },
            {
                "sourceId": "gib-traffic-fine-payment-current",
                "sourceGroupId": "tr_late_interest_rates",
                "title": "Trafik cezası ödeme ve gecikme uygulaması",
                "publisher": "Gelir İdaresi Başkanlığı",
                "url": "https://gib.gov.tr/vergi-konulari/1/7_diger_kazanc_ve_iratlar/7/45/140",
                "kind": "official-government",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "erken ödeme indirimi",
                    "gecikme zammı",
                    "ödeme süresi"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "TL",
                    "minimum": 1,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 2,
                    "options": [],
                    "visibleWhen": null,
                    "id": "fineAmount",
                    "type": "number",
                    "label": "Ceza tutarı",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "erken",
                            "label": "Erken ödeme"
                        },
                        {
                            "value": "gecikmeli",
                            "label": "Gecikmeli ödeme"
                        }
                    ],
                    "visibleWhen": null,
                    "id": "paymentStatus",
                    "type": "radio",
                    "label": "Ödeme zamanı",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "ay",
                    "minimum": 0,
                    "maximum": 120,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "delayMonths",
                    "type": "number",
                    "label": "Gecikme ayı",
                    "required": false
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "gün",
                    "minimum": 0,
                    "maximum": 30,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "delayDays",
                    "type": "number",
                    "label": "Artan gecikme günü",
                    "required": false
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "fineAmount",
                    "type": "money",
                    "label": "Ceza tutarı",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "paymentStatus",
                    "type": "text",
                    "label": "Ödeme zamanı",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "delayMonths",
                    "type": "number",
                    "label": "Gecikme ayı",
                    "unit": "ay",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "delayDays",
                    "type": "number",
                    "label": "Artan gecikme günü",
                    "unit": "gün",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "earlyPaymentAmount",
                    "type": "money",
                    "label": "Erken ödeme tutarı",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "earlyPaymentSaving",
                    "type": "money",
                    "label": "Erken ödeme indirimi",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "lateSurcharge",
                    "type": "money",
                    "label": "Gecikme zammı",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "payableAmount",
                    "type": "money",
                    "label": "Ödenecek tutar",
                    "unit": null,
                    "nullable": false,
                    "prominent": true
                }
            ]
        },
        "content": {
            "title": "Trafik Cezası Ödeme Hesaplama",
            "shortTitle": "Trafik Cezası Ödeme Hesaplama",
            "summary": "Ceza tutarına erken ödeme indirimini veya ay ve artan gün üzerinden gecikme zammını uygular.",
            "method": "Ceza tutarına erken ödeme indirimini veya ay ve artan gün üzerinden gecikme zammını uygular.",
            "limitations": [
                "Tarife, vergi ve mevzuat değerleri kişisel işlem tarihinde ayrıca doğrulanmalıdır."
            ],
            "faqs": [
                {
                    "question": "Sonuç kesin veya resmî bir tutar mıdır?",
                    "answer": "Hayır. Sonuç, gösterilen girdiler ve tarihli kaynak kaydıyla üretilen yardımcı bir hesaplamadır."
                }
            ],
            "sourcePanel": {
                "title": "Kaynak ve inceleme durumu",
                "summary": "Formül ve oranlar belirlenen aralıkta resmî kaynaktan yeniden incelenir. Değişiklik yoksa inceleme tarihi, değişiklik varsa config sürümü yenilenir.",
                "sourceIds": [
                    "egm-traffic-fines-2026",
                    "gib-traffic-fine-payment-current"
                ],
                "notes": [
                    "Tarife, vergi ve mevzuat değerleri kişisel işlem tarihinde ayrıca doğrulanmalıdır."
                ]
            }
        },
        "config": {
            "kind": "traffic-fine-v1",
            "calculatorId": "arac-emlak-yasam.trafik-cezasi-hesaplama",
            "fineAmount": [
                1,
                1000000000
            ],
            "delayMonths": [
                0,
                120
            ],
            "delayDays": [
                0,
                30
            ],
            "policy": {
                "earlyPaymentDiscountRate": 0.25,
                "monthlyLateRate": 0.037,
                "daysPerLateMonth": 30
            },
            "policyLifecycle": {
                "configVersion": "1.0.0",
                "sourceIds": [
                    "egm-traffic-fines-2026",
                    "gib-traffic-fine-payment-current"
                ],
                "reviewedOn": "2026-08-25",
                "effectiveFrom": "2026-01-01",
                "effectiveTo": "2026-12-31",
                "reviewAfter": "2026-09-01"
            }
        },
        "testVectors": {
            "kind": "vehicle-finance-oracle-v1",
            "cases": [
                {
                    "caseId": "fine-early-discount",
                    "scenario": "positive",
                    "input": {
                        "fineAmount": 1000,
                        "paymentStatus": "erken"
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "earlyPaymentSaving": 250,
                            "earlyPaymentAmount": 750,
                            "lateSurcharge": 0,
                            "payableAmount": 750
                        }
                    },
                    "tolerance": 1e-8
                },
                {
                    "caseId": "fine-delay-without-duration",
                    "scenario": "adversarial",
                    "input": {
                        "fineAmount": 1000,
                        "paymentStatus": "gecikmeli",
                        "delayMonths": 0,
                        "delayDays": 0
                    },
                    "expected": {
                        "ok": false,
                        "field": "delayMonths",
                        "errorCode": "OUT_OF_RANGE"
                    },
                    "tolerance": 1e-9
                }
            ]
        },
        "lastReviewed": "2026-08-25"
    },
    {
        "toolId": "valuable-housing-tax",
        "slug": "degerli-konut-vergisi-hesaplama",
        "path": "/bireysel-finans/degerli-konut-vergisi-hesaplama",
        "category": {
            "id": "bireysel-finans",
            "label": "Bireysel Finans"
        },
        "classification": "remote-config",
        "engineId": "valuable-housing-tax-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-01-01",
        "effectiveTo": "2026-12-31",
        "minAppVersion": "0.4.0",
        "updateCadence": {
            "mode": "scheduled-source-review",
            "days": 129,
            "reviewAfter": "2027-01-01",
            "specialMonths": [],
            "note": "Belirlenen tarihte resmî kaynak incelenir; değişiklik varsa config, yoksa inceleme tarihi yenilenir."
        },
        "sources": [
            {
                "sourceId": "gib-valuable-housing-tax-guide-2026",
                "sourceGroupId": "tr_valuable_housing_tax",
                "title": "2026 değerli konut vergisi rehberi",
                "publisher": "Gelir İdaresi Başkanlığı",
                "url": "https://cdn.gib.gov.tr/api/gibportal-file/file/getFile?objectKey=DUYURU%2FUNIVERSAL%2F2026%2F2026_Degerli_Konut_Vergisi_Rehberi.pdf",
                "kind": "official-government",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "istisna",
                    "dilim",
                    "binde oran",
                    "yuvarlama"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "tek",
                            "label": "Türkiye’de tek mesken"
                        },
                        {
                            "value": "coklu",
                            "label": "Birden fazla mesken"
                        }
                    ],
                    "visibleWhen": null,
                    "id": "residenceStatus",
                    "type": "radio",
                    "label": "Mesken durumu",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "TL",
                    "minimum": 1,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 2,
                    "options": [],
                    "visibleWhen": null,
                    "id": "buildingTaxValue",
                    "type": "number",
                    "label": "Bina vergi değeri",
                    "required": true
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "residenceStatus",
                    "type": "text",
                    "label": "Mesken durumu",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "buildingTaxValue",
                    "type": "money",
                    "label": "Bina vergi değeri",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "roundedBuildingTaxValue",
                    "type": "money",
                    "label": "Yuvarlanmış bina vergi değeri",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "exempt",
                    "type": "boolean",
                    "label": "İstisna kapsamında",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "exemptionReason",
                    "type": "text",
                    "label": "İstisna açıklaması",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "taxableExcess",
                    "type": "money",
                    "label": "Vergilendirilebilir aşan tutar",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "marginalRatePerThousand",
                    "type": "number",
                    "label": "Marjinal binde oran",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "annualTax",
                    "type": "money",
                    "label": "Yıllık vergi",
                    "unit": null,
                    "nullable": false,
                    "prominent": true
                }
            ]
        },
        "content": {
            "title": "Değerli Konut Vergisi Hesaplama",
            "shortTitle": "Değerli Konut Vergisi Hesaplama",
            "summary": "Bina vergi değerini resmî yuvarlama birimine indirger, tek mesken istisnasını ve artan oranlı 2026 tarifesini uygular.",
            "method": "Bina vergi değerini resmî yuvarlama birimine indirger, tek mesken istisnasını ve artan oranlı 2026 tarifesini uygular.",
            "limitations": [
                "Tarife, vergi ve mevzuat değerleri kişisel işlem tarihinde ayrıca doğrulanmalıdır."
            ],
            "faqs": [
                {
                    "question": "Sonuç kesin veya resmî bir tutar mıdır?",
                    "answer": "Hayır. Sonuç, gösterilen girdiler ve tarihli kaynak kaydıyla üretilen yardımcı bir hesaplamadır."
                }
            ],
            "sourcePanel": {
                "title": "Kaynak ve inceleme durumu",
                "summary": "Formül ve oranlar belirlenen aralıkta resmî kaynaktan yeniden incelenir. Değişiklik yoksa inceleme tarihi, değişiklik varsa config sürümü yenilenir.",
                "sourceIds": [
                    "gib-valuable-housing-tax-guide-2026"
                ],
                "notes": [
                    "Tarife, vergi ve mevzuat değerleri kişisel işlem tarihinde ayrıca doğrulanmalıdır."
                ]
            }
        },
        "config": {
            "kind": "valuable-housing-tax-v1",
            "calculatorId": "bireysel-finans.degerli-konut-vergisi-hesaplama",
            "buildingTaxValue": [
                1,
                1000000000
            ],
            "policy": {
                "exemptionThreshold": 17711000,
                "assessmentRoundingUnit": 1000,
                "brackets": [
                    {
                        "lowerInclusive": 17711000,
                        "upperInclusive": 26567000,
                        "ratePerThousand": 3,
                        "baseTax": 0
                    },
                    {
                        "lowerInclusive": 26567000,
                        "upperInclusive": 35425000,
                        "ratePerThousand": 6,
                        "baseTax": 26568
                    },
                    {
                        "lowerInclusive": 35425000,
                        "upperInclusive": null,
                        "ratePerThousand": 10,
                        "baseTax": 79716
                    }
                ]
            },
            "policyLifecycle": {
                "configVersion": "1.0.0",
                "sourceIds": [
                    "gib-valuable-housing-tax-guide-2026"
                ],
                "reviewedOn": "2026-08-25",
                "effectiveFrom": "2026-01-01",
                "effectiveTo": "2026-12-31",
                "reviewAfter": "2027-01-01"
            }
        },
        "testVectors": {
            "kind": "vehicle-finance-oracle-v1",
            "cases": [
                {
                    "caseId": "valuable-house-second-bracket",
                    "scenario": "positive",
                    "input": {
                        "residenceStatus": "coklu",
                        "buildingTaxValue": 30000000
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "roundedBuildingTaxValue": 30000000,
                            "taxableExcess": 12289000,
                            "marginalRatePerThousand": 6,
                            "annualTax": 47166
                        }
                    },
                    "tolerance": 1e-8
                },
                {
                    "caseId": "valuable-house-missing-value",
                    "scenario": "adversarial",
                    "input": {
                        "residenceStatus": "coklu"
                    },
                    "expected": {
                        "ok": false,
                        "field": "buildingTaxValue",
                        "errorCode": "REQUIRED"
                    },
                    "tolerance": 1e-9
                }
            ]
        },
        "lastReviewed": "2026-08-25"
    },
    {
        "toolId": "income-tax",
        "slug": "gelir-vergisi-hesaplama",
        "path": "/bireysel-finans/gelir-vergisi-hesaplama",
        "category": {
            "id": "bireysel-finans",
            "label": "Bireysel Finans"
        },
        "classification": "remote-config",
        "engineId": "income-tax-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-01-01",
        "effectiveTo": "2026-12-31",
        "minAppVersion": "0.4.0",
        "updateCadence": {
            "mode": "scheduled-source-review",
            "days": 129,
            "reviewAfter": "2027-01-01",
            "specialMonths": [],
            "note": "Belirlenen tarihte resmî kaynak incelenir; değişiklik varsa config, yoksa inceleme tarihi yenilenir."
        },
        "sources": [
            {
                "sourceId": "gib-income-tax-2026",
                "sourceGroupId": "tr_income_tax_tariff",
                "title": "2026 gelir vergisi tarifesi",
                "publisher": "Gelir İdaresi Başkanlığı",
                "url": "https://cdn.gib.gov.tr/api/gibportal-file/file/getFileResources?objectKey=arsiv%2Fyardim-kaynaklar%2Fyararli-bilgiler%2Fgelir-vergisi-tarifeleri%2Fgelir-vergisi-tarifesi-2026.pdf",
                "kind": "official-government",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "ücret dilimleri",
                    "diğer gelir dilimleri",
                    "oranlar"
                ]
            },
            {
                "sourceId": "gib-wage-income-guide-2026",
                "sourceGroupId": "tr_income_tax_tariff",
                "title": "2026 ücret geliri rehberi",
                "publisher": "Gelir İdaresi Başkanlığı",
                "url": "https://intvrg.gib.gov.tr/hazirbeyan/assets/pdf/DUYURU_UNIVERSAL_2026_2026_Ucret_Geliri.pdf",
                "kind": "official-government",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "tek işveren",
                    "sonraki işveren",
                    "beyan sınırı"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "TL",
                    "minimum": 1,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 2,
                    "options": [],
                    "visibleWhen": null,
                    "id": "income",
                    "type": "number",
                    "label": "Gelir",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "ucret",
                            "label": "Ücret geliri"
                        },
                        {
                            "value": "diger",
                            "label": "Ücret dışı diğer gelir"
                        }
                    ],
                    "visibleWhen": null,
                    "id": "incomeType",
                    "type": "radio",
                    "label": "Gelir türü",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "yillik",
                            "label": "Yıllık"
                        },
                        {
                            "value": "aylik",
                            "label": "Aylık"
                        }
                    ],
                    "visibleWhen": null,
                    "id": "period",
                    "type": "radio",
                    "label": "Gelir dönemi",
                    "required": true
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "income",
                    "type": "money",
                    "label": "Girilen gelir",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "incomeType",
                    "type": "text",
                    "label": "Gelir türü",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "period",
                    "type": "text",
                    "label": "Dönem",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "annualIncome",
                    "type": "money",
                    "label": "Yıllık gelir",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "tax",
                    "type": "money",
                    "label": "Gelir vergisi",
                    "unit": null,
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "netIncome",
                    "type": "money",
                    "label": "Vergi sonrası gelir",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "averageRatePercent",
                    "type": "percentage",
                    "label": "Ortalama vergi oranı",
                    "unit": "%",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "marginalRatePercent",
                    "type": "percentage",
                    "label": "Marjinal vergi oranı",
                    "unit": "%",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "declarationReference",
                    "type": "text",
                    "label": "Beyan sınırı açıklaması",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                }
            ]
        },
        "content": {
            "title": "Gelir Vergisi Hesaplama",
            "shortTitle": "Gelir Vergisi Hesaplama",
            "summary": "Aylık girdiyi yıllığa çevirir; ücret ve diğer gelir için ayrı 2026 dilimlerini uygular ve güncel beyan sınırı referansını gösterir.",
            "method": "Aylık girdiyi yıllığa çevirir; ücret ve diğer gelir için ayrı 2026 dilimlerini uygular ve güncel beyan sınırı referansını gösterir.",
            "limitations": [
                "Mahsup, indirim, istisna ve birden fazla gelir kaynağı sonucu değiştirebilir; sonuç beyanname değildir."
            ],
            "faqs": [
                {
                    "question": "Sonuç kesin veya resmî bir tutar mıdır?",
                    "answer": "Hayır. Sonuç, gösterilen girdiler ve tarihli kaynak kaydıyla üretilen yardımcı bir hesaplamadır."
                }
            ],
            "sourcePanel": {
                "title": "Kaynak ve inceleme durumu",
                "summary": "Formül ve oranlar belirlenen aralıkta resmî kaynaktan yeniden incelenir. Değişiklik yoksa inceleme tarihi, değişiklik varsa config sürümü yenilenir.",
                "sourceIds": [
                    "gib-income-tax-2026",
                    "gib-wage-income-guide-2026"
                ],
                "notes": [
                    "Mahsup, indirim, istisna ve birden fazla gelir kaynağı sonucu değiştirebilir; sonuç beyanname değildir."
                ]
            }
        },
        "config": {
            "kind": "income-tax-v1",
            "calculatorId": "bireysel-finans.gelir-vergisi-hesaplama",
            "income": [
                1,
                1000000000
            ],
            "wageBrackets": [
                {
                    "upperLimit": 190000,
                    "rate": 0.15
                },
                {
                    "upperLimit": 400000,
                    "rate": 0.2
                },
                {
                    "upperLimit": 1500000,
                    "rate": 0.27
                },
                {
                    "upperLimit": 5300000,
                    "rate": 0.35
                },
                {
                    "upperLimit": null,
                    "rate": 0.4
                }
            ],
            "otherIncomeBrackets": [
                {
                    "upperLimit": 190000,
                    "rate": 0.15
                },
                {
                    "upperLimit": 400000,
                    "rate": 0.2
                },
                {
                    "upperLimit": 1000000,
                    "rate": 0.27
                },
                {
                    "upperLimit": 5300000,
                    "rate": 0.35
                },
                {
                    "upperLimit": null,
                    "rate": 0.4
                }
            ],
            "declarationThresholds": {
                "singleEmployerWage": 5300000,
                "subsequentEmployersWage": 400000,
                "aggregateWage": 5300000,
                "otherIncome": 1000000
            },
            "policyLifecycle": {
                "configVersion": "1.0.0",
                "sourceIds": [
                    "gib-income-tax-2026",
                    "gib-wage-income-guide-2026"
                ],
                "reviewedOn": "2026-08-25",
                "effectiveFrom": "2026-01-01",
                "effectiveTo": "2026-12-31",
                "reviewAfter": "2027-01-01"
            }
        },
        "testVectors": {
            "kind": "vehicle-finance-oracle-v1",
            "cases": [
                {
                    "caseId": "income-other-500k",
                    "scenario": "positive",
                    "input": {
                        "income": 500000,
                        "incomeType": "diger",
                        "period": "yillik"
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "annualIncome": 500000,
                            "tax": 97500,
                            "netIncome": 402500,
                            "marginalRatePercent": 27
                        }
                    },
                    "tolerance": 1e-8
                },
                {
                    "caseId": "income-missing-type",
                    "scenario": "adversarial",
                    "input": {
                        "income": 500000,
                        "period": "yillik"
                    },
                    "expected": {
                        "ok": false,
                        "field": "incomeType",
                        "errorCode": "REQUIRED"
                    },
                    "tolerance": 1e-9
                }
            ]
        },
        "lastReviewed": "2026-08-25"
    },
    {
        "toolId": "unemployment-benefit",
        "slug": "issizlik-maasi-hesaplama",
        "path": "/bireysel-finans/issizlik-maasi-hesaplama",
        "category": {
            "id": "bireysel-finans",
            "label": "Bireysel Finans"
        },
        "classification": "remote-config",
        "engineId": "unemployment-benefit-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-01-01",
        "effectiveTo": "2026-12-31",
        "minAppVersion": "0.4.0",
        "updateCadence": {
            "mode": "scheduled-source-review",
            "days": 129,
            "reviewAfter": "2027-01-01",
            "specialMonths": [],
            "note": "Belirlenen tarihte resmî kaynak incelenir; değişiklik varsa config, yoksa inceleme tarihi yenilenir."
        },
        "sources": [
            {
                "sourceId": "iskur-unemployment-insurance-services-current",
                "sourceGroupId": "tr_unemployment_benefit_manual",
                "title": "İşsizlik sigortası hizmetleri",
                "publisher": "İŞKUR",
                "url": "https://media.iskur.gov.tr/48434/issizlik-sigortasi-hizmetleri.pdf",
                "kind": "official-government",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "prim günü",
                    "son 120 gün",
                    "irade dışı iş kaybı",
                    "ödenek süresi"
                ]
            },
            {
                "sourceId": "sgk-pec-2026",
                "sourceGroupId": "tr_labor_sgk_annual",
                "title": "2026 prime esas kazanç tutarları",
                "publisher": "Sosyal Güvenlik Kurumu",
                "url": "https://www.sgk.gov.tr/Content/Post/2e0c9e1a-2cfe-4456-af10-49d3de0c58ba/Prime-Esas-Kazanc-Miktarlari-2026-01-14-10-35-39",
                "kind": "official-government",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "asgari ücret",
                    "prime esas kazanç",
                    "dönem"
                ]
            },
            {
                "sourceId": "csgb-wage-statistics-2026-07",
                "sourceGroupId": "tr_labor_sgk_annual",
                "title": "Temmuz 2026 ücret istatistikleri",
                "publisher": "Çalışma ve Sosyal Güvenlik Bakanlığı",
                "url": "https://www.csgb.gov.tr/yayinlar/calisma-hayati-istatistikleri-e-bulteni/temmuz-2026/ucret-ve-sendikal-istatistikler.html",
                "kind": "official-government",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "brüt asgari ücret",
                    "yürürlük dönemi"
                ]
            },
            {
                "sourceId": "gib-stamp-tax-current",
                "sourceGroupId": "tr_stamp_tax",
                "title": "Güncel damga vergisi oran ve tutarları",
                "publisher": "Gelir İdaresi Başkanlığı",
                "url": "https://cdn.gib.gov.tr/api/gibportal-file/file/getFileResources?objectKey=arsiv%2Fyardim-kaynaklar%2Fyararli-bilgiler%2Fvergi-beyannamelerinde-uygulanan-damga-vergisi-tutarlari.pdf",
                "kind": "official-government",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "damga vergisi oranı",
                    "yürürlük"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "TL",
                    "minimum": 1,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 2,
                    "options": [],
                    "visibleWhen": null,
                    "id": "averageGrossSalary",
                    "type": "number",
                    "label": "Son dört aylık ortalama brüt ücret",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "gün",
                    "minimum": 600,
                    "maximum": 10000,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "premiumDays",
                    "type": "number",
                    "label": "Son üç yıldaki işsizlik sigortası prim günü",
                    "required": true
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "averageGrossSalary",
                    "type": "money",
                    "label": "Ortalama brüt ücret",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "premiumDays",
                    "type": "number",
                    "label": "Prim günü",
                    "unit": "gün",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "grossMonthlyBenefit",
                    "type": "money",
                    "label": "Aylık brüt ödenek",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "maximumGrossBenefit",
                    "type": "money",
                    "label": "Aylık üst sınır",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "stampTax",
                    "type": "money",
                    "label": "Damga vergisi",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "netMonthlyBenefit",
                    "type": "money",
                    "label": "Aylık net ödenek",
                    "unit": null,
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "benefitMonths",
                    "type": "number",
                    "label": "Ödenek süresi",
                    "unit": "ay",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "benefitDays",
                    "type": "number",
                    "label": "Ödenek süresi",
                    "unit": "gün",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "totalNetBenefit",
                    "type": "money",
                    "label": "Toplam net ödenek",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "eligibilityScope",
                    "type": "text",
                    "label": "Hak kazanma kapsamı",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                }
            ]
        },
        "content": {
            "title": "İşsizlik Ödeneği Hesaplama",
            "shortTitle": "İşsizlik Ödeneği Hesaplama",
            "summary": "Son dört aylık ortalama brüt ücretin %40'ını, brüt asgari ücretin %80'i tavanıyla sınırlar; damga vergisini ve prim gününe göre süreyi hesaplar.",
            "method": "Son dört aylık ortalama brüt ücretin %40'ını, brüt asgari ücretin %80'i tavanıyla sınırlar; damga vergisini ve prim gününe göre süreyi hesaplar.",
            "limitations": [
                "Araç yalnız tutar ve süre senaryosu üretir; son 120 gün hizmet akdine tabi olma, son üç yıl primi, 30 gün başvuru ve irade dışı iş kaybı şartlarının tamamı İŞKUR tarafından değerlendirilir."
            ],
            "faqs": [
                {
                    "question": "Sonuç kesin veya resmî bir tutar mıdır?",
                    "answer": "Hayır. Sonuç, gösterilen girdiler ve tarihli kaynak kaydıyla üretilen yardımcı bir hesaplamadır."
                }
            ],
            "sourcePanel": {
                "title": "Kaynak ve inceleme durumu",
                "summary": "Formül ve oranlar belirlenen aralıkta resmî kaynaktan yeniden incelenir. Değişiklik yoksa inceleme tarihi, değişiklik varsa config sürümü yenilenir.",
                "sourceIds": [
                    "iskur-unemployment-insurance-services-current",
                    "sgk-pec-2026",
                    "csgb-wage-statistics-2026-07",
                    "gib-stamp-tax-current"
                ],
                "notes": [
                    "Araç yalnız tutar ve süre senaryosu üretir; son 120 gün hizmet akdine tabi olma, son üç yıl primi, 30 gün başvuru ve irade dışı iş kaybı şartlarının tamamı İŞKUR tarafından değerlendirilir."
                ]
            }
        },
        "config": {
            "kind": "unemployment-benefit-v1",
            "calculatorId": "bireysel-finans.issizlik-maasi-hesaplama",
            "averageGrossSalary": [
                1,
                1000000000
            ],
            "premiumDays": [
                600,
                10000
            ],
            "policy": {
                "grossMinimumWage": 33030,
                "benefitRate": 0.4,
                "maximumWageRate": 0.8,
                "stampTaxRate": 0.00759,
                "durationRules": [
                    {
                        "minimumPremiumDays": 600,
                        "benefitMonths": 6
                    },
                    {
                        "minimumPremiumDays": 900,
                        "benefitMonths": 8
                    },
                    {
                        "minimumPremiumDays": 1080,
                        "benefitMonths": 10
                    }
                ],
                "requiresLast120Days": true,
                "requiresInvoluntaryJobLoss": true
            },
            "policyLifecycle": {
                "configVersion": "1.0.0",
                "sourceIds": [
                    "iskur-unemployment-insurance-services-current",
                    "sgk-pec-2026",
                    "csgb-wage-statistics-2026-07",
                    "gib-stamp-tax-current"
                ],
                "reviewedOn": "2026-08-25",
                "effectiveFrom": "2026-01-01",
                "effectiveTo": "2026-12-31",
                "reviewAfter": "2027-01-01"
            }
        },
        "testVectors": {
            "kind": "vehicle-finance-oracle-v1",
            "cases": [
                {
                    "caseId": "unemployment-600-days",
                    "scenario": "threshold",
                    "input": {
                        "averageGrossSalary": 50000,
                        "premiumDays": 600
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "grossMonthlyBenefit": 20000,
                            "stampTax": 151.8,
                            "netMonthlyBenefit": 19848.2,
                            "benefitMonths": 6,
                            "benefitDays": 180,
                            "totalNetBenefit": 119089.2
                        }
                    },
                    "tolerance": 1e-8
                },
                {
                    "caseId": "unemployment-missing-days",
                    "scenario": "adversarial",
                    "input": {
                        "averageGrossSalary": 50000
                    },
                    "expected": {
                        "ok": false,
                        "field": "premiumDays",
                        "errorCode": "REQUIRED"
                    },
                    "tolerance": 1e-9
                }
            ]
        },
        "lastReviewed": "2026-08-25"
    },
    {
        "toolId": "severance-notice-pay",
        "slug": "kidem-ihbar-tazminati-hesaplama",
        "path": "/bireysel-finans/kidem-ihbar-tazminati-hesaplama",
        "category": {
            "id": "bireysel-finans",
            "label": "Bireysel Finans"
        },
        "classification": "remote-config",
        "engineId": "severance-notice-pay-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-01-01",
        "effectiveTo": "2026-12-31",
        "minAppVersion": "0.4.0",
        "updateCadence": {
            "mode": "scheduled-source-review",
            "days": 129,
            "reviewAfter": "2027-01-01",
            "specialMonths": [],
            "note": "Belirlenen tarihte resmî kaynak incelenir; değişiklik varsa config, yoksa inceleme tarihi yenilenir."
        },
        "sources": [
            {
                "sourceId": "hmb-severance-cap-2026-07",
                "sourceGroupId": "tr_severance_cap",
                "title": "Temmuz 2026 kıdem tazminatı tavanı",
                "publisher": "Hazine ve Maliye Bakanlığı",
                "url": "https://ms.hmb.gov.tr/uploads/sites/23/2026/07/2026-Yili-Temmuz-Ayina-Ait-Mali-ve-Sosyal-Haklara-Iliskin-Genelge-8c09eafa597c3878.pdf",
                "kind": "official-government",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "kıdem tazminatı tavanı",
                    "yürürlük dönemi"
                ]
            },
            {
                "sourceId": "sgk-pec-2026",
                "sourceGroupId": "tr_labor_sgk_annual",
                "title": "2026 prime esas kazanç tutarları",
                "publisher": "Sosyal Güvenlik Kurumu",
                "url": "https://www.sgk.gov.tr/Content/Post/2e0c9e1a-2cfe-4456-af10-49d3de0c58ba/Prime-Esas-Kazanc-Miktarlari-2026-01-14-10-35-39",
                "kind": "official-government",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "asgari ücret",
                    "prime esas kazanç",
                    "dönem"
                ]
            },
            {
                "sourceId": "csgb-wage-statistics-2026-07",
                "sourceGroupId": "tr_labor_sgk_annual",
                "title": "Temmuz 2026 ücret istatistikleri",
                "publisher": "Çalışma ve Sosyal Güvenlik Bakanlığı",
                "url": "https://www.csgb.gov.tr/yayinlar/calisma-hayati-istatistikleri-e-bulteni/temmuz-2026/ucret-ve-sendikal-istatistikler.html",
                "kind": "official-government",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "brüt asgari ücret",
                    "yürürlük dönemi"
                ]
            },
            {
                "sourceId": "gib-stamp-tax-current",
                "sourceGroupId": "tr_stamp_tax",
                "title": "Güncel damga vergisi oran ve tutarları",
                "publisher": "Gelir İdaresi Başkanlığı",
                "url": "https://cdn.gib.gov.tr/api/gibportal-file/file/getFileResources?objectKey=arsiv%2Fyardim-kaynaklar%2Fyararli-bilgiler%2Fvergi-beyannamelerinde-uygulanan-damga-vergisi-tutarlari.pdf",
                "kind": "official-government",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "damga vergisi oranı",
                    "yürürlük"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [],
                    "visibleWhen": null,
                    "id": "employmentStartDate",
                    "type": "date",
                    "label": "İşe giriş tarihi",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [],
                    "visibleWhen": null,
                    "id": "employmentEndDate",
                    "type": "date",
                    "label": "İşten ayrılış tarihi",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "TL",
                    "minimum": 1,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 2,
                    "options": [],
                    "visibleWhen": null,
                    "id": "grossSalary",
                    "type": "number",
                    "label": "Aylık giydirilmiş brüt ücret",
                    "required": true
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "employmentStartDate",
                    "type": "text",
                    "label": "İşe giriş tarihi",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "employmentEndDate",
                    "type": "text",
                    "label": "İşten ayrılış tarihi",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "grossSalary",
                    "type": "money",
                    "label": "Brüt ücret",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "serviceDays",
                    "type": "number",
                    "label": "Hizmet süresi",
                    "unit": "gün",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "serviceYears",
                    "type": "number",
                    "label": "Hizmet süresi",
                    "unit": "yıl",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "completedYears",
                    "type": "number",
                    "label": "Tam yıl",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "remainingMonths",
                    "type": "number",
                    "label": "Artan ay",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "remainingDays",
                    "type": "number",
                    "label": "Artan gün",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "dailyGrossSalary",
                    "type": "money",
                    "label": "Günlük brüt ücret",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "appliedMonthlyCap",
                    "type": "money",
                    "label": "Uygulanan kıdem tavanı",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "severanceGross",
                    "type": "money",
                    "label": "Brüt kıdem tazminatı",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "noticeWeeks",
                    "type": "number",
                    "label": "İhbar süresi",
                    "unit": "hafta",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "noticeGross",
                    "type": "money",
                    "label": "Brüt ihbar tazminatı",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "stampTax",
                    "type": "money",
                    "label": "Damga vergisi",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "netBeforeNoticeIncomeTax",
                    "type": "money",
                    "label": "İhbar gelir vergisi öncesi net",
                    "unit": null,
                    "nullable": false,
                    "prominent": true
                }
            ]
        },
        "content": {
            "title": "Kıdem ve İhbar Tazminatı Hesaplama",
            "shortTitle": "Kıdem ve İhbar Tazminatı Hesaplama",
            "summary": "Takvim gününden hizmet süresini çıkarır; ayrılış tarihindeki kıdem tavanını, damga vergisini ve hizmet süresine bağlı ihbar haftasını uygular.",
            "method": "Takvim gününden hizmet süresini çıkarır; ayrılış tarihindeki kıdem tavanını, damga vergisini ve hizmet süresine bağlı ihbar haftasını uygular.",
            "limitations": [
                "Hak kazanma nedeni, giydirilmiş ücret unsurları ve ihbar gelir vergisi kişisel bordro ve hukuki değerlendirme gerektirir."
            ],
            "faqs": [
                {
                    "question": "Sonuç kesin veya resmî bir tutar mıdır?",
                    "answer": "Hayır. Sonuç, gösterilen girdiler ve tarihli kaynak kaydıyla üretilen yardımcı bir hesaplamadır."
                }
            ],
            "sourcePanel": {
                "title": "Kaynak ve inceleme durumu",
                "summary": "Formül ve oranlar belirlenen aralıkta resmî kaynaktan yeniden incelenir. Değişiklik yoksa inceleme tarihi, değişiklik varsa config sürümü yenilenir.",
                "sourceIds": [
                    "hmb-severance-cap-2026-07",
                    "sgk-pec-2026",
                    "csgb-wage-statistics-2026-07",
                    "gib-stamp-tax-current"
                ],
                "notes": [
                    "Hak kazanma nedeni, giydirilmiş ücret unsurları ve ihbar gelir vergisi kişisel bordro ve hukuki değerlendirme gerektirir."
                ]
            }
        },
        "config": {
            "kind": "severance-notice-pay-v1",
            "calculatorId": "bireysel-finans.kidem-ihbar-tazminati-hesaplama",
            "grossSalary": [
                1,
                1000000000
            ],
            "employmentYear": [
                2026,
                2026
            ],
            "policy": {
                "stampTaxRate": 0.00759,
                "daysPerSalaryMonth": 30,
                "averageDaysPerYear": 365.25,
                "capPeriods": [
                    {
                        "effectiveFrom": "2026-01-01",
                        "effectiveTo": "2026-06-30",
                        "monthlyCap": 64948.77
                    },
                    {
                        "effectiveFrom": "2026-07-01",
                        "effectiveTo": "2026-12-31",
                        "monthlyCap": 73729.87
                    }
                ],
                "noticeRules": [
                    {
                        "maximumServiceDaysExclusive": 180,
                        "noticeWeeks": 2
                    },
                    {
                        "maximumServiceDaysExclusive": 540,
                        "noticeWeeks": 4
                    },
                    {
                        "maximumServiceDaysExclusive": 1080,
                        "noticeWeeks": 6
                    },
                    {
                        "maximumServiceDaysExclusive": null,
                        "noticeWeeks": 8
                    }
                ]
            },
            "policyLifecycle": {
                "configVersion": "1.0.0",
                "sourceIds": [
                    "hmb-severance-cap-2026-07",
                    "sgk-pec-2026",
                    "csgb-wage-statistics-2026-07",
                    "gib-stamp-tax-current"
                ],
                "reviewedOn": "2026-08-25",
                "effectiveFrom": "2026-01-01",
                "effectiveTo": "2026-12-31",
                "reviewAfter": "2027-01-01"
            }
        },
        "testVectors": {
            "kind": "vehicle-finance-oracle-v1",
            "cases": [
                {
                    "caseId": "severance-2026-second-half",
                    "scenario": "positive",
                    "input": {
                        "employmentStartDate": "2025-01-01",
                        "employmentEndDate": "2026-07-01",
                        "grossSalary": 50000
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "serviceDays": 546,
                            "appliedMonthlyCap": 73729.87,
                            "noticeWeeks": 6,
                            "noticeGross": 70000
                        }
                    },
                    "tolerance": 1e-8
                },
                {
                    "caseId": "severance-reversed-dates",
                    "scenario": "adversarial",
                    "input": {
                        "employmentStartDate": "2026-08-01",
                        "employmentEndDate": "2026-07-01",
                        "grossSalary": 50000
                    },
                    "expected": {
                        "ok": false,
                        "field": "employmentEndDate",
                        "errorCode": "OUT_OF_RANGE"
                    },
                    "tolerance": 1e-9
                }
            ]
        },
        "lastReviewed": "2026-08-25"
    },
    {
        "toolId": "rental-income-tax",
        "slug": "kira-geliri-vergisi-hesaplama",
        "path": "/bireysel-finans/kira-geliri-vergisi-hesaplama",
        "category": {
            "id": "bireysel-finans",
            "label": "Bireysel Finans"
        },
        "classification": "remote-config",
        "engineId": "rental-income-tax-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-01-01",
        "effectiveTo": "2026-12-31",
        "minAppVersion": "0.4.0",
        "updateCadence": {
            "mode": "scheduled-source-review",
            "days": 129,
            "reviewAfter": "2027-01-01",
            "specialMonths": [],
            "note": "Belirlenen tarihte resmî kaynak incelenir; değişiklik varsa config, yoksa inceleme tarihi yenilenir."
        },
        "sources": [
            {
                "sourceId": "gib-rental-income-guide-2026",
                "sourceGroupId": "tr_rental_income_tax",
                "title": "2026 kira geliri rehberi",
                "publisher": "Gelir İdaresi Başkanlığı",
                "url": "https://cdn.gib.gov.tr/api/gibportal-file/file/getFile?objectKey=DUYURU%2FUNIVERSAL%2F2026%2F2026_Kira_Geliri.pdf",
                "kind": "official-government",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "konut istisnası",
                    "götürü gider",
                    "işyeri beyan sınırı"
                ]
            },
            {
                "sourceId": "gib-rental-income-explanation-current",
                "sourceGroupId": "tr_rental_income_tax",
                "title": "Kira geliri güncel açıklamaları",
                "publisher": "Gelir İdaresi Başkanlığı",
                "url": "https://gib.gov.tr/vergi-konulari/1_bireysel/9_kira_geliri/9",
                "kind": "official-government",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "tevkifat",
                    "beyan",
                    "istisna"
                ]
            },
            {
                "sourceId": "gib-income-tax-2026",
                "sourceGroupId": "tr_income_tax_tariff",
                "title": "2026 gelir vergisi tarifesi",
                "publisher": "Gelir İdaresi Başkanlığı",
                "url": "https://cdn.gib.gov.tr/api/gibportal-file/file/getFileResources?objectKey=arsiv%2Fyardim-kaynaklar%2Fyararli-bilgiler%2Fgelir-vergisi-tarifeleri%2Fgelir-vergisi-tarifesi-2026.pdf",
                "kind": "official-government",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "ücret dilimleri",
                    "diğer gelir dilimleri",
                    "oranlar"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "konut",
                            "label": "Konut kira geliri"
                        },
                        {
                            "value": "isyeri",
                            "label": "İşyeri kira geliri"
                        }
                    ],
                    "visibleWhen": null,
                    "id": "source",
                    "type": "radio",
                    "label": "Kira geliri türü",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "TL",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 2,
                    "options": [],
                    "visibleWhen": null,
                    "id": "grossRent",
                    "type": "number",
                    "label": "Yıllık brüt kira geliri",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "goturu",
                            "label": "Götürü gider (%15)"
                        },
                        {
                            "value": "gercek",
                            "label": "Gerçek gider"
                        }
                    ],
                    "visibleWhen": null,
                    "id": "expenseMethod",
                    "type": "radio",
                    "label": "Gider yöntemi",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "TL",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 2,
                    "options": [],
                    "visibleWhen": null,
                    "id": "actualExpense",
                    "type": "number",
                    "label": "Gerçek gider",
                    "required": false
                },
                {
                    "defaultValue": true,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [],
                    "visibleWhen": null,
                    "id": "applyHousingExemption",
                    "type": "boolean",
                    "label": "Konut istisnasını uygula",
                    "required": false
                },
                {
                    "defaultValue": false,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [],
                    "visibleWhen": null,
                    "id": "workplaceWithholding",
                    "type": "boolean",
                    "label": "İşyeri kirasında tevkifat yapıldı",
                    "required": false
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "source",
                    "type": "text",
                    "label": "Gelir türü",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "grossRent",
                    "type": "money",
                    "label": "Brüt kira geliri",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "expenseMethod",
                    "type": "text",
                    "label": "Gider yöntemi",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "actualExpense",
                    "type": "money",
                    "label": "Gerçek gider",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "applyHousingExemption",
                    "type": "boolean",
                    "label": "Konut istisnası uygulandı",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "workplaceWithholding",
                    "type": "boolean",
                    "label": "Tevkifat yapıldı",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "deductedExemption",
                    "type": "money",
                    "label": "İndirilen istisna",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "incomeAfterExemption",
                    "type": "money",
                    "label": "İstisna sonrası gelir",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "deductibleExpense",
                    "type": "money",
                    "label": "İndirilebilir gider",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "taxBase",
                    "type": "money",
                    "label": "Vergi matrahı",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "calculatedTax",
                    "type": "money",
                    "label": "Hesaplanan vergi",
                    "unit": null,
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "withheldTax",
                    "type": "money",
                    "label": "Tevkif edilen vergi",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "declarationRequiredByThisIncome",
                    "type": "boolean",
                    "label": "Bu gelir için beyan gerekir",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "declarationThreshold",
                    "type": "money",
                    "label": "Beyan sınırı",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "declarationGuidance",
                    "type": "text",
                    "label": "Beyan açıklaması",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                }
            ]
        },
        "content": {
            "title": "Kira Geliri Vergisi Hesaplama",
            "shortTitle": "Kira Geliri Vergisi Hesaplama",
            "summary": "Konut istisnasını, seçilen gider yöntemini ve işyeri tevkifatını ayrı uygular; tevkifatlı 400 bin TL ile tevkifatsız 22 bin TL beyan sınırlarını ayırır.",
            "method": "Konut istisnasını, seçilen gider yöntemini ve işyeri tevkifatını ayrı uygular; tevkifatlı 400 bin TL ile tevkifatsız 22 bin TL beyan sınırlarını ayırır.",
            "limitations": [
                "Başka gelirler, istisna kaybı, gerçek gider belgeleri ve mahsuplar beyan sonucunu değiştirebilir."
            ],
            "faqs": [
                {
                    "question": "Sonuç kesin veya resmî bir tutar mıdır?",
                    "answer": "Hayır. Sonuç, gösterilen girdiler ve tarihli kaynak kaydıyla üretilen yardımcı bir hesaplamadır."
                }
            ],
            "sourcePanel": {
                "title": "Kaynak ve inceleme durumu",
                "summary": "Formül ve oranlar belirlenen aralıkta resmî kaynaktan yeniden incelenir. Değişiklik yoksa inceleme tarihi, değişiklik varsa config sürümü yenilenir.",
                "sourceIds": [
                    "gib-rental-income-guide-2026",
                    "gib-rental-income-explanation-current",
                    "gib-income-tax-2026"
                ],
                "notes": [
                    "Başka gelirler, istisna kaybı, gerçek gider belgeleri ve mahsuplar beyan sonucunu değiştirebilir."
                ]
            }
        },
        "config": {
            "kind": "rental-income-tax-v1",
            "calculatorId": "bireysel-finans.kira-geliri-vergisi-hesaplama",
            "grossRent": [
                0,
                1000000000
            ],
            "actualExpense": [
                0,
                1000000000
            ],
            "policy": {
                "housingExemption": 58000,
                "lumpSumExpenseRate": 0.15,
                "workplaceWithholdingRate": 0.2,
                "withheldWorkplaceDeclarationThreshold": 400000,
                "nonWithheldWorkplaceDeclarationThreshold": 22000,
                "brackets": [
                    {
                        "upperLimit": 190000,
                        "rate": 0.15
                    },
                    {
                        "upperLimit": 400000,
                        "rate": 0.2
                    },
                    {
                        "upperLimit": 1000000,
                        "rate": 0.27
                    },
                    {
                        "upperLimit": 5300000,
                        "rate": 0.35
                    },
                    {
                        "upperLimit": null,
                        "rate": 0.4
                    }
                ]
            },
            "policyLifecycle": {
                "configVersion": "1.0.0",
                "sourceIds": [
                    "gib-rental-income-guide-2026",
                    "gib-rental-income-explanation-current",
                    "gib-income-tax-2026"
                ],
                "reviewedOn": "2026-08-25",
                "effectiveFrom": "2026-01-01",
                "effectiveTo": "2026-12-31",
                "reviewAfter": "2027-01-01"
            }
        },
        "testVectors": {
            "kind": "vehicle-finance-oracle-v1",
            "cases": [
                {
                    "caseId": "rental-housing-lump-sum",
                    "scenario": "positive",
                    "input": {
                        "source": "konut",
                        "grossRent": 100000,
                        "expenseMethod": "goturu",
                        "applyHousingExemption": true
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "deductedExemption": 58000,
                            "incomeAfterExemption": 42000,
                            "deductibleExpense": 6300,
                            "taxBase": 35700,
                            "calculatedTax": 5355,
                            "declarationRequiredByThisIncome": true
                        }
                    },
                    "tolerance": 1e-8
                },
                {
                    "caseId": "rental-expense-above-rent",
                    "scenario": "adversarial",
                    "input": {
                        "source": "konut",
                        "grossRent": 100000,
                        "expenseMethod": "gercek",
                        "actualExpense": 100001
                    },
                    "expected": {
                        "ok": false,
                        "field": "actualExpense",
                        "errorCode": "OUT_OF_RANGE"
                    },
                    "tolerance": 1e-9
                }
            ]
        },
        "lastReviewed": "2026-08-25"
    },
    {
        "toolId": "early-loan-closure",
        "slug": "kredi-erken-kapama-hesaplama",
        "path": "/bireysel-finans/kredi-erken-kapama-hesaplama",
        "category": {
            "id": "bireysel-finans",
            "label": "Bireysel Finans"
        },
        "classification": "remote-config",
        "engineId": "early-loan-closure-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2014-05-28",
        "effectiveTo": null,
        "minAppVersion": "0.4.0",
        "updateCadence": {
            "mode": "scheduled-source-review",
            "days": 31,
            "reviewAfter": "2026-09-25",
            "specialMonths": [],
            "note": "Belirlenen tarihte resmî kaynak incelenir; değişiklik varsa config, yoksa inceleme tarihi yenilenir."
        },
        "sources": [
            {
                "sourceId": "mevzuat-consumer-protection-law-6502",
                "sourceGroupId": "tr_early_payment_compensation",
                "title": "6502 sayılı Tüketicinin Korunması Hakkında Kanun",
                "publisher": "T.C. Mevzuat Bilgi Sistemi",
                "url": "https://www.mevzuat.gov.tr/mevzuatmetin/1.5.6502.pdf",
                "kind": "official-government",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "erken ödeme",
                    "konut finansmanı tazminatı",
                    "sabit faiz"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "TL",
                    "minimum": 1,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 2,
                    "options": [],
                    "visibleWhen": null,
                    "id": "remainingPrincipal",
                    "type": "number",
                    "label": "Kalan anapara",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "ay",
                    "minimum": 1,
                    "maximum": 600,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "monthsRemaining",
                    "type": "number",
                    "label": "Kalan vade",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "%",
                    "minimum": 0,
                    "maximum": 100,
                    "maxDecimalPlaces": 6,
                    "options": [],
                    "visibleWhen": null,
                    "id": "monthlyInterestRatePercent",
                    "type": "number",
                    "label": "Aylık faiz oranı",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "TL",
                    "minimum": 0,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 2,
                    "options": [],
                    "visibleWhen": null,
                    "id": "partialPayment",
                    "type": "number",
                    "label": "Kısmi erken ödeme",
                    "required": false
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "ihtiyac",
                            "label": "İhtiyaç kredisi"
                        },
                        {
                            "value": "tasit",
                            "label": "Taşıt kredisi"
                        },
                        {
                            "value": "konut",
                            "label": "Konut kredisi"
                        }
                    ],
                    "visibleWhen": null,
                    "id": "loanType",
                    "type": "radio",
                    "label": "Kredi türü",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "sabit",
                            "label": "Sabit faizli"
                        },
                        {
                            "value": "degisken",
                            "label": "Değişken faizli"
                        }
                    ],
                    "visibleWhen": null,
                    "id": "interestType",
                    "type": "radio",
                    "label": "Faiz türü",
                    "required": true
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "remainingPrincipal",
                    "type": "money",
                    "label": "Kalan anapara",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "monthsRemaining",
                    "type": "number",
                    "label": "Kalan vade",
                    "unit": "ay",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "monthlyInterestRatePercent",
                    "type": "percentage",
                    "label": "Aylık faiz oranı",
                    "unit": "%",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "partialPayment",
                    "type": "money",
                    "label": "Kısmi ödeme",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "loanType",
                    "type": "text",
                    "label": "Kredi türü",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "interestType",
                    "type": "text",
                    "label": "Faiz türü",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "paymentMode",
                    "type": "text",
                    "label": "Ödeme biçimi",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "earlyPaidPrincipal",
                    "type": "money",
                    "label": "Erken ödenen anapara",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "compensation",
                    "type": "money",
                    "label": "Erken ödeme tazminatı",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "payableToday",
                    "type": "money",
                    "label": "Bugün ödenecek tutar",
                    "unit": null,
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "continuationInterest",
                    "type": "money",
                    "label": "Devam senaryosu faizi",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "grossInterestSaving",
                    "type": "money",
                    "label": "Brüt faiz tasarrufu",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "netSaving",
                    "type": "money",
                    "label": "Net tasarruf",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                }
            ]
        },
        "content": {
            "title": "Kredi Erken Kapama Hesaplama",
            "shortTitle": "Kredi Erken Kapama Hesaplama",
            "summary": "Tam veya kısmi erken ödenen anaparayı ayırır; yalnız sabit faizli konut finansmanında kalan vadeye göre %1/%2 tazminat senaryosu gösterir.",
            "method": "Tam veya kısmi erken ödenen anaparayı ayırır; yalnız sabit faizli konut finansmanında kalan vadeye göre %1/%2 tazminat senaryosu gösterir.",
            "limitations": [
                "Banka kapama yazısındaki tahakkuk, vergi, gün içi faiz ve sözleşme hükümleri nihai tutarı değiştirebilir."
            ],
            "faqs": [
                {
                    "question": "Sonuç kesin veya resmî bir tutar mıdır?",
                    "answer": "Hayır. Sonuç, gösterilen girdiler ve tarihli kaynak kaydıyla üretilen yardımcı bir hesaplamadır."
                }
            ],
            "sourcePanel": {
                "title": "Kaynak ve inceleme durumu",
                "summary": "Formül ve oranlar belirlenen aralıkta resmî kaynaktan yeniden incelenir. Değişiklik yoksa inceleme tarihi, değişiklik varsa config sürümü yenilenir.",
                "sourceIds": [
                    "mevzuat-consumer-protection-law-6502"
                ],
                "notes": [
                    "Banka kapama yazısındaki tahakkuk, vergi, gün içi faiz ve sözleşme hükümleri nihai tutarı değiştirebilir."
                ]
            }
        },
        "config": {
            "kind": "early-loan-closure-v1",
            "calculatorId": "bireysel-finans.kredi-erken-kapama-hesaplama",
            "remainingPrincipal": [
                1,
                1000000000
            ],
            "monthsRemaining": [
                1,
                600
            ],
            "monthlyInterestRatePercent": [
                0,
                100
            ],
            "policy": {
                "housingMonthThreshold": 36,
                "shortHousingCompensationRate": 0.01,
                "longHousingCompensationRate": 0.02
            },
            "policyLifecycle": {
                "configVersion": "1.0.0",
                "sourceIds": [
                    "mevzuat-consumer-protection-law-6502"
                ],
                "reviewedOn": "2026-08-25",
                "effectiveFrom": "2014-05-28",
                "effectiveTo": null,
                "reviewAfter": "2026-09-25"
            }
        },
        "testVectors": {
            "kind": "vehicle-finance-oracle-v1",
            "cases": [
                {
                    "caseId": "early-housing-36-months",
                    "scenario": "threshold",
                    "input": {
                        "remainingPrincipal": 100000,
                        "monthsRemaining": 36,
                        "monthlyInterestRatePercent": 1,
                        "loanType": "konut",
                        "interestType": "sabit"
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "paymentMode": "full",
                            "earlyPaidPrincipal": 100000,
                            "compensation": 1000,
                            "payableToday": 101000
                        }
                    },
                    "tolerance": 1e-8
                },
                {
                    "caseId": "early-partial-equals-principal",
                    "scenario": "adversarial",
                    "input": {
                        "remainingPrincipal": 100000,
                        "monthsRemaining": 36,
                        "monthlyInterestRatePercent": 1,
                        "partialPayment": 100000,
                        "loanType": "konut",
                        "interestType": "sabit"
                    },
                    "expected": {
                        "ok": false,
                        "field": "partialPayment",
                        "errorCode": "OUT_OF_RANGE"
                    },
                    "tolerance": 1e-9
                }
            ]
        },
        "lastReviewed": "2026-08-25"
    },
    {
        "toolId": "credit-card-minimum-payment",
        "slug": "kredi-karti-asgari-tutar-hesaplama",
        "path": "/bireysel-finans/kredi-karti-asgari-tutar-hesaplama",
        "category": {
            "id": "bireysel-finans",
            "label": "Bireysel Finans"
        },
        "classification": "remote-config",
        "engineId": "credit-card-minimum-payment-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "2026.10.29",
        "configVersion": "2026.10.29",
        "effectiveFrom": "2026-10-01",
        "effectiveTo": null,
        "minAppVersion": "0.4.0",
        "updateCadence": {
            "mode": "scheduled-source-review",
            "days": 37,
            "reviewAfter": "2026-12-05",
            "specialMonths": [],
            "note": "Belirlenen tarihte resmî kaynak incelenir; değişiklik varsa config, yoksa inceleme tarihi yenilenir."
        },
        "sources": [
            {
                "sourceId": "bddk-credit-card-minimum-2026-10",
                "sourceGroupId": "tr_credit_card_rules",
                "title": "Kredi kartı asgari ödeme oranları",
                "publisher": "Bankacılık Düzenleme ve Denetleme Kurumu",
                "url": "https://www.bddk.gov.tr/Mevzuat/DokumanGetir/1349",
                "kind": "official-government",
                "reviewedOn": "2026-10-29",
                "watchFields": [
                    "kart limiti eşiği",
                    "asgari ödeme oranı"
                ]
            },
            {
                "sourceId": "tcmb-credit-card-max-interest-current",
                "sourceGroupId": "tr_credit_card_rules",
                "title": "Kredi kartı azami faiz oranları",
                "publisher": "Türkiye Cumhuriyet Merkez Bankası",
                "url": "https://www.tcmb.gov.tr/wps/wcm/connect/TR/TCMB%2BTR/Main%2BMenu/Istatistikler/Bankacilik%2BVerileri/Kredi_Karti_Islemlerinde_Uygulanacak_Azami_Faiz_Oranlari",
                "kind": "official-government",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "akdi faiz",
                    "azami oran",
                    "dönem"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "TL",
                    "minimum": 1,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 2,
                    "options": [],
                    "visibleWhen": null,
                    "id": "statementDebt",
                    "type": "number",
                    "label": "Ekstre borcu",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "TL",
                    "minimum": 1,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 2,
                    "options": [],
                    "visibleWhen": null,
                    "id": "cardLimit",
                    "type": "number",
                    "label": "Kart limiti",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "%",
                    "minimum": 0,
                    "maximum": 100,
                    "maxDecimalPlaces": 6,
                    "options": [],
                    "visibleWhen": null,
                    "id": "monthlyInterestRatePercent",
                    "type": "number",
                    "label": "Aylık faiz oranı",
                    "required": true
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "statementDebt",
                    "type": "money",
                    "label": "Ekstre borcu",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "cardLimit",
                    "type": "money",
                    "label": "Kart limiti",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "monthlyInterestRatePercent",
                    "type": "percentage",
                    "label": "Aylık faiz oranı",
                    "unit": "%",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "minimumPaymentRatePercent",
                    "type": "percentage",
                    "label": "Asgari ödeme oranı",
                    "unit": "%",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "minimumPayment",
                    "type": "money",
                    "label": "Asgari ödeme",
                    "unit": null,
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "remainingDebt",
                    "type": "money",
                    "label": "Kalan borç",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "assumedOneMonthInterest",
                    "type": "money",
                    "label": "Bir aylık faiz varsayımı",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "nextBalanceScenario",
                    "type": "money",
                    "label": "Sonraki bakiye senaryosu",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                }
            ]
        },
        "content": {
            "title": "Kredi Kartı Asgari Ödeme Hesaplama",
            "shortTitle": "Kredi Kartı Asgari Ödeme Hesaplama",
            "summary": "Asgari ödeme oranını kart limitinden seçer; ekstre borcu kart limitini aşsa da borcu geçerli kabul ederek kalan bakiye ve bir aylık faiz senaryosu üretir.",
            "method": "Asgari ödeme oranını kart limitinden seçer; ekstre borcu kart limitini aşsa da borcu geçerli kabul ederek kalan bakiye ve bir aylık faiz senaryosu üretir.",
            "limitations": [
                "Faiz oranı kullanıcıdan alınır; vergi, gecikme, alışveriş ve nakit avans ayrımları banka ekstresine göre değişebilir."
            ],
            "faqs": [
                {
                    "question": "Sonuç kesin veya resmî bir tutar mıdır?",
                    "answer": "Hayır. Sonuç, gösterilen girdiler ve tarihli kaynak kaydıyla üretilen yardımcı bir hesaplamadır."
                }
            ],
            "sourcePanel": {
                "title": "Kaynak ve inceleme durumu",
                "summary": "Formül ve oranlar belirlenen aralıkta resmî kaynaktan yeniden incelenir. Değişiklik yoksa inceleme tarihi, değişiklik varsa config sürümü yenilenir.",
                "sourceIds": [
                    "bddk-credit-card-minimum-2026-10",
                    "tcmb-credit-card-max-interest-current"
                ],
                "notes": [
                    "Faiz oranı kullanıcıdan alınır; vergi, gecikme, alışveriş ve nakit avans ayrımları banka ekstresine göre değişebilir."
                ]
            }
        },
        "config": {
            "kind": "credit-card-minimum-payment-v1",
            "calculatorId": "bireysel-finans.kredi-karti-asgari-tutar-hesaplama",
            "debt": [
                1,
                1000000000
            ],
            "cardLimit": [
                1,
                1000000000
            ],
            "monthlyInterestRatePercent": [
                0,
                100
            ],
            "policy": {
                "limitThreshold": 100000,
                "lowerMinimumPaymentRate": 0.2,
                "upperMinimumPaymentRate": 0.4
            },
            "policyLifecycle": {
                "configVersion": "2026.10.29",
                "sourceIds": [
                    "bddk-credit-card-minimum-2026-10",
                    "tcmb-credit-card-max-interest-current"
                ],
                "reviewedOn": "2026-10-29",
                "effectiveFrom": "2026-10-01",
                "effectiveTo": null,
                "reviewAfter": "2026-12-05"
            }
        },
        "testVectors": {
            "kind": "vehicle-finance-oracle-v1",
            "cases": [
                {
                    "caseId": "card-debt-over-limit",
                    "scenario": "threshold",
                    "input": {
                        "statementDebt": 60000,
                        "cardLimit": 50000,
                        "monthlyInterestRatePercent": 4
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "minimumPaymentRatePercent": 20,
                            "minimumPayment": 12000,
                            "remainingDebt": 48000,
                            "assumedOneMonthInterest": 1920,
                            "nextBalanceScenario": 49920
                        }
                    },
                    "tolerance": 1e-8
                },
                {
                    "caseId": "card-missing-limit",
                    "scenario": "adversarial",
                    "input": {
                        "statementDebt": 60000,
                        "monthlyInterestRatePercent": 4
                    },
                    "expected": {
                        "ok": false,
                        "field": "cardLimit",
                        "errorCode": "REQUIRED"
                    },
                    "tolerance": 1e-9
                }
            ]
        },
        "lastReviewed": "2026-10-29"
    },
    {
        "toolId": "deposit-return",
        "slug": "mevduat-getirisi-hesaplama",
        "path": "/bireysel-finans/mevduat-getirisi-hesaplama",
        "category": {
            "id": "bireysel-finans",
            "label": "Bireysel Finans"
        },
        "classification": "remote-config",
        "engineId": "deposit-return-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2025-07-09",
        "effectiveTo": null,
        "minAppVersion": "0.4.0",
        "updateCadence": {
            "mode": "scheduled-source-review",
            "days": 37,
            "reviewAfter": "2026-10-01",
            "specialMonths": [],
            "note": "Belirlenen tarihte resmî kaynak incelenir; değişiklik varsa config, yoksa inceleme tarihi yenilenir."
        },
        "sources": [
            {
                "sourceId": "gib-temporary-67-guide-2026",
                "sourceGroupId": "tr_deposit_withholding",
                "title": "2026 Geçici 67 rehberi",
                "publisher": "Gelir İdaresi Başkanlığı",
                "url": "https://cdn.gib.gov.tr/api/gibportal-file/file/getFile?objectKey=DUYURU%2FUNIVERSAL%2F2026%2F2026_Gecici67.pdf",
                "kind": "official-government",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "mevduat stopajı",
                    "vade dilimi",
                    "oran"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "TL",
                    "minimum": 1,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 2,
                    "options": [],
                    "visibleWhen": null,
                    "id": "principal",
                    "type": "number",
                    "label": "Anapara",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "%",
                    "minimum": 0,
                    "maximum": 10000,
                    "maxDecimalPlaces": 6,
                    "options": [],
                    "visibleWhen": null,
                    "id": "annualInterestRatePercent",
                    "type": "number",
                    "label": "Yıllık brüt faiz oranı",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "gün",
                    "minimum": 1,
                    "maximum": 10000,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "termDays",
                    "type": "number",
                    "label": "Vade",
                    "required": true
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "principal",
                    "type": "money",
                    "label": "Anapara",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "annualInterestRatePercent",
                    "type": "percentage",
                    "label": "Yıllık brüt faiz oranı",
                    "unit": "%",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "termDays",
                    "type": "number",
                    "label": "Vade",
                    "unit": "gün",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "grossInterest",
                    "type": "money",
                    "label": "Brüt faiz",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "withholdingRatePercent",
                    "type": "percentage",
                    "label": "Stopaj oranı",
                    "unit": "%",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "withholdingTax",
                    "type": "money",
                    "label": "Stopaj",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "netInterest",
                    "type": "money",
                    "label": "Net faiz",
                    "unit": null,
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "maturityAmount",
                    "type": "money",
                    "label": "Vade sonu tutarı",
                    "unit": null,
                    "nullable": false,
                    "prominent": true
                }
            ]
        },
        "content": {
            "title": "Mevduat Getirisi Hesaplama",
            "shortTitle": "Mevduat Getirisi Hesaplama",
            "summary": "Basit brüt faizi 365 gün esasıyla hesaplar; vade gününe göre stopajı uygular ve net faiz ile vade sonu tutarını gösterir.",
            "method": "Basit brüt faizi 365 gün esasıyla hesaplar; vade gününe göre stopajı uygular ve net faiz ile vade sonu tutarını gösterir.",
            "limitations": [
                "Banka gün hesabı, bileşik/temdit yöntemi, kampanya ve müşteri özel oranları bu sade senaryodan farklı olabilir."
            ],
            "faqs": [
                {
                    "question": "Sonuç kesin veya resmî bir tutar mıdır?",
                    "answer": "Hayır. Sonuç, gösterilen girdiler ve tarihli kaynak kaydıyla üretilen yardımcı bir hesaplamadır."
                }
            ],
            "sourcePanel": {
                "title": "Kaynak ve inceleme durumu",
                "summary": "Formül ve oranlar belirlenen aralıkta resmî kaynaktan yeniden incelenir. Değişiklik yoksa inceleme tarihi, değişiklik varsa config sürümü yenilenir.",
                "sourceIds": [
                    "gib-temporary-67-guide-2026"
                ],
                "notes": [
                    "Banka gün hesabı, bileşik/temdit yöntemi, kampanya ve müşteri özel oranları bu sade senaryodan farklı olabilir."
                ]
            }
        },
        "config": {
            "kind": "deposit-return-v1",
            "calculatorId": "bireysel-finans.mevduat-getirisi-hesaplama",
            "principal": [
                1,
                1000000000
            ],
            "annualInterestRatePercent": [
                0,
                10000
            ],
            "termDays": [
                1,
                10000
            ],
            "policy": {
                "daysPerYear": 365,
                "withholdingTiers": [
                    {
                        "maximumTermDaysInclusive": 180,
                        "rate": 0.175
                    },
                    {
                        "maximumTermDaysInclusive": 365,
                        "rate": 0.15
                    },
                    {
                        "maximumTermDaysInclusive": null,
                        "rate": 0.1
                    }
                ]
            },
            "policyLifecycle": {
                "configVersion": "1.0.0",
                "sourceIds": [
                    "gib-temporary-67-guide-2026"
                ],
                "reviewedOn": "2026-08-25",
                "effectiveFrom": "2025-07-09",
                "effectiveTo": null,
                "reviewAfter": "2026-10-01"
            }
        },
        "testVectors": {
            "kind": "vehicle-finance-oracle-v1",
            "cases": [
                {
                    "caseId": "deposit-365-day-tier",
                    "scenario": "threshold",
                    "input": {
                        "principal": 100000,
                        "annualInterestRatePercent": 20,
                        "termDays": 365
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "grossInterest": 20000,
                            "withholdingRatePercent": 15,
                            "withholdingTax": 3000,
                            "netInterest": 17000,
                            "maturityAmount": 117000
                        }
                    },
                    "tolerance": 1e-8
                },
                {
                    "caseId": "deposit-missing-term",
                    "scenario": "adversarial",
                    "input": {
                        "principal": 100000,
                        "annualInterestRatePercent": 20
                    },
                    "expected": {
                        "ok": false,
                        "field": "termDays",
                        "errorCode": "REQUIRED"
                    },
                    "tolerance": 1e-9
                }
            ]
        },
        "lastReviewed": "2026-08-25"
    },
    {
        "toolId": "vehicle-loan",
        "slug": "tasit-kredisi-hesaplama",
        "path": "/bireysel-finans/tasit-kredisi-hesaplama",
        "category": {
            "id": "bireysel-finans",
            "label": "Bireysel Finans"
        },
        "classification": "remote-config",
        "engineId": "vehicle-loan-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "2026.10.29",
        "configVersion": "2026.10.29",
        "effectiveFrom": "2026-01-01",
        "effectiveTo": null,
        "minAppVersion": "0.4.0",
        "updateCadence": {
            "mode": "scheduled-source-review",
            "days": 37,
            "reviewAfter": "2026-12-05",
            "specialMonths": [],
            "note": "Belirlenen tarihte resmî kaynak incelenir; değişiklik varsa config, yoksa inceleme tarihi yenilenir."
        },
        "sources": [
            {
                "sourceId": "official-gazette-consumer-credit-bsmv-2023-07",
                "sourceGroupId": "tr_consumer_credit_taxes",
                "title": "Tüketici kredilerinde BSMV kararı",
                "publisher": "T.C. Resmî Gazete",
                "url": "https://www.resmigazete.gov.tr/eskiler/2023/07/20230707-10.pdf",
                "kind": "official-government",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "BSMV oranı",
                    "kredi kapsamı"
                ]
            },
            {
                "sourceId": "gib-consumer-credit-kkdf-current",
                "sourceGroupId": "tr_consumer_credit_kkdf",
                "title": "Tüketici kredilerinde KKDF uygulaması",
                "publisher": "Gelir İdaresi Başkanlığı",
                "url": "https://gib.gov.tr/mevzuat/kanun/502/ozelge/38469",
                "kind": "official-government",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "KKDF oranı",
                    "faiz matrahı"
                ]
            },
            {
                "sourceId": "bddk-vehicle-credit-standard-current",
                "sourceGroupId": "tr_vehicle_credit_limits",
                "title": "Taşıt kredisi standart sınırları",
                "publisher": "Bankacılık Düzenleme ve Denetleme Kurumu",
                "url": "https://www.bddk.org.tr/Mevzuat/EkGetir/1210?ekId=271",
                "kind": "official-government",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "taşıt değeri",
                    "kredi oranı",
                    "azami vade"
                ]
            },
            {
                "sourceId": "bddk-vehicle-credit-domestic-ev-current",
                "sourceGroupId": "tr_vehicle_credit_limits",
                "title": "Yerli elektrikli taşıt kredisi sınırları",
                "publisher": "Bankacılık Düzenleme ve Denetleme Kurumu",
                "url": "https://www.bddk.org.tr/Mevzuat/EkGetir/1210?ekId=282",
                "kind": "official-government",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "yerli elektrikli taşıt",
                    "kredi oranı",
                    "azami vade"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "standart",
                            "label": "Benzin / Dizel / Standart"
                        },
                        {
                            "value": "elektrikYerli",
                            "label": "Türkiye’de üretilen nitelikli salt elektrikli"
                        }
                    ],
                    "visibleWhen": null,
                    "id": "fuelType",
                    "type": "select",
                    "label": "Taşıt finansman grubu",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "TL",
                    "minimum": 1000,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 2,
                    "options": [],
                    "visibleWhen": null,
                    "id": "vehicleValue",
                    "type": "number",
                    "label": "Taşıt değeri",
                    "required": false
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "TL",
                    "minimum": 1000,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 2,
                    "options": [],
                    "visibleWhen": null,
                    "id": "principal",
                    "type": "number",
                    "label": "Kredi anaparası",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "ay",
                    "minimum": 1,
                    "maximum": 48,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "termMonths",
                    "type": "number",
                    "label": "Vade",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "%",
                    "minimum": 0,
                    "maximum": 20,
                    "maxDecimalPlaces": 6,
                    "options": [],
                    "visibleWhen": null,
                    "id": "monthlyInterestRatePercent",
                    "type": "number",
                    "label": "Aylık faiz oranı",
                    "required": true
                },
                {
                    "defaultValue": true,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [],
                    "visibleWhen": null,
                    "id": "includeAllocationFee",
                    "type": "boolean",
                    "label": "Tahsis ücreti eklensin",
                    "required": true
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "fuelType",
                    "type": "text",
                    "label": "Finansman grubu",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "vehicleValue",
                    "type": "money",
                    "label": "Taşıt değeri",
                    "unit": null,
                    "nullable": true,
                    "prominent": false
                },
                {
                    "id": "principal",
                    "type": "money",
                    "label": "Kredi anaparası",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "termMonths",
                    "type": "number",
                    "label": "Vade",
                    "unit": "ay",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "monthlyInterestRatePercent",
                    "type": "percentage",
                    "label": "Aylık faiz oranı",
                    "unit": "%",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "includeAllocationFee",
                    "type": "boolean",
                    "label": "Tahsis ücreti eklendi",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "taxInclusiveMonthlyRatePercent",
                    "type": "percentage",
                    "label": "Vergiler dâhil aylık oran",
                    "unit": "%",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "monthlyPayment",
                    "type": "money",
                    "label": "Aylık taksit",
                    "unit": null,
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "pureInterest",
                    "type": "money",
                    "label": "Saf faiz",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "interestTaxes",
                    "type": "money",
                    "label": "Faiz vergileri",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "allocationFee",
                    "type": "money",
                    "label": "Tahsis ücreti",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "totalRepayment",
                    "type": "money",
                    "label": "Toplam geri ödeme",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "maximumLoan",
                    "type": "money",
                    "label": "Azami kredi",
                    "unit": null,
                    "nullable": true,
                    "prominent": false
                },
                {
                    "id": "maximumMonths",
                    "type": "number",
                    "label": "Azami vade",
                    "unit": "ay",
                    "nullable": true,
                    "prominent": false
                },
                {
                    "id": "policyCompliant",
                    "type": "boolean",
                    "label": "Sınırlarla uyumlu",
                    "unit": null,
                    "nullable": true,
                    "prominent": false
                },
                {
                    "id": "policyGuidance",
                    "type": "text",
                    "label": "Sınır açıklaması",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                }
            ]
        },
        "content": {
            "title": "Taşıt Kredisi Hesaplama",
            "shortTitle": "Taşıt Kredisi Hesaplama",
            "summary": "Aylık faize BSMV ve KKDF'yi ekleyerek taksiti hesaplar; taşıt değeri girilmişse standart veya nitelikli yerli elektrikli kredi/vade sınırlarını ayrıca denetler.",
            "method": "Aylık faize BSMV ve KKDF'yi ekleyerek taksiti hesaplar; taşıt değeri girilmişse standart veya nitelikli yerli elektrikli kredi/vade sınırlarını ayrıca denetler.",
            "limitations": [
                "Banka tahsis, sigorta ve rehin masrafları ile müşteri özel oranı tekliften doğrulanmalıdır."
            ],
            "faqs": [
                {
                    "question": "Sonuç kesin veya resmî bir tutar mıdır?",
                    "answer": "Hayır. Sonuç, gösterilen girdiler ve tarihli kaynak kaydıyla üretilen yardımcı bir hesaplamadır."
                }
            ],
            "sourcePanel": {
                "title": "Kaynak ve inceleme durumu",
                "summary": "Formül ve oranlar belirlenen aralıkta resmî kaynaktan yeniden incelenir. Değişiklik yoksa inceleme tarihi, değişiklik varsa config sürümü yenilenir.",
                "sourceIds": [
                    "official-gazette-consumer-credit-bsmv-2023-07",
                    "gib-consumer-credit-kkdf-current",
                    "bddk-vehicle-credit-standard-current",
                    "bddk-vehicle-credit-domestic-ev-current"
                ],
                "notes": [
                    "Banka tahsis, sigorta ve rehin masrafları ile müşteri özel oranı tekliften doğrulanmalıdır."
                ]
            }
        },
        "config": {
            "kind": "vehicle-loan-v1",
            "calculatorId": "bireysel-finans.tasit-kredisi-hesaplama",
            "principal": [
                1000,
                1000000000
            ],
            "vehicleValue": [
                1000,
                1000000000
            ],
            "termMonths": [
                1,
                48
            ],
            "monthlyInterestRatePercent": [
                0,
                20
            ],
            "policy": {
                "kkdfRate": 0.15,
                "bsmvRate": 0.15,
                "allocationFeeRate": 0.005,
                "allocationFeeBsmvRate": 0.15,
                "standardLimitTiers": [
                    {
                        "maximumVehicleValue": 400000,
                        "maximumLoanToValueRate": 0.7,
                        "maximumMonths": 48
                    },
                    {
                        "maximumVehicleValue": 800000,
                        "maximumLoanToValueRate": 0.5,
                        "maximumMonths": 36
                    },
                    {
                        "maximumVehicleValue": 1200000,
                        "maximumLoanToValueRate": 0.3,
                        "maximumMonths": 24
                    },
                    {
                        "maximumVehicleValue": 2000000,
                        "maximumLoanToValueRate": 0.2,
                        "maximumMonths": 12
                    }
                ],
                "qualifiedDomesticElectricLimitTiers": [
                    {
                        "maximumVehicleValue": 2500000,
                        "maximumLoanToValueRate": 0.7,
                        "maximumMonths": 48
                    },
                    {
                        "maximumVehicleValue": 5000000,
                        "maximumLoanToValueRate": 0.5,
                        "maximumMonths": 36
                    },
                    {
                        "maximumVehicleValue": 6500000,
                        "maximumLoanToValueRate": 0.3,
                        "maximumMonths": 24
                    },
                    {
                        "maximumVehicleValue": 7500000,
                        "maximumLoanToValueRate": 0.2,
                        "maximumMonths": 12
                    }
                ]
            },
            "policyLifecycle": {
                "configVersion": "2026.10.29",
                "sourceIds": [
                    "official-gazette-consumer-credit-bsmv-2023-07",
                    "gib-consumer-credit-kkdf-current",
                    "bddk-vehicle-credit-standard-current",
                    "bddk-vehicle-credit-domestic-ev-current"
                ],
                "reviewedOn": "2026-10-29",
                "effectiveFrom": "2026-01-01",
                "effectiveTo": null,
                "reviewAfter": "2026-12-05"
            }
        },
        "testVectors": {
            "kind": "vehicle-finance-oracle-v1",
            "cases": [
                {
                    "caseId": "vehicle-loan-standard-first-tier",
                    "scenario": "positive",
                    "input": {
                        "fuelType": "standart",
                        "vehicleValue": 400000,
                        "principal": 100000,
                        "termMonths": 12,
                        "monthlyInterestRatePercent": 1,
                        "includeAllocationFee": true
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "taxInclusiveMonthlyRatePercent": 1.3,
                            "allocationFee": 575,
                            "maximumLoan": 280000,
                            "maximumMonths": 48,
                            "policyCompliant": true
                        }
                    },
                    "tolerance": 1e-8
                },
                {
                    "caseId": "vehicle-loan-missing-principal",
                    "scenario": "adversarial",
                    "input": {
                        "fuelType": "standart",
                        "termMonths": 12,
                        "monthlyInterestRatePercent": 1,
                        "includeAllocationFee": false
                    },
                    "expected": {
                        "ok": false,
                        "field": "principal",
                        "errorCode": "REQUIRED"
                    },
                    "tolerance": 1e-9
                }
            ]
        },
        "lastReviewed": "2026-10-29"
    },
    {
        "toolId": "inheritance-gift-tax",
        "slug": "veraset-intikal-vergisi-hesaplama",
        "path": "/bireysel-finans/veraset-intikal-vergisi-hesaplama",
        "category": {
            "id": "bireysel-finans",
            "label": "Bireysel Finans"
        },
        "classification": "remote-config",
        "engineId": "inheritance-gift-tax-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-01-01",
        "effectiveTo": "2026-12-31",
        "minAppVersion": "0.4.0",
        "updateCadence": {
            "mode": "scheduled-source-review",
            "days": 129,
            "reviewAfter": "2027-01-01",
            "specialMonths": [],
            "note": "Belirlenen tarihte resmî kaynak incelenir; değişiklik varsa config, yoksa inceleme tarihi yenilenir."
        },
        "sources": [
            {
                "sourceId": "gib-inheritance-gift-tax-2026",
                "sourceGroupId": "tr_inheritance_gift_tax",
                "title": "2026 veraset ve intikal vergisi tarifesi",
                "publisher": "Gelir İdaresi Başkanlığı",
                "url": "https://cdn.gib.gov.tr/api/gibportal-file/file/getFile?objectKey=MEVZUAT_TEBLIGLER%2FUNIVERSAL%2F2025%2F7338_Teblig57.pdf",
                "kind": "official-government",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "istisna",
                    "matrah dilimleri",
                    "veraset",
                    "ivazsız intikal"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "veraset",
                            "label": "Veraset (miras) — eş/çocuk"
                        },
                        {
                            "value": "verasetEs",
                            "label": "Veraset — yalnız eş (çocuk yok)"
                        },
                        {
                            "value": "ivazsiz",
                            "label": "İvazsız bağış"
                        },
                        {
                            "value": "ivazsizYakin",
                            "label": "Ana/baba/eş/çocuğa bağış (yarı oran)"
                        },
                        {
                            "value": "sansOyunu",
                            "label": "Yarışma / çekiliş / şans oyunu"
                        }
                    ],
                    "visibleWhen": null,
                    "id": "transferType",
                    "type": "select",
                    "label": "İntikal türü",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "TL",
                    "minimum": 1,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 2,
                    "options": [],
                    "visibleWhen": null,
                    "id": "amount",
                    "type": "number",
                    "label": "İntikal tutarı",
                    "required": true
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "transferType",
                    "type": "text",
                    "label": "İntikal türü",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "transferTypeLabel",
                    "type": "text",
                    "label": "İntikal türü açıklaması",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "amount",
                    "type": "money",
                    "label": "İntikal tutarı",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "deductedExemption",
                    "type": "money",
                    "label": "İndirilen istisna",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "taxBase",
                    "type": "money",
                    "label": "Vergi matrahı",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "tax",
                    "type": "money",
                    "label": "Hesaplanan vergi",
                    "unit": null,
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "effectiveRatePercent",
                    "type": "percentage",
                    "label": "Efektif vergi oranı",
                    "unit": "%",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "afterTaxAmount",
                    "type": "money",
                    "label": "Vergi sonrası tutar",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                }
            ]
        },
        "content": {
            "title": "Veraset ve İntikal Vergisi Hesaplama",
            "shortTitle": "Veraset ve İntikal Vergisi Hesaplama",
            "summary": "İntikal türüne ait 2026 istisnasını düşer ve artan matrah dilimlerine veraset, bağış, yakın bağışı veya şans oyunu oranlarını uygular.",
            "method": "İntikal türüne ait 2026 istisnasını düşer ve artan matrah dilimlerine veraset, bağış, yakın bağışı veya şans oyunu oranlarını uygular.",
            "limitations": [
                "Mirasçı payı, akrabalık, değerleme ve özel istisnalar beyan sonucunu değiştirebilir."
            ],
            "faqs": [
                {
                    "question": "Sonuç kesin veya resmî bir tutar mıdır?",
                    "answer": "Hayır. Sonuç, gösterilen girdiler ve tarihli kaynak kaydıyla üretilen yardımcı bir hesaplamadır."
                }
            ],
            "sourcePanel": {
                "title": "Kaynak ve inceleme durumu",
                "summary": "Formül ve oranlar belirlenen aralıkta resmî kaynaktan yeniden incelenir. Değişiklik yoksa inceleme tarihi, değişiklik varsa config sürümü yenilenir.",
                "sourceIds": [
                    "gib-inheritance-gift-tax-2026"
                ],
                "notes": [
                    "Mirasçı payı, akrabalık, değerleme ve özel istisnalar beyan sonucunu değiştirebilir."
                ]
            }
        },
        "config": {
            "kind": "inheritance-gift-tax-v1",
            "calculatorId": "bireysel-finans.veraset-intikal-vergisi-hesaplama",
            "amount": [
                1,
                1000000000
            ],
            "policy": {
                "thresholds": [
                    3000000,
                    10000000,
                    25000000,
                    55000000
                ],
                "transfers": {
                    "veraset": {
                        "exemption": 2907136,
                        "rates": [
                            0.01,
                            0.03,
                            0.05,
                            0.07,
                            0.1
                        ]
                    },
                    "verasetEs": {
                        "exemption": 5817845,
                        "rates": [
                            0.01,
                            0.03,
                            0.05,
                            0.07,
                            0.1
                        ]
                    },
                    "ivazsiz": {
                        "exemption": 66935,
                        "rates": [
                            0.1,
                            0.15,
                            0.2,
                            0.25,
                            0.3
                        ]
                    },
                    "ivazsizYakin": {
                        "exemption": 66935,
                        "rates": [
                            0.05,
                            0.075,
                            0.1,
                            0.125,
                            0.15
                        ]
                    },
                    "sansOyunu": {
                        "exemption": 66935,
                        "rates": [
                            0.2,
                            0.2,
                            0.2,
                            0.2,
                            0.2
                        ]
                    }
                }
            },
            "policyLifecycle": {
                "configVersion": "1.0.0",
                "sourceIds": [
                    "gib-inheritance-gift-tax-2026"
                ],
                "reviewedOn": "2026-08-25",
                "effectiveFrom": "2026-01-01",
                "effectiveTo": "2026-12-31",
                "reviewAfter": "2027-01-01"
            }
        },
        "testVectors": {
            "kind": "vehicle-finance-oracle-v1",
            "cases": [
                {
                    "caseId": "inheritance-first-bracket",
                    "scenario": "positive",
                    "input": {
                        "transferType": "veraset",
                        "amount": 4000000
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "deductedExemption": 2907136,
                            "taxBase": 1092864,
                            "tax": 10928.64,
                            "afterTaxAmount": 3989071.36
                        }
                    },
                    "tolerance": 1e-8
                },
                {
                    "caseId": "inheritance-missing-amount",
                    "scenario": "adversarial",
                    "input": {
                        "transferType": "veraset"
                    },
                    "expected": {
                        "ok": false,
                        "field": "amount",
                        "errorCode": "REQUIRED"
                    },
                    "tolerance": 1e-9
                }
            ]
        },
        "lastReviewed": "2026-08-25"
    },
    {
        "toolId": "open-high-school-obp",
        "slug": "acik-lise-obp",
        "path": "/egitim-sinav/acik-lise-obp-hesaplama",
        "category": {
            "id": "egitim-sinav",
            "label": "Eğitim ve Sınav"
        },
        "classification": "remote-config",
        "engineId": "open-high-school-obp-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-08-25",
        "effectiveTo": null,
        "minAppVersion": "0.4.0",
        "updateCadence": {
            "mode": "scheduled-source-review",
            "days": 90,
            "reviewAfter": "2027-01-01",
            "specialMonths": [],
            "note": "Belirlenen aralıkta resmî kaynak incelenir; değişiklik varsa config, yoksa doğrulanmış inceleme tarihi yenilenir."
        },
        "sources": [
            {
                "sourceId": "tr-open-high-school-regulation-2024",
                "sourceGroupId": "tr_open_high_school_rules",
                "title": "Millî Eğitim Bakanlığı Açık Öğretim Lisesi Yönetmeliği",
                "publisher": "Millî Eğitim Bakanlığı Açık Öğretim Lisesi",
                "url": "https://aol.meb.gov.tr/meb_iys_dosyalar/2024_12/26164940_acikogretimlisesiyonetmelik.pdf",
                "kind": "official-government",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "Mezuniyet kredisi",
                    "Zorunlu ders",
                    "Diploma notu"
                ]
            },
            {
                "sourceId": "tr-yks-2026-application-guide",
                "sourceGroupId": "tr_yks_rules_and_content",
                "title": "2026-YKS Başvuru Kılavuzu",
                "publisher": "Ölçme, Seçme ve Yerleştirme Merkezi",
                "url": "https://dokuman.osym.gov.tr/pdfdokuman/2026/YKS/basvuru_kilavuz06022026.pdf",
                "kind": "official-government",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "Test ve soru sayıları",
                    "OBP dönüşümü ve yerleştirme katkısı"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [],
                    "visibleWhen": null,
                    "id": "courses",
                    "type": "object-list",
                    "label": "Dersler",
                    "required": true,
                    "itemFields": [
                        {
                            "id": "name",
                            "type": "text",
                            "label": "Ders adı",
                            "required": false,
                            "minimum": null,
                            "maximum": null,
                            "maxDecimalPlaces": null,
                            "options": []
                        },
                        {
                            "id": "grade",
                            "type": "number",
                            "label": "Ders notu",
                            "required": true,
                            "minimum": 0,
                            "maximum": 100,
                            "maxDecimalPlaces": 4,
                            "options": []
                        },
                        {
                            "id": "weeklyHours",
                            "type": "number",
                            "label": "Haftalık ders saati",
                            "required": true,
                            "minimum": 1,
                            "maximum": 12,
                            "maxDecimalPlaces": 4,
                            "options": []
                        }
                    ]
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "diplomaGrade",
                    "type": "number",
                    "label": "Ağırlıklı diploma notu",
                    "unit": null,
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "totalWeeklyHours",
                    "type": "number",
                    "label": "Toplam haftalık ders saati",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "obp",
                    "type": "number",
                    "label": "Ortaöğretim Başarı Puanı (OBP)",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "yksContribution",
                    "type": "number",
                    "label": "YKS yerleştirme katkısı",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "maximumContributionPercent",
                    "type": "percentage",
                    "label": "Azami katkıya oran",
                    "unit": "%",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "courseCount",
                    "type": "number",
                    "label": "Ders sayısı",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                }
            ]
        },
        "content": {
            "title": "Açık Lise OBP Hesaplama",
            "shortTitle": "Açık Lise OBP",
            "summary": "Ders notlarını haftalık ders saatleriyle ağırlıklandırır; diploma notu, OBP ve standart YKS katkısını gösterir.",
            "method": "Ders notu×haftalık saat toplamı toplam saate bölünür. Diploma notu 5 ile OBP'ye, OBP de 0,12 ile standart yerleştirme katkısına çevrilir.",
            "limitations": [
                "Mezuniyet hakkı, kredi yeterliliği ve zorunlu ders durumu hesaplanmaz.",
                "Kesin diploma notu ve OBP için AÖL/ÖSYM kaydı esas alınır."
            ],
            "faqs": [
                {
                    "question": "Açık Lise OBP sonucu resmî sınav veya yerleştirme sonucu mudur?",
                    "answer": "Hayır. Araç yalnız gösterilen aritmetiği ve kaynak incelemesinden geçmiş dönemsel configi uygular. Standart puan, aday kitlesi, kurum kararı ve tercih uygunluğu ayrıca resmî kaynaktan doğrulanmalıdır."
                }
            ],
            "sourcePanel": {
                "title": "Resmî kaynak ve inceleme kapsamı",
                "summary": "Formül ve dönemsel sınav verileri kaynak kimlikli configte tutulur. İnceleme tarihi geldiğinde, kaynak değişmemiş olsa bile insan kontrolü yapılmadan kayıt yenilenmez.",
                "sourceIds": [
                    "tr-open-high-school-regulation-2024",
                    "tr-yks-2026-application-guide"
                ],
                "notes": [
                    "Kaynakta değişiklik varsa config güncellenir; değişiklik yoksa yalnız doğrulanmış inceleme tarihi yenilenir."
                ],
                "nextReviewOn": "2027-01-01"
            }
        },
        "config": {
            "kind": "open-high-school-obp-v1",
            "calculatorId": "egitim-sinav.acik-lise-obp-hesaplama",
            "policyLifecycle": {
                "configVersion": "1.0.0",
                "sourceIds": [
                    "tr-open-high-school-regulation-2024",
                    "tr-yks-2026-application-guide"
                ],
                "reviewedOn": "2026-08-25",
                "effectiveFrom": "2026-08-25",
                "effectiveTo": null,
                "reviewAfter": "2027-01-01"
            },
            "gradeRange": [
                0,
                100
            ],
            "weeklyHoursRange": [
                1,
                12
            ],
            "maximumCourses": 100,
            "obpMultiplier": 5,
            "placementContributionCoefficient": 0.12
        },
        "testVectors": {
            "kind": "source-education-oracle-v1",
            "cases": [
                {
                    "caseId": "weighted-diploma-grade",
                    "scenario": "positive",
                    "input": {
                        "courses": [
                            {
                                "grade": 80,
                                "weeklyHours": 4
                            },
                            {
                                "grade": 100,
                                "weeklyHours": 2
                            }
                        ]
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "diplomaGrade": 86.66666666666667,
                            "obp": 433.33333333333337,
                            "yksContribution": 52
                        }
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "course-grade-over-limit",
                    "scenario": "adversarial",
                    "input": {
                        "courses": [
                            {
                                "grade": 101,
                                "weeklyHours": 4
                            }
                        ]
                    },
                    "expected": {
                        "ok": false,
                        "field": "courses[0].grade",
                        "errorCode": "OUT_OF_RANGE"
                    },
                    "tolerance": 1e-10
                }
            ]
        },
        "lastReviewed": "2026-08-25"
    },
    {
        "toolId": "open-education-gpa",
        "slug": "acikogretim-not-ortalamasi",
        "path": "/egitim-sinav/acikogretim-not-ortalamasi-hesaplama",
        "category": {
            "id": "egitim-sinav",
            "label": "Eğitim ve Sınav"
        },
        "classification": "remote-config",
        "engineId": "open-education-gpa-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-08-25",
        "effectiveTo": null,
        "minAppVersion": "0.4.0",
        "updateCadence": {
            "mode": "scheduled-source-review",
            "days": 90,
            "reviewAfter": "2027-01-01",
            "specialMonths": [
                9
            ],
            "note": "Belirlenen aralıkta resmî kaynak incelenir; değişiklik varsa config, yoksa doğrulanmış inceleme tarihi yenilenir."
        },
        "sources": [
            {
                "sourceId": "tr-anadolu-aof-assessment-system",
                "sourceGroupId": "tr_anadolu_aof_assessment",
                "title": "Açıköğretim Öğrenci Değerlendirme Sistemi",
                "publisher": "Anadolu Üniversitesi",
                "url": "https://www.anadolu.edu.tr/acikogretim/sinavlar-ve-sorumluluk-uniteleri/ogrenci-degerlendirme-sistemi",
                "kind": "authoritative-education",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "Sınav ağırlıkları",
                    "Harf notları",
                    "Başarı alt sınırı ve bağıl değerlendirme"
                ]
            },
            {
                "sourceId": "tr-anadolu-aof-assessment-principles",
                "sourceGroupId": "tr_anadolu_aof_assessment",
                "title": "Açıköğretim Öğrenci Değerlendirme Sistemi Esasları",
                "publisher": "Anadolu Üniversitesi",
                "url": "https://www.anadolu.edu.tr/acikogretim/yonetmelikler-ve-esaslar-yonergeler/esaslar-yonergeler/anadolu-universitesi-acikogretim-iktisat-ve-isletme-fakulteleri-ogrenci-degerlendirme-sistemi-esasla",
                "kind": "authoritative-education",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "Ders başarı notu",
                    "Harf katsayıları",
                    "Değerlendirme ilkeleri"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [],
                    "visibleWhen": null,
                    "id": "courses",
                    "type": "object-list",
                    "label": "Dersler",
                    "required": true,
                    "itemFields": [
                        {
                            "id": "name",
                            "type": "text",
                            "label": "Ders adı",
                            "required": false,
                            "minimum": null,
                            "maximum": null,
                            "maxDecimalPlaces": null,
                            "options": []
                        },
                        {
                            "id": "letterGrade",
                            "type": "select",
                            "label": "Harf notu",
                            "required": true,
                            "minimum": null,
                            "maximum": null,
                            "maxDecimalPlaces": null,
                            "options": [
                                {
                                    "value": "AA",
                                    "label": "AA (4,00)"
                                },
                                {
                                    "value": "AB",
                                    "label": "AB (3,70)"
                                },
                                {
                                    "value": "BA",
                                    "label": "BA (3,30)"
                                },
                                {
                                    "value": "BB",
                                    "label": "BB (3,00)"
                                },
                                {
                                    "value": "BC",
                                    "label": "BC (2,70)"
                                },
                                {
                                    "value": "CB",
                                    "label": "CB (2,30)"
                                },
                                {
                                    "value": "CC",
                                    "label": "CC (2,00)"
                                },
                                {
                                    "value": "CD",
                                    "label": "CD (1,70)"
                                },
                                {
                                    "value": "DC",
                                    "label": "DC (1,30)"
                                },
                                {
                                    "value": "DD",
                                    "label": "DD (1,00)"
                                },
                                {
                                    "value": "FF",
                                    "label": "FF (0,00)"
                                }
                            ]
                        },
                        {
                            "id": "ects",
                            "type": "number",
                            "label": "AKTS kredisi",
                            "required": true,
                            "minimum": 1,
                            "maximum": 30,
                            "maxDecimalPlaces": 4,
                            "options": []
                        }
                    ]
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "totalEcts",
                    "type": "number",
                    "label": "Toplam AKTS",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "gpa",
                    "type": "number",
                    "label": "Genel not ortalaması",
                    "unit": null,
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "passedCourseCount",
                    "type": "number",
                    "label": "Başarılı ders sayısı",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "failedCourseCount",
                    "type": "number",
                    "label": "Başarısız ders sayısı",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "passedEcts",
                    "type": "number",
                    "label": "Başarılı AKTS",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "failedEcts",
                    "type": "number",
                    "label": "Başarısız AKTS",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "courseCount",
                    "type": "number",
                    "label": "Toplam ders sayısı",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                }
            ]
        },
        "content": {
            "title": "Açıköğretim Not Ortalaması Hesaplama",
            "shortTitle": "Açıköğretim Ortalaması",
            "summary": "Harf notu katsayılarını derslerin AKTS değerleriyle ağırlıklandırarak 4'lük genel not ortalamasını hesaplar.",
            "method": "Her harf notunun configteki katsayısı AKTS ile çarpılır; toplam, toplam AKTS'ye bölünür. FF başarısız ders olarak ayrı raporlanır.",
            "limitations": [
                "Araç kurumun bağıl değerlendirme sürecini veya mezuniyet kararını yeniden üretmez.",
                "Transkriptteki resmî harf notları ve AKTS değerleri kullanılmalıdır."
            ],
            "faqs": [
                {
                    "question": "Açıköğretim Ortalaması sonucu resmî sınav veya yerleştirme sonucu mudur?",
                    "answer": "Hayır. Araç yalnız gösterilen aritmetiği ve kaynak incelemesinden geçmiş dönemsel configi uygular. Standart puan, aday kitlesi, kurum kararı ve tercih uygunluğu ayrıca resmî kaynaktan doğrulanmalıdır."
                }
            ],
            "sourcePanel": {
                "title": "Resmî kaynak ve inceleme kapsamı",
                "summary": "Formül ve dönemsel sınav verileri kaynak kimlikli configte tutulur. İnceleme tarihi geldiğinde, kaynak değişmemiş olsa bile insan kontrolü yapılmadan kayıt yenilenmez.",
                "sourceIds": [
                    "tr-anadolu-aof-assessment-system",
                    "tr-anadolu-aof-assessment-principles"
                ],
                "notes": [
                    "Kaynakta değişiklik varsa config güncellenir; değişiklik yoksa yalnız doğrulanmış inceleme tarihi yenilenir."
                ],
                "nextReviewOn": "2027-01-01"
            }
        },
        "config": {
            "kind": "open-education-gpa-v1",
            "calculatorId": "egitim-sinav.acikogretim-not-ortalamasi-hesaplama",
            "policyLifecycle": {
                "configVersion": "1.0.0",
                "sourceIds": [
                    "tr-anadolu-aof-assessment-system",
                    "tr-anadolu-aof-assessment-principles"
                ],
                "reviewedOn": "2026-08-25",
                "effectiveFrom": "2026-08-25",
                "effectiveTo": null,
                "reviewAfter": "2027-01-01"
            },
            "ectsRange": [
                1,
                30
            ],
            "maximumCourses": 100,
            "gradePoints": {
                "AA": 4,
                "AB": 3.7,
                "BA": 3.3,
                "BB": 3,
                "BC": 2.7,
                "CB": 2.3,
                "CC": 2,
                "CD": 1.7,
                "DC": 1.3,
                "DD": 1,
                "FF": 0
            }
        },
        "testVectors": {
            "kind": "source-education-oracle-v1",
            "cases": [
                {
                    "caseId": "weighted-letter-grades",
                    "scenario": "positive",
                    "input": {
                        "courses": [
                            {
                                "letterGrade": "AA",
                                "ects": 6
                            },
                            {
                                "letterGrade": "BB",
                                "ects": 6
                            },
                            {
                                "letterGrade": "FF",
                                "ects": 3
                            }
                        ]
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "totalEcts": 15,
                            "gpa": 2.8,
                            "passedCourseCount": 2,
                            "failedCourseCount": 1
                        }
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "unknown-letter-grade",
                    "scenario": "adversarial",
                    "input": {
                        "courses": [
                            {
                                "letterGrade": "ZZ",
                                "ects": 6
                            }
                        ]
                    },
                    "expected": {
                        "ok": false,
                        "field": "courses[0].letterGrade",
                        "errorCode": "UNSUPPORTED_VALUE"
                    },
                    "tolerance": 1e-10
                }
            ]
        },
        "lastReviewed": "2026-08-25"
    },
    {
        "toolId": "ales-score",
        "slug": "ales-puan",
        "path": "/egitim-sinav/ales-puan-hesaplama",
        "category": {
            "id": "egitim-sinav",
            "label": "Eğitim ve Sınav"
        },
        "classification": "remote-config",
        "engineId": "ales-net-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-08-25",
        "effectiveTo": null,
        "minAppVersion": "0.4.0",
        "updateCadence": {
            "mode": "scheduled-source-review",
            "days": 30,
            "reviewAfter": "2027-01-01",
            "specialMonths": [],
            "note": "Belirlenen aralıkta resmî kaynak incelenir; değişiklik varsa config, yoksa doğrulanmış inceleme tarihi yenilenir."
        },
        "sources": [
            {
                "sourceId": "tr-ales-2026-2-guide",
                "sourceGroupId": "tr_ales_rules",
                "title": "2026-ALES/2 Kılavuz ve Başvuru Bilgileri",
                "publisher": "Ölçme, Seçme ve Yerleştirme Merkezi",
                "url": "https://www.osym.gov.tr/2026ales2-kilavuz-ve-basvuru-bilgileri",
                "kind": "official-government",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "Sayısal ve sözel soru sayıları",
                    "Net kuralı",
                    "Puan türleri",
                    "İptal soru durumu"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 0,
                    "maximum": 50,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "numericalCorrect",
                    "type": "number",
                    "label": "Sayısal doğru",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 0,
                    "maximum": 50,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "numericalWrong",
                    "type": "number",
                    "label": "Sayısal yanlış",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 0,
                    "maximum": 50,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "verbalCorrect",
                    "type": "number",
                    "label": "Sözel doğru",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 0,
                    "maximum": 50,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "verbalWrong",
                    "type": "number",
                    "label": "Sözel yanlış",
                    "required": true
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "numericalNet",
                    "type": "number",
                    "label": "Sayısal net",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "verbalNet",
                    "type": "number",
                    "label": "Sözel net",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "totalNet",
                    "type": "number",
                    "label": "Toplam net",
                    "unit": null,
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "officialScoreAvailable",
                    "type": "boolean",
                    "label": "Resmî puan üretilebilir mi?",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                }
            ]
        },
        "content": {
            "title": "ALES Net Hesaplama",
            "shortTitle": "ALES Net",
            "summary": "Sayısal ve sözel doğru-yanlış sayılarını dört yanlışın bir doğruyu götürdüğü kuralla ham nete çevirir.",
            "method": "Her bölümde net=doğru−yanlış/4 uygulanır; iki bölümün netleri toplanır.",
            "limitations": [
                "ÖSYM standart puanı aday kitlesi istatistiklerine bağlıdır; araç resmî ALES puanı üretmez.",
                "İptal sorusu veya soru sayısı değişirse config yeniden incelenir."
            ],
            "faqs": [
                {
                    "question": "ALES Net sonucu resmî sınav veya yerleştirme sonucu mudur?",
                    "answer": "Hayır. Araç yalnız gösterilen aritmetiği ve kaynak incelemesinden geçmiş dönemsel configi uygular. Standart puan, aday kitlesi, kurum kararı ve tercih uygunluğu ayrıca resmî kaynaktan doğrulanmalıdır."
                }
            ],
            "sourcePanel": {
                "title": "Resmî kaynak ve inceleme kapsamı",
                "summary": "Formül ve dönemsel sınav verileri kaynak kimlikli configte tutulur. İnceleme tarihi geldiğinde, kaynak değişmemiş olsa bile insan kontrolü yapılmadan kayıt yenilenmez.",
                "sourceIds": [
                    "tr-ales-2026-2-guide"
                ],
                "notes": [
                    "Kaynakta değişiklik varsa config güncellenir; değişiklik yoksa yalnız doğrulanmış inceleme tarihi yenilenir."
                ],
                "nextReviewOn": "2027-01-01"
            }
        },
        "config": {
            "kind": "ales-net-v1",
            "calculatorId": "egitim-sinav.ales-puan-hesaplama",
            "policyLifecycle": {
                "configVersion": "1.0.0",
                "sourceIds": [
                    "tr-ales-2026-2-guide"
                ],
                "reviewedOn": "2026-08-25",
                "effectiveFrom": "2026-08-25",
                "effectiveTo": null,
                "reviewAfter": "2027-01-01"
            },
            "questionsPerSection": 50,
            "wrongPenaltyDivisor": 4
        },
        "testVectors": {
            "kind": "source-education-oracle-v1",
            "cases": [
                {
                    "caseId": "two-section-net",
                    "scenario": "positive",
                    "input": {
                        "numericalCorrect": 40,
                        "numericalWrong": 8,
                        "verbalCorrect": 30,
                        "verbalWrong": 4
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "numericalNet": 38,
                            "verbalNet": 29,
                            "totalNet": 67
                        }
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "numerical-count-over-limit",
                    "scenario": "adversarial",
                    "input": {
                        "numericalCorrect": 51,
                        "numericalWrong": 0,
                        "verbalCorrect": 1,
                        "verbalWrong": 0
                    },
                    "expected": {
                        "ok": false,
                        "field": "numericalCorrect",
                        "errorCode": "OUT_OF_RANGE"
                    },
                    "tolerance": 1e-10
                }
            ]
        },
        "lastReviewed": "2026-08-25"
    },
    {
        "toolId": "aof-final-pass-score",
        "slug": "aof-final-gecme-notu",
        "path": "/egitim-sinav/aof-final-gecme-notu-hesaplama",
        "category": {
            "id": "egitim-sinav",
            "label": "Eğitim ve Sınav"
        },
        "classification": "remote-config",
        "engineId": "aof-required-final-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-08-25",
        "effectiveTo": null,
        "minAppVersion": "0.4.0",
        "updateCadence": {
            "mode": "scheduled-source-review",
            "days": 90,
            "reviewAfter": "2027-01-01",
            "specialMonths": [
                9
            ],
            "note": "Belirlenen aralıkta resmî kaynak incelenir; değişiklik varsa config, yoksa doğrulanmış inceleme tarihi yenilenir."
        },
        "sources": [
            {
                "sourceId": "tr-anadolu-aof-assessment-system",
                "sourceGroupId": "tr_anadolu_aof_assessment",
                "title": "Açıköğretim Öğrenci Değerlendirme Sistemi",
                "publisher": "Anadolu Üniversitesi",
                "url": "https://www.anadolu.edu.tr/acikogretim/sinavlar-ve-sorumluluk-uniteleri/ogrenci-degerlendirme-sistemi",
                "kind": "authoritative-education",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "Sınav ağırlıkları",
                    "Harf notları",
                    "Başarı alt sınırı ve bağıl değerlendirme"
                ]
            },
            {
                "sourceId": "tr-anadolu-aof-assessment-principles",
                "sourceGroupId": "tr_anadolu_aof_assessment",
                "title": "Açıköğretim Öğrenci Değerlendirme Sistemi Esasları",
                "publisher": "Anadolu Üniversitesi",
                "url": "https://www.anadolu.edu.tr/acikogretim/yonetmelikler-ve-esaslar-yonergeler/esaslar-yonergeler/anadolu-universitesi-acikogretim-iktisat-ve-isletme-fakulteleri-ogrenci-degerlendirme-sistemi-esasla",
                "kind": "authoritative-education",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "Ders başarı notu",
                    "Harf katsayıları",
                    "Değerlendirme ilkeleri"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 0,
                    "maximum": 100,
                    "maxDecimalPlaces": 4,
                    "options": [],
                    "visibleWhen": null,
                    "id": "midtermScore",
                    "type": "number",
                    "label": "Ara sınav notu",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 0,
                    "maximum": 100,
                    "maxDecimalPlaces": 4,
                    "options": [],
                    "visibleWhen": null,
                    "id": "targetScore",
                    "type": "number",
                    "label": "Hedef başarı notu",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "%",
                    "minimum": 0,
                    "maximum": 100,
                    "maxDecimalPlaces": 4,
                    "options": [],
                    "visibleWhen": null,
                    "id": "midtermWeightPercent",
                    "type": "number",
                    "label": "Ara sınav ağırlığı",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "%",
                    "minimum": 0,
                    "maximum": 100,
                    "maxDecimalPlaces": 4,
                    "options": [],
                    "visibleWhen": null,
                    "id": "finalWeightPercent",
                    "type": "number",
                    "label": "Dönem sonu sınavı ağırlığı",
                    "required": true
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "midtermContribution",
                    "type": "number",
                    "label": "Ara sınav katkısı",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "targetScore",
                    "type": "number",
                    "label": "Hedef başarı notu",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "requiredFinalScore",
                    "type": "number",
                    "label": "Gerekli dönem sonu sınav notu",
                    "unit": null,
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "status",
                    "type": "text",
                    "label": "Erişilebilirlik durumu",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "attainable",
                    "type": "boolean",
                    "label": "Hedef erişilebilir mi?",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                }
            ]
        },
        "content": {
            "title": "AÖF Final Geçme Notu Hesaplama",
            "shortTitle": "AÖF Gerekli Final",
            "summary": "Girilen ara sınav notu, hedef ve ağırlıklardan dönem sonu sınavında gereken notu hesaplar.",
            "method": "Gerekli final=(hedef−ara sınav katkısı)/(final ağırlığı/100) olarak bulunur; ağırlıkların toplamı 100 olmalıdır.",
            "limitations": [
                "Bağıl değerlendirme, ders özelindeki alt sınırlar ve harf notu kararı bu aritmetik kapsamın dışındadır.",
                "Kullanıcı ağırlıkları güncel resmî sistemden doğrulamalıdır."
            ],
            "faqs": [
                {
                    "question": "AÖF Gerekli Final sonucu resmî sınav veya yerleştirme sonucu mudur?",
                    "answer": "Hayır. Araç yalnız gösterilen aritmetiği ve kaynak incelemesinden geçmiş dönemsel configi uygular. Standart puan, aday kitlesi, kurum kararı ve tercih uygunluğu ayrıca resmî kaynaktan doğrulanmalıdır."
                }
            ],
            "sourcePanel": {
                "title": "Resmî kaynak ve inceleme kapsamı",
                "summary": "Formül ve dönemsel sınav verileri kaynak kimlikli configte tutulur. İnceleme tarihi geldiğinde, kaynak değişmemiş olsa bile insan kontrolü yapılmadan kayıt yenilenmez.",
                "sourceIds": [
                    "tr-anadolu-aof-assessment-system",
                    "tr-anadolu-aof-assessment-principles"
                ],
                "notes": [
                    "Kaynakta değişiklik varsa config güncellenir; değişiklik yoksa yalnız doğrulanmış inceleme tarihi yenilenir."
                ],
                "nextReviewOn": "2027-01-01"
            }
        },
        "config": {
            "kind": "aof-required-final-v1",
            "calculatorId": "egitim-sinav.aof-final-gecme-notu-hesaplama",
            "policyLifecycle": {
                "configVersion": "1.0.0",
                "sourceIds": [
                    "tr-anadolu-aof-assessment-system",
                    "tr-anadolu-aof-assessment-principles"
                ],
                "reviewedOn": "2026-08-25",
                "effectiveFrom": "2026-08-25",
                "effectiveTo": null,
                "reviewAfter": "2027-01-01"
            },
            "scoreRange": [
                0,
                100
            ],
            "weightPercentRange": [
                0,
                100
            ],
            "weightSum": 100,
            "weightTolerance": 0.01
        },
        "testVectors": {
            "kind": "source-education-oracle-v1",
            "cases": [
                {
                    "caseId": "attainable-final",
                    "scenario": "positive",
                    "input": {
                        "midtermScore": 70,
                        "targetScore": 50,
                        "midtermWeightPercent": 30,
                        "finalWeightPercent": 70
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "midtermContribution": 21,
                            "requiredFinalScore": 41.42857142857143,
                            "status": "attainable"
                        }
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "zero-final-weight",
                    "scenario": "adversarial",
                    "input": {
                        "midtermScore": 70,
                        "targetScore": 50,
                        "midtermWeightPercent": 100,
                        "finalWeightPercent": 0
                    },
                    "expected": {
                        "ok": false,
                        "field": "finalWeightPercent",
                        "errorCode": "DIVISION_BY_ZERO"
                    },
                    "tolerance": 1e-10
                }
            ]
        },
        "lastReviewed": "2026-08-25"
    },
    {
        "toolId": "scholarship-iokbs-score",
        "slug": "bursluluk-iokbs-puan",
        "path": "/egitim-sinav/bursluluk-iokbs-puan-hesaplama",
        "category": {
            "id": "egitim-sinav",
            "label": "Eğitim ve Sınav"
        },
        "classification": "remote-config",
        "engineId": "iokbs-net-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-08-25",
        "effectiveTo": null,
        "minAppVersion": "0.4.0",
        "updateCadence": {
            "mode": "scheduled-source-review",
            "days": 30,
            "reviewAfter": "2027-01-01",
            "specialMonths": [
                1,
                2,
                4
            ],
            "note": "Belirlenen aralıkta resmî kaynak incelenir; değişiklik varsa config, yoksa doğrulanmış inceleme tarihi yenilenir."
        },
        "sources": [
            {
                "sourceId": "tr-iokbs-2026-guide",
                "sourceGroupId": "tr_iokbs_rules",
                "title": "2026 İOKBS Başvuru ve Uygulama Kılavuzu",
                "publisher": "Millî Eğitim Bakanlığı",
                "url": "https://odsgm.meb.gov.tr/meb_iys_dosyalar/2026_02/6982fcaec967e589152464_iokbs_basvuru_2026.pdf",
                "kind": "official-government",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "Sınıf düzeyleri",
                    "Ders ve soru sayıları",
                    "Net kuralı",
                    "Başvuru koşulları"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "5",
                            "label": "5. Sınıf"
                        },
                        {
                            "value": "6",
                            "label": "6. Sınıf"
                        },
                        {
                            "value": "7",
                            "label": "7. Sınıf"
                        },
                        {
                            "value": "8",
                            "label": "8. Sınıf"
                        },
                        {
                            "value": "hazirlik",
                            "label": "Hazırlık Sınıfı"
                        },
                        {
                            "value": "9",
                            "label": "9. Sınıf"
                        },
                        {
                            "value": "10",
                            "label": "10. Sınıf"
                        },
                        {
                            "value": "11",
                            "label": "11. Sınıf"
                        }
                    ],
                    "visibleWhen": null,
                    "id": "gradeLevel",
                    "type": "select",
                    "label": "Sınıf düzeyi",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 0,
                    "maximum": 20,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "turkishCorrect",
                    "type": "number",
                    "label": "Türkçe doğru",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 0,
                    "maximum": 20,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "turkishWrong",
                    "type": "number",
                    "label": "Türkçe yanlış",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 0,
                    "maximum": 20,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "mathematicsCorrect",
                    "type": "number",
                    "label": "Matematik doğru",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 0,
                    "maximum": 20,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "mathematicsWrong",
                    "type": "number",
                    "label": "Matematik yanlış",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 0,
                    "maximum": 20,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "scienceCorrect",
                    "type": "number",
                    "label": "Fen Bilimleri doğru",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 0,
                    "maximum": 20,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "scienceWrong",
                    "type": "number",
                    "label": "Fen Bilimleri yanlış",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 0,
                    "maximum": 20,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "socialCorrect",
                    "type": "number",
                    "label": "Sosyal Bilimler doğru",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 0,
                    "maximum": 20,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "socialWrong",
                    "type": "number",
                    "label": "Sosyal Bilimler yanlış",
                    "required": true
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "gradeLevel",
                    "type": "text",
                    "label": "Sınıf düzeyi",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "turkishNet",
                    "type": "number",
                    "label": "Türkçe neti",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "mathematicsNet",
                    "type": "number",
                    "label": "Matematik neti",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "scienceNet",
                    "type": "number",
                    "label": "Fen Bilimleri neti",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "socialNet",
                    "type": "number",
                    "label": "Sosyal Bilimler neti",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "totalNet",
                    "type": "number",
                    "label": "Toplam net",
                    "unit": null,
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "officialScoreAvailable",
                    "type": "boolean",
                    "label": "Resmî puan üretilebilir mi?",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                }
            ]
        },
        "content": {
            "title": "Bursluluk İOKBS Net Hesaplama",
            "shortTitle": "İOKBS Net",
            "summary": "Dört testteki doğru-yanlış sayılarını üç yanlışın bir doğruyu götürdüğü kuralla ham nete çevirir.",
            "method": "Her testte net=doğru−yanlış/3 uygulanır ve test netleri toplanır.",
            "limitations": [
                "Gelir sınırı, kontenjan kategorisi ve standart puan hesaplanmaz.",
                "Araç resmî bursluluk sonucu veya hak sahipliği kararı üretmez."
            ],
            "faqs": [
                {
                    "question": "İOKBS Net sonucu resmî sınav veya yerleştirme sonucu mudur?",
                    "answer": "Hayır. Araç yalnız gösterilen aritmetiği ve kaynak incelemesinden geçmiş dönemsel configi uygular. Standart puan, aday kitlesi, kurum kararı ve tercih uygunluğu ayrıca resmî kaynaktan doğrulanmalıdır."
                }
            ],
            "sourcePanel": {
                "title": "Resmî kaynak ve inceleme kapsamı",
                "summary": "Formül ve dönemsel sınav verileri kaynak kimlikli configte tutulur. İnceleme tarihi geldiğinde, kaynak değişmemiş olsa bile insan kontrolü yapılmadan kayıt yenilenmez.",
                "sourceIds": [
                    "tr-iokbs-2026-guide"
                ],
                "notes": [
                    "Kaynakta değişiklik varsa config güncellenir; değişiklik yoksa yalnız doğrulanmış inceleme tarihi yenilenir."
                ],
                "nextReviewOn": "2027-01-01"
            }
        },
        "config": {
            "kind": "iokbs-net-v1",
            "calculatorId": "egitim-sinav.bursluluk-iokbs-puan-hesaplama",
            "policyLifecycle": {
                "configVersion": "1.0.0",
                "sourceIds": [
                    "tr-iokbs-2026-guide"
                ],
                "reviewedOn": "2026-08-25",
                "effectiveFrom": "2026-08-25",
                "effectiveTo": null,
                "reviewAfter": "2027-01-01"
            },
            "gradeLevels": [
                "5",
                "6",
                "7",
                "8",
                "hazirlik",
                "9",
                "10",
                "11"
            ],
            "questionsPerTest": 20,
            "wrongPenaltyDivisor": 3
        },
        "testVectors": {
            "kind": "source-education-oracle-v1",
            "cases": [
                {
                    "caseId": "four-test-net",
                    "scenario": "positive",
                    "input": {
                        "gradeLevel": "8",
                        "turkishCorrect": 15,
                        "turkishWrong": 3,
                        "mathematicsCorrect": 15,
                        "mathematicsWrong": 3,
                        "scienceCorrect": 15,
                        "scienceWrong": 3,
                        "socialCorrect": 15,
                        "socialWrong": 3
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "turkishNet": 14,
                            "mathematicsNet": 14,
                            "scienceNet": 14,
                            "socialNet": 14,
                            "totalNet": 56
                        }
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "turkish-count-over-test",
                    "scenario": "adversarial",
                    "input": {
                        "gradeLevel": "8",
                        "turkishCorrect": 20,
                        "turkishWrong": 1,
                        "mathematicsCorrect": 1,
                        "mathematicsWrong": 0,
                        "scienceCorrect": 1,
                        "scienceWrong": 0,
                        "socialCorrect": 1,
                        "socialWrong": 0
                    },
                    "expected": {
                        "ok": false,
                        "field": "turkishCorrect",
                        "errorCode": "OUT_OF_RANGE"
                    },
                    "tolerance": 1e-10
                }
            ]
        },
        "lastReviewed": "2026-08-25"
    },
    {
        "toolId": "dgs-score",
        "slug": "dgs-puan",
        "path": "/egitim-sinav/dgs-puan-hesaplama",
        "category": {
            "id": "egitim-sinav",
            "label": "Eğitim ve Sınav"
        },
        "classification": "remote-config",
        "engineId": "dgs-net-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-08-25",
        "effectiveTo": null,
        "minAppVersion": "0.4.0",
        "updateCadence": {
            "mode": "scheduled-source-review",
            "days": 7,
            "reviewAfter": "2027-01-01",
            "specialMonths": [
                5,
                6,
                7,
                8
            ],
            "note": "Belirlenen aralıkta resmî kaynak incelenir; değişiklik varsa config, yoksa doğrulanmış inceleme tarihi yenilenir."
        },
        "sources": [
            {
                "sourceId": "tr-dgs-2026-application-guide",
                "sourceGroupId": "tr_dgs_rules_and_program_map",
                "title": "2026-DGS Kılavuz ve Başvuru Bilgileri",
                "publisher": "Ölçme, Seçme ve Yerleştirme Merkezi",
                "url": "https://www.osym.gov.tr/2026dgs-kilavuz-ve-basvuru-bilgileri",
                "kind": "official-government",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "Soru sayıları",
                    "Net kuralı",
                    "ÖBP ve başvuru ilkeleri"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 0,
                    "maximum": 50,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "numericalCorrect",
                    "type": "number",
                    "label": "Sayısal doğru",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 0,
                    "maximum": 50,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "numericalWrong",
                    "type": "number",
                    "label": "Sayısal yanlış",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 0,
                    "maximum": 50,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "verbalCorrect",
                    "type": "number",
                    "label": "Sözel doğru",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 0,
                    "maximum": 50,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "verbalWrong",
                    "type": "number",
                    "label": "Sözel yanlış",
                    "required": true
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "numericalNet",
                    "type": "number",
                    "label": "Sayısal net",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "verbalNet",
                    "type": "number",
                    "label": "Sözel net",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "totalNet",
                    "type": "number",
                    "label": "Toplam net",
                    "unit": null,
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "officialScoreAvailable",
                    "type": "boolean",
                    "label": "Resmî puan üretilebilir mi?",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                }
            ]
        },
        "content": {
            "title": "DGS Net Hesaplama",
            "shortTitle": "DGS Net",
            "summary": "Sayısal ve sözel doğru-yanlış sayılarını dört yanlışın bir doğruyu götürdüğü kuralla ham nete çevirir.",
            "method": "Her bölümde net=doğru−yanlış/4 uygulanır; bölüm netleri toplanır.",
            "limitations": [
                "ÖBP ve aday kitlesinin standartlaştırması olmadan resmî DGS puanı üretilemez.",
                "Araç yalnız ham net analizi verir."
            ],
            "faqs": [
                {
                    "question": "DGS Net sonucu resmî sınav veya yerleştirme sonucu mudur?",
                    "answer": "Hayır. Araç yalnız gösterilen aritmetiği ve kaynak incelemesinden geçmiş dönemsel configi uygular. Standart puan, aday kitlesi, kurum kararı ve tercih uygunluğu ayrıca resmî kaynaktan doğrulanmalıdır."
                }
            ],
            "sourcePanel": {
                "title": "Resmî kaynak ve inceleme kapsamı",
                "summary": "Formül ve dönemsel sınav verileri kaynak kimlikli configte tutulur. İnceleme tarihi geldiğinde, kaynak değişmemiş olsa bile insan kontrolü yapılmadan kayıt yenilenmez.",
                "sourceIds": [
                    "tr-dgs-2026-application-guide"
                ],
                "notes": [
                    "Kaynakta değişiklik varsa config güncellenir; değişiklik yoksa yalnız doğrulanmış inceleme tarihi yenilenir."
                ],
                "nextReviewOn": "2027-01-01"
            }
        },
        "config": {
            "kind": "dgs-net-v1",
            "calculatorId": "egitim-sinav.dgs-puan-hesaplama",
            "policyLifecycle": {
                "configVersion": "1.0.0",
                "sourceIds": [
                    "tr-dgs-2026-application-guide"
                ],
                "reviewedOn": "2026-08-25",
                "effectiveFrom": "2026-08-25",
                "effectiveTo": null,
                "reviewAfter": "2027-01-01"
            },
            "questionsPerSection": 50,
            "wrongPenaltyDivisor": 4
        },
        "testVectors": {
            "kind": "source-education-oracle-v1",
            "cases": [
                {
                    "caseId": "two-section-net",
                    "scenario": "positive",
                    "input": {
                        "numericalCorrect": 40,
                        "numericalWrong": 8,
                        "verbalCorrect": 30,
                        "verbalWrong": 4
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "numericalNet": 38,
                            "verbalNet": 29,
                            "totalNet": 67
                        }
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "verbal-count-over-limit",
                    "scenario": "adversarial",
                    "input": {
                        "numericalCorrect": 1,
                        "numericalWrong": 0,
                        "verbalCorrect": 50,
                        "verbalWrong": 1
                    },
                    "expected": {
                        "ok": false,
                        "field": "verbalCorrect",
                        "errorCode": "OUT_OF_RANGE"
                    },
                    "tolerance": 1e-10
                }
            ]
        },
        "lastReviewed": "2026-08-25"
    },
    {
        "toolId": "dgs-preference-ranking",
        "slug": "dgs-tercih-siralama",
        "path": "/egitim-sinav/dgs-tercih-siralama-hesaplama",
        "category": {
            "id": "egitim-sinav",
            "label": "Eğitim ve Sınav"
        },
        "classification": "remote-config",
        "engineId": "dgs-ranking-difference-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-08-25",
        "effectiveTo": null,
        "minAppVersion": "0.4.0",
        "updateCadence": {
            "mode": "scheduled-source-review",
            "days": 7,
            "reviewAfter": "2027-01-01",
            "specialMonths": [
                5,
                6,
                7,
                8
            ],
            "note": "Belirlenen aralıkta resmî kaynak incelenir; değişiklik varsa config, yoksa doğrulanmış inceleme tarihi yenilenir."
        },
        "sources": [
            {
                "sourceId": "tr-dgs-2026-application-guide",
                "sourceGroupId": "tr_dgs_rules_and_program_map",
                "title": "2026-DGS Kılavuz ve Başvuru Bilgileri",
                "publisher": "Ölçme, Seçme ve Yerleştirme Merkezi",
                "url": "https://www.osym.gov.tr/2026dgs-kilavuz-ve-basvuru-bilgileri",
                "kind": "official-government",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "Soru sayıları",
                    "Net kuralı",
                    "ÖBP ve başvuru ilkeleri"
                ]
            },
            {
                "sourceId": "tr-dgs-2026-table-2",
                "sourceGroupId": "tr_dgs_rules_and_program_map",
                "title": "2026-DGS Tablo-2 — Ön Lisans Alanlarına Göre Geçiş Programları",
                "publisher": "Ölçme, Seçme ve Yerleştirme Merkezi",
                "url": "https://dokuman.osym.gov.tr/web/2026/7/tablo-2-on-lisans-mezuniyet-alanlarina-gore-dikey-gecis-yapilabilecek-lisans-programlari-neb9zs-30102212.pdf",
                "kind": "official-government",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "Ön lisans mezuniyet alanı",
                    "Geçiş yapılabilecek lisans programları"
                ]
            },
            {
                "sourceId": "tr-dgs-official-menu",
                "sourceGroupId": "tr_dgs_rules_and_program_map",
                "title": "DGS Resmî Duyuru ve Kılavuz Dizini",
                "publisher": "Ölçme, Seçme ve Yerleştirme Merkezi",
                "url": "https://www.osym.gov.tr/SinavGrubu/Menu/761",
                "kind": "official-government",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "2026 tercih kılavuzunun yayımlanma durumu",
                    "Tercih tarihleri ve koşulları"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "say",
                            "label": "Sayısal (SAY)"
                        },
                        {
                            "value": "ea",
                            "label": "Eşit Ağırlık (EA)"
                        },
                        {
                            "value": "soz",
                            "label": "Sözel (SÖZ)"
                        }
                    ],
                    "visibleWhen": null,
                    "id": "scoreType",
                    "type": "select",
                    "label": "DGS puan türü",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 1,
                    "maximum": 999999999,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "candidateRank",
                    "type": "number",
                    "label": "Aday başarı sırası",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 1,
                    "maximum": 999999999,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "targetRank",
                    "type": "number",
                    "label": "Hedef programın önceki başarı sırası",
                    "required": true
                },
                {
                    "defaultValue": false,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [],
                    "visibleWhen": null,
                    "id": "table2Confirmed",
                    "type": "boolean",
                    "label": "Mezuniyet alanı Tablo-2 ile doğrulandı",
                    "required": true
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "scoreType",
                    "type": "text",
                    "label": "Puan türü",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "candidateRank",
                    "type": "number",
                    "label": "Aday başarı sırası",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "targetRank",
                    "type": "number",
                    "label": "Hedef program başarı sırası",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "difference",
                    "type": "number",
                    "label": "Sıralama farkı",
                    "unit": null,
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "position",
                    "type": "text",
                    "label": "Aritmetik konum",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "claimScope",
                    "type": "text",
                    "label": "Sonuç kapsamı",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "table2Confirmed",
                    "type": "boolean",
                    "label": "Tablo-2 doğrulandı",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "currentPreferenceGuideAvailable",
                    "type": "boolean",
                    "label": "Güncel tercih kılavuzu mevcut mu?",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                }
            ]
        },
        "content": {
            "title": "DGS Tercih Sıralama Farkı Hesaplama",
            "shortTitle": "DGS Tercih Sıralaması",
            "summary": "Aday sırası ile hedef programın geçmiş sırası arasındaki salt aritmetik farkı gösterir; Tablo-2 alan doğrulamasını zorunlu tutar.",
            "method": "Mutlak sıra farkı hesaplanır ve adayın hedef sıranın önünde, gerisinde veya eşit olduğu belirtilir.",
            "limitations": [
                "25.08.2026 incelemesinde 2026 DGS tercih kılavuzu yayımlanmamıştır; sonuç güncel tercih önerisi değildir.",
                "Kontenjan, özel koşul, program uygunluğu ve yerleşme olasılığı üretilmez."
            ],
            "faqs": [
                {
                    "question": "DGS Tercih Sıralaması sonucu resmî sınav veya yerleştirme sonucu mudur?",
                    "answer": "Hayır. Araç yalnız gösterilen aritmetiği ve kaynak incelemesinden geçmiş dönemsel configi uygular. Standart puan, aday kitlesi, kurum kararı ve tercih uygunluğu ayrıca resmî kaynaktan doğrulanmalıdır."
                }
            ],
            "sourcePanel": {
                "title": "Resmî kaynak ve inceleme kapsamı",
                "summary": "Formül ve dönemsel sınav verileri kaynak kimlikli configte tutulur. İnceleme tarihi geldiğinde, kaynak değişmemiş olsa bile insan kontrolü yapılmadan kayıt yenilenmez.",
                "sourceIds": [
                    "tr-dgs-2026-application-guide",
                    "tr-dgs-2026-table-2",
                    "tr-dgs-official-menu"
                ],
                "notes": [
                    "Kaynakta değişiklik varsa config güncellenir; değişiklik yoksa yalnız doğrulanmış inceleme tarihi yenilenir."
                ],
                "nextReviewOn": "2027-01-01"
            }
        },
        "config": {
            "kind": "dgs-ranking-difference-v1",
            "calculatorId": "egitim-sinav.dgs-tercih-siralama-hesaplama",
            "policyLifecycle": {
                "configVersion": "1.0.0",
                "sourceIds": [
                    "tr-dgs-2026-application-guide",
                    "tr-dgs-2026-table-2",
                    "tr-dgs-official-menu"
                ],
                "reviewedOn": "2026-08-25",
                "effectiveFrom": "2026-08-25",
                "effectiveTo": null,
                "reviewAfter": "2027-01-01"
            },
            "scoreTypes": [
                "say",
                "ea",
                "soz"
            ],
            "rankRange": [
                1,
                999999999
            ],
            "requireTable2Confirmation": true,
            "preferenceGuidePublished": false,
            "applicationGuideSourceId": "tr-dgs-2026-application-guide",
            "table2SourceId": "tr-dgs-2026-table-2",
            "preferenceGuideSourceId": null
        },
        "testVectors": {
            "kind": "source-education-oracle-v1",
            "cases": [
                {
                    "caseId": "arithmetic-ahead-without-current-guide",
                    "scenario": "positive",
                    "input": {
                        "scoreType": "say",
                        "candidateRank": 10000,
                        "targetRank": 12000,
                        "table2Confirmed": true
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "difference": 2000,
                            "position": "ahead",
                            "claimScope": "arithmetic-only",
                            "currentPreferenceGuideAvailable": false
                        }
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "table-two-not-confirmed",
                    "scenario": "adversarial",
                    "input": {
                        "scoreType": "say",
                        "candidateRank": 10000,
                        "targetRank": 12000,
                        "table2Confirmed": false
                    },
                    "expected": {
                        "ok": false,
                        "field": "table2Confirmed",
                        "errorCode": "REQUIRED"
                    },
                    "tolerance": 1e-10
                }
            ]
        },
        "lastReviewed": "2026-08-25"
    },
    {
        "toolId": "kpss-score",
        "slug": "kpss-puan",
        "path": "/egitim-sinav/kpss-puan-hesaplama",
        "category": {
            "id": "egitim-sinav",
            "label": "Eğitim ve Sınav"
        },
        "classification": "remote-config",
        "engineId": "kpss-net-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-08-25",
        "effectiveTo": null,
        "minAppVersion": "0.4.0",
        "updateCadence": {
            "mode": "scheduled-source-review",
            "days": 30,
            "reviewAfter": "2027-01-01",
            "specialMonths": [
                6,
                7
            ],
            "note": "Belirlenen aralıkta resmî kaynak incelenir; değişiklik varsa config, yoksa doğrulanmış inceleme tarihi yenilenir."
        },
        "sources": [
            {
                "sourceId": "tr-kpss-2026-licence-guide",
                "sourceGroupId": "tr_kpss_rules",
                "title": "2026-KPSS Lisans Kılavuzu",
                "publisher": "Ölçme, Seçme ve Yerleştirme Merkezi",
                "url": "https://dokuman.osym.gov.tr/pdfdokuman/2026/KPSS/LISANS/kilavuz_Ld01072026.pdf",
                "kind": "official-government",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "Test ve soru sayıları",
                    "Net kuralı",
                    "Standart puan ve iptal soruları"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "lisans",
                            "label": "Lisans (4 yıllık)"
                        },
                        {
                            "value": "onlisans",
                            "label": "Önlisans (2 yıllık)"
                        },
                        {
                            "value": "ortaogretim",
                            "label": "Ortaöğretim (Lise)"
                        }
                    ],
                    "visibleWhen": null,
                    "id": "level",
                    "type": "select",
                    "label": "KPSS öğrenim düzeyi",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 0,
                    "maximum": 60,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "generalAbilityCorrect",
                    "type": "number",
                    "label": "Genel Yetenek doğru",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 0,
                    "maximum": 60,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "generalAbilityWrong",
                    "type": "number",
                    "label": "Genel Yetenek yanlış",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 0,
                    "maximum": 60,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "generalCultureCorrect",
                    "type": "number",
                    "label": "Genel Kültür doğru",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 0,
                    "maximum": 60,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "generalCultureWrong",
                    "type": "number",
                    "label": "Genel Kültür yanlış",
                    "required": true
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "level",
                    "type": "text",
                    "label": "Öğrenim düzeyi",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "generalAbilityNet",
                    "type": "number",
                    "label": "Genel Yetenek neti",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "generalCultureNet",
                    "type": "number",
                    "label": "Genel Kültür neti",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "totalNet",
                    "type": "number",
                    "label": "Toplam net",
                    "unit": null,
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "officialScoreAvailable",
                    "type": "boolean",
                    "label": "Resmî puan üretilebilir mi?",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                }
            ]
        },
        "content": {
            "title": "KPSS Net Hesaplama",
            "shortTitle": "KPSS Net",
            "summary": "Genel Yetenek ve Genel Kültür doğru-yanlış sayılarını dört yanlışın bir doğruyu götürdüğü kuralla ham nete çevirir.",
            "method": "Her testte net=doğru−yanlış/4 uygulanır ve iki testin netleri toplanır.",
            "limitations": [
                "Standart sapma, ortalama ve aday kitlesi olmadan KPSS puanı üretilemez.",
                "Her iki testte de en az bir cevap işaretlenmiş olmalıdır."
            ],
            "faqs": [
                {
                    "question": "KPSS Net sonucu resmî sınav veya yerleştirme sonucu mudur?",
                    "answer": "Hayır. Araç yalnız gösterilen aritmetiği ve kaynak incelemesinden geçmiş dönemsel configi uygular. Standart puan, aday kitlesi, kurum kararı ve tercih uygunluğu ayrıca resmî kaynaktan doğrulanmalıdır."
                }
            ],
            "sourcePanel": {
                "title": "Resmî kaynak ve inceleme kapsamı",
                "summary": "Formül ve dönemsel sınav verileri kaynak kimlikli configte tutulur. İnceleme tarihi geldiğinde, kaynak değişmemiş olsa bile insan kontrolü yapılmadan kayıt yenilenmez.",
                "sourceIds": [
                    "tr-kpss-2026-licence-guide"
                ],
                "notes": [
                    "Kaynakta değişiklik varsa config güncellenir; değişiklik yoksa yalnız doğrulanmış inceleme tarihi yenilenir."
                ],
                "nextReviewOn": "2027-01-01"
            }
        },
        "config": {
            "kind": "kpss-net-v1",
            "calculatorId": "egitim-sinav.kpss-puan-hesaplama",
            "policyLifecycle": {
                "configVersion": "1.0.0",
                "sourceIds": [
                    "tr-kpss-2026-licence-guide"
                ],
                "reviewedOn": "2026-08-25",
                "effectiveFrom": "2026-08-25",
                "effectiveTo": null,
                "reviewAfter": "2027-01-01"
            },
            "levels": [
                "lisans",
                "onlisans",
                "ortaogretim"
            ],
            "questionsPerTest": 60,
            "wrongPenaltyDivisor": 4,
            "requireEachTestAttempted": true
        },
        "testVectors": {
            "kind": "source-education-oracle-v1",
            "cases": [
                {
                    "caseId": "general-tests-net",
                    "scenario": "positive",
                    "input": {
                        "level": "lisans",
                        "generalAbilityCorrect": 40,
                        "generalAbilityWrong": 8,
                        "generalCultureCorrect": 30,
                        "generalCultureWrong": 4
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "generalAbilityNet": 38,
                            "generalCultureNet": 29,
                            "totalNet": 67
                        }
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "general-ability-not-attempted",
                    "scenario": "adversarial",
                    "input": {
                        "level": "lisans",
                        "generalAbilityCorrect": 0,
                        "generalAbilityWrong": 0,
                        "generalCultureCorrect": 1,
                        "generalCultureWrong": 0
                    },
                    "expected": {
                        "ok": false,
                        "field": "generalAbilityCorrect",
                        "errorCode": "REQUIRED"
                    },
                    "tolerance": 1e-10
                }
            ]
        },
        "lastReviewed": "2026-08-25"
    },
    {
        "toolId": "lgs-score",
        "slug": "lgs-puan",
        "path": "/egitim-sinav/lgs-puan-hesaplama",
        "category": {
            "id": "egitim-sinav",
            "label": "Eğitim ve Sınav"
        },
        "classification": "remote-config",
        "engineId": "lgs-net-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-08-25",
        "effectiveTo": null,
        "minAppVersion": "0.4.0",
        "updateCadence": {
            "mode": "scheduled-source-review",
            "days": 30,
            "reviewAfter": "2027-01-01",
            "specialMonths": [
                3,
                4,
                6
            ],
            "note": "Belirlenen aralıkta resmî kaynak incelenir; değişiklik varsa config, yoksa doğrulanmış inceleme tarihi yenilenir."
        },
        "sources": [
            {
                "sourceId": "tr-lgs-2026-guide",
                "sourceGroupId": "tr_lgs_rules",
                "title": "2026 LGS Başvuru ve Uygulama Kılavuzu",
                "publisher": "Millî Eğitim Bakanlığı",
                "url": "https://www.meb.gov.tr/meb_iys_dosyalar/2026_04/03170012_LGS_Basvuru_ve_Uygulama_Kilavuzu_2026_.pdf",
                "kind": "official-government",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "Dersler ve soru sayıları",
                    "Net kuralı",
                    "Merkezî sınav değerlendirmesi"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 0,
                    "maximum": 20,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "turkishCorrect",
                    "type": "number",
                    "label": "Türkçe doğru",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 0,
                    "maximum": 20,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "turkishWrong",
                    "type": "number",
                    "label": "Türkçe yanlış",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 0,
                    "maximum": 10,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "revolutionHistoryCorrect",
                    "type": "number",
                    "label": "T.C. İnkılap Tarihi doğru",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 0,
                    "maximum": 10,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "revolutionHistoryWrong",
                    "type": "number",
                    "label": "T.C. İnkılap Tarihi yanlış",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 0,
                    "maximum": 10,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "foreignLanguageCorrect",
                    "type": "number",
                    "label": "Yabancı Dil doğru",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 0,
                    "maximum": 10,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "foreignLanguageWrong",
                    "type": "number",
                    "label": "Yabancı Dil yanlış",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 0,
                    "maximum": 10,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "religionCorrect",
                    "type": "number",
                    "label": "Din Kültürü doğru",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 0,
                    "maximum": 10,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "religionWrong",
                    "type": "number",
                    "label": "Din Kültürü yanlış",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 0,
                    "maximum": 20,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "mathematicsCorrect",
                    "type": "number",
                    "label": "Matematik doğru",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 0,
                    "maximum": 20,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "mathematicsWrong",
                    "type": "number",
                    "label": "Matematik yanlış",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 0,
                    "maximum": 20,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "scienceCorrect",
                    "type": "number",
                    "label": "Fen Bilimleri doğru",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 0,
                    "maximum": 20,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "scienceWrong",
                    "type": "number",
                    "label": "Fen Bilimleri yanlış",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 0,
                    "maximum": 100,
                    "maxDecimalPlaces": 4,
                    "options": [],
                    "visibleWhen": null,
                    "id": "schoolAchievementScore",
                    "type": "number",
                    "label": "Okul başarı puanı (bilgi amaçlı)",
                    "required": false
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "testNets",
                    "type": "object",
                    "label": "Test netleri",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "verbalNet",
                    "type": "number",
                    "label": "Sözel bölüm neti",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "numericalNet",
                    "type": "number",
                    "label": "Sayısal bölüm neti",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "totalNet",
                    "type": "number",
                    "label": "Toplam net",
                    "unit": null,
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "schoolAchievementScore",
                    "type": "number",
                    "label": "Okul başarı puanı",
                    "unit": null,
                    "nullable": true,
                    "prominent": false
                },
                {
                    "id": "schoolAchievementIncludedInCentralScore",
                    "type": "boolean",
                    "label": "Okul puanı merkezî skora katıldı mı?",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "officialCentralScoreAvailable",
                    "type": "boolean",
                    "label": "Resmî merkezî puan üretilebilir mi?",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                }
            ]
        },
        "content": {
            "title": "LGS Net Hesaplama",
            "shortTitle": "LGS Net",
            "summary": "Altı testteki doğru ve yanlışlardan üç yanlışın bir doğruyu götürdüğü kuralla test ve bölüm netlerini çıkarır.",
            "method": "Her testte net=doğru−yanlış/3 uygulanır; sözel ve sayısal testler ayrı toplanır.",
            "limitations": [
                "Standartlaştırma olmadan resmî merkezî LGS puanı hesaplanmaz.",
                "Okul başarı puanı yalnız bilgi amaçlı taşınır ve merkezî net toplamına eklenmez."
            ],
            "faqs": [
                {
                    "question": "LGS Net sonucu resmî sınav veya yerleştirme sonucu mudur?",
                    "answer": "Hayır. Araç yalnız gösterilen aritmetiği ve kaynak incelemesinden geçmiş dönemsel configi uygular. Standart puan, aday kitlesi, kurum kararı ve tercih uygunluğu ayrıca resmî kaynaktan doğrulanmalıdır."
                }
            ],
            "sourcePanel": {
                "title": "Resmî kaynak ve inceleme kapsamı",
                "summary": "Formül ve dönemsel sınav verileri kaynak kimlikli configte tutulur. İnceleme tarihi geldiğinde, kaynak değişmemiş olsa bile insan kontrolü yapılmadan kayıt yenilenmez.",
                "sourceIds": [
                    "tr-lgs-2026-guide"
                ],
                "notes": [
                    "Kaynakta değişiklik varsa config güncellenir; değişiklik yoksa yalnız doğrulanmış inceleme tarihi yenilenir."
                ],
                "nextReviewOn": "2027-01-01"
            }
        },
        "config": {
            "kind": "lgs-net-v1",
            "calculatorId": "egitim-sinav.lgs-puan-hesaplama",
            "policyLifecycle": {
                "configVersion": "1.0.0",
                "sourceIds": [
                    "tr-lgs-2026-guide"
                ],
                "reviewedOn": "2026-08-25",
                "effectiveFrom": "2026-08-25",
                "effectiveTo": null,
                "reviewAfter": "2027-01-01"
            },
            "tests": [
                {
                    "id": "turkish",
                    "maximumQuestions": 20
                },
                {
                    "id": "revolution-history",
                    "maximumQuestions": 10
                },
                {
                    "id": "foreign-language",
                    "maximumQuestions": 10
                },
                {
                    "id": "religion",
                    "maximumQuestions": 10
                },
                {
                    "id": "mathematics",
                    "maximumQuestions": 20
                },
                {
                    "id": "science",
                    "maximumQuestions": 20
                }
            ],
            "wrongPenaltyDivisor": 3,
            "optionalSchoolAchievementRange": [
                0,
                100
            ]
        },
        "testVectors": {
            "kind": "source-education-oracle-v1",
            "cases": [
                {
                    "caseId": "six-test-net",
                    "scenario": "positive",
                    "input": {
                        "turkishCorrect": 15,
                        "turkishWrong": 3,
                        "revolutionHistoryCorrect": 7,
                        "revolutionHistoryWrong": 0,
                        "foreignLanguageCorrect": 6,
                        "foreignLanguageWrong": 0,
                        "religionCorrect": 9,
                        "religionWrong": 0,
                        "mathematicsCorrect": 17,
                        "mathematicsWrong": 0,
                        "scienceCorrect": 16,
                        "scienceWrong": 0
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "verbalNet": 36,
                            "numericalNet": 33,
                            "totalNet": 69
                        }
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "turkish-count-over-test",
                    "scenario": "adversarial",
                    "input": {
                        "turkishCorrect": 20,
                        "turkishWrong": 1,
                        "revolutionHistoryCorrect": 1,
                        "revolutionHistoryWrong": 0,
                        "foreignLanguageCorrect": 1,
                        "foreignLanguageWrong": 0,
                        "religionCorrect": 1,
                        "religionWrong": 0,
                        "mathematicsCorrect": 1,
                        "mathematicsWrong": 0,
                        "scienceCorrect": 1,
                        "scienceWrong": 0
                    },
                    "expected": {
                        "ok": false,
                        "field": "turkishCorrect",
                        "errorCode": "OUT_OF_RANGE"
                    },
                    "tolerance": 1e-10
                }
            ]
        },
        "lastReviewed": "2026-08-25"
    },
    {
        "toolId": "driving-license-exam",
        "slug": "motorlu-tasit-surucu-sinavi",
        "path": "/egitim-sinav/motorlu-tasit-surucu-sinavi-hesaplama",
        "category": {
            "id": "egitim-sinav",
            "label": "Eğitim ve Sınav"
        },
        "classification": "remote-config",
        "engineId": "driving-license-exam-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-08-25",
        "effectiveTo": null,
        "minAppVersion": "0.4.0",
        "updateCadence": {
            "mode": "scheduled-source-review",
            "days": 30,
            "reviewAfter": "2027-01-01",
            "specialMonths": [],
            "note": "Belirlenen aralıkta resmî kaynak incelenir; değişiklik varsa config, yoksa doğrulanmış inceleme tarihi yenilenir."
        },
        "sources": [
            {
                "sourceId": "tr-driving-exam-2026-guide",
                "sourceGroupId": "tr_driving_exam_rules",
                "title": "2026 MTSKS e-Sınav Başvuru ve Uygulama Kılavuzu",
                "publisher": "Millî Eğitim Bakanlığı",
                "url": "https://odsgm.meb.gov.tr/www/motorlu-tasit-surucu-kursiyerleri-e-sinav-basvuru-ve-uygulama-kilavuzu-2026/icerik/1531",
                "kind": "official-government",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "Soru sayısı",
                    "Yanlışların etkisi",
                    "Başarı puanı",
                    "Sınav süresi"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 0,
                    "maximum": 50,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "correctAnswers",
                    "type": "number",
                    "label": "Doğru cevap",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 0,
                    "maximum": 50,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "wrongAnswers",
                    "type": "number",
                    "label": "Yanlış cevap",
                    "required": false
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 0,
                    "maximum": 50,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "blankAnswers",
                    "type": "number",
                    "label": "Boş cevap",
                    "required": false
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "correctAnswers",
                    "type": "number",
                    "label": "Doğru cevap",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "wrongAnswers",
                    "type": "number",
                    "label": "Yanlış cevap",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "blankAnswers",
                    "type": "number",
                    "label": "Boş cevap",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "score",
                    "type": "number",
                    "label": "Sınav puanı",
                    "unit": null,
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "passingScore",
                    "type": "number",
                    "label": "Başarı eşiği",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "minimumCorrectAnswers",
                    "type": "number",
                    "label": "Gerekli en az doğru",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "passed",
                    "type": "boolean",
                    "label": "Başarılı mı?",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "differenceFromThreshold",
                    "type": "number",
                    "label": "Başarı eşiği farkı",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                }
            ]
        },
        "content": {
            "title": "Motorlu Taşıt Sürücü Kursiyerleri e-Sınav Puanı",
            "shortTitle": "MTSKS e-Sınav",
            "summary": "Doğru cevap sayısını soru başına iki puanla çarpar ve 70 puanlık başarı eşiğiyle karşılaştırır.",
            "method": "Yanlışlar puanı düşürmez. Puan=doğru×2; en az 35 doğru 70 puana ulaşır.",
            "limitations": [
                "Araç sınava katılım, sertifika veya ehliyet düzenleme hakkı üretmez.",
                "Yanlış/boş girilirse doğru+yanlış+boş toplamı 50 olmalıdır."
            ],
            "faqs": [
                {
                    "question": "MTSKS e-Sınav sonucu resmî sınav veya yerleştirme sonucu mudur?",
                    "answer": "Hayır. Araç yalnız gösterilen aritmetiği ve kaynak incelemesinden geçmiş dönemsel configi uygular. Standart puan, aday kitlesi, kurum kararı ve tercih uygunluğu ayrıca resmî kaynaktan doğrulanmalıdır."
                }
            ],
            "sourcePanel": {
                "title": "Resmî kaynak ve inceleme kapsamı",
                "summary": "Formül ve dönemsel sınav verileri kaynak kimlikli configte tutulur. İnceleme tarihi geldiğinde, kaynak değişmemiş olsa bile insan kontrolü yapılmadan kayıt yenilenmez.",
                "sourceIds": [
                    "tr-driving-exam-2026-guide"
                ],
                "notes": [
                    "Kaynakta değişiklik varsa config güncellenir; değişiklik yoksa yalnız doğrulanmış inceleme tarihi yenilenir."
                ],
                "nextReviewOn": "2027-01-01"
            }
        },
        "config": {
            "kind": "driving-license-exam-v1",
            "calculatorId": "egitim-sinav.motorlu-tasit-surucu-sinavi-hesaplama",
            "policyLifecycle": {
                "configVersion": "1.0.0",
                "sourceIds": [
                    "tr-driving-exam-2026-guide"
                ],
                "reviewedOn": "2026-08-25",
                "effectiveFrom": "2026-08-25",
                "effectiveTo": null,
                "reviewAfter": "2027-01-01"
            },
            "totalQuestions": 50,
            "pointsPerCorrect": 2,
            "passingScore": 70
        },
        "testVectors": {
            "kind": "source-education-oracle-v1",
            "cases": [
                {
                    "caseId": "passing-threshold",
                    "scenario": "threshold",
                    "input": {
                        "correctAnswers": 35,
                        "wrongAnswers": 10,
                        "blankAnswers": 5
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "score": 70,
                            "passed": true,
                            "differenceFromThreshold": 0
                        }
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "answer-total-mismatch",
                    "scenario": "adversarial",
                    "input": {
                        "correctAnswers": 35,
                        "wrongAnswers": 10,
                        "blankAnswers": 4
                    },
                    "expected": {
                        "ok": false,
                        "field": "correctAnswers",
                        "errorCode": "OUT_OF_RANGE"
                    },
                    "tolerance": 1e-10
                }
            ]
        },
        "lastReviewed": "2026-08-25"
    },
    {
        "toolId": "obp",
        "slug": "obp",
        "path": "/egitim-sinav/obp-hesaplama",
        "category": {
            "id": "egitim-sinav",
            "label": "Eğitim ve Sınav"
        },
        "classification": "remote-config",
        "engineId": "obp-placement-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-08-25",
        "effectiveTo": null,
        "minAppVersion": "0.4.0",
        "updateCadence": {
            "mode": "scheduled-source-review",
            "days": 30,
            "reviewAfter": "2027-01-01",
            "specialMonths": [
                1,
                2,
                6,
                7
            ],
            "note": "Belirlenen aralıkta resmî kaynak incelenir; değişiklik varsa config, yoksa doğrulanmış inceleme tarihi yenilenir."
        },
        "sources": [
            {
                "sourceId": "tr-yks-2026-application-guide",
                "sourceGroupId": "tr_yks_rules_and_content",
                "title": "2026-YKS Başvuru Kılavuzu",
                "publisher": "Ölçme, Seçme ve Yerleştirme Merkezi",
                "url": "https://dokuman.osym.gov.tr/pdfdokuman/2026/YKS/basvuru_kilavuz06022026.pdf",
                "kind": "official-government",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "Test ve soru sayıları",
                    "OBP dönüşümü ve yerleştirme katkısı"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 50,
                    "maximum": 100,
                    "maxDecimalPlaces": 4,
                    "options": [],
                    "visibleWhen": null,
                    "id": "diplomaGrade",
                    "type": "number",
                    "label": "Diploma notu",
                    "required": true
                },
                {
                    "defaultValue": "obp",
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "obp",
                            "label": "Yalnız OBP"
                        },
                        {
                            "value": "placement-contribution",
                            "label": "Yerleştirme katkısı"
                        }
                    ],
                    "visibleWhen": null,
                    "id": "mode",
                    "type": "radio",
                    "label": "Hesaplama türü",
                    "required": true
                },
                {
                    "defaultValue": "standard",
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "standard",
                            "label": "Standart katsayı"
                        },
                        {
                            "value": "reduced",
                            "label": "Önceki yerleşme indirimi"
                        }
                    ],
                    "visibleWhen": null,
                    "id": "placementStatus",
                    "type": "radio",
                    "label": "Yerleşme durumu katsayısı",
                    "required": true
                },
                {
                    "defaultValue": false,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [],
                    "visibleWhen": null,
                    "id": "vocationalExtra",
                    "type": "boolean",
                    "label": "Mesleki ek puan uygulansın",
                    "required": true
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "diplomaGrade",
                    "type": "number",
                    "label": "Diploma notu",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "obp",
                    "type": "number",
                    "label": "OBP",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "mode",
                    "type": "text",
                    "label": "Hesaplama türü",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "placementStatus",
                    "type": "text",
                    "label": "Yerleşme durumu",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "baseCoefficient",
                    "type": "number",
                    "label": "Temel katkı katsayısı",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "baseContribution",
                    "type": "number",
                    "label": "Temel katkı",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "vocationalCoefficient",
                    "type": "number",
                    "label": "Mesleki ek katsayı",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "vocationalContribution",
                    "type": "number",
                    "label": "Mesleki ek katkı",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "totalContribution",
                    "type": "number",
                    "label": "Toplam yerleştirme katkısı",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "displayedValue",
                    "type": "number",
                    "label": "Gösterilen sonuç",
                    "unit": null,
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "scenarioMaximum",
                    "type": "number",
                    "label": "Senaryo üst sınırı",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "ratioPercent",
                    "type": "percentage",
                    "label": "Üst sınıra oran",
                    "unit": "%",
                    "nullable": false,
                    "prominent": false
                }
            ]
        },
        "content": {
            "title": "OBP ve Yerleştirme Katkısı Hesaplama",
            "shortTitle": "OBP",
            "summary": "Diploma notunu OBP'ye dönüştürür; seçilen standart/indirimli ve mesleki ek puan senaryosunda yerleştirme katkısını gösterir.",
            "method": "OBP=diploma notu×5. Temel katkıda 0,12 veya 0,06; mesleki ek katkıda 0,06 veya 0,03 katsayısı kullanılır.",
            "limitations": [
                "Önceki yerleşme ve mesleki ek puan uygunluğu kullanıcı tarafından resmî kılavuzdan doğrulanmalıdır.",
                "Araç YKS ham/yerleştirme puanı üretmez."
            ],
            "faqs": [
                {
                    "question": "OBP sonucu resmî sınav veya yerleştirme sonucu mudur?",
                    "answer": "Hayır. Araç yalnız gösterilen aritmetiği ve kaynak incelemesinden geçmiş dönemsel configi uygular. Standart puan, aday kitlesi, kurum kararı ve tercih uygunluğu ayrıca resmî kaynaktan doğrulanmalıdır."
                }
            ],
            "sourcePanel": {
                "title": "Resmî kaynak ve inceleme kapsamı",
                "summary": "Formül ve dönemsel sınav verileri kaynak kimlikli configte tutulur. İnceleme tarihi geldiğinde, kaynak değişmemiş olsa bile insan kontrolü yapılmadan kayıt yenilenmez.",
                "sourceIds": [
                    "tr-yks-2026-application-guide"
                ],
                "notes": [
                    "Kaynakta değişiklik varsa config güncellenir; değişiklik yoksa yalnız doğrulanmış inceleme tarihi yenilenir."
                ],
                "nextReviewOn": "2027-01-01"
            }
        },
        "config": {
            "kind": "obp-placement-v1",
            "calculatorId": "egitim-sinav.obp-hesaplama",
            "policyLifecycle": {
                "configVersion": "1.0.0",
                "sourceIds": [
                    "tr-yks-2026-application-guide"
                ],
                "reviewedOn": "2026-08-25",
                "effectiveFrom": "2026-08-25",
                "effectiveTo": null,
                "reviewAfter": "2027-01-01"
            },
            "diplomaGradeRange": [
                50,
                100
            ],
            "obpMultiplier": 5,
            "standardBaseCoefficient": 0.12,
            "reducedBaseCoefficient": 0.06,
            "standardVocationalCoefficient": 0.06,
            "reducedVocationalCoefficient": 0.03
        },
        "testVectors": {
            "kind": "source-education-oracle-v1",
            "cases": [
                {
                    "caseId": "standard-vocational-contribution",
                    "scenario": "positive",
                    "input": {
                        "diplomaGrade": 80,
                        "mode": "placement-contribution",
                        "placementStatus": "standard",
                        "vocationalExtra": true
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "obp": 400,
                            "baseContribution": 48,
                            "vocationalContribution": 24,
                            "totalContribution": 72
                        }
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "obp-mode-with-placement-scenario",
                    "scenario": "adversarial",
                    "input": {
                        "diplomaGrade": 80,
                        "mode": "obp",
                        "placementStatus": "standard",
                        "vocationalExtra": true
                    },
                    "expected": {
                        "ok": false,
                        "field": "placementStatus",
                        "errorCode": "UNSUPPORTED_VALUE"
                    },
                    "tolerance": 1e-10
                }
            ]
        },
        "lastReviewed": "2026-08-25"
    },
    {
        "toolId": "honor-certificate",
        "slug": "takdir-tesekkur-belgesi",
        "path": "/egitim-sinav/takdir-tesekkur-belgesi-hesaplama",
        "category": {
            "id": "egitim-sinav",
            "label": "Eğitim ve Sınav"
        },
        "classification": "remote-config",
        "engineId": "honor-certificate-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-08-25",
        "effectiveTo": null,
        "minAppVersion": "0.4.0",
        "updateCadence": {
            "mode": "scheduled-source-review",
            "days": 90,
            "reviewAfter": "2027-01-01",
            "specialMonths": [
                1,
                6
            ],
            "note": "Belirlenen aralıkta resmî kaynak incelenir; değişiklik varsa config, yoksa doğrulanmış inceleme tarihi yenilenir."
        },
        "sources": [
            {
                "sourceId": "tr-middle-school-certificate-regulation",
                "sourceGroupId": "tr_school_certificate_rules",
                "title": "Okul Öncesi Eğitim ve İlköğretim Kurumları Yönetmeliği",
                "publisher": "Millî Eğitim Bakanlığı",
                "url": "https://tut.meb.gov.tr/meb_iys_dosyalar/2025_03/06154745_millegitimbakanligiokuloncesiegitimveilkogretimkurumlariyonetmeligi.pdf",
                "kind": "official-government",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "Ortaokul ders başarı ön koşulu",
                    "Teşekkür ve takdir eşikleri"
                ]
            },
            {
                "sourceId": "tr-high-school-certificate-regulation",
                "sourceGroupId": "tr_school_certificate_rules",
                "title": "Ortaöğretim Kurumları Yönetmeliği",
                "publisher": "Millî Eğitim Bakanlığı",
                "url": "https://tut.meb.gov.tr/meb_iys_dosyalar/2025_03/07084037_mebortaogretimkurumlariyonetmeligi.pdf",
                "kind": "official-government",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "Lise ders başarı ön koşulu",
                    "Özürsüz devamsızlık sınırı",
                    "Teşekkür ve takdir eşikleri"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "middle-school",
                            "label": "Ortaokul"
                        },
                        {
                            "value": "high-school",
                            "label": "Lise / Ortaöğretim"
                        }
                    ],
                    "visibleWhen": null,
                    "id": "stage",
                    "type": "select",
                    "label": "Okul kademesi",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "eligible",
                            "label": "Belge koşuluna uygun"
                        },
                        {
                            "value": "ineligible",
                            "label": "Belge koşuluna uygun değil"
                        }
                    ],
                    "visibleWhen": null,
                    "id": "behaviorStatus",
                    "type": "select",
                    "label": "Davranış ve disiplin koşulu",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "within-limit",
                            "label": "Özürsüz devamsızlık 5 günü geçmedi"
                        },
                        {
                            "value": "exceeded",
                            "label": "Özürsüz devamsızlık 5 günü geçti"
                        }
                    ],
                    "visibleWhen": {
                        "fieldId": "stage",
                        "equals": "high-school"
                    },
                    "id": "highSchoolAbsenceStatus",
                    "type": "select",
                    "label": "Lise özürsüz devamsızlık durumu",
                    "required": false
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [],
                    "visibleWhen": null,
                    "id": "courses",
                    "type": "object-list",
                    "label": "Dersler",
                    "required": true,
                    "itemFields": [
                        {
                            "id": "name",
                            "type": "text",
                            "label": "Ders adı",
                            "required": true,
                            "minimum": null,
                            "maximum": null,
                            "maxDecimalPlaces": null,
                            "options": []
                        },
                        {
                            "id": "weeklyHours",
                            "type": "number",
                            "label": "Haftalık ders saati",
                            "required": true,
                            "minimum": 1,
                            "maximum": 40,
                            "maxDecimalPlaces": 4,
                            "options": []
                        },
                        {
                            "id": "grade",
                            "type": "number",
                            "label": "Dönem puanı",
                            "required": true,
                            "minimum": 0,
                            "maximum": 100,
                            "maxDecimalPlaces": 4,
                            "options": []
                        }
                    ]
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "stage",
                    "type": "text",
                    "label": "Okul kademesi",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "weightedAverage",
                    "type": "number",
                    "label": "Ağırlıklı dönem ortalaması",
                    "unit": null,
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "courseCount",
                    "type": "number",
                    "label": "Ders sayısı",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "minimumGrade",
                    "type": "number",
                    "label": "En düşük ders puanı",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "maximumGrade",
                    "type": "number",
                    "label": "En yüksek ders puanı",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "behaviorEligible",
                    "type": "boolean",
                    "label": "Davranış koşulu uygun",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "absenceEligible",
                    "type": "boolean",
                    "label": "Devamsızlık koşulu uygun",
                    "unit": null,
                    "nullable": true,
                    "prominent": false
                },
                {
                    "id": "turkishCourseChecked",
                    "type": "boolean",
                    "label": "Türkçe dersi denetlendi",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "certificateStatus",
                    "type": "text",
                    "label": "Belge durumu",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "blockerCodes",
                    "type": "string-list",
                    "label": "Belgeyi engelleyen koşullar",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                }
            ]
        },
        "content": {
            "title": "Takdir ve Teşekkür Belgesi Hesaplama",
            "shortTitle": "Takdir / Teşekkür",
            "summary": "Dersleri haftalık saatle ağırlıklandırır; kademeye özgü ders, davranış ve belge eşiklerini denetler.",
            "method": "Ortalama 70-84,99 ise teşekkür, 85 ve üzeriyse takdir adayıdır; ayrıca ders geçme ve davranış koşulları uygulanır.",
            "limitations": [
                "Lise için özürsüz devamsızlığın 5 günü aşması belgeyi engeller; ortaokul hesabında bu alan kullanılmaz.",
                "Araç okul yönetiminin resmî belge kararının yerine geçmez."
            ],
            "faqs": [
                {
                    "question": "Takdir / Teşekkür sonucu resmî sınav veya yerleştirme sonucu mudur?",
                    "answer": "Hayır. Araç yalnız gösterilen aritmetiği ve kaynak incelemesinden geçmiş dönemsel configi uygular. Standart puan, aday kitlesi, kurum kararı ve tercih uygunluğu ayrıca resmî kaynaktan doğrulanmalıdır."
                }
            ],
            "sourcePanel": {
                "title": "Resmî kaynak ve inceleme kapsamı",
                "summary": "Formül ve dönemsel sınav verileri kaynak kimlikli configte tutulur. İnceleme tarihi geldiğinde, kaynak değişmemiş olsa bile insan kontrolü yapılmadan kayıt yenilenmez.",
                "sourceIds": [
                    "tr-middle-school-certificate-regulation",
                    "tr-high-school-certificate-regulation"
                ],
                "notes": [
                    "Kaynakta değişiklik varsa config güncellenir; değişiklik yoksa yalnız doğrulanmış inceleme tarihi yenilenir."
                ],
                "nextReviewOn": "2027-01-01"
            }
        },
        "config": {
            "kind": "honor-certificate-v1",
            "calculatorId": "egitim-sinav.takdir-tesekkur-belgesi-hesaplama",
            "policyLifecycle": {
                "configVersion": "1.0.0",
                "sourceIds": [
                    "tr-middle-school-certificate-regulation",
                    "tr-high-school-certificate-regulation"
                ],
                "reviewedOn": "2026-08-25",
                "effectiveFrom": "2026-08-25",
                "effectiveTo": null,
                "reviewAfter": "2027-01-01"
            },
            "gradeRange": [
                0,
                100
            ],
            "weeklyHoursRange": [
                1,
                40
            ],
            "minimumCourses": 3,
            "maximumCourses": 100,
            "gratitudeThreshold": 70,
            "commendationThreshold": 85,
            "middleSchoolDefaultPassGrade": 50,
            "middleSchoolTurkishPassGrade": 70,
            "highSchoolPassGrade": 50,
            "maximumUnexcusedAbsenceDays": 5
        },
        "testVectors": {
            "kind": "source-education-oracle-v1",
            "cases": [
                {
                    "caseId": "high-school-commendation",
                    "scenario": "positive",
                    "input": {
                        "stage": "high-school",
                        "behaviorStatus": "eligible",
                        "highSchoolAbsenceStatus": "within-limit",
                        "courses": [
                            {
                                "name": "Türkçe",
                                "weeklyHours": 5,
                                "grade": 85
                            },
                            {
                                "name": "Matematik",
                                "weeklyHours": 5,
                                "grade": 90
                            },
                            {
                                "name": "Fen",
                                "weeklyHours": 4,
                                "grade": 80
                            }
                        ]
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "weightedAverage": 85.35714285714286,
                            "certificateStatus": "takdir",
                            "absenceEligible": true
                        }
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "middle-school-rejects-high-school-absence",
                    "scenario": "adversarial",
                    "input": {
                        "stage": "middle-school",
                        "behaviorStatus": "eligible",
                        "highSchoolAbsenceStatus": "within-limit",
                        "courses": [
                            {
                                "name": "Türkçe",
                                "weeklyHours": 5,
                                "grade": 85
                            },
                            {
                                "name": "Matematik",
                                "weeklyHours": 5,
                                "grade": 90
                            },
                            {
                                "name": "Fen",
                                "weeklyHours": 4,
                                "grade": 80
                            }
                        ]
                    },
                    "expected": {
                        "ok": false,
                        "field": "highSchoolAbsenceStatus",
                        "errorCode": "UNSUPPORTED_VALUE"
                    },
                    "tolerance": 1e-10
                }
            ]
        },
        "lastReviewed": "2026-08-25"
    },
    {
        "toolId": "university-preference-ranking",
        "slug": "universite-tercih-siralama",
        "path": "/egitim-sinav/universite-tercih-siralama-hesaplama",
        "category": {
            "id": "egitim-sinav",
            "label": "Eğitim ve Sınav"
        },
        "classification": "remote-config",
        "engineId": "university-ranking-difference-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-08-25",
        "effectiveTo": null,
        "minAppVersion": "0.4.0",
        "updateCadence": {
            "mode": "scheduled-source-review",
            "days": 14,
            "reviewAfter": "2027-01-01",
            "specialMonths": [
                7,
                8
            ],
            "note": "Belirlenen aralıkta resmî kaynak incelenir; değişiklik varsa config, yoksa doğrulanmış inceleme tarihi yenilenir."
        },
        "sources": [
            {
                "sourceId": "tr-yks-2026-preference-announcement",
                "sourceGroupId": "tr_yks_preferences_and_quotas",
                "title": "2026-YKS Tercihlerinin Alınması",
                "publisher": "Ölçme, Seçme ve Yerleştirme Merkezi",
                "url": "https://www.osym.gov.tr/2026-yks-tercihlerin-alinmasi",
                "kind": "official-government",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "Tercih tarihleri",
                    "Güncel kılavuzun yayımlanma durumu"
                ]
            },
            {
                "sourceId": "tr-yks-2026-program-guide",
                "sourceGroupId": "tr_yks_preferences_and_quotas",
                "title": "2026-YKS Yükseköğretim Programları ve Kontenjanları Kılavuzu",
                "publisher": "Ölçme, Seçme ve Yerleştirme Merkezi",
                "url": "https://www.osym.gov.tr/2026-yuksekogretim-kurumlari-sinavi-yks-yuksekogretim-programlari-ve-kontenjanlari-kilavuzu",
                "kind": "official-government",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "Program kodu",
                    "Puan türü",
                    "Kontenjan",
                    "Başarı sırası ve özel koşullar"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "say",
                            "label": "Sayısal (SAY)"
                        },
                        {
                            "value": "ea",
                            "label": "Eşit Ağırlık (EA)"
                        },
                        {
                            "value": "soz",
                            "label": "Sözel (SÖZ)"
                        },
                        {
                            "value": "dil",
                            "label": "Yabancı Dil (DİL)"
                        },
                        {
                            "value": "tyt",
                            "label": "TYT (Ön lisans)"
                        }
                    ],
                    "visibleWhen": null,
                    "id": "scoreType",
                    "type": "select",
                    "label": "YKS puan türü",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 1,
                    "maximum": 999999999,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "candidateRank",
                    "type": "number",
                    "label": "Aday başarı sırası",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 1,
                    "maximum": 999999999,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "targetRank",
                    "type": "number",
                    "label": "Hedef programın önceki başarı sırası",
                    "required": true
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "scoreType",
                    "type": "text",
                    "label": "Puan türü",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "candidateRank",
                    "type": "number",
                    "label": "Aday başarı sırası",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "targetRank",
                    "type": "number",
                    "label": "Hedef program başarı sırası",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "difference",
                    "type": "number",
                    "label": "Sıralama farkı",
                    "unit": null,
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "position",
                    "type": "text",
                    "label": "Aritmetik konum",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "claimScope",
                    "type": "text",
                    "label": "Sonuç kapsamı",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "currentProgramGuideAvailable",
                    "type": "boolean",
                    "label": "Güncel program kılavuzu mevcut mu?",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                }
            ]
        },
        "content": {
            "title": "Üniversite Tercih Sıralama Farkı Hesaplama",
            "shortTitle": "Üniversite Tercih Sıralaması",
            "summary": "Aday sırası ile hedef programın geçmiş başarı sırası arasındaki salt aritmetik farkı ve konumu gösterir.",
            "method": "İki başarı sırasının mutlak farkı alınır; aday sırası küçükse hedefin önünde, büyükse gerisinde gösterilir.",
            "limitations": [
                "Geçmiş sıralama gelecekte yerleşme garantisi veya olasılığı değildir.",
                "Kontenjan, özel koşullar, başarı sırası barajı ve program kodu güncel kılavuzdan ayrıca doğrulanmalıdır."
            ],
            "faqs": [
                {
                    "question": "Üniversite Tercih Sıralaması sonucu resmî sınav veya yerleştirme sonucu mudur?",
                    "answer": "Hayır. Araç yalnız gösterilen aritmetiği ve kaynak incelemesinden geçmiş dönemsel configi uygular. Standart puan, aday kitlesi, kurum kararı ve tercih uygunluğu ayrıca resmî kaynaktan doğrulanmalıdır."
                }
            ],
            "sourcePanel": {
                "title": "Resmî kaynak ve inceleme kapsamı",
                "summary": "Formül ve dönemsel sınav verileri kaynak kimlikli configte tutulur. İnceleme tarihi geldiğinde, kaynak değişmemiş olsa bile insan kontrolü yapılmadan kayıt yenilenmez.",
                "sourceIds": [
                    "tr-yks-2026-preference-announcement",
                    "tr-yks-2026-program-guide"
                ],
                "notes": [
                    "Kaynakta değişiklik varsa config güncellenir; değişiklik yoksa yalnız doğrulanmış inceleme tarihi yenilenir."
                ],
                "nextReviewOn": "2027-01-01"
            }
        },
        "config": {
            "kind": "university-ranking-difference-v1",
            "calculatorId": "egitim-sinav.universite-tercih-siralama-hesaplama",
            "policyLifecycle": {
                "configVersion": "1.0.0",
                "sourceIds": [
                    "tr-yks-2026-preference-announcement",
                    "tr-yks-2026-program-guide"
                ],
                "reviewedOn": "2026-08-25",
                "effectiveFrom": "2026-08-25",
                "effectiveTo": null,
                "reviewAfter": "2027-01-01"
            },
            "scoreTypes": [
                "say",
                "ea",
                "soz",
                "dil",
                "tyt"
            ],
            "rankRange": [
                1,
                999999999
            ],
            "programGuidePublished": true
        },
        "testVectors": {
            "kind": "source-education-oracle-v1",
            "cases": [
                {
                    "caseId": "candidate-ahead",
                    "scenario": "positive",
                    "input": {
                        "scoreType": "ea",
                        "candidateRank": 10000,
                        "targetRank": 12000
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "difference": 2000,
                            "position": "ahead",
                            "claimScope": "arithmetic-only",
                            "currentProgramGuideAvailable": true
                        }
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "zero-rank",
                    "scenario": "adversarial",
                    "input": {
                        "scoreType": "ea",
                        "candidateRank": 0,
                        "targetRank": 12000
                    },
                    "expected": {
                        "ok": false,
                        "field": "candidateRank",
                        "errorCode": "OUT_OF_RANGE"
                    },
                    "tolerance": 1e-10
                }
            ]
        },
        "lastReviewed": "2026-08-25"
    },
    {
        "toolId": "yds-yokdil-score",
        "slug": "yds-yokdil-puan",
        "path": "/egitim-sinav/yds-yokdil-puan-hesaplama",
        "category": {
            "id": "egitim-sinav",
            "label": "Eğitim ve Sınav"
        },
        "classification": "remote-config",
        "engineId": "yds-yokdil-score-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-08-25",
        "effectiveTo": null,
        "minAppVersion": "0.4.0",
        "updateCadence": {
            "mode": "scheduled-source-review",
            "days": 30,
            "reviewAfter": "2027-01-01",
            "specialMonths": [],
            "note": "Belirlenen aralıkta resmî kaynak incelenir; değişiklik varsa config, yoksa doğrulanmış inceleme tarihi yenilenir."
        },
        "sources": [
            {
                "sourceId": "tr-yds-2026-1-guide",
                "sourceGroupId": "tr_language_exam_rules",
                "title": "2026-YDS/1 Kılavuz ve Başvuru Bilgileri",
                "publisher": "Ölçme, Seçme ve Yerleştirme Merkezi",
                "url": "https://www.osym.gov.tr/2026yds1-kilavuz-ve-basvuru-bilgileri",
                "kind": "official-government",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "Soru sayısı",
                    "Yanlışların puana etkisi",
                    "Soru başına puan"
                ]
            },
            {
                "sourceId": "tr-yokdil-2026-2-guide",
                "sourceGroupId": "tr_language_exam_rules",
                "title": "2026-YÖKDİL/2 Kılavuz ve Başvuru Bilgileri",
                "publisher": "Ölçme, Seçme ve Yerleştirme Merkezi",
                "url": "https://www.osym.gov.tr/yokdil2-kilavuz-ve-basvuru-bilgileri-20260624105822135",
                "kind": "official-government",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "Bilim alanları",
                    "Soru sayısı",
                    "Yanlışların puana etkisi",
                    "Soru başına puan"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "yds",
                            "label": "YDS"
                        },
                        {
                            "value": "yokdil",
                            "label": "YÖKDİL"
                        }
                    ],
                    "visibleWhen": null,
                    "id": "examType",
                    "type": "select",
                    "label": "Sınav",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "genel",
                            "label": "Genel / YDS"
                        },
                        {
                            "value": "fen",
                            "label": "YÖKDİL Fen Bilimleri"
                        },
                        {
                            "value": "sosyal",
                            "label": "YÖKDİL Sosyal Bilimler"
                        },
                        {
                            "value": "saglik",
                            "label": "YÖKDİL Sağlık Bilimleri"
                        }
                    ],
                    "visibleWhen": null,
                    "id": "field",
                    "type": "select",
                    "label": "Sınav alanı",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 0,
                    "maximum": 80,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "correctAnswers",
                    "type": "number",
                    "label": "Doğru cevap",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 0,
                    "maximum": 80,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "wrongAnswers",
                    "type": "number",
                    "label": "Yanlış cevap",
                    "required": true
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "examType",
                    "type": "text",
                    "label": "Sınav",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "field",
                    "type": "text",
                    "label": "Sınav alanı",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "correctAnswers",
                    "type": "number",
                    "label": "Doğru cevap",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "wrongAnswers",
                    "type": "number",
                    "label": "Yanlış cevap",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "blankAnswers",
                    "type": "number",
                    "label": "Boş cevap",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "score",
                    "type": "number",
                    "label": "Sınav puanı",
                    "unit": null,
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "cefrMappingAvailable",
                    "type": "boolean",
                    "label": "CEFR eşlemesi mevcut mu?",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                }
            ]
        },
        "content": {
            "title": "YDS ve YÖKDİL Puan Hesaplama",
            "shortTitle": "YDS / YÖKDİL Puanı",
            "summary": "Doğru cevap sayısını soru başına 1,25 puanla çarpar; yanlışlar puanı düşürmez.",
            "method": "80 soruluk sınavda puan=doğru×1,25; boş sayısı toplamdan doğru ve yanlış çıkarılarak bulunur.",
            "limitations": [
                "YDS için Genel/YDS, YÖKDİL için ilgili bilim alanı seçilmelidir.",
                "Araç dil eşdeğerliği veya CEFR seviyesi üretmez."
            ],
            "faqs": [
                {
                    "question": "YDS / YÖKDİL Puanı sonucu resmî sınav veya yerleştirme sonucu mudur?",
                    "answer": "Hayır. Araç yalnız gösterilen aritmetiği ve kaynak incelemesinden geçmiş dönemsel configi uygular. Standart puan, aday kitlesi, kurum kararı ve tercih uygunluğu ayrıca resmî kaynaktan doğrulanmalıdır."
                }
            ],
            "sourcePanel": {
                "title": "Resmî kaynak ve inceleme kapsamı",
                "summary": "Formül ve dönemsel sınav verileri kaynak kimlikli configte tutulur. İnceleme tarihi geldiğinde, kaynak değişmemiş olsa bile insan kontrolü yapılmadan kayıt yenilenmez.",
                "sourceIds": [
                    "tr-yds-2026-1-guide",
                    "tr-yokdil-2026-2-guide"
                ],
                "notes": [
                    "Kaynakta değişiklik varsa config güncellenir; değişiklik yoksa yalnız doğrulanmış inceleme tarihi yenilenir."
                ],
                "nextReviewOn": "2027-01-01"
            }
        },
        "config": {
            "kind": "yds-yokdil-score-v1",
            "calculatorId": "egitim-sinav.yds-yokdil-puan-hesaplama",
            "policyLifecycle": {
                "configVersion": "1.0.0",
                "sourceIds": [
                    "tr-yds-2026-1-guide",
                    "tr-yokdil-2026-2-guide"
                ],
                "reviewedOn": "2026-08-25",
                "effectiveFrom": "2026-08-25",
                "effectiveTo": null,
                "reviewAfter": "2027-01-01"
            },
            "examTypes": [
                "yds",
                "yokdil"
            ],
            "fields": [
                "genel",
                "fen",
                "sosyal",
                "saglik"
            ],
            "totalQuestions": 80,
            "pointsPerCorrect": 1.25
        },
        "testVectors": {
            "kind": "source-education-oracle-v1",
            "cases": [
                {
                    "caseId": "yds-score",
                    "scenario": "positive",
                    "input": {
                        "examType": "yds",
                        "field": "genel",
                        "correctAnswers": 60,
                        "wrongAnswers": 20
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "blankAnswers": 0,
                            "score": 75
                        }
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "yds-rejects-yokdil-field",
                    "scenario": "adversarial",
                    "input": {
                        "examType": "yds",
                        "field": "fen",
                        "correctAnswers": 60,
                        "wrongAnswers": 20
                    },
                    "expected": {
                        "ok": false,
                        "field": "field",
                        "errorCode": "UNSUPPORTED_VALUE"
                    },
                    "tolerance": 1e-10
                }
            ]
        },
        "lastReviewed": "2026-08-25"
    },
    {
        "toolId": "yks-score",
        "slug": "yks-puan",
        "path": "/egitim-sinav/yks-puan-hesaplama",
        "category": {
            "id": "egitim-sinav",
            "label": "Eğitim ve Sınav"
        },
        "classification": "remote-config",
        "engineId": "yks-net-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-08-25",
        "effectiveTo": null,
        "minAppVersion": "0.4.0",
        "updateCadence": {
            "mode": "scheduled-source-review",
            "days": 30,
            "reviewAfter": "2027-01-01",
            "specialMonths": [
                1,
                2,
                6,
                7
            ],
            "note": "Belirlenen aralıkta resmî kaynak incelenir; değişiklik varsa config, yoksa doğrulanmış inceleme tarihi yenilenir."
        },
        "sources": [
            {
                "sourceId": "tr-yks-2026-application-guide",
                "sourceGroupId": "tr_yks_rules_and_content",
                "title": "2026-YKS Başvuru Kılavuzu",
                "publisher": "Ölçme, Seçme ve Yerleştirme Merkezi",
                "url": "https://dokuman.osym.gov.tr/pdfdokuman/2026/YKS/basvuru_kilavuz06022026.pdf",
                "kind": "official-government",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "Test ve soru sayıları",
                    "OBP dönüşümü ve yerleştirme katkısı"
                ]
            },
            {
                "sourceId": "tr-yks-2026-evaluation",
                "sourceGroupId": "tr_yks_rules_and_content",
                "title": "2026-YKS Değerlendirme İşlemleri",
                "publisher": "Ölçme, Seçme ve Yerleştirme Merkezi",
                "url": "https://www.osym.gov.tr/2026-yuksekogretim-kurumlari-sinavi-2026yks-degerlendirme-islemleri",
                "kind": "official-government",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "Net kuralı",
                    "İptal edilen soruların değerlendirmeye etkisi"
                ]
            },
            {
                "sourceId": "tr-yks-2026-results",
                "sourceGroupId": "tr_yks_rules_and_content",
                "title": "2026-YKS Sonuç Duyurusu",
                "publisher": "Ölçme, Seçme ve Yerleştirme Merkezi",
                "url": "https://www.osym.gov.tr/2026-yks-sonuclari-aciklandi",
                "kind": "official-government",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "Nihai sonuç duyurusu",
                    "İptal edilen soru numaraları"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": "all",
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "all",
                            "label": "Tüm YKS"
                        },
                        {
                            "value": "tyt",
                            "label": "Yalnız TYT"
                        },
                        {
                            "value": "ayt",
                            "label": "Yalnız AYT"
                        },
                        {
                            "value": "ydt",
                            "label": "Yalnız YDT"
                        }
                    ],
                    "visibleWhen": null,
                    "id": "mode",
                    "type": "radio",
                    "label": "YKS oturumu",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 0,
                    "maximum": 40,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "tytTurkishCorrect",
                    "type": "number",
                    "label": "TYT Türkçe doğru",
                    "required": false
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 0,
                    "maximum": 40,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "tytTurkishWrong",
                    "type": "number",
                    "label": "TYT Türkçe yanlış",
                    "required": false
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 0,
                    "maximum": 20,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "tytSocialCorrect",
                    "type": "number",
                    "label": "TYT Sosyal Bilimler doğru",
                    "required": false
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 0,
                    "maximum": 20,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "tytSocialWrong",
                    "type": "number",
                    "label": "TYT Sosyal Bilimler yanlış",
                    "required": false
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 0,
                    "maximum": 40,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "tytMathCorrect",
                    "type": "number",
                    "label": "TYT Temel Matematik doğru",
                    "required": false
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 0,
                    "maximum": 40,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "tytMathWrong",
                    "type": "number",
                    "label": "TYT Temel Matematik yanlış",
                    "required": false
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 0,
                    "maximum": 20,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "tytScienceCorrect",
                    "type": "number",
                    "label": "TYT Fen Bilimleri doğru",
                    "required": false
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 0,
                    "maximum": 20,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "tytScienceWrong",
                    "type": "number",
                    "label": "TYT Fen Bilimleri yanlış",
                    "required": false
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 0,
                    "maximum": 39,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "aytMathCorrect",
                    "type": "number",
                    "label": "AYT Matematik doğru (39 geçerli soru)",
                    "required": false
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 0,
                    "maximum": 39,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "aytMathWrong",
                    "type": "number",
                    "label": "AYT Matematik yanlış (39 geçerli soru)",
                    "required": false
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 0,
                    "maximum": 40,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "aytScienceCorrect",
                    "type": "number",
                    "label": "AYT Fen Bilimleri doğru",
                    "required": false
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 0,
                    "maximum": 40,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "aytScienceWrong",
                    "type": "number",
                    "label": "AYT Fen Bilimleri yanlış",
                    "required": false
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 0,
                    "maximum": 39,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "aytLiteratureSocial1Correct",
                    "type": "number",
                    "label": "AYT Edebiyat-Sosyal-1 doğru (39 geçerli soru)",
                    "required": false
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 0,
                    "maximum": 39,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "aytLiteratureSocial1Wrong",
                    "type": "number",
                    "label": "AYT Edebiyat-Sosyal-1 yanlış (39 geçerli soru)",
                    "required": false
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 0,
                    "maximum": 40,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "aytSocial2Correct",
                    "type": "number",
                    "label": "AYT Sosyal Bilimler-2 doğru",
                    "required": false
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 0,
                    "maximum": 40,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "aytSocial2Wrong",
                    "type": "number",
                    "label": "AYT Sosyal Bilimler-2 yanlış",
                    "required": false
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 0,
                    "maximum": 80,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "ydtCorrect",
                    "type": "number",
                    "label": "YDT doğru",
                    "required": false
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 0,
                    "maximum": 80,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "ydtWrong",
                    "type": "number",
                    "label": "YDT yanlış",
                    "required": false
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 50,
                    "maximum": 500,
                    "maxDecimalPlaces": 4,
                    "options": [],
                    "visibleWhen": null,
                    "id": "obpOrDiploma",
                    "type": "number",
                    "label": "OBP veya diploma notu",
                    "required": false
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "mode",
                    "type": "text",
                    "label": "YKS oturumu",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "testNets",
                    "type": "object",
                    "label": "Test netleri",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "tytTotalNet",
                    "type": "number",
                    "label": "TYT toplam net",
                    "unit": null,
                    "nullable": true,
                    "prominent": false
                },
                {
                    "id": "aytTotalNet",
                    "type": "number",
                    "label": "AYT toplam net",
                    "unit": null,
                    "nullable": true,
                    "prominent": false
                },
                {
                    "id": "aytSayNet",
                    "type": "number",
                    "label": "AYT SAY ham net toplamı",
                    "unit": null,
                    "nullable": true,
                    "prominent": false
                },
                {
                    "id": "aytEaNet",
                    "type": "number",
                    "label": "AYT EA ham net toplamı",
                    "unit": null,
                    "nullable": true,
                    "prominent": false
                },
                {
                    "id": "aytSozNet",
                    "type": "number",
                    "label": "AYT SÖZ ham net toplamı",
                    "unit": null,
                    "nullable": true,
                    "prominent": false
                },
                {
                    "id": "ydtNet",
                    "type": "number",
                    "label": "YDT neti",
                    "unit": null,
                    "nullable": true,
                    "prominent": false
                },
                {
                    "id": "obp",
                    "type": "number",
                    "label": "OBP",
                    "unit": null,
                    "nullable": true,
                    "prominent": false
                },
                {
                    "id": "obpContribution",
                    "type": "number",
                    "label": "Standart OBP katkısı",
                    "unit": null,
                    "nullable": true,
                    "prominent": false
                },
                {
                    "id": "officialScoreAvailable",
                    "type": "boolean",
                    "label": "Resmî puan üretilebilir mi?",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "claimScope",
                    "type": "text",
                    "label": "Sonuç kapsamı",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                }
            ]
        },
        "content": {
            "title": "2026 YKS Net ve OBP Katkısı Hesaplama",
            "shortTitle": "YKS Net",
            "summary": "TYT, AYT ve YDT doğru-yanlışlarından ham netleri; girilirse OBP ve standart katkıyı ayrı gösterir.",
            "method": "Her testte net=doğru−yanlış/4 uygulanır. Diploma notu 5 ile OBP'ye, OBP 0,12 ile standart katkıya çevrilir.",
            "limitations": [
                "2026'da iptal edilen AYT Matematik 23 ve AYT Edebiyat-Sosyal-1 20 nedeniyle bu iki testte geçerli üst sınır 39'dur.",
                "Standartlaştırma ve puan katsayıları uygulanmadığı için resmî YKS puanı veya tercih önerisi üretilmez."
            ],
            "faqs": [
                {
                    "question": "YKS Net sonucu resmî sınav veya yerleştirme sonucu mudur?",
                    "answer": "Hayır. Araç yalnız gösterilen aritmetiği ve kaynak incelemesinden geçmiş dönemsel configi uygular. Standart puan, aday kitlesi, kurum kararı ve tercih uygunluğu ayrıca resmî kaynaktan doğrulanmalıdır."
                }
            ],
            "sourcePanel": {
                "title": "Resmî kaynak ve inceleme kapsamı",
                "summary": "Formül ve dönemsel sınav verileri kaynak kimlikli configte tutulur. İnceleme tarihi geldiğinde, kaynak değişmemiş olsa bile insan kontrolü yapılmadan kayıt yenilenmez.",
                "sourceIds": [
                    "tr-yks-2026-application-guide",
                    "tr-yks-2026-evaluation",
                    "tr-yks-2026-results"
                ],
                "notes": [
                    "Kaynakta değişiklik varsa config güncellenir; değişiklik yoksa yalnız doğrulanmış inceleme tarihi yenilenir."
                ],
                "nextReviewOn": "2027-01-01"
            }
        },
        "config": {
            "kind": "yks-net-v1",
            "calculatorId": "egitim-sinav.yks-puan-hesaplama",
            "policyLifecycle": {
                "configVersion": "1.0.0",
                "sourceIds": [
                    "tr-yks-2026-application-guide",
                    "tr-yks-2026-evaluation",
                    "tr-yks-2026-results"
                ],
                "reviewedOn": "2026-08-25",
                "effectiveFrom": "2026-08-25",
                "effectiveTo": null,
                "reviewAfter": "2027-01-01"
            },
            "tests": [
                {
                    "id": "tyt-tr",
                    "maximumQuestions": 40
                },
                {
                    "id": "tyt-social",
                    "maximumQuestions": 20
                },
                {
                    "id": "tyt-math",
                    "maximumQuestions": 40
                },
                {
                    "id": "tyt-science",
                    "maximumQuestions": 20
                },
                {
                    "id": "ayt-math",
                    "maximumQuestions": 39
                },
                {
                    "id": "ayt-science",
                    "maximumQuestions": 40
                },
                {
                    "id": "ayt-literature-social1",
                    "maximumQuestions": 39
                },
                {
                    "id": "ayt-social2",
                    "maximumQuestions": 40
                },
                {
                    "id": "ydt",
                    "maximumQuestions": 80
                }
            ],
            "wrongPenaltyDivisor": 4,
            "diplomaGradeRange": [
                50,
                100
            ],
            "obpRange": [
                250,
                500
            ],
            "obpMultiplier": 5,
            "placementContributionCoefficient": 0.12,
            "cancelledQuestionIds": [
                "ayt-math-23",
                "ayt-literature-social1-20"
            ]
        },
        "testVectors": {
            "kind": "source-education-oracle-v1",
            "cases": [
                {
                    "caseId": "all-session-net-and-obp",
                    "scenario": "positive",
                    "input": {
                        "mode": "all",
                        "tytTurkishCorrect": 30,
                        "tytTurkishWrong": 4,
                        "tytSocialCorrect": 15,
                        "tytSocialWrong": 4,
                        "tytMathCorrect": 25,
                        "tytMathWrong": 4,
                        "tytScienceCorrect": 10,
                        "tytScienceWrong": 4,
                        "aytMathCorrect": 39,
                        "aytMathWrong": 0,
                        "aytScienceCorrect": 30,
                        "aytScienceWrong": 4,
                        "aytLiteratureSocial1Correct": 39,
                        "aytLiteratureSocial1Wrong": 0,
                        "aytSocial2Correct": 20,
                        "aytSocial2Wrong": 4,
                        "ydtCorrect": 60,
                        "ydtWrong": 4,
                        "obpOrDiploma": 80
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "tytTotalNet": 76,
                            "aytTotalNet": 126,
                            "aytSayNet": 68,
                            "aytEaNet": 78,
                            "aytSozNet": 58,
                            "ydtNet": 59,
                            "obp": 400,
                            "obpContribution": 48
                        }
                    },
                    "tolerance": 1e-10
                },
                {
                    "caseId": "cancelled-question-boundary",
                    "scenario": "adversarial",
                    "input": {
                        "mode": "ayt",
                        "aytMathCorrect": 40,
                        "aytMathWrong": 0
                    },
                    "expected": {
                        "ok": false,
                        "field": "aytMathCorrect",
                        "errorCode": "OUT_OF_RANGE"
                    },
                    "tolerance": 1e-10
                }
            ]
        },
        "lastReviewed": "2026-08-25"
    },
    {
        "toolId": "military-service-countdown",
        "slug": "askerlik-safak-hesaplama",
        "path": "/tarih-zaman/askerlik-safak-hesaplama",
        "category": {
            "id": "tarih-zaman",
            "label": "Tarih ve Zaman"
        },
        "classification": "remote-config",
        "engineId": "military-service-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-08-25",
        "effectiveTo": null,
        "minAppVersion": "0.4.0",
        "updateCadence": {
            "mode": "scheduled-source-review",
            "days": 180,
            "reviewAfter": "2026-10-01",
            "specialMonths": [],
            "note": "Belirlenen tarihte kaynak incelenir; değişiklik varsa config, yoksa inceleme tarihi yenilenir."
        },
        "sources": [
            {
                "sourceId": "tr_military_service_law",
                "sourceGroupId": null,
                "title": "7179 sayılı Askeralma Kanunu hizmet süreleri",
                "publisher": "T.C. Resmî Gazete",
                "url": "https://www.resmigazete.gov.tr/eskiler/2019/06/20190626-1.htm",
                "kind": "official-government",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "er hizmet süresi",
                    "yedek subay/astsubay",
                    "bedelli",
                    "izin ve yol"
                ]
            },
            {
                "sourceId": "tr_military_service_msb_manual",
                "sourceGroupId": null,
                "title": "MSB muvazzaflık ve terhis uygulaması",
                "publisher": "Millî Savunma Bakanlığı",
                "url": "https://www.msb.gov.tr/Askeralma/icerik/muvazzaflik-ve-terhis-islemleri",
                "kind": "official-government",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "terhis",
                    "birlikten ayrılış",
                    "izin",
                    "sevk ve celp"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [],
                    "visibleWhen": null,
                    "id": "serviceStartDate",
                    "type": "date",
                    "label": "Hizmete başlangıç tarihi",
                    "required": true
                },
                {
                    "defaultValue": "er",
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "er",
                            "label": "Er/erbaş (6 ay)"
                        },
                        {
                            "value": "astay",
                            "label": "Yedek subay/astsubay (12 ay)"
                        },
                        {
                            "value": "bedelli",
                            "label": "Bedelli (1 ay)"
                        }
                    ],
                    "visibleWhen": null,
                    "id": "serviceType",
                    "type": "select",
                    "label": "Hizmet türü",
                    "required": true
                },
                {
                    "defaultValue": 0,
                    "placeholder": null,
                    "unit": "gün",
                    "minimum": 0,
                    "maximum": 30,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "usedLeaveDays",
                    "type": "number",
                    "label": "Kullanılmış izin",
                    "required": false
                },
                {
                    "defaultValue": 0,
                    "placeholder": null,
                    "unit": "gün",
                    "minimum": 0,
                    "maximum": 10,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "travelLeaveDays",
                    "type": "number",
                    "label": "Yol izni",
                    "required": false
                },
                {
                    "defaultValue": 0,
                    "placeholder": null,
                    "unit": "gün",
                    "minimum": 0,
                    "maximum": 90,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "nonServiceDays",
                    "type": "number",
                    "label": "Hizmetten sayılmayan gün",
                    "required": false
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "serviceStartDate",
                    "type": "text",
                    "label": "Hizmete başlangıç",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "serviceType",
                    "type": "text",
                    "label": "Hizmet türü",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "referenceDate",
                    "type": "text",
                    "label": "Hesap tarihi",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "appliedUsedLeaveDays",
                    "type": "number",
                    "label": "Uygulanan izin",
                    "unit": "gün",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "travelLeaveDays",
                    "type": "number",
                    "label": "Yol izni",
                    "unit": "gün",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "nonServiceDays",
                    "type": "number",
                    "label": "Hizmetten sayılmayan",
                    "unit": "gün",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "remainingLeaveDays",
                    "type": "number",
                    "label": "Kalan izin",
                    "unit": "gün",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "officialServiceEndDate",
                    "type": "text",
                    "label": "Resmî hizmet sonu",
                    "unit": null,
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "estimatedUnitDepartureDate",
                    "type": "text",
                    "label": "Tahminî birlikten ayrılış",
                    "unit": null,
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "daysRemaining",
                    "type": "number",
                    "label": "Kalan süre",
                    "unit": "gün",
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "estimatedDeparturePassed",
                    "type": "boolean",
                    "label": "Tahminî ayrılış geçti mi?",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "elapsedServiceDays",
                    "type": "number",
                    "label": "Geçen hizmet",
                    "unit": "gün",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "plateCity",
                    "type": "text",
                    "label": "Plaka karşılığı",
                    "unit": null,
                    "nullable": true,
                    "prominent": false
                },
                {
                    "id": "plateStatus",
                    "type": "text",
                    "label": "Plaka durumu",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                }
            ]
        },
        "content": {
            "title": "Askerlik Hizmet Sonu ve Tahminî Birlikten Ayrılış",
            "shortTitle": "Askerlik Hizmet Sonu ve Tahminî Birlikten Ayrılış",
            "summary": "Hizmet türüne göre ay ekler; izin, yol ve hizmetten sayılmayan günleri ayrı göstererek resmî bitiş ile tahminî ayrılışı ayırır.",
            "method": "Hizmet türüne göre ay ekler; izin, yol ve hizmetten sayılmayan günleri ayrı göstererek resmî bitiş ile tahminî ayrılışı ayırır.",
            "limitations": [
                "Sevk, disiplin, izin ve kurum kaydı sonucu değiştirebilir."
            ],
            "faqs": [
                {
                    "question": "Sonuç kesin veya resmî bir karar mıdır?",
                    "answer": "Hayır. Sonuç yalnız gösterilen yöntem, kullanıcı girdileri ve tarihli kaynak kaydıyla üretilen yardımcı bir hesaplamadır."
                }
            ],
            "sourcePanel": {
                "title": "Kaynak ve inceleme durumu",
                "summary": "Formül ve eşikler belirlenen aralıkta resmî/otoritatif kaynaktan yeniden incelenir. Değişiklik yoksa inceleme tarihi, değişiklik varsa config sürümü yenilenir.",
                "sourceIds": [
                    "tr_military_service_law",
                    "tr_military_service_msb_manual"
                ],
                "notes": [
                    "Sevk, disiplin, izin ve kurum kaydı sonucu değiştirebilir."
                ]
            }
        },
        "config": {
            "kind": "military-service-v1",
            "calculatorId": "tarih-zaman.askerlik-safak-hesaplama",
            "startYear": [
                1900,
                2100
            ],
            "usedLeaveDays": [
                0,
                30
            ],
            "travelLeaveDays": [
                0,
                10
            ],
            "nonServiceDays": [
                0,
                90
            ],
            "rules": {
                "er": {
                    "serviceMonths": 6,
                    "leaveEntitlementDays": 6
                },
                "astay": {
                    "serviceMonths": 12,
                    "leaveEntitlementDays": 0
                },
                "bedelli": {
                    "serviceMonths": 1,
                    "leaveEntitlementDays": 0
                }
            },
            "policyLifecycle": {
                "configVersion": "1.0.0",
                "sourceIds": [
                    "tr_military_service_law",
                    "tr_military_service_msb_manual"
                ],
                "reviewedOn": "2026-08-25",
                "effectiveFrom": "2026-08-25",
                "effectiveTo": null,
                "reviewAfter": "2026-10-01"
            }
        },
        "testVectors": {
            "kind": "date-health-oracle-v1",
            "cases": [
                {
                    "caseId": "er-six-months",
                    "scenario": "month-end",
                    "input": {
                        "serviceStartDate": "2026-05-31",
                        "serviceType": "er",
                        "usedLeaveDays": 0,
                        "travelLeaveDays": 1,
                        "nonServiceDays": 0
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "officialServiceEndDate": "2026-11-30",
                            "estimatedUnitDepartureDate": "2026-11-23",
                            "daysRemaining": 90
                        }
                    },
                    "tolerance": 1e-9
                },
                {
                    "caseId": "missing-start",
                    "scenario": "adversarial",
                    "input": {
                        "serviceType": "er"
                    },
                    "expected": {
                        "ok": false,
                        "field": "serviceStartDate",
                        "errorCode": "REQUIRED"
                    },
                    "tolerance": 1e-9
                }
            ]
        },
        "lastReviewed": "2026-08-25"
    },
    {
        "toolId": "countdown",
        "slug": "geri-sayim-hesaplama",
        "path": "/tarih-zaman/geri-sayim-hesaplama",
        "category": {
            "id": "tarih-zaman",
            "label": "Tarih ve Zaman"
        },
        "classification": "timeless",
        "engineId": "countdown-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-08-25",
        "effectiveTo": null,
        "minAppVersion": "0.4.0",
        "updateCadence": {
            "mode": "method-change-only",
            "days": null,
            "reviewAfter": null,
            "specialMonths": [],
            "note": "Dönemsel veri içermez."
        },
        "sources": [],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [],
                    "visibleWhen": null,
                    "id": "targetDate",
                    "type": "date",
                    "label": "Hedef tarih",
                    "required": true
                },
                {
                    "defaultValue": "00:00",
                    "placeholder": "SS:DD",
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [],
                    "visibleWhen": null,
                    "id": "targetTime",
                    "type": "text",
                    "label": "Hedef saat",
                    "required": false
                },
                {
                    "defaultValue": null,
                    "placeholder": "Örn. Sınav günü",
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [],
                    "visibleWhen": null,
                    "id": "description",
                    "type": "text",
                    "label": "Açıklama",
                    "required": false
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "targetEpochMilliseconds",
                    "type": "number",
                    "label": "Hedef zaman damgası",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "referenceEpochMilliseconds",
                    "type": "number",
                    "label": "Referans zaman damgası",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "description",
                    "type": "text",
                    "label": "Açıklama",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "isExpired",
                    "type": "boolean",
                    "label": "Süre doldu mu?",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "totalSeconds",
                    "type": "number",
                    "label": "Toplam saniye",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "days",
                    "type": "number",
                    "label": "Gün",
                    "unit": "gün",
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "remainingHours",
                    "type": "number",
                    "label": "Saat",
                    "unit": "saat",
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "remainingMinutes",
                    "type": "number",
                    "label": "Dakika",
                    "unit": "dakika",
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "remainingSeconds",
                    "type": "number",
                    "label": "Saniye",
                    "unit": "saniye",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "totalHours",
                    "type": "number",
                    "label": "Toplam saat",
                    "unit": "saat",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "totalMinutes",
                    "type": "number",
                    "label": "Toplam dakika",
                    "unit": "dakika",
                    "nullable": false,
                    "prominent": false
                }
            ]
        },
        "content": {
            "title": "Hedef Tarihe Kaç Gün Kaldı? Geri Sayım Sayacı",
            "shortTitle": "Hedef Tarihe Kaç Gün Kaldı? Geri Sayım Sayacı",
            "summary": "Hedef yerel anı ile cihazdan doğrulanan referans anı arasındaki farkı gün, saat, dakika ve saniyeye böler.",
            "method": "Hedef yerel anı ile cihazdan doğrulanan referans anı arasındaki farkı gün, saat, dakika ve saniyeye böler.",
            "limitations": [
                "Saat dilimi cihazın verdiği ofsetle hesaplanır; uygulama sistem saatini gizlice çekirdeğe taşımaz."
            ],
            "faqs": [
                {
                    "question": "Sonuç kesin veya resmî bir karar mıdır?",
                    "answer": "Hayır. Sonuç yalnız gösterilen yöntem, kullanıcı girdileri ve tarihli kaynak kaydıyla üretilen yardımcı bir hesaplamadır."
                }
            ],
            "sourcePanel": {
                "title": "Yöntem kapsamı",
                "summary": "Bu araç yalnız kullanıcı tarihleriyle çalışan zamansız matematik kullanır; dönemsel oran veya resmî tarife içermez.",
                "sourceIds": [],
                "notes": [
                    "Saat dilimi cihazın verdiği ofsetle hesaplanır; uygulama sistem saatini gizlice çekirdeğe taşımaz."
                ]
            }
        },
        "config": {
            "kind": "countdown-v1",
            "calculatorId": "tarih-zaman.geri-sayim-hesaplama",
            "maximumDurationSeconds": 6324480000,
            "requireFutureTarget": true,
            "policyLifecycle": {
                "configVersion": "1.0.0",
                "sourceIds": [],
                "reviewedOn": "2026-08-25",
                "effectiveFrom": "2026-08-25",
                "effectiveTo": null,
                "reviewAfter": null
            }
        },
        "testVectors": {
            "kind": "date-health-oracle-v1",
            "cases": [
                {
                    "caseId": "two-days",
                    "scenario": "positive",
                    "input": {
                        "targetDate": "2026-08-27",
                        "targetTime": "15:30",
                        "description": "Lansman"
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "days": 2,
                            "remainingHours": 3,
                            "remainingMinutes": 30,
                            "totalHours": 51,
                            "totalMinutes": 3090
                        }
                    },
                    "tolerance": 1e-9
                },
                {
                    "caseId": "missing-target",
                    "scenario": "adversarial",
                    "input": {},
                    "expected": {
                        "ok": false,
                        "field": "targetEpochMilliseconds",
                        "errorCode": "NOT_FINITE"
                    },
                    "tolerance": 1e-9
                }
            ]
        },
        "lastReviewed": "2026-08-25"
    },
    {
        "toolId": "hijri-gregorian",
        "slug": "hicri-miladi-tarih-cevirici-hesaplama",
        "path": "/tarih-zaman/hicri-miladi-tarih-cevirici-hesaplama",
        "category": {
            "id": "tarih-zaman",
            "label": "Tarih ve Zaman"
        },
        "classification": "remote-config",
        "engineId": "hijri-gregorian-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-08-25",
        "effectiveTo": null,
        "minAppVersion": "0.4.0",
        "updateCadence": {
            "mode": "scheduled-source-review",
            "days": 90,
            "reviewAfter": "2027-01-01",
            "specialMonths": [],
            "note": "Belirlenen tarihte kaynak incelenir; değişiklik varsa config, yoksa inceleme tarihi yenilenir."
        },
        "sources": [
            {
                "sourceId": "tr_religious_holidays_diyanet_manual",
                "sourceGroupId": null,
                "title": "Diyanet yıllık dinî günler ve takvim açıklaması",
                "publisher": "Diyanet İşleri Başkanlığı",
                "url": "https://vakithesaplama.diyanet.gov.tr/icerik.php?icerik=153",
                "kind": "official-government",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "dinî günler",
                    "Hicrî-Miladi gün hizası",
                    "Ramazan ve Kurban Bayramı"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": "gregorian-to-hijri",
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "gregorian-to-hijri",
                            "label": "Miladî → Hicrî"
                        },
                        {
                            "value": "hijri-to-gregorian",
                            "label": "Hicrî → Miladî"
                        }
                    ],
                    "visibleWhen": null,
                    "id": "mode",
                    "type": "radio",
                    "label": "Dönüşüm yönü",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [],
                    "visibleWhen": {
                        "fieldId": "mode",
                        "equals": "gregorian-to-hijri"
                    },
                    "id": "gregorianDate",
                    "type": "date",
                    "label": "Miladî tarih",
                    "required": false
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 1,
                    "maximum": 30,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": {
                        "fieldId": "mode",
                        "equals": "hijri-to-gregorian"
                    },
                    "id": "hijriDay",
                    "type": "number",
                    "label": "Hicrî gün",
                    "required": false
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": 1,
                            "label": "Muharrem"
                        },
                        {
                            "value": 2,
                            "label": "Safer"
                        },
                        {
                            "value": 3,
                            "label": "Rebiülevvel"
                        },
                        {
                            "value": 4,
                            "label": "Rebiülahir"
                        },
                        {
                            "value": 5,
                            "label": "Cemaziyelevvel"
                        },
                        {
                            "value": 6,
                            "label": "Cemaziyelahir"
                        },
                        {
                            "value": 7,
                            "label": "Recep"
                        },
                        {
                            "value": 8,
                            "label": "Şaban"
                        },
                        {
                            "value": 9,
                            "label": "Ramazan"
                        },
                        {
                            "value": 10,
                            "label": "Şevval"
                        },
                        {
                            "value": 11,
                            "label": "Zilkade"
                        },
                        {
                            "value": 12,
                            "label": "Zilhicce"
                        }
                    ],
                    "visibleWhen": {
                        "fieldId": "mode",
                        "equals": "hijri-to-gregorian"
                    },
                    "id": "hijriMonth",
                    "type": "select",
                    "label": "Hicrî ay",
                    "required": false
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": 1300,
                    "maximum": 1520,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": {
                        "fieldId": "mode",
                        "equals": "hijri-to-gregorian"
                    },
                    "id": "hijriYear",
                    "type": "number",
                    "label": "Hicrî yıl",
                    "required": false
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "mode",
                    "type": "text",
                    "label": "Dönüşüm yönü",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "gregorianDate",
                    "type": "text",
                    "label": "Miladî tarih",
                    "unit": null,
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "gregorianYear",
                    "type": "number",
                    "label": "Miladî yıl",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "gregorianMonth",
                    "type": "number",
                    "label": "Miladî ay",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "gregorianDay",
                    "type": "number",
                    "label": "Miladî gün",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "hijriYear",
                    "type": "number",
                    "label": "Hicrî yıl",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "hijriMonth",
                    "type": "number",
                    "label": "Hicrî ay",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "hijriDay",
                    "type": "number",
                    "label": "Hicrî gün",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "weekdayIndex",
                    "type": "number",
                    "label": "Haftanın günü",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "officialObservance",
                    "type": "text",
                    "label": "Resmî dinî gün",
                    "unit": null,
                    "nullable": true,
                    "prominent": false
                },
                {
                    "id": "approximation",
                    "type": "boolean",
                    "label": "Yaklaşık sonuç",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                }
            ]
        },
        "content": {
            "title": "Yaklaşık Hicri-Miladi Tarih Çevirici",
            "shortTitle": "Yaklaşık Hicri-Miladi Tarih Çevirici",
            "summary": "Takvimsel Julian Day dönüşümü yaklaşık tarihi üretir; Diyanet'in yayımladığı günler ayrıca resmî gözlem etiketi olarak gösterilir.",
            "method": "Takvimsel Julian Day dönüşümü yaklaşık tarihi üretir; Diyanet'in yayımladığı günler ayrıca resmî gözlem etiketi olarak gösterilir.",
            "limitations": [
                "Hicrî gün başlangıcı ve yerel gözlem nedeniyle yaklaşık dönüşüm resmî takvimin yerine geçmez."
            ],
            "faqs": [
                {
                    "question": "Sonuç kesin veya resmî bir karar mıdır?",
                    "answer": "Hayır. Sonuç yalnız gösterilen yöntem, kullanıcı girdileri ve tarihli kaynak kaydıyla üretilen yardımcı bir hesaplamadır."
                }
            ],
            "sourcePanel": {
                "title": "Kaynak ve inceleme durumu",
                "summary": "Formül ve eşikler belirlenen aralıkta resmî/otoritatif kaynaktan yeniden incelenir. Değişiklik yoksa inceleme tarihi, değişiklik varsa config sürümü yenilenir.",
                "sourceIds": [
                    "tr_religious_holidays_diyanet_manual"
                ],
                "notes": [
                    "Hicrî gün başlangıcı ve yerel gözlem nedeniyle yaklaşık dönüşüm resmî takvimin yerine geçmez."
                ]
            }
        },
        "config": {
            "kind": "hijri-gregorian-v1",
            "calculatorId": "tarih-zaman.hicri-miladi-tarih-cevirici-hesaplama",
            "gregorianYear": [
                1900,
                2100
            ],
            "hijriYear": [
                1300,
                1520
            ],
            "officialObservances": [
                {
                    "gregorianDate": "2026-03-20",
                    "label": "Ramazan Bayramı 1. günü"
                },
                {
                    "gregorianDate": "2026-08-24",
                    "label": "Mevlid Kandili"
                }
            ],
            "policyLifecycle": {
                "configVersion": "1.0.0",
                "sourceIds": [
                    "tr_religious_holidays_diyanet_manual"
                ],
                "reviewedOn": "2026-08-25",
                "effectiveFrom": "2026-08-25",
                "effectiveTo": null,
                "reviewAfter": "2027-01-01"
            }
        },
        "testVectors": {
            "kind": "date-health-oracle-v1",
            "cases": [
                {
                    "caseId": "ramadan-feast",
                    "scenario": "positive",
                    "input": {
                        "mode": "gregorian-to-hijri",
                        "gregorianDate": "2026-03-20"
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "hijriYear": 1447,
                            "hijriMonth": 10,
                            "hijriDay": 1,
                            "officialObservance": "Ramazan Bayramı 1. günü"
                        }
                    },
                    "tolerance": 1e-9
                },
                {
                    "caseId": "invalid-mode",
                    "scenario": "adversarial",
                    "input": {
                        "mode": "x"
                    },
                    "expected": {
                        "ok": false,
                        "field": "mode",
                        "errorCode": "UNSUPPORTED_VALUE"
                    },
                    "tolerance": 1e-9
                }
            ]
        },
        "lastReviewed": "2026-08-25"
    },
    {
        "toolId": "business-days",
        "slug": "is-gunu-mesai-hesaplama",
        "path": "/tarih-zaman/is-gunu-mesai-hesaplama",
        "category": {
            "id": "tarih-zaman",
            "label": "Tarih ve Zaman"
        },
        "classification": "remote-config",
        "engineId": "business-days-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-08-25",
        "effectiveTo": null,
        "minAppVersion": "0.4.0",
        "updateCadence": {
            "mode": "scheduled-source-review",
            "days": 90,
            "reviewAfter": "2027-01-01",
            "specialMonths": [],
            "note": "Belirlenen tarihte kaynak incelenir; değişiklik varsa config, yoksa inceleme tarihi yenilenir."
        },
        "sources": [
            {
                "sourceId": "tr_public_holidays_law",
                "sourceGroupId": null,
                "title": "Ulusal bayram ve genel tatiller hakkında 2429 sayılı Kanun",
                "publisher": "T.C. Mevzuat Bilgi Sistemi",
                "url": "https://www.mevzuat.gov.tr/mevzuatmetin/1.5.2429.pdf",
                "kind": "official-government",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "sabit resmî tatiller",
                    "arife yarım günleri"
                ]
            },
            {
                "sourceId": "tr_religious_holidays_diyanet_manual",
                "sourceGroupId": null,
                "title": "Diyanet yıllık dinî günler ve takvim açıklaması",
                "publisher": "Diyanet İşleri Başkanlığı",
                "url": "https://vakithesaplama.diyanet.gov.tr/icerik.php?icerik=153",
                "kind": "official-government",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "dinî günler",
                    "Hicrî-Miladi gün hizası",
                    "Ramazan ve Kurban Bayramı"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [],
                    "visibleWhen": null,
                    "id": "startDate",
                    "type": "date",
                    "label": "Başlangıç tarihi",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [],
                    "visibleWhen": null,
                    "id": "endDate",
                    "type": "date",
                    "label": "Bitiş tarihi",
                    "required": true
                },
                {
                    "defaultValue": "5gun",
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "5gun",
                            "label": "Haftada 5 gün"
                        },
                        {
                            "value": "6gun",
                            "label": "Haftada 6 gün"
                        }
                    ],
                    "visibleWhen": null,
                    "id": "workWeekMode",
                    "type": "radio",
                    "label": "Çalışma düzeni",
                    "required": true
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "startDate",
                    "type": "text",
                    "label": "Başlangıç",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "endDate",
                    "type": "text",
                    "label": "Bitiş",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "workWeekMode",
                    "type": "text",
                    "label": "Çalışma düzeni",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "totalCalendarDays",
                    "type": "number",
                    "label": "Takvim günü",
                    "unit": "gün",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "businessDays",
                    "type": "number",
                    "label": "İş günü",
                    "unit": "gün",
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "weekendDays",
                    "type": "number",
                    "label": "Hafta sonu",
                    "unit": "gün",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "officialHolidayDays",
                    "type": "number",
                    "label": "Resmî tatil",
                    "unit": "gün",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "workHoursPerDay",
                    "type": "number",
                    "label": "Günlük varsayım",
                    "unit": "saat",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "totalWorkHours",
                    "type": "number",
                    "label": "Toplam varsayımsal mesai",
                    "unit": "saat",
                    "nullable": false,
                    "prominent": true
                }
            ]
        },
        "content": {
            "title": "İki Tarih Arası İş Günü ve Mesai Hesaplama",
            "shortTitle": "İki Tarih Arası İş Günü ve Mesai Hesaplama",
            "summary": "Başlangıç ve bitiş günlerini dahil ederek hafta sonu ile tam/yarım resmî tatilleri ayırır; çalışma düzenine göre varsayımsal saat üretir.",
            "method": "Başlangıç ve bitiş günlerini dahil ederek hafta sonu ile tam/yarım resmî tatilleri ayırır; çalışma düzenine göre varsayımsal saat üretir.",
            "limitations": [
                "İşyeri sözleşmesi, vardiya ve fazla mesai düzeni ayrıca değerlendirilmelidir."
            ],
            "faqs": [
                {
                    "question": "Sonuç kesin veya resmî bir karar mıdır?",
                    "answer": "Hayır. Sonuç yalnız gösterilen yöntem, kullanıcı girdileri ve tarihli kaynak kaydıyla üretilen yardımcı bir hesaplamadır."
                }
            ],
            "sourcePanel": {
                "title": "Kaynak ve inceleme durumu",
                "summary": "Formül ve eşikler belirlenen aralıkta resmî/otoritatif kaynaktan yeniden incelenir. Değişiklik yoksa inceleme tarihi, değişiklik varsa config sürümü yenilenir.",
                "sourceIds": [
                    "tr_public_holidays_law",
                    "tr_religious_holidays_diyanet_manual"
                ],
                "notes": [
                    "İşyeri sözleşmesi, vardiya ve fazla mesai düzeni ayrıca değerlendirilmelidir."
                ]
            }
        },
        "config": {
            "kind": "business-days-v1",
            "calculatorId": "tarih-zaman.is-gunu-mesai-hesaplama",
            "calendarYear": 2026,
            "fullHolidayDates": [
                "2026-01-01",
                "2026-03-20",
                "2026-03-21",
                "2026-03-22",
                "2026-04-23",
                "2026-05-01",
                "2026-05-19",
                "2026-05-27",
                "2026-05-28",
                "2026-05-29",
                "2026-05-30",
                "2026-07-15",
                "2026-08-30",
                "2026-10-29"
            ],
            "halfHolidayDates": [
                "2026-03-19",
                "2026-05-26",
                "2026-10-28"
            ],
            "workHoursPerDay": {
                "5gun": 8,
                "6gun": 7.5
            },
            "policyLifecycle": {
                "configVersion": "1.0.0",
                "sourceIds": [
                    "tr_public_holidays_law",
                    "tr_religious_holidays_diyanet_manual"
                ],
                "reviewedOn": "2026-08-25",
                "effectiveFrom": "2026-08-25",
                "effectiveTo": null,
                "reviewAfter": "2027-01-01"
            }
        },
        "testVectors": {
            "kind": "date-health-oracle-v1",
            "cases": [
                {
                    "caseId": "feast-window",
                    "scenario": "boundary",
                    "input": {
                        "startDate": "2026-03-19",
                        "endDate": "2026-03-23",
                        "workWeekMode": "5gun"
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "totalCalendarDays": 5,
                            "businessDays": 1.5,
                            "officialHolidayDays": 3.5,
                            "totalWorkHours": 12
                        }
                    },
                    "tolerance": 1e-9
                },
                {
                    "caseId": "missing-end",
                    "scenario": "adversarial",
                    "input": {
                        "startDate": "2026-03-19",
                        "workWeekMode": "5gun"
                    },
                    "expected": {
                        "ok": false,
                        "field": "endDate",
                        "errorCode": "REQUIRED"
                    },
                    "tolerance": 1e-9
                }
            ]
        },
        "lastReviewed": "2026-08-25"
    },
    {
        "toolId": "waist-hip-ratio",
        "slug": "bel-kalca-orani-hesaplama",
        "path": "/saglik-yasam/bel-kalca-orani-hesaplama",
        "category": {
            "id": "saglik-yasam",
            "label": "Sağlık ve Yaşam"
        },
        "classification": "remote-config",
        "engineId": "waist-hip-ratio-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-08-25",
        "effectiveTo": null,
        "minAppVersion": "0.4.0",
        "updateCadence": {
            "mode": "scheduled-source-review",
            "days": 365,
            "reviewAfter": "2027-08-25",
            "specialMonths": [],
            "note": "Belirlenen tarihte kaynak incelenir; değişiklik varsa config, yoksa inceleme tarihi yenilenir."
        },
        "sources": [
            {
                "sourceId": "health_waist_hip_risk_thresholds",
                "sourceGroupId": null,
                "title": "Waist Circumference and Waist-Hip Ratio — WHO Expert Consultation",
                "publisher": "World Health Organization",
                "url": "https://www.who.int/publications/i/item/9789241501491",
                "kind": "international-standards-body",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "ölçüm protokolü",
                    "kadın ve erkek eşikleri",
                    "bel çevresi"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "kadin",
                            "label": "Kadın"
                        },
                        {
                            "value": "erkek",
                            "label": "Erkek"
                        }
                    ],
                    "visibleWhen": null,
                    "id": "sex",
                    "type": "radio",
                    "label": "Cinsiyet referansı",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "cm",
                    "minimum": 40,
                    "maximum": 200,
                    "maxDecimalPlaces": 2,
                    "options": [],
                    "visibleWhen": null,
                    "id": "waistCm",
                    "type": "number",
                    "label": "Bel çevresi",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "cm",
                    "minimum": 40,
                    "maximum": 200,
                    "maxDecimalPlaces": 2,
                    "options": [],
                    "visibleWhen": null,
                    "id": "hipCm",
                    "type": "number",
                    "label": "Kalça çevresi",
                    "required": true
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "sex",
                    "type": "text",
                    "label": "Referans",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "waistCm",
                    "type": "number",
                    "label": "Bel",
                    "unit": "cm",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "hipCm",
                    "type": "number",
                    "label": "Kalça",
                    "unit": "cm",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "ratio",
                    "type": "number",
                    "label": "Bel-kalça oranı",
                    "unit": null,
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "actionThreshold",
                    "type": "number",
                    "label": "Eylem eşiği",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "thresholdStatus",
                    "type": "text",
                    "label": "Eşik durumu",
                    "unit": null,
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "waistActionReferencesCm",
                    "type": "object",
                    "label": "Bel çevresi referansları",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                }
            ]
        },
        "content": {
            "title": "Bel-Kalça Oranı ve WHO Eylem Eşiği",
            "shortTitle": "Bel-Kalça Oranı ve WHO Eylem Eşiği",
            "summary": "Bel çevresini kalça çevresine böler ve WHO'nun cinsiyet referanslı eylem eşiğiyle karşılaştırır.",
            "method": "Bel çevresini kalça çevresine böler ve WHO'nun cinsiyet referanslı eylem eşiğiyle karşılaştırır.",
            "limitations": [
                "Sonuç kişisel tanı, resmî kayıt veya kurumsal uygunluk kararı değildir."
            ],
            "faqs": [
                {
                    "question": "Sonuç kesin veya resmî bir karar mıdır?",
                    "answer": "Hayır. Sonuç yalnız gösterilen yöntem, kullanıcı girdileri ve tarihli kaynak kaydıyla üretilen yardımcı bir hesaplamadır."
                }
            ],
            "sourcePanel": {
                "title": "Kaynak ve inceleme durumu",
                "summary": "Formül ve eşikler belirlenen aralıkta resmî/otoritatif kaynaktan yeniden incelenir. Değişiklik yoksa inceleme tarihi, değişiklik varsa config sürümü yenilenir.",
                "sourceIds": [
                    "health_waist_hip_risk_thresholds"
                ],
                "notes": [
                    "Sonuç kişisel tanı, resmî kayıt veya kurumsal uygunluk kararı değildir."
                ]
            }
        },
        "config": {
            "kind": "waist-hip-ratio-v1",
            "calculatorId": "saglik-yasam.bel-kalca-orani-hesaplama",
            "waistCm": [
                40,
                200
            ],
            "hipCm": [
                40,
                200
            ],
            "policy": {
                "erkek": {
                    "actionThreshold": 0.9,
                    "waistActionReferencesCm": [
                        94,
                        102
                    ]
                },
                "kadin": {
                    "actionThreshold": 0.85,
                    "waistActionReferencesCm": [
                        80,
                        88
                    ]
                }
            },
            "policyLifecycle": {
                "configVersion": "1.0.0",
                "sourceIds": [
                    "health_waist_hip_risk_thresholds"
                ],
                "reviewedOn": "2026-08-25",
                "effectiveFrom": "2026-08-25",
                "effectiveTo": null,
                "reviewAfter": "2027-08-25"
            }
        },
        "testVectors": {
            "kind": "date-health-oracle-v1",
            "cases": [
                {
                    "caseId": "women-threshold",
                    "scenario": "threshold",
                    "input": {
                        "sex": "kadin",
                        "waistCm": 85,
                        "hipCm": 100
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "ratio": 0.85,
                            "actionThreshold": 0.85,
                            "thresholdStatus": "at-or-above"
                        }
                    },
                    "tolerance": 1e-9
                },
                {
                    "caseId": "zero-hip",
                    "scenario": "adversarial",
                    "input": {
                        "sex": "kadin",
                        "waistCm": 85,
                        "hipCm": 0
                    },
                    "expected": {
                        "ok": false,
                        "field": "hipCm",
                        "errorCode": "OUT_OF_RANGE"
                    },
                    "tolerance": 1e-9
                }
            ]
        },
        "lastReviewed": "2026-08-25"
    },
    {
        "toolId": "pregnancy-dating",
        "slug": "gebelik-haftasi-hesaplama",
        "path": "/saglik-yasam/gebelik-haftasi-hesaplama",
        "category": {
            "id": "saglik-yasam",
            "label": "Sağlık ve Yaşam"
        },
        "classification": "remote-config",
        "engineId": "pregnancy-dating-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-08-25",
        "effectiveTo": null,
        "minAppVersion": "0.4.0",
        "updateCadence": {
            "mode": "scheduled-source-review",
            "days": 365,
            "reviewAfter": "2027-08-25",
            "specialMonths": [],
            "note": "Belirlenen tarihte kaynak incelenir; değişiklik varsa config, yoksa inceleme tarihi yenilenir."
        },
        "sources": [
            {
                "sourceId": "health_pregnancy_dating_guideline",
                "sourceGroupId": null,
                "title": "Methods for Estimating the Due Date",
                "publisher": "American College of Obstetricians and Gynecologists",
                "url": "https://www.acog.org/clinical/clinical-guidance/committee-opinion/articles/2017/05/methods-for-estimating-the-due-date",
                "kind": "international-standards-body",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "SAT ve 280 gün",
                    "döngü düzeltmesi",
                    "ultrason önceliği"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [],
                    "visibleWhen": null,
                    "id": "lastMenstrualPeriodDate",
                    "type": "date",
                    "label": "Son adet tarihinin ilk günü",
                    "required": true
                },
                {
                    "defaultValue": 28,
                    "placeholder": null,
                    "unit": "gün",
                    "minimum": 21,
                    "maximum": 35,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "cycleDays",
                    "type": "number",
                    "label": "Ortalama döngü süresi",
                    "required": false
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "lastMenstrualPeriodDate",
                    "type": "text",
                    "label": "Son adet tarihi",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "cycleDays",
                    "type": "number",
                    "label": "Döngü",
                    "unit": "gün",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "referenceDate",
                    "type": "text",
                    "label": "Hesap tarihi",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "estimatedConceptionDate",
                    "type": "text",
                    "label": "Tahminî döllenme",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "estimatedDueDate",
                    "type": "text",
                    "label": "Tahminî doğum tarihi",
                    "unit": null,
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "gestationalAgeDays",
                    "type": "number",
                    "label": "Gebelik yaşı",
                    "unit": "gün",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "gestationalWeeks",
                    "type": "number",
                    "label": "Tam hafta",
                    "unit": "hafta",
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "gestationalRemainderDays",
                    "type": "number",
                    "label": "Artan gün",
                    "unit": "gün",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "trimester",
                    "type": "number",
                    "label": "Trimester",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "daysUntilDueDate",
                    "type": "number",
                    "label": "Doğuma kalan",
                    "unit": "gün",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "dueDatePassed",
                    "type": "boolean",
                    "label": "Tahminî tarih geçti mi?",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "completionPercent",
                    "type": "percentage",
                    "label": "Tahminî ilerleme",
                    "unit": "%",
                    "nullable": false,
                    "prominent": false
                }
            ]
        },
        "content": {
            "title": "Gebelik Haftası ve Doğum Tarihi Hesaplama",
            "shortTitle": "Gebelik Haftası ve Doğum Tarihi Hesaplama",
            "summary": "Son adet tarihine 280 gün ekler, 28 günden farklı döngüyü gün bazında düzeltir ve bugüne göre yaklaşık gebelik haftasını verir.",
            "method": "Son adet tarihine 280 gün ekler, 28 günden farklı döngüyü gün bazında düzeltir ve bugüne göre yaklaşık gebelik haftasını verir.",
            "limitations": [
                "Ultrason ve hekim tarihlemesi, özellikle düzensiz döngüde bu yaklaşık sonucu değiştirebilir."
            ],
            "faqs": [
                {
                    "question": "Sonuç kesin veya resmî bir karar mıdır?",
                    "answer": "Hayır. Sonuç yalnız gösterilen yöntem, kullanıcı girdileri ve tarihli kaynak kaydıyla üretilen yardımcı bir hesaplamadır."
                }
            ],
            "sourcePanel": {
                "title": "Kaynak ve inceleme durumu",
                "summary": "Formül ve eşikler belirlenen aralıkta resmî/otoritatif kaynaktan yeniden incelenir. Değişiklik yoksa inceleme tarihi, değişiklik varsa config sürümü yenilenir.",
                "sourceIds": [
                    "health_pregnancy_dating_guideline"
                ],
                "notes": [
                    "Ultrason ve hekim tarihlemesi, özellikle düzensiz döngüde bu yaklaşık sonucu değiştirebilir."
                ]
            }
        },
        "config": {
            "kind": "pregnancy-dating-v1",
            "calculatorId": "saglik-yasam.gebelik-haftasi-hesaplama",
            "cycleDays": [
                21,
                35
            ],
            "defaultCycleDays": 28,
            "standardCycleDays": 28,
            "standardGestationDays": 280,
            "lutealPhaseDays": 14,
            "maximumGestationalAgeDays": 294,
            "trimesterStartsWeeks": [
                14,
                28
            ],
            "policyLifecycle": {
                "configVersion": "1.0.0",
                "sourceIds": [
                    "health_pregnancy_dating_guideline"
                ],
                "reviewedOn": "2026-08-25",
                "effectiveFrom": "2026-08-25",
                "effectiveTo": null,
                "reviewAfter": "2027-08-25"
            }
        },
        "testVectors": {
            "kind": "date-health-oracle-v1",
            "cases": [
                {
                    "caseId": "ninety-nine-days",
                    "scenario": "positive",
                    "input": {
                        "lastMenstrualPeriodDate": "2026-05-18",
                        "cycleDays": 28
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "gestationalAgeDays": 99,
                            "gestationalWeeks": 14,
                            "gestationalRemainderDays": 1,
                            "trimester": 2,
                            "estimatedDueDate": "2027-02-22"
                        }
                    },
                    "tolerance": 1e-9
                },
                {
                    "caseId": "missing-lmp",
                    "scenario": "adversarial",
                    "input": {},
                    "expected": {
                        "ok": false,
                        "field": "lastMenstrualPeriodDate",
                        "errorCode": "REQUIRED"
                    },
                    "tolerance": 1e-9
                }
            ]
        },
        "lastReviewed": "2026-08-25"
    },
    {
        "toolId": "daily-calorie-need",
        "slug": "gunluk-kalori-ihtiyaci-hesaplama",
        "path": "/saglik-yasam/gunluk-kalori-ihtiyaci-hesaplama",
        "category": {
            "id": "saglik-yasam",
            "label": "Sağlık ve Yaşam"
        },
        "classification": "remote-config",
        "engineId": "daily-calorie-need-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-08-25",
        "effectiveTo": null,
        "minAppVersion": "0.4.0",
        "updateCadence": {
            "mode": "scheduled-source-review",
            "days": 365,
            "reviewAfter": "2027-08-25",
            "specialMonths": [],
            "note": "Belirlenen tarihte kaynak incelenir; değişiklik varsa config, yoksa inceleme tarihi yenilenir."
        },
        "sources": [
            {
                "sourceId": "health_energy_need_equations",
                "sourceGroupId": null,
                "title": "A new predictive equation for resting energy expenditure in healthy individuals",
                "publisher": "U.S. National Library of Medicine / PubMed",
                "url": "https://eutils.ncbi.nlm.nih.gov/entrez/eutils/efetch.fcgi?db=pubmed&id=2305711&retmode=xml",
                "kind": "peer-reviewed-medical-research",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "Mifflin-St Jeor denklem katsayıları",
                    "yaş kapsamı"
                ]
            },
            {
                "sourceId": "health_energy_activity_multipliers",
                "sourceGroupId": null,
                "title": "Günlük enerji ihtiyacı aktivite katsayıları",
                "publisher": "PubMed Central",
                "url": "https://pmc.ncbi.nlm.nih.gov/articles/PMC10798935/",
                "kind": "peer-reviewed-medical-research",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "1,2 hareketsiz",
                    "1,375 hafif aktif",
                    "1,55 orta aktif",
                    "1,725 çok aktif",
                    "1,9 ekstra aktif"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "kadin",
                            "label": "Kadın"
                        },
                        {
                            "value": "erkek",
                            "label": "Erkek"
                        }
                    ],
                    "visibleWhen": null,
                    "id": "sex",
                    "type": "radio",
                    "label": "Cinsiyet referansı",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "yıl",
                    "minimum": 18,
                    "maximum": 100,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "ageYears",
                    "type": "number",
                    "label": "Yaş",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "cm",
                    "minimum": 100,
                    "maximum": 250,
                    "maxDecimalPlaces": 2,
                    "options": [],
                    "visibleWhen": null,
                    "id": "heightCm",
                    "type": "number",
                    "label": "Boy",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "kg",
                    "minimum": 20,
                    "maximum": 300,
                    "maxDecimalPlaces": 2,
                    "options": [],
                    "visibleWhen": null,
                    "id": "weightKg",
                    "type": "number",
                    "label": "Kilo",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "sedentary",
                            "label": "Hareketsiz"
                        },
                        {
                            "value": "light",
                            "label": "Hafif aktif"
                        },
                        {
                            "value": "moderate",
                            "label": "Orta aktif"
                        },
                        {
                            "value": "very-active",
                            "label": "Çok aktif"
                        },
                        {
                            "value": "extra-active",
                            "label": "Ekstra aktif"
                        }
                    ],
                    "visibleWhen": null,
                    "id": "activityLevel",
                    "type": "select",
                    "label": "Aktivite düzeyi",
                    "required": true
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "sex",
                    "type": "text",
                    "label": "Referans",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "ageYears",
                    "type": "number",
                    "label": "Yaş",
                    "unit": "yıl",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "heightCm",
                    "type": "number",
                    "label": "Boy",
                    "unit": "cm",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "weightKg",
                    "type": "number",
                    "label": "Kilo",
                    "unit": "kg",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "activityLevel",
                    "type": "text",
                    "label": "Aktivite düzeyi",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "activityMultiplier",
                    "type": "number",
                    "label": "Aktivite katsayısı",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "estimatedRestingEnergyKcalPerDay",
                    "type": "number",
                    "label": "Tahminî dinlenme enerjisi",
                    "unit": "kcal/gün",
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "estimatedTotalEnergyKcalPerDay",
                    "type": "number",
                    "label": "Tahminî toplam enerji",
                    "unit": "kcal/gün",
                    "nullable": false,
                    "prominent": true
                }
            ]
        },
        "content": {
            "title": "Yaklaşık Günlük Toplam Enerji Harcaması",
            "shortTitle": "Yaklaşık Günlük Toplam Enerji Harcaması",
            "summary": "Mifflin-St Jeor tahminini seçilen aktivite katsayısıyla çarpar; dinlenme ve toplam enerji tahminini ayrı verir.",
            "method": "Mifflin-St Jeor tahminini seçilen aktivite katsayısıyla çarpar; dinlenme ve toplam enerji tahminini ayrı verir.",
            "limitations": [
                "Hamilelik, emzirme, büyüme, hastalık ve atletik dönemlerde kişisel değerlendirme gerekir."
            ],
            "faqs": [
                {
                    "question": "Sonuç kesin veya resmî bir karar mıdır?",
                    "answer": "Hayır. Sonuç yalnız gösterilen yöntem, kullanıcı girdileri ve tarihli kaynak kaydıyla üretilen yardımcı bir hesaplamadır."
                }
            ],
            "sourcePanel": {
                "title": "Kaynak ve inceleme durumu",
                "summary": "Formül ve eşikler belirlenen aralıkta resmî/otoritatif kaynaktan yeniden incelenir. Değişiklik yoksa inceleme tarihi, değişiklik varsa config sürümü yenilenir.",
                "sourceIds": [
                    "health_energy_need_equations",
                    "health_energy_activity_multipliers"
                ],
                "notes": [
                    "Hamilelik, emzirme, büyüme, hastalık ve atletik dönemlerde kişisel değerlendirme gerekir."
                ]
            }
        },
        "config": {
            "kind": "daily-calorie-need-v1",
            "calculatorId": "saglik-yasam.gunluk-kalori-ihtiyaci-hesaplama",
            "ageYears": [
                18,
                100
            ],
            "heightCm": [
                100,
                250
            ],
            "weightKg": [
                20,
                300
            ],
            "equation": {
                "weightCoefficient": 10,
                "heightCoefficient": 6.25,
                "ageCoefficient": 5,
                "sexConstant": {
                    "erkek": 5,
                    "kadin": -161
                }
            },
            "activityMultipliers": {
                "sedentary": 1.2,
                "light": 1.375,
                "moderate": 1.55,
                "very-active": 1.725,
                "extra-active": 1.9
            },
            "policyLifecycle": {
                "configVersion": "1.0.0",
                "sourceIds": [
                    "health_energy_need_equations",
                    "health_energy_activity_multipliers"
                ],
                "reviewedOn": "2026-08-25",
                "effectiveFrom": "2026-08-25",
                "effectiveTo": null,
                "reviewAfter": "2027-08-25"
            }
        },
        "testVectors": {
            "kind": "date-health-oracle-v1",
            "cases": [
                {
                    "caseId": "adult-moderate",
                    "scenario": "positive",
                    "input": {
                        "sex": "erkek",
                        "ageYears": 30,
                        "heightCm": 180,
                        "weightKg": 80,
                        "activityLevel": "moderate"
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "activityMultiplier": 1.55,
                            "estimatedRestingEnergyKcalPerDay": 1780,
                            "estimatedTotalEnergyKcalPerDay": 2759
                        }
                    },
                    "tolerance": 1e-9
                },
                {
                    "caseId": "minor",
                    "scenario": "adversarial",
                    "input": {
                        "sex": "erkek",
                        "ageYears": 17,
                        "heightCm": 180,
                        "weightKg": 80,
                        "activityLevel": "moderate"
                    },
                    "expected": {
                        "ok": false,
                        "field": "ageYears",
                        "errorCode": "OUT_OF_RANGE"
                    },
                    "tolerance": 1e-9
                }
            ]
        },
        "lastReviewed": "2026-08-25"
    },
    {
        "toolId": "daily-water-reference",
        "slug": "gunluk-su-ihtiyaci-hesaplama",
        "path": "/saglik-yasam/gunluk-su-ihtiyaci-hesaplama",
        "category": {
            "id": "saglik-yasam",
            "label": "Sağlık ve Yaşam"
        },
        "classification": "remote-config",
        "engineId": "daily-water-reference-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-08-25",
        "effectiveTo": null,
        "minAppVersion": "0.4.0",
        "updateCadence": {
            "mode": "scheduled-source-review",
            "days": 365,
            "reviewAfter": "2027-08-25",
            "specialMonths": [],
            "note": "Belirlenen tarihte kaynak incelenir; değişiklik varsa config, yoksa inceleme tarihi yenilenir."
        },
        "sources": [
            {
                "sourceId": "health_water_intake_pubmed",
                "sourceGroupId": null,
                "title": "Yetişkin toplam su referansı ve hidrasyon kapsamı",
                "publisher": "U.S. National Library of Medicine / PubMed",
                "url": "https://eutils.ncbi.nlm.nih.gov/entrez/eutils/efetch.fcgi?db=pubmed&id=28985128&retmode=xml",
                "kind": "peer-reviewed-medical-research",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "toplam su ve içecek ayrımı",
                    "egzersiz ve sıcaklık"
                ]
            },
            {
                "sourceId": "health_water_intake_efsa_manual",
                "sourceGroupId": null,
                "title": "Scientific Opinion on Dietary Reference Values for water",
                "publisher": "European Food Safety Authority",
                "url": "https://efsa.onlinelibrary.wiley.com/doi/abs/10.2903/j.efsa.2010.1459",
                "kind": "international-standards-body",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "kadın",
                    "erkek",
                    "gebelik",
                    "emzirme",
                    "toplam su"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "kadin",
                            "label": "Kadın"
                        },
                        {
                            "value": "erkek",
                            "label": "Erkek"
                        }
                    ],
                    "visibleWhen": null,
                    "id": "adultGroup",
                    "type": "select",
                    "label": "Yetişkin referansı",
                    "required": true
                },
                {
                    "defaultValue": false,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [],
                    "visibleWhen": null,
                    "id": "sweating",
                    "type": "boolean",
                    "label": "Belirgin terleme/egzersiz var",
                    "required": true
                },
                {
                    "defaultValue": false,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [],
                    "visibleWhen": null,
                    "id": "hotEnvironment",
                    "type": "boolean",
                    "label": "Sıcak ortamda bulunuyorum",
                    "required": true
                },
                {
                    "defaultValue": "none",
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "none",
                            "label": "Yok"
                        },
                        {
                            "value": "pregnancy",
                            "label": "Gebelik"
                        },
                        {
                            "value": "lactation",
                            "label": "Emzirme"
                        }
                    ],
                    "visibleWhen": null,
                    "id": "specialStatus",
                    "type": "select",
                    "label": "Özel durum",
                    "required": true
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "adultGroup",
                    "type": "text",
                    "label": "Yetişkin referansı",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "sweating",
                    "type": "boolean",
                    "label": "Terleme/egzersiz",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "hotEnvironment",
                    "type": "boolean",
                    "label": "Sıcak ortam",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "specialStatus",
                    "type": "text",
                    "label": "Özel durum",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "baseTotalWaterMl",
                    "type": "number",
                    "label": "Temel toplam su",
                    "unit": "ml",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "specialStatusAdditionMl",
                    "type": "number",
                    "label": "Özel durum ilavesi",
                    "unit": "ml",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "totalWaterMl",
                    "type": "number",
                    "label": "Toplam su referansı",
                    "unit": "ml/gün",
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "exerciseAdditionMl",
                    "type": "number",
                    "label": "Egzersiz ilavesi",
                    "unit": "ml",
                    "nullable": true,
                    "prominent": false
                },
                {
                    "id": "climateAdditionMl",
                    "type": "number",
                    "label": "İklim ilavesi",
                    "unit": "ml",
                    "nullable": true,
                    "prominent": false
                },
                {
                    "id": "individualReviewNeeded",
                    "type": "boolean",
                    "label": "Kişisel değerlendirme gerekli",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "includesFoodAndBeverages",
                    "type": "boolean",
                    "label": "Yiyecek ve içecekleri içerir",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                }
            ]
        },
        "content": {
            "title": "Yetişkinler İçin Günlük Toplam Su Referansı",
            "shortTitle": "Yetişkinler İçin Günlük Toplam Su Referansı",
            "summary": "EFSA yetişkin toplam su referansına gebelik veya emzirme ilavesini ekler; terleme ve sıcaklık için uydurma sabit ilave üretmez, kişisel değerlendirme uyarısı verir.",
            "method": "EFSA yetişkin toplam su referansına gebelik veya emzirme ilavesini ekler; terleme ve sıcaklık için uydurma sabit ilave üretmez, kişisel değerlendirme uyarısı verir.",
            "limitations": [
                "Toplam su, yiyecek ve tüm içeceklerden gelen suyu kapsar; hastalıkta klinik öneri gerekir."
            ],
            "faqs": [
                {
                    "question": "Sonuç kesin veya resmî bir karar mıdır?",
                    "answer": "Hayır. Sonuç yalnız gösterilen yöntem, kullanıcı girdileri ve tarihli kaynak kaydıyla üretilen yardımcı bir hesaplamadır."
                }
            ],
            "sourcePanel": {
                "title": "Kaynak ve inceleme durumu",
                "summary": "Formül ve eşikler belirlenen aralıkta resmî/otoritatif kaynaktan yeniden incelenir. Değişiklik yoksa inceleme tarihi, değişiklik varsa config sürümü yenilenir.",
                "sourceIds": [
                    "health_water_intake_pubmed",
                    "health_water_intake_efsa_manual"
                ],
                "notes": [
                    "Toplam su, yiyecek ve tüm içeceklerden gelen suyu kapsar; hastalıkta klinik öneri gerekir."
                ]
            }
        },
        "config": {
            "kind": "daily-water-reference-v1",
            "calculatorId": "saglik-yasam.gunluk-su-ihtiyaci-hesaplama",
            "baseTotalWaterMl": {
                "kadin": 2000,
                "erkek": 2500
            },
            "specialStatusAdditionMl": {
                "none": 0,
                "pregnancy": 300,
                "lactation": 700
            },
            "policyLifecycle": {
                "configVersion": "1.0.0",
                "sourceIds": [
                    "health_water_intake_pubmed",
                    "health_water_intake_efsa_manual"
                ],
                "reviewedOn": "2026-08-25",
                "effectiveFrom": "2026-08-25",
                "effectiveTo": null,
                "reviewAfter": "2027-08-25"
            }
        },
        "testVectors": {
            "kind": "date-health-oracle-v1",
            "cases": [
                {
                    "caseId": "pregnancy-and-exercise",
                    "scenario": "positive",
                    "input": {
                        "adultGroup": "kadin",
                        "sweating": true,
                        "hotEnvironment": false,
                        "specialStatus": "pregnancy"
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "baseTotalWaterMl": 2000,
                            "specialStatusAdditionMl": 300,
                            "totalWaterMl": 2300,
                            "exerciseAdditionMl": null,
                            "climateAdditionMl": null,
                            "individualReviewNeeded": true
                        }
                    },
                    "tolerance": 1e-9
                },
                {
                    "caseId": "invalid-group",
                    "scenario": "adversarial",
                    "input": {
                        "adultGroup": "x",
                        "sweating": false,
                        "hotEnvironment": false,
                        "specialStatus": "none"
                    },
                    "expected": {
                        "ok": false,
                        "field": "adultGroup",
                        "errorCode": "UNSUPPORTED_VALUE"
                    },
                    "tolerance": 1e-9
                }
            ]
        },
        "lastReviewed": "2026-08-25"
    },
    {
        "toolId": "adult-bmi",
        "slug": "vki-hesaplama",
        "path": "/saglik-yasam/vki-hesaplama",
        "category": {
            "id": "saglik-yasam",
            "label": "Sağlık ve Yaşam"
        },
        "classification": "remote-config",
        "engineId": "adult-bmi-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-08-25",
        "effectiveTo": null,
        "minAppVersion": "0.4.0",
        "updateCadence": {
            "mode": "scheduled-source-review",
            "days": 365,
            "reviewAfter": "2027-08-25",
            "specialMonths": [],
            "note": "Belirlenen tarihte kaynak incelenir; değişiklik varsa config, yoksa inceleme tarihi yenilenir."
        },
        "sources": [
            {
                "sourceId": "health_adult_bmi_categories",
                "sourceGroupId": null,
                "title": "Adult BMI Categories",
                "publisher": "U.S. Centers for Disease Control and Prevention",
                "url": "https://www.cdc.gov/bmi/adult-calculator/bmi-categories.html",
                "kind": "official-government",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "yetişkin yaş kapsamı",
                    "18.5",
                    "25",
                    "30",
                    "35",
                    "40"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "cm",
                    "minimum": 100,
                    "maximum": 250,
                    "maxDecimalPlaces": 2,
                    "options": [],
                    "visibleWhen": null,
                    "id": "heightCm",
                    "type": "number",
                    "label": "Boy",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "kg",
                    "minimum": 20,
                    "maximum": 300,
                    "maxDecimalPlaces": 2,
                    "options": [],
                    "visibleWhen": null,
                    "id": "weightKg",
                    "type": "number",
                    "label": "Kilo",
                    "required": true
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "heightCm",
                    "type": "number",
                    "label": "Boy",
                    "unit": "cm",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "weightKg",
                    "type": "number",
                    "label": "Kilo",
                    "unit": "kg",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "bmi",
                    "type": "number",
                    "label": "Vücut kitle indeksi",
                    "unit": null,
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "categoryId",
                    "type": "text",
                    "label": "Kategori kimliği",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "categoryLabel",
                    "type": "text",
                    "label": "Yetişkin referans kategorisi",
                    "unit": null,
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "referenceMinimumWeightKg",
                    "type": "number",
                    "label": "Referans alt ağırlık",
                    "unit": "kg",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "referenceMaximumWeightKg",
                    "type": "number",
                    "label": "Referans üst ağırlık",
                    "unit": "kg",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "referencePosition",
                    "type": "text",
                    "label": "Referans konumu",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "differenceFromReferenceKg",
                    "type": "number",
                    "label": "Referans farkı",
                    "unit": "kg",
                    "nullable": false,
                    "prominent": false
                }
            ]
        },
        "content": {
            "title": "Yetişkin Vücut Kitle İndeksi (VKİ)",
            "shortTitle": "Yetişkin Vücut Kitle İndeksi (VKİ)",
            "summary": "Kilogram cinsinden ağırlığı metre cinsinden boyun karesine böler ve yalnız yetişkin WHO/CDC referans kategorisini gösterir.",
            "method": "Kilogram cinsinden ağırlığı metre cinsinden boyun karesine böler ve yalnız yetişkin WHO/CDC referans kategorisini gösterir.",
            "limitations": [
                "Çocuklar, gebeler, yaşlılar ve yüksek kas kütlesi olanlarda tek başına yorumlanmamalıdır."
            ],
            "faqs": [
                {
                    "question": "Sonuç kesin veya resmî bir karar mıdır?",
                    "answer": "Hayır. Sonuç yalnız gösterilen yöntem, kullanıcı girdileri ve tarihli kaynak kaydıyla üretilen yardımcı bir hesaplamadır."
                }
            ],
            "sourcePanel": {
                "title": "Kaynak ve inceleme durumu",
                "summary": "Formül ve eşikler belirlenen aralıkta resmî/otoritatif kaynaktan yeniden incelenir. Değişiklik yoksa inceleme tarihi, değişiklik varsa config sürümü yenilenir.",
                "sourceIds": [
                    "health_adult_bmi_categories"
                ],
                "notes": [
                    "Çocuklar, gebeler, yaşlılar ve yüksek kas kütlesi olanlarda tek başına yorumlanmamalıdır."
                ]
            }
        },
        "config": {
            "kind": "adult-bmi-v1",
            "calculatorId": "saglik-yasam.vki-hesaplama",
            "heightCm": [
                100,
                250
            ],
            "weightKg": [
                20,
                300
            ],
            "referenceBmi": [
                18.5,
                24.9
            ],
            "categories": [
                {
                    "id": "underweight",
                    "label": "Düşük kilolu",
                    "minimumInclusive": null,
                    "maximumExclusive": 18.5
                },
                {
                    "id": "reference",
                    "label": "VKİ yetişkin referans aralığı",
                    "minimumInclusive": 18.5,
                    "maximumExclusive": 25
                },
                {
                    "id": "overweight",
                    "label": "Fazla kilolu",
                    "minimumInclusive": 25,
                    "maximumExclusive": 30
                },
                {
                    "id": "obesity-1",
                    "label": "Obezite (Sınıf I)",
                    "minimumInclusive": 30,
                    "maximumExclusive": 35
                },
                {
                    "id": "obesity-2",
                    "label": "Obezite (Sınıf II)",
                    "minimumInclusive": 35,
                    "maximumExclusive": 40
                },
                {
                    "id": "obesity-3",
                    "label": "Obezite (Sınıf III)",
                    "minimumInclusive": 40,
                    "maximumExclusive": null
                }
            ],
            "policyLifecycle": {
                "configVersion": "1.0.0",
                "sourceIds": [
                    "health_adult_bmi_categories"
                ],
                "reviewedOn": "2026-08-25",
                "effectiveFrom": "2026-08-25",
                "effectiveTo": null,
                "reviewAfter": "2027-08-25"
            }
        },
        "testVectors": {
            "kind": "date-health-oracle-v1",
            "cases": [
                {
                    "caseId": "bmi-25",
                    "scenario": "threshold",
                    "input": {
                        "heightCm": 180,
                        "weightKg": 81
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "bmi": 25,
                            "categoryId": "overweight",
                            "categoryLabel": "Fazla kilolu",
                            "referencePosition": "above"
                        }
                    },
                    "tolerance": 1e-9
                },
                {
                    "caseId": "zero-height",
                    "scenario": "adversarial",
                    "input": {
                        "heightCm": 0,
                        "weightKg": 81
                    },
                    "expected": {
                        "ok": false,
                        "field": "heightCm",
                        "errorCode": "OUT_OF_RANGE"
                    },
                    "tolerance": 1e-9
                }
            ]
        },
        "lastReviewed": "2026-08-25"
    },
    {
        "toolId": "historical-body-fat",
        "slug": "yag-orani-hesaplama",
        "path": "/saglik-yasam/yag-orani-hesaplama",
        "category": {
            "id": "saglik-yasam",
            "label": "Sağlık ve Yaşam"
        },
        "classification": "remote-config",
        "engineId": "historical-body-fat-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-08-25",
        "effectiveTo": null,
        "minAppVersion": "0.4.0",
        "updateCadence": {
            "mode": "scheduled-source-review",
            "days": 30,
            "reviewAfter": "2027-01-03",
            "specialMonths": [],
            "note": "Belirlenen tarihte kaynak incelenir; değişiklik varsa config, yoksa inceleme tarihi yenilenir."
        },
        "sources": [
            {
                "sourceId": "health_body_fat_historical_equation",
                "sourceGroupId": null,
                "title": "Body Composition Equations and Their Origins",
                "publisher": "National Academies / NCBI Bookshelf",
                "url": "https://www.ncbi.nlm.nih.gov/books/NBK235939/",
                "kind": "peer-reviewed-medical-research",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "Hodgdon-Beckett çevre denklemleri",
                    "inç birimi",
                    "denklem katsayıları"
                ]
            },
            {
                "sourceId": "health_body_fat_army_standard",
                "sourceGroupId": null,
                "title": "Army Directive 2026-13 — Army Body Composition Program",
                "publisher": "U.S. Department of the Army",
                "url": "https://home.army.mil/carson/8117/8345/8209/ARMY_DIR_2026-13_ABCP.pdf",
                "kind": "official-government",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "WHtR formülü",
                    "0,55 eşiği",
                    "eski çevre yöntemlerinin yürürlük durumu"
                ]
            },
            {
                "sourceId": "health_body_fat_navy_manual",
                "sourceGroupId": null,
                "title": "Guide 4 — Body Composition Assessment",
                "publisher": "MyNavy HR",
                "url": "https://www.mynavyhr.navy.mil/Portals/55/Support/Culture%20Resilience/Physical/Guide-4%20Body%20Composition%20Assessment.pdf",
                "kind": "official-government",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "WHtR birinci adım",
                    "ikinci adım",
                    "ölçüm protokolü"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "erkek",
                            "label": "Erkek çevre denklemi"
                        },
                        {
                            "value": "kadin",
                            "label": "Kadın çevre denklemi"
                        }
                    ],
                    "visibleWhen": null,
                    "id": "sex",
                    "type": "radio",
                    "label": "Denklem",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "cm",
                    "minimum": 140,
                    "maximum": 220,
                    "maxDecimalPlaces": 2,
                    "options": [],
                    "visibleWhen": null,
                    "id": "heightCm",
                    "type": "number",
                    "label": "Boy",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "cm",
                    "minimum": 50,
                    "maximum": 180,
                    "maxDecimalPlaces": 2,
                    "options": [],
                    "visibleWhen": null,
                    "id": "waistCm",
                    "type": "number",
                    "label": "Bel çevresi",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "cm",
                    "minimum": 25,
                    "maximum": 60,
                    "maxDecimalPlaces": 2,
                    "options": [],
                    "visibleWhen": null,
                    "id": "neckCm",
                    "type": "number",
                    "label": "Boyun çevresi",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "cm",
                    "minimum": 60,
                    "maximum": 180,
                    "maxDecimalPlaces": 2,
                    "options": [],
                    "visibleWhen": null,
                    "id": "hipCm",
                    "type": "number",
                    "label": "Kalça çevresi",
                    "required": false
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "kg",
                    "minimum": 30,
                    "maximum": 250,
                    "maxDecimalPlaces": 2,
                    "options": [],
                    "visibleWhen": null,
                    "id": "weightKg",
                    "type": "number",
                    "label": "Kilo",
                    "required": false
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "sex",
                    "type": "text",
                    "label": "Denklem",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "heightCm",
                    "type": "number",
                    "label": "Boy",
                    "unit": "cm",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "waistCm",
                    "type": "number",
                    "label": "Bel",
                    "unit": "cm",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "neckCm",
                    "type": "number",
                    "label": "Boyun",
                    "unit": "cm",
                    "nullable": false,
                    "prominent": false
                },
                {
                    "id": "hipCm",
                    "type": "number",
                    "label": "Kalça",
                    "unit": "cm",
                    "nullable": true,
                    "prominent": false
                },
                {
                    "id": "weightKg",
                    "type": "number",
                    "label": "Kilo",
                    "unit": "kg",
                    "nullable": true,
                    "prominent": false
                },
                {
                    "id": "bodyFatPercent",
                    "type": "percentage",
                    "label": "Tahminî yağ oranı",
                    "unit": "%",
                    "nullable": false,
                    "prominent": true
                },
                {
                    "id": "fatMassKg",
                    "type": "number",
                    "label": "Tahminî yağ kütlesi",
                    "unit": "kg",
                    "nullable": true,
                    "prominent": false
                },
                {
                    "id": "leanMassKg",
                    "type": "number",
                    "label": "Tahminî yağsız kütle",
                    "unit": "kg",
                    "nullable": true,
                    "prominent": false
                },
                {
                    "id": "method",
                    "type": "text",
                    "label": "Yöntem",
                    "unit": null,
                    "nullable": false,
                    "prominent": false
                }
            ]
        },
        "content": {
            "title": "Çevre Ölçülerinden Vücut Yağ Oranı Tahmini",
            "shortTitle": "Çevre Ölçülerinden Vücut Yağ Oranı Tahmini",
            "summary": "Santimetre ölçülerini inçe çevirip tarihsel Hodgdon-Beckett logaritmik çevre denklemini uygular; güncel Army veya Navy uygunluğu üretmez.",
            "method": "Santimetre ölçülerini inçe çevirip tarihsel Hodgdon-Beckett logaritmik çevre denklemini uygular; güncel Army veya Navy uygunluğu üretmez.",
            "limitations": [
                "Bu tarihsel tahmin klinik ölçüm değildir; güncel Army WHtR ve Navy BCA yöntemleri ayrıdır."
            ],
            "faqs": [
                {
                    "question": "Sonuç kesin veya resmî bir karar mıdır?",
                    "answer": "Hayır. Sonuç yalnız gösterilen yöntem, kullanıcı girdileri ve tarihli kaynak kaydıyla üretilen yardımcı bir hesaplamadır."
                }
            ],
            "sourcePanel": {
                "title": "Kaynak ve inceleme durumu",
                "summary": "Formül ve eşikler belirlenen aralıkta resmî/otoritatif kaynaktan yeniden incelenir. Değişiklik yoksa inceleme tarihi, değişiklik varsa config sürümü yenilenir.",
                "sourceIds": [
                    "health_body_fat_historical_equation",
                    "health_body_fat_army_standard",
                    "health_body_fat_navy_manual"
                ],
                "notes": [
                    "Bu tarihsel tahmin klinik ölçüm değildir; güncel Army WHtR ve Navy BCA yöntemleri ayrıdır."
                ]
            }
        },
        "config": {
            "kind": "historical-body-fat-v1",
            "calculatorId": "saglik-yasam.yag-orani-hesaplama",
            "heightCm": [
                140,
                220
            ],
            "waistCm": [
                50,
                180
            ],
            "neckCm": [
                25,
                60
            ],
            "hipCm": [
                60,
                180
            ],
            "optionalWeightKg": [
                30,
                250
            ],
            "centimetresPerInch": 2.54,
            "validResultPercent": [
                0,
                75
            ],
            "coefficients": {
                "erkek": {
                    "circumferenceCoefficient": 86.01,
                    "heightCoefficient": 70.041,
                    "constant": 36.76
                },
                "kadin": {
                    "circumferenceCoefficient": 163.205,
                    "heightCoefficient": 97.684,
                    "constant": -78.387
                }
            },
            "policyLifecycle": {
                "configVersion": "1.0.0",
                "sourceIds": [
                    "health_body_fat_historical_equation",
                    "health_body_fat_army_standard",
                    "health_body_fat_navy_manual"
                ],
                "reviewedOn": "2026-08-25",
                "effectiveFrom": "2026-08-25",
                "effectiveTo": null,
                "reviewAfter": "2027-01-03"
            }
        },
        "testVectors": {
            "kind": "date-health-oracle-v1",
            "cases": [
                {
                    "caseId": "historical-men",
                    "scenario": "positive",
                    "input": {
                        "sex": "erkek",
                        "heightCm": 180,
                        "waistCm": 90,
                        "neckCm": 40,
                        "weightKg": 80
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "bodyFatPercent": 18.46207892229652,
                            "fatMassKg": 14.769663137837217,
                            "leanMassKg": 65.23033686216279
                        }
                    },
                    "tolerance": 1e-9
                },
                {
                    "caseId": "missing-neck",
                    "scenario": "adversarial",
                    "input": {
                        "sex": "erkek",
                        "heightCm": 180,
                        "waistCm": 90
                    },
                    "expected": {
                        "ok": false,
                        "field": "neckCm",
                        "errorCode": "REQUIRED"
                    },
                    "tolerance": 1e-9
                }
            ]
        },
        "lastReviewed": "2026-08-25"
    },
    {
        "toolId": "stamp-tax",
        "slug": "damga-vergisi-hesaplama",
        "path": "/ticari-muhasebe/damga-vergisi-hesaplama",
        "category": {
            "id": "ticari-muhasebe",
            "label": "Ticari ve Muhasebe"
        },
        "classification": "remote-config",
        "engineId": "stamp-tax-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-08-25",
        "effectiveTo": null,
        "minAppVersion": "0.4.0",
        "updateCadence": {
            "mode": "scheduled-source-review",
            "days": 30,
            "reviewAfter": "2027-01-01",
            "specialMonths": [
                1,
                12
            ],
            "note": "Belirlenen tarihte resmî kaynak incelenir; değişiklik varsa config, yoksa inceleme tarihi yenilenir."
        },
        "sources": [
            {
                "sourceId": "tr_stamp_tax",
                "sourceGroupId": null,
                "title": "Damga vergisi oran, maktu tutar ve azami tutar tablosu",
                "publisher": "Gelir İdaresi Başkanlığı",
                "url": "https://cdn.gib.gov.tr/api/gibportal-file/file/getFileResources?objectKey=arsiv%2Fyardim-kaynaklar%2Fyararli-bilgiler%2Fvergi-beyannamelerinde-uygulanan-damga-vergisi-tutarlari.pdf",
                "kind": "official-government",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "nispi oranlar",
                    "maktu tutarlar",
                    "azami tutar"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": "contract",
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "contract",
                            "label": "Sözleşme (genel)"
                        },
                        {
                            "value": "rental-contract",
                            "label": "Kira sözleşmesi"
                        },
                        {
                            "value": "payroll",
                            "label": "Ücret / bordro"
                        },
                        {
                            "value": "public-tender-decision",
                            "label": "Resmî ihale kararı"
                        },
                        {
                            "value": "balance-sheet",
                            "label": "Bilanço"
                        },
                        {
                            "value": "income-statement-summary",
                            "label": "Gelir tablosu / işletme hesabı özeti"
                        },
                        {
                            "value": "income-tax-return",
                            "label": "Yıllık gelir vergisi beyannamesi"
                        },
                        {
                            "value": "corporate-tax-return",
                            "label": "Kurumlar vergisi beyannamesi"
                        },
                        {
                            "value": "vat-return",
                            "label": "KDV beyannamesi"
                        },
                        {
                            "value": "withholding-premium-return",
                            "label": "Muhtasar ve prim hizmet beyannamesi"
                        }
                    ],
                    "visibleWhen": null,
                    "id": "paperType",
                    "type": "select",
                    "label": "Kâğıt veya beyanname türü",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "TL",
                    "minimum": 0.01,
                    "maximum": 1000000000000,
                    "maxDecimalPlaces": 2,
                    "options": [],
                    "visibleWhen": null,
                    "id": "amount",
                    "type": "number",
                    "label": "Belge tutarı veya aylık kira/ücret",
                    "required": false
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "yıl",
                    "minimum": 1,
                    "maximum": 10,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": {
                        "fieldId": "paperType",
                        "equals": "rental-contract"
                    },
                    "id": "rentalYears",
                    "type": "number",
                    "label": "Kira süresi",
                    "required": false
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "minimum-wage-exempt",
                            "label": "Asgari ücret istisnası"
                        },
                        {
                            "value": "above-minimum-wage",
                            "label": "Asgari ücret üzeri"
                        }
                    ],
                    "visibleWhen": {
                        "fieldId": "paperType",
                        "equals": "payroll"
                    },
                    "id": "salaryScope",
                    "type": "radio",
                    "label": "Ücret damga vergisi kapsamı",
                    "required": false
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "paperLabel",
                    "type": "text",
                    "label": "Kâğıt türü",
                    "nullable": false,
                    "prominent": false,
                    "unit": null
                },
                {
                    "id": "taxableBase",
                    "type": "money",
                    "label": "Damga vergisi matrahı",
                    "nullable": true,
                    "prominent": false,
                    "unit": null
                },
                {
                    "id": "effectiveRatePercent",
                    "type": "percentage",
                    "label": "Uygulanan oran",
                    "nullable": true,
                    "prominent": false,
                    "unit": null
                },
                {
                    "id": "rateLabel",
                    "type": "text",
                    "label": "Oran / maktu tutar",
                    "nullable": false,
                    "prominent": false,
                    "unit": null
                },
                {
                    "id": "responsibleParty",
                    "type": "text",
                    "label": "Vergi sorumlusu",
                    "nullable": false,
                    "prominent": false,
                    "unit": null
                },
                {
                    "id": "declarationTiming",
                    "type": "text",
                    "label": "Beyan zamanı",
                    "nullable": false,
                    "prominent": false,
                    "unit": null
                },
                {
                    "id": "taxAmount",
                    "type": "money",
                    "label": "Hesaplanan damga vergisi",
                    "nullable": false,
                    "prominent": true,
                    "unit": null
                },
                {
                    "id": "capped",
                    "type": "boolean",
                    "label": "Azami tutar uygulandı mı?",
                    "nullable": false,
                    "prominent": false,
                    "unit": null
                },
                {
                    "id": "exemptionApplied",
                    "type": "boolean",
                    "label": "İstisna uygulandı mı?",
                    "nullable": false,
                    "prominent": false,
                    "unit": null
                }
            ]
        },
        "content": {
            "title": "Damga Vergisi Hesaplama",
            "shortTitle": "Damga Vergisi Hesaplama",
            "summary": "Seçilen kâğıdın 2026 nispi oranını veya maktu tutarını uygular; kira süresini matraha katar, istisnayı ve azami vergiyi ayrı gösterir.",
            "method": "Seçilen kâğıdın 2026 nispi oranını veya maktu tutarını uygular; kira süresini matraha katar, istisnayı ve azami vergiyi ayrı gösterir.",
            "limitations": [
                "Sözleşmenin hukuki niteliği, nüsha ve imza sayısı ile özel istisnalar ayrıca değerlendirilmelidir."
            ],
            "faqs": [
                {
                    "question": "Sonuç resmî tahakkuk veya mali müşavir görüşü müdür?",
                    "answer": "Hayır. Sonuç, seçilen kapsam ve tarihli resmî kaynak kaydıyla üretilen yardımcı bir hesaplamadır; beyan öncesi somut olay ayrıca incelenmelidir."
                }
            ],
            "sourcePanel": {
                "title": "Kaynak ve inceleme durumu",
                "summary": "Oran, eşik ve uygulama kuralları belirlenen aralıkta resmî kaynaktan yeniden incelenir. Değişiklik varsa config, yoksa inceleme tarihi yenilenir.",
                "sourceIds": [
                    "tr_stamp_tax"
                ],
                "notes": [
                    "Sözleşmenin hukuki niteliği, nüsha ve imza sayısı ile özel istisnalar ayrıca değerlendirilmelidir."
                ]
            }
        },
        "config": {
            "kind": "stamp-tax-v1",
            "calculatorId": "ticari-muhasebe.damga-vergisi-hesaplama",
            "amount": [
                0.01,
                1000000000000
            ],
            "rentalYears": [
                1,
                10
            ],
            "monthsPerYear": 12,
            "maximumTax": 29115961.1,
            "paperRules": {
                "contract": {
                    "label": "Sözleşme (genel)",
                    "basis": "proportional",
                    "rate": 0.00948,
                    "fixedAmount": null,
                    "responsibleParty": "Sözleşmenin tarafları",
                    "declarationTiming": "Belge düzenlendikten sonra"
                },
                "rental-contract": {
                    "label": "Kira sözleşmesi",
                    "basis": "proportional",
                    "rate": 0.00189,
                    "fixedAmount": null,
                    "responsibleParty": "Kiracı ve kiralayan",
                    "declarationTiming": "Sözleşme tarihinden itibaren"
                },
                "payroll": {
                    "label": "Ücret / bordro",
                    "basis": "proportional",
                    "rate": 0.00759,
                    "fixedAmount": null,
                    "responsibleParty": "İşveren",
                    "declarationTiming": "İzleyen beyan dönemi"
                },
                "public-tender-decision": {
                    "label": "Resmî ihale kararı",
                    "basis": "proportional",
                    "rate": 0.00569,
                    "fixedAmount": null,
                    "responsibleParty": "İhaleyi kazanan",
                    "declarationTiming": "Karar tebliğinden itibaren"
                },
                "balance-sheet": {
                    "label": "Bilanço",
                    "basis": "fixed",
                    "rate": null,
                    "fixedAmount": 616.3,
                    "responsibleParty": "Düzenleyen mükellef",
                    "declarationTiming": "İlgili beyannameyle"
                },
                "income-statement-summary": {
                    "label": "Gelir tablosu / işletme hesabı özeti",
                    "basis": "fixed",
                    "rate": null,
                    "fixedAmount": 294.2,
                    "responsibleParty": "Düzenleyen mükellef",
                    "declarationTiming": "İlgili beyannameyle"
                },
                "income-tax-return": {
                    "label": "Yıllık gelir vergisi beyannamesi",
                    "basis": "fixed",
                    "rate": null,
                    "fixedAmount": 1189.5,
                    "responsibleParty": "Beyannameyi veren",
                    "declarationTiming": "Beyanname sırasında"
                },
                "corporate-tax-return": {
                    "label": "Kurumlar vergisi beyannamesi",
                    "basis": "fixed",
                    "rate": null,
                    "fixedAmount": 1605.8,
                    "responsibleParty": "Beyannameyi veren",
                    "declarationTiming": "Beyanname sırasında"
                },
                "vat-return": {
                    "label": "KDV beyannamesi",
                    "basis": "fixed",
                    "rate": null,
                    "fixedAmount": 791,
                    "responsibleParty": "Beyannameyi veren",
                    "declarationTiming": "İzleyen beyan dönemi"
                },
                "withholding-premium-return": {
                    "label": "Muhtasar ve prim hizmet beyannamesi",
                    "basis": "fixed",
                    "rate": null,
                    "fixedAmount": 939.7,
                    "responsibleParty": "Beyannameyi veren",
                    "declarationTiming": "İzleyen beyan dönemi"
                }
            },
            "policyLifecycle": {
                "configVersion": "1.0.0",
                "sourceIds": [
                    "tr_stamp_tax"
                ],
                "reviewedOn": "2026-08-25",
                "effectiveFrom": "2026-08-25",
                "effectiveTo": null,
                "reviewAfter": "2027-01-01"
            }
        },
        "testVectors": {
            "kind": "commercial-oracle-v1",
            "cases": [
                {
                    "caseId": "contract-100k",
                    "scenario": "positive",
                    "input": {
                        "paperType": "contract",
                        "amount": 100000
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "taxableBase": 100000,
                            "effectiveRatePercent": 0.948,
                            "taxAmount": 948,
                            "capped": false,
                            "exemptionApplied": false
                        }
                    },
                    "tolerance": 1e-9
                },
                {
                    "caseId": "contract-missing-amount",
                    "scenario": "adversarial",
                    "input": {
                        "paperType": "contract"
                    },
                    "expected": {
                        "ok": false,
                        "field": "amount",
                        "errorCode": "REQUIRED"
                    },
                    "tolerance": 1e-9
                }
            ]
        },
        "lastReviewed": "2026-08-25"
    },
    {
        "toolId": "property-tax",
        "slug": "emlak-vergisi-hesaplama",
        "path": "/ticari-muhasebe/emlak-vergisi-hesaplama",
        "category": {
            "id": "ticari-muhasebe",
            "label": "Ticari ve Muhasebe"
        },
        "classification": "remote-config",
        "engineId": "property-tax-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-08-25",
        "effectiveTo": null,
        "minAppVersion": "0.4.0",
        "updateCadence": {
            "mode": "scheduled-source-review",
            "days": 30,
            "reviewAfter": "2027-01-01",
            "specialMonths": [],
            "note": "Belirlenen tarihte resmî kaynak incelenir; değişiklik varsa config, yoksa inceleme tarihi yenilenir."
        },
        "sources": [
            {
                "sourceId": "tr_property_tax",
                "sourceGroupId": null,
                "title": "Emlak vergisi oranları ve büyükşehir uygulaması",
                "publisher": "Gelir İdaresi Başkanlığı",
                "url": "https://gib.gov.tr/api/gibportal/vergiKonulari/template/listByContentTopicId?contentTopicId=6&preview=false",
                "kind": "official-government",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "konut",
                    "işyeri",
                    "arsa",
                    "arazi",
                    "büyükşehir oranı",
                    "indirimli vergi"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "TL",
                    "minimum": 0.01,
                    "maximum": 1000000000000,
                    "maxDecimalPlaces": 2,
                    "options": [],
                    "visibleWhen": null,
                    "id": "assessedValue",
                    "type": "number",
                    "label": "Emlak vergi değeri",
                    "required": true
                },
                {
                    "defaultValue": "residence",
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "residence",
                            "label": "Konut"
                        },
                        {
                            "value": "workplace",
                            "label": "İşyeri"
                        },
                        {
                            "value": "plot",
                            "label": "Arsa"
                        },
                        {
                            "value": "land",
                            "label": "Arazi"
                        }
                    ],
                    "visibleWhen": null,
                    "id": "propertyType",
                    "type": "radio",
                    "label": "Taşınmaz türü",
                    "required": true
                },
                {
                    "defaultValue": "normal",
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "normal",
                            "label": "Büyükşehir dışı"
                        },
                        {
                            "value": "metropolitan",
                            "label": "Büyükşehir"
                        }
                    ],
                    "visibleWhen": null,
                    "id": "municipalityType",
                    "type": "radio",
                    "label": "Belediye türü",
                    "required": true
                },
                {
                    "defaultValue": false,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [],
                    "visibleWhen": null,
                    "id": "zeroRateEligibilityConfirmed",
                    "type": "boolean",
                    "label": "Konut için sıfır oran koşullarını belediyeden teyit ettim",
                    "required": true
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "propertyLabel",
                    "type": "text",
                    "label": "Taşınmaz türü",
                    "nullable": false,
                    "prominent": false,
                    "unit": null
                },
                {
                    "id": "municipalityLabel",
                    "type": "text",
                    "label": "Belediye türü",
                    "nullable": false,
                    "prominent": false,
                    "unit": null
                },
                {
                    "id": "assessedValue",
                    "type": "money",
                    "label": "Vergi değeri",
                    "nullable": false,
                    "prominent": false,
                    "unit": null
                },
                {
                    "id": "effectiveRatePercent",
                    "type": "percentage",
                    "label": "Uygulanan oran",
                    "nullable": false,
                    "prominent": false,
                    "unit": null
                },
                {
                    "id": "annualTax",
                    "type": "money",
                    "label": "Yıllık emlak vergisi",
                    "nullable": false,
                    "prominent": true,
                    "unit": null
                },
                {
                    "id": "firstInstallment",
                    "type": "money",
                    "label": "Birinci taksit",
                    "nullable": false,
                    "prominent": false,
                    "unit": null
                },
                {
                    "id": "secondInstallment",
                    "type": "money",
                    "label": "İkinci taksit",
                    "nullable": false,
                    "prominent": false,
                    "unit": null
                },
                {
                    "id": "zeroRateApplied",
                    "type": "boolean",
                    "label": "Sıfır oran uygulandı mı?",
                    "nullable": false,
                    "prominent": false,
                    "unit": null
                },
                {
                    "id": "exemptionStatus",
                    "type": "text",
                    "label": "İndirimli oran açıklaması",
                    "nullable": false,
                    "prominent": false,
                    "unit": null
                }
            ]
        },
        "content": {
            "title": "Emlak Vergisi Hesaplama",
            "shortTitle": "Emlak Vergisi Hesaplama",
            "summary": "Vergi değerini taşınmaz ve belediye türüne ait oranla çarpar; yıllık tutarı iki eşit takside böler ve sıfır oranı yalnız açık konut teyidiyle uygular.",
            "method": "Vergi değerini taşınmaz ve belediye türüne ait oranla çarpar; yıllık tutarı iki eşit takside böler ve sıfır oranı yalnız açık konut teyidiyle uygular.",
            "limitations": [
                "Vergi değeri, muafiyet koşulları ve belediye uygulaması resmî kayıttan ayrıca doğrulanmalıdır."
            ],
            "faqs": [
                {
                    "question": "Sonuç resmî tahakkuk veya mali müşavir görüşü müdür?",
                    "answer": "Hayır. Sonuç, seçilen kapsam ve tarihli resmî kaynak kaydıyla üretilen yardımcı bir hesaplamadır; beyan öncesi somut olay ayrıca incelenmelidir."
                }
            ],
            "sourcePanel": {
                "title": "Kaynak ve inceleme durumu",
                "summary": "Oran, eşik ve uygulama kuralları belirlenen aralıkta resmî kaynaktan yeniden incelenir. Değişiklik varsa config, yoksa inceleme tarihi yenilenir.",
                "sourceIds": [
                    "tr_property_tax"
                ],
                "notes": [
                    "Vergi değeri, muafiyet koşulları ve belediye uygulaması resmî kayıttan ayrıca doğrulanmalıdır."
                ]
            }
        },
        "config": {
            "kind": "property-tax-v1",
            "calculatorId": "ticari-muhasebe.emlak-vergisi-hesaplama",
            "assessedValue": [
                0.01,
                1000000000000
            ],
            "rates": {
                "residence": {
                    "normal": 0.001,
                    "metropolitan": 0.002
                },
                "workplace": {
                    "normal": 0.002,
                    "metropolitan": 0.004
                },
                "plot": {
                    "normal": 0.003,
                    "metropolitan": 0.006
                },
                "land": {
                    "normal": 0.001,
                    "metropolitan": 0.002
                }
            },
            "policyLifecycle": {
                "configVersion": "1.0.0",
                "sourceIds": [
                    "tr_property_tax"
                ],
                "reviewedOn": "2026-08-25",
                "effectiveFrom": "2026-08-25",
                "effectiveTo": null,
                "reviewAfter": "2027-01-01"
            }
        },
        "testVectors": {
            "kind": "commercial-oracle-v1",
            "cases": [
                {
                    "caseId": "metropolitan-residence",
                    "scenario": "positive",
                    "input": {
                        "assessedValue": 1000000,
                        "propertyType": "residence",
                        "municipalityType": "metropolitan",
                        "zeroRateEligibilityConfirmed": false
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "effectiveRatePercent": 0.2,
                            "annualTax": 2000,
                            "firstInstallment": 1000,
                            "secondInstallment": 1000,
                            "zeroRateApplied": false
                        }
                    },
                    "tolerance": 1e-9
                },
                {
                    "caseId": "zero-assessed-value",
                    "scenario": "adversarial",
                    "input": {
                        "assessedValue": 0,
                        "propertyType": "residence",
                        "municipalityType": "normal",
                        "zeroRateEligibilityConfirmed": false
                    },
                    "expected": {
                        "ok": false,
                        "field": "assessedValue",
                        "errorCode": "OUT_OF_RANGE"
                    },
                    "tolerance": 1e-9
                }
            ]
        },
        "lastReviewed": "2026-08-25"
    },
    {
        "toolId": "advance-tax",
        "slug": "gecici-vergi-hesaplama",
        "path": "/ticari-muhasebe/gecici-vergi-hesaplama",
        "category": {
            "id": "ticari-muhasebe",
            "label": "Ticari ve Muhasebe"
        },
        "classification": "remote-config",
        "engineId": "advance-tax-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-08-25",
        "effectiveTo": null,
        "minAppVersion": "0.4.0",
        "updateCadence": {
            "mode": "scheduled-source-review",
            "days": 14,
            "reviewAfter": "2026-09-08",
            "specialMonths": [
                2,
                5,
                8,
                11
            ],
            "note": "Belirlenen tarihte resmî kaynak incelenir; değişiklik varsa config, yoksa inceleme tarihi yenilenir."
        },
        "sources": [
            {
                "sourceId": "tr_advance_tax",
                "sourceGroupId": null,
                "title": "Geçici vergi oranları ve dönemleri",
                "publisher": "Gelir İdaresi Başkanlığı",
                "url": "https://cdn.gib.gov.tr/api/gibportal-file/file/getFileResources?objectKey=arsiv%2Fyardim-kaynaklar%2Fyararli-bilgiler%2Fgecici-vergi-oranlari.pdf",
                "kind": "official-government",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "gelir vergisi oranı",
                    "kurum oranı",
                    "finans oranı",
                    "dönemler"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": "income",
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "income",
                            "label": "Gelir vergisi mükellefi"
                        },
                        {
                            "value": "corporate",
                            "label": "Genel kurum"
                        },
                        {
                            "value": "financial",
                            "label": "Banka / finans kurumu"
                        },
                        {
                            "value": "yid-koi",
                            "label": "3996 YİD / 6428 KÖİ proje kazancı"
                        }
                    ],
                    "visibleWhen": null,
                    "id": "taxpayerType",
                    "type": "select",
                    "label": "Mükellef türü",
                    "required": true
                },
                {
                    "defaultValue": 1,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": 1,
                            "label": "1. dönem"
                        },
                        {
                            "value": 2,
                            "label": "2. dönem"
                        },
                        {
                            "value": 3,
                            "label": "3. dönem"
                        },
                        {
                            "value": 4,
                            "label": "4. dönem"
                        }
                    ],
                    "visibleWhen": null,
                    "id": "period",
                    "type": "select",
                    "label": "Geçici vergi dönemi",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "TL",
                    "minimum": 0,
                    "maximum": 1000000000000,
                    "maxDecimalPlaces": 2,
                    "options": [],
                    "visibleWhen": null,
                    "id": "cumulativeTaxBase",
                    "type": "number",
                    "label": "Kümülatif geçici vergi matrahı",
                    "required": true
                },
                {
                    "defaultValue": 0,
                    "placeholder": null,
                    "unit": "TL",
                    "minimum": 0,
                    "maximum": 1000000000000,
                    "maxDecimalPlaces": 2,
                    "options": [],
                    "visibleWhen": null,
                    "id": "previousAdvanceTax",
                    "type": "number",
                    "label": "Önceki dönemlerde hesaplanan geçici vergi",
                    "required": true
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "taxpayerLabel",
                    "type": "text",
                    "label": "Mükellef türü",
                    "nullable": false,
                    "prominent": false,
                    "unit": null
                },
                {
                    "id": "periodLabel",
                    "type": "text",
                    "label": "Dönem",
                    "nullable": false,
                    "prominent": false,
                    "unit": null
                },
                {
                    "id": "ratePercent",
                    "type": "percentage",
                    "label": "Geçici vergi oranı",
                    "nullable": false,
                    "prominent": false,
                    "unit": null
                },
                {
                    "id": "cumulativeTax",
                    "type": "money",
                    "label": "Kümülatif vergi",
                    "nullable": false,
                    "prominent": false,
                    "unit": null
                },
                {
                    "id": "previousAdvanceTax",
                    "type": "money",
                    "label": "Önceki geçici vergi",
                    "nullable": false,
                    "prominent": false,
                    "unit": null
                },
                {
                    "id": "payableThisPeriod",
                    "type": "money",
                    "label": "Bu dönem ödenecek",
                    "nullable": false,
                    "prominent": true,
                    "unit": null
                },
                {
                    "id": "carryforwardCredit",
                    "type": "money",
                    "label": "Devreden mahsup",
                    "nullable": false,
                    "prominent": false,
                    "unit": null
                },
                {
                    "id": "dueDateLabel",
                    "type": "text",
                    "label": "Son ödeme tarihi",
                    "nullable": false,
                    "prominent": false,
                    "unit": null
                }
            ]
        },
        "content": {
            "title": "Geçici Vergi Hesaplama",
            "shortTitle": "Geçici Vergi Hesaplama",
            "summary": "Kümülatif matraha mükellef türünün oranını uygular, önceki geçici vergiyi mahsup eder ve dört dönemin son ödeme bilgisini gösterir.",
            "method": "Kümülatif matraha mükellef türünün oranını uygular, önceki geçici vergiyi mahsup eder ve dört dönemin son ödeme bilgisini gösterir.",
            "limitations": [
                "Mahsup, istisna ve beyanname uzatmaları mükellefin kendi kayıtları ve GİB duyurularıyla doğrulanmalıdır."
            ],
            "faqs": [
                {
                    "question": "Sonuç resmî tahakkuk veya mali müşavir görüşü müdür?",
                    "answer": "Hayır. Sonuç, seçilen kapsam ve tarihli resmî kaynak kaydıyla üretilen yardımcı bir hesaplamadır; beyan öncesi somut olay ayrıca incelenmelidir."
                }
            ],
            "sourcePanel": {
                "title": "Kaynak ve inceleme durumu",
                "summary": "Oran, eşik ve uygulama kuralları belirlenen aralıkta resmî kaynaktan yeniden incelenir. Değişiklik varsa config, yoksa inceleme tarihi yenilenir.",
                "sourceIds": [
                    "tr_advance_tax"
                ],
                "notes": [
                    "Mahsup, istisna ve beyanname uzatmaları mükellefin kendi kayıtları ve GİB duyurularıyla doğrulanmalıdır."
                ]
            }
        },
        "config": {
            "kind": "advance-tax-v1",
            "calculatorId": "ticari-muhasebe.gecici-vergi-hesaplama",
            "taxBase": [
                0,
                1000000000000
            ],
            "previousTax": [
                0,
                1000000000000
            ],
            "taxpayerRules": {
                "income": {
                    "label": "Gelir vergisi mükellefi",
                    "ratePercent": 15
                },
                "corporate": {
                    "label": "Genel kurum",
                    "ratePercent": 25
                },
                "financial": {
                    "label": "Banka / finans kurumu",
                    "ratePercent": 30
                },
                "yid-koi": {
                    "label": "3996 YİD / 6428 KÖİ proje kazancı",
                    "ratePercent": 30
                }
            },
            "periodRules": {
                "1": {
                    "label": "1. Dönem (Ocak-Mart)",
                    "dueDateLabel": "18 Mayıs 2026"
                },
                "2": {
                    "label": "2. Dönem (Ocak-Haziran)",
                    "dueDateLabel": "17 Ağustos 2026"
                },
                "3": {
                    "label": "3. Dönem (Ocak-Eylül)",
                    "dueDateLabel": "17 Kasım 2026"
                },
                "4": {
                    "label": "4. Dönem (Ocak-Aralık)",
                    "dueDateLabel": "17 Şubat 2027"
                }
            },
            "policyLifecycle": {
                "configVersion": "1.0.0",
                "sourceIds": [
                    "tr_advance_tax"
                ],
                "reviewedOn": "2026-08-25",
                "effectiveFrom": "2026-08-25",
                "effectiveTo": null,
                "reviewAfter": "2026-09-08"
            }
        },
        "testVectors": {
            "kind": "commercial-oracle-v1",
            "cases": [
                {
                    "caseId": "q4-yid-koi-30",
                    "scenario": "boundary",
                    "input": {
                        "taxpayerType": "yid-koi",
                        "period": 4,
                        "cumulativeTaxBase": 1000000,
                        "previousAdvanceTax": 100000
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "ratePercent": 30,
                            "cumulativeTax": 300000,
                            "payableThisPeriod": 200000,
                            "dueDateLabel": "17 Şubat 2027"
                        }
                    },
                    "tolerance": 1e-9
                },
                {
                    "caseId": "invalid-period",
                    "scenario": "adversarial",
                    "input": {
                        "taxpayerType": "corporate",
                        "period": 5,
                        "cumulativeTaxBase": 1000000,
                        "previousAdvanceTax": 0
                    },
                    "expected": {
                        "ok": false,
                        "field": "period",
                        "errorCode": "UNSUPPORTED_VALUE"
                    },
                    "tolerance": 1e-9
                }
            ]
        },
        "lastReviewed": "2026-08-25"
    },
    {
        "toolId": "late-legal-interest",
        "slug": "gecikme-zammi-yasal-faiz-hesaplama",
        "path": "/ticari-muhasebe/gecikme-zammi-yasal-faiz-hesaplama",
        "category": {
            "id": "ticari-muhasebe",
            "label": "Ticari ve Muhasebe"
        },
        "classification": "remote-config",
        "engineId": "late-legal-interest-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-08-25",
        "effectiveTo": null,
        "minAppVersion": "0.4.0",
        "updateCadence": {
            "mode": "scheduled-source-review",
            "days": 7,
            "reviewAfter": "2026-09-01",
            "specialMonths": [
                1,
                7,
                12
            ],
            "note": "Belirlenen tarihte resmî kaynak incelenir; değişiklik varsa config, yoksa inceleme tarihi yenilenir."
        },
        "sources": [
            {
                "sourceId": "tr_late_interest_rates_gib",
                "sourceGroupId": "tr_late_interest_rates",
                "title": "Gecikme zammı ve kanuni faiz yürürlük kaydı",
                "publisher": "Gelir İdaresi Başkanlığı",
                "url": "https://gib.gov.tr/api/gibportal/mevzuat/cbk/findById?id=405",
                "kind": "official-government",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "aylık gecikme zammı",
                    "kanuni faiz oranı",
                    "yürürlük tarihleri"
                ]
            },
            {
                "sourceId": "tr_late_interest_rates_tcmb",
                "sourceGroupId": "tr_late_interest_rates",
                "title": "Reeskont ve avans faiz oranları",
                "publisher": "Türkiye Cumhuriyet Merkez Bankası",
                "url": "https://www.tcmb.gov.tr/wps/wcm/connect/TR/TCMB%2BTR/Main%2BMenu/Temel%2BFaaliyetler/Para%2BPolitikasi/Reeskont%2Bve%2BAvans%2BFaiz%2BOranlari?v=1.0.8",
                "kind": "official-government",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "reeskont oranı",
                    "avans faizi",
                    "yürürlük tarihleri"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "TL",
                    "minimum": 0.01,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 2,
                    "options": [],
                    "visibleWhen": null,
                    "id": "principal",
                    "type": "number",
                    "label": "Ana para",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [],
                    "visibleWhen": null,
                    "id": "dueDate",
                    "type": "date",
                    "label": "Vade tarihi",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [],
                    "visibleWhen": null,
                    "id": "paymentDate",
                    "type": "date",
                    "label": "Ödeme tarihi",
                    "required": true
                },
                {
                    "defaultValue": "legal",
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "late-charge",
                            "label": "Kamu alacağı gecikme zammı"
                        },
                        {
                            "value": "legal",
                            "label": "Kanuni faiz"
                        },
                        {
                            "value": "advance",
                            "label": "Ticari avans faizi"
                        }
                    ],
                    "visibleWhen": null,
                    "id": "interestType",
                    "type": "select",
                    "label": "Faiz türü",
                    "required": true
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "interestTypeLabel",
                    "type": "text",
                    "label": "Faiz türü",
                    "nullable": false,
                    "prominent": false,
                    "unit": null
                },
                {
                    "id": "principal",
                    "type": "money",
                    "label": "Ana para",
                    "nullable": false,
                    "prominent": false,
                    "unit": null
                },
                {
                    "id": "elapsedDays",
                    "type": "number",
                    "label": "Geçen gün",
                    "nullable": false,
                    "prominent": false,
                    "unit": "gün"
                },
                {
                    "id": "fullMonths",
                    "type": "number",
                    "label": "Tam ay",
                    "nullable": false,
                    "prominent": false,
                    "unit": null
                },
                {
                    "id": "remainingDays",
                    "type": "number",
                    "label": "Artan gün",
                    "nullable": false,
                    "prominent": false,
                    "unit": "gün"
                },
                {
                    "id": "rateLabel",
                    "type": "text",
                    "label": "Uygulanan oran dilimleri",
                    "nullable": false,
                    "prominent": false,
                    "unit": null
                },
                {
                    "id": "interestAmount",
                    "type": "money",
                    "label": "Faiz / gecikme zammı",
                    "nullable": false,
                    "prominent": true,
                    "unit": null
                },
                {
                    "id": "totalPayable",
                    "type": "money",
                    "label": "Toplam ödeme",
                    "nullable": false,
                    "prominent": true,
                    "unit": null
                }
            ]
        },
        "content": {
            "title": "Gecikme Zammı, Kanuni Faiz ve Avans Faizi",
            "shortTitle": "Gecikme Zammı, Kanuni Faiz ve Avans Faizi",
            "summary": "Vade gününü hariç, ödeme gününü dahil sayar; kanuni faizi 30 Temmuz 2026 dahil yüzde 24 ve 31 Temmuz itibarıyla yüzde 31 dilimlerine böler.",
            "method": "Vade gününü hariç, ödeme gününü dahil sayar; kanuni faizi 30 Temmuz 2026 dahil yüzde 24 ve 31 Temmuz itibarıyla yüzde 31 dilimlerine böler.",
            "limitations": [
                "Temerrüt türü, sözleşme hükmü, mahkeme kararı ve özel mevzuat uygulanacak faiz türünü değiştirebilir."
            ],
            "faqs": [
                {
                    "question": "Sonuç resmî tahakkuk veya mali müşavir görüşü müdür?",
                    "answer": "Hayır. Sonuç, seçilen kapsam ve tarihli resmî kaynak kaydıyla üretilen yardımcı bir hesaplamadır; beyan öncesi somut olay ayrıca incelenmelidir."
                }
            ],
            "sourcePanel": {
                "title": "Kaynak ve inceleme durumu",
                "summary": "Oran, eşik ve uygulama kuralları belirlenen aralıkta resmî kaynaktan yeniden incelenir. Değişiklik varsa config, yoksa inceleme tarihi yenilenir.",
                "sourceIds": [
                    "tr_late_interest_rates_gib",
                    "tr_late_interest_rates_tcmb"
                ],
                "notes": [
                    "Temerrüt türü, sözleşme hükmü, mahkeme kararı ve özel mevzuat uygulanacak faiz türünü değiştirebilir."
                ]
            }
        },
        "config": {
            "kind": "late-legal-interest-v1",
            "calculatorId": "ticari-muhasebe.gecikme-zammi-yasal-faiz-hesaplama",
            "principal": [
                0.01,
                1000000000
            ],
            "supportedDateRange": [
                "2024-06-01",
                "2027-12-31"
            ],
            "daysPerYear": 365,
            "daysPerMonth": 30,
            "lateChargeMonthlyRatePercent": 3.7,
            "advanceAnnualRatePercent": 39.75,
            "legalRateSchedule": [
                {
                    "effectiveFrom": "2024-06-01",
                    "effectiveTo": "2026-07-30",
                    "annualRatePercent": 24
                },
                {
                    "effectiveFrom": "2026-07-31",
                    "effectiveTo": null,
                    "annualRatePercent": 31
                }
            ],
            "policyLifecycle": {
                "configVersion": "1.0.0",
                "sourceIds": [
                    "tr_late_interest_rates_gib",
                    "tr_late_interest_rates_tcmb"
                ],
                "reviewedOn": "2026-08-25",
                "effectiveFrom": "2026-08-25",
                "effectiveTo": null,
                "reviewAfter": "2026-09-01"
            }
        },
        "testVectors": {
            "kind": "commercial-oracle-v1",
            "cases": [
                {
                    "caseId": "legal-july-split",
                    "scenario": "boundary",
                    "input": {
                        "principal": 100000,
                        "dueDate": "2026-07-29",
                        "paymentDate": "2026-07-31",
                        "interestType": "legal"
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "elapsedDays": 2,
                            "interestAmount": 150.6849315068493,
                            "totalPayable": 100150.68493150685
                        }
                    },
                    "tolerance": 1e-9
                },
                {
                    "caseId": "payment-before-due",
                    "scenario": "adversarial",
                    "input": {
                        "principal": 100000,
                        "dueDate": "2026-08-02",
                        "paymentDate": "2026-08-01",
                        "interestType": "legal"
                    },
                    "expected": {
                        "ok": false,
                        "field": "paymentDate",
                        "errorCode": "OUT_OF_RANGE"
                    },
                    "tolerance": 1e-9
                }
            ]
        },
        "lastReviewed": "2026-08-25"
    },
    {
        "toolId": "employer-cost",
        "slug": "isveren-maliyeti-hesaplama",
        "path": "/ticari-muhasebe/isveren-maliyeti-hesaplama",
        "category": {
            "id": "ticari-muhasebe",
            "label": "Ticari ve Muhasebe"
        },
        "classification": "remote-config",
        "engineId": "employer-cost-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-08-25",
        "effectiveTo": null,
        "minAppVersion": "0.4.0",
        "updateCadence": {
            "mode": "scheduled-source-review",
            "days": 7,
            "reviewAfter": "2027-01-01",
            "specialMonths": [
                1,
                7,
                12
            ],
            "note": "Belirlenen tarihte resmî kaynak incelenir; değişiklik varsa config, yoksa inceleme tarihi yenilenir."
        },
        "sources": [
            {
                "sourceId": "tr_labor_sgk_annual_pek",
                "sourceGroupId": "tr_labor_sgk_annual",
                "title": "2026 prime esas kazanç alt ve üst sınırları",
                "publisher": "Sosyal Güvenlik Kurumu",
                "url": "https://www.sgk.gov.tr/Content/Post/2e0c9e1a-2cfe-4456-af10-49d3de0c58ba/Prime-Esas-Kazanc-Miktarlari-2026-01-14-10-35-39",
                "kind": "official-government",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "günlük PEK tabanı",
                    "günlük PEK tavanı"
                ]
            },
            {
                "sourceId": "tr_labor_sgk_annual_employer_rates",
                "sourceGroupId": "tr_labor_sgk_annual",
                "title": "2026 işveren prim oranları",
                "publisher": "Sosyal Güvenlik Kurumu",
                "url": "https://www.sgk.gov.tr/Content/Post/c7812ea8-5087-413f-aeb5-d3c1d153e11a/Isveren-Prim-Oranlari-2026-01-13-04-52-38",
                "kind": "official-government",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "işveren sigorta primi",
                    "işveren işsizlik primi"
                ]
            },
            {
                "sourceId": "tr_labor_sgk_annual_discounts",
                "sourceGroupId": "tr_labor_sgk_annual",
                "title": "Güncel sigorta primi teşvik, destek ve indirimleri",
                "publisher": "Sosyal Güvenlik Kurumu",
                "url": "https://sgk.gov.tr/Content/Post/c52f99f0-da6c-4029-9b9a-a0d6f96a6a42/Guncel-Sigorta-Primi-Tesvik-Destek-ve-Indirimleri-2026-08-04-10-29-55",
                "kind": "official-government",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "genel indirim",
                    "imalat indirimi",
                    "uygunluk koşulları"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "TL",
                    "minimum": 0.01,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 2,
                    "options": [],
                    "visibleWhen": null,
                    "id": "grossPeriodWage",
                    "type": "number",
                    "label": "Dönem brüt ücreti",
                    "required": true
                },
                {
                    "defaultValue": 30,
                    "placeholder": null,
                    "unit": "gün",
                    "minimum": 1,
                    "maximum": 30,
                    "maxDecimalPlaces": 0,
                    "options": [],
                    "visibleWhen": null,
                    "id": "workDays",
                    "type": "number",
                    "label": "Prim günü",
                    "required": true
                },
                {
                    "defaultValue": "general",
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "general",
                            "label": "Genel özel sektör — 2 puan"
                        },
                        {
                            "value": "manufacturing",
                            "label": "İmalat — 5 puan"
                        },
                        {
                            "value": "none",
                            "label": "İndirimsiz"
                        }
                    ],
                    "visibleWhen": null,
                    "id": "discountMode",
                    "type": "radio",
                    "label": "İşveren prim indirimi",
                    "required": true
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "discountLabel",
                    "type": "text",
                    "label": "İndirim",
                    "nullable": false,
                    "prominent": false,
                    "unit": null
                },
                {
                    "id": "workDays",
                    "type": "number",
                    "label": "Prim günü",
                    "nullable": false,
                    "prominent": false,
                    "unit": "gün"
                },
                {
                    "id": "grossPeriodWage",
                    "type": "money",
                    "label": "Brüt dönem ücreti",
                    "nullable": false,
                    "prominent": false,
                    "unit": null
                },
                {
                    "id": "minimumInsurableEarnings",
                    "type": "money",
                    "label": "Dönemlik PEK tabanı",
                    "nullable": false,
                    "prominent": false,
                    "unit": null
                },
                {
                    "id": "maximumInsurableEarnings",
                    "type": "money",
                    "label": "Dönemlik PEK tavanı",
                    "nullable": false,
                    "prominent": false,
                    "unit": null
                },
                {
                    "id": "insurableEarnings",
                    "type": "money",
                    "label": "Prime esas kazanç",
                    "nullable": false,
                    "prominent": false,
                    "unit": null
                },
                {
                    "id": "effectiveEmployerSocialSecurityRatePercent",
                    "type": "percentage",
                    "label": "İşveren SGK oranı",
                    "nullable": false,
                    "prominent": false,
                    "unit": null
                },
                {
                    "id": "employerSocialSecurityAmount",
                    "type": "money",
                    "label": "İşveren SGK primi",
                    "nullable": false,
                    "prominent": false,
                    "unit": null
                },
                {
                    "id": "employerUnemploymentAmount",
                    "type": "money",
                    "label": "İşveren işsizlik primi",
                    "nullable": false,
                    "prominent": false,
                    "unit": null
                },
                {
                    "id": "totalPeriodCost",
                    "type": "money",
                    "label": "Toplam dönem maliyeti",
                    "nullable": false,
                    "prominent": true,
                    "unit": null
                },
                {
                    "id": "annualProjection",
                    "type": "money",
                    "label": "360 günlük projeksiyon",
                    "nullable": false,
                    "prominent": false,
                    "unit": null
                }
            ]
        },
        "content": {
            "title": "İşveren Maliyeti Hesaplama",
            "shortTitle": "İşveren Maliyeti Hesaplama",
            "summary": "Brüt dönem ücretini prim günüyle oluşan günlük PEK taban ve tavanına sınırlar; seçilen işveren indirimini ve işsizlik primini ekler.",
            "method": "Brüt dönem ücretini prim günüyle oluşan günlük PEK taban ve tavanına sınırlar; seçilen işveren indirimini ve işsizlik primini ekler.",
            "limitations": [
                "Teşvik uygunluğu, işkolu, çalışan statüsü ve bordro kalemleri gerçek işveren maliyetini değiştirebilir."
            ],
            "faqs": [
                {
                    "question": "Sonuç resmî tahakkuk veya mali müşavir görüşü müdür?",
                    "answer": "Hayır. Sonuç, seçilen kapsam ve tarihli resmî kaynak kaydıyla üretilen yardımcı bir hesaplamadır; beyan öncesi somut olay ayrıca incelenmelidir."
                }
            ],
            "sourcePanel": {
                "title": "Kaynak ve inceleme durumu",
                "summary": "Oran, eşik ve uygulama kuralları belirlenen aralıkta resmî kaynaktan yeniden incelenir. Değişiklik varsa config, yoksa inceleme tarihi yenilenir.",
                "sourceIds": [
                    "tr_labor_sgk_annual_pek",
                    "tr_labor_sgk_annual_employer_rates",
                    "tr_labor_sgk_annual_discounts"
                ],
                "notes": [
                    "Teşvik uygunluğu, işkolu, çalışan statüsü ve bordro kalemleri gerçek işveren maliyetini değiştirebilir."
                ]
            }
        },
        "config": {
            "kind": "employer-cost-v1",
            "calculatorId": "ticari-muhasebe.isveren-maliyeti-hesaplama",
            "grossWage": [
                0.01,
                1000000000
            ],
            "workDays": [
                1,
                30
            ],
            "dailyMinimumInsurableEarnings": 1101,
            "dailyMaximumInsurableEarnings": 9909,
            "employerSocialSecurityRate": 0.2175,
            "employerUnemploymentRate": 0.02,
            "discountPoints": {
                "general": 0.02,
                "manufacturing": 0.05,
                "none": 0
            },
            "annualProjectionDays": 360,
            "policyLifecycle": {
                "configVersion": "1.0.0",
                "sourceIds": [
                    "tr_labor_sgk_annual_pek",
                    "tr_labor_sgk_annual_employer_rates",
                    "tr_labor_sgk_annual_discounts"
                ],
                "reviewedOn": "2026-08-25",
                "effectiveFrom": "2026-08-25",
                "effectiveTo": null,
                "reviewAfter": "2027-01-01"
            }
        },
        "testVectors": {
            "kind": "commercial-oracle-v1",
            "cases": [
                {
                    "caseId": "daily-pek-15-days",
                    "scenario": "boundary",
                    "input": {
                        "grossPeriodWage": 16515,
                        "workDays": 15,
                        "discountMode": "general"
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "minimumInsurableEarnings": 16515,
                            "maximumInsurableEarnings": 148635,
                            "insurableEarnings": 16515,
                            "totalPeriodCost": 20107.0125
                        }
                    },
                    "tolerance": 1e-9
                },
                {
                    "caseId": "zero-work-days",
                    "scenario": "adversarial",
                    "input": {
                        "grossPeriodWage": 16515,
                        "workDays": 0,
                        "discountMode": "general"
                    },
                    "expected": {
                        "ok": false,
                        "field": "workDays",
                        "errorCode": "OUT_OF_RANGE"
                    },
                    "tolerance": 1e-9
                }
            ]
        },
        "lastReviewed": "2026-08-25"
    },
    {
        "toolId": "vat",
        "slug": "kdv-hesaplama",
        "path": "/ticari-muhasebe/kdv-hesaplama",
        "category": {
            "id": "ticari-muhasebe",
            "label": "Ticari ve Muhasebe"
        },
        "classification": "remote-config",
        "engineId": "vat-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-08-25",
        "effectiveTo": null,
        "minAppVersion": "0.4.0",
        "updateCadence": {
            "mode": "scheduled-source-review",
            "days": 7,
            "reviewAfter": "2026-09-01",
            "specialMonths": [],
            "note": "Belirlenen tarihte resmî kaynak incelenir; değişiklik varsa config, yoksa inceleme tarihi yenilenir."
        },
        "sources": [
            {
                "sourceId": "tr_vat_rates",
                "sourceGroupId": null,
                "title": "KDV oranları ve mal/hizmet kapsamı",
                "publisher": "Gelir İdaresi Başkanlığı",
                "url": "https://cdn.gib.gov.tr/api/gibportal-file/file/getFileResources?objectKey=arsiv%2Fyardim-kaynaklar%2Fyararli-bilgiler%2Fkdv-oranlari.pdf",
                "kind": "official-government",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "KDV oranları",
                    "mal ve hizmet listeleri"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "TL",
                    "minimum": 0.01,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 2,
                    "options": [],
                    "visibleWhen": null,
                    "id": "amount",
                    "type": "number",
                    "label": "Tutar",
                    "required": true
                },
                {
                    "defaultValue": 20,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": 1,
                            "label": "%1"
                        },
                        {
                            "value": 10,
                            "label": "%10"
                        },
                        {
                            "value": 20,
                            "label": "%20"
                        }
                    ],
                    "visibleWhen": null,
                    "id": "vatRatePercent",
                    "type": "select",
                    "label": "KDV oranı",
                    "required": true
                },
                {
                    "defaultValue": "excluding-vat",
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "excluding-vat",
                            "label": "KDV hariç"
                        },
                        {
                            "value": "including-vat",
                            "label": "KDV dahil"
                        }
                    ],
                    "visibleWhen": null,
                    "id": "direction",
                    "type": "radio",
                    "label": "Hesap yönü",
                    "required": true
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "directionLabel",
                    "type": "text",
                    "label": "Hesap yönü",
                    "nullable": false,
                    "prominent": false,
                    "unit": null
                },
                {
                    "id": "vatRatePercent",
                    "type": "percentage",
                    "label": "KDV oranı",
                    "nullable": false,
                    "prominent": false,
                    "unit": null
                },
                {
                    "id": "netAmount",
                    "type": "money",
                    "label": "KDV hariç tutar",
                    "nullable": false,
                    "prominent": false,
                    "unit": null
                },
                {
                    "id": "vatAmount",
                    "type": "money",
                    "label": "KDV",
                    "nullable": false,
                    "prominent": true,
                    "unit": null
                },
                {
                    "id": "totalAmount",
                    "type": "money",
                    "label": "KDV dahil toplam",
                    "nullable": false,
                    "prominent": true,
                    "unit": null
                }
            ]
        },
        "content": {
            "title": "KDV Hesaplama",
            "shortTitle": "KDV Hesaplama",
            "summary": "KDV hariç tutara seçilen oranı ekler veya KDV dahil toplamı aynı oranla net tutar ve vergi payına ayırır.",
            "method": "KDV hariç tutara seçilen oranı ekler veya KDV dahil toplamı aynı oranla net tutar ve vergi payına ayırır.",
            "limitations": [
                "Doğru oran, teslim veya hizmetin mevzuattaki kapsamına göre kullanıcı tarafından seçilmelidir."
            ],
            "faqs": [
                {
                    "question": "Sonuç resmî tahakkuk veya mali müşavir görüşü müdür?",
                    "answer": "Hayır. Sonuç, seçilen kapsam ve tarihli resmî kaynak kaydıyla üretilen yardımcı bir hesaplamadır; beyan öncesi somut olay ayrıca incelenmelidir."
                }
            ],
            "sourcePanel": {
                "title": "Kaynak ve inceleme durumu",
                "summary": "Oran, eşik ve uygulama kuralları belirlenen aralıkta resmî kaynaktan yeniden incelenir. Değişiklik varsa config, yoksa inceleme tarihi yenilenir.",
                "sourceIds": [
                    "tr_vat_rates"
                ],
                "notes": [
                    "Doğru oran, teslim veya hizmetin mevzuattaki kapsamına göre kullanıcı tarafından seçilmelidir."
                ]
            }
        },
        "config": {
            "kind": "vat-v1",
            "calculatorId": "ticari-muhasebe.kdv-hesaplama",
            "amount": [
                0.01,
                1000000000
            ],
            "allowedRatesPercent": [
                1,
                10,
                20
            ],
            "policyLifecycle": {
                "configVersion": "1.0.0",
                "sourceIds": [
                    "tr_vat_rates"
                ],
                "reviewedOn": "2026-08-25",
                "effectiveFrom": "2026-08-25",
                "effectiveTo": null,
                "reviewAfter": "2026-09-01"
            }
        },
        "testVectors": {
            "kind": "commercial-oracle-v1",
            "cases": [
                {
                    "caseId": "excluding-vat-20",
                    "scenario": "positive",
                    "input": {
                        "amount": 1000,
                        "vatRatePercent": 20,
                        "direction": "excluding-vat"
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "netAmount": 1000,
                            "vatAmount": 200,
                            "totalAmount": 1200
                        }
                    },
                    "tolerance": 1e-9
                },
                {
                    "caseId": "unsupported-rate",
                    "scenario": "adversarial",
                    "input": {
                        "amount": 1000,
                        "vatRatePercent": 18,
                        "direction": "excluding-vat"
                    },
                    "expected": {
                        "ok": false,
                        "field": "vatRatePercent",
                        "errorCode": "UNSUPPORTED_VALUE"
                    },
                    "tolerance": 1e-9
                }
            ]
        },
        "lastReviewed": "2026-08-25"
    },
    {
        "toolId": "vat-withholding",
        "slug": "kdv-tevkifati-hesaplama",
        "path": "/ticari-muhasebe/kdv-tevkifati-hesaplama",
        "category": {
            "id": "ticari-muhasebe",
            "label": "Ticari ve Muhasebe"
        },
        "classification": "remote-config",
        "engineId": "vat-withholding-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-08-25",
        "effectiveTo": null,
        "minAppVersion": "0.4.0",
        "updateCadence": {
            "mode": "scheduled-source-review",
            "days": 7,
            "reviewAfter": "2026-10-01",
            "specialMonths": [],
            "note": "Belirlenen tarihte resmî kaynak incelenir; değişiklik varsa config, yoksa inceleme tarihi yenilenir."
        },
        "sources": [
            {
                "sourceId": "tr_vat_rates",
                "sourceGroupId": null,
                "title": "KDV oranları ve mal/hizmet kapsamı",
                "publisher": "Gelir İdaresi Başkanlığı",
                "url": "https://cdn.gib.gov.tr/api/gibportal-file/file/getFileResources?objectKey=arsiv%2Fyardim-kaynaklar%2Fyararli-bilgiler%2Fkdv-oranlari.pdf",
                "kind": "official-government",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "KDV oranları",
                    "mal ve hizmet listeleri"
                ]
            },
            {
                "sourceId": "tr_vat_withholding_communique",
                "sourceGroupId": "tr_vat_withholding",
                "title": "KDV Genel Uygulama Tebliği",
                "publisher": "Gelir İdaresi Başkanlığı",
                "url": "https://cdn.gib.gov.tr/api/gibportal-file/file/getFile?objectKey=MEVZUAT_TEBLIGLER%2FUNIVERSAL%2F2026%2Fkdv_genteb.pdf",
                "kind": "official-government",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "fatura alt sınırı",
                    "işlem kapsamı",
                    "belirlenmiş alıcı"
                ]
            },
            {
                "sourceId": "tr_vat_withholding_rates",
                "sourceGroupId": "tr_vat_withholding",
                "title": "Kısmi KDV tevkifatı oranları",
                "publisher": "Gelir İdaresi Başkanlığı",
                "url": "https://cdn.gib.gov.tr/api/gibportal-file/file/getFileResources?objectKey=arsiv%2Fyardim-kaynaklar%2Fyararli-bilgiler%2Fkdvde-kismi-tevkifat-uygulamasinda-tevkifat-oranlari.pdf",
                "kind": "official-government",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "tevkifat oranları",
                    "işlem türleri"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "TL",
                    "minimum": 0.01,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 2,
                    "options": [],
                    "visibleWhen": null,
                    "id": "amount",
                    "type": "number",
                    "label": "Fatura tutarı",
                    "required": true
                },
                {
                    "defaultValue": 20,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": 1,
                            "label": "%1"
                        },
                        {
                            "value": 10,
                            "label": "%10"
                        },
                        {
                            "value": 20,
                            "label": "%20"
                        }
                    ],
                    "visibleWhen": null,
                    "id": "vatRatePercent",
                    "type": "select",
                    "label": "KDV oranı",
                    "required": true
                },
                {
                    "defaultValue": "5/10",
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "2/10",
                            "label": "2/10"
                        },
                        {
                            "value": "3/10",
                            "label": "3/10"
                        },
                        {
                            "value": "4/10",
                            "label": "4/10"
                        },
                        {
                            "value": "5/10",
                            "label": "5/10"
                        },
                        {
                            "value": "6/10",
                            "label": "6/10"
                        },
                        {
                            "value": "7/10",
                            "label": "7/10"
                        },
                        {
                            "value": "8/10",
                            "label": "8/10"
                        },
                        {
                            "value": "9/10",
                            "label": "9/10"
                        }
                    ],
                    "visibleWhen": null,
                    "id": "withholdingRatio",
                    "type": "select",
                    "label": "Tevkifat oranı",
                    "required": true
                },
                {
                    "defaultValue": "including-vat",
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "excluding-vat",
                            "label": "KDV hariç"
                        },
                        {
                            "value": "including-vat",
                            "label": "KDV dahil"
                        }
                    ],
                    "visibleWhen": null,
                    "id": "direction",
                    "type": "radio",
                    "label": "Tutar türü",
                    "required": true
                },
                {
                    "defaultValue": false,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [],
                    "visibleWhen": null,
                    "id": "transactionInScopeConfirmed",
                    "type": "boolean",
                    "label": "İşlemin kısmi tevkifat kapsamında olduğunu teyit ettim",
                    "required": true
                },
                {
                    "defaultValue": false,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [],
                    "visibleWhen": null,
                    "id": "eligibleBuyerConfirmed",
                    "type": "boolean",
                    "label": "Alıcının tevkifat yapacak sıfatta olduğunu teyit ettim",
                    "required": true
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "directionLabel",
                    "type": "text",
                    "label": "Tutar türü",
                    "nullable": false,
                    "prominent": false,
                    "unit": null
                },
                {
                    "id": "vatRatePercent",
                    "type": "percentage",
                    "label": "KDV oranı",
                    "nullable": false,
                    "prominent": false,
                    "unit": null
                },
                {
                    "id": "selectedWithholdingRatioLabel",
                    "type": "text",
                    "label": "Seçilen tevkifat oranı",
                    "nullable": false,
                    "prominent": false,
                    "unit": null
                },
                {
                    "id": "appliedWithholdingRatioLabel",
                    "type": "text",
                    "label": "Uygulanan tevkifat",
                    "nullable": false,
                    "prominent": false,
                    "unit": null
                },
                {
                    "id": "thresholdExceeded",
                    "type": "boolean",
                    "label": "12.000 TL sınırı aşıldı mı?",
                    "nullable": false,
                    "prominent": false,
                    "unit": null
                },
                {
                    "id": "netAmount",
                    "type": "money",
                    "label": "KDV hariç tutar",
                    "nullable": false,
                    "prominent": false,
                    "unit": null
                },
                {
                    "id": "vatAmount",
                    "type": "money",
                    "label": "Hesaplanan KDV",
                    "nullable": false,
                    "prominent": false,
                    "unit": null
                },
                {
                    "id": "withheldVatAmount",
                    "type": "money",
                    "label": "Tevkif edilen KDV",
                    "nullable": false,
                    "prominent": true,
                    "unit": null
                },
                {
                    "id": "remainingVatAmount",
                    "type": "money",
                    "label": "Satıcıya kalan KDV",
                    "nullable": false,
                    "prominent": false,
                    "unit": null
                },
                {
                    "id": "collectionAmount",
                    "type": "money",
                    "label": "Satıcının tahsil edeceği",
                    "nullable": false,
                    "prominent": false,
                    "unit": null
                }
            ]
        },
        "content": {
            "title": "KDV Tevkifatı Hesaplama",
            "shortTitle": "KDV Tevkifatı Hesaplama",
            "summary": "KDV dahil toplam 12.000 TL'yi kesin olarak aştığında ve iki hukuki ön koşul açıkça onaylandığında seçilen kesri hesaplanan KDV'ye uygular.",
            "method": "KDV dahil toplam 12.000 TL'yi kesin olarak aştığında ve iki hukuki ön koşul açıkça onaylandığında seçilen kesri hesaplanan KDV'ye uygular.",
            "limitations": [
                "İşlem kapsamı ile belirlenmiş alıcı sıfatı kullanıcı tarafından resmî tebliğe göre doğrulanmadan hesap yapılmaz."
            ],
            "faqs": [
                {
                    "question": "Sonuç resmî tahakkuk veya mali müşavir görüşü müdür?",
                    "answer": "Hayır. Sonuç, seçilen kapsam ve tarihli resmî kaynak kaydıyla üretilen yardımcı bir hesaplamadır; beyan öncesi somut olay ayrıca incelenmelidir."
                }
            ],
            "sourcePanel": {
                "title": "Kaynak ve inceleme durumu",
                "summary": "Oran, eşik ve uygulama kuralları belirlenen aralıkta resmî kaynaktan yeniden incelenir. Değişiklik varsa config, yoksa inceleme tarihi yenilenir.",
                "sourceIds": [
                    "tr_vat_rates",
                    "tr_vat_withholding_communique",
                    "tr_vat_withholding_rates"
                ],
                "notes": [
                    "İşlem kapsamı ile belirlenmiş alıcı sıfatı kullanıcı tarafından resmî tebliğe göre doğrulanmadan hesap yapılmaz."
                ]
            }
        },
        "config": {
            "kind": "vat-withholding-v1",
            "calculatorId": "ticari-muhasebe.kdv-tevkifati-hesaplama",
            "amount": [
                0.01,
                1000000000
            ],
            "allowedVatRatesPercent": [
                1,
                10,
                20
            ],
            "withholdingRatios": {
                "2/10": 0.2,
                "3/10": 0.3,
                "4/10": 0.4,
                "5/10": 0.5,
                "6/10": 0.6,
                "7/10": 0.7,
                "8/10": 0.8,
                "9/10": 0.9
            },
            "invoiceThresholdIncludingVat": 12000,
            "policyLifecycle": {
                "configVersion": "1.0.0",
                "sourceIds": [
                    "tr_vat_rates",
                    "tr_vat_withholding_communique",
                    "tr_vat_withholding_rates"
                ],
                "reviewedOn": "2026-08-25",
                "effectiveFrom": "2026-08-25",
                "effectiveTo": null,
                "reviewAfter": "2026-10-01"
            }
        },
        "testVectors": {
            "kind": "commercial-oracle-v1",
            "cases": [
                {
                    "caseId": "threshold-at-no-withholding",
                    "scenario": "threshold",
                    "input": {
                        "amount": 12000,
                        "vatRatePercent": 20,
                        "withholdingRatio": "5/10",
                        "direction": "including-vat",
                        "transactionInScopeConfirmed": true,
                        "eligibleBuyerConfirmed": true
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "thresholdExceeded": false,
                            "netAmount": 10000,
                            "vatAmount": 2000,
                            "withheldVatAmount": 0,
                            "collectionAmount": 12000
                        }
                    },
                    "tolerance": 1e-9
                },
                {
                    "caseId": "threshold-above-withholding",
                    "scenario": "boundary",
                    "input": {
                        "amount": 12000.01,
                        "vatRatePercent": 20,
                        "withholdingRatio": "5/10",
                        "direction": "including-vat",
                        "transactionInScopeConfirmed": true,
                        "eligibleBuyerConfirmed": true
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "thresholdExceeded": true,
                            "withheldVatAmount": 1000.0008333333335,
                            "collectionAmount": 11000.009166666667
                        }
                    },
                    "tolerance": 1e-9
                },
                {
                    "caseId": "scope-not-confirmed",
                    "scenario": "adversarial",
                    "input": {
                        "amount": 20000,
                        "vatRatePercent": 20,
                        "withholdingRatio": "5/10",
                        "direction": "including-vat",
                        "transactionInScopeConfirmed": false,
                        "eligibleBuyerConfirmed": true
                    },
                    "expected": {
                        "ok": false,
                        "field": "transactionInScopeConfirmed",
                        "errorCode": "UNSUPPORTED_VALUE"
                    },
                    "tolerance": 1e-9
                }
            ]
        },
        "lastReviewed": "2026-08-25"
    },
    {
        "toolId": "corporate-tax",
        "slug": "kurumlar-vergisi-hesaplama",
        "path": "/ticari-muhasebe/kurumlar-vergisi-hesaplama",
        "category": {
            "id": "ticari-muhasebe",
            "label": "Ticari ve Muhasebe"
        },
        "classification": "remote-config",
        "engineId": "corporate-tax-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-08-25",
        "effectiveTo": null,
        "minAppVersion": "0.4.0",
        "updateCadence": {
            "mode": "scheduled-source-review",
            "days": 14,
            "reviewAfter": "2027-01-01",
            "specialMonths": [
                1,
                4,
                12
            ],
            "note": "Belirlenen tarihte resmî kaynak incelenir; değişiklik varsa config, yoksa inceleme tarihi yenilenir."
        },
        "sources": [
            {
                "sourceId": "tr_corporate_tax_rates",
                "sourceGroupId": "tr_corporate_tax",
                "title": "2026 kurumlar vergisi oranları",
                "publisher": "Gelir İdaresi Başkanlığı",
                "url": "https://cdn.gib.gov.tr/api/gibportal-file/file/getFile?objectKey=DUYURU%2FUNIVERSAL%2F2026%2F2026_KurumlarVergisiOran.pdf",
                "kind": "official-government",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "genel oran",
                    "finans oranı",
                    "3996",
                    "6428",
                    "indirimli oranlar"
                ]
            },
            {
                "sourceId": "tr_corporate_tax_domestic_minimum",
                "sourceGroupId": "tr_corporate_tax",
                "title": "2026 yurt içi asgari kurumlar vergisi rehberi",
                "publisher": "Gelir İdaresi Başkanlığı",
                "url": "https://cdn.gib.gov.tr/api/gibportal-file/file/getFile?objectKey=DUYURU%2FUNIVERSAL%2F2026%2F2026_YurtIci_Asgari_KurumlarVergisiRehberi.pdf",
                "kind": "official-government",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "asgari kurumlar vergisi",
                    "düzeltilmiş matrah",
                    "ilk üç hesap dönemi"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": "general",
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "general",
                            "label": "Genel kurum"
                        },
                        {
                            "value": "financial",
                            "label": "Banka / finans kurumu"
                        },
                        {
                            "value": "yid-3996",
                            "label": "3996 kapsamı YİD proje şirketi"
                        },
                        {
                            "value": "koi-6428",
                            "label": "6428 kapsamı KÖİ proje şirketi"
                        }
                    ],
                    "visibleWhen": null,
                    "id": "institutionType",
                    "type": "select",
                    "label": "Kurum türü",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "TL",
                    "minimum": 0.01,
                    "maximum": 1000000000000,
                    "maxDecimalPlaces": 2,
                    "options": [],
                    "visibleWhen": null,
                    "id": "taxableProfit",
                    "type": "number",
                    "label": "Vergiye tabi kurum kazancı",
                    "required": true
                },
                {
                    "defaultValue": "none",
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "none",
                            "label": "Standart oran"
                        },
                        {
                            "value": "exports",
                            "label": "İhracat −5 puan"
                        },
                        {
                            "value": "manufacturing",
                            "label": "Üretim −1 puan"
                        },
                        {
                            "value": "public-offering",
                            "label": "Halka arz −2 puan"
                        },
                        {
                            "value": "public-offering-exports",
                            "label": "Halka arz + ihracat −7 puan"
                        },
                        {
                            "value": "public-offering-manufacturing",
                            "label": "Halka arz + üretim −3 puan"
                        }
                    ],
                    "visibleWhen": null,
                    "id": "discountType",
                    "type": "select",
                    "label": "İndirimli oran kapsamı",
                    "required": true
                },
                {
                    "defaultValue": "not-calculated",
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "not-calculated",
                            "label": "Düzeltilmiş matrah girilmeyecek"
                        },
                        {
                            "value": "adjusted-base",
                            "label": "Düzeltilmiş matrahla karşılaştır"
                        },
                        {
                            "value": "first-three-periods",
                            "label": "İlk üç hesap dönemi"
                        }
                    ],
                    "visibleWhen": null,
                    "id": "minimumTaxStatus",
                    "type": "select",
                    "label": "Yurt içi asgari kurumlar vergisi",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "TL",
                    "minimum": 0.01,
                    "maximum": 1000000000000,
                    "maxDecimalPlaces": 2,
                    "options": [],
                    "visibleWhen": {
                        "fieldId": "minimumTaxStatus",
                        "equals": "adjusted-base"
                    },
                    "id": "adjustedMinimumTaxBase",
                    "type": "number",
                    "label": "KVK 32/C düzeltilmiş matrah",
                    "required": false
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "institutionLabel",
                    "type": "text",
                    "label": "Kurum türü",
                    "nullable": false,
                    "prominent": false,
                    "unit": null
                },
                {
                    "id": "discountLabel",
                    "type": "text",
                    "label": "İndirim",
                    "nullable": false,
                    "prominent": false,
                    "unit": null
                },
                {
                    "id": "minimumTaxStatusLabel",
                    "type": "text",
                    "label": "Asgari vergi durumu",
                    "nullable": false,
                    "prominent": false,
                    "unit": null
                },
                {
                    "id": "effectiveRatePercent",
                    "type": "percentage",
                    "label": "Etkin kurumlar vergisi oranı",
                    "nullable": false,
                    "prominent": false,
                    "unit": null
                },
                {
                    "id": "calculatedCorporateTax",
                    "type": "money",
                    "label": "Hesaplanan kurumlar vergisi",
                    "nullable": false,
                    "prominent": false,
                    "unit": null
                },
                {
                    "id": "domesticMinimumTax",
                    "type": "money",
                    "label": "Yurt içi asgari kurumlar vergisi",
                    "nullable": false,
                    "prominent": false,
                    "unit": null
                },
                {
                    "id": "payableCorporateTax",
                    "type": "money",
                    "label": "Ödenecek kurumlar vergisi",
                    "nullable": false,
                    "prominent": true,
                    "unit": null
                },
                {
                    "id": "afterTaxProfit",
                    "type": "money",
                    "label": "Vergi sonrası kazanç",
                    "nullable": false,
                    "prominent": false,
                    "unit": null
                },
                {
                    "id": "minimumTaxOutcome",
                    "type": "text",
                    "label": "Asgari vergi sonucu",
                    "nullable": false,
                    "prominent": false,
                    "unit": null
                }
            ]
        },
        "content": {
            "title": "Kurumlar Vergisi Hesaplama",
            "shortTitle": "Kurumlar Vergisi Hesaplama",
            "summary": "Kurum türünün yüzde 25 veya yüzde 30 oranını uygular; izinli indirim puanlarını ve seçildiğinde yüzde 10 yurt içi asgari vergi karşılaştırmasını ayrı hesaplar.",
            "method": "Kurum türünün yüzde 25 veya yüzde 30 oranını uygular; izinli indirim puanlarını ve seçildiğinde yüzde 10 yurt içi asgari vergi karşılaştırmasını ayrı hesaplar.",
            "limitations": [
                "İstisna ve indirim matrahlarının KVK kapsamı ile ilk üç hesap dönemi durumu ayrıca doğrulanmalıdır."
            ],
            "faqs": [
                {
                    "question": "Sonuç resmî tahakkuk veya mali müşavir görüşü müdür?",
                    "answer": "Hayır. Sonuç, seçilen kapsam ve tarihli resmî kaynak kaydıyla üretilen yardımcı bir hesaplamadır; beyan öncesi somut olay ayrıca incelenmelidir."
                }
            ],
            "sourcePanel": {
                "title": "Kaynak ve inceleme durumu",
                "summary": "Oran, eşik ve uygulama kuralları belirlenen aralıkta resmî kaynaktan yeniden incelenir. Değişiklik varsa config, yoksa inceleme tarihi yenilenir.",
                "sourceIds": [
                    "tr_corporate_tax_rates",
                    "tr_corporate_tax_domestic_minimum"
                ],
                "notes": [
                    "İstisna ve indirim matrahlarının KVK kapsamı ile ilk üç hesap dönemi durumu ayrıca doğrulanmalıdır."
                ]
            }
        },
        "config": {
            "kind": "corporate-tax-v1",
            "calculatorId": "ticari-muhasebe.kurumlar-vergisi-hesaplama",
            "taxableProfit": [
                0.01,
                1000000000000
            ],
            "adjustedMinimumTaxBase": [
                0.01,
                1000000000000
            ],
            "institutionRules": {
                "general": {
                    "label": "Genel kurum",
                    "baseRatePercent": 25,
                    "permitsDiscount": true
                },
                "financial": {
                    "label": "Banka / finans kurumu",
                    "baseRatePercent": 30,
                    "permitsDiscount": false
                },
                "yid-3996": {
                    "label": "3996 kapsamı YİD proje şirketi",
                    "baseRatePercent": 30,
                    "permitsDiscount": false
                },
                "koi-6428": {
                    "label": "6428 kapsamı KÖİ proje şirketi",
                    "baseRatePercent": 30,
                    "permitsDiscount": false
                }
            },
            "discountRules": {
                "none": {
                    "label": "Standart oran",
                    "points": 0
                },
                "exports": {
                    "label": "İhracat −5 puan",
                    "points": 5
                },
                "manufacturing": {
                    "label": "Üretim −1 puan",
                    "points": 1
                },
                "public-offering": {
                    "label": "Halka arz −2 puan",
                    "points": 2
                },
                "public-offering-exports": {
                    "label": "Halka arz + ihracat −7 puan",
                    "points": 7
                },
                "public-offering-manufacturing": {
                    "label": "Halka arz + üretim −3 puan",
                    "points": 3
                }
            },
            "domesticMinimumTaxRatePercent": 10,
            "policyLifecycle": {
                "configVersion": "1.0.0",
                "sourceIds": [
                    "tr_corporate_tax_rates",
                    "tr_corporate_tax_domestic_minimum"
                ],
                "reviewedOn": "2026-08-25",
                "effectiveFrom": "2026-08-25",
                "effectiveTo": null,
                "reviewAfter": "2027-01-01"
            }
        },
        "testVectors": {
            "kind": "commercial-oracle-v1",
            "cases": [
                {
                    "caseId": "general-25",
                    "scenario": "positive",
                    "input": {
                        "institutionType": "general",
                        "taxableProfit": 1000000,
                        "discountType": "none",
                        "minimumTaxStatus": "not-calculated"
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "effectiveRatePercent": 25,
                            "calculatedCorporateTax": 250000,
                            "payableCorporateTax": 250000
                        }
                    },
                    "tolerance": 1e-9
                },
                {
                    "caseId": "financial-30",
                    "scenario": "boundary",
                    "input": {
                        "institutionType": "financial",
                        "taxableProfit": 1000000,
                        "discountType": "none",
                        "minimumTaxStatus": "not-calculated"
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "effectiveRatePercent": 30,
                            "payableCorporateTax": 300000
                        }
                    },
                    "tolerance": 1e-9
                },
                {
                    "caseId": "yid-3996-30",
                    "scenario": "boundary",
                    "input": {
                        "institutionType": "yid-3996",
                        "taxableProfit": 1000000,
                        "discountType": "none",
                        "minimumTaxStatus": "not-calculated"
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "effectiveRatePercent": 30,
                            "payableCorporateTax": 300000
                        }
                    },
                    "tolerance": 1e-9
                },
                {
                    "caseId": "koi-6428-30",
                    "scenario": "boundary",
                    "input": {
                        "institutionType": "koi-6428",
                        "taxableProfit": 1000000,
                        "discountType": "none",
                        "minimumTaxStatus": "not-calculated"
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "effectiveRatePercent": 30,
                            "payableCorporateTax": 300000
                        }
                    },
                    "tolerance": 1e-9
                },
                {
                    "caseId": "financial-discount",
                    "scenario": "adversarial",
                    "input": {
                        "institutionType": "financial",
                        "taxableProfit": 1000000,
                        "discountType": "exports",
                        "minimumTaxStatus": "not-calculated"
                    },
                    "expected": {
                        "ok": false,
                        "field": "discountType",
                        "errorCode": "UNSUPPORTED_VALUE"
                    },
                    "tolerance": 1e-9
                }
            ]
        },
        "lastReviewed": "2026-08-25"
    },
    {
        "toolId": "self-employment-receipt",
        "slug": "serbest-meslek-makbuzu-hesaplama",
        "path": "/ticari-muhasebe/serbest-meslek-makbuzu-hesaplama",
        "category": {
            "id": "ticari-muhasebe",
            "label": "Ticari ve Muhasebe"
        },
        "classification": "remote-config",
        "engineId": "self-employment-receipt-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-08-25",
        "effectiveTo": null,
        "minAppVersion": "0.4.0",
        "updateCadence": {
            "mode": "scheduled-source-review",
            "days": 14,
            "reviewAfter": "2026-10-01",
            "specialMonths": [],
            "note": "Belirlenen tarihte resmî kaynak incelenir; değişiklik varsa config, yoksa inceleme tarihi yenilenir."
        },
        "sources": [
            {
                "sourceId": "tr_withholding_general_rates",
                "sourceGroupId": "tr_withholding_general",
                "title": "Gelir Vergisi Kanunu 94. madde kesinti oranları",
                "publisher": "Gelir İdaresi Başkanlığı",
                "url": "https://cdn.gib.gov.tr/api/gibportal-file/file/getFileResources?objectKey=arsiv%2Fyardim-kaynaklar%2Fyararli-bilgiler%2Fgelir-vergisi-kanununun-94uncu-maddesinde-yer-alan-kesinti-oranlari.pdf",
                "kind": "official-government",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "işyeri kira stopajı",
                    "serbest meslek stopajı",
                    "telif",
                    "kâr payı"
                ]
            },
            {
                "sourceId": "tr_withholding_general_self_employment",
                "sourceGroupId": "tr_withholding_general",
                "title": "2026 serbest meslek kazancı vergi rehberi",
                "publisher": "Gelir İdaresi Başkanlığı",
                "url": "https://cdn.gib.gov.tr/api/gibportal-file/file/getFile?objectKey=DUYURU%2FUNIVERSAL%2F2026%2F2026_Serbest_Meslek_Kazanci.pdf",
                "kind": "official-government",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "serbest meslek makbuzu",
                    "stopaj",
                    "KDV"
                ]
            },
            {
                "sourceId": "tr_vat_rates",
                "sourceGroupId": null,
                "title": "KDV oranları ve mal/hizmet kapsamı",
                "publisher": "Gelir İdaresi Başkanlığı",
                "url": "https://cdn.gib.gov.tr/api/gibportal-file/file/getFileResources?objectKey=arsiv%2Fyardim-kaynaklar%2Fyararli-bilgiler%2Fkdv-oranlari.pdf",
                "kind": "official-government",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "KDV oranları",
                    "mal ve hizmet listeleri"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": "gross-to-net",
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "gross-to-net",
                            "label": "Brütten nete"
                        },
                        {
                            "value": "net-to-gross",
                            "label": "Netten brüte"
                        }
                    ],
                    "visibleWhen": null,
                    "id": "direction",
                    "type": "radio",
                    "label": "Hesap yönü",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "TL",
                    "minimum": 0.01,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 2,
                    "options": [],
                    "visibleWhen": null,
                    "id": "amount",
                    "type": "number",
                    "label": "Brüt veya net ücret",
                    "required": true
                },
                {
                    "defaultValue": 20,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": 0,
                            "label": "%0"
                        },
                        {
                            "value": 17,
                            "label": "%17"
                        },
                        {
                            "value": 20,
                            "label": "%20"
                        }
                    ],
                    "visibleWhen": null,
                    "id": "withholdingRatePercent",
                    "type": "select",
                    "label": "Gelir vergisi stopaj oranı",
                    "required": true
                },
                {
                    "defaultValue": 20,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": 0,
                            "label": "%0"
                        },
                        {
                            "value": 10,
                            "label": "%10"
                        },
                        {
                            "value": 20,
                            "label": "%20"
                        }
                    ],
                    "visibleWhen": null,
                    "id": "vatRatePercent",
                    "type": "select",
                    "label": "KDV oranı",
                    "required": true
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "directionLabel",
                    "type": "text",
                    "label": "Hesap yönü",
                    "nullable": false,
                    "prominent": false,
                    "unit": null
                },
                {
                    "id": "withholdingRatePercent",
                    "type": "percentage",
                    "label": "Stopaj oranı",
                    "nullable": false,
                    "prominent": false,
                    "unit": null
                },
                {
                    "id": "vatRatePercent",
                    "type": "percentage",
                    "label": "KDV oranı",
                    "nullable": false,
                    "prominent": false,
                    "unit": null
                },
                {
                    "id": "grossAmount",
                    "type": "money",
                    "label": "Brüt meslek ücreti",
                    "nullable": false,
                    "prominent": false,
                    "unit": null
                },
                {
                    "id": "withholdingAmount",
                    "type": "money",
                    "label": "Gelir vergisi stopajı",
                    "nullable": false,
                    "prominent": false,
                    "unit": null
                },
                {
                    "id": "netAmount",
                    "type": "money",
                    "label": "Net meslek ücreti",
                    "nullable": false,
                    "prominent": false,
                    "unit": null
                },
                {
                    "id": "vatAmount",
                    "type": "money",
                    "label": "KDV",
                    "nullable": false,
                    "prominent": false,
                    "unit": null
                },
                {
                    "id": "collectionAmount",
                    "type": "money",
                    "label": "Tahsil edilecek",
                    "nullable": false,
                    "prominent": true,
                    "unit": null
                }
            ]
        },
        "content": {
            "title": "Serbest Meslek Makbuzu Hesaplama",
            "shortTitle": "Serbest Meslek Makbuzu Hesaplama",
            "summary": "Brüt veya net serbest meslek ücretini seçilen stopaj oranıyla dönüştürür; KDV'yi brüt ücret üzerinden ayrıca hesaplayıp tahsilatı gösterir.",
            "method": "Brüt veya net serbest meslek ücretini seçilen stopaj oranıyla dönüştürür; KDV'yi brüt ücret üzerinden ayrıca hesaplayıp tahsilatı gösterir.",
            "limitations": [
                "Stopaj sorumlusu, KDV istisnası ve özel mesleki durum somut işlem için ayrıca doğrulanmalıdır."
            ],
            "faqs": [
                {
                    "question": "Sonuç resmî tahakkuk veya mali müşavir görüşü müdür?",
                    "answer": "Hayır. Sonuç, seçilen kapsam ve tarihli resmî kaynak kaydıyla üretilen yardımcı bir hesaplamadır; beyan öncesi somut olay ayrıca incelenmelidir."
                }
            ],
            "sourcePanel": {
                "title": "Kaynak ve inceleme durumu",
                "summary": "Oran, eşik ve uygulama kuralları belirlenen aralıkta resmî kaynaktan yeniden incelenir. Değişiklik varsa config, yoksa inceleme tarihi yenilenir.",
                "sourceIds": [
                    "tr_withholding_general_rates",
                    "tr_withholding_general_self_employment",
                    "tr_vat_rates"
                ],
                "notes": [
                    "Stopaj sorumlusu, KDV istisnası ve özel mesleki durum somut işlem için ayrıca doğrulanmalıdır."
                ]
            }
        },
        "config": {
            "kind": "self-employment-receipt-v1",
            "calculatorId": "ticari-muhasebe.serbest-meslek-makbuzu-hesaplama",
            "amount": [
                0.01,
                1000000000
            ],
            "allowedWithholdingRatesPercent": [
                0,
                17,
                20
            ],
            "allowedVatRatesPercent": [
                0,
                10,
                20
            ],
            "policyLifecycle": {
                "configVersion": "1.0.0",
                "sourceIds": [
                    "tr_withholding_general_rates",
                    "tr_withholding_general_self_employment",
                    "tr_vat_rates"
                ],
                "reviewedOn": "2026-08-25",
                "effectiveFrom": "2026-08-25",
                "effectiveTo": null,
                "reviewAfter": "2026-10-01"
            }
        },
        "testVectors": {
            "kind": "commercial-oracle-v1",
            "cases": [
                {
                    "caseId": "gross-receipt-20",
                    "scenario": "positive",
                    "input": {
                        "direction": "gross-to-net",
                        "amount": 10000,
                        "withholdingRatePercent": 20,
                        "vatRatePercent": 20
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "grossAmount": 10000,
                            "withholdingAmount": 2000,
                            "netAmount": 8000,
                            "vatAmount": 2000,
                            "collectionAmount": 10000
                        }
                    },
                    "tolerance": 1e-9
                },
                {
                    "caseId": "unsupported-withholding",
                    "scenario": "adversarial",
                    "input": {
                        "direction": "gross-to-net",
                        "amount": 10000,
                        "withholdingRatePercent": 100,
                        "vatRatePercent": 20
                    },
                    "expected": {
                        "ok": false,
                        "field": "withholdingRatePercent",
                        "errorCode": "UNSUPPORTED_VALUE"
                    },
                    "tolerance": 1e-9
                }
            ]
        },
        "lastReviewed": "2026-08-25"
    },
    {
        "toolId": "withholding-tax",
        "slug": "stopaj-hesaplama",
        "path": "/ticari-muhasebe/stopaj-hesaplama",
        "category": {
            "id": "ticari-muhasebe",
            "label": "Ticari ve Muhasebe"
        },
        "classification": "remote-config",
        "engineId": "withholding-tax-v1",
        "schemaVersion": "1.0.0",
        "contentVersion": "1.0.0",
        "configVersion": "1.0.0",
        "effectiveFrom": "2026-08-25",
        "effectiveTo": null,
        "minAppVersion": "0.4.0",
        "updateCadence": {
            "mode": "scheduled-source-review",
            "days": 7,
            "reviewAfter": "2026-10-01",
            "specialMonths": [],
            "note": "Belirlenen tarihte resmî kaynak incelenir; değişiklik varsa config, yoksa inceleme tarihi yenilenir."
        },
        "sources": [
            {
                "sourceId": "tr_withholding_general_rates",
                "sourceGroupId": "tr_withholding_general",
                "title": "Gelir Vergisi Kanunu 94. madde kesinti oranları",
                "publisher": "Gelir İdaresi Başkanlığı",
                "url": "https://cdn.gib.gov.tr/api/gibportal-file/file/getFileResources?objectKey=arsiv%2Fyardim-kaynaklar%2Fyararli-bilgiler%2Fgelir-vergisi-kanununun-94uncu-maddesinde-yer-alan-kesinti-oranlari.pdf",
                "kind": "official-government",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "işyeri kira stopajı",
                    "serbest meslek stopajı",
                    "telif",
                    "kâr payı"
                ]
            },
            {
                "sourceId": "tr_deposit_withholding",
                "sourceGroupId": null,
                "title": "Mevduat, repo ve menkul gelir stopaj rejimi",
                "publisher": "Gelir İdaresi Başkanlığı",
                "url": "https://cdn.gib.gov.tr/api/gibportal-file/file/getFile?objectKey=DUYURU%2FUNIVERSAL%2F2026%2F2026_Gecici67.pdf",
                "kind": "official-government",
                "reviewedOn": "2026-08-25",
                "watchFields": [
                    "TL mevduat vade oranları",
                    "döviz stopajı",
                    "repo",
                    "Eurobond"
                ]
            }
        ],
        "inputSchema": {
            "type": "object",
            "fields": [
                {
                    "defaultValue": "rent",
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "rent",
                            "label": "İşyeri veya konut kirası"
                        },
                        {
                            "value": "self-employment",
                            "label": "Serbest meslek ödemesi"
                        },
                        {
                            "value": "copyright",
                            "label": "Telif hakkı ödemesi"
                        },
                        {
                            "value": "tl-deposit",
                            "label": "TL mevduat faizi"
                        },
                        {
                            "value": "foreign-currency-deposit",
                            "label": "Döviz mevduat faizi"
                        },
                        {
                            "value": "dividend",
                            "label": "Kâr payı / temettü"
                        },
                        {
                            "value": "repo",
                            "label": "Repo geliri"
                        },
                        {
                            "value": "eurobond",
                            "label": "Hazine Eurobond faizi"
                        }
                    ],
                    "visibleWhen": null,
                    "id": "incomeType",
                    "type": "select",
                    "label": "Gelir / ödeme türü",
                    "required": true
                },
                {
                    "defaultValue": "gross-to-net",
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "gross-to-net",
                            "label": "Brütten nete"
                        },
                        {
                            "value": "net-to-gross",
                            "label": "Netten brüte"
                        }
                    ],
                    "visibleWhen": null,
                    "id": "direction",
                    "type": "radio",
                    "label": "Hesap yönü",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": "TL",
                    "minimum": 0.01,
                    "maximum": 1000000000,
                    "maxDecimalPlaces": 2,
                    "options": [],
                    "visibleWhen": null,
                    "id": "amount",
                    "type": "number",
                    "label": "Brüt veya net tutar",
                    "required": true
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "applies",
                            "label": "Tevkifat sorumluluğu var"
                        },
                        {
                            "value": "does-not-apply",
                            "label": "Tevkifat sorumluluğu yok"
                        }
                    ],
                    "visibleWhen": {
                        "fieldId": "incomeType",
                        "equals": "rent"
                    },
                    "id": "withholdingResponsibility",
                    "type": "radio",
                    "label": "Kira stopaj sorumluluğu",
                    "required": false
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "monthly",
                            "label": "Aylık"
                        },
                        {
                            "value": "annual",
                            "label": "Yıllık"
                        }
                    ],
                    "visibleWhen": {
                        "fieldId": "incomeType",
                        "equals": "rent"
                    },
                    "id": "rentPeriod",
                    "type": "radio",
                    "label": "Kira tutarının dönemi",
                    "required": false
                },
                {
                    "defaultValue": null,
                    "placeholder": null,
                    "unit": null,
                    "minimum": null,
                    "maximum": null,
                    "maxDecimalPlaces": null,
                    "options": [
                        {
                            "value": "up-to-six-months",
                            "label": "6 aya kadar"
                        },
                        {
                            "value": "six-to-twelve-months",
                            "label": "6 aydan uzun, 1 yıla kadar"
                        },
                        {
                            "value": "over-one-year",
                            "label": "1 yıldan uzun"
                        }
                    ],
                    "visibleWhen": {
                        "fieldId": "incomeType",
                        "equals": "tl-deposit"
                    },
                    "id": "tlDepositTerm",
                    "type": "radio",
                    "label": "TL mevduat vadesi",
                    "required": false
                }
            ]
        },
        "resultSchema": {
            "type": "object",
            "fields": [
                {
                    "id": "incomeLabel",
                    "type": "text",
                    "label": "Gelir türü",
                    "nullable": false,
                    "prominent": false,
                    "unit": null
                },
                {
                    "id": "directionLabel",
                    "type": "text",
                    "label": "Hesap yönü",
                    "nullable": false,
                    "prominent": false,
                    "unit": null
                },
                {
                    "id": "ratePercent",
                    "type": "percentage",
                    "label": "Stopaj oranı",
                    "nullable": false,
                    "prominent": false,
                    "unit": null
                },
                {
                    "id": "rateLabel",
                    "type": "text",
                    "label": "Oran",
                    "nullable": false,
                    "prominent": false,
                    "unit": null
                },
                {
                    "id": "grossAmount",
                    "type": "money",
                    "label": "Brüt tutar",
                    "nullable": false,
                    "prominent": false,
                    "unit": null
                },
                {
                    "id": "withholdingAmount",
                    "type": "money",
                    "label": "Stopaj",
                    "nullable": false,
                    "prominent": true,
                    "unit": null
                },
                {
                    "id": "netAmount",
                    "type": "money",
                    "label": "Net tutar",
                    "nullable": false,
                    "prominent": false,
                    "unit": null
                },
                {
                    "id": "declarationLabel",
                    "type": "text",
                    "label": "Beyan / kesinti sorumlusu",
                    "nullable": false,
                    "prominent": false,
                    "unit": null
                },
                {
                    "id": "monthlyGrossAmount",
                    "type": "money",
                    "label": "Aylık brüt kira",
                    "nullable": true,
                    "prominent": false,
                    "unit": null
                },
                {
                    "id": "monthlyWithholdingAmount",
                    "type": "money",
                    "label": "Aylık kira stopajı",
                    "nullable": true,
                    "prominent": false,
                    "unit": null
                },
                {
                    "id": "monthlyNetAmount",
                    "type": "money",
                    "label": "Aylık net kira",
                    "nullable": true,
                    "prominent": false,
                    "unit": null
                },
                {
                    "id": "annualGrossAmount",
                    "type": "money",
                    "label": "Yıllık brüt kira",
                    "nullable": true,
                    "prominent": false,
                    "unit": null
                },
                {
                    "id": "annualWithholdingAmount",
                    "type": "money",
                    "label": "Yıllık kira stopajı",
                    "nullable": true,
                    "prominent": false,
                    "unit": null
                },
                {
                    "id": "annualNetAmount",
                    "type": "money",
                    "label": "Yıllık net kira",
                    "nullable": true,
                    "prominent": false,
                    "unit": null
                }
            ]
        },
        "content": {
            "title": "Stopaj Hesaplama",
            "shortTitle": "Stopaj Hesaplama",
            "summary": "Gelir türünün tarihli stopaj oranını brüt veya net akışa uygular; kira için sorumluluk ve dönem, TL mevduat için vade açıkça seçilmeden hesap yapmaz.",
            "method": "Gelir türünün tarihli stopaj oranını brüt veya net akışa uygular; kira için sorumluluk ve dönem, TL mevduat için vade açıkça seçilmeden hesap yapmaz.",
            "limitations": [
                "Beyan sınırı, gelir sahibinin statüsü, anlaşmalar ve geçici kararların yürürlük tarihi ayrıca incelenmelidir."
            ],
            "faqs": [
                {
                    "question": "Sonuç resmî tahakkuk veya mali müşavir görüşü müdür?",
                    "answer": "Hayır. Sonuç, seçilen kapsam ve tarihli resmî kaynak kaydıyla üretilen yardımcı bir hesaplamadır; beyan öncesi somut olay ayrıca incelenmelidir."
                }
            ],
            "sourcePanel": {
                "title": "Kaynak ve inceleme durumu",
                "summary": "Oran, eşik ve uygulama kuralları belirlenen aralıkta resmî kaynaktan yeniden incelenir. Değişiklik varsa config, yoksa inceleme tarihi yenilenir.",
                "sourceIds": [
                    "tr_withholding_general_rates",
                    "tr_deposit_withholding"
                ],
                "notes": [
                    "Beyan sınırı, gelir sahibinin statüsü, anlaşmalar ve geçici kararların yürürlük tarihi ayrıca incelenmelidir."
                ]
            }
        },
        "config": {
            "kind": "withholding-tax-v1",
            "calculatorId": "ticari-muhasebe.stopaj-hesaplama",
            "amount": [
                0.01,
                1000000000
            ],
            "incomeRules": {
                "self-employment": {
                    "label": "Serbest meslek ödemesi",
                    "ratePercent": 20,
                    "declarationLabel": "Muhtasar ve prim hizmet beyannamesi"
                },
                "copyright": {
                    "label": "Telif hakkı ödemesi",
                    "ratePercent": 17,
                    "declarationLabel": "Muhtasar ve prim hizmet beyannamesi"
                },
                "foreign-currency-deposit": {
                    "label": "Döviz mevduat faizi",
                    "ratePercent": 25,
                    "declarationLabel": "Banka / aracı kurum"
                },
                "dividend": {
                    "label": "Kâr payı / temettü",
                    "ratePercent": 15,
                    "declarationLabel": "Kâr payını dağıtan kurum"
                },
                "repo": {
                    "label": "Repo geliri",
                    "ratePercent": 15,
                    "declarationLabel": "Banka / aracı kurum"
                },
                "eurobond": {
                    "label": "Hazine Eurobond faizi",
                    "ratePercent": 0,
                    "declarationLabel": "Kaynakta kesinti yok; yıllık beyan ayrı değerlendirilir"
                }
            },
            "rentRatePercent": 20,
            "rentDeclarationWithholding": "GVK 94 kapsamındaki kiracı beyan eder",
            "rentDeclarationNoWithholding": "Kesinti yapan taraf yok; kiralayanın yıllık beyanı ayrı incelenir",
            "tlDepositRatesPercent": {
                "up-to-six-months": 17.5,
                "six-to-twelve-months": 15,
                "over-one-year": 10
            },
            "tlDepositDeclarationLabel": "Banka / aracı kurum",
            "policyLifecycle": {
                "configVersion": "1.0.0",
                "sourceIds": [
                    "tr_withholding_general_rates",
                    "tr_deposit_withholding"
                ],
                "reviewedOn": "2026-08-25",
                "effectiveFrom": "2026-08-25",
                "effectiveTo": null,
                "reviewAfter": "2026-10-01"
            }
        },
        "testVectors": {
            "kind": "commercial-oracle-v1",
            "cases": [
                {
                    "caseId": "monthly-rent-20",
                    "scenario": "positive",
                    "input": {
                        "incomeType": "rent",
                        "direction": "gross-to-net",
                        "amount": 10000,
                        "withholdingResponsibility": "applies",
                        "rentPeriod": "monthly"
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "ratePercent": 20,
                            "grossAmount": 10000,
                            "withholdingAmount": 2000,
                            "netAmount": 8000,
                            "annualWithholdingAmount": 24000
                        }
                    },
                    "tolerance": 1e-9
                },
                {
                    "caseId": "tl-deposit-short",
                    "scenario": "boundary",
                    "input": {
                        "incomeType": "tl-deposit",
                        "direction": "gross-to-net",
                        "amount": 10000,
                        "tlDepositTerm": "up-to-six-months"
                    },
                    "expected": {
                        "ok": true,
                        "value": {
                            "ratePercent": 17.5,
                            "withholdingAmount": 1750,
                            "netAmount": 8250
                        }
                    },
                    "tolerance": 1e-9
                },
                {
                    "caseId": "rent-missing-responsibility",
                    "scenario": "adversarial",
                    "input": {
                        "incomeType": "rent",
                        "direction": "gross-to-net",
                        "amount": 10000,
                        "rentPeriod": "monthly"
                    },
                    "expected": {
                        "ok": false,
                        "field": "withholdingResponsibility",
                        "errorCode": "REQUIRED"
                    },
                    "tolerance": 1e-9
                }
            ]
        },
        "lastReviewed": "2026-08-25"
    }
];
